// Thin fetch wrapper for the Stewardship OS API.
//
// credentials: "include" so session cookies round-trip.
//
// Dev: Vite proxies /api/* to the deployed dev worker (see
// web/vite.config.ts) — requests go to same-origin localhost:5173
// and cookies round-trip cleanly.
//
// Prod (Cloudflare Pages): we hit the worker directly via cross-
// origin fetch + credentials:include. Worker CORS is configured to
// allow any *.pages.dev origin; Better Auth trustedOrigins lists
// the production Pages URL.
//
// VITE_API_ORIGIN env var overrides the default for both builds —
// useful for local dev pointing at staging, or for CI smoke tests.

// Type-only import — the notification shape (also used by the bell panel
// + /notifications page). notifications-data is a pure module, so no
// runtime cycle.
import type { DemographicsRow, LoanClaimRow, LoanFileRow, LoanFileView } from "@shared/db/mortgage-loan-files";
import type { ProviderMatch } from "@shared/aca/lookup";

/** One claim as the claims list returns it (A1c's popover). */
export type LoanClaimListItem = LoanClaimRow & { value: string | number | boolean | null; is_qualifying: boolean };
import type { EnrollmentRowInput } from "@shared/employers/enrollment-import";
import type { FamilyDecPageReading } from "@shared/services/declaration-page";
import type { PlanningStatementView } from "@shared/db/planning-statements";
import type { CarrierMonthRow, PlanningCandidate, PlanningDeposit } from "@shared/db/planning-matching";
import type { PlanningStatementFormat } from "@shared/planning-statements/statement-formats";
import type { HeldPerson } from "@shared/marketing/held-people";
import type { GoogleLeadOnOpportunity } from "@shared/ads/local-services-state";
import type { CheckinAnswers, CheckinView } from "@shared/marketing/checkin";
import type { GoalLearningView, GoalScoreboard } from "@shared/marketing/goal-scoreboard";
import type {
  ConnectionStatusDto,
  ConnectionCheckDto,
  ConnectionLinkDto,
  ConnectionGroupDto,
  ConnectionRowDto,
  ConnectionsHealthDto,
  IntegrationMeterDto,
  IntegrationChargeLineDto,
  IntegrationInvoiceDto,
  IntegrationCostRowDto,
  IntegrationCostReportDto,
  CostBillingPeriod,
  CostChargeKind,
  CostMonthCost,
  CostTierMode,
} from "@shared/types/integrations";
import type { CompPlanDetail, CompPlanGap } from "@shared/types/comp-plans";
import type {
  AchConfirmationRow,
  GustoImportTestRow,
  GustoLinkRow,
  PayAccess,
  PayApproverRow,
  PayCalendarSettingsRow,
  PayCompany,
  PayDateOverview,
  PaymentExceptionRow,
  PayMonthProof,
  PayRunDetail,
  PayRunExportRow,
  SeparationRow,
  UnpaidLedger,
} from "@shared/types/pay-runs";
import type { MyStatements, PayStatementView, RunStatements } from "@shared/types/pay-statements";
import type { ManualActionReport, OpsDeskView } from "@shared/types/ops-desk";
import type { RetirementFileSetup, RetirementNameSource } from "@shared/types/retirement-file";
import type { CompCalculatorKey } from "@shared/comp-schedules/calculators";
import type { CompScheduleCard } from "@shared/comp-schedules/resolve";
import type { RatePlanRow } from "@shared/comp-schedules/rate-entry";
import type { NewRuleInput } from "@shared/comp-plans/form";
import type { CarrierRow } from "@shared/comp-schedules/carriers";
import type { WaitingItem } from "@shared/marketing/inbox";
import type { ReplyWay } from "@shared/marketing/social-reply";
import type { EmailBlock as EmailBlockT } from "@shared/marketing/email-piece";
import type { GoalResult, ResultsResponse } from "@shared/marketing/results";
import type { CampaignHoldoutResult } from "@shared/marketing/holdouts";
import type { OutcomeInfluenceRow, PaidAdjustAction, PaidHelp, PaidKind, PaidPieceResult } from "@shared/marketing/paid-results";
import type { LeaderboardPeriod, LeaderboardResponse } from "@shared/marketing/leaderboard";
import type { RecognitionChannel, RecognitionDraft, RecognitionSendResponse } from "@shared/marketing/recognition";
import type { BudgetMeterWire, MarketingRuleKey, RuleChangeResponse, RuleHistoryEntry, SettingsHomeResponse } from "@shared/marketing/marketing-rules";
import type { QuestionRead, Suggestions as ReadSuggestions } from "@shared/marketing/email-read";
import type { GoalCounts, GoalEnvelope, GoalReading, Proposal, StoredPlan } from "@shared/marketing/goal-plan";
import type { GoalAnswers, GoalPace, GoalQuestion, OutcomeMeasure } from "@shared/marketing/goal-conversation";
import type { Alternative, AudienceShape, RankedFamily, ReachChannel } from "@shared/marketing/goal-audience";
import type { ResearchBrief } from "@shared/marketing/research-brief";
import type { Recommendation, Variation } from "@shared/marketing/goal-recommendation";
import type { PlaybookFields, PlaybookStatus } from "@shared/marketing/playbooks";
import type { LaunchCheck, PlayChannel, PlayFields, PlayStatus, PlayWords } from "@shared/marketing/plays";
import type { ResearchTopic } from "@shared/marketing/outside-research";
import type { AdFormat as MarketAdFormat, SourceKind as MarketSourceKind } from "@shared/marketing/market-research";
import type { GrowthLevel } from "@shared/marketing/growth-levels";
import type { HeaderNumbers, QueueDetail, QueueKind, QueueRow, QueueState, SignerRole, SignoffState } from "@shared/marketing/approval-queue";
import type { VersionView } from "@shared/ads/paid-versions";
import type { ClipCompany, ClipView } from "@shared/marketing/clips";
import type { RelevantTouchLike } from "@shared/activities/relevant-touch-line";
import type {
  FinancialBook,
  FinancialBookOwner,
  FinancialBookSort,
  FinancialBookStatusFilter,
  FinancialCareTrail,
} from "@shared/db/financial-care-book";
import {
  withHealthAccess,
  type HealthAccessDenial,
  type HealthAccessReason,
} from "./lib/health-access";
import type { MockNotification } from "./components/notifications-data";
import type { EsignSendableKind } from "@shared/esign/sendable-kinds";
import type { EsignTextStyle } from "@shared/esign/text-style";
import type {
  AttentionItem as CompanyCoverageAttentionItem,
  CompanyPolicyAggregate,
  CompanyPolicyInput,
  CoverageCapabilities,
  CoverageCompany,
  DecPageReading,
  ImportReading as CoverageImportReading,
  LifeAccessView,
  CoverageManagersView,
  ProgramReview,
  ProgramReviewDecision,
  ProgramReviewItem,
  RenewalReview,
  CheckCell,
  CoverageType,
  RequirementBasis,
} from "@shared/types/company-coverage";
import type { CoverageResearchNote } from "@shared/company-coverage/research-notes";
import type { CoverageTotals, PayerLine, PolicySummary } from "@shared/company-coverage/derive";
import type {
  ChecklistAppliesTo,
  ChecklistItemType,
  ChecklistKind,
  ChecklistSide,
  ChecklistVisibility,
} from "@shared/checklists/checklist-model";
import type { SubjectPropertyAddressOption } from "@shared/realty/subject-property-options";
import type { AdvisorRequestInboxRow } from "./pages/baker-requests-helpers";
import type { CompiledAction, RuleSelection } from "@shared/automations/compile";
import type { ConnectedPerson } from "@shared/types/household";
import type { LenderProgram } from "@shared/types/lender-programs";
import type { DecisionVerdict } from "@shared/marketing/decision-vocabulary";
import type { TodayResponse } from "@shared/marketing/today";
import type { HouseholdCardResponse } from "@shared/marketing/household-card";
import type { HouseholdMarketingResponse } from "@shared/marketing/household-tab";
import type { PreferenceSave, PreferenceView, PrivacyNoticeLog } from "@shared/marketing/preferences";
import type { QueueResult } from "@shared/marketing/ask-queue";
import type { IncomeSource, StoredBrief } from "@shared/marketing/brief";
import type { ThankYouEmailComposer, ThankYouSide, ThankYouSubject } from "@shared/marketing/thank-you";
import type { TodayEmailComposer, TodayEmailMode } from "@shared/marketing/today-email";
import type { AskOneComposer } from "@shared/marketing/household-ask";
import type { PrioritiesResponse, FourWay, GoalAudience, GoalMode, OpportunitiesResponse, OpportunityGap, QuickEstimate } from "@shared/marketing/opportunities";
import type { NoteEntitySegment } from "@shared/types/notes";
import type { AssetOwnership, AssetType } from "@shared/planning/schedule-of-assets";
// The close-out cockpit's shapes are the server's own — re-exported
// rather than restated, so a field added on one side cannot go missing
// on the other.
import type {
  MortgageCloseOutQueue,
  MortgageCloseOutTasks,
  MortgageUpcomingClosings,
} from "@shared/db/mortgage-close-out-queue";
import type { PmLoanDesk } from "@shared/db/private-money-loan-desk";
import type { GoalPieceView } from "@shared/marketing/goal-pieces";
import type { CampaignStatus, PiecesTableRow } from "@shared/marketing/piece-ladder";
import type { ReferralNetworkScoped } from "@shared/db/referral-network";
import type { PmLenderProfileRow } from "@shared/db/pm-lender-profiles";
import type { RequestCard } from "@shared/private-money/request-card";
import type { PackageSection } from "@shared/private-money/loan-package";
import type { EligibilityAnswers } from "@shared/planning/eligibility";
import type { HealthQuoteFacts } from "@shared/planning/health-quote";
import type { PlanningBookOfBusiness } from "@shared/db/planning-book-of-business";
import type { ComparisonPlanInput, PlanComparison } from "@shared/planning/plan-comparison-fields";
import type { ContactRelationshipType, HouseholdRole } from "@shared/types/enums";
import type { AnswerShape } from "@shared/cleanup/answer-shape";
import type { CleanupAskedField, CleanupFieldWriter, CleanupFix } from "@shared/cleanup/fix";
import { ANSWER_WRITERS, encodeAnswer } from "@shared/cleanup/answer-writers";
import type { ClosingAddressDecisionState } from "@shared/db/closing-address-decision";
import type { ClosingAddressAnswer } from "@shared/realty/closing-address-decision";
import type { CommissionSplitType } from "@shared/types/documents";
import type { ConditionNode } from "@shared/automations/condition-types";
import type { RuleWarning } from "@shared/automations/warnings";
import type { Recurrence } from "@shared/automations/recurrence";
import type { FilterCondition, FilterSet } from "@shared/filters/filter-model";
import type { RealtySide } from "@shared/realty/transaction-side";
import type { StageTaskOwnerKind } from "@shared/processing/stage-tasks";
import type { ProcessingWorkspace } from "@shared/processing/workspace";
import type { StageTaskTemplate } from "@shared/processing/stage-tasks";
import type { ScopedKeyDateDefinition } from "@shared/db/processing-workspace";
import { serializeCondition } from "@shared/filters/url-codec";
import { shouldSignalSessionExpiry } from "./session-expiry";
import { recordActivity } from "./session-idle";
import type {
  DashboardHomeResponse,
  MeetingPrepResponse,
  TodayTodoResponse,
} from "@shared/types/dashboard-home";
import type { RealtyDealsResponse } from "@shared/types/realty-deals";
import type { TaskLinkContext } from "@shared/db/task-link-context";
import type { MemberMergePair } from "@shared/db/household-merge-member-pairs";
import type {
  MemberMergeOutcome,
  MemberMergeRequest,
} from "@shared/db/household-merge-with-members";
import type {
  CouponEconomics,
  NotePerformance,
  NoteTimeline,
  RiskBand as StructuredNoteRiskBand,
} from "@shared/structured-notes/payoff";
import type {
  BarrierType,
  CouponFrequency,
  NoteStatus,
  NoteType,
  StructuredNote,
} from "@shared/structured-notes/types";

const API_ORIGIN = import.meta.env["VITE_API_ORIGIN"] ?? "";

function apiUrl(path: string): string {
  return API_ORIGIN ? `${API_ORIGIN}${path}` : path;
}

// Re-exported from ./lib/media-url so existing `import { resolveMediaUrl }
// from "../api"` callers keep working. The implementation lives in the
// standalone module so low-level UI primitives can use it without
// importing this API client (circular-dependency safety).
export { resolveMediaUrl } from "./lib/media-url";

// ─── response types ─────────────────────────────────────────────────────

/** GET /api/service-records/:id/cancellation-request — mirrors
 *  CancellationRequestOptions in shared/db/insurance-cancellation-requests.ts
 *  (not imported: that module reaches the database client). */
/** The narrated Stew brief as the band shows it (Stage 2 PR 6). */
/** Ask Stew Stage 2 PR 7: one verified question (the admin library). */
export interface StewVerifiedQuestionRow {
  id: string;
  question: string;
  synonyms: string[];
  plan: Array<{ tool: string; args_summary: string }>;
  plan_tools: string[];
  notes: string | null;
  company_scope: string[] | null;
  owner_user_id: string;
  owner_name: string | null;
  approved_at: string;
  source_trace_id: string | null;
  source_message_id: string | null;
  active: boolean;
  updated_at: string;
}

/** Ask Stew Stage 2 PR 7: one proposed eval case. */
export interface StewEvalCaseRow {
  id: string;
  question: string;
  asker_role_slug: string | null;
  asker_company_scope: string[] | null;
  cause_code: string | null;
  expected_note: string | null;
  source_feedback_id: string | null;
  source_trace_id: string | null;
  status: "proposed" | "written" | "retired";
  created_by_user_id: string;
  created_by_name: string | null;
  created_at: string;
}

export interface StewBriefNarration {
  text: string;
  could_not_check: string[];
  generated_at: string;
  generated_by: "nightly" | "on_demand";
  trigger_reason: string;
  current: boolean;
}

export interface CancellablePolicyOption {
  service_record_id: string;
  household_id: string;
  product_noun: string;
  carrier: string | null;
  policy_number: string | null;
}

export interface CancellationRequestView {
  id: string;
  status: "sent" | "partly_signed" | "signed" | "declined" | "expired" | "cancelled";
  effective_date: string;
  reason: string;
  reason_other: string | null;
  requote_next_year: boolean;
  requested_at: string;
  requested_by_name: string | null;
  envelope_id: string | null;
  policies: Array<{ service_record_id: string; policy_number: string }>;
  signers: Array<{ name: string; email: string; signed_at: string | null; declined_at: string | null }>;
  signed_document: { id: string; filename: string; web_url: string | null } | null;
}

export interface CancellationRequestOptions {
  policy: CancellablePolicyOption;
  other_policies: CancellablePolicyOption[];
  people: Array<{
    contact_id: string;
    name: string;
    email: string | null;
    on_this_policy: boolean;
    is_primary_on_policy: boolean;
  }>;
  latest_request: CancellationRequestView | null;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  // Profile photo. App-internal URL served by /api/me/avatar/:userId
  // (carries a ?v= cache-buster), or null when the user has no photo and
  // the UI should fall back to the initials circle.
  image: string | null;
  // user_type + employee_role_slug are derived server-side from the
  // users + employee_roles tables and are null only when the account
  // is an orphan (signed-in but not mapped to an employee role). The
  // RequireAuth guard uses this to force-logout unmapped users.
  user_type: "employee" | "client" | "vendor_contact" | null;
  employee_role_slug: string | null;
  // Companies this user may CREATE/EDIT Ongoing Care plans for. "all" =
  // system_admin/compliance or an unscoped account; otherwise the slug
  // list. Drives the calendar lock badges + the New Plan company gate.
  // Server still enforces independently on every write. (PR 2.)
  editable_company_slugs: string[] | "all";
  // May this user see the Stewardship Productions e-sign library and
  // send team onboarding / contractor paperwork (2026-09-28)? Narrower
  // than editable_company_slugs: "all" does not imply it. Optional so a
  // cached pre-upgrade Worker response reads as "no", the safe answer.
  // The server enforces it on every route regardless.
  can_handle_productions_documents?: boolean;
  // Whether this user has 2FA enrolled (users.mfa_enabled — the same
  // source /api/me/mfa-status reads). Carried on the session response so
  // the boot path doesn't need a second serial round trip; optional so a
  // cached pre-upgrade Worker response can't break the type. MfaContext
  // falls back to /api/me/mfa-status when it's absent.
  mfa_enabled?: boolean;
}

// One entry in the team avatar directory (GET /api/team/avatars). Minimal
// non-sensitive identity so any advisor-avatar render site can resolve a
// teammate's profile photo by user id or name.
export interface TeamAvatar {
  user_id: string;
  name: string | null;
  image: string | null;
}

// Impersonation shape returned by /api/session when the signed-in user
// has an active impersonation session. `user` above reports the TARGET
// (so dashboards + sidebars render as them) and `real_user` reports
// the actual signed-in human (so the TopNav avatar stays correct).
export interface SessionImpersonationContext {
  session_id: string;
  started_at: string;
}

export interface SessionResponse {
  user: SessionUser | null;
  // Populated only while impersonating. The TopNav + user menu read
  // this to show the actual signed-in human's avatar + role.
  real_user?: SessionUser | null;
  impersonation?: SessionImpersonationContext | null;
  session?: { id: string; expires_at: string };
}

// Response shape for the impersonation admin endpoints.
export interface ImpersonationTargetSummary {
  id: string;
  name: string | null;
  email: string;
  employee_role_slug: string | null;
  advisor_user_id: string;
}

export interface ImpersonationStatusResponse {
  active: boolean;
  session_id?: string;
  started_at?: string;
  target_user?: ImpersonationTargetSummary;
}

export interface ImpersonationStartResponse {
  session_id: string;
  started_at: string;
  target_user: ImpersonationTargetSummary;
}

export interface ImpersonationHistoryRow {
  id: string;
  impersonator_user_id: string;
  impersonator_name: string | null;
  impersonator_email: string | null;
  target_user_id: string;
  target_name: string | null;
  target_email: string | null;
  target_role_slug: string | null;
  started_at: string;
  ended_at: string | null;
  last_activity_at: string;
  end_reason: "user_click" | "idle_timeout" | "max_duration" | "logout" | null;
  duration_ms: number | null;
  action_count: number;
}

export interface ImpersonationSessionDetail {
  session: ImpersonationHistoryRow;
  impersonator: {
    id: string;
    name: string | null;
    email: string;
  } | null;
  target: {
    id: string;
    name: string | null;
    email: string;
    role_slug: string | null;
  } | null;
  events: Array<{
    id: string;
    occurred_at: string;
    action: string;
    entity_type: string;
    entity_id: string | null;
    actor_user_id: string | null;
    field_name: string | null;
    purpose: string | null;
  }>;
}

export interface Household {
  id: string;
  display_name: string;
  /** Set only on an employer's hidden anchor household: the page sends the
   *  viewer to that employer instead. */
  anchor_business_entity_id?: string | null;
  primary_contact_id: string | null;
  primary_city: string | null;
  primary_state: string | null;
  primary_zip: string | null;
  // Migration 0181 (Joy, 2026-07-03): mailing street lines + a separate
  // physical address. Optional so older cached payloads stay assignable.
  primary_street_line_1?: string | null;
  primary_street_line_2?: string | null;
  physical_street_line_1?: string | null;
  physical_street_line_2?: string | null;
  physical_city?: string | null;
  physical_state?: string | null;
  physical_zip?: string | null;
  primary_county?: string | null;
  physical_county?: string | null;
  client_since: string | null;
  archived_at: string | null;
  archived_reason: string | null;
  notes: string | null;
  /** Migration 0144: household-level pets, free-text rapport cue. */
  pets?: string | null;
  /** Optimistic-lock counter (migration 0184): an edit sends it back so a
   *  concurrent edit returns 409 instead of silently clobbering. */
  version_number?: number;
  is_quarantined: boolean;
  quarantine_reason: string | null;
  /** Migration 0090 (2026-05-22): person / couple / family / business.
   *  Drives icon + tab label on the household detail page (building
   *  icon + "Principals" for 'business'; default family treatment
   *  for the rest). */
  entity_type?: "person" | "couple" | "family" | "business";
  /** ISO timestamp; surfaced in the household-details lifecycle card. */
  created_at?: string;
  /** ISO timestamp; surfaced in the household-details lifecycle card. */
  updated_at?: string;

  // Wave D.1 (April 2026): What Matters Most banner field.
  what_matters_most?: string | null;
  what_matters_most_updated_at?: string | null;
  what_matters_most_updated_by_user_id?: string | null;
  // Nate 2026-07-24: display name for what_matters_most_updated_by_user_id,
  // resolved server-side so the banner can render "Updated Jul 23 by
  // Nate Swartz" without a second round-trip. Optional so older cached
  // payloads (before this field existed) stay assignable.
  what_matters_most_updated_by_name?: string | null;
  // 2026-07-01: "we don't know this family" terminal state (migration 0173).
  what_matters_most_unknown_reason?: string | null;
  what_matters_most_unknown_at?: string | null;
  what_matters_most_unknown_by_user_id?: string | null;

  // Stage of Wealth — Control / Margin / Impact, ONE per family
  // (2026-09-18). Not the same fact as the "life stage" the status line
  // shows Financial viewers, which is an age bracket from birthdays.
  // null = nobody has said yet.
  wealth_stage?: "control" | "margin" | "impact" | null;
  wealth_stage_set_by_user_id?: string | null;
  wealth_stage_set_at?: string | null;
  // Resolved server-side alongside the What Matters Most author, in the
  // same one query, so the pill can say who recorded it without a second
  // round-trip and without spending the route's query budget.
  wealth_stage_set_by_name?: string | null;
  // The honest "we can't say yet" (Grant, 2026-09-18). While set,
  // wealth_stage stays null.
  wealth_stage_unknown_reason?: string | null;
  wealth_stage_unknown_at?: string | null;
  wealth_stage_unknown_by_user_id?: string | null;
}

// PATCH /api/households/:id body shape. Mirrors EditableHouseholdFields
// in shared/db/households.ts. Excludes archive lifecycle, system
// columns, and primary_contact_id is editable as a UUID (a contact
// picker UI is a v1.x polish).
export interface EditableHouseholdFields {
  display_name: string;
  primary_contact_id: string | null;
  primary_city: string | null;
  primary_state: string | null;
  primary_zip: string | null;
  primary_street_line_1: string | null;
  primary_street_line_2: string | null;
  physical_street_line_1: string | null;
  physical_street_line_2: string | null;
  physical_city: string | null;
  physical_state: string | null;
  physical_zip: string | null;
  primary_county: string | null;
  physical_county: string | null;
  notes: string | null;
  pets: string | null;
  client_since: string | null;
  is_quarantined: boolean;
  quarantine_reason: string | null;
}

export interface UpdateHouseholdResponse {
  household: Household;
  changes_count: number;
  // Baker 2026-07-31 — members who stopped being marked primary as a
  // side effect of this edit designating one. Optional so a response
  // from an older Worker deploy still parses.
  primary_demoted_contact_ids?: string[];
}

export interface HouseholdContact {
  id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  phone_primary: string | null;
  /** Secondary contact channels — already projected via the `SELECT *`
   *  in `listContactsByHousehold`. Declared so the Cleanup research
   *  drawer doesn't say "no contact details on file" for a member whose
   *  only channel is a secondary email/phone (Grant 2026-07-08). */
  secondary_email?: string | null;
  phone_secondary?: string | null;
  household_role: string | null;
  birthday: string | null;
  /** Surfaced on the household members card so the Prefers row can
   *  honour the advisor "do not email" override (otherwise the
   *  computed fallback would mislabel the contact as preferring email
   *  when the advisor has explicitly disabled email). The household
   *  endpoint already projects this column via `SELECT *` in
   *  `listContactsByHousehold`. */
  do_not_email?: boolean;
  /** The client's own stated preferred contact method (migration 0238),
   *  surfaced on the household members card's "Prefers" row alongside
   *  the do_not_email override and the phone/email fallback guess.
   *  Already projected via the `SELECT *` in `listContactsByHousehold`. */
  preferred_contact_method?: string | null;
  /** The marketing email opt-in (contacts.marketing_ok, 1 or 0), already
   *  projected by the household endpoint's SELECT *; the Marketing tab's
   *  "How they want to hear from us" reads it (row 3.2). */
  marketing_ok?: number | boolean | null;
  /** "How they found us" — surfaced on the household details card from
   *  the primary contact (Nate's realty ask). Already projected via the
   *  `SELECT *` in `listContactsByHousehold`; declared here so the UI can
   *  read it. */
  referral_source_type?: string | null;
  referral_source_detail?: string | null;
  /** Address + creation metadata — all projected via the `SELECT *` in
   *  `listContactsByHousehold`. Declared so the Cleanup research drawer
   *  can show enough detail to TELL TWO MEMBERS APART (Brian 2026-07-07:
   *  two same-name members with no other fields shown are impossible to
   *  decide between). */
  street_line_1?: string | null;
  street_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  created_at?: string | null;
  /** Deceased status (migration 0081 + 0207) — already projected via the
   *  `SELECT *` in `listContactsByHousehold`. Declared here so the
   *  household page can show the "Deceased" pill and offer the mark/
   *  unmark actions directly on the People tab (Ryan, 2026-08-13). */
  is_deceased?: boolean;
  deceased_at?: string | null;
}

// Expeditions — shared, collaborator-enabled planning notes (PR 10).
export interface Expedition {
  id: string;
  owner_user_id: string;
  title: string;
  note: string | null;
  event_date: string | null;
  status: "active" | "done" | "archived";
  created_at: string;
  updated_at: string;
  collaborator_user_ids: string[];
  participant_names: { user_id: string; name: string | null }[];
}

// Listing engagement (PR 7) — every metric nullable; advisor logs what
// they have. Notes carries showing feedback / neighborhood context.
export interface EngagementSnapshot {
  id: string;
  realty_listing_id: string;
  captured_by_user_id: string | null;
  captured_at: string;
  zillow_views: number | null;
  zillow_saves: number | null;
  fb_views: number | null;
  fb_reactions: number | null;
  fb_clicks: number | null;
  showings_total: number | null;
  showings_last_7: number | null;
  email_sent_count: number | null;
  email_opens: number | null;
  notes: string | null;
}

export interface ListingEngagement {
  latest: EngagementSnapshot | null;
  history: EngagementSnapshot[];
}

export type ListingEngagementInput = Partial<
  Omit<EngagementSnapshot, "id" | "realty_listing_id" | "captured_by_user_id">
>;

// Seller listing-update draft (PR 8).
export interface SellerUpdateDraft {
  subject: string;
  email_body: string;
  sms_body: string;
  recommended_price_reduction: number | null;
}

// Realty market notes (PR 9).
export interface MarketNote {
  id: string;
  kind: "city_development" | "cromford";
  city: string | null;
  title: string | null;
  body: string;
  captured_by_user_id: string | null;
  captured_at: string;
}

export interface MarketUpdate {
  cromford: MarketNote | null;
  cities: { city: string; note: MarketNote | null }[];
}

export interface ExpeditionTeammate {
  user_id: string;
  name: string | null;
  email: string;
}

export interface RhythmInstance {
  id: string;
  rhythm_template_slug: string;
  rhythm_template_display_name: string;
  cycle_anchor_date: string;
  cycle_end_date: string;
  status: string;
  linked_service_record_id: string | null;
  /** Optional — present on the contact-detail response (Wave C,
   *  April 2026) so the rail card can label "cycle 2 of N". The
   *  household-detail response also surfaces it; older surfaces
   *  might not include it which is why this is marked optional. */
  cycle_number?: number;
}

export interface ServiceRecord {
  id: string;
  company_id: string;
  company_slug: string;
  service_type: string;
  service_family: string;
  display_label: string | null;
  status: string;
  effective_date: string | null;
  termination_date: string | null;
  external_ref: string | null;
  external_system: string | null;
  product_metric: number | null;
  product_metric_unit: string | null;
  estimated_revenue_year_1: number | null;
  rhythm_instances: RhythmInstance[];
  // Joy feedback PR 8 — linked insurance-policy summary on the
  // household-detail response. Null for non-insurance services.
  policy_type?: string | null;
  /** The carrier's policy number — the strongest identifier when a
   *  household has multiple look-alike policies (Mylah, 2026-08-03). */
  policy_number?: string | null;
  /** Vehicles on an auto policy — answers "which car is on which policy"
   *  when a carrier's per-policy vehicle cap forces a second auto policy
   *  (Mylah, 2026-08-03). Render via formatPolicyVehicles. */
  policy_vehicles?: string | null;
  /** How many policy rows this record carries. >1 means the policy
   *  fields above describe one of several (Mylah, 2026-08-05). */
  policy_row_count?: number;
  carrier_name?: string | null;
  policy_expiration_date?: string | null;
  /** The linked policy's effective_date — tells the UI whether the term
   *  above is already the upcoming/not-yet-effective one (Joy, 2026-09-14). */
  policy_effective_date?: string | null;
  annual_premium?: number | null;
  renewal_premium_offer?: number | null;
  /** Snapshot of what "current premium" read just before the linked
   *  policy's most recent renewal rolled forward — see
   *  shared/db/ivans-policy-sync.ts's PRE-RENEWAL PREMIUM SNAPSHOT note. */
  pre_renewal_premium?: number | null;
  /** True when this SR has a linked insurance_policies row — lets the UI
   *  offer "Cancel policy" only where a real policy exists (a won-
   *  opportunity conversion creates a policy-family SR with no policy row). */
  has_insurance_policy?: boolean;
  /** The linked policy row's id (PATCH /api/insurance-policies/:id). */
  insurance_policy_id?: string | null;
  /** Term length in months and how the premium is paid, off the linked
   *  policy row (the Insurance household view's policy lines). */
  policy_term_length_months?: number | null;
  policy_payment_method?: string | null;
  policy_payment_frequency?: string | null;
  /** The carrier's commission rate for this policy (thousandths of a
   *  percent) and its confidence: with the live premium, the revenue
   *  estimate. null = no rate known yet. */
  policy_commission_rate_milli?: number | null;
  policy_commission_rate_confidence?: "high" | "medium" | "low" | null;
  /** `engine`: the reconciliation engine's stored rate; `carrier_comp_plan`
   *  / `observed`: read from the carrier's rate table or statement history
   *  because the engine hadn't scored the policy. */
  policy_commission_rate_source?: "engine" | "carrier_comp_plan" | "observed" | null;
  /** True when that read assumed the term (no start date on the policy). */
  policy_commission_rate_term_assumed?: boolean;
  /** The people this record is FOR — borrower / insured / account holder
   *  / seller (0232, Grant 2026-08-03). Primary first. Empty = a
   *  grandfathered pre-0232 record whose person is still unconfirmed. */
  people?: ServiceRecordPerson[];
  /** The Processing record delivering this service (Nate, 2026-08-11).
   *  Null = none exists, which the Services tab says out loud rather
   *  than leaving the row looking merely empty. */
  processing_link?: ServiceRecordProcessingLink | null;
}

/** One pre-filled email off a Realty transaction (Nate, 2026-08-11).
 *  `source` says whether the wording came from an admin-editable
 *  communication template or the built-in fallback. */
export interface RealtyMessageDraft {
  kind: "title" | "trade";
  label: string;
  template_slug: string;
  subject: string;
  /** Plain text — the composer wants HTML, so the caller converts. */
  body: string;
  source: "template" | "built_in";
  suggested_to: string[];
  suggested_to_name: string | null;
}

// ─── Seller net proceeds (Nate, 2026-08-19) ──────────────────────────
//
// Shapes mirror shared/realty/net-proceeds.ts + the /api/net-proceeds
// routes. The BREAKDOWN is always computed server-side from the stored
// inputs so the number on the card, in the comparison, and in the emailed
// copy can never disagree.

// ─── Realty offers ───────────────────────────────────────────────────
//
// Shapes mirror shared/db/realty-offers.ts + the /api/realty-offers
// routes. The three DERIVED fields on an offer (status_label,
// allowed_transitions, can_counter) are computed server-side from the
// status machine in shared/realty/offer-status.ts — the pane asks rather
// than re-deriving them, because a second copy of that rule would end up
// disagreeing and showing a button that does nothing.

export type RealtyOfferDirection = "outgoing" | "incoming";

export type RealtyOfferStatus =
  | "drafted"
  | "sent"
  | "submitted"
  | "countered"
  | "accepted"
  | "rejected"
  | "withdrawn"
  | "cancelled";

/**
 * What POST /realty-offers/:id/status reports when the move ended an
 * accepted offer's contract and sent the Processing record back to
 * Opportunities in the same call (Grant, 2026-09-21). Null on an ordinary
 * status move.
 */
export interface RealtyOfferMovedBack {
  transaction_id: string;
  opportunity_id: string;
  opportunity_created: boolean;
  stage: string;
  stage_label: string;
  /** Realty: the record was parked for the next contract, not closed. */
  parked: boolean;
}

export interface RealtyOfferDto {
  id: string;
  direction: RealtyOfferDirection;
  opportunity_id: string | null;
  transaction_id: string | null;
  household_id: string;
  service_record_id: string | null;
  label: string;
  property_address: string | null;
  property_address_line_1: string | null;
  property_address_line_2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  counterparty_name: string | null;
  counterparty_agent_name: string | null;
  counterparty_agent_contact: string | null;
  counterparty_agent_email: string | null;
  counterparty_agent_phone: string | null;
  counterparty_agent_license: string | null;
  counterparty_agent_mls_code: string | null;
  counterparty_firm_name: string | null;
  counterparty_firm_mls_code: string | null;
  counterparty_firm_license_number: string | null;
  escrow_company: string | null;
  /** The MLS sheet's facts and the escrow choice (2026-09-25). */
  captured_apn: string | null;
  captured_legal_description: string | null;
  captured_county: string | null;
  captured_year_built: number | null;
  counterparty_agent_2_name: string | null;
  counterparty_agent_2_email: string | null;
  counterparty_agent_2_phone: string | null;
  counterparty_agent_2_license: string | null;
  counterparty_agent_2_mls_code: string | null;
  counterparty_agent_2_firm_name: string | null;
  counterparty_agent_2_firm_mls_code: string | null;
  counterparty_agent_2_firm_license_number: string | null;
  listing_escrow_officer_name: string | null;
  listing_escrow_officer_phone: string | null;
  listing_escrow_officer_email: string | null;
  listing_escrow_officer_address: string | null;
  escrow_choice: RealtyEscrowChoice | null;
  escrow_officer_company: string | null;
  escrow_officer_name: string | null;
  escrow_officer_email: string | null;
  escrow_officer_phone: string | null;
  escrow_officer_address: string | null;
  escrow_vendor_id: string | null;
  status: RealtyOfferStatus;
  status_changed_at: string | null;
  countered_from_offer_id: string | null;
  sent_at: string | null;
  received_at: string | null;
  expires_at: string | null;
  notes: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  /** The status in words, worded for the direction: "Submitted" on ours,
   *  "Received" on theirs. */
  status_label: string;
  /** Where this offer may move next. Drives the status picker, so an
   *  option the server would refuse is never offered. */
  allowed_transitions: RealtyOfferStatus[];
  can_counter: boolean;
}

/** One entry in the closed catalog of things an offer can say. */
export interface RealtyOfferSlotDto {
  key: string;
  label: string;
  type: string;
  token: string;
  example: string;
  options: string[] | null;
}

/** One negotiation: a root offer and the counters that answered it. */
export interface RealtyOfferThreadDto {
  root_id: string;
  /** Oldest first — a negotiation reads forwards. */
  offer_ids: string[];
  /** The offer that represents this negotiation right now. */
  current_id: string;
  open: boolean;
  counter_count: number;
}

export interface RealtyOffersResponse {
  parent_kind: "opportunity" | "transaction";
  parent_id: string;
  side: "buy" | "sell" | null;
  /** What a NEW offer on this record defaults to: a listing receives
   *  them, a buyer's record writes them. */
  default_direction: RealtyOfferDirection;
  suggested_property_address: string | null;
  offers: (RealtyOfferDto & { terms: Record<string, string> })[];
  threads: RealtyOfferThreadDto[];
  summary: {
    total_threads: number;
    open_threads: number;
    accepted_thread_id: string | null;
  };
  slots: RealtyOfferSlotDto[];
}

export interface RealtyOfferTermDto {
  id: string;
  offer_id: string;
  slot_key: string;
  value: string;
  provenance: "ai_accepted" | "ai_edited" | "typed";
  /** The words this value came from, verbatim. Null for a typed value. */
  source_quote: string | null;
  updated_at: string;
}

export interface RealtyOfferEventDto {
  id: string;
  offer_id: string;
  kind: string;
  from_status: string | null;
  to_status: string | null;
  /** One plain-English sentence, written by the server, so a timeline
   *  never renders an enum at a person. */
  summary: string;
  detail_json: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  created_at: string;
}

export interface RealtyOfferDetailResponse {
  offer: RealtyOfferDto;
  terms: RealtyOfferTermDto[];
  events: RealtyOfferEventDto[];
  documents: {
    id: string;
    document_id: string;
    file_name: string | null;
    document_type: string | null;
    /** "received" when the other side wrote it. */
    origin: "ours" | "received";
    created_at: string;
  }[];
  envelopes: {
    id: string;
    envelope_id: string;
    role: string;
    title: string | null;
    status: string | null;
    sent_at: string | null;
    completed_at: string | null;
  }[];
  counter_offer_id: string | null;
  slots: RealtyOfferSlotDto[];
}

/** A term an accepted offer could not carry onto the contract card. */
export interface UncarriedOfferTermDto {
  slot_key: string;
  label: string;
  value: string;
  /** Plain English, shown to the advisor verbatim. */
  reason: string;
}

export interface RealtyOfferAcceptResponse {
  offer: RealtyOfferDto | null;
  carried: Record<string, string | number>;
  uncarried: UncarriedOfferTermDto[];
  summary: string;
  /** Still live on this file after this one was accepted. Reported, not
   *  closed: a seller can accept one offer and keep a backup alive, so
   *  the pane asks rather than deciding. */
  other_open_offers: {
    id: string;
    label: string;
    status: RealtyOfferStatus;
    direction: RealtyOfferDirection;
  }[];
}

/** One value the reader proposes, with the words it came from. */
export interface OfferProposalDto {
  slotKey: string;
  value: string;
  /** What the model DID to get here. The gate: only "heard" — a value the
   *  advisor literally said, unconverted — starts ticked. */
  reading: "heard" | "interpreted" | "ambiguous";
  /** Verbatim words from the summary. Never empty; a proposal without one
   *  is dropped before it reaches the screen. */
  quote: string;
  /** Plain English, only when the model converted or inferred. */
  note: string | null;
  /** Other defensible readings, so an ambiguous value is picked, not
   *  retyped. */
  alternatives: string[];
}

export interface OfferSummaryReadResponse {
  proposals: OfferProposalDto[];
  /** Slots the summary genuinely never mentioned — an unheard thing must
   *  never look like a heard one. */
  not_mentioned: string[];
  /** Things said that no slot covers, in the model's own words. */
  uncovered: string[];
}

export interface OfferSummaryStateResponse {
  offer_id: string;
  latest_summary: {
    id: string;
    text: string;
    input_method: "voice" | "typed";
    created_at: string;
  } | null;
  values: {
    slot_key: string;
    value: string;
    provenance: "ai_accepted" | "ai_edited" | "typed";
    source_quote: string | null;
    updated_at: string;
  }[];
  slots: RealtyOfferSlotDto[];
}

export interface OfferNetSheetResponse {
  scenario: NetProceedsScenarioDto | null;
  /** Terms that couldn't become inputs, each with a sentence saying why.
   *  A number that quietly didn't reach a seller's sheet is worse than
   *  one that plainly didn't. */
  uncarried: UncarriedOfferTermDto[];
}

export interface RealtyOfferWrite {
  label: string;
  /** The display line. Either send this, or the structured parts below —
   *  the server composes one from the other so they cannot drift. */
  property_address?: string | null;
  property_address_line_1?: string | null;
  property_address_line_2?: string | null;
  property_city?: string | null;
  property_state?: string | null;
  property_zip?: string | null;
  counterparty_name?: string | null;
  counterparty_agent_name?: string | null;
  counterparty_agent_contact?: string | null;
  counterparty_agent_email?: string | null;
  counterparty_agent_phone?: string | null;
  counterparty_agent_license?: string | null;
  counterparty_agent_mls_code?: string | null;
  counterparty_firm_name?: string | null;
  counterparty_firm_mls_code?: string | null;
  counterparty_firm_license_number?: string | null;
  escrow_company?: string | null;
  /** The MLS sheet's facts and the escrow choice (2026-09-25). */
  captured_apn?: string | null;
  captured_legal_description?: string | null;
  captured_county?: string | null;
  captured_year_built?: number | null;
  counterparty_agent_2_name?: string | null;
  counterparty_agent_2_email?: string | null;
  counterparty_agent_2_phone?: string | null;
  counterparty_agent_2_license?: string | null;
  counterparty_agent_2_mls_code?: string | null;
  counterparty_agent_2_firm_name?: string | null;
  counterparty_agent_2_firm_mls_code?: string | null;
  counterparty_agent_2_firm_license_number?: string | null;
  listing_escrow_officer_name?: string | null;
  listing_escrow_officer_phone?: string | null;
  listing_escrow_officer_email?: string | null;
  listing_escrow_officer_address?: string | null;
  escrow_choice?: RealtyEscrowChoice | null;
  escrow_officer_company?: string | null;
  escrow_officer_name?: string | null;
  escrow_officer_email?: string | null;
  escrow_officer_phone?: string | null;
  escrow_officer_address?: string | null;
  escrow_vendor_id?: string | null;
  expires_at?: string | null;
  notes?: string | null;
}

/** Which title officer an offer names: the drafting agent's preferred
 *  one, the one the listing side asked for, or someone else. */
export type RealtyEscrowChoice = "preferred" | "listing_side" | "other";

export type NetProceedsLineMode = "percent" | "amount";

export type NetProceedsLineKey =
  | "stewardship_commission"
  | "buyer_agent_commission"
  | "title_insurance"
  | "escrow_fee"
  | "misc_title_fees"
  | "hoa_transfer_fee"
  | "hoa_disclosure_fee"
  | "hoa_prepaid_fees"
  | "hoa_other_fees"
  // LEGACY: the one combined HOA row scenarios carried before
  // 2026-09-03. Still returned by the API for those rows, so the type
  // keeps it forever.
  | "hoa_fees"
  | "buyer_concessions"
  | "home_warranty"
  | "property_taxes"
  | "repair_credits"
  | "custom";

/** One loan being paid off at closing. */
export interface NetProceedsLoanDto {
  label: string;
  balance: number | null;
  /** Annual rate as a percent — 6.875 means 6.875%. */
  rate_percent: number | null;
  fees: number | null;
}

export interface NetProceedsLineDto {
  key: NetProceedsLineKey;
  label: string;
  mode: NetProceedsLineMode;
  value: number | null;
}

export interface NetProceedsBreakdownDto {
  sale_price: number;
  price_per_sf: number | null;
  lines: (NetProceedsLineDto & { amount: number })[];
  total_costs: number;
  transaction_proceeds: number;
  /** What each loan takes at closing, itemised. Empty for a free-and-clear
   *  home and for a scenario written before loans existed. */
  loans: (NetProceedsLoanDto & {
    daily_interest: number;
    accrued_interest: number;
    payoff: number;
  })[];
  loan_balances: number;
  accrued_interest: number;
  payoff_fees: number;
  daily_interest: number;
  days_to_close: number | null;
  /** Balances + accrued interest + fees. */
  loan_payoff: number;
  /** A loan has a rate but the sheet has no close date, so the interest
   *  couldn't be worked out — say so rather than showing a zero that
   *  looks like a paid-off loan. */
  interest_not_accrued: boolean;
  costs_percent_of_price: number;
  net_proceeds: number;
  seller_owes: boolean;
}

export interface NetProceedsScenarioDto {
  id: string;
  parent_kind: "opportunity" | "transaction";
  parent_id: string;
  household_id: string;
  name: string;
  property_address: string | null;
  sale_price: number | null;
  /** LEGACY flat payoff, on every scenario written before loans existed. */
  loan_payoff: number | null;
  loans: NetProceedsLoanDto[];
  close_of_escrow_date: string | null;
  buyer_name: string | null;
  square_feet: number | null;
  lines: NetProceedsLineDto[];
  notes: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  breakdown: NetProceedsBreakdownDto | null;
}

export interface NetProceedsListResponse {
  parent_kind: "opportunity" | "transaction";
  parent_id: string;
  household_id: string;
  /** Seeds for a NEW scenario, read off the record. */
  defaults: {
    property_address: string | null;
    sale_price: number | null;
    square_feet: number | null;
    stewardship_commission_pct: number | null;
    buyer_agent_commission_pct: number | null;
  };
  scenarios: NetProceedsScenarioDto[];
}

export interface NetProceedsScenarioWrite {
  name: string;
  property_address: string | null;
  sale_price: number | null;
  /** LEGACY flat payoff. Sent only by a scenario that still has one and
   *  no loans; the server ignores it the moment `loans` is non-empty, so
   *  a payoff can never be subtracted twice. */
  loan_payoff: number | null;
  loans: NetProceedsLoanDto[];
  /** ISO `YYYY-MM-DD`. Drives the interest accruing until closing. */
  close_of_escrow_date: string | null;
  buyer_name: string | null;
  square_feet: number | null;
  lines: NetProceedsLineDto[];
  notes: string | null;
}

export interface NetProceedsDraftResponse {
  scenario_id: string;
  household_id: string;
  subject: string;
  /** Plain text — the composer takes HTML, so the caller converts. */
  body: string;
  suggested_to: string[];
  suggested_to_name: string | null;
}

export interface SubjectPropertyResponse {
  id: string;
  subject_property_line_1: string | null;
  subject_property_line_2: string | null;
  subject_property_city: string | null;
  subject_property_state: string | null;
  subject_property_zip: string | null;
  subject_property_square_feet: number | null;
  subject_property_display: string | null;
}

export interface RealtyMessageDraftsResponse {
  transaction_id: string;
  household_id: string;
  property_address: string;
  side: "sell" | "buy" | null;
  drafts: RealtyMessageDraft[];
}

/** The Processing record behind a Services row — enough to link to it
 *  and say where it stands, without a second fetch. */
export interface ServiceRecordProcessingLink {
  service_record_id: string;
  transaction_id: string;
  stage: string;
  /** null = still in flight. */
  outcome: string | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
}

/** Another household on the same purchase (record_co_households,
 *  2026-09-23) — a family buying together across two households. Realty
 *  and Mortgage records only; the record keeps its own lead household. */
export type CoHouseholdRecordKind = "opportunity" | "transaction" | "service_record";

export interface RecordCoHousehold {
  household_id: string;
  display_name: string;
  members: { contact_id: string; display_name: string }[];
  created_at: string;
}

/** A purchase this household is part of but does not own. */
export interface SharedPurchase {
  record_kind: CoHouseholdRecordKind;
  record_id: string;
  label: string;
  company_slug: string;
  company_display_name: string;
  lead_household_id: string;
  lead_household_name: string;
  status_label: string;
  closed: boolean;
}

function coHouseholdBase(kind: CoHouseholdRecordKind, recordId: string): string {
  const family =
    kind === "opportunity" ? "opportunities" : kind === "transaction" ? "transactions" : "service-records";
  return `/api/${family}/${encodeURIComponent(recordId)}/co-households`;
}

export interface ServiceRecordPerson {
  contact_id: string;
  is_primary: boolean;
  /** 'user' = a human chose; 'defaulted'/'backfill' = system-derived
   *  (multi-member defaults get confirmed through cleanup). */
  source: "user" | "backfill" | "defaulted" | "import";
  first_name: string | null;
  last_name: string | null;
  display_name: string;
  /** Plain contacts.email — not a SENSITIVE_FIELD column. Lets the
   *  record's people panel and the inline composer address this person
   *  directly. */
  email: string | null;
}

// Detail shape returned by GET /api/service-records/:id. Joins the
// SR row to the company, household, current advisor, and finalized-by
// user for one-shot rendering on the detail page.
export interface ServiceRecordDetail {
  id: string;
  household_id: string;
  household_display_name: string;
  company_id: string;
  company_slug: string;
  company_display_name: string;
  service_type: string;
  service_family: string;
  display_label: string | null;
  status: string;
  // Planning-case carrier (Medicare/Health/Life). Null for insurance
  // policies (those use the dedicated Carrier Data card) + estate plans.
  carrier_name: string | null;
  current_advisor_user_id: string | null;
  current_advisor_name: string | null;
  current_advisor_email: string | null;
  effective_date: string | null;
  /** Durable "start date not recoverable — stop asking" marker (0203).
   *  Read by the in-record data-needed prompt (B6, 2026-07-23). */
  effective_date_unknown_reason?: string | null;
  termination_date: string | null;
  /** The "deal closed on this day" date (migration 0195) — Realty close,
   *  loan funding, etc. Null for open pipelines + records with no close. */
  closed_date: string | null;
  /** Derived loan outcome (loan-family SRs only; null elsewhere) —
   *  'closed' (funded) vs 'cancelled' (fell through) vs 'open'.
   *  Computed from mortgage_loans dates, never stored. */
  loan_disposition: "closed" | "cancelled" | "open" | null;
  /** Funded/closing date when closed; cancelled/withdrawn when cancelled. */
  loan_disposition_date: string | null;
  external_ref: string | null;
  external_system: string | null;
  external_url: string | null;
  last_synced_at: string | null;
  product_metric: number | null;
  product_metric_unit: string | null;
  estimated_revenue_year_1: number | null;
  estimated_lifetime_revenue: number | null;
  lifetime_revenue_calc_method: string | null;
  finalized_at: string | null;
  finalized_by_user_id: string | null;
  finalized_by_user_name: string | null;
  created_at: string;
  updated_at: string;
  /** Planning-case product details (Medicare/Life/Health/etc.). Null
   *  when this SR has no planning_cases subtype row. */
  planning_case: PlanningCaseDetail | null;
  /** Mortgage-loan product details (loan-family SRs). Null when this SR
   *  has no mortgage_loans subtype row. Also surfaced top-level on
   *  ServiceRecordDetailResponse beside insurance_policy. */
  mortgage_loan: MortgageLoanDetail | null;
  /** Realty subtype row (listing = sell side, purchase = buy side). Null
   *  when this SR isn't Realty or has no subtype row. Baker, 2026-07-31:
   *  a closed Realty record had nowhere to update its sale price. */
  realty_record: RealtyRecordDetail | null;
  /** The people this record is FOR (0232). Primary first. Empty = a
   *  grandfathered pre-0232 record whose person is still unconfirmed. */
  people?: ServiceRecordPerson[];
}

/** Realty property + money details, as the record page renders and edits
 *  them. Money is in DOLLARS — BrokerMint reports dollars and the
 *  three-metric model (Grant, 2026-07-29) makes entry a copy-across.
 *  Mirrors RealtyRecordDetail in shared/db/realty-records.ts. */
export type FirmProfileField =
  | "legalName"
  | "addressLine1"
  | "addressLine2"
  | "city"
  | "state"
  | "postalCode"
  | "phone"
  | "fax"
  | "email"
  | "stateLicenseNumber"
  | "industryCode"
  | "industryCodeLabel"
  | "googleReviewUrl";

export interface FirmProfile {
  id: string;
  companySlug: string;
  legalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  stateLicenseNumber: string | null;
  industryCode: string | null;
  industryCodeLabel: string | null;
  googleReviewUrl: string | null;
  updatedAt: string | null;
}

export interface FirmProfileResponse {
  profile: FirmProfile;
  may_edit: boolean;
}

export interface ContractFormSummary {
  id: string;
  form_key: string;
  display_name: string;
  revision_label: string;
  category: string;
  applies_to_side: string;
  triggered_by_key: string | null;
  superseded_at: string | null;
  source_url: string | null;
  // 'aar' for a publisher form; 'stewardship' for one we authored
  // ourselves (2026-09-02). Groups the form into its own "Stewardship
  // documents" section on the packet card and this library page, apart
  // from its `category`-driven required/available logic.
  library: string;
  // The bridge to e-signature (2026-08-29): which blank document IS
  // this form, if one has been uploaded and linked yet.
  esign_template_id: string | null;
  esign_template_name: string | null;
  esign_template_archived_at: string | null;
  /** Set on a form made by duplicating another for one side of a deal
   *  (2026-10-26): the form_key it varies. */
  side_variant_of_form_key?: string | null;
  // Whether this form's blank has a saved field map yet (2026-09-01).
  // Resolved server-side by fingerprint; null means nobody has mapped it,
  // so the next person to send it starts from the suggestion pass.
  esign_layout_id?: string | null;
  esign_layout_name?: string | null;
  esign_layout_field_count?: number | null;
  /** 1 when this form's map was never measured against the real document
   *  — its blanks were reconstructed from a remembered layout because the
   *  PDF's text would not extract (2026-09-16). The Library says so on the
   *  row, because a box on a contract is a promise about where it lands
   *  and a map nobody checked must not pass as one that was. */
  esign_layout_positions_reconstructed?: number | null;
}

export interface ContractFormsAdminResponse {
  forms: ContractFormSummary[];
  availableTemplates: { id: string; name: string; documentType: string | null }[];
}

/** One row on the Industry Documents page for a company with no
 *  external catalog of its own (every company except Realty). The
 *  template row IS the catalog row: "in library" is always true for a
 *  row that appears at all — see esign-templates.ts's
 *  EsignTemplateWithMappingStatus doc comment. */
export interface IndustryDocumentRow {
  id: string;
  display_name: string;
  publisher: string | null;
  revision_label: string | null;
  category: string | null;
  applies_to_side: string | null;
  esign_template_id: string;
  esign_template_name: string;
  esign_template_archived_at: string | null;
  esign_layout_id: string | null;
  esign_layout_field_count: number | null;
}

export type IndustryDocumentsResponse =
  | {
      company: string;
      availableCompanies: string[];
      source: "realty_catalog";
      forms: ContractFormSummary[];
      availableTemplates: { id: string; name: string; documentType: string | null }[];
    }
  | {
      company: string;
      availableCompanies: string[];
      source: "template_catalog";
      documents: IndustryDocumentRow[];
    };

export interface ContractPacketEntry {
  form: ContractFormSummary;
  /** 'required' | 'unresolved' | 'available' */
  reason: string;
  because: string;
}

export interface ContractPacketResponse {
  realty_record_id: string;
  side: "listing" | "purchase";
  entries: ContractPacketEntry[];
  requiredCount: number;
  unresolvedCount: number;
  /** Required addenda no catalogued form covers — a catalog gap, shown
   *  rather than silently dropped. */
  missingFormKeys: string[];
  /** Contract facts (Assessor's #, county, legal description, year
   *  built, …) these forms ask for that nobody has entered yet — the
   *  same blanks that will stop a send at Prepare & Send once a field is
   *  bound to one. Surfaced here so an agent finds out before placing a
   *  field, not after, mid-appointment. Plain-English labels, in the
   *  order shared/realty/property-facts.ts declares them. */
  unansweredFactLabels: string[];
}

/** The configured commission split for one Realty record — a suggestion
 *  the calculator prefills, never a stored value. Mirrors
 *  RealtyRecordCommissionSuggestion in shared/db/realty-record-commission.ts. */
export interface RealtyCommissionSuggestion {
  advisor_user_id: string | null;
  advisor_name: string | null;
  /** Basis points: 5000 = 50%. */
  agent_split_bps: number | null;
  agent_split_label: string | null;
  match_reason: string | null;
  broker_review_cents: number;
  broker_review_name: string | null;
  broker_review_label: string | null;
  warnings: { code: string; message: string }[];
  trustworthy: boolean;
}

export interface RealtyRecordDetail {
  /** A commission typed as flat dollars (2026-09-22); while set, the
   *  matching _pct is worked out from it and the price. */
  listing_side_flat?: number | null;
  buyer_broker_flat?: number | null;
  id: string;
  service_record_id: string;
  side: "listing" | "purchase";
  sale_price: number | null;
  office_gross: number | null;
  agent_net: number | null;
  office_net: number | null;
  closed_at: string | null;
  /** The representation agreement: a Listing Agreement on the sell side,
   *  a Buyer-Broker Agreement on the buy side (Nate, 2026-08-18). */
  agreement_signed_at: string | null;
  agreement_expires_at: string | null;
  /** Contract acceptance — day zero for the whole key-dates chain. */
  under_contract_at: string | null;
  property_address_line_1: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  /** A listing's asking price; a purchase's agreed contract price. */
  list_price: number | null;
  /** Our commission on a listing, as a percent (1.98 = 1.98%). Null on a
   *  purchase — we are the buyer's agent there, so there is no such
   *  number, and a 0 would read as "we earned nothing". */
  listing_side_pct: number | null;
  /** The buyer's-agent commission percent: offered on a listing, earned
   *  on a purchase. */
  buyer_broker_pct: number | null;
  /** The home warranty's cost, in dollars — a concession component. */
  home_warranty: number | null;
  // ── The listing agreement's own terms (Grant, 2026-10-14). Listing
  // side only: null on every purchase. Optional so older fixtures stay
  // valid. See shared/db/realty-records.ts LISTING_ONLY_FIELDS.
  /** The asking price when the listing began; never moved by a price change. */
  original_list_price?: number | null;
  /** Listing Contract §3 "commencing on". The end is agreement_expires_at. */
  listing_period_starts_at?: string | null;
  /** Listing Contract §5: 1 = something is leased, 0 = nothing, null = not asked. */
  has_leased_items?: number | null;
  leased_items_detail?: string | null;
  /** Fixtures or personal property the seller is keeping. */
  items_not_conveying?: string | null;
  /** The title company's file number, on both sides of a deal. */
  escrow_number?: string | null;
  // ── Property facts the AAR contracts require (2026-08-28) ──
  // Null means "nobody has answered yet", never "no".
  apn: string | null;
  legal_description: string | null;
  county: string | null;
  subdivision: string | null;
  lot_size_sqft: number | null;
  year_built: number | null;
  sewer_type: string | null;
  water_source: string | null;
  hoa_status: string | null;
  hoa_name: string | null;
  solar_status: string | null;
  /** 1 = yes, 0 = no, null = nobody has checked. */
  in_unincorporated_area: number | null;
  /** Rentals (2026-09-22): the product type, whether this is a rental,
   *  and the lease terms (null on every sale). */
  service_type?: string | null;
  is_rental?: boolean;
  /** Dollars per month. */
  monthly_rent?: number | null;
  lease_start_date?: string | null;
  lease_end_date?: string | null;
  sub_type: string | null;
  transaction_status: string | null;
  brokermint_transaction_id: string | null;
  mls_number: string | null;
  sales_volume: number | null;
}

/** Planning-case product details — the fields Ryan thinks in for a
 *  Medicare/Life/Health case (2026-07-02 ask). has_subsidy / aca_income /
 *  subsidy_amount (2026-08-10) are the ACA subsidy fields — has_subsidy
 *  is 0/1/null (null = not yet asked), matching how SQLite stores the
 *  planning_cases.has_subsidy column. */
export interface PlanningCaseDetail {
  id: string;
  case_type: string;
  carrier_name: string | null;
  /** The vendors-backed carrier link (S5). Null on cases still on the
   *  old free-text-only carrier_name. */
  carrier_vendor_id: string | null;
  policy_number: string | null;
  product_name: string | null;
  plan_tier: string | null;
  life_type: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  annual_premium: number | null;
  monthly_premium: number | null;
  death_benefit: number | null;
  cash_value: number | null;
  has_subsidy: number | null;
  aca_income: number | null;
  subsidy_amount: number | null;
  // The four open-enrollment eligibility answers (S7, migration
  // 20260825172905_add_planning_case_eligibility_and_net_worth). 0/1/null,
  // where null means the question has never been asked — which is a
  // different fact from "asked, said no" and renders differently.
  //
  // NOTE the `eligibility_` prefix: contacts.tobacco_use is a DIFFERENT
  // question at a different grain ("does this person smoke", the
  // underwriting fact). This one is "did anyone in this household answer
  // yes on this year's enrollment form". Do not sync them.
  eligibility_tobacco_use: number | null;
  eligibility_employer_coverage: number | null;
  eligibility_will_file_taxes: number | null;
  eligibility_ahcccs_enrolled: number | null;
  /** When those four were last answered. Lets a screen show a year-old
   *  answer for what it is instead of presenting it as this year's fact. */
  eligibility_answered_at: string | null;
  /** Net-worth inputs for estate + life cases. Liabilities are a POSITIVE
   *  magnitude of what is owed. Net worth itself is never stored — compute
   *  it with netWorth() from shared/planning/net-worth.ts. */
  total_assets: number | null;
  total_liabilities: number | null;
  /** Short-Term Medical renewal decision (S16). Null on every other case
   *  type, and null here until Ryan/Andrea decide. */
  stm_renewal_decision: string | null;
  // S20 — the three line-of-business splits.
  disability_term: string | null;
  care_term: string | null;
  estate_scope: string | null;
  stm_no_follow_up_reason: string | null;
  stm_no_follow_up_reason_detail: string | null;
  /** Estate-plan execution facts (Ryan Delviken, 2026-08-28, on the Saylor
   *  trust: "there should be a place for me to enter the name of the
   *  trust, the date that it was signed, kind of all the compliance
   *  stuff"). Columns since migration 0009; never editable until now, so
   *  an estate case rendered the generic carrier/policy/premium row —
   *  insurance fields on a trust. */
  estate_plan_type: string | null;
  attorney_name: string | null;
  /** When the client actually signed. Distinct from effective_date, which
   *  the AC import stamped on all 157 estate cases. */
  signed_at: string | null;
  /** The ORIGINAL document's own signing date — Trust amendment cases
   *  only (Ryan Delviken, 2026-09-15). Null on every other Plan type. */
  original_signed_at: string | null;
  trust_document_url: string | null;
  will_document_url: string | null;
  /** Estate plans are flat-fee priced, never premium-priced. */
  flat_fee: number | null;
}

/** Mortgage-loan product details — the fields the record page's Loan
 *  details card renders + edits (Baker's 2026-07-13 bug: a Private Money
 *  loan's page showed no loan data and nothing to edit). Mirrors
 *  MortgageLoanDetail in shared/types/mortgage.ts. */
export interface MortgageLoanDetail {
  id: string;
  service_record_id: string;
  loan_type: "traditional" | "private_money";
  loan_product: string | null;
  loan_program_type: string | null;
  loan_program_type_note: string | null;
  lender_vendor_id: string | null;
  lender_name: string | null;
  loan_number: string | null;
  /** The LENDER's own loan number, distinct from ours. */
  lender_loan_number: string | null;
  origination_amount: number | null;
  loan_amount_cents: number | null;
  /** Loan amount at origination (CLR authoritative), distinct from the
   *  current/final loan_amount_cents. */
  initial_loan_amount_cents: number | null;
  purchase_price_cents: number | null;
  /** Appraised value, once the appraisal lands. Cents. */
  appraisal_value_cents: number | null;
  /** Financials (Joey PR 4) — cents; totals/down payment are computed. */
  cash_to_close_cents: number | null;
  monthly_pi_cents: number | null;
  monthly_taxes_cents: number | null;
  monthly_insurance_cents: number | null;
  monthly_mi_cents: number | null;
  monthly_hoa_cents: number | null;
  earnest_money_cents: number | null;
  seller_credit_cents: number | null;
  lender_credit_cents: number | null;
  interest_rate: number | null;
  interest_rate_bps: number | null;
  /** Where the rate came from; null = recorded, origin not stated. */
  interest_rate_source:
    | "closed_loan_report"
    | "activecampaign"
    | "market_estimate"
    | "attom"
    | "manual"
    | null;
  /** 1 = market average for the funding month, not the note rate. Show "est.". */
  interest_rate_is_estimate: 0 | 1;
  rate_type: "fixed" | "arm" | "variable" | "interest_only" | null;
  term_months: number | null;
  ltv_percent: number | null;
  /** The fee Stewardship earned on this loan — the field the pre-2018 PM
   *  revenue-gap cleanup item asks Baker to set. Cents. */
  total_amount_earned_cents: number | null;
  broker_fee_cents: number | null;
  voe_condo_other_fee_cents: number | null;
  client_savings_cents: number | null;
  application_date: string | null;
  clear_to_close_date: string | null;
  /** Wet-sign appointment date — distinct from closing_date (COE). */
  signing_date: string | null;
  closing_date: string | null;
  funded_date: string | null;
  /** Normally read off the Note, not the Closing Disclosure. */
  first_payment_date: string | null;
  property_address_line_1: string | null;
  property_address_line_2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  property_type: string | null;
  property_use: string | null;
  borrower_tier: string | null;
  // NMLS Mortgage Call Report classifications (migration 0249). Null =
  // not yet answered.
  lien_status: string | null;
  hoepa_status: number | null;
  qm_status: string | null;
  borrower_full_name_raw: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  loan_officer_name: string | null;
  /** The Processing owner's name, offered only when loan_officer_name is
   *  blank — read-only, never stored. */
  loan_officer_fallback_name: string | null;
  gross_monthly_income: number | null;
  dti_percent: number | null;
  financials_as_of: string | null;
}

/**
 * The loan's numbers as the Processing screen reads them, resolved from
 * whichever home holds them — the funded loan, or the application record
 * that carries them while the file is still in Processing.
 *
 * `source` is what a card uses to say plainly whether these are
 * application figures or a funded loan's.
 */
export interface MortgageLoanFacts {
  source: "loan" | "application";
  mortgage_loan_id: string | null;
  loan_type: "traditional" | "private_money" | null;
  loan_amount_cents: number | null;
  purchase_price_cents: number | null;
  appraisal_value_cents: number | null;
  /** Computed from loan ÷ price when both are known; never stored. */
  ltv_percent: number | null;
  loan_program_type: string | null;
  loan_program_type_note: string | null;
  interest_rate: number | null;
  rate_type: "fixed" | "arm" | "variable" | "interest_only" | null;
  term_months: number | null;
  signing_date: string | null;
  closing_date: string | null;
  funded_date: string | null;
  /** Normally read off the Note, not the Closing Disclosure. */
  first_payment_date: string | null;
  lender_name: string | null;
  loan_number: string | null;
  lender_loan_number: string | null;
  cash_to_close_cents: number | null;
  monthly_pi_cents: number | null;
  monthly_taxes_cents: number | null;
  monthly_insurance_cents: number | null;
  monthly_mi_cents: number | null;
  monthly_hoa_cents: number | null;
  earnest_money_cents: number | null;
  seller_credit_cents: number | null;
  lender_credit_cents: number | null;
  // Subject property — entered on the application while the file is
  // still in Processing (20260902203452), carried to the loan at Funded.
  property_address_line_1: string | null;
  property_address_line_2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  property_use: "primary" | "second_home" | "investment" | null;
}

/** Fields settable through PATCH /api/transactions/:id/loan-facts. */
export type MortgageLoanFactField =
  | "loan_amount_cents"
  | "purchase_price_cents"
  | "appraisal_value_cents"
  | "loan_program_type"
  | "loan_program_type_note"
  | "interest_rate"
  | "rate_type"
  | "term_months"
  | "signing_date"
  | "first_payment_date"
  | "lender_name"
  | "loan_number"
  | "lender_loan_number"
  | "cash_to_close_cents"
  | "monthly_pi_cents"
  | "monthly_taxes_cents"
  | "monthly_insurance_cents"
  | "monthly_mi_cents"
  | "monthly_hoa_cents"
  | "earnest_money_cents"
  | "seller_credit_cents"
  | "lender_credit_cents"
  | "property_address_line_1"
  | "property_address_line_2"
  | "property_city"
  | "property_state"
  | "property_zip"
  | "property_use";

/**
 * Insurance-specific carrier data shown on the policy detail page's
 * Carrier Data card (PR-D3). Null on non-insurance service records.
 */
export interface InsurancePolicyDetail {
  id: string;
  service_record_id: string;
  /** How many policy rows the SERVICE RECORD carries. 1 is healthy; more
   *  means this card shows one policy while the record holds others
   *  (Mylah, 2026-08-05). */
  policy_row_count?: number;
  carrier_name: string | null;
  policy_number: string | null;
  policy_type: string;
  effective_date: string | null;
  expiration_date: string | null;
  annual_premium: number | null;
  payment_method: string | null;
  payment_frequency: string | null;
  /** Vehicles on an auto policy (free text or a JSON array — render via
   *  formatPolicyVehicles). Mylah, 2026-08-03. */
  vehicles: string | null;
  /** Coverage elections — tri-state: null means nobody has recorded an
   *  answer, which is NOT the same as false. Drives which conditional
   *  items an Insurance checklist spawns. */
  um_uim_rejected: number | null;
  has_scheduled_jewelry: number | null;
  has_good_student_discount: number | null;
  original_inception_date: string | null;
  issued_date: string | null;
  term_length_months: number | null;
  term_number: number | null;
  tenure_years: number | null;
  term_premium: number | null;
  is_new_business: boolean | null;
  underwriting_company_name: string | null;
  prior_carrier_name: string | null;
  prior_policy_number: string | null;
  source_of_business: string | null;
  // Carrier-side deep URL for this policy (Carrier Data "Open in [carrier] →").
  carrier_portal_url: string | null;
  producer_code: string | null;
  producer_name: string | null;
  agent_code: string | null;
  telematics_program_name: string | null;
  telematics_eligible: boolean | null;
  telematics_enrolled: boolean | null;
  mailing_address_line1: string | null;
  mailing_address_line2: string | null;
  mailing_city: string | null;
  mailing_state: string | null;
  mailing_zip: string | null;
  source: string | null;
  provenance_flag: string | null;
  source_row_hash: string | null;
  ac_deal_id: string | null;
  /** When these cached carrier figures were last refreshed — what the
   *  Carrier Data card's "as of" line reads. Null means no sync was ever
   *  recorded, and the card says so rather than implying the numbers are
   *  current (Mylah, 2026-08-25). */
  last_synced_at: string | null;
  last_sync_source: string | null;
  /** Derived, not stored: MAX(matched_at) over the IVANS transactions
   *  matched to this policy — "when did the carrier feed last mention
   *  this?". `last_synced_at` above has never been written by any path.
   *  See shared/db/insurance-policy-detail.ts for why this is computed at
   *  read time rather than kept in a column. */
  carrier_feed_last_seen_at: string | null;
  carrier_extract_raw: Record<string, unknown> | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  renewal_premium_offer: number | null;
  /** Snapshot of what "current premium" read the instant before this
   *  policy's most recent renewal rolled forward — see
   *  shared/db/ivans-policy-sync.ts's PRE-RENEWAL PREMIUM SNAPSHOT note.
   *  Shown in place of the (now-null) offer while the term above is
   *  still upcoming (Joy, 2026-09-14). */
  pre_renewal_premium: number | null;
  /** The insured PROPERTY's address, read from the carrier extract.
   *  Null when the carrier gave no address, or the policy type has no
   *  notion of one (auto, umbrella…). */
  insured_property_address: string | null;
  /** The insured's address as the carrier's live feed last sent it, when
   *  the spreadsheet extract and mailing columns gave none. */
  feed_insured_address: { text: string; as_of: string | null } | null;
}

// Service Records PR 9a — universal employee modules now carry real
// data on every SR detail. NBA + missing items computed server-side
// by computeNextBestAction / computeMissingItems in shared/db/
// service-record-nba.ts.
export interface ServiceRecordNextBestAction {
  kind:
    | "pending_cancellation"
    | "renewal_due_soon"
    | "renewal_due"
    | "renewal_overdue"
    | "schedule_annual_review"
    | "advisor_unassigned"
    | "review_record";
  headline: string;
  detail?: string;
  severity: "high" | "medium" | "low";
}

export interface ServiceRecordMissingItem {
  slug: string;
  label: string;
  resolution?: string;
  severity: "hard" | "soft";
}

/** The Processing record delivering a service record (Nate, 2026-08-18).
 *  Its expected close date and Year-1 revenue are the LIVE figures — the
 *  service record's own copies are derived, and only this one can be
 *  edited. Null when the record has no Processing record at all. */
export interface ServiceRecordProcessingLink {
  service_record_id: string;
  transaction_id: string;
  stage: string;
  /** null = still in flight; otherwise how it ended. */
  outcome: string | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
  estimated_revenue_year_1: number | null;
}

export interface ServiceRecordDetailResponse {
  service_record: ServiceRecordDetail;
  rhythms: RhythmInstance[];
  processing_link: ServiceRecordProcessingLink | null;
  insurance_policy: InsurancePolicyDetail | null;
  /** Carrier-reported claims on this policy (empty for non-insurance
   *  records and claim-free policies). */
  insurance_claims?: InsuranceClaimDto[];
  /** Coverage / vehicle / driver / mortgagee detail from the carrier's
   *  feed (2026-08-20 audit). null for non-insurance records; all-empty
   *  until the detail sync has reached this policy. */
  policy_detail?: PolicyDetailDto | null;
  /** Mortgage subtype row for loan-family SRs; null elsewhere. */
  mortgage_loan: MortgageLoanDetail | null;
  /** What the county record says beside this loan (win-back). Only sent to
   *  users whose company scope includes Mortgage; null when there is
   *  nothing to say. */
  county_line?: CountyLine | null;
  next_best_action: ServiceRecordNextBestAction;
  missing_items: ServiceRecordMissingItem[];
  /** Each driver the carrier rated, paired with the household member it
   *  is (shared/insurance/policy-drivers.ts). Null when the carrier has
   *  sent no drivers. Never carries a license number, only its last four. */
  policy_drivers?: PolicyDriverRow[] | null;
  /** The household's own address, for a policy whose carrier feed sent none. */
  household_address?: { kind: "physical" | "mailing"; text: string } | null;
  /** Where the person this record is for lives, on every record page
   *  (Andrea, 2026-10-15): their own contact address, else the
   *  household's. `source` says which, so the page never passes one off
   *  as the other. Null when neither is on file. */
  record_address?: {
    source: "person" | "household_physical" | "household_mailing";
    person_name: string | null;
    text: string;
    /** The household's county for that address; null when none is on file
     *  or the address is a person's own (no county is stored for those). */
    county: string | null;
    /** Set only when `source` is "person": the household's county (physical,
     *  else mailing), for a page that wants to show it labeled as the
     *  household's beside the person's own address. */
    household_county: string | null;
  } | null;
}

/** Mirrors shared/insurance/policy-drivers.ts PolicyDriverRow. */
export interface PolicyDriverRow {
  position: number;
  carrier_name: string;
  relation: string | null;
  match: "matched" | "ambiguous" | "no_match" | "unusable_name";
  contact: {
    contact_id: string;
    display_name: string;
    birthday: string | null;
    drivers_license_state: string | null;
    drivers_license_last_four: string | null;
    has_drivers_license_on_file: boolean;
  } | null;
}

/** Carrier-fed policy detail satellites — pure cached reference (the
 *  carrier portal stays authoritative; synced_at is the "as of"). */
export interface PolicyDetailDto {
  synced_at: string | null;
  coverages: Array<{
    vehicle_position: number | null;
    coverage_code: string;
    premium_cents: number | null;
    limit1: number | null;
    limit2: number | null;
    deductible: number | null;
    description: string | null;
  }>;
  vehicles: Array<{
    position: number;
    model_year: number | null;
    make: string | null;
    model: string | null;
    vin: string | null;
    garage_state: string | null;
  }>;
  drivers: Array<{
    position: number;
    first_name: string | null;
    middle_name: string | null;
    last_name: string | null;
    marital_status: string | null;
    relation_code: string | null;
  }>;
  interests: Array<{
    position: number;
    interest_code: string | null;
    rank: string | null;
    name: string | null;
    loan_number: string | null;
    city: string | null;
    state: string | null;
  }>;
}

// ── Migration cleanup progress (PR5) ──────────────────────────────────

export type MigrationCleanupMetricSlug =
  | "quarantined_households"
  | "spouse_pass_ambiguous"
  | "duplicate_candidate_clusters"
  | "households_one_contact"
  | "vendor_lookalike_contacts";

export interface MigrationCleanupMetric {
  metric_slug: MigrationCleanupMetricSlug;
  display_name: string;
  baseline_count: number | null;
  current_count: number;
  cleaned_count: number;
  baseline_recorded_at: string | null;
}

export interface MigrationCleanupProgressResponse {
  total_baseline: number;
  total_current: number;
  total_cleaned: number;
  percent_complete: number;
  by_metric: MigrationCleanupMetric[];
  pace_per_day: number | null;
  eta_days_remaining: number | null;
  computed_at: string;
}

export interface CompanyRelationship {
  id: string;
  company_id: string;
  company_slug: string;
  company_display_name: string;
  relationship_state: string;
  fit_assessment: string;
  // 'advisor_departed' when fit is recorded as un-gradeable (serving
  // advisor left). null/absent = normal. Migration 0172.
  fit_unknowable_reason?: string | null;
  fit_assessed_at?: string | null;
  current_advisor_user_id: string | null;
  /** Advisor display name. Wave B (April 2026) — joined from
   *  users.name. Null when no advisor assigned. */
  current_advisor_name?: string | null;
  /** Advisor email. Null when no advisor assigned. */
  current_advisor_email?: string | null;
  // Joy PR 9 — coverage wishes (interim intake highlight).
  coverage_preferences?: string | null;
  coverage_preferences_updated_at?: string | null;
  service_records: ServiceRecord[];
}

// Joy PRs 10-12 — an AI-proposed follow-up awaiting a human verdict.
export interface FollowupSuggestion {
  id: string;
  household_id: string | null;
  household_display_name?: string | null;
  source_entity_type: "note" | "logged_email";
  source_entity_id: string;
  suggestion_title: string;
  suggested_due_date: string | null;
  status: "proposed" | "approved" | "dismissed";
  task_id: string | null;
  created_at: string;
}

// Nate 2026-07-09 — additional household addresses (beyond mailing +
// physical) and the read-only property addresses derived from deals.
export type HouseholdAddressKind = "property" | "vacation" | "investment" | "rental" | "other";

export interface HouseholdAddress {
  id: string;
  household_id: string;
  kind: HouseholdAddressKind;
  label: string | null;
  street_line_1: string | null;
  street_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  created_at: string;
  updated_at: string;
}

export interface LinkedProperty {
  source: "realty" | "mortgage";
  /** When the product took effect (funded / closed / inception) — the
   *  year is what tells four refinances of one house apart. */
  effective_date: string | null;
  service_record_id: string;
  company_slug: string;
  service_type: string | null;
  street_line_1: string | null;
  street_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export interface HouseholdAddressInput {
  kind?: HouseholdAddressKind;
  label?: string | null;
  street_line_1?: string | null;
  street_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

export interface Note {
  id: string;
  title: string | null;
  body: string;
  pinned: boolean;
  important: boolean;
  source: string;
  author_user_id: string | null;
  // Nate 2026-07-09 — author display name (joined server-side) so notes
  // show WHO wrote them, and updated_at so the UI can flag edited notes.
  author_name: string | null;
  created_at: string;
  updated_at: string;
}

// The six record kinds a household's notes can live on. Mirrors
// HOUSEHOLD_NOTE_KINDS in shared/db/household-notes.ts.
export type HouseholdNoteKind =
  | "household"
  | "contact"
  | "service_record"
  | "opportunity"
  | "transaction"
  | "rhythm_instance";

// The same six kinds expressed as API path segments (plural, kebab).
// The notes endpoints are `/api/{segment}/:id/notes` for EVERY kind —
// households and contacts included — so one CRUD family covers all six
// (S13). Before this, only the four workflow kinds went through
// listEntityNotes/addEntityNote and household/contact had their own
// hand-written pair, which is why the two vocabularies exist at all.
//
// Defined in shared/ because both sides speak it — the client builds the
// URL from it, the Worker mounts the routes under exactly these
// segments. Re-exported here so client code has one import for the notes
// API and its vocabulary.
export type { NoteEntitySegment as EntityNotesKind } from "@shared/types/notes";
export {
  NOTE_ENTITY_TYPE_BY_SEGMENT as NOTE_KIND_BY_SEGMENT,
  NOTE_SEGMENT_BY_ENTITY_TYPE as NOTE_SEGMENT_BY_KIND,
} from "@shared/types/notes";

// A note from somewhere else in the same household, carrying enough
// context to say where it lives and get you there. Read-only in every
// surface that renders it — the note's home is still the record it was
// written on.
export interface HouseholdNote extends Note {
  entity_type: HouseholdNoteKind;
  entity_id: string;
  /** Plain-English name of the record it lives on ("Medicare Policy",
   *  "Barbara Fournier"). Resolved server-side, never a raw slug. */
  origin_label: string;
  /** Optional extra context — a policy number, "won", a cycle date. */
  origin_detail: string | null;
}

export interface HouseholdNotesPage {
  household_id: string | null;
  notes: HouseholdNote[];
  /** COUNT(*) over the whole matching set — not the length of `notes`. */
  total: number;
  counts_by_kind: Record<HouseholdNoteKind, number>;
  limit: number;
  offset: number;
}

// The Financial-advisor lens's "Notes & meetings" feed (H3) — notes,
// meeting activities, and logged emails for a household, merged into one
// newest-first stream. See shared/db/household-notes-meetings-feed.ts.
export type NotesMeetingsFeedFilter = "all" | "note" | "meeting" | "email" | "from_ac";

export interface NotesMeetingsFeedEntry {
  id: string;
  kind: "note" | "meeting" | "email";
  occurred_at: string;
  author_name: string | null;
  from_ac: boolean;
  headline: string;
  body: string | null;
}

export interface HouseholdGiftEntry {
  id: string;
  occurred_at: string;
  author_name: string | null;
  description: string | null;
  /** Changed after it was first logged. */
  edited: boolean;
}

export interface HouseholdGiftsPage {
  entries: HouseholdGiftEntry[];
  total: number;
  limit: number;
}

export interface NotesMeetingsFeedPage {
  entries: NotesMeetingsFeedEntry[];
  /** COUNT(*) over the whole matching set — not the length of `entries`. */
  total: number;
  limit: number;
  offset: number;
}

// H2b — the household NOTES summary tile (Financial lens). A second AI
// job beside the relationship summary, written from this family's notes
// and meetings only. See shared/ai/notes-summary.ts.
export interface HouseholdNotesSummaryResponse {
  summary_text: string;
  model: string;
  generated_at: string;
  /** What the paragraph was written from, for the card's header. */
  note_count: number;
  meeting_count: number;
  email_count: number;
  refreshed: boolean;
  refresh_error: boolean;
}

// H2 — the household Action items card (Financial lens): what we owe
// them and what they owe us, pulled out of the notes and meetings.
export type HouseholdActionItemSide = "ours" | "theirs";
export type HouseholdActionItemStatus = "proposed" | "confirmed" | "done" | "dismissed";

export interface HouseholdActionItem {
  id: string;
  household_id: string;
  side: HouseholdActionItemSide;
  title: string;
  /** "from her Jun 1 email" — where the AI found it. */
  source_label: string | null;
  promised_on: string | null;
  status: HouseholdActionItemStatus;
  confirmed_at: string | null;
  confirmed_by_name: string | null;
  completed_at: string | null;
  completed_by_name: string | null;
  proposed_at: string;
  /** 'ai' = the extractor proposed it; 'manual' = a person typed it. */
  origin: "ai" | "manual";
  created_by_name: string | null;
  /** The AI's wording, kept once a person rewords an AI item. */
  original_title: string | null;
  edited_at: string | null;
  edited_by_name: string | null;
  /** Row version, sent back on edit so a teammate's change isn't clobbered. */
  updated_at: string;
  /** The To Do task made from this item, or null. The fields below are
   *  read live from that task, so a tick in To Do shows here. */
  task_id: string | null;
  task_assigned_to_user_id: string | null;
  task_assigned_to_name: string | null;
  task_due_date: string | null;
  task_sync_status: "local_only" | "pending_push" | "synced" | "sync_error" | null;
}

/** What a person may type on the card: add sends all of side + title,
 *  edit sends only what changed. */
export interface HouseholdActionItemInput {
  side?: HouseholdActionItemSide;
  title?: string;
  /** YYYY-MM-DD, or null to clear the date. */
  promised_on?: string | null;
}

export interface HouseholdActionItemsResponse {
  items: HouseholdActionItem[];
  /** True when this request paid for a fresh look at the notes. */
  scanned: boolean;
  /** True when AI is off or out of budget, so an empty list does not
   *  get read as "nothing to follow up on". */
  ai_unavailable: boolean;
}

export type HouseholdActionItemDecision = "confirm" | "complete" | "reopen" | "dismiss";

export interface Activity {
  id: string;
  occurred_at: string;
  activity_type: string;
  direction: "inbound" | "outbound" | "neutral";
  entity_type: string;
  entity_id: string;
  contact_id: string | null;
  user_id: string | null;
  summary: string | null;
  body: string | null;
  source: string;
  duration_minutes: number | null;
  external_ref: string | null;
  email_subject: string | null;
  email_from_address: string | null;
  email_to_addresses: string | null;
  email_conversation_id: string | null;
  email_bounce_state: string | null;
}

// Part 3: Financial Summary card payload (mirrors shared/db/dashboard-jake
// FinancialSummary). null when the household has no Financial relationship.
export interface FinancialSummary {
  aum_cents: number;
  /** Accounts with a value that aren't custodian-confirmed yet —
   *  excluded from aum_cents; the card explains them. */
  uncounted_accounts_count: number;
  est_annual_revenue_cents: number;
  client_tier: "A" | "B" | "C" | "D";
  life_stage: "accumulator" | "pre_retiree" | "retiree" | "unknown";
  last_contact_at: string | null;
  cross_company: { slug: string; state: string }[];
  members: {
    name: string;
    email: string | null;
    phone: string | null;
    role: string | null;
    /** Whole years from birthday; null when birthday is missing. */
    age: number | null;
  }[];
}

// Ongoing Care record's and the household page's Accounts card (mirrors
// shared/db/financial-household-accounts HouseholdFinancialAccountRow).
// One row per portfolio/retirement-plan account on the household's
// active Financial service records — custodian-confirmed AND
// not-yet-confirmed rows alike. Never sum value_cents for AUM; the
// canonical total is FinancialSummary.aum_cents.
export interface FinancialAccountRow {
  id: string;
  account_table: "portfolios" | "retirement_plans";
  label: string;
  owner_label: string | null;
  custodian_name: string | null;
  external_account_id: string | null;
  value_cents: number | null;
  as_of: string | null;
  is_custodian_confirmed: boolean;
  custodian_portal_url: string | null;
}

// Lending Summary card payload (mirrors shared/db/lending-summary
// LendingSummary). Household-level mortgage rollup for Mike Zins / Private
// Money. null when the household has no mortgage loans. Amounts are dollars.
export interface LendingSummary {
  total_borrowed: number;
  total_paid_off: number;
  current_outstanding: number;
  loan_count: number;
  active_loan_count: number;
  private_money_count: number;
  /** "Open in …" servicer links for open loans that have one. */
  servicer_links?: Array<{ label: string; url: string }>;
}

// Service Records PR 4 — Household 360 payload from
// GET /api/households/:id/portfolio. Mirrors shared/db/household-portfolio
// HouseholdPortfolioResult.
export interface HouseholdPortfolioSummary {
  open_opportunities: number;
  active_processing: number;
  active_care_plans: number;
  overdue_tasks: number;
  next_30_days: number;
}

export interface HouseholdPortfolioItem {
  service_record_id: string;
  service_type: string;
  display_label: string;
  status: string;
  company_id: string;
  company_slug: string;
  /** Linked insurance-policy identifiers — rendered as a secondary line
   *  in the services table so look-alike policies are identifiable
   *  (Mylah, 2026-07-20). Null on non-insurance / no-policy-row SRs. */
  policy_type: string | null;
  policy_number: string | null;
  carrier_name: string | null;
  /** How many policy rows the record carries. The three fields above
   *  describe only one of them, so >1 means the table says how many it
   *  isn't showing (Mylah, 2026-08-05). */
  policy_row_count: number;
  /** Medicare plan type off the linked Planning case ('pdp', 'medigap_g'/
   *  'n'/'f', 'ma') — same idea as policy_type above, for Planning's
   *  Medicare Cases (Ryan Delviken, 2026-08-31). Null otherwise. */
  plan_tier: string | null;
  active_workflow_bucket: "opp" | "proc" | "care" | null;
  active_workflow_label: string | null;
  /** Independent lifecycle-strip flags (the strip shows every stage a
   *  record has touched, not just the single active bucket). */
  has_open_opportunity: boolean;
  has_open_processing: boolean;
  has_active_care: boolean;
  current_advisor_user_id: string | null;
  /** Advisor display name + initials, resolved server-side from
   *  current_advisor_user_id (the services table renders these). */
  current_advisor_name?: string | null;
  current_advisor_initials?: string | null;
  effective_date: string | null;
  termination_date: string | null;
  /** Derived loan outcome (loan-family SRs only) — lets the services
   *  table show "Past · Closed" vs "Past · Cancelled". */
  loan_disposition?: "closed" | "cancelled" | "open" | null;
  loan_disposition_date?: string | null;
  /** Whose record this is (migration 0232). Empty on records the
   *  attach-people sweep hasn't reached yet. */
  people: ServiceRecordPerson[];
}

export interface HouseholdPortfolioResponse {
  items: HouseholdPortfolioItem[];
  summary: HouseholdPortfolioSummary;
}

// H4 "Client moved assets out" — the confirmation screen's preview and
// the result the Undo toast carries. Mirrors
// shared/db/financial-assets-transfer.ts.
//
// `accounts` and `care_plans` are the WHOLE un-capped set for one
// household, so their lengths are the real totals — there is
// deliberately no separate count field to disagree with them.
export interface FinancialAssetsTransferAccount {
  service_record_id: string;
  display_label: string | null;
  service_type: string;
  status: string;
  termination_date: string | null;
}

export interface FinancialAssetsTransferCarePlan {
  rhythm_instance_id: string;
  plan_name: string;
  cycle_anchor_date: string;
  cycle_end_date: string;
}

export interface FinancialAssetsTransferPreview {
  household_id: string;
  household_display_name: string;
  accounts: FinancialAssetsTransferAccount[];
  aum_cents: number;
  care_plans: FinancialAssetsTransferCarePlan[];
  relationship_state: string | null;
  existing_transfer: {
    destination: string;
    transfer_date: string;
    bulk_action_id: string;
  } | null;
}

export interface FinancialAssetsTransferResult {
  ok: true;
  bulk_action_id: string;
  household_id: string;
  accounts_closed: number;
  care_plans_ended: number;
  aum_at_transfer_cents: number;
  transfer_date: string;
  destination: string;
  relationship_state: string | null;
  partial: boolean;
}

// Service Records PR 13 — Contact 360 payload from
// GET /api/contacts/:id/portfolio. Mirrors shared/db/contact-portfolio
// ContactPortfolioResult — same shape as HouseholdPortfolioResponse
// with an extra `role_badge` per item indicating the contact's role
// on each SR ("Primary Insured", "Co-borrower", "Beneficiary", etc.).
export interface ContactRoleBadge {
  label: string;
  detail?: string;
}

export interface ContactPortfolioItem extends HouseholdPortfolioItem {
  role_badge: ContactRoleBadge | null;
  /** False only when real attached-people data (migration 0232)
   *  positively says this record belongs to a DIFFERENT household
   *  member. True (including the pre-0232 default) when it's this
   *  contact's own record or the record has no attached-people data
   *  yet. Ryan Delviken, 2026-08-05 (Carnes household) — see
   *  shared/db/contact-portfolio.ts for the full rule. */
  for_this_contact: boolean;
  /** Populated only when for_this_contact is false. */
  belongs_to_names: string[] | null;
}

export interface ContactPortfolioResponse {
  items: ContactPortfolioItem[];
  summary: HouseholdPortfolioSummary;
}

// Service Records PR 17 — Workload Planner monthly capacity.
export type CapacityBucket = "light" | "normal" | "heavy" | "overloaded";

export interface CapacityCell {
  month: string;
  open_opportunities: number;
  open_transactions: number;
  active_rhythms: number;
  scheduled_touchpoints: number;
  total: number;
  bucket: CapacityBucket;
  /** Service Records PR 25 — estimated effort hours summed
   *  across this cell's workload. */
  total_hours: number;
}

export interface CapacityAdvisorRow {
  user_id: string;
  name: string | null;
  email: string;
  employee_role_slug: string | null;
  cells: CapacityCell[];
}

export interface WorkloadCapacityResponse {
  months: string[];
  advisors: CapacityAdvisorRow[];
  generated_at: string;
}

// Service Records PR 18 — Workload Planner weekly + annual views.
export interface WeeklyCell {
  week: string;
  week_start: string;
  open_opportunities: number;
  open_transactions: number;
  active_rhythms: number;
  scheduled_touchpoints: number;
  total: number;
  bucket: CapacityBucket;
  /** Service Records PR 25 — see CapacityCell.total_hours. */
  total_hours: number;
}

export interface WeeklyAdvisorRow {
  user_id: string;
  name: string | null;
  email: string;
  employee_role_slug: string | null;
  cells: WeeklyCell[];
}

export interface WorkloadWeeklyResponse {
  weeks: { key: string; start: string; label: string }[];
  advisors: WeeklyAdvisorRow[];
  generated_at: string;
}

export interface AnnualStripCell {
  month: string;
  open_opportunities: number;
  open_transactions: number;
  active_rhythms: number;
  scheduled_touchpoints: number;
  total: number;
  /** Service Records PR 25 — total estimated hours across the
   *  firm for this month. */
  total_hours: number;
}

export interface WorkloadAnnualResponse {
  months: AnnualStripCell[];
  generated_at: string;
}

// Service Records PR 19 — Communication templates library.
export type TemplateScope = "all" | "mortgage" | "insurance" | "planning" | "realty" | "financial";

export const TEMPLATE_SCOPES: readonly TemplateScope[] = [
  "all",
  "mortgage",
  "insurance",
  "planning",
  "realty",
  "financial",
];

export interface CommunicationTemplate {
  id: string;
  slug: string;
  display_name: string;
  description: string | null;
  subject_template: string;
  body_template: string;
  scope: TemplateScope;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Set when Grant approved this wording for sending; changing its words clears it. */
  approved_at?: string | null;
}

export interface CreateCommunicationTemplateBody {
  slug: string;
  display_name: string;
  description?: string | null;
  subject_template: string;
  body_template: string;
  scope?: TemplateScope;
}

export interface UpdateCommunicationTemplateBody {
  display_name?: string;
  description?: string | null;
  subject_template?: string;
  body_template?: string;
  scope?: TemplateScope;
}

// Service Records PR 9b — Donna's "Coming in today" badge data.
export interface ComingInTodayResponse {
  coming_in_today: boolean;
  next_appointment_at: string | null;
  next_appointment_label: string | null;
  next_appointment_activity_id: string | null;
}

// Service Records PR 12 — Automation Workspace UI types.
// Service Records PR 21 — expanded lifecycle states.
export type AutomationState =
  | "draft"
  | "test"
  | "awaiting_review"
  | "active"
  | "paused"
  | "error"
  | "rolled_back"
  | "archived";

// "What runs by itself" — GET /api/system-jobs response shapes.
// Mirrors shared/system-jobs/registry.ts's SystemJobEntry plus the
// per-request health merge computed in workers/api/src/routes/system-jobs.ts.
export interface SystemJobHealth {
  status: "healthy" | "attention" | "hung" | "never_run" | "external" | "skipped";
  lastRunAt: string | null;
  lastSucceededAt: string | null;
  consecutiveFailures: number;
  /** Program audit M12: the job's own sentence when its last check-in did no work. */
  skippedReason: string | null;
  // Admin-only (system_admin / compliance) — absent for everyone else.
  lastError?: string | null;
  lastDurationMs?: number | null;
  lastFinishedAt?: string | null;
  lastFailedAt?: string | null;
}

export interface SystemJobRow {
  key: string;
  label: string;
  description: string;
  cadenceLabel: string;
  cronExpressions: string[];
  worker: "api" | "backup-daily" | "backup-weekly-offsite" | "monitor";
  category: "engine" | "infrastructure" | "scoring" | "data-sync" | "business-action";
  companies: "all" | string[] | null;
  ownerLabel: string;
  actsOnClients: boolean;
  killSwitchEnvVar: string | null;
  automationCandidate: boolean;
  health: SystemJobHealth;
}

// ── PM Guidelines (Private Money) ─────────────────────────────────────────
// The numbers a Private Money loan is judged by. One flat list; the screen's
// arrangement of them lives in shared/private-money/guideline-display.ts.
export interface PmGuidelineRec {
  id: string;
  group_key: string;
  guideline_key: string;
  label: string;
  help_text: string | null;
  value_kind: string;
  value_numeric: number | null;
  value_text: string | null;
  is_locked: number;
  locked_reason: string | null;
  source_note: string | null;
  sort_order: number;
  version_number: number;
  updated_by_user_id: string | null;
  updated_at: string;
}

/** The lender profiles shown on the PM Guidelines screen. The server's own
 *  shape, re-exported rather than restated — see the close-out cockpit's
 *  types for the same reasoning. */
export type PmLenderProfileRec = PmLenderProfileRow;

export interface PmGuidelineHistoryRec {
  id: string;
  guideline_key: string;
  previous_numeric: number | null;
  previous_text: string | null;
  new_numeric: number | null;
  new_text: string | null;
  reason: string | null;
  changed_by_user_id: string | null;
  changed_at: string;
  change_kind: string;
}

// ── PM underwriting reports (Private Money) ───────────────────────────────
// One row per RUN, never overwritten. `rules_json` / `thresholds_json` are
// JSON strings as stored; the page parses them once.
export interface PmUnderwritingRuleRec {
  rule: string;
  label: string;
  flag: "pass" | "inspect" | "fail";
  measured: number | null;
  detail: string;
  thresholds: Array<{ guideline_key: string; value: number }>;
  /** Keys this rule needed and PM Guidelines has no number for. */
  missing_guidelines?: string[];
}

export type PmCountySource = "county_record" | "address_state" | "person" | "unknown";
export type PmAsIsSource = "county_record_estimate" | "application" | "person";
export type PmCompletedDealsSource = "application_band" | "person";

export interface PmUnderwritingReportRec {
  id: string;
  subject_type: string;
  subject_id: string;
  version_number: number;
  loan_amount_cents: number | null;
  purchase_price_cents: number | null;
  arv_cents: number | null;
  as_is_value_cents: number | null;
  construction_budget_cents: number | null;
  /** The final loan entered; null = the run used loan + budget. */
  final_loan_amount_cents: number | null;
  fico: number | null;
  completed_deals: number | null;
  in_maricopa_county: number;
  property_type: string | null;
  is_income_property: number;
  monthly_rent_cents: number | null;
  noi_annual_cents: number | null;
  initial_ltv_percent: number | null;
  final_ltv_percent: number | null;
  initial_ltc_percent: number | null;
  final_ltc_percent: number | null;
  debt_yield_percent: number | null;
  verdict: "pass" | "inspect" | "fail";
  rules_json: string;
  thresholds_json: string;
  property_address: string | null;
  property_json: string | null;
  property_pulled_at: string | null;
  lien_verified_at: string | null;
  lien_verified_by_user_id: string | null;
  lien_verification_note: string | null;
  /** NULL on a report written before 2026-09-23. 'unknown' = the
   *  in_maricopa_county 0/1 is not a fact. */
  county_source: PmCountySource | null;
  as_is_source: PmAsIsSource | null;
  completed_deals_source: PmCompletedDealsSource | null;
  initial_ltv_basis: "purchase_price" | "as_is_value" | null;
  fund_as_of_date: string | null;
  override_flag: "pass" | "inspect" | "fail" | null;
  override_reason: string | null;
  override_by_user_id: string | null;
  override_at: string | null;
  review_notes: string | null;
  comps_verified: number;
  comps_verified_by_user_id: string | null;
  comps_verified_at: string | null;
  review_version: number;
  created_by_user_id: string | null;
  created_at: string;
}

/** Who and what the loan is — the report page's identity line. */
export interface PmUnderwritingSubjectRec {
  type: "opportunity" | "transaction";
  id: string;
  is_private_money: boolean;
  borrower_name: string | null;
  entity_name: string | null;
  address: string | null;
  lender_label: string | null;
}

/** "Your review" as a person sets it (PATCH …/reports/:id/review). */
export interface PmUnderwritingReviewInput {
  override_flag: "pass" | "inspect" | "fail" | null;
  override_reason: string | null;
  review_notes: string | null;
  comps_verified: boolean;
}

// What ATTOM said about the collateral. Context for a person to read —
// never a lien check (see shared/attom/underwriting-facts.ts).
export interface PmPropertyFactsRec {
  structure: {
    apn: string | null;
    subdivision: string | null;
    property_type: string | null;
    zoning: string | null;
    lot_size_sqft: number | null;
    living_sqft: number | null;
    stories: number | null;
    year_built: number | null;
    bedrooms: number | null;
    bathrooms: number | null;
    has_pool: boolean | null;
    construction: string | null;
    owner_of_record: string | null;
  };
  deeds: Array<{
    date: string;
    buyer: string | null;
    seller: string | null;
    amount_cents: number | null;
    deed_type: string | null;
    document_number: string | null;
  }>;
  recorded_loans: Array<{
    date: string;
    lender: string | null;
    amount_cents: number | null;
    loan_type: string | null;
    document_number: string | null;
  }>;
  valuation: {
    value_cents: number | null;
    high_cents: number | null;
    low_cents: number | null;
    band_percent: number | null;
  };
  /** Where the county record puts it. Absent on a pre-2026-09-23 report. */
  location?: {
    fips: string | null;
    county: string | null;
    state: string | null;
    geo_id_n4?: string | null;
  };
  /** Comparable sales near the subject. Absent before 2026-10-05. */
  comps?: {
    comps: Array<{
      address: string | null;
      distance_miles: number | null;
      sale_date: string | null;
      sale_amount_cents: number | null;
      living_sqft: number | null;
      price_per_sqft_cents: number | null;
      year_built: number | null;
      stories: number | null;
      bedrooms: number | null;
      bathrooms: number | null;
    }>;
    median_price_per_sqft_cents: number | null;
    pulled_at: string;
  };
  /** RentCast's automated value, added by its on-demand button. */
  rentcast_value?: {
    value_cents: number;
    low_cents: number | null;
    high_cents: number | null;
    pulled_at: string;
  };
  /** The subdivision's own numbers, added by the on-demand comparison. */
  subdivision?: {
    subdivision: string;
    properties_read: number;
    improved_lots: number;
    single_story: number;
    multiple_story: number;
    with_pool?: number;
    avg_living_sqft: number | null;
    avg_lot_sqft: number | null;
    year_built_min: number | null;
    year_built_max: number | null;
    truncated: boolean;
    pulled_at: string;
  };
  pulled_at: string;
  notes: string[];
}

// ── The KlinkLoans fund mirror ────────────────────────────────────────────
export interface PmFundUploadRec {
  id: string;
  as_of_date: string;
  original_filename: string | null;
  loan_count: number;
  total_upb_cents: number;
  skipped_json: string | null;
  /** JSON array of the export columns this upload was imported WITHOUT
   *  (the person chose "import anyway"). NULL when nothing was missing. */
  missing_columns_json: string | null;
  /** KlinkLoans' Total Current Assets from its balance sheet, when entered.
   *  NULL → every percentage is of the summed UPB. */
  fund_size_cents: number | null;
  fund_size_as_of: string | null;
  superseded_at: string | null;
  uploaded_by_user_id: string | null;
  created_at: string;
}

/** One exposure reading. `flag` carries "unknown" as well as the three
 *  verdicts — a category whose Limit nobody set must not read green. */
export interface PmFundReadingRec {
  key: string;
  label: string;
  now_percent: number | null;
  target_percent: number | null;
  limit_percent: number | null;
  flag: "pass" | "inspect" | "fail" | "unknown";
  detail: string;
  amount_cents: number;
  /** When present, what the BAR is drawn against instead of the whole fund
   *  — a per-lender reading is drawn against its own cap. The printed
   *  number is always the share of the fund either way. */
  scale_percent?: number;
}

/** One loan in the uploaded book. The category flags are what every
 *  exposure percentage is summed from, so they are on the row rather than
 *  re-derived from the address or the notes. */
export interface PmFundLoanRec {
  id: string;
  loan_number: string | null;
  address: string | null;
  city: string | null;
  zip_code: string | null;
  upb_cents: number;
  unfunded_draws_cents: number | null;
  interest_rate: number | null;
  transaction_type: string | null;
  origination_date: string | null;
  maturity_date: string | null;
  borrower_code: string | null;
  colender_code: string | null;
  is_construction_loan: boolean;
  is_new_construction: boolean;
  is_subdivision: boolean;
  is_multifamily: boolean;
  is_commercial: boolean;
  is_land: boolean;
  is_group_home: boolean;
  is_outside_maricopa: boolean;
  is_profit_split_100: boolean;
}

export interface PmFundExposureRec {
  total_upb_cents: number;
  loan_count: number;
  /** What every percentage was divided by, and which figure it was. */
  denominator: { basis: "fund_size" | "summed_upb"; cents: number };
  categories: PmFundReadingRec[];
  concentration: PmFundReadingRec[];
  cabi_aobi: { total: PmFundReadingRec; perLender: PmFundReadingRec[] };
}

/** A Private Money loan's protected details, masked
 *  (GET /api/pm-underwriting/:type/:id/protected). */
export type PmLoanSensitiveFieldName =
  | "ein"
  | "wire_account_name"
  | "wire_routing_number"
  | "wire_account_number";

export interface PmProtectedDetailsRec {
  loan_request_id: string | null;
  entity: { id: string; name: string; ein_on_file: boolean; ein_last_four: string | null } | null;
  wire: {
    account_name_on_file: boolean;
    routing_number_on_file: boolean;
    account_number_on_file: boolean;
    account_number_last_four: string | null;
    received_at: string | null;
  };
}

/** The borrower's application, as the loan's own screen reads it
 *  (GET /api/pm-underwriting/:type/:id/application). Shapes come from the
 *  shared builders, so the card, the full view and the lender's package
 *  cannot drift apart. */
export interface PmLoanApplicationRec {
  /** The team member who filled it in on the borrower's behalf, when one did. */
  filled_in_by?: string | null;
  received_at: string;
  inquiry_kind: string | null;
  form_submission_id: string | null;
  /** Read through the Opportunity this Processing record came from. */
  via_conversion: boolean;
  card: RequestCard;
  sections: PackageSection[];
  inputs: PmApplicationInputs;
  address: { line1: string | null; city: string | null; state: string | null; zip: string | null };
  /** Where inputs.fico came from: a recorded tri-merge pull, the band the
   *  borrower picked (judged at its low end), or nothing. */
  fico_source: "pulled" | "self_reported" | null;
  /** The date on the credit report the pulled score came from. */
  fico_pulled_on?: string | null;
  /** The rate and fee that apply (shared/private-money/loan-terms.ts). */
  terms: PmLoanTermsRec;
  /** The lender the borrower picked, when the OS knows it. */
  lender_label: string | null;
}

/** "loan" = set on this loan; "lender_default" = the lender's standing
 *  value, applied because the loan's own field is blank. */
export type PmTermSource = "loan" | "lender_default";

export interface PmLoanTermsRec {
  rate_percent: number | null;
  rate_source: PmTermSource | null;
  origination_fee_cents: number | null;
  fee_source: PmTermSource | null;
  lender_default_rate_percent: number | null;
  lender_default_fee_cents: number | null;
  monthly_payment_cents: number | null;
  /** "Other Loan Terms Specified" on the Disclosure to Private Investor. */
  other_loan_terms: string | null;
  /** The loan's own balloon when set, else the lender's standing term. */
  balloon_months: number | null;
  balloon_source: PmTermSource | null;
  lender_default_balloon_months: number | null;
  /** Prepayment and extension terms, as words: the loan's own when set,
   *  else the lender's standing terms. Printed on the loan application
   *  the borrower signs. */
  prepayment_terms: string | null;
  prepayment_source: PmTermSource | null;
  lender_default_prepayment_terms: string | null;
  extension_terms: string | null;
  extension_source: PmTermSource | null;
  lender_default_extension_terms: string | null;
  /** Facts Mike can correct on the loan; they start as the borrower's. */
  loan_amount_cents: number | null;
  funding_source: string | null;
  insurance_agent_name: string | null;
  insurance_agent_email: string | null;
  /** The structure and conditions the terms email states (Mike, 2026-10-21). */
  detail: PmLoanTermsDetailRec;
  /** Sent back as expected_version so a stale save is refused. */
  version: string | null;
}

export interface PmFeeLine {
  recipient: string;
  percent: number | null;
  amount_cents: number | null;
}

/** shared/private-money/loan-terms-detail.ts, LoanTermsDetail. */
export interface PmLoanTermsDetailRec {
  initial_loan_amount_cents: number | null;
  construction_loan_cents: number | null;
  construction_holdback_cents: number | null;
  interest_reserve_cents: number | null;
  ltv_cushion_cents: number | null;
  ltv_draw_terms: string | null;
  min_interest_days_initial: number | null;
  min_interest_days_draws: number | null;
  max_ltv_percent: number | null;
  min_draw_cents: number | null;
  draw_fee_cents: number | null;
  valuation_type: string | null;
  valuation_scope: string | null;
  valuation_notes: string | null;
  underwriting_conditions: string | null;
  origination_fee_lines: PmFeeLine[];
  loan_type: string | null;
  purchase_price_cents: number | null;
  construction_budget_cents: number | null;
}

/** POST /api/pm-underwriting/:type/:id/terms-email. `blockers` are terms the
 *  email can't go without; `warnings` are figures that don't reconcile. */
export interface PmTermsEmailDraft {
  ok: boolean;
  subject: string;
  body_html: string;
  blockers: string[];
  warnings: string[];
  notes: string[];
  to: string[];
  household_id: string | null;
  contact_id: string | null;
  record_label: string | null;
  form: { slug: string; title: string };
}

/** The lenders a Private Money loan can be placed with (the values the
 *  server accepts for funding_source; pm-loan-package.ts). */
export const PM_FUNDING_SOURCE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "first_tier", label: "First Tier Capital" },
  { value: "pinnacle", label: "Pinnacle Phx" },
  { value: "optima", label: "Optima Giving Foundation" },
  { value: "klinkloans", label: "KlinkLoans Fund" },
];

/** A save of the loan's terms. The three terms are always sent; each loan
 *  fact only when it changed. */
export type PmLoanTermsSave = {
  rate_percent: number | null;
  origination_fee_cents: number | null;
  other_loan_terms: string | null;
  loan_amount_cents?: number | null;
  funding_source?: string | null;
  balloon_months?: number | null;
  prepayment_terms?: string | null;
  extension_terms?: string | null;
  insurance_agent_name?: string | null;
  insurance_agent_email?: string | null;
  expected_version?: string | null;
  // The terms-email fields, each only when changed. Keys are the row's
  // columns (shared/private-money/loan-terms-detail.ts); the fee breakdown
  // is a JSON string.
  [detailColumn: string]: string | number | null | undefined;
};

/** Where a Private Money loan's Disclosure to Private Investor stands
 *  (GET /api/pm-underwriting/transaction/:id/disclosure). */
export type PmDisclosureRec = {
  parties?: {
    broker: { name: string; email: string } | null;
    /** is_person: the lender's vendor record names who signs for it. */
    investor: { name: string; email: string; is_person?: boolean } | null;
  };
  /** The last time someone moved the loan to Closed - Compliance before
   *  this was signed, and why. Absent once signed. */
  overridden?: { at: string; by_name: string | null; reason: string } | null;
  /** A copy uploaded by hand that nothing yet says is signed (signed at
   *  title or on paper). "This is the signed copy" marks it. */
  uploaded_copy?: { document_id: string; filename: string; web_url: string | null; uploaded_at: string } | null;
} & (
  | { state: "not_private_money" }
  | { state: "no_loan_request" }
  | { state: "missing"; missing: string[] }
  | { state: "ready" }
  | { state: "prepared"; document_id: string; filename: string; web_url: string | null; prepared_at: string }
  | {
      state: "sent";
      envelope_id: string;
      sent_at: string | null;
      signers: Array<{ name: string; email: string; signed_at: string | null; signer_order: number }>;
      /** The application was edited after this copy went out. */
      loan_changed_since_sent: boolean;
    }
  | {
      state: "ended";
      envelope_id: string;
      ended: "declined" | "expired" | "cancelled";
      sent_at: string | null;
      declined_by: string | null;
      declined_at: string | null;
      decline_reason: string | null;
      missing: string[];
    }
  | { state: "signed"; document_id: string; filename: string; web_url: string | null; signed_at: string | null }
);

export interface PmCreditPullRec {
  id: string;
  contact_id: string;
  score: number;
  pulled_on: string;
  source_note: string | null;
  entered_by_user_id: string | null;
  created_at: string;
}

/** INTERNAL_ONLY — the borrower's approved experience summary. */
export interface PmExperienceSummaryRec {
  id: string;
  contact_id: string;
  summary_text: string;
  drafted_by_stew: number;
  approved_by_user_id: string;
  created_at: string;
}

export interface PmBorrowerExperienceRec {
  borrower: { contact_id: string; name: string | null } | null;
  summary: PmExperienceSummaryRec | null;
  experience_band_label: string | null;
  can_draft: boolean;
}

/** INTERNAL_ONLY — the borrower's pulled credit, Mortgage-scoped. */
export interface PmBorrowerCreditRec {
  borrower: { contact_id: string; name: string | null } | null;
  latest: PmCreditPullRec | null;
  history: PmCreditPullRec[];
  freshness: {
    ageDays: number;
    ageAtCloseDays: number | null;
    staleAtClose: boolean;
    staleNow: boolean;
  } | null;
  /** The close date the freshness is measured against: Mike's expected
   *  close, else the borrower's wished-for date (close_date_is_borrowers). */
  close_date?: string | null;
  close_date_is_borrowers?: boolean;
  self_reported_fico_band: string | null;
  experience_band: string | null;
}

/** What the application says, in the engine's units. */
export interface PmApplicationInputs {
  loan_amount_cents: number | null;
  purchase_price_cents: number | null;
  arv_cents: number | null;
  as_is_value_cents: number | null;
  construction_budget_cents: number | null;
  fico: number | null;
  completed_deals: number | null;
  /** True when completed_deals is the low end of the band they ticked. */
  completed_deals_from_band?: boolean;
  /** Always null from the application — the report reads the county. */
  in_maricopa_county: boolean | null;
  property_type: string | null;
  is_income_property: boolean;
  monthly_rent_cents: number | null;
  noi_annual_cents: number | null;
}

export interface PmUnderwritingRunInput {
  loan_amount_cents: number | null;
  purchase_price_cents: number | null;
  arv_cents: number | null;
  as_is_value_cents: number | null;
  construction_budget_cents: number | null;
  /** The projected loan once the budget is drawn. null = loan + budget. */
  final_loan_amount_cents: number | null;
  fico: number | null;
  completed_deals: number | null;
  /** Yes / no / not known. null lets the server read the county record. */
  in_maricopa_county: boolean | null;
  property_type: string | null;
  is_income_property: boolean;
  monthly_rent_cents: number | null;
  noi_annual_cents: number | null;
  /** The lookup this server made for this loan. The property itself is
   *  never sent — the server reads what IT fetched. Omitted → the newest
   *  lookup for the loan is carried forward. */
  property_lookup_id?: string | null;
  /** Where the as-is value / project count came from, as the form knows
   *  it. Checked by the server against the numbers themselves. */
  as_is_source?: PmAsIsSource | null;
  completed_deals_source?: PmCompletedDealsSource | null;
}

export interface SystemJobsResponse {
  viewerScope: {
    companies: string[] | null;
    widened: boolean;
    isAdmin: boolean;
  };
  count: number;
  jobs: SystemJobRow[];
}

// Slow-query telemetry (speed program PR 7) — GET /api/admin/slow-queries.
// Mirrors shared/db/slow-query-log.ts.
export interface SlowQueryShapeSummary {
  sql_shape: string;
  occurrences: number;
  total_wall_ms: number;
  max_wall_ms: number;
  avg_wall_ms: number;
  max_rows_read: number | null;
  last_seen_at: string;
  kinds: string;
}

export interface SlowQueryReport {
  window_days: number;
  total_occurrences: number;
  total_shapes: number;
  shapes: SlowQueryShapeSummary[];
  shape_limit: number;
}

// Database health (Database Portability Program, Lane E) —
// ── Client sign-in, employee side (/mortgage/client-sign-in) ─────────
// Mirrors shared/db/mortgage-credit-reports.ts (Mortgage OS A6a).
export interface LoanCreditSummary {
  current: {
    id: string;
    pull_kind: "hard" | "soft" | "unknown";
    agency_name: string | null;
    reference: string | null;
    report_type: string | null;
    pulled_on: string | null;
    good_through: string | null;
    has_pdf: boolean;
    imported_at: string;
    imported_by_name: string | null;
    tradelines_imported: number;
    tradelines_skipped: number;
  } | null;
  scores: { loan_borrower_id: string | null; bureau: string | null; model_name: string | null; model_family: "classic_fico" | "vantagescore_4" | "other"; value: number | null; scored_on: string | null }[];
  representative: { family: string; per_borrower: Record<string, number | null>; loan: number | null };
  alerts: { id: string; loan_borrower_id: string | null; bureau: string | null; kind: string; label: string; text: string | null; status: "open" | "resolved"; owner_name: string | null; resolved_at: string | null; resolution_note: string | null }[];
  proposals: { liability_id: string; creditor_name: string | null; monthly_payment_cents: number | null; months_remaining: number | null; rule: string; label: string }[];
  history: { id: string; reference: string | null; pulled_on: string | null; imported_at: string; status: string }[];
  rules_used: { key: string; label: string; value: number; citation: string }[];
}
export interface LoanCreditImportResult {
  ok: true;
  report_id: string;
  added: number;
  updated: number;
  waiting_for_decision: number;
  removed: number;
  skipped_closed: number;
  skipped_no_balance: number;
  proposals: number;
  alerts: number;
  paired: number;
}

// Mirrors workers/api/src/routes/loan-file-bank-data.ts (Mortgage OS A5a).
export type LoanBankEndReason = "loan_funded" | "loan_denied" | "loan_withdrawn" | "borrower_request";
export interface LoanBankDataAsk {
  id: string;
  status: "asked" | "connected" | "withdrawn" | "ended";
  asked_at: string;
  asked_by_name: string | null;
  connected_at: string | null;
  closed_at: string | null;
  closed_reason: string | null;
}
export interface LoanBankAccount {
  institutionName: string | null;
  name: string | null;
  officialName: string | null;
  type: string | null;
  subtype: string | null;
  mask: string | null;
  currentBalance: number | null;
  availableBalance: number | null;
  averageBalance60Days: number | null;
  currency: string | null;
  daysAvailable: number | null;
  ownerCount: number;
}
export interface LoanBankDataBorrower {
  loan_borrower_id: string;
  name: string | null;
  role: "primary" | "co_borrower";
  login: "ready" | "none" | "switched_off";
  missing_detail: string | null;
  ask: LoanBankDataAsk | null;
  connection: {
    live: boolean;
    connected_at: string;
    ended_at: string | null;
    end_reason: string | null;
    attention_code: string | null;
    attention_at: string | null;
  } | null;
  reports: {
    id: string;
    ask_id: string;
    generated_at: string | null;
    saved_at: string;
    days_covered: number | null;
    gse_eligibility: string;
    lender_acceptance: string;
    accounts: LoanBankAccount[];
  }[];
}
export interface LoanBankDataView {
  enabled: boolean;
  borrowers: LoanBankDataBorrower[];
}

// Mirrors workers/api/src/routes/client-access.ts (Mortgage OS A2a).
export interface ClientAccessAccount {
  user_id: string;
  email: string;
  name: string | null;
  contact_id: string;
  household_id: string;
  household_name: string | null;
  invited_at: string;
  invited_by_name: string | null;
  last_invite_sent_at: string | null;
  first_signed_in_at: string | null;
  last_signed_in_at: string | null;
  disabled_at: string | null;
  disabled_reason: string | null;
  version_number: number;
  has_passkey: boolean;
}

export interface ClientAccessList {
  company: "mortgage";
  sign_in_url: string | null;
  total: number;
  cap: number;
  accounts: ClientAccessAccount[];
}

export interface ClientInviteResponse {
  user_id: string;
  email: string;
  invitation: "sent" | "not_sent";
  sign_in_url: string | null;
}

// ── Plaid Limited Production test (/admin/plaid-live-test) ──────────
// Mirrors workers/api/src/routes/admin/plaid-live-test.ts. No token other
// than a Link token ever reaches the browser, and that only to open Link.
export type PlaidLiveTestLock = "switch_off" | "not_system_admin" | "impersonating" | "passkey_stale";

export interface PlaidLiveTestChecks {
  switchOn: boolean;
  systemAdmin: boolean;
  notImpersonating: boolean;
  passkeyFresh: boolean;
  passkeyRefusal: string | null;
  passkeyAgeSeconds: number | null;
}

export interface PlaidLiveTestConnection {
  id: string;
  provider: string;
  railKind: string;
  providerConnectionRef: string;
  consentVersion: string;
  consentAcceptedAt: string;
  connectedAt: string;
  endedAt: string | null;
  endReason: string | null;
  live: boolean;
  /** Set from Plaid's signed notices; null when nothing is wrong. */
  attentionCode: string | null;
  attentionDetail: string | null;
  attentionAt: string | null;
  /** Saved Home Lending Reports on this connection, newest first. */
  reports: PlaidLiveTestReport[];
}

/** One account in a saved report: no transactions, no owner names. */
export interface PlaidReportAccount {
  institutionName: string | null;
  name: string | null;
  officialName: string | null;
  type: string | null;
  subtype: string | null;
  mask: string | null;
  currentBalance: number | null;
  availableBalance: number | null;
  averageBalance60Days: number | null;
  currency: string | null;
  daysAvailable: number | null;
  ownerCount: number;
}

export interface PlaidLiveTestReport {
  id: string;
  connectionId: string;
  kind: string;
  providerReportId: string | null;
  generatedAt: string | null;
  daysCovered: number | null;
  accounts: PlaidReportAccount[];
  pdfBytes: number | null;
  gseEligibility: string;
  lenderAcceptance: string;
  savedAt: string;
}

/** What Plaid Check needs before a report. Sent to Plaid, never stored. */
export interface PlaidLiveTestIdentity {
  given_name: string;
  family_name: string;
  date_of_birth: string;
  email: string;
  phone: string;
  street_1: string;
  street_2: string;
  city: string;
  region: string;
  postal_code: string;
}

export interface PlaidCallSummary {
  operation: string;
  environment: string;
  http_status: number | null;
  request_id: string | null;
  error_code: string | null;
  latency_ms: number;
}

export interface PlaidLiveTestStatus {
  checks: PlaidLiveTestChecks;
  lock: PlaidLiveTestLock | null;
  lock_message: string | null;
  start_lock: PlaidLiveTestLock | null;
  environment: "sandbox" | "production" | null;
  configured: boolean;
  redirect_uri: string | null;
  redirect_uri_problem: string | null;
  consent: { version: string; paragraphs: string[] };
  test_data_life_days: number;
  delete_phrase: string;
  products: string[];
  permissible_purpose: string;
  connections: PlaidLiveTestConnection[];
  open_attempt: { started_at: string } | null;
}

// GET /api/admin/database-health. Mirrors shared/db/database-health.ts.
export type DatabaseHealthStatus = "green" | "yellow" | "red" | "unknown";

export interface DatabaseWaitReading {
  window_days: number;
  fast_db_over_500ms: number;
  fast_db_over_1s: number;
  per_week_over_500ms: number;
  per_week_over_1s: number;
  baseline_window: string;
  baseline_per_week_over_500ms: number;
  baseline_per_week_over_1s: number;
  status: DatabaseHealthStatus;
}

export interface DatabaseRouteCost {
  route: string;
  observed_requests: number;
  avg_queries: number;
  max_queries: number;
  avg_rows_read: number;
  max_rows_read: number;
}

export interface DatabaseScreenCostReading {
  window_days: number;
  routes: DatabaseRouteCost[];
  total_routes: number;
  display_limit: number;
  truncated: boolean;
}

export interface DatabaseTableSize {
  table: string;
  bytes: number;
  rows: number;
}

export interface DatabaseStorageReading {
  date_key: string;
  total_bytes: number;
  total_rows: number;
  largest: DatabaseTableSize[];
  email_bytes: number;
  email_share: number;
}

export interface DatabaseStatisticsReading {
  last_succeeded_at: string | null;
  last_failed_at: string | null;
  status: DatabaseHealthStatus;
  days_since_success: number | null;
}

// Email Health (/admin/email-health, 2026-10-07): the DMARC verdict.
export type EmailHealthStatus =
  | "cannot_check"
  | "needs_a_look"
  | "possible_spoofing"
  | "no_reports"
  | "all_good";

// Staging outbox (2026-11-02): every email staging sent or held.
// workers/api/src/routes/admin/staging-outbox.ts. 404 not_staging elsewhere.
export type StagingOutboxState = "sending" | "delivered" | "held" | "refused" | "failed" | "draft";
export interface StagingOutboxActor {
  id: string;
  name: string | null;
  email: string | null;
  is_qa: boolean;
}
export interface StagingOutboxAttachment {
  name: string;
  contentType: string | null;
  bytes: number | null;
}
export interface StagingOutboxSummary {
  id: string;
  created_at: string;
  provider: "graph" | "resend" | "ses" | "boldsign";
  kind: "email" | "draft" | "calendar_invite" | "meeting_update" | "reply" | "other";
  state: StagingOutboxState;
  state_reason: string | null;
  from: string | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string | null;
  attachments: StagingOutboxAttachment[];
  triggered_by: StagingOutboxActor | null;
  /** A QA account caused it, even when no one was signed in (a pretend Outlook's background send). */
  triggered_by_qa: boolean;
  origin: "request" | "background";
  route: string | null;
}
export interface StagingOutboxPage {
  total: number;
  next: { created_at: string; id: string } | null;
  emails: StagingOutboxSummary[];
}
export interface StagingOutboxEmail extends StagingOutboxSummary {
  updated_at: string;
  html: string | null;
  text: string | null;
  body_truncated: boolean;
  provider_status: number | null;
}

export interface EmailHealthReport {
  mailbox: string;
  window_days: number;
  computed_at: string;
  verdict: {
    status: EmailHealthStatus;
    headline: string;
    problems: { status: Exclude<EmailHealthStatus, "all_good">; sentence: string }[];
  };
  latest_check: {
    checked_at: string;
    outcome: "ok" | "no_reader" | "no_access" | "not_found" | "error";
    detail: string | null;
    reader_name: string | null;
    messages_seen: number;
    reports_stored: number;
    reports_already_stored: number;
    unreadable_attachments: number;
    read_through: string | null;
  } | null;
  reports_in_window: number;
  messages_in_window: number;
  newest_report_end: string | null;
  reporters: { reporter_org: string; reports: number; messages: number }[];
  senders: {
    key: string;
    label: string;
    category: "ours" | "forwarder" | "unknown";
    messages: number;
    failed: number;
    reason: string;
  }[];
  unknown_servers: { server: string; messages: number; failed: number; reason: string }[];
  unknown_server_count: number;
  /** Unknown servers not named yet; the daily check looks them up. */
  unknown_unnamed_server_count?: number;
}

export interface EmailHealthCheckNowResult {
  outcome: "ok" | "no_reader" | "no_access" | "not_found" | "error";
  messages_seen: number;
  reports_stored: number;
  reports_already_stored: number;
  unreadable_attachments: number;
  truncated: boolean;
  status: EmailHealthStatus;
}

export interface DatabaseHealthReport {
  generated_at: string;
  wait: DatabaseWaitReading;
  screens: DatabaseScreenCostReading;
  storage: DatabaseStorageReading | null;
  statistics: DatabaseStatisticsReading;
}

export interface AutomationListRow {
  id: string;
  slug: string;
  display_name: string;
  description: string | null;
  trigger_type: string;
  // Automations v2 (PR7b) — JSON columns so the row can decompile to a
  // plain-English sentence client-side.
  trigger_config: string;
  conditions: string | null;
  actions: string;
  state: AutomationState;
  // Organization fields (E, 2026-07-03) — scope/company/owner drive the
  // list-page filters + the scope line on each row.
  scope: string | null;
  scope_company: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  backup_owner_user_id: string | null;
  created_by_user_id: string | null;
  failure_count: number;
  last_failure_at: string | null;
  paused_at: string | null;
  paused_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  last_run_at: string | null;
  last_run_status: string | null;
  // Redesigned home (FG) — 7-day run metrics for success% + volume/week.
  runs_7d: number;
  success_7d: number;
  // Lifetime run count — drives the "Ran N times" plain-words run history.
  runs_total: number;
  // Why the viewer can see this rule (2026-08-10, ownership visibility
  // widening) — "owner" and "operator" earn no badge (the unremarkable
  // default); the list shows a small badge for the other two so a rule
  // that isn't yours reads as intentional.
  visibility_reason: "operator" | "owner" | "backup_owner" | "company_admin" | null;
}

export interface AutomationDetailResponse {
  automation: {
    id: string;
    slug: string;
    display_name: string;
    description: string | null;
    trigger_type: string;
    trigger_config: string;
    conditions: string;
    actions: string;
    state: AutomationState;
    // Organization fields (E, 2026-07-03) + live-edit (2026-07-04): scope
    // drives the list-page filters + scope line, and the builder's edit
    // mode shows/honors it (immutable via PATCH in v1).
    scope: string | null;
    scope_company: string | null;
    owner_user_id: string | null;
    owner_name: string | null;
    backup_owner_user_id: string | null;
    backup_owner_name: string | null;
    created_by_user_id: string | null;
    failure_count: number;
    last_failure_at: string | null;
    paused_at: string | null;
    paused_by_user_id: string | null;
    /** Per-rule recurrence JSON (migration 0183). Null on legacy rows
     *  read as the every_n_days:20 default by parseRecurrence. */
    recurrence: string | null;
    /** Optional per-rule send time (migration 0186) — Arizona local "HH:MM"
     *  or null (no specific time; fires on the first business-hours tick). */
    send_time_local: string | null;
    created_at: string;
    updated_at: string;
  };
  /** True when the viewer may reassign this rule's owner (owner / creator
   *  / global operator). */
  can_reassign_owner?: boolean;
  /** True when the viewer may assign the rule to SOMEONE ELSE (global
   *  operator only); false = self-assign only. */
  can_assign_others?: boolean;
  /** True when the viewer may OPERATE this rule — activate it, walk the
   *  activation gates, roll back a version. Gates the Safe-activation panel
   *  and the version "Roll back" buttons (the endpoints enforce the same
   *  matrix, so a false value hides a control that would 403). */
  can_activate?: boolean;
  /** Why the viewer can see this rule — same badge input as the list row. */
  visibility_reason?: "operator" | "owner" | "backup_owner" | "company_admin" | null;
  runs: {
    id: string;
    triggered_at: string;
    triggered_by: string | null;
    entity_type: string | null;
    entity_id: string | null;
    status: string;
    error_message: string | null;
  }[];
  failures: {
    id: string;
    occurred_at: string;
    trigger_source: string;
    entity_type: string | null;
    entity_id: string | null;
    error_message: string;
    retry_count: number;
  }[];
  /** C3 — in-flight `wait` continuations frozen under an earlier version.
   *  They run their stored action list on resume regardless of edits;
   *  surfaced so frozen work is visible. Empty for rules with no pending
   *  waits. version_number is null for rows predating any snapshot. */
  pending_continuations: {
    id: string;
    created_at: string;
    run_after: string;
    version_number: number | null;
  }[];
}

// Automations v2 PR13 (v1.5) — global Ask Stew engage response.
export interface AutomationEngageProposal {
  automation_id: string;
  automation_name: string;
  // L4: only the three states the engage agent may propose + the panel
  // can render — not the full 8-state union.
  target_state: "active" | "paused" | "draft";
  current_state: AutomationState;
  reason: string | null;
}
export interface AutomationEngageResponse {
  answer: string;
  // Names of the read-only tools the agent invoked, in order.
  tool_trace: string[];
  // At most one state change for the user to confirm; null for a
  // question-only answer.
  proposed: AutomationEngageProposal | null;
}

// Service Records PR 15 — Automation builder wizard.

export type AutomationPreviewEntityType =
  | "opportunity"
  | "transaction"
  | "service_record"
  | "rhythm_instance"
  | "household"
  // Contact grain (2026-07-03, PR 6): birthday rules run per person.
  | "contact";

export interface AutomationPreviewBody {
  entity_type: AutomationPreviewEntityType;
  /** JSON DSL ConditionNode | null. Passing null matches everything,
   *  so the wizard surfaces "X recent records of this type" as the
   *  baseline before any conditions are added. */
  conditions: ConditionNode | null;
  /** Optional cap; defaults to 500, max 2000. */
  limit?: number;
  /** Optional — pass the compiled actions so the server also validates
   *  email content (template exists, subject present) with the count. */
  actions?: unknown;
  /** Optional compiled trigger kind — lets the server frame the headline
   *  count honestly (event rules are uncountable ahead of time). */
  trigger_type?: "event" | "record_condition" | "schedule";
  /** The saved rule being edited, when there is one. Needed by per-rule
   *  derived fields ("they already got this email"); without it the Test
   *  count would include households the rule is about to skip. */
  automation_id?: string;
  /** The builder's current "Who it affects" choice. Only "personal"
   *  changes the answer: it asks the server whether the builder owns any
   *  record a personal rule could fire on. */
  scope?: "personal" | "company" | "global";
}

export interface AutomationEmailActionProblem {
  action_index: number;
  code:
    | "email_missing_content"
    | "email_missing_subject"
    | "template_not_found"
    | "send_as_unknown_user";
  message: string;
}

export interface AutomationPreviewResponse {
  entity_type: AutomationPreviewEntityType;
  limit: number;
  total_evaluated: number;
  match_count: number;
  sample_matches: { id: string; label: string }[];
  /** True when the scan hit its cap — the real match count can be
   *  higher. Optional defensively: older workers may omit it. */
  truncated?: boolean;
  /** full_book = targeted fetch (birthday rules scan every record);
   *  recent_sample = the recent-N scan. Optional defensively — older
   *  workers don't send it; absent renders as the recent-sample copy. */
  scan_scope?: "full_book" | "recent_sample";
  /** Email-content problems that would make every fire skip (2026-07-03). */
  action_checks?: AutomationEmailActionProblem[];
  /** How to frame the headline count (2026-07-05). Optional defensively:
   *  an older worker omits it, and the client falls back to the
   *  same-day match-count framing. */
  trigger_kind?: "event" | "date_anchored" | "scan";
  /** For a date-anchored rule, the whole-year reachable audience — the
   *  distinct records that will EVENTUALLY get an action over one cycle.
   *  Null for scan / event rules (and older workers). */
  reachable_count?: number | null;
  /** Which anchor drives `reachable_count` (birthday / renewal / review),
   *  when trigger_kind is date_anchored. */
  reachable_field?:
    | "days_until_birthday"
    | "days_until_renewal"
    | "days_until_review"
    | "days_until_plan_restart"
    | null;
  /** Renewal rules only: how many of `reachable_count` got their renewal
   *  date by rolling a LAPSED expiration forward. A subset of
   *  `reachable_count`, never additional to it. Null elsewhere (and on
   *  older workers). */
  stale_anchor_count?: number | null;
  /** True when this is a personal rule and the builder owns no records of
   *  the kind it fires on, so every fire would be skipped. Optional
   *  defensively: an older worker omits it. */
  owner_owns_no_records?: boolean;
}

/** The impact snapshot the ActivationConfirmModal displayed, echoed to
 *  the server on activation so the acknowledgement is recorded
 *  server-side (safety patch 2026-07-03). */
export interface AutomationImpactAcknowledgement {
  match_count: number | null;
  match_count_capped?: boolean;
  sendable_count?: number | null;
  action_summary: string;
}

// Audience preview + per-person exclusions (2026-07-03, PR 5).
export type AutomationSendBlockReason =
  | "no_email"
  | "bad_address"
  | "do_not_email"
  | "deceased"
  | "archived"
  | "vendor_contact"
  | "suppressed";

export interface AutomationAudienceRow {
  entity_id: string;
  contact_id: string | null;
  household_id: string | null;
  label: string;
  email: string | null;
  excluded: boolean;
  /** Why the engine's send floor would hold this recipient's email —
   *  null when nothing blocks it (safety patch 2026-07-03). */
  send_block: AutomationSendBlockReason | null;
  /** Manually added to this rule — force-matched regardless of the
   *  rule's conditions. Optional defensively (older workers omit). */
  included?: boolean;
  /** "Added manually by Joy Smith on Jul 3, 2026" for included rows. */
  included_note?: string | null;
  /** This person's occurrences of an auto-send rule become drafts. */
  always_draft?: boolean;
}

export interface AutomationAudiencePreview {
  total: number;
  /** Matched recipients that would actually clear the engine's send
   *  floor today (not excluded, no send_block) — the honest companion
   *  to `total` (safety patch 2026-07-03). */
  sendable: number;
  /** True when the rows list hit the server's 2,000-row cap — `total`
   *  is then a floor, not the whole answer. */
  capped?: boolean;
  /** full_book = targeted fetch (birthday rules); recent_sample = the
   *  recent-N scan. The UI states which so the number never overclaims. */
  scan_scope: "full_book" | "recent_sample";
  rows_truncated?: boolean;
  excluded_count?: number;
  rows: AutomationAudienceRow[];
  duplicates: { email: string; labels: string[] }[];
  projection_30d: number | null;
  projection_annual: number | null;
}

export interface CreateAutomationBody {
  slug: string;
  display_name: string;
  description?: string | null;
  trigger_type: "event" | "schedule" | "record_condition";
  trigger_config?: unknown;
  // L6: type the compiler↔engine seam so a drift between compileRule's
  // output and the wire shape is a compile error, not a silent no-match.
  conditions?: ConditionNode | null;
  actions: CompiledAction[];
  owner_user_id?: string | null;
  backup_owner_user_id?: string | null;
  // Automations v2 (PR2/PR3) — rule scope drives the permission matrix.
  scope?: "personal" | "company" | "global";
  scope_company?: string | null;
  // Per-rule recurrence (migration 0183) — how often one contact goes
  // through the rule. Omitted → the every_n_days:20 default.
  recurrence?: Recurrence;
  // Optional per-rule send time (migration 0186) — Arizona local "HH:MM"
  // or null. Only meaningful on date-anchored triggers.
  send_time_local?: string | null;
}

export interface UpdateAutomationBody {
  display_name?: string;
  description?: string | null;
  trigger_type?: "event" | "schedule" | "record_condition";
  trigger_config?: unknown;
  conditions?: ConditionNode | null;
  actions?: CompiledAction[];
  owner_user_id?: string | null;
  backup_owner_user_id?: string | null;
  // Rule scope (2026-08-03) — mutable now, gated server-side by the
  // authoring matrix against the NEW scope. Sent as a pair.
  scope?: "personal" | "company" | "global";
  scope_company?: string | null;
  // Live-edit safety (2026-07-04): a definition change to a rule that has
  // already fired must echo the edit-impact counts the operator saw.
  impact_ack?: { added: number; removed: number; unchanged: number };
  // Per-rule recurrence (migration 0183).
  recurrence?: Recurrence;
  // Optional per-rule send time (migration 0186) — Arizona local "HH:MM"
  // or null (clears it).
  send_time_local?: string | null;
}

// Edit-impact preview (build item B) — the pre-save answer to "who does
// this edit add/remove, and what's already in flight?"
export interface EditImpactResponse {
  audience: {
    entity_type: AutomationPreviewEntityType;
    entity_type_changed: boolean;
    added: number;
    removed: number;
    unchanged: number;
    capped: boolean;
    added_samples: { id: string; label: string }[];
    removed_samples: { id: string; label: string }[];
  };
  in_flight: {
    queued_events: number;
    pending_waits: number;
    pending_wait_due_dates: string[];
    resend_window_recipients: number;
    resend_window_earliest_lapse: string | null;
    resend_window_days: number;
  };
  warnings: RuleWarning[];
}

// Service Records PR 21 — Conflict detection.
export type AutomationConflictSeverity = "identical" | "overlapping";

export interface AutomationConflict {
  conflicting_automation_id: string;
  conflicting_automation_slug: string;
  conflicting_automation_display_name: string;
  conflicting_automation_state: AutomationState;
  severity: AutomationConflictSeverity;
  reason: string;
}

export interface DetectAutomationConflictsBody {
  /** Optional — exclude this rule from the comparison (for the
   *  edit case where the rule shouldn't match itself). */
  id?: string;
  trigger_type: "event" | "schedule" | "record_condition";
  trigger_config?: unknown;
  conditions?: unknown | null;
}

// Service Records PR 22 — Impact preview + safe activation.
export type ActivationGate = "test_passed" | "impact_acknowledged" | "conflicts_resolved";

export const ACTIVATION_GATES: readonly ActivationGate[] = [
  "test_passed",
  "impact_acknowledged",
  "conflicts_resolved",
];

export interface ActivationAcknowledgement {
  id: string;
  automation_id: string;
  gate: ActivationGate;
  acknowledged_at: string;
  acknowledged_by_user_id: string | null;
  /** Audit fix #18 — display name joined from users. null when
   *  acknowledged_by_user_id is null or the join misses. */
  acknowledged_by_user_name: string | null;
  notes: string | null;
}

export interface ActivationGateStatusResponse {
  gates: Record<ActivationGate, ActivationAcknowledgement | null>;
  all_clear: boolean;
}

export interface ImpactActionBreakdown {
  action_type: string;
  count: number;
}

export interface ImpactPreviewResponse {
  entity_type: AutomationPreviewEntityType;
  total_evaluated: number;
  match_count: number;
  truncated: boolean;
  /** True when the count came from the exhaustive whole-book scan —
   *  the number is exact, not a recent-N sample (2026-07-22). */
  exhaustive?: boolean;
  /** True for EVENT-triggered rules, where `match_count` is how many
   *  records pass the rule's IF — not how many times it will act. The
   *  UI must say so; presenting it as an action forecast is what made
   *  an office-visit rule look like 412 pending tasks (2026-08-07). */
  audience_gated_by_event?: boolean;
  /** Present only for date-anchored rules (renewal / birthday / review /
   *  anniversary). An exact-day rule acts on each record on that record's
   *  own day, so `match_count` alone reads as a batch size when it is one
   *  day's slice of a year-long schedule (Grant, 2026-08-10). */
  schedule?: DateAnchoredSchedule | null;
  sample_matches: { id: string; label: string }[];
  action_breakdown: ImpactActionBreakdown[];
  total_action_firings: number;
  /** Why a zero (or low) count is what it is, for date-anchored rules:
   *  `no_date` records can NEVER fire until their date is filled in;
   *  `wrong_day` records have the date and just aren't at the mark
   *  today. Absent on rules with no date window / older workers. */
  zero_when?: { no_date: number; wrong_day: number } | null;
}

export type DateAnchorField =
  | "days_until_birthday"
  | "days_until_renewal"
  | "days_until_review"
  | "days_until_anniversary"
  | "days_until_plan_restart";

export interface UpcomingScheduleDay {
  /** Days from today. 0 = reaches the mark today. */
  in_days: number;
  count: number;
}

export interface DateAnchoredSchedule {
  anchor_field: DateAnchorField;
  mark_days: number;
  /** False for a rule saved before exact-day timing — it still acts
   *  across its whole window, so its match count really IS a batch and
   *  must not be described as a schedule. */
  exact_day: boolean;
  /** About how many records reach the mark over a full year. */
  reachable_per_year: number | null;
  upcoming: UpcomingScheduleDay[];
  upcoming_total: number;
  horizon_days: number;
}

// ─── Missed marks + catch-up ────────────────────────────────────────

export interface MissedMarkRow {
  entity_id: string;
  label: string;
  /** Days since this record passed the rule's exact day. 1 = yesterday. */
  days_past_mark: number;
  days_until_anchor: number;
  /** Set when the rule DID look at this record and declined to act
   *  (no email address, do_not_email, the volume breaker). "We never
   *  ran" and "we ran and refused" want different responses. */
  last_skip_reason: string | null;
}

export type MissedMarkStatus = "checked" | "not_date_anchored" | "window_rule";

export interface AutomationRunnerHealth {
  runner_enabled: boolean;
  globally_paused: boolean;
  global_pause_reason: string | null;
  last_succeeded_at: string | null;
  minutes_since_last_success: number | null;
  is_stalled: boolean;
  consecutive_failures: number;
  last_error: string | null;
}

export interface MissedMarksResponse {
  automation_id: string;
  status: MissedMarkStatus;
  anchor_field: DateAnchorField | null;
  mark_days: number | null;
  lookback_days: number;
  /** Counted across the whole walk — never measured off `rows`. */
  total: number;
  rows: MissedMarkRow[];
  sample_truncated: boolean;
  can_catch_up: boolean;
  rule_state: string;
  runner_health: AutomationRunnerHealth;
}

export interface CatchUpResponse {
  requested: number;
  fired: number;
  skipped: number;
  errored: number;
  refused_reason: string | null;
}

/** Mid-scan tick of the exhaustive impact test: keep calling
 *  getAutomationImpact with `cursor` until the response is the final
 *  ImpactPreviewResponse (no `scanning` flag). */
export interface ImpactScanProgressResponse {
  scanning: true;
  cursor: string;
  evaluated_so_far: number;
  matched_so_far: number;
}

export type ImpactScanResponse = ImpactPreviewResponse | ImpactScanProgressResponse;

export function isImpactScanProgress(resp: ImpactScanResponse): resp is ImpactScanProgressResponse {
  return "scanning" in resp && resp.scanning === true;
}

// Service Records PR 23 — Automation versioning.
export interface AutomationSnapshot {
  slug: string;
  display_name: string;
  description: string | null;
  trigger_type: string;
  trigger_config: unknown;
  conditions: unknown | null;
  actions: unknown[];
  owner_user_id: string | null;
  backup_owner_user_id: string | null;
  state: string;
}

export interface AutomationVersion {
  id: string;
  automation_id: string;
  version_number: number;
  snapshot: AutomationSnapshot;
  change_reason_code: string | null;
  change_reason_text: string | null;
  created_at: string;
  created_by_user_id: string | null;
  /** Audit fix #18 — display name joined from users. null when
   *  created_by_user_id is null or the join misses. */
  created_by_user_name: string | null;
}

// Service Records PR 24 — Kill switch + ownership.
export type KillSwitchScope = "all" | "company" | "team";

export interface KillSwitchRequest {
  scope: KillSwitchScope;
  scope_value?: string | null;
  reason: string;
}

export interface KillSwitchResult {
  kill_switch_id: string;
  paused_automation_ids: string[];
  invoked_at: string;
}

// Automations v2 page — global kill switch (a real on AND off).
export interface GlobalPauseState {
  is_paused: boolean;
  reason: string | null;
  updated_at: string | null;
  updated_by_user_id: string | null;
}

export type OwnershipStatus = "fresh" | "due_soon" | "stale" | "orphaned";

export interface OwnershipHealthRow {
  id: string;
  slug: string;
  display_name: string;
  state: string;
  owner_user_id: string | null;
  last_ownership_review_at: string | null;
  owner_is_active: number;
  ownership_status: OwnershipStatus;
}

export interface OwnershipSweepResult {
  paused_automation_ids: string[];
  stale_automation_ids: string[];
}

// Service Records PR 26 — Drag-to-rebalance.
export type WorkloadItemType = "opportunity" | "transaction" | "rhythm_instance";

export interface WorkloadItem {
  item_type: WorkloadItemType;
  id: string;
  label: string;
  household_display_name: string | null;
  company_slug: string | null;
  bucket_date: string | null;
  required_skills: string[];
}

export interface AdvisorCandidate {
  user_id: string;
  name: string | null;
  email: string;
  employee_role_slug: string | null;
  skill_tags: string[];
  current_open_count: number;
}

export interface RankedCandidate extends AdvisorCandidate {
  match_score: number;
  skills_match: boolean;
}

export interface ReassignResult {
  item_type: WorkloadItemType;
  item_id: string;
  previous_owner_user_id: string | null;
  new_owner_user_id: string;
}

export interface AffectingAutomationRow {
  id: string;
  slug: string;
  display_name: string;
  state: AutomationState;
  owner_user_id: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
}

// Forward-looking: active rules currently scheduled to act on a record
// (read-only, cross-creator). Carries the JSON columns so the UI can
// decompile to a plain-English sentence.
/** Per-person controls on one rule for a record's target contact
 *  (2026-07-03, per-contact controls program). */
export interface AutomationRuleControlState {
  excluded: boolean;
  included: boolean;
  skip_next_pending: boolean;
  always_draft: boolean;
}

export interface ScheduledAutomationRow {
  id: string;
  display_name: string;
  owner_user_id: string | null;
  created_by_user_id?: string | null;
  trigger_type: string;
  trigger_config: string;
  conditions: string | null;
  actions: string;
  /** Why the rule is set to act: conditions match, a manual inclusion
   *  ("Add this person to this rule"), or both. Optional defensively —
   *  an older worker omits the control fields. */
  matched_via?: "conditions" | "manual_inclusion" | "both";
  controls?: AutomationRuleControlState;
  /** Rule has any email action / a true auto-send action. */
  emails?: boolean;
  auto_sends?: boolean;
  /** Whether the CALLER may manage this rule's per-person controls
   *  (owner / creator / global operator). */
  can_manage?: boolean;
}

export interface ScheduledAutomationsResponse {
  automations: ScheduledAutomationRow[];
  /** Who the per-rule controls act on: the viewed contact, or the
   *  household's primary contact. */
  target_contact_id?: string | null;
  target_household_id?: string | null;
  /** Manageable scan rules this record's person could be manually
   *  added to ("Add this person to this rule"). */
  addable_rules?: { id: string; display_name: string; grain: "contact" | "household" }[];
}

// Ongoing Care → Automations, step 3: pipeline-level automation visibility.
export type PipelineBoard = "opportunity" | "transaction" | "ongoing_care";

export interface PipelineRuleSummary {
  id: string;
  display_name: string;
  /** The rule as one plain "When … , then …" sentence, or null (fall back
   *  to display_name). */
  sentence: string | null;
  /** 'active' | 'paused'. */
  state: string;
  /** The stage this rule targets, or null when it acts board-wide. */
  stage: string | null;
}

export interface PipelineAutomations {
  board: PipelineBoard;
  /** stage slug → rules acting on that column. */
  per_stage: Record<string, PipelineRuleSummary[]>;
  /** rules acting on the whole board (no stage). */
  board_wide: PipelineRuleSummary[];
}

export interface HouseholdDetail {
  household: Household;
  contacts: HouseholdContact[];
  company_relationships: CompanyRelationship[];
  unattached_service_records: ServiceRecord[];
  household_rhythms: RhythmInstance[];
  notes: Note[];
  /** Live note counts on the household's MEMBERS (Joy, 2026-08-03). The
   *  Notes tab shows only household-scoped notes; without this a note
   *  saved on a person was invisible here. Members with none are absent. */
  member_note_counts?: Array<{ contact_id: string; count: number }>;
  activities: Activity[];
  /** Wave C (April 2026): open opportunities + transactions for the
   *  household. Drives the household-page rail Active Opportunities +
   *  Active Transactions cards. */
  open_opportunities?: HouseholdOpportunityRow[];
  /** Closed (won/lost) opportunities, most recently closed first, capped
   *  server-side. Grant, 2026-08-08 — a record marked Lost used to
   *  disappear from every household surface. */
  closed_opportunities?: HouseholdClosedOpportunityRow[];
  /** TRUE total of closed opportunities (own COUNT, not this array's
   *  length) so a capped list can say so honestly. */
  closed_opportunity_total?: number;
  open_transactions?: HouseholdTransactionRow[];
  /** Wave C-2 (April 2026): open manual tasks linked to this household.
   *  Drives the "Tasks About This Household" rail card. */
  household_tasks?: RailManualTask[];
  /** Wave C-2 (April 2026): documents linked to this household
   *  (entity_type='household'). Drives the Documents footer tab. */
  household_documents?: RailDocument[];
  /** v1.x phase 11.13: most recent activity involving this household
   *  or any of its contacts. Rendered as "Last contact: 3 days ago
   *  via email" in the page header. null when there's no recorded
   *  activity yet. */
  last_contact?: LastContactSummary | null;
  /** Part 3: Financial Summary card payload. null when the household
   *  has no Financial relationship (card hidden). */
  financial_summary?: FinancialSummary | null;
  /** Financial-lens Accounts card (mockup v4): per-account rows sharing
   *  financial_summary.aum_cents's account universe (shared/db/
   *  financial-household-accounts.ts). Empty array, never absent, when
   *  the household has no Financial accounts on file. */
  financial_accounts?: FinancialAccountRow[];
  /** Lending Summary card payload — household mortgage rollup (Mike Zins /
   *  Private Money). null when the household has no mortgage loans. */
  lending_summary?: LendingSummary | null;
  /** Cross-household connections (migration 0160): members' links to
   *  people in OTHER households (extended-family / connection graph). */
  connected_people?: ConnectedPerson[];
  /** Brandon, 2026-08-19: other households at this street address and the
   *  insurance they carry. Drives the "two auto policies at this address"
   *  heads-up, whose point is the underwriting rule that every driver at
   *  an address be listed or excluded on each policy. */
  address_neighbors?: AddressNeighbors;
  /** Joy, 2026-08-19: members who are the named owner of a live service
   *  record here — her test for a young adult being ready for their own
   *  household ("full control of their policy"). A hint, never a rule. */
  record_owner_contact_ids?: string[];
  /** Carrier-reported claims across the household's policies (Brandon,
   *  2026-08-19). Empty for the vast majority of households. */
  insurance_claims?: InsuranceClaimDto[];
}

/** One other household at the same street address. */
export interface AddressNeighborHousehold {
  household_id: string;
  display_name: string;
  member_label: string;
  policies: Array<{
    policy_type: string;
    label: string;
    carrier_name: string | null;
  }>;
  /** Policy types BOTH households carry — "two AUTO policies". */
  overlapping_labels: string[];
  /** True when a relationship edge joins the two, i.e. the OS knows
   *  these are one family (usually because a split linked them). */
  family_linked: boolean;
}

export interface AddressNeighbors {
  address_label: string | null;
  neighbors: AddressNeighborHousehold[];
  /** Someone at this address (this household included) has a live
   *  insurance policy. False means the heads-up has nothing to say. */
  insurance_involved: boolean;
}

/** What undoing a household split would do — including what it can't. */
export interface SplitOrigin {
  new_household: {
    id: string;
    display_name: string;
    split_at: string;
    split_from_household_id: string;
    split_by_user_id: string | null;
  };
  source_household: { id: string; display_name: string };
  /** Table → rows that would move back. */
  will_move_back: Record<string, number>;
  /** How many people the split recorded moving, for the honesty check. */
  split_moved_contacts: number | null;
  /** Movers whose original role can't be restored (no snapshot). */
  roles_not_restorable: string[];
  /** Rows created on this household AFTER the split — these did not come
   *  from the source, and dissolving the household moves them anyway. */
  created_since_split: Array<{ kind: string; label: string; id: string }>;
  bulk_action_id: string | null;
}

/** A record (open or closed) that names a person being split out of a household. */
export interface SplitMovableRecord {
  kind: "service_record" | "opportunity" | "transaction";
  id: string;
  label: string;
  company_slug: string | null;
  status: string | null;
  /** They are the ONLY person named — safe to move with them. */
  is_only_person: boolean;
  /** Everyone else named, for the joint-record warning. */
  other_person_names: string[];
}

/** One carrier-reported claim (IVANS feed) with its payment rollup.
 *  Mirrors shared/db/ivans-claim-ingest.ts ClaimWithTotal. */
export interface InsuranceClaimDto {
  id: string;
  carrier_claim_number: string;
  carrier_name: string | null;
  claim_status_code: string | null;
  claim_type_code: string | null;
  lob_code: string | null;
  policy_number_raw: string | null;
  loss_date: string | null;
  reported_date: string | null;
  incident_description: string | null;
  adjuster_name: string | null;
  adjuster_phone: string | null;
  insurance_policy_id: string | null;
  household_id: string | null;
  total_paid_cents: number;
  payment_count: number;
}

// Re-export the shared connection type so page components can import it
// from the api module alongside the response shapes they already use.
export type { ConnectedPerson } from "@shared/types/household";

// ─── household list ─────────────────────────────────────────────────────

export type HouseholdSort =
  | "display_name"
  | "client_since"
  | "updated_at"
  // Canonical AUM (custodian-confirmed accounts) — PR 3.
  | "aum";
export type SortOrder = "asc" | "desc";

export interface HouseholdListParams {
  q?: string;
  sort?: HouseholdSort;
  order?: SortOrder;
  limit?: number;
  offset?: number;
  archived?: boolean;
  /** Abort the underlying fetch (D1-stability fix, 2026-08-12): the
   *  households list is the most expensive list query in the OS, and a
   *  superseded request left running server-side still burns database
   *  CPU. Callers that re-issue this call on every search keystroke
   *  (ListView) pass the effect's AbortSignal so the stale request is
   *  actually cancelled, not just ignored. */
  signal?: AbortSignal;
  /** Operator conditions ("+ Filter" builder) — sent as tilde chunks
   *  in the `f` query param. */
  conditions?: FilterCondition[];
  // Phase A4 (May 2026): when true, includes households with
  // is_quarantined=1 (the migration's fuzzy-match flag) in the result
  // set. Default is to hide them — set to true when the caller is
  // specifically reviewing quarantined records (e.g. the financial
  // firm dashboard's "(unassigned)" advisor-card drill-through, which
  // needs the same total count as the dashboard).
  includeQuarantined?: boolean;
  // Restrict to households with an active service record in this
  // company slug (mortgage / insurance / planning / realty / financial).
  company?: string;
  // UUID or "unassigned". Filter households whose advisor for the
  // scoped company is the given user_id (or null when "unassigned").
  // Requires `company` to be set.
  advisor?: string;
}

export interface HouseholdListItem {
  id: string;
  display_name: string;
  primary_city: string | null;
  primary_state: string | null;
  primary_zip: string | null;
  client_since: string | null;
  archived_at: string | null;
  updated_at: string;
  // Phase A4 (May 2026): the migration's fuzzy-match quarantine flag.
  // List rows surface a "Quarantined · merge-review pending" pill so
  // the user knows to route those records to /admin/reconciliation
  // before normal household actions.
  is_quarantined: boolean;
  quarantine_reason: string | null;
  contact_count: number;
  company_slugs: string[];
  // Who in the household asked to stop marketing email, in words, or null.
  marketing_opt_out: string | null;
  // The distinguishing "who's in this household" line derived from member
  // contacts — e.g. "Tom & Carolyn Keller" or a single person's name. Null
  // when no member has a usable name. Shown as a sub-line under the surname
  // household label so same-surname households are distinguishable.
  members_label: string | null;
  // Stage of Wealth — Control / Margin / Impact, one per family
  // (2026-09-18). A column on the list and in the export, so "Financial
  // clients in Margin" is two chips and a download. null with no unknown
  // reason below means nobody has said yet.
  wealth_stage?: "control" | "margin" | "impact" | null;
  // Set when somebody recorded the honest "we can't say yet", so the
  // list shows an answer where it would otherwise show a blank.
  wealth_stage_unknown_reason?: string | null;
}

export interface HouseholdListResponse {
  households: HouseholdListItem[];
  total: number;
  limit: number;
  offset: number;
}

// Nate 2026-07-11 — minimal search-result row for the reusable
// ─── The referral record (Marketing Hub row 4.1). Mirrors
// shared/db/marketing-referrals.ts. ──────────────────────────────────
export type ReferralSenderPick = { contact_id: string } | { vendor_id: string };

export interface ReferralSenderFacts {
  kind: "person" | "partner";
  id: string;
  name: string;
  household_id: string | null;
  household_name: string | null;
  sent_before: number;
  sent_by: { household_id: string | null; name: string; year: string | null } | null;
}

export interface ReferralRecordDefaults {
  household: { id: string; name: string; state_word: string } | null;
  people: Array<{ id: string; name: string; is_primary: boolean }>;
  companies: Array<{ slug: string; name: string; owner_user_id: string | null; owner_name: string | null; rule_line: string }>;
  clock_hours: number;
}

/** The Referrals screen: shared/db/marketing-referrals-screen.ts plus the
 *  owner's thank-you list for a firm viewer. */
export type ShareGuide = import("@shared/db/introduction-share").ShareGuide;
export type IntroductionShare = import("@shared/db/introduction-share").IntroductionShare;
export type ReferralsScreenResponse = import("@shared/db/marketing-referrals-screen").ReferralsScreen & {
  thank_yous: import("@shared/marketing/thank-you").ThankYouList | null;
  /** The companies whose desk the viewer sits at: "Share an introduction" offers only these. */
  share_companies: string[];
};

/** GET /api/marketing/held-people (shared/marketing/held-people.ts has the row shape). */
export interface HeldPeopleResponse {
  items: HeldPerson[];
  /** The whole count (its own COUNT); items is at most one page of it. */
  total: number;
  scope: { label: string; firm: boolean };
}

export interface ReferralRecordView {
  id: string;
  kind: string;
  status: string;
  company_slug: string;
  company_name: string;
  household_id: string;
  household_name: string;
  to_contact_name: string | null;
  sender: { kind: "person" | "partner"; id: string; name: string } | null;
  owner_user_id: string | null;
  owner_name: string | null;
  introduced_at: string;
  clock_due_at: string | null;
  note: string | null;
  source: string;
  rule_line: string;
}

// ContactPicker. Mirrors ContactPickerResult in
// shared/db/contact_safe_projection.ts.
export interface ContactPickerResult {
  id: string;
  name: string;
  email: string | null;
  phone_primary: string | null;
  household_id: string | null;
  household_label: string | null;
}

// ─── Contact detail ─────────────────────────────────────────────────────
//
// The shape returned by GET /api/contacts/:id/detail. Mirrors
// ContactDetailResponse in workers/api/src/routes/contacts.ts — keep
// the two in sync when fields are added. SafeContact fields are a
// subset of what the SafeContact interface on the server exposes
// (only the ones the UI actually reads); add more here as the page
// needs them.

export interface WhatMattersMostFields {
  what_matters_most: string | null;
  what_matters_most_updated_at: string | null;
  what_matters_most_updated_by_user_id: string | null;
  // 2026-07-01: "we don't know this family" terminal state (migration 0173).
  what_matters_most_unknown_reason?: string | null;
  what_matters_most_unknown_at?: string | null;
  what_matters_most_unknown_by_user_id?: string | null;
}

export interface SafeContact {
  id: string;
  legal_first_name: string | null;
  legal_middle_name: string | null;
  legal_last_name: string | null;
  legal_suffix: string | null;
  preferred_name: string | null;
  email: string | null;
  /** Secondary email — companion to phone_secondary. Populated by
   * the contact-merge wizard's "Keep both" affordance on email
   * conflicts. Never used as the active "send to" address. */
  secondary_email: string | null;
  phone_primary: string | null;
  phone_secondary: string | null;
  /** The client's own answer to "preferred way of communication"
   *  (migration 0238) — 'call' | 'text' | 'email', or null when never
   *  answered. See computePreferredMethod in lib/contact-display.ts for
   *  how this combines with do_not_email / phone_primary / email. */
  preferred_contact_method: string | null;
  street_line_1: string | null;
  street_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  birthday: string | null;
  gender: string | null;
  marital_status: string | null;
  preferred_pharmacy: string | null;
  // Planning underwriting facts about this person (S7,
  // ryan-planning-views.md §4). tobacco_use is TRI-STATE: null means
  // nobody has asked, false means they were asked and said no — never
  // render the first as "No".
  tobacco_use: boolean | null;
  height_inches: number | null;
  weight_lbs: number | null;
  employer_name: string | null;
  occupation: string | null;
  /** THIS person's annual income (a life/disability application
   *  underwrites one earner), distinct from the household figure on the
   *  Planning case. */
  individual_annual_income: number | null;
  household_id: string | null;
  household_role: string | null;
  referral_source_type: string | null;
  referral_source_detail: string | null;
  referred_by_contact_id: string | null;
  internal_referred_from_company_id: string | null;
  internal_referrer_user_id: string | null;
  real_estate_occupation: string | null;
  /** Who Stewardship actually contacts on this person's behalf when they
   *  have no email/phone on file (migration 20260904171230) — e.g. an
   *  elderly client whose adult child is the real point of contact.
   *  Cross-household by design. */
  communicates_via_contact_id: string | null;
  /** Why, and optionally a pointer to documented proof on file (the
   *  "attach proof" uploader tags an ordinary contact document, not a
   *  separate proof record — see COMMUNICATES_VIA_DOCUMENT_TYPE). */
  communicates_via_note: string | null;
  // Auto-policy driver discount qualifiers (migration 0226, Joy
  // 2026-07-30) — Good Student and Driver Training.
  good_student: boolean;
  driver_training: boolean;
  transactional_ok: boolean;
  relational_ok: boolean;
  marketing_ok: boolean;
  do_not_email: boolean;
  sms_opt_in: boolean;
  tcpa_consent_at: string | null;
  contact_type: "person" | "vendor";
  archived_at: string | null;
  archived_reason: string | null;
  created_at: string;
  updated_at: string;

  // Deceased status (migration 0081 + 0207). Written only via
  // api.markContactDeceased / api.unmarkContactDeceased — never
  // through the generic contact-edit PATCH.
  is_deceased: boolean;
  deceased_at: string | null;

  // Display-only metadata for SensitiveField reveal (deferred to a
  // later session but fields come through so the page doesn't have to
  // refetch when reveal ships).
  drivers_license_state: string | null;
  bank_account_type: "checking" | "savings" | null;
  bank_name: string | null;
  ssn_last_four: string | null;
  spouse_ssn_last_four: string | null;
  coborrower_ssn_last_four: string | null;
  drivers_license_last_four: string | null;
  bank_account_number_last_four: string | null;
  has_ssn_on_file: boolean;
  has_spouse_ssn_on_file: boolean;
  has_coborrower_ssn_on_file: boolean;
  has_drivers_license_on_file: boolean;
  has_bank_account_on_file: boolean;
  has_bank_routing_on_file: boolean;
  has_bank_account_name_on_file: boolean;
  ach_on_file: boolean;

  // Payment card (2026-08-19). Number/CVV/name are encrypted — only the
  // number's masked last-four + on-file flags come through here; expiry
  // and billing ZIP are plaintext metadata (nothing without the number).
  card_number_last_four: string | null;
  card_expiration: string | null;
  card_billing_zip: string | null;
  has_card_number_on_file: boolean;
  has_card_cvv_on_file: boolean;
  has_card_name_on_file: boolean;

  // Medicare (migration 0137). MBI is encrypted; only the masked last-four
  // + on-file flag come through here. Part A/B are plaintext dates.
  medicare_beneficiary_id_last_four: string | null;
  has_medicare_id_on_file: boolean;
  medicare_part_a_start_date: string | null;
  medicare_part_b_start_date: string | null;

  // Wave D.1 (April 2026): What Matters Most banner field. Free
  // advisor-judgment text + edit metadata.
  what_matters_most: string | null;
  what_matters_most_updated_at: string | null;
  what_matters_most_updated_by_user_id: string | null;
  // Nate 2026-07-24: display name for what_matters_most_updated_by_user_id
  // — see the matching Household field for why this rides along instead
  // of resolving client-side. Optional so contacts fetched via routes
  // that don't decorate it (e.g. merge/queue comparison rows) stay
  // assignable.
  what_matters_most_updated_by_name?: string | null;
  // 2026-07-01: "we don't know this family" terminal state (migration 0173).
  what_matters_most_unknown_reason?: string | null;
  what_matters_most_unknown_at?: string | null;
  what_matters_most_unknown_by_user_id?: string | null;

  // v1.x phase 11.5: cross-team email history visibility flag.
  // system_admin-only; defaults false. When true, this contact's
  // emails are excluded from /api/email/history feeds on shared
  // record views.
  email_history_private?: boolean;

  // Phase D4 (May 2026 — reversibility-first architecture):
  // optimistic-locking version number. Send back as
  // `expected_version_number` on PATCH so two users editing the
  // same record can't silently overwrite each other.
  version_number?: number;
}

export interface ContactHouseholdMember {
  id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  household_role: string | null;
  /** Cleanup research aid (May 2026 — Baker feedback): birthday lets a
   *  steward tell a true duplicate (same person, two records → merge)
   *  from two different people who share a phone/last name (spouses →
   *  dismiss) without leaving /cleanup. OS-native replacement for AC's
   *  "INSURANCE: Household Members 18+" field. Plain (non-sensitive). */
  birthday: string | null;
}

export interface ContactDetailHouseholdSummary {
  id: string;
  display_name: string;
  primary_contact_id: string | null;
  contact_count: number;
  /** All members of the household. Wave B (April 2026) — lets the
   *  contact hero render "Spouse to Mary Smith" without a second
   *  fetch. */
  members: ContactHouseholdMember[];
}

export interface ContactCompanyRelationship {
  company_id: string;
  company_slug: string;
  company_display_name: string;
  relationship_state: string;
  /** Per-(household, company) Fit Assessment. Wave B (April 2026). */
  fit_assessment: string;
  fit_assessed_at: string | null;
  current_advisor_user_id: string | null;
  /** Advisor display name (users.name). Null when no advisor. */
  current_advisor_name: string | null;
  /** Advisor email (users.email). Null when no advisor. */
  current_advisor_email: string | null;
  client_since_this_company: string | null;
  /** 1 = derived FLOOR — render as "at least <date>" (migration 0200). */
  client_since_is_floor: number;
}

// Wave C (April 2026): per-row shapes carried by the contact-detail
// + household-detail responses for the Active Opportunities / Active
// Transactions rail cards. The fields are a subset of the underlying
// rows — enough for the rail card to render a one-line summary +
// company chip + advisor name without a follow-up fetch.
export interface HouseholdOpportunityRow {
  id: string;
  household_id: string;
  company_id: string;
  company_slug: string;
  company_display_name: string;
  opportunity_type: string;
  stage: string;
  expected_close_date: string | null;
  product_metric: number | null;
  product_metric_unit: string | null;
  estimated_revenue_year_1: number | null;
  owner_user_id: string | null;
  owner_name: string | null;
  opened_at: string;
}

/** A closed (won / lost / abandoned) opportunity on a household — the
 *  "Closed" section of the household's Opportunities tab. */
export interface HouseholdClosedOpportunityRow extends HouseholdOpportunityRow {
  outcome: string;
  outcome_reason: string | null;
  closed_at: string | null;
}

export interface HouseholdTransactionRow {
  id: string;
  household_id: string;
  company_id: string;
  company_slug: string;
  company_display_name: string;
  transaction_type: string;
  stage: string;
  started_at: string;
  expected_close_date: string | null;
  product_metric: number | null;
  product_metric_unit: string | null;
  estimated_revenue_year_1: number | null;
  owner_user_id: string | null;
  owner_name: string | null;
  /** What it delivers: the product it made, or the one it was won as
   *  (LTC PR 10, for the Planning card's long-term care line). */
  intended_service_type?: string | null;
  linked_service_type?: string | null;
}

// Wave C-2 (April 2026): manual tasks shape for the rail cards.
// Mirrors shared/types/compliance.ts ManualTask but kept inline here
// to avoid a cross-package import in the web bundle.
export interface RailManualTask {
  id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  priority: "low" | "normal" | "high" | "urgent" | null;
  assigned_to_user_id: string;
  entity_type:
    | "contact"
    | "household"
    | "opportunity"
    | "transaction"
    | "service_record"
    | "rhythm_instance"
    | null;
  entity_id: string | null;
  created_at: string;
}

// Wave C-2 (April 2026): contact + household documents shape used by
// the Documents footer tab. Same fields the underlying Document type
// carries — kept inline here for frontend bundle hygiene.
export interface RailDocument {
  id: string;
  entity_type: string;
  entity_id: string;
  document_type: string | null;
  filename: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  signed_status: string | null;
  signed_at: string | null;
  uploaded_at: string;
  archived: boolean;
}

// v1.x phase 11.13 — "Last contact" summary for record headers.
// Shape mirrors shared/db/activities.ts LastContactSummary so the
// frontend type matches the API surface.
export interface LastContactSummary {
  occurred_at: string;
  activity_type: string;
  direction: "inbound" | "outbound" | "neutral";
  channel_label: string;
  preview: string | null;
}

/** One person on a "might be the same person" cleanup card
 *  (GET /api/cleanup/issues/:id/people; shared/db/duplicate-people.ts). */
export interface DuplicatePerson {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  birthday: string | null;
  household_id: string | null;
  household_name: string | null;
  household_role: string | null;
  record_count: number;
  note_count: number;
  gone: boolean;
}

/** One household on a "same household?" cleanup card
 *  (GET /api/cleanup/issues/:id/households; shared/db/duplicate-households.ts). */
export interface DuplicateHousehold {
  id: string;
  name: string;
  address: string | null;
  members: { id: string; name: string; birthday: string | null }[];
  emails: string[];
  phones: string[];
  companies: string[];
  record_count: number;
  open_work_count: number;
  gone: boolean;
}

/** A missing-documents item's live state
 *  (GET /api/cleanup/issues/:id/missing-documents). */
export interface MissingDocumentsState {
  service_record_id: string;
  missing: { definition_id: string; name: string; code: string | null; when_required: string | null }[];
  waived: { waiver_id: string; definition_id: string; name: string; reason: string; by: string | null; at: string }[];
}

export interface ContactDetailResponse {
  contact: SafeContact;
  household: ContactDetailHouseholdSummary | null;
  company_relationships: ContactCompanyRelationship[];
  activities: Activity[];
  /** Wave C (April 2026): open opportunities for the contact's
   *  household. Empty when there's no household linkage or no open
   *  opps. Drives the contact-page Active Opportunities rail card. */
  household_opportunities: HouseholdOpportunityRow[];
  /** Open transactions for the contact's household. */
  household_transactions: HouseholdTransactionRow[];
  /** Active rhythm instances for the contact's household (status =
   *  'active'). Reuses the RhythmInstance shape. */
  household_rhythms: RhythmInstance[];
  /** Wave C-2 (April 2026): open manual tasks linked DIRECTLY to
   *  this contact. Drives the "Tasks About <Contact>" rail card. */
  contact_tasks: RailManualTask[];
  /** Notes scoped to this contact (entity_type='contact'). Drives
   *  the Notes footer tab. */
  contact_notes: Note[];
  /** How many notes live on this contact's HOUSEHOLD (Joy, 2026-08-03).
   *  The Notes tab shows only contact-scoped notes; without this the
   *  household's notes were invisible from here and read as lost. */
  household_notes_count?: number;
  /** Documents scoped to this contact (entity_type='contact').
   *  Drives the Documents footer tab. */
  contact_documents: RailDocument[];
  /** Per-(contact, company) role assignments (borrower, trustee,
   *  etc.). Drives the Roles footer tab. */
  contact_roles: ContactRoleRow[];
  /** v1.x phase 11.13: most recent activity involving this contact.
   *  Rendered as "Last contact: 3 days ago via email" in the page
   *  header. null when there's no recorded activity yet. */
  last_contact: LastContactSummary | null;
  /** The household's last touch; only populated when `last_contact` is
   *  null (this person has none). Drives "household touched today". */
  household_last_contact?: LastContactSummary | null;
  /** Part 3: Financial Summary card (household-level context on the
   *  secondary contact record). null when no household / no Financial
   *  relationship. */
  financial_summary?: FinancialSummary | null;
  /** Lending Summary card payload — household mortgage rollup (Mike Zins /
   *  Private Money). null when the household has no mortgage loans. */
  lending_summary?: LendingSummary | null;
  /** Cross-household connections (migration 0160) — this contact's links
   *  to people in other households, from this contact's perspective. */
  connected_people?: ConnectedPerson[];
  /** Resolved "Contact via" summary for communicates_via_contact_id
   *  (migration 20260904171230) — null when unset, or when the linked
   *  contact could no longer be found. */
  communicates_via_contact?: ContactDetailCommunicatesViaSummary | null;
}

/** Resolved display summary for contact.communicates_via_contact_id —
 *  see ContactDetailResponse.communicates_via_contact. */
export interface ContactDetailCommunicatesViaSummary {
  id: string;
  name: string;
  email: string | null;
  phone_primary: string | null;
}

// PATCH /api/contacts/:id body shape. Mirrors EditableContactFields in
// shared/db/contacts.ts. Sensitive *_encrypted / *_last_four columns
// are NOT here — they have a dedicated decryption flow.
export interface EditableContactFields {
  legal_first_name: string | null;
  legal_middle_name: string | null;
  legal_last_name: string | null;
  legal_suffix: string | null;
  preferred_name: string | null;
  email: string | null;
  secondary_email: string | null;
  phone_primary: string | null;
  phone_secondary: string | null;
  preferred_contact_method: string | null;
  street_line_1: string | null;
  street_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  birthday: string | null;
  gender: string | null;
  marital_status: string | null;
  preferred_pharmacy: string | null;
  // Planning underwriting facts (S7). See SafeContact above.
  tobacco_use: boolean | null;
  height_inches: number | null;
  weight_lbs: number | null;
  employer_name: string | null;
  occupation: string | null;
  individual_annual_income: number | null;
  contact_type: "person" | "vendor";
  referral_source_type: string | null;
  referral_source_detail: string | null;
  referred_by_contact_id: string | null;
  internal_referred_from_company_id: string | null;
  internal_referrer_user_id: string | null;
  real_estate_occupation: string | null;
  drivers_license_state: string | null;
  bank_account_type: "checking" | "savings" | null;
  bank_name: string | null;
  // Auto-policy driver discount qualifiers (migration 0226).
  good_student: boolean;
  driver_training: boolean;
  transactional_ok: boolean;
  relational_ok: boolean;
  marketing_ok: boolean;
  do_not_email: boolean;
  sms_opt_in: boolean;
  tcpa_consent_at: string | null;
}

export interface UpdateContactResponse {
  contact: SafeContact;
  changes_count: number;
}

// Audit-log row in display shape — what the Change History panel
// consumes. Mirrors AuditEventDisplay in shared/db/audit.ts.
export interface AuditEventDisplay {
  id: string;
  occurred_at: string;
  action: string;
  actor: {
    user_id: string | null;
    name: string | null;
    email: string | null;
  };
  field_name: string | null;
  old_value: unknown;
  new_value: unknown;
  purpose: string | null;
}

export interface ContactHistoryResponse {
  events: AuditEventDisplay[];
}

export interface HouseholdHistoryResponse {
  events: AuditEventDisplay[];
}

// Merged contacts (unmerge admin surface).
export interface MergedContactItem {
  merged_contact_id: string;
  merged_legal_first_name: string | null;
  merged_legal_last_name: string | null;
  merged_preferred_name: string | null;
  merged_at: string;
  primary_contact_id: string;
  primary_legal_first_name: string | null;
  primary_legal_last_name: string | null;
  primary_preferred_name: string | null;
  /** PR #315 — per-pair audit context. Null when no audit row
   *  matched (legacy migration merges that pre-dated the
   *  context.merge schema). */
  reason: string | null;
  source: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
}

export interface MergedContactsResult {
  total: number;
  limit: number;
  offset: number;
  items: MergedContactItem[];
}

export interface UnmergeContactResponse {
  restored_contact_id: string;
  primary_contact_id: string;
  reverted_fields: Array<{
    field_name: string;
    reverted_to_value: unknown;
    was_value: unknown;
  }>;
}

// Stale fields queue.
export interface StaleFieldsQueueItem {
  contact_id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  phone_primary: string | null;
  household_id: string | null;
  household_display_name: string | null;
  updated_at: string;
  days_since_update: number;
}

export interface StaleFieldsQueueResult {
  total: number;
  limit: number;
  offset: number;
  threshold_days: number;
  items: StaleFieldsQueueItem[];
}

export type StaleFieldsSort = "oldest_first" | "newest_first" | "name_az" | "name_za";

// Vendor-lookalikes queue (#6) — person contacts whose name suggests
// they're really a business / vendor / borrower entity. Surfaces
// both pure entities (safe to retag) and hybrids (LLC borrower with
// a real human signer) so admins can review per-row.
export interface VendorLookalikeQueueItem {
  contact_id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  phone_primary: string | null;
  household_id: string | null;
  household_display_name: string | null;
  match_in_first: boolean;
  match_in_last: boolean;
  created_at: string;
}

export interface VendorLookalikeQueueResult {
  total: number;
  limit: number;
  offset: number;
  items: VendorLookalikeQueueItem[];
}

export type VendorLookalikesSort = "oldest_first" | "newest_first" | "name_az" | "name_za";

// Bad-data alerts queue.
export type BadDataMatchType = "placeholder_email" | "malformed_email" | "placeholder_phone";

export interface BadDataAlertItem {
  contact_id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  household_id: string | null;
  household_display_name: string | null;
  match_type: BadDataMatchType;
  offending_value: string;
}

export interface BadDataAlertsQueueResult {
  total: number;
  limit: number;
  offset: number;
  counts_by_type: Record<BadDataMatchType, number>;
  items: BadDataAlertItem[];
}

export type BadDataAlertsSort = "default" | "value_az" | "value_za";

// Duplicate candidates queue.
export type DuplicateMatchType = "email" | "phone" | "name_zip";

// Shape of a single match from GET /api/contacts/duplicate-precheck
// (and the same-shaped ContactDuplicateMatch the server returns on a
// 409 duplicate_contact refusal from POST /api/contacts).
// Ambassador referral surface (Ambassador program PR 2). Thin by
// design — see routes/ambassador.ts for the disclosure rules.
export interface AmbassadorPrecheckMatch {
  contact_id: string;
  display_name: string;
  already_with_stewardship: boolean;
  // The relationship strip (program PR 3): product noun + date +
  // advisor per service — never premium, carrier, numbers, or values.
  services: Array<{
    company: string;
    label: string;
    since: string | null;
    advisor: string | null;
    active: boolean;
  }>;
}

export interface AmbassadorReferralRow {
  id: string;
  created_at: string;
  contact_existed: boolean;
  display_name: string;
  stage: string | null;
  outcome: string | null;
  /** Which company they were referred to (migration 20260814223351). */
  company: string | null;
  company_label: string | null;
  /**
   * 'ambassador_form' when she filed it herself; 'advisor_designated'
   * when an employee credited her with one that came in by phone.
   */
  origin: string;
}

// The ambassador referral fee (program PR 6). Money is always integer
// cents over the wire — no floating-point dollars anywhere.
export interface AmbassadorEarningRow {
  id: string;
  referred_person: string;
  closed_at: string;
  fee_cents: number;
  rate_bps: number;
  status: "owed" | "paid" | "void";
  paid_at: string | null;
}

/** What the program pays for one company's referrals. */
export interface AmbassadorProgramRate {
  company: string | null;
  company_label: string | null;
  /** Null when the rate has not been decided yet — NOT zero. */
  rate_bps: number | null;
  rate_pending: boolean;
}

export interface AmbassadorEarningsResponse {
  fees: AmbassadorEarningRow[];
  owed_cents: number;
  paid_cents: number;
  /** The Realty rate — the only company with a settled rate today. */
  current_rate_bps: number | null;
  /** Per-company rates, including the honest "not set yet" (migration 20260814223351). */
  program_rates: AmbassadorProgramRate[];
}

export interface AmbassadorProgramSettings {
  id: string;
  rate_bps: number;
  fee_basis: "office_gross" | "office_net";
  first_deal_only: boolean;
  exclude_existing_realty_client: boolean;
  require_title_payment_before_paid: boolean;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
  /** Which company this policy governs (migration 20260814223351). */
  company_slug: string | null;
  /** True when the program pays but the rate is undecided (migration 20260814223351). */
  rate_pending: boolean;
}

// A won referral into a company whose referral rate has not been set
// (migration 20260814223351). No money on it, on purpose — see the admin route.
export interface AmbassadorReferralAwaitingRate {
  referral_id: string;
  ambassador_user_id: string;
  ambassador_name: string | null;
  referred_person: string;
  company_slug: string | null;
  company_name: string | null;
  referred_at: string;
  outcome: string | null;
  stage: string | null;
  origin: string;
}

export interface AmbassadorFeeAdminRow {
  id: string;
  referral_id: string;
  ambassador_user_id: string;
  ambassador_name: string | null;
  referred_person: string;
  service_record_id: string;
  closed_at: string;
  rate_bps: number;
  fee_basis: "office_gross" | "office_net";
  basis_cents: number;
  fee_cents: number;
  status: "owed" | "paid" | "void";
  title_payment_received_at: string | null;
  paid_at: string | null;
  contact_existed: boolean;
}

/** The advisor-facing panel on a referred Realty deal. */
export interface ServiceRecordAmbassadorFee {
  fee_id: string;
  ambassador_name: string | null;
  referred_person: string;
  rate_bps: number;
  fee_basis: "office_gross" | "office_net";
  basis_cents: number;
  fee_cents: number;
  /** gross − fee: what a split gets figured on. */
  reduced_base_cents: number;
  status: "owed" | "paid" | "void";
  contact_existed: boolean;
}

// Ambassador calendar access (program PR 5). Note what a busy block
// does NOT carry: no subject, no location, no Outlook link. The server
// constructs it that way (routes/ambassador-calendar.ts) — an advisor's
// calendar titles are a client list, and this role sees times only.
export interface AmbassadorBusyBlock {
  start_iso: string;
  end_iso: string;
}

export interface AmbassadorOpenSlot {
  start_iso: string;
  end_iso: string;
  minutes: number;
}

export interface AmbassadorAvailabilityDay {
  date: string;
  busy: AmbassadorBusyBlock[];
  open: AmbassadorOpenSlot[];
}

export interface AmbassadorBookableAdvisor {
  user_id: string;
  name: string;
}

export interface AmbassadorAvailabilityResponse {
  advisor: AmbassadorBookableAdvisor;
  date: string;
  days: AmbassadorAvailabilityDay[];
  booking_pages: Array<{
    page_id: string;
    page_name: string | null;
    page_url: string | null;
    is_shared: boolean;
    services: Array<{
      id: string;
      name: string;
      duration_minutes: number | null;
      kind: string;
      url: string | null;
    }>;
  }>;
  can_book: boolean;
  office_hours: { start_hour: number; end_hour: number };
  /** Non-null when the calendar couldn't be read — render it, never an empty day. */
  calendar_error: string | null;
  /** Checked blocking calendars that couldn't be read this load, by name.
   *  Openings are real but may include time these would have blocked. */
  unreadable_calendars?: string[];
  as_of: string;
}

export interface DuplicatePrecheckMatch {
  contact_id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  phone_primary: string | null;
  household_id: string | null;
  household_display_name: string | null;
  match_signals: DuplicateMatchType[];
}

export interface DuplicateContactSide {
  id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  phone_primary: string | null;
  household_id: string | null;
  household_display_name: string | null;
  // Phase 11.x (May 2026 — full-field comparison cards): the queue
  // page comparison cards now render every editable field with a
  // "Show identical fields" toggle, mirroring MERGEABLE_FIELDS_UI.
  legal_middle_name: string | null;
  legal_suffix: string | null;
  secondary_email: string | null;
  phone_secondary: string | null;
  street_line_1: string | null;
  street_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  birthday: string | null;
  gender: string | null;
  marital_status: string | null;
  referral_source_type: string | null;
  referral_source_detail: string | null;
  real_estate_occupation: string | null;
  drivers_license_state: string | null;
  bank_account_type: string | null;
  transactional_ok: number | null;
  relational_ok: number | null;
  marketing_ok: number | null;
  do_not_email: number | null;
  sms_opt_in: number | null;
  tcpa_consent_at: string | null;
  /** Household-context + freshness extension. Surfaced on the merge
   *  / queue cards so the user has enough context to decide which
   *  record to keep without round-tripping to the household page. */
  what_matters_most: string | null;
  contact_created_at: string | null;
  contact_updated_at: string | null;
  household_primary_city: string | null;
  household_primary_state: string | null;
  household_primary_zip: string | null;
  household_member_count: number | null;
}

// A cluster groups every active person contact that shares one match
// value (same email, same phone, or same name+zip across different
// households). N=2 clusters are pairs; N=3+ clusters render with all
// members listed inline so the user can see the full duplicate group
// at once.
export interface DuplicateCluster {
  match_type: DuplicateMatchType;
  match_value: string;
  members: DuplicateContactSide[];
}

export interface DuplicateCandidatesQueueResult {
  total: number;
  limit: number;
  offset: number;
  counts_by_type: Record<DuplicateMatchType, number>;
  items: DuplicateCluster[];
}

// ── Bug B Phase 5: spouse-pass quarantine ─────────────────────────────

export type SpousePassMatchMethod = "strict" | "loose";

export interface SpousePassQuarantineSuggestedMember {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface SpousePassQuarantineSuggested {
  household_id: string;
  display_name: string | null;
  member_count: number;
  members: SpousePassQuarantineSuggestedMember[];
  score: number | null;
  reasons: string[];
}

export interface SpousePassQuarantinePrimaryHousehold {
  household_id: string | null;
  display_name: string | null;
}

export interface SpousePassQuarantineRaw {
  ac_legacy_id: string;
  primary_email: string | null;
  primary_first_name: string | null;
  primary_last_name: string | null;
  spouse_name_raw: string;
  match_method: SpousePassMatchMethod;
  parsed_spouse_first: string;
  parsed_spouse_last: string;
}

export interface SpousePassQuarantineItem {
  id: string;
  raw: SpousePassQuarantineRaw;
  primary_household: SpousePassQuarantinePrimaryHousehold;
  suggested_households: SpousePassQuarantineSuggested[];
  created_at: string;
}

export interface SpousePassQuarantineList {
  total_pending: number;
  items: SpousePassQuarantineItem[];
}

export type SpousePassQuarantineResolveResponse =
  | {
      ok: true;
      resolution: "skipped";
      id: string;
    }
  | {
      ok: true;
      resolution: "merged";
      id: string;
      primary_household_id: string;
      merged_household_id: string;
      audit_log_id: string;
      counts: Record<string, number>;
    };

export type DuplicateCandidatesSort =
  | "default"
  | "value_az"
  | "value_za"
  | "size_desc"
  | "size_asc";

// Duplicate dismissals.
export type DismissalMatchType = "email" | "phone" | "name_zip";

export interface DuplicateDismissal {
  id: string;
  match_type: DismissalMatchType;
  match_value: string;
  dismissed_by_user_id: string;
  dismissed_at: string;
  reason: string | null;
  restored_at: string | null;
  restored_by_user_id: string | null;
  restored_reason: string | null;
}

export interface DuplicateDismissalWithActor extends DuplicateDismissal {
  dismissed_by_name: string | null;
  dismissed_by_email: string | null;
  restored_by_name: string | null;
  restored_by_email: string | null;
}

// ────────────────────────────────────────────────────────────────────
// Vendors
// ────────────────────────────────────────────────────────────────────

export type VendorType =
  | "carrier"
  | "lender"
  | "custodian"
  | "title_company"
  | "escrow"
  | "inspector"
  | "lender_partner"
  | "referral_source_partner"
  | "service_provider"
  | "other";

export const VENDOR_TYPE_LABELS: Record<VendorType, string> = {
  carrier: "Insurance carrier",
  lender: "Mortgage lender",
  custodian: "Investment custodian",
  title_company: "Title company",
  escrow: "Escrow",
  inspector: "Inspector",
  lender_partner: "Lender partner (originator only)",
  referral_source_partner: "Referral partner",
  service_provider: "Service provider",
  other: "Other",
};

export interface Vendor {
  id: string;
  slug: string | null;
  display_name: string;
  type: VendorType;
  portal_url: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  primary_contact_phone: string | null;
  /** Mailing address of the primary contact. Optional on the type so older
   *  fixtures still fit; the server always sends it (2026-10-15). */
  primary_contact_address?: string | null;
  relationship_owner_user_id: string | null;
  notes: string | null;
  /** 'approved' | 'pending' | 'not_approved'; null = never assessed. The
   *  server has always sent it; typed 2026-09-24 for the roster editor. */
  approval_status?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ListDismissalsResult {
  total: number;
  limit: number;
  offset: number;
  items: DuplicateDismissalWithActor[];
}

// Result shape for the auto-suppress-couples bulk-dismiss endpoint.
// `sample` is the first 10 clusters that match the rule (or would be
// dismissed in apply mode), each with the household name + the names
// of every member, so the admin can spot-check before pulling the
// trigger.
export interface AutoSuppressCouplesCluster {
  match_value: string;
  member_count: number;
  household_id: string;
  household_display_name: string | null;
  member_names: string[];
}

export interface AutoSuppressCouplesResult {
  dry_run: boolean;
  total_safe_clusters: number;
  suppressed: number;
  sample: AutoSuppressCouplesCluster[];
}

// Result shape for the auto-merge-same-name-phone bulk-merge endpoint.
// Each cluster member is returned in survivor-priority order;
// members[0] is the chosen survivor.
export interface AutoMergeSameNameMember {
  contact_id: string;
  household_id: string | null;
  household_display_name: string | null;
  email: string | null;
  phone_primary: string | null;
  preferred_name: string | null;
  population_score: number;
  created_at: string;
}

export interface AutoMergeSameNameCluster {
  match_value: string;
  member_count: number;
  legal_first_name: string;
  legal_last_name: string;
  members: AutoMergeSameNameMember[];
}

export interface AutoMergeSameNameResult {
  dry_run: boolean;
  total_safe_clusters: number;
  clusters_processed: number;
  merges_succeeded: number;
  merges_failed: number;
  errors: Array<{ cluster_phone: string; error_message: string }>;
  sample: AutoMergeSameNameCluster[];
}

// Fin-G2 — READ-ONLY trust→family re-attachment preview, backed by
// GET /api/admin/financial/trust-reattachment-preview. Mirrors the
// shared TrustReattachmentPreview shape. No mutation.
export type TrustReattachStatus =
  | "confident_match"
  | "multi_account_collapsed"
  | "needs_review"
  | "no_family_match";

export interface TrustReattachGrantor {
  first: string | null;
  last: string;
  confidence: "high" | "medium" | "low";
  basis: string;
}

export interface TrustReattachAccount {
  portfolio_id: string;
  external_account_id: string | null;
  account_title: string | null;
  registration_kind: string | null;
  aum_cents: number;
  extracted: TrustReattachGrantor[];
}

export interface TrustReattachRow {
  quarantined_household_id: string;
  quarantined_display_name: string;
  account_count: number;
  total_aum_cents: number;
  accounts: TrustReattachAccount[];
  status: TrustReattachStatus;
  account_is_trust_or_entity: boolean;
  proposed_target_household_id: string | null;
  proposed_target_display_name: string | null;
  match_confidence: number | null;
  match_reasons: string[];
}

export interface TrustReattachApplyResult {
  applied: boolean;
  quarantined_household_id: string;
  proposed_target_household_id: string | null;
  proposed_target_display_name: string | null;
  reason?:
    | "not_found_or_not_quarantined"
    | "needs_split"
    | "needs_review"
    | "no_family_match"
    | "merge_failed";
  message?: string;
  merge?: {
    success: boolean;
    primary_household_id: string;
    merged_household_id: string;
    audit_log_id: string;
    error?: string;
    counts?: Record<string, number>;
  };
}

export interface TrustReattachmentPreviewResult {
  generated_at: string;
  summary: {
    quarantined_households_scanned: number;
    confident_match: number;
    multi_account_collapsed: number;
    needs_review: number;
    no_family_match: number;
    total_aum_cents_in_scope: number;
  };
  rows: TrustReattachRow[];
}

// Financial firm dashboard — backed by GET /api/admin/financial/firm-dashboard.
// All `*_cents` values are integers (cents); the page formats to dollars.
export interface FinancialFirmDashboardSummary {
  total_aum_cents: number;
  household_count: number;
  portfolio_count: number;
  service_record_active_count: number;
  avg_portfolio_size_cents: number;
  aum_per_household_cents: number;
  new_households_ytd: number;
  open_opportunity_count: number;
  open_transaction_count: number;
  active_review_cycle_count: number;
  // Round 3 — period-over-period deltas + monthly sparkline series.
  new_households_prior_ytd: number;
  households_served_timeline: TimelineCountPoint[];
  open_opportunity_timeline: TimelineCountPoint[];
  open_transaction_timeline: TimelineCountPoint[];
  active_review_cycle_timeline: TimelineCountPoint[];
  open_opportunity_count_30d_ago: number;
  open_transaction_count_30d_ago: number;
  active_review_cycle_count_30d_ago: number;
  total_aum_timeline: TimelineCentsPoint[];
  // Round 4 — revenue from commission_disbursements
  revenue_mtd_cents: number;
  revenue_ytd_cents: number;
  revenue_ttm_cents: number;
  revenue_prior_ttm_cents: number;
  revenue_prior_mtd_cents: number;
  revenue_per_household_cents: number;
  advisory_fee_yield_bps: number;
  revenue_retention_pct: number;
  revenue_monthly_timeline: TimelineCentsPoint[];
}
export interface TimelineCountPoint {
  period: string; // YYYY-MM
  count: number;
}
export interface TimelineCentsPoint {
  period: string; // YYYY-MM
  cents: number;
}
export interface FinancialAdvisorBreakdown {
  advisor_user_id: string | null;
  advisor_name: string | null;
  household_count: number;
  portfolio_count: number;
  aum_cents: number;
  aum_pct_of_firm: number;
  capacity_target_households: number;
  capacity_pct: number;
  new_households_ytd: number;
  // Round 5 — quarantine breakdown for the (unassigned) card.
  actionable_household_count: number;
  pending_merge_review_household_count: number;
}
export interface FinancialTopHousehold {
  household_id: string;
  household_display_name: string;
  advisor_name: string | null;
  portfolio_count: number;
  aum_cents: number;
}
export interface FinancialUnderpenetratedHousehold {
  household_id: string;
  household_display_name: string;
  advisor_name: string | null;
  aum_cents: number;
  has_companies: string[];
  missing_companies: string[];
}
export interface FinancialSnapshotsBySource {
  source: string;
  snapshot_count: number;
  total_balance_cents: number;
  latest_period_end: string | null;
}
export interface FinancialRecentSnapshot {
  snapshot_id: string;
  source: string;
  account_id: string;
  external_account_id: string | null;
  account_title: string | null;
  household_display_name: string | null;
  balance_cents: number;
  period_end: string;
  created_at: string;
  custodian_name: string | null;
}
/** Insurance dashboard response — /api/dashboards/insurance. */
export interface InsuranceDashboardQbMonth {
  month: string;
  total_revenue: number | null;
  net_income: number | null;
  period_start: string | null;
  period_end: string | null;
  pulled_at: string | null;
}

/** One bar pair in the YoY chart: current 12-month period + same month
 *  a year earlier. Either side can be null if no QB snapshot exists. */
export interface InsuranceYoYMonthEntry {
  month: string; // YYYY-MM (current period bar)
  prior_year_month: string; // YYYY-MM (prior period bar, 12 months earlier)
  current_period: {
    total_revenue: number | null;
    net_income: number | null;
  } | null;
  prior_period: {
    total_revenue: number | null;
    net_income: number | null;
  } | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Mortgage dashboard — /api/dashboards/mortgage
// ─────────────────────────────────────────────────────────────────────────

export interface MortgageOriginationByClass {
  traditional: number;
  private_money: number;
  total: number;
}
export interface MortgageOriginationDollarsByClass {
  traditional_dollars: number;
  private_money_dollars: number;
  total_dollars: number;
}
export interface MortgagePeriodTotals {
  funded_count: MortgageOriginationByClass;
  funded_volume: MortgageOriginationDollarsByClass;
  revenue: MortgageOriginationDollarsByClass;
  avg_loan_size_dollars: {
    traditional: number | null;
    private_money: number | null;
  };
  avg_revenue_per_loan_dollars: {
    traditional: number | null;
    private_money: number | null;
  };
}
export interface MortgageOriginationPulse {
  ttm: MortgagePeriodTotals;
  prior_ttm: MortgagePeriodTotals;
  ttm_start_iso: string;
  prior_ttm_start_iso: string;
}
export interface MortgageMixSlice {
  key: string;
  label: string;
  count: number;
  volume_dollars: number;
  pct_of_count: number;
}
export interface MortgageLenderMixRow {
  vendor_id: string | null;
  display_name: string;
  count: number;
  volume_dollars: number;
  pct_of_count: number;
  auto_created: boolean;
}
export interface MortgageLoanOfficerRow {
  user_id: string | null;
  name: string;
  total_count: number;
  traditional_count: number;
  private_money_count: number;
  total_volume_dollars: number;
  total_revenue_dollars: number;
  avg_revenue_per_loan: number | null;
}
export interface MortgageRetention {
  lifetime_repeat_household_pct: number | null;
  stewardship_on_stewardship_refi_pct: number | null;
  household_bucket_distribution: Array<{
    bucket: "1" | "2" | "3+";
    households: number;
  }>;
}
export interface MortgagePmAudit {
  pm_funded_total: number;
  pm_audit_completed: number;
  pm_audit_pending: number;
  latest_pm_audited_at: string | null;
  oldest_pending_pm_funded_at: string | null;
}
export interface MortgageActivePipeline {
  traditional_processing_open: number;
  private_money_processing_open: number;
  opportunities_open: number;
  rate_watch_cycles_active: number;
}
// QB YoY shape mirrors what the Insurance dashboard consumes — it's the
// same RevenueAndProfit returned by computeRevenueAndProfit.
export interface MortgageYoYMonthEntry {
  month: string;
  prior_year_month: string;
  current_period: { total_revenue: number | null; net_income: number | null } | null;
  prior_period: { total_revenue: number | null; net_income: number | null } | null;
}
export interface MortgageRevenueAndProfit {
  monthly_series: MortgageYoYMonthEntry[];
  last_full_month: {
    month: string;
    total_revenue: number | null;
    net_income: number | null;
    period_start: string | null;
    period_end: string | null;
    pulled_at: string | null;
  } | null;
  prior_year_total_profit: number | null;
  prior_year_label: number | null;
  current_year_ytd_profit: number | null;
  current_year_ytd_through_month: string | null;
}
export interface MortgageDashboardResponse {
  generated_at: string;
  origination_pulse: MortgageOriginationPulse;
  loan_program_mix: MortgageMixSlice[];
  loan_purpose_mix: MortgageMixSlice[];
  lender_mix_traditional: MortgageLenderMixRow[];
  lender_mix_private_money: MortgageLenderMixRow[];
  loan_officer_breakdown: MortgageLoanOfficerRow[];
  retention: MortgageRetention;
  pm_audit: MortgagePmAudit;
  active_pipeline: MortgageActivePipeline;
  revenue_and_profit: MortgageRevenueAndProfit;
}

// ─── Realty dashboard (PR 5 / Grant 2026-05-23) ─────────────────────────

export interface RealtyPeriodTotals {
  count: number;
  volume_dollars: number;
  commission_dollars: number;
  agent_net_dollars: number;
  office_net_dollars: number;
  avg_deal_size_dollars: number | null;
}

export interface RealtyVolumePulse {
  current_ttm: RealtyPeriodTotals;
  prior_ttm: RealtyPeriodTotals;
}

export interface RealtySubTypeSlice {
  sub_type: string;
  count: number;
  volume_dollars: number;
}

export interface RealtyRepresentingSlice {
  representing: string;
  count: number;
  volume_dollars: number;
}

export interface RealtyAgentRow {
  user_id: string;
  display_name: string;
  is_active: boolean;
  count: number;
  volume_dollars: number;
  commission_dollars: number;
}

export interface RealtyPipelineCounts {
  active_opportunities: number;
  active_processing: number;
  stale_leads: number;
  pending_close: number;
}

export interface RealtyRevenueAndProfit {
  monthly_series: Array<{
    month: string;
    prior_year_month: string;
    current_period: { total_revenue: number | null; net_income: number | null } | null;
    prior_period: { total_revenue: number | null; net_income: number | null } | null;
  }>;
  last_full_month: {
    month: string;
    total_revenue: number | null;
    net_income: number | null;
    period_start: string | null;
    period_end: string | null;
    pulled_at: string | null;
  } | null;
  prior_year_total_profit: number | null;
  prior_year_label: number | null;
  current_year_ytd_profit: number | null;
  current_year_ytd_through_month: string | null;
}

export interface RealtyDashboardResponse {
  generated_at: string;
  volume_pulse: RealtyVolumePulse;
  sub_type_mix: RealtySubTypeSlice[];
  representing_mix: RealtyRepresentingSlice[];
  agent_performance: RealtyAgentRow[];
  pipeline: RealtyPipelineCounts;
  revenue_and_profit: RealtyRevenueAndProfit | null;
}

export interface InsuranceDashboardResponse {
  generated_at: string;
  book_of_business: {
    total_premium_annualized: number;
    active_policies: number;
    households: number;
    avg_premium_per_policy: number;
    policies_per_household: number;
    premium_per_household: number;
  };
  premium_by_carrier: Array<{
    carrier_canonical_slug: string;
    carrier_display_name: string;
    premium: number;
    policies: number;
    mix_pct: number;
    retention_pct: number | null;
    /** Trailing-12-mo commission revenue in dollars. Null when no
     *  QB account is mapped to this carrier slug (the dashboard
     *  renders "—"). EXCLUDES bonus income per Grant 2026-05-14 —
     *  commission income only. */
    revenue_ttm: number | null;
    /** revenue_ttm / policies. Null when revenue_ttm is null or
     *  policies is zero. */
    revenue_per_policy: number | null;
    /** revenue_ttm / premium × 100. Null when revenue_ttm is null
     *  or premium is zero. */
    revenue_per_premium_pct: number | null;
  }>;
  carriers_total: number;
  policy_type_mix: Array<{
    policy_type: string;
    policies: number;
    premium: number;
  }>;
  revenue_and_profit: {
    monthly_series: InsuranceYoYMonthEntry[];
    last_full_month: InsuranceDashboardQbMonth | null;
    /** Total operating profit for previous calendar year (sum of Jan-Dec
     *  net_income). Null when any month is missing a QB snapshot. */
    prior_year_total_profit: number | null;
    /** The calendar year label, e.g. 2025. */
    prior_year_label: number | null;
    /** Current calendar year YTD operating profit, full months only
     *  (Jan through last fully-completed month). Null when any month
     *  in the window is missing. */
    current_year_ytd_profit: number | null;
    /** YYYY-MM of the last fully-completed month in the YTD window,
     *  e.g. "2026-04". Null in the early-January edge case. */
    current_year_ytd_through_month: string | null;
  };
  growth: {
    new_business_premium_mtd: number;
    new_households_mtd: number;
    quote_to_bind_ttm_pct: number | null;
    quote_to_bind_ttm_counts: { quoted: number; bound: number } | null;
    quote_to_bind_lifetime_pct: number | null;
    quote_to_bind_lifetime_counts: { quoted: number; bound: number } | null;
  };
  retention: {
    agency_retention_lifetime_pct: number | null;
    carrier_retention_lifetime_pct: number | null;
    lost_premium_lifetime: number;
    agency_retention_ttm_os_pct: number | null;
    carrier_retention_ttm_os_pct: number | null;
    lost_premium_ttm_os: number;
    ttm_os_cancellation_count: number;
    household_retention_lifetime_pct: number | null;
    households_active: number;
    households_lost_lifetime: number;
    household_retention_ttm_os_pct: number | null;
    households_lost_ttm_os: number;
  };
  awaiting_history: {
    mom_pp_changes: null;
    yoy_pp_changes: null;
  };
}

// ─── Referral Network (Phase 4, 2026-07-18) ────────────────────────────
export interface ReferralNetworkWindow {
  volume: { source_type: string | null; count: number }[];
  revenue: {
    source_type: string | null;
    company_slug: string;
    won_count: number;
    revenue_year_1: number;
    lifetime_revenue: number;
  }[];
}
export interface ReferralNetworkCrossCompanyFlow {
  from_company_slug: string;
  from_company_name: string;
  to_company_slug: string;
  to_company_name: string;
  opportunity_count: number;
  won_count: number;
  revenue_year_1: number;
}
export interface ReferralNetworkReferrerRow {
  user_id: string;
  user_name: string | null;
  opportunity_count: number;
  won_count: number;
  revenue_year_1: number;
}
/** Row 4.6: the report every employee reads for their own companies.
 *  The firm-wide pieces (volume, revenue, flow, the leaderboard) come
 *  narrowed to the viewer's scope; the scoped pieces are
 *  ReferralNetworkScoped (shared/db/referral-network.ts). */
export interface ReferralNetworkReport extends ReferralNetworkScoped {
  generated_at: string;
  windows: {
    month: ReferralNetworkWindow;
    quarter: ReferralNetworkWindow;
    year: ReferralNetworkWindow;
  };
  cross_company_flow: ReferralNetworkCrossCompanyFlow[];
  /** Empty for a company viewer: only the firm viewer sees colleagues ranked. */
  referrer_leaderboard: ReferralNetworkReferrerRow[];
  firm_viewer: boolean;
}

export interface FinancialFirmDashboardResult {
  as_of: string;
  summary: FinancialFirmDashboardSummary;
  by_advisor: FinancialAdvisorBreakdown[];
  top_households: FinancialTopHousehold[];
  underpenetrated_households: FinancialUnderpenetratedHousehold[];
  snapshots_by_source: FinancialSnapshotsBySource[];
  recent_snapshots: FinancialRecentSnapshot[];
  reconciliation_reference_sources: string[];
  // QB revenue + profit YoY — same shape as the Insurance dashboard's
  // (mirrors shared/db/qb-revenue-profit.ts). Grant 2026-05-16.
  revenue_and_profit: {
    monthly_series: InsuranceYoYMonthEntry[];
    last_full_month: InsuranceDashboardQbMonth | null;
    prior_year_total_profit: number | null;
    prior_year_label: number | null;
    current_year_ytd_profit: number | null;
    current_year_ytd_through_month: string | null;
  };
  // Household retention — active (Schwab/Betterment SoT) /
  // (active + lost-Financial-deal-with-no-active-portfolio).
  household_retention: {
    retention_pct: number | null;
    households_active: number;
    households_lost: number;
  };
}

// Boolean-quarantine queue row.
export interface BooleanQuarantineQueueItem {
  household_id: string;
  household_display_name: string;
  primary_city: string | null;
  primary_state: string | null;
  household_created_at: string;
  quarantine_reason: string | null;
  contact_count: number;
}

export interface BooleanQuarantineQueueResult {
  total: number;
  limit: number;
  offset: number;
  items: BooleanQuarantineQueueItem[];
}

export type BooleanQuarantineSort = "oldest_first" | "newest_first" | "name_az" | "name_za";

// Spouse-review queue row. Mirrors SpouseReviewQueueItem in
// shared/db/data-hygiene.ts.
export interface SpouseReviewQueueItem {
  household_id: string;
  household_display_name: string;
  primary_city: string | null;
  primary_state: string | null;
  household_created_at: string;
  contact_id: string;
  contact_legal_first_name: string | null;
  contact_legal_last_name: string | null;
  contact_preferred_name: string | null;
  contact_email: string | null;
  contact_phone_primary: string | null;
}

export interface SpouseReviewQueueResult {
  total: number;
  limit: number;
  offset: number;
  items: SpouseReviewQueueItem[];
}

export type SpouseReviewSort = "oldest_first" | "newest_first" | "name_az" | "name_za";

// /api/admin/data-hygiene/summary response. Mirrors DataHygieneSummary
// in shared/db/data-hygiene.ts.
export interface DataHygieneSummary {
  quality_flags: {
    quarantined_households: number;
    households_without_primary: number;
    households_with_only_one_contact: number;
    contacts_without_household: number;
    contacts_with_no_name: number;
    person_contacts_looking_like_vendor: number;
    duplicate_candidate_pairs: number;
    bad_data_alerts: number;
    stale_contacts: number;
    spouse_pass_ambiguous_pending: number;
  };
  recent_activity_7d: {
    field_edits: number;
    merges: number;
    archives: number;
    restores: number;
    add_members: number;
  };
  database_scale: {
    active_contacts: number;
    active_households: number;
    quarantined_households: number;
    archived_contacts: number;
    archived_households: number;
    merged_contacts: number;
  };
  generated_at: string;
}

// ─── request helpers ────────────────────────────────────────────────────

// Errors get a normalized shape so callers can check status + error code.
// ─── Marketing settings (row 2.7b) ────────────────────────────────────
export interface IncomeSetting {
  company_slug: string;
  service_key: string;
  label: string;
  /** Row 7.12b: the comp table's owner (null: Grant's). */
  comp_owner?: string | null;
  /** Row 7.12b: whether this viewer may override this row (Grant, system_admin, or its comp owner). */
  can_override?: boolean;
  income: IncomeSource;
  row: {
    id: string;
    income_y1: number | null;
    renewal_per_year: number | null;
    annual_retention: number | null;
    expected_years: number | null;
    lifetime_override: number | null;
    source: string;
    reason: string | null;
    effective_from: string;
    set_by_user_id: string | null;
  } | null;
  pending: { effective_from: string; income_y1: number | null; lifetime: number | null } | null;
}
/** Row 5.0: Connected accounts (screen 64) and the firm's claims. */
export interface ConnectedAccount {
  id: string;
  platform: string;
  username: string | null;
  display_name: string | null;
  profile_url: string | null;
  is_personal: boolean;
  owner_user_id: string | null;
  /** Row 5.6: the owner's name when a personal account is an advisor's own. */
  owner_name?: string | null;
  /** Dead since S5-13: an account is never a company's. */
  company_id: string | null;
  /** S5-13: whose the account is; "unset" until Grant says. */
  scope: "unset" | "personal" | "firm";
  vendor_status: "healthy" | "needs_reconnection" | "inactive" | "missing";
  followers: number | null;
  disconnected_at: string | null;
  disconnect_reason: string | null;
  scheduled_pieces: number;
  held_pieces: number;
}
export interface FirmClaim {
  id: string;
  slug: string | null;
  purpose: "claim" | "footer" | "regulatory";
  kind: string;
  template: string;
  text: string | null;
  values: Record<string, string | number | null>;
  held_reason: string | null;
  substantiation: string | null;
  source: string | null;
  permitted_companies: string[];
  permitted_channels: string[];
  status: "draft" | "approved" | "expired" | "retired";
  approved_at: string | null;
  approved_by_user_id: string | null;
  version_number: number;
  as_of: string;
}
/** Row 5.1: the photo library picker's reads. */
export interface MediaLibraryPhoto {
  id: string;
  name: string;
  folder_path: string;
  mime_type: string | null;
  width: number | null;
  height: number | null;
  taken_at: string | null;
  modified_at: string | null;
  web_url: string | null;
  /** "OK for marketing": set by Grant or a company admin; nothing goes out with a photo that is not marked. */
  marketing_ok: boolean;
  marketing_ok_at: string | null;
  marketing_ok_by: string | null;
  marketing_flag_version: number;
}
export type PhotoFlagFilter = "all" | "ok" | "not_ok";
export interface PhotoBulkFlagResult {
  bulk_action_id: string;
  changed: number;
  unchanged: number;
  skipped: number;
  undo_until: string;
}
export interface MediaLibraryResponse {
  connected: boolean;
  problem: string | null;
  photos: number;
  /** Photos marked OK for marketing: a COUNT over the whole library, not this page. */
  flagged: number;
  /** Whether the viewer may mark photos (the server checks again on every press). */
  may_flag: boolean;
  last_indexed_at: string | null;
  total: number;
  items: MediaLibraryPhoto[];
  limit: number;
  offset: number;
}
/** Row 8.1: an upload or a made image the OS holds (modal 37's write half). */
export interface MarketingMediaItem {
  id: string;
  source: "upload" | "made";
  media_type: "image" | "video";
  name: string;
  content_type: string;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  created_at: string;
  yours: boolean;
  use_words: string;
}
/** Row 11.10: the clip maker's opening read. */
export interface ClipsResponse {
  clips: ClipView[];
  companies: { slug: ClipCompany; word: string; caption_style: string | null }[];
  cutting: boolean;
  captions: boolean;
  record_max_seconds: number;
  ceiling: { per_day: number; made_today: number };
}

/** Row 11.10: Grant's caption styles on Marketing settings. */
export interface CaptionStylesResponse {
  configured: boolean;
  offered: { id: string; name: string; previewUrl: string | null }[];
  problem: string | null;
  companies: { slug: ClipCompany; word: string; template_id: string | null; template_name: string | null; set_at: string | null }[];
}

export interface MarketingMediaPage {
  tab: "yours" | "made";
  items: MarketingMediaItem[];
  total: number;
  hidden_reason: string | null;
}
/** Where a picked or made image is going; a made image never goes into an ad or a listing. */
export type MediaContext = "browse" | "email" | "post" | "blog" | "ad" | "listing";
export type PhotoIndexResult =
  | { ok: true; listed: number; foldersWalked: number; truncated: boolean; write: { inserted: number; updated: number; removed: number; live: number }; ms: number }
  | { ok: false; reason: string; message: string; ms: number };
/** Row 5.2: Founder Studio's source and its package (modal 33, screen 6). */
export interface StudioSource {
  id: string;
  kind: "voice" | "video" | "story" | "question_theme" | "existing_piece";
  title: string | null;
  transcript_text: string | null;
  /** How many timed segments the transcription gave (clip cut-points). */
  segments: number;
  /** Row 11.10: the video is kept with the source, so "Make a clip" can cut it. */
  video_kept?: boolean;
  extracted_story: string | null;
  extracted_lesson: string | null;
  services_touched: string[];
  claims_used: string[];
  edited_source_text: string | null;
  edited_at: string | null;
  only_our_facts: boolean;
  status: "open" | "packaged" | "archived";
  created_at: string;
  updated_at: string;
}

export type StudioDerivativeKind =
  | "personal_post"
  | "short_post"
  | "digest_section"
  | "blog_or_faq"
  | "clip_cut_points"
  | "advisor_share"
  | "business_profile_update"
  | "older_pieces_to_link";

export interface StudioAsset {
  id: string;
  derivative_kind: StudioDerivativeKind | null;
  label: string;
  brief: string | null;
  model: string | null;
  version: number;
  body: string | null;
  edited_body: string | null;
  edited_at: string | null;
  edited_by_user_id: string | null;
  accepted_at: string | null;
  held_reason: string | null;
  held_sentence: string | null;
  state: "draft" | "held" | "accepted" | "published" | "discarded";
  created_at: string;
}

export interface StudioStew {
  available: boolean;
  reason: "not_configured" | "out_for_today" | null;
  message: string | null;
  resets_at: string | null;
}

export type StudioMemoryKind = "accepted_edit" | "removed_phrase" | "preferred_structure" | "productive_subject" | "not_my_voice";
export type StudioRerunOption = "warm" | "story_first" | "rewrite" | "shorter" | "different_angle";
export type StudioProducedGroup = "your_accounts" | "stewardship_pages" | "blog" | "email" | "ads";

export interface StudioPost {
  id: string;
  asset_id: string | null;
  company_id: string | null;
  /** S5-13: the companies the post speaks for; empty is Stewardship as a whole. */
  company_slugs: string[];
  /** "Stewardship as a whole", "Stewardship Mortgage", "Mortgage and Realty"; null on a personal post. */
  scope_words: string | null;
  is_personal: boolean;
  social_account_id: string;
  /** Row 5.4: the account named, so a row reads "@grantbotma · instagram". */
  platform: string | null;
  handle: string | null;
  rendered_text: string;
  media_item_id: string | null;
  marketing_media_id?: string | null;
  media_name: string | null;
  footer_claim_id: string | null;
  piece_state: "draft" | "approval" | "scheduled" | "sent" | "failed" | "held";
  held_reason: string | null;
  failure_reason: string | null;
  approved_at: string | null;
  scheduled_at: string | null;
  published_at: string | null;
  platform_post_url: string | null;
  vendor_post_id: string | null;
  publish_attempts: number;
  last_attempted_at: string | null;
  deleted_from_platform_at: string | null;
  version_number: number;
  created_at: string;
  updated_at: string;
}

/** Row 5.4: one of Grant's own connected accounts, the targets of a personal post. */
/** Row 5.5: the unified inbox (screen 20), one read. */
export interface MarketingInboxUnattributed {
  id: string;
  kind: "comment" | "message";
  platform: string;
  author_handle: string | null;
  author_name: string | null;
  text: string;
  word: string;
  url: string | null;
  happened_at: string;
  is_question: boolean;
  post_snippet: string | null;
}
export interface MarketingInboxResponse {
  firm_viewer: boolean;
  scope: { view: "mine" | "company" | "firm"; companies: string[] | null; label: string; can_see_firm: boolean };
  counts: { open: number; overdue: number };
  histogram: { buckets: { key: string; label: string; count: number }[]; answered: number; caption: string; since: string };
  items: WaitingItem[];
  total: number;
  unattributed: { items: MarketingInboxUnattributed[]; total: number };
  sources: { id: string; platform: string; handle: string | null; read_at: string | null; problem: string | null }[];
  last_handled_at: string | null;
}

export interface StudioPersonalAccount {
  id: string;
  platform: string;
  handle: string;
  disconnected: boolean;
  needs_reconnection: boolean;
  /** The platform refuses a post with no picture. */
  needs_photo: boolean;
}

/** Row 5.4: a publish's answer: published, scheduled, or the vendor's refusal. */
export interface StudioPublishResponse {
  ok?: true;
  outcome?: "published" | "scheduled";
  message: string;
  post: StudioPost | null;
}

export interface StudioPostVersion {
  id: string;
  post_id: string;
  version_n: number;
  prior_version_id: string | null;
  event: "created" | "edited" | "approved" | "scheduled" | "unscheduled" | "publish_requested" | "published" | "publish_failed" | "held" | "deleted_from_platform";
  snapshot: unknown;
  snapshot_hash: string;
  prior_hash: string | null;
  reason_text: string | null;
  actor_user_id: string | null;
  source: "founder" | "scheduler" | "system";
  retain_until: string;
  created_at: string;
}

export interface FounderStudioResponse {
  source: StudioSource | null;
  assets: StudioAsset[];
  kinds: { kind: StudioDerivativeKind; label: string }[];
  recent: { id: string; kind: StudioSource["kind"]; title: string | null; status: StudioSource["status"]; created_at: string }[];
  claims_approved: number;
  stew: StudioStew;
  /** Row 5.3: screen 6's header counts, each a COUNT over one WHERE. */
  counts: { sources_week: number; pieces: number; conversations: number; hand_raises: number; week_start: string; since: string };
  /** Row 5.3: "What your content produced", from content_performance (row 5.5 writes it). */
  produced: { since: string; rows: { group: StudioProducedGroup; conversations: number; hand_raises: number; outcomes: number; pieces: number }[]; performance_rows: number; as_of: string | null };
  memory: { total: number; groups: { kind: StudioMemoryKind; label: string; items: { id: string; text: string; observed_at: string; asset_id: string | null }[] }[] };
  /** Row 5.5: comments and messages on Grant's own posts; `state` is the hand-raise's, or `touch` (no reply needed) or `unattributed` (nobody has linked the handle; the inbox's "This is …" does). */
  engagement: { since: string; items: { id: string; kind: string; arrived_at: string; state: "open" | "handled" | "touch" | "unattributed"; household_id: string | null; household_name: string | null; author_handle: string | null; owner_user_id: string | null; post_id: string; post_snippet: string }[] };
  themes: { id: string; title: string | null; transcript_text: string | null; theme_source_kinds: string[]; theme_count: number | null; theme_week_start: string | null; created_at: string }[];
  options: { option: StudioRerunOption; label: string }[];
  /** S5-13: Stewardship's own pages, one per network; the companies a cut speaks for are ticked at the cut. */
  stewardship_accounts: { id: string; platform: string; handle: string }[];
  companies: { slug: string; name: string }[];
  /** Row 5.4: Grant's own accounts. */
  personal_accounts: StudioPersonalAccount[];
  posts: StudioPost[];
  /** Row 5.4: the posts that need Grant (failed with the vendor's reason, or held with the OS's). */
  attention: { total: number; items: StudioPost[] };
  publishing: { vendor_configured: boolean; photo_library_connected: boolean; public_origin_set: boolean };
  rules: { personal_no_queue: string };
}

export interface TranscribedSegment {
  start: number;
  end: number;
  text: string;
}

/** Row 5.6: one advisor share package as the queue prints it. */
export interface SharePackage {
  asset_id: string;
  source_id: string;
  title: string | null;
  body: string;
  company_slug: string;
  company_words: string;
  accepted_at: string | null;
  version: number;
  shares: number;
  people: number;
  my_last_shared_at: string | null;
  my_last_channel: "copied" | "linkedin" | null;
}
export interface SharePackagesResponse {
  scope: { companies: string[] | null; label: string };
  packages: SharePackage[];
  total: number;
  linkedin: { account_id: string; handle: string; needs_reconnection: boolean } | null;
}
/** Row 5.7: the Results Content tab. */
export interface ContentAccountRow {
  social_account_id: string;
  platform: string;
  handle: string | null;
  is_personal: boolean;
  owner_user_id: string | null;
  owner_name: string | null;
  pieces: number;
  reach: number | null;
  clicks: number;
  conversations: number;
  hand_raises: number;
  outcomes: number;
  best_post_id: string | null;
  best_post_snippet: string | null;
  best_post_conversations: number;
  last_post_at: string | null;
  as_of: string | null;
  read_problem: string | null;
}
export interface ContentTabResponse {
  scope: { companies: string[] | null; label: string; sees_every_account: boolean };
  window: { days: number; since: string };
  header: { accounts: number; pieces: number; as_of: string | null; connected?: number };
  rows: ContentAccountRow[];
  what_works: { id: string; platform: string; feature: string; feature_word: string; pattern: string; pattern_word: string; lift: number; sample: number; baseline_sample: number; pinned: boolean }[];
  /** Row 8.7: the blog posts and emails of the window with their results. */
  blog_rows: { id: string; title: string; company_slug: string | null; published_at: string; published_url: string | null; clicks: number; bookings: number; forms: number; outcomes: number; results_as_of: string }[];
  email_rows: { id: string; subject: string; company_slug: string | null; sent_at: string; recipients: number; replies: number; clicks: number; bookings: number; forms: number; outcomes: number }[];
  /** Row 9b.7: the window's search campaigns and boosts; Adjust (modal 45) is Grant's. */
  ad_rows: PaidPieceResult[];
  can_adjust: boolean;
  unattributed: { social: number; bookings: number; forms: number };
  heard: { rows: { answer: string; bookings: number; forms: number }[]; forms_read: number; forms_total: number };
  asks: { personal_networks_missing: string[]; claims_not_approved: number; is_founder: boolean };
}
/** Row 8.8: one row of the search opportunity list (shared/marketing/search-demand.ts). */
export interface SearchDemandRow {
  key: string;
  company: "insurance" | "mortgage" | "planning" | "realty" | "financial";
  company_word: string;
  service_key: string;
  title: string;
  city: string | null;
  demand_month: number;
  trend_word: string | null;
  position: number;
  ctr: number;
  weak_ctr: boolean;
  gain_clicks_month: number;
  value_month: number | null;
  value_why: string | null;
  value_line: string;
  income_y1: number | null;
  product_word: string;
  room_word: string;
  level: string;
  verdict: "act" | "hold";
  verdict_why: string | null;
  line: string;
  top_page: string | null;
  phrases: number;
  top_phrases: string[];
  in_scope: boolean;
  can_write: boolean;
  blog_post: { id: string; title: string } | null;
}
export interface SearchConsoleRead {
  read_at: string;
  outcome: "ok" | "not_connected" | "refused" | "error";
  rows_written: number;
  possibly_truncated: boolean;
  sentence: string;
}
export interface SearchConsoleCounts {
  days: number;
  phrases: number;
  pages: number;
  impressions: number;
  clicks: number;
  first_day: string | null;
  last_day: string | null;
}
export interface SearchDemandResponse {
  rows: SearchDemandRow[];
  rows_shown: number;
  min_impressions: number;
  brand_impressions: number;
  other_impressions: number;
  window: { start: string; end: string };
  counts: SearchConsoleCounts;
  read: SearchConsoleRead | null;
  configured: boolean;
  scope: "all" | string[];
}
/** Row 8.9: one Google review as the Reviews page reads it. */
export interface GoogleReviewRow {
  id: string;
  company_id: string | null;
  company_slug: string | null;
  company_name: string | null;
  rating_stars: number | null;
  body_text: string | null;
  reviewer_name: string | null;
  source_url: string | null;
  submitted_at: string;
  household_id: string | null;
  household_name: string | null;
  contact_id: string | null;
  match_rule: string | null;
  platform_reply_text: string | null;
  platform_reply_at: string | null;
  social_account_id: string | null;
  reply_id: string | null;
  reply_state: "draft" | "approval" | "sent_back" | "sent" | "failed" | null;
  reply_text: string | null;
  reply_version: number | null;
  reply_drafted_by_user_id: string | null;
  reply_problem: string | null;
  reply_sent_at: string | null;
  inbox_item_id: string | null;
  can_reply: boolean;
  cannot_reply_because: string | null;
  can_take_back: boolean;
  who_answers: string | null;
}
export interface BusinessProfileHealth {
  account: {
    id: string;
    username: string | null;
    display_name: string | null;
    location_name: string | null;
    reviews_read_at: string | null;
    reviews_read_problem: string | null;
    vendor_is_active: number;
    needs_reconnection: number;
  } | null;
  reviews: number;
  unreplied: number;
  average_rating: number | null;
  month: { month: string; review_count: number; average_rating: number | null; as_of: string; keywords: boolean } | null;
}
export interface GoogleReviewsResponse {
  rows: GoogleReviewRow[];
  shown: number;
  total: number;
  concerns: number;
  unreplied: number;
  profile: BusinessProfileHealth;
  scope: "all" | string[];
  reply_limit: number;
  reply_comfort: number;
  financial_sentence: string;
}
export interface ReviewReplyRow {
  id: string;
  review_id: string;
  state: "draft" | "approval" | "sent_back" | "sent" | "failed";
  text: string;
  version_number: number;
  submitted_at: string | null;
  sent_at: string | null;
  problem: string | null;
}
/** Row 8.11: one mail or card task on the Mail work list. */
export interface MailTaskRow {
  send_id: string;
  task_id: string;
  kind: "mail" | "card";
  kind_word: string;
  done_word: string;
  household_id: string;
  household_name: string;
  person_name: string | null;
  company_slug: string;
  company_name: string;
  series_name: string;
  piece_position: number;
  assignee_user_id: string;
  assignee_name: string;
  due_date: string | null;
  description: string;
  address: string | null;
  returned_reason: "returned" | "vacant" | null;
  returned_word: string | null;
  link: string | null;
  mailed_at: string | null;
  can_undo: boolean;
}
/** Row 8.12: a household's video messages, as the tab reads them. */
export interface VideoRecipientView {
  id: string;
  contact_id: string;
  person_name: string;
  sent_at: string | null;
  first_played_at: string | null;
  play_count: number;
  /** "Not watched yet" / "Watched once" / "Watched 3 times": a hint, never a reply. */
  hint: string;
}
export interface VideoMessageView {
  id: string;
  company_id: string;
  advisor_user_id: string;
  advisor_name: string | null;
  duration_seconds: number;
  state: "recorded" | "sent";
  subject: string | null;
  message_text: string | null;
  sent_at: string | null;
  created_at: string;
  mine: boolean;
  can_send: boolean;
  recipients: VideoRecipientView[];
}
export interface HouseholdVideoResponse {
  household: { id: string; name: string };
  videos: VideoMessageView[];
  /** The household's companies on the viewer's desk: the ones they may record for. */
  can_record: { id: string; slug: string; name: string }[];
  people: { contact_id: string; name: string; first_name: string; is_primary: boolean; emailable: boolean; reason: string | null; notes?: string[] }[];
  outlook_connected: boolean;
  advisor_first_name: string;
  max_seconds: number;
  never_sentence: string;
}
export interface MailTaskListResponse {
  rows: MailTaskRow[];
  open: number;
  mailed_30d: number;
  /** Thank-you cards marked mailed on the thank-you screens, counted apart from the tasks. */
  thank_you_cards_30d: number;
  scope: "mine" | "everyone";
  scope_word: string;
  undo_seconds: number;
  no_vendor_sentence: string;
  return_reasons: { key: "returned" | "vacant"; word: string }[];
  is_founder: boolean;
}
/** Row 8.10: an event as the Events pages read it. */
export interface MarketingEventRow {
  id: string;
  company_id: string;
  company_slug: string;
  company_name: string;
  kind: "seminar" | "webinar" | "client_dinner" | "class" | "workshop";
  kind_word: string;
  title: string;
  description: string | null;
  starts_at: string;
  place: string;
  seats: number;
  presenter_user_id: string;
  presenter_name: string | null;
  service_key: string | null;
  cash_cost: number;
  campaign_id: string | null;
  registration_code: string;
  state: "planned" | "open" | "closed" | "cancelled";
  state_word: string;
  version_number: number;
}
export interface EventFunnel {
  invited: number;
  registered: number;
  waitlisted: number;
  attended: number;
  consultations: number;
  seats_left: number;
  taken: number;
}
export interface EventsListResponse {
  upcoming: (MarketingEventRow & { funnel: EventFunnel; can_save: boolean })[];
  past: (MarketingEventRow & { funnel: EventFunnel; can_save: boolean })[];
  scope: "all" | string[];
  can_create: string[];
  kinds: { key: string; word: string }[];
  products: Record<string, { key: string; label: string }[]>;
  financial_line: string;
  no_product_for_financial: string;
}
export interface EventRegistration {
  id: string;
  event_id: string;
  contact_id: string;
  household_id: string;
  household_name: string | null;
  person_name: string | null;
  email: string | null;
  guests: number;
  state: "registered" | "waitlisted" | "cancelled";
  source: "public_page" | "advisor";
  registered_at: string;
  checked_in_at: string | null;
  cancelled_at: string | null;
}
export interface EventPlanRow {
  role: "invitation" | "reminder" | "follow_up";
  series: CampaignSeries & { event_role: string | null };
  piece: SeriesPiece | null;
  families: number | null;
  audience_name: string | null;
  queue: QueueState | null;
}
export interface EventBriefView {
  registered: number;
  attended: number;
  consultations: number;
  clients: number;
  income_y1: number | null;
  income_lifetime: number | null;
  cash_cost: number;
  net_lifetime: number | null;
  evidence: "assumed" | "actual";
  lines: string[];
}
export interface EventScreen {
  event: MarketingEventRow;
  campaign_id: string | null;
  funnel: EventFunnel;
  plan: EventPlanRow[];
  registrations: EventRegistration[];
  brief: EventBriefView;
  rates: { register: number; attend: number; consult: number; client: number };
  public_url: string;
  can_save: boolean;
  can_door: boolean;
  financial_line: string | null;
  runner_on: boolean;
}
/** Row 9a.3: one provider connection as the screen sees it (never a token). */
/** Row 9b.2: the ad builder's screen (spec 17). */
/** S11-29: a published post's boost, as its row in "Where it went" shows it; the figures are Results' own. */
export interface PostBoost {
  ad_id: string;
  href: string;
  state_word: string;
  since: string | null;
  spend: number | null;
  spend_as_of: string | null;
  budget_words: string | null;
  hand_raises: number | null;
}

export interface AdBuilderResponse {
  piece: {
    id: string;
    company_slug: "mortgage" | "insurance" | "planning" | "realty" | "financial";
    /** null for a new ad (written for the ad, not boosted from a post). */
    post_id: string | null;
    goal: "reach" | "traffic" | "engagement";
    center_address: string | null;
    radius_miles: number;
    budget_total_dollars: number | null;
    starts_on: string | null;
    ends_on: string | null;
    exclude_current_clients: number;
    piece_state: "draft" | "approval" | "approved" | "taken_back";
    failure_reason: string | null;
    sent_back_note: string | null;
    reach_lower: number | null;
    reach_upper: number | null;
    reach_read_at: string | null;
    submitted_at: string | null;
    approved_at: string | null;
    version_number: number;
  };
  /** S11-29: the network the boosted post is on. */
  network: "facebook" | "instagram";
  /** S11-31: the Instagram handle the preview draws, or null when none is connected. */
  instagram_handle: string | null;
  post: { id: string; text: string; url: string | null; picture: { source: "team" | "upload"; id: string } | null };
  author_name: string | null;
  category: string | null;
  category_word: string;
  min_radius: number;
  checks: { key: string; ok: boolean; blocking: boolean; text: string; small?: string }[];
  blocking: string | null;
  cap: { company: string; remaining: number | null; cap: number | null; words: string };
  exclusion: { holds_members: boolean; sent: number | null } | null;
  outbox: { state: string; problem: string | null } | null;
  ad_set: { state: string | null; status: string | null } | null;
  can_edit: boolean;
  can_send: boolean;
  can_take_back: boolean;
  can_approve: boolean;
  can_manage_running: boolean;
  /** Row 11.9b: the batch's versions (empty for a single boost), the landing page they link to, and the post's own first line. */
  versions: VersionView[];
  landing_page: { id: string; title: string; url: string | null } | null;
  post_first_line: string;
  landing_choices: { id: string; title: string; url: string }[];
  /** "Write a new ad": the service, the shared words and the company's approved footer; null for a boosted post. */
  new_ad: { product_family: string; product_label: string; body: string; footer: string | null } | null;
}

/** Row 11.9b: one Meta version as the builder saves it. */
export interface MetaVersionBody {
  hook: string;
  first_line: string;
  media_item_id: string | null;
  marketing_media_id: string | null;
}

export interface AdDraftBody {
  goal: "reach" | "traffic" | "engagement";
  center_address: string | null;
  radius_miles: number;
  budget_total_dollars: number | null;
  starts_on: string | null;
  ends_on: string | null;
  exclude_current_clients: boolean;
  expected_version_number: number;
}

/** Row 9b.3: the Google search campaign builder's screen (spec 18). */
export interface SearchKeywordView {
  text: string;
  source: "search_console" | "landing" | "desk";
  impressions: number | null;
  monthly_searches: number | null;
}
export interface SearchBuilderResponse {
  piece: {
    id: string;
    company_slug: "mortgage" | "insurance" | "planning" | "realty";
    landing_page_id: string;
    title: string;
    monthly_dollars: number | null;
    starts_on: string | null;
    ends_on: string | null;
    step_up_after_days: number | null;
    step_up_to_daily_dollars: number | null;
    step_up_if_cost_per_ask_under: number | null;
    stop_after_outcomes: number | null;
    stop_if_cost_per_outcome_over: number | null;
    exclude_current_clients: number;
    piece_state: "draft" | "approval" | "approved" | "taken_back";
    failure_reason: string | null;
    sent_back_note: string | null;
    volumes_read_at: string | null;
    volumes_words: string | null;
    forecast_clicks: number | null;
    forecast_cost_dollars: number | null;
    forecast_read_at: string | null;
    forecast_words: string | null;
    submitted_at: string | null;
    approved_at: string | null;
    stepped_up_at: string | null;
    version_number: number;
  };
  keywords: SearchKeywordView[];
  headlines: string[];
  descriptions: string[];
  cities: string[];
  all_cities: string[];
  never_for: { id: string; phrase: string }[];
  landing: { id: string; title: string; url: string | null; campaign_id: string | null; blog_post_id: string | null };
  author_name: string | null;
  licence_line: string | null;
  daily_dollars: number | null;
  checks: { key: string; ok: boolean; blocking: boolean; text: string; small?: string }[];
  blocking: string | null;
  cap: { company: string; remaining: number | null; cap: number | null; words: string };
  exclusion: { possible: boolean; allowed: boolean; holds_members: boolean; sent: number | null };
  outbox: { state: string; problem: string | null } | null;
  campaign: { state: string | null; status: string | null } | null;
  performance: { spend: number; asks: number; outcomes: number; days_running: number; stop_reached: boolean; step_up_reached: boolean; cost_per_ask: number | null; cost_per_outcome: number | null } | null;
  can_edit: boolean;
  can_send: boolean;
  can_take_back: boolean;
  can_approve: boolean;
  can_manage_running: boolean;
  can_edit_never_for: boolean;
  /** Row 11.9b: the batch's three versions (empty for a single ad). */
  versions: VersionView[];
}

export interface SearchDraftBody {
  title: string;
  keywords: { text: string; source: "search_console" | "landing" | "desk" }[];
  headlines: string[];
  descriptions: string[];
  cities: string[];
  monthly_dollars: number | null;
  starts_on: string | null;
  ends_on: string | null;
  step_up_after_days: number | null;
  step_up_to_daily_dollars: number | null;
  step_up_if_cost_per_ask_under: number | null;
  stop_after_outcomes: number | null;
  stop_if_cost_per_outcome_over: number | null;
  exclude_current_clients: boolean;
  expected_version_number: number;
}

/** Row 9b.4: Realty's Local Services leads and ad, as Connected accounts shows them (counts and household names, never a lead's details). */
/** Row 9b.6: Meta lead forms as Connected accounts shows them (counts and states, never a person's answers). */
export interface MetaLeadsView {
  can_act: boolean;
  callback_path: string;
  verify_token: string | null;
  secret_present: boolean;
  page: { id: string | null; name: string | null; subscribed_at: string | null; problem: string | null };
  last_delivery_at: string | null;
  last_refusal: { at: string | null; reason: string | null };
  forms: { form_id: string; name: string | null; status: string | null; company_slug: string | null }[];
  counts: { done: number; failed: number; unassigned: number };
  recent: { id: string; received_at: string; company_slug: string | null; household_id: string | null; match_status: string | null; state: string; problem: string | null }[];
}

/** Row 9b.5: conversion feedback as Connected accounts shows it (counts and states, never an identifier). */
export interface ConversionFeedbackView {
  can_act: boolean;
  switches: Record<"meta" | "google_ads", { enabled: boolean; changed_at: string | null; changed_by_name: string | null; rule_ok: boolean; rule_sentence: string }>;
  meta_dataset_id: string | null;
  google_accounts: { provider_account_id: string; company_slug: string; conversion_action_id: string | null }[];
  counts: { provider: "meta" | "google_ads"; state: string; n: number }[];
  recent: { id: string; provider: "meta" | "google_ads"; company_slug: string; household_id: string; touch_kind: "search_ask" | "ad_comment" | "meta_lead"; event_time: string; state: string; problem: string | null; value_cents: number | null }[];
}

export interface LocalServicesView {
  connected: boolean;
  owner: { id: string; name: string | null } | null;
  owner_is_named: boolean;
  owner_choices: { id: string; name: string | null }[];
  leads_since: string | null;
  leads_read_at: string | null;
  problem: string | null;
  /** The state read-back: when Google's lead states were last kept, and why not when they were not. */
  states_read_at: string | null;
  states_problem: string | null;
  counts: { live: number; history: number; matched: number; created: number; unmatched: number; failed: number };
  recent: { id: string; day: string; word: string; match_status: string; household_id: string | null; household_name: string | null; problem: string | null; hand_state: string | null }[];
  campaign: { object_id: string; status: string | null; state: string; budget_dollars: number | null; budget_period: string | null } | null;
  can_act: boolean;
}

/** Row 9b.1: one audience list as a screen sees it: counts and the provider's words, never a member. */
export interface AdListView {
  id: string;
  provider: "meta" | "google_ads";
  company_slug: "mortgage" | "insurance" | "realty";
  purpose: "exclusion" | "inclusion";
  audience_id: string | null;
  audience_name: string | null;
  provider_account_id: string;
  has_provider_list: boolean;
  enabled: boolean;
  enabled_at: string | null;
  sending: boolean;
  last_run_at: string | null;
  last_outcome: "synced" | "emptied" | "skipped" | "refused" | "interrupted" | null;
  last_problem: string | null;
  last_source_count: number | null;
  last_sent_count: number | null;
  provider_has_members: boolean;
  provider_size_words: string | null;
  provider_size_at: string | null;
  last_run_words: string;
}

export interface AdListsResponse {
  can_act: boolean;
  lists: AdListView[];
  assignments: { provider: string; provider_account_id: string; company_slug: string }[];
}

export interface AdConnectionView {
  id: string;
  provider: "meta" | "google_ads" | "google_lsa";
  provider_word: string;
  company_slug: string;
  account_id: string;
  account_name: string | null;
  has_token: boolean;
  token_entered_at: string | null;
  status: "active" | "held" | "pending_access" | "error";
  last_health_at: string | null;
  last_health_word: string | null;
  last_health_problem: string | null;
  health: unknown;
  connected_by_name: string | null;
  connected_at: string | null;
  held_at: string | null;
  hold_reason: string | null;
  version_number: number;
}
/** Row 10.1: one outside feed's switch on Connected accounts. */
export interface OutsideSourceSwitch {
  source_kind: "deed" | "lien" | "obituary" | "inquiry" | "life_event";
  provider: "none" | "attom";
  enabled: boolean;
  changed_by_name: string | null;
  changed_at: string | null;
  last_batch_at: string | null;
  last_batch_outcome: "written" | "refused" | null;
  last_batch_words: string | null;
  /** Row 10.5: the inquiry watch's counsel record; NULL elsewhere. */
  counsel_confirmed_on: string | null;
  counsel_confirmed_by: string | null;
  counsel_recorded_by_name: string | null;
  counsel_recorded_at: string | null;
}

/** Row 10.5: GET /api/mortgage-watch (Mortgage only). */
export interface MortgageWatchRow {
  signal_id: string;
  household_id: string;
  household_name: string | null;
  person_first_name: string | null;
  inquired_on: string | null;
  inquiry_kind: string | null;
  details: string | null;
  noted_at: string;
  advisor_name: string | null;
}

export interface MortgageWatchResponse {
  total: number;
  rows: MortgageWatchRow[];
  notice: { subject: string; body: string };
  switch: OutsideSourceSwitch | null;
}

export interface ConnectedAccountsResponse {
  /** Row 10.1: the outside feeds behind Grant's switches. */
  outside_sources?: OutsideSourceSwitch[];
  can_act: boolean;
  /** Row 9a.3: Google Ads, Meta ads and Local Services, each with the provider's last answer and its age. */
  ad_connections?: AdConnectionView[];
  /** Whether the Google OAuth client is on this Worker (S9-2); "Connect Google Ads" needs it. */
  google_ads_oauth_configured?: boolean;
  google_ads_developer_token_present?: boolean;
  /** Row 9a.4 (S9-18): which company each provider ad account serves. */
  ad_account_companies?: { provider: "meta" | "google_ads"; provider_account_id: string; company_slug: string }[];
  /** Row 8.9: the firm's Business Profile through the social vendor. */
  business_profile?: BusinessProfileHealth;
  /** Audit S3-M14: whether Google Postmaster has answered once (the same read Results uses). */
  postmaster?: { connected: boolean; last_read_at: string | null };
  /** Row 8.8: Search Console, read nightly with the Postmaster service account. */
  search_console?: {
    property: string;
    configured: boolean;
    service_account_email: string | null;
    read: SearchConsoleRead | null;
    counts: SearchConsoleCounts;
  };
  photo_library: { connected: boolean; problem: string | null; photos: number; last_indexed_at: string | null };
  /** Row 8.1: images Stew made this month, per company charged. */
  made_images?: { configured: boolean; model: string; month_start: string; by_company: { company_slug: string | null; company_name: string; images: number; cost_microdollars: number }[] };
  vendor: { vendor: "zernio"; configured: boolean; reachable: boolean; problem: string | null; checked_at: string; connected: number };
  /** Row 8.5: where blog posts publish, and whether the OS can reach it (never the password). */
  blog_destination?: {
    kind: "wordpress" | "none";
    word: string;
    configured: boolean;
    reachable: boolean;
    account: string | null;
    site: string | null;
    problem: string | null;
    checked_at: string;
    status: "draft" | "publish";
    environment: string | null;
  };
  accounts: ConnectedAccount[];
  firm_blocks: { company_slug: string; missing: string[]; empty: boolean }[];
  claims: FirmClaim[];
  companies: { id: string; slug: string; name: string }[];
}
export interface EconomicsSettingsResponse {
  today: string;
  income: IncomeSetting[];
  units: { kind: "discount" | "unit_price"; key: string; value: number; source: string; effective_from: string; set_by_user_id: string | null }[];
  can_edit: boolean;
}
export interface BenchmarkSetting {
  id: string;
  family: string;
  play: string;
  play_label: string;
  channel: string;
  step: string;
  low: number;
  mid: number | null;
  high: number | null;
  unit: string;
  source: string | null;
  checked_at: string;
  secondary: number;
  notes: string | null;
}
/** Row 7.1: one step as the brief uses it today. */
export interface BenchmarkOsStep {
  step: string;
  from: "benchmark" | "cohort" | "blend";
  rate: number;
  sample: number | null;
}
export interface BenchmarkSettingsResponse {
  today: string;
  rows: BenchmarkSetting[];
  /** Per play: what the brief uses for each step (the same stepsFrom). */
  os_data?: Record<string, BenchmarkOsStep[]>;
  /** When the OS's own numbers were last counted (nightly), or null. */
  os_data_counted_at?: string | null;
  can_edit: boolean;
}
export interface DeskSetting {
  company_id: string;
  company_slug: string;
  company_name: string;
  owner: { user_id: string; name: string } | null;
  /** Where the owner comes from: Grant's setting, or the code list until he sets one. */
  owner_from: "setting" | "fallback";
  setting: { id: string; slots_per_week: number; processing_ceiling: number | null; ceiling_label: string | null; note: string | null; effective_from: string; set_by_user_id: string | null } | null;
  used_this_week: number;
  /** Row 7.3: what the desk's last twelve weeks propose (shared/marketing/desk-capacity.ts). */
  proposal: { slots: number | null; weeks: { week_start: string; conversations: number }[]; total: number; why: string };
}
/** Row 6.4: one season on the calendar (shared/db/digest-inputs.ts). */
export interface SeasonalCalendarEntry {
  id: string;
  slug: string;
  label: string;
  company_slug: string | null;
  start_md: string;
  end_md: string;
  line_text: string;
  medicare_disclaimer: boolean;
  is_active: boolean;
  updated_by_user_id: string | null;
  updated_at: string;
}

export interface SeasonalCalendarResponse {
  entries: SeasonalCalendarEntry[];
  can_edit: boolean;
  /** Whether Ryan's Medicare disclaimer is approved with no blanks left. */
  medicare_disclaimer_ready: boolean;
}

export interface SeasonalEditBody {
  label: string;
  company_slug: string | null;
  start_md: string;
  end_md: string;
  line_text: string;
  is_active: boolean;
  medicare_disclaimer: boolean;
}

/** Row 6.5: Grant's preview of the month (shared/db/digest-verdicts.ts). */
export interface DigestPreviewResponse {
  month: string;
  computed_at: string | null;
  households: number;
  earned: number;
  by_reason: { reason: import("@shared/marketing/digest").SilentReason; households: number }[];
  by_fact: { kind: string; company_slug: string; households: number }[];
  by_lead: { company_slug: string; households: number }[];
  /** Row 6.7: the month's sends, the rules that send it, the wording. */
  sending: {
    month: string;
    queued: number;
    sent: number;
    failed: number;
    refused: { reason: string; words: string; households: number }[];
    rules: { id: string; name: string; state: string }[];
    wording: { slug: string; display_name: string; approved: boolean; subject: string; body: string }[];
    /** Row 6.9: the month's subject test (judged on taps and replies). */
    subject_test: { inconclusive: boolean; winner: "a" | "b" | null; sentence: string } | null;
    resent: number;
  };
}

// ─── Row 7.4: saved audiences (modal 36) and who may hear (modal 30) ──

export interface AudienceDraft {
  company: FilterCompanySlugForAudience;
  name?: string | undefined;
  rule_set: FilterSet;
  service_key: string | null;
  topic_key: string | null;
}
export type FilterCompanySlugForAudience = "mortgage" | "insurance" | "planning" | "realty" | "financial";

export interface AudienceRefused {
  condition: FilterCondition;
  label: string;
  reason: string;
}

export interface SavedAudience {
  id: string;
  company: FilterCompanySlugForAudience;
  name: string;
  service_key: string | null;
  topic_key: string | null;
  owner_name: string | null;
  version_number: number;
  updated_at: string;
  archived_at: string | null;
  /** Made for a goal that was taken back, and nothing live reads it (audit S3-M7). */
  from_taken_back_goal?: boolean;
  can_save: boolean;
  rule_set: FilterSet;
  refused: AudienceRefused[];
  unreadable: { reason: string }[];
}

export interface AudiencesResponse {
  audiences: SavedAudience[];
  /** How many left-over audiences from taken-back goals the working list leaves out. */
  hidden_from_taken_back_goals?: number;
  read_companies: readonly string[] | "all";
  save_companies: readonly string[] | "all";
}

export interface AudienceCountResponse {
  four: {
    match: number;
    eligible: number | null;
    hear_nothing: { total: number; elsewhere: number; not_now: number };
    one_question: number;
    relevant: number;
    left_out: { held: number; never_ask: number; not_applicable: number };
  };
  hear_nothing_line: string;
  left_out_line: string | null;
  source_line: string;
  eligible_withheld: string | null;
  excluded_by: Record<"paused" | "at_ceiling" | "nobody_reachable", number>;
  eligible: { household_id: string; name: string }[];
  excluded: { household_id: string; name: string; why: string }[];
  counted_at: string;
  rule_set: FilterSet;
  refused: AudienceRefused[];
  unreadable: { reason: string }[];
}

export interface AudienceHouseholdResponse {
  household_id: string;
  name: string;
  company: FilterCompanySlugForAudience;
  eligible: boolean;
  household_reason: string | null;
  people: { contactId: string; firstName: string; verdict: "eligible" | "excluded" | "never"; reason: string }[];
}

export interface AudienceVersion {
  id: string;
  version_number: number;
  event: "created" | "edited" | "archived" | "restored" | "undone";
  actor_name: string | null;
  created_at: string;
}

// ─── Row 7.5: campaigns, series and their pieces (screens 11, 12; modal 34) ──

export type PieceStateWord = "draft" | "approval" | "scheduled" | "sent" | "failed" | "held";
export type SeriesKindWord = "email" | "advisor_draft" | "text_task" | "post" | "blog" | "event" | "mail" | "card";
export type CadenceWord = "weekly" | "every_other_week" | "monthly";

export interface MarketingCampaign {
  id: string;
  company_id: string;
  company_slug: string;
  company_name: string;
  name: string;
  mode: "sprint" | "ongoing";
  starts_on: string;
  ends_on: string | null;
  review_on: string | null;
  audience_id: string | null;
  goal_id: string | null;
  state: "active" | "paused" | "ended";
  paused_at: string | null;
  owner_name: string | null;
  /** Row 10.7: Grant's holdout switch (1 on) and the first day a family was held. */
  holdout_on?: number;
  holdout_started_on?: string | null;
  version_number: number;
  can_save?: boolean;
  /** Split 3 audit M8: the list's status, read from the series and the goal ("Active" only once approved). */
  status?: { word: string; tone: "good" | "warn" | "plain" };
}

export interface CampaignSeries {
  id: string;
  campaign_id: string;
  name: string;
  kind: SeriesKindWord;
  piece_count: number;
  cadence: CadenceWord;
  send_time: string;
  starts_on: string;
  audience_id: string | null;
  brief: string | null;
  state: "draft" | "approval" | "approved" | "paused";
  approved_at: string | null;
  version_number: number;
}

export interface SeriesPiece {
  carrier: "email" | "advisor";
  id: string;
  series_id: string;
  position: number;
  channel: "email" | "email_draft" | "text_task" | "mail_task" | "card_task";
  subject: string;
  body: string;
  planned_at: string;
  piece_state: PieceStateWord;
  held_reason: string | null;
  version_number: number;
}

export interface CampaignScreen {
  campaign: MarketingCampaign;
  audience: { name: string; eligible: number } | null;
  goal: { sentence: string } | null;
  series: (CampaignSeries & { pieces: SeriesPiece[]; audience_name: string | null; families: number | null })[];
  /** Row 11.5b: every piece on one ladder, with the provider's evidence, and the campaign's status. */
  table: PiecesTableRow[];
  status: CampaignStatus;
  weeks: { week_start: string; pieces: { series_id: string | null; series_name: string; position: number; channel: SeriesPiece["channel"]; at: string }[]; touches: number; status: string }[];
  story: { household_id: string; name: string; events: { at: string; what: string; detail: string }[] } | null;
  can_save: boolean;
  ceiling_sentence: string;
  paid_sentence: string;
  pause_sentence: string;
  /** Program audit H10: what a paused plan holds, and what it does not. */
  paused_sentence: string;
  channel_jobs: { channel: string; job: string }[];
  /** Row 10.7: the holdout. `can_switch` is Grant's (the server checks him again). */
  holdout?: { can_switch: boolean; refusal: string | null; result: CampaignHoldoutResult; share_percent: number; never_hold: string[] };
}

export interface SeriesScreen {
  series: CampaignSeries;
  campaign: MarketingCampaign;
  pieces: SeriesPiece[];
  audience: { name: string; eligible: number } | null;
  checks: { ok: boolean; text: string; small?: string }[];
  versions: { version_number: number; event: string; actor_name: string | null; created_at: string }[];
  can_save: boolean;
  can_approve: boolean;
  /** Why Grant cannot approve the set now (the server's own approve check), or null. */
  approve_blocked?: string | null;
  /** Row 7.9a: where the set stands in the queue. */
  queue?: QueueState;
  exit_rule_sentence: string;
  /** The sentence for a hand-sent kind: a text, a letter or a card (row 8.11). */
  text_task_sentence: string | null;
  /** Row 8.11: what a mail or card series costs per piece sent, from the typed unit prices; null for other kinds. */
  mail_cost_line?: string | null;
}

/** Row 7.6: the email builder (screen 14). The block shapes are the
 *  server's own (shared/marketing/email-piece.ts). */
export type { EmailBlock } from "@shared/marketing/email-piece";

export interface EmailPieceRow {
  id: string;
  series_id: string;
  position: number;
  subject: string;
  preview: string;
  blocks: EmailBlockT[];
  brief: string | null;
  voice: "warm" | "plain" | "short" | "story" | null;
  piece_state: PieceStateWord;
  held_reason: string | null;
  planned_at: string;
  version_number: number;
  from_email: string;
  sending_company_slug: string | null;
  from_user_id: string | null;
  from_name: string | null;
  asset_id: string | null;
  body_text: string;
  new?: boolean;
}

export interface GoalPlanView {
  reading: GoalReading;
  product_label: string;
  product_plural: string;
  company_word: string;
  population: string;
  counts: GoalCounts;
  desk: { slots: number | null; used: number };
  income: { y1: number | null; lifetime: number | null; source: string; share: string };
  steps: { step: string; rate: number; source: string | null; from: string; sample: number | null }[];
  proposal: Proposal;
  envelope: GoalEnvelope;
  envelope_lines: string[];
  starts_on: string;
  ends_on: string;
  in_scope: boolean;
  owner_name: string | null;
  level: { current: GrowthLevel; does: string; moves_to: GrowthLevel | null; refusal: string | null; books: { name: string | null; level: GrowthLevel }[] };
  /** Row 11.1: the answers the plan was made with, the questions still open, the cap and why a budget cannot be saved. */
  answers: GoalAnswers;
  questions: GoalQuestion[];
  cap_dollars: number;
  budget_refusal: string | null;
  /** Row 11.3: the research brief, five sections never mixed. Absent on plans stored before row 11.3. */
  research?: ResearchBrief;
  /** Row 11.4: the one recommended plan, its three variations each with its own financial brief, and the "soon" work list. */
  recommendation?: Recommendation;
  variations?: { key: Variation; word: string; does: string; recommendation: Recommendation; proposal: Proposal; channel_line?: string }[];
  soon?: SoonListView | null;
}

/** Row 11.4: the "soon" work list, Today's own list for a goal's audience. */
export interface SoonListView {
  rows: { householdId: string; name: string; reason: string; nextStep: string; chanceBand: string | null }[];
  total: number;
  passDay: string | null;
}

/** A playbook as the list draws it (row 11.3). */
export interface PlaybookSummary {
  id: string;
  company_slug: string;
  company_word: string;
  product_family: string;
  product_label: string;
  status: PlaybookStatus;
  status_word: string;
  recheck_due: boolean;
  owner_name: string;
  updated_at: string;
  can_edit: boolean;
}

export interface PlaybookDetail {
  id: string;
  company_slug: string;
  company_word: string;
  product_family: string;
  product_label: string;
  fields: PlaybookFields;
  status: PlaybookStatus;
  status_word: string;
  recheck_due: boolean;
  owner_name: string;
  approved_by_name: string | null;
  approved_at: string | null;
  version_number: number;
  updated_by_name: string | null;
  updated_at: string;
  can_edit: boolean;
  can_approve: boolean;
  can_research: boolean;
  approval_refusal: string | null;
}

export interface PlaybookVersion {
  id: string;
  version_number: number;
  event: "seeded" | "edited" | "approved" | "approval_withdrawn";
  approval_reset: boolean;
  fields: PlaybookFields;
  status: PlaybookStatus;
  actor_name: string | null;
  created_at: string;
}

export interface OutsideClaim {
  id: string;
  topic: ResearchTopic;
  topic_word: string;
  claim: string;
  source_url: string;
  source_title: string | null;
  scope: string;
  retrieved_on: string;
  recheck_on: string;
  recheck_due: boolean;
  client_usable: boolean;
  discarded_at: string | null;
  discarded_by_name: string | null;
  discard_reason: string | null;
}

export interface ResearchRun {
  id: string;
  topic: ResearchTopic;
  /** Row 11.9a: 'ad_libraries' searched only the Meta Ad Library and Google's Ads Transparency Center. */
  source?: "web" | "ad_libraries";
  topic_word?: string;
  note: string | null;
  state: "sending" | "done" | "refused" | "failed" | "interrupted";
  refusal: string | null;
  searches_used: number;
  claims_kept: number;
  requested_by_name: string | null;
  created_at: string;
}

export interface PlaybookScreen {
  playbook: PlaybookDetail;
  history: PlaybookVersion[];
  outside: OutsideClaim[];
  runs: ResearchRun[];
  research: { cap: number; used_today: number; topics: { key: ResearchTopic; word: string }[]; note_max: number };
  /** Row 11.9a: what others are running for this product. */
  others: MarketOthers;
  today: string;
}

/** One ad another firm is running, from the two ad libraries or seen by a person (row 11.9a). */
export interface MarketFinding {
  id: string;
  source_kind: MarketSourceKind;
  source_word: string;
  advertiser: string;
  ad_format: MarketAdFormat;
  format_word: string;
  hook: string;
  offer: string | null;
  words: string | null;
  source_url: string;
  found_on: string;
  recheck_on: string;
  recheck_due: boolean;
  seen_by_name: string | null;
  discarded_at: string | null;
}

export interface MarketOthers {
  findings: MarketFinding[];
  /** Live and set-aside findings, from their own COUNT(*), never the list's length. */
  live_count: number;
  set_aside_count: number;
  formats: { key: MarketAdFormat; word: string }[];
  words_max: number;
  rule: string;
}

export interface SeenFindingBody {
  url: string;
  advertiser: string;
  format: MarketAdFormat;
  hook: string;
  offer: string;
  words: string;
}

/** The body a playbook save sends: the fields as the API names them. */
export interface PlaybookSaveBody {
  why_buy: string;
  objections: string[];
  offer: string;
  never_words: string[];
  disclosures: string;
  recheck_on: string | null;
  expected_version_number: number;
}

/** A company's preset ask (row 11.1), with who may edit it. */
export interface GoalPreset {
  id: string;
  company_slug: string;
  company_word: string;
  product_family: string;
  product_label: string;
  product_plural: string;
  sentence: string;
  audience_words: string;
  audience_mode: GoalReading["audience"];
  outcome_measure: OutcomeMeasure;
  pace: GoalPace;
  window_weeks: number;
  status: "stew_draft" | "saved";
  status_word: string;
  version_number: number;
  owner_name: string;
  updated_by_name: string | null;
  in_scope: boolean;
  can_edit: boolean;
}

/** What "Ask Stew" on the goal page answers (row 11.1). */
export interface GoalReadResult {
  reading: GoalReading | null;
  note: string | null;
  plan: GoalPlanView | null;
  answers?: GoalAnswers;
  stew_out?: boolean;
  unsupported?: { what: string; company: string | null; company_word: string | null; owner_name: string };
  broad?: { options: { gap_key: string; rank: number; reason: string; held_down: string | null }[]; why: string | null };
}

/** Who a goal reaches, as evidence (row 11.2). */
export interface GoalAudienceView {
  counts: GoalCounts;
  people: number;
  reach: Record<ReachChannel, number>;
  moments: number;
  /** Who may hear now, each family once (null when the interested are too many to check). */
  may_hear: number | null;
  /** Of `may_hear`, the families with a moment now who are not among the interested. */
  moments_may_hear: number;
  ranked: RankedFamily[];
  shown_line: string;
  shape: AudienceShape;
  shape_sentence: string;
  alternatives: Alternative[];
  counted_at: string;
  in_scope: boolean;
  conditions: FilterCondition[];
  condition_labels: string[];
  refused: { label: string; reason: string }[];
  note: string | null;
}

/** The answers as the API takes them. */
export interface GoalAnswerBody {
  outcome: OutcomeMeasure | null;
  pace: GoalPace | null;
  capacity_per_week: number | null;
  audience_words: string;
  budget_dollars: number | null;
}

export interface GoalAsk {
  catalog: { company: string; companyWord: string; services: { key: string; label: string }[]; in_scope: boolean }[];
  examples: string[];
  presets: GoalPreset[];
  carried: { company: string; service_key: string; audience: GoalReading["audience"]; mode: GoalReading["mode"]; weeks: number; reason: string; target: number | null; sentence: string; gap_key: string } | null;
  /** Ask Stew's start_goal sentence, read once by its one-time id (never carried in the link). */
  stew_sentence: string | null;
  can_prepare_any: boolean;
}

export interface GoalScreen {
  goal: {
    id: string;
    company_slug: string;
    product_family: string;
    sentence: string;
    audience_mode: string;
    mode: string;
    window_weeks: number | null;
    status: string;
    reason_now: string | null;
    starts_on: string | null;
    ends_on: string | null;
    prepared_at: string | null;
    prepared_by_name: string | null;
    approved_at: string | null;
    approved_by_name: string | null;
    owner_name: string | null;
    target_outcomes: number | null;
    version_number: number;
  };
  plan: StoredPlan | null;
  snapshots: { id: string; moment: "prepared" | "approved"; level: GrowthLevel; evidence: string; computed_at: string; computed_by_name: string | null }[];
  envelope: GoalEnvelope | null;
  envelope_lines: string[];
  campaign: { id: string; name: string } | null;
  series: { id: string; name: string; kind: string; state: string; pieces: number; written: number }[];
  brief: { id: string; computed_at: string } | null;
  can_send: boolean;
  /** Split 2 audit H11: in Grant's queue now (the queue's own rule). */
  waiting_for_grant?: boolean;
  can_approve: boolean;
  can_withdraw: boolean;
  /** Row 7.9a: where the plan stands in the queue. */
  queue?: QueueState;
  /** Row 9b.7: where an approved goal stands, Accelerate's paid help, and whether the viewer may adjust a paid row (Grant). */
  rollup?: GoalResult | null;
  paid_help?: (PaidHelp & { can_draft: boolean }) | null;
  can_adjust?: boolean;
  /** Row 11.5a: every piece the plan named, each with its checklist; empty for another desk. */
  pieces?: GoalPieceView[];
  /** Row 11.6: the play this goal was launched from; held when the play moved after a direct launch. */
  play?: GoalPlayLaunch | null;
  /** Row 11.8: the scoreboard (approved goals) and what the goal taught (once its end day has passed). */
  scoreboard?: GoalScoreboard | null;
  learning?: GoalLearningView | null;
}

export type { GoalPieceView };

// ─── Row 11.6: approved plays ───

export interface GoalPlayLaunch {
  play_id: string;
  play_name: string;
  play_version: number;
  launch: "direct" | "queue" | "released";
  held: boolean;
  /** Why the runner holds it, in a sentence, while held (the runner's own check). */
  held_reason: string | null;
}

export interface PlaySummary {
  id: string;
  company_slug: string;
  company_word: string;
  product_label: string;
  name: string;
  channel_words: { key: PlayChannel; word: string }[];
  status: PlayStatus;
  status_word: string;
  version_number: number;
  launch: LaunchCheck;
  owner_name: string;
  can_edit: boolean;
  can_launch: boolean;
}

export interface PlayDetail {
  id: string;
  company_slug: string;
  company_word: string;
  product_family: string;
  product_label: string;
  product_plural: string;
  fields: PlayFields;
  channel_words: { key: PlayChannel; word: string }[];
  audience_labels: string[];
  spend_dollars: number;
  status: PlayStatus;
  status_word: string;
  approved_by_name: string | null;
  approved_at: string | null;
  approved_version: number | null;
  version_number: number;
  updated_by_name: string | null;
  updated_at: string;
  owner_name: string;
  signers: { role: SignerRole; name: string; signed_version: number | null; on_this_version: boolean }[];
  launch: LaunchCheck;
  can_edit: boolean;
  can_approve: boolean;
  can_sign: SignerRole | null;
  can_launch: boolean;
  approval_refusal: string | null;
}

export interface PlayVersion {
  id: string;
  version_number: number;
  event: "seeded" | "edited" | "approved" | "approval_withdrawn" | "signed" | "retired";
  approval_reset: boolean;
  actor_name: string | null;
  created_at: string;
  words: PlayWords | null;
}

export interface PlayScreen {
  play: PlayDetail;
  history: PlayVersion[];
  launch_audience: string;
  /** Split 3 audit H2: what a launch by this viewer would reach today; null for a desk that cannot launch it. */
  reach?: PlayReach | null;
}

export interface PlayReach {
  eligible: number | null;
  never_asked: number;
  first_send_day: string;
  audience: string;
  nobody: string | null;
  counted_at: string;
}

export interface PlaySaveBody {
  name: string;
  channels: PlayChannel[];
  email_count: number;
  cadence: string;
  audience_words: string;
  words: PlayWords;
  expected_version_number: number;
}

export interface PlayLaunchResult {
  goal_id: string;
  campaign_id: string;
  mode: "direct" | "queue";
  why: string | null;
  audience_word: string;
  /** True when the set really reached Grant's queue; false when something (a footer, a held sentence) stopped it. */
  sent_to_grant?: boolean;
  waiting?: string | null;
  held: { series: string; position: number; sentence: string; reason: string }[];
}

export interface LevelFamily {
  company: string;
  company_word: string;
  /** Whether the family is in one of the viewer's own companies (the page starts on these). */
  in_scope: boolean;
  family: string;
  label: string;
  level: GrowthLevel;
  set: boolean;
  set_at: string | null;
  set_by_name: string | null;
  reason: string | null;
  books: { advisor_user_id: string | null; name: string | null; level: GrowthLevel }[];
}

export interface LevelSettingsRow {
  company_id: string;
  company_slug: string;
  cooldown_days: number;
  unassigned_book_user_id: string | null;
  unassigned_book_name: string | null;
}

export interface LevelsResponse {
  families: LevelFamily[];
  settings: LevelSettingsRow[];
  does: Record<GrowthLevel, string>;
  can_change: boolean;
  /** The viewer's own companies, or "all" for someone who works across the firm. */
  scope: string[] | "all";
  /** The scope in words, for the header ("Insurance", "Mortgage and Realty", "All companies"). */
  scope_label: string;
}

export interface LevelDetail {
  card: {
    company: string;
    family: string;
    product_label: string;
    level: GrowthLevel;
    does: string;
    strong: { strong: boolean; reasons: string[] };
    suggestion: { level: GrowthLevel; reason: string; safety: string | null } | null;
    target: { goal_id: string; outcomes: number; recorded: number } | null;
    money: { cash: number; income_lifetime: number | null; net_lifetime: number | null; evidence: string };
    counted_at: string;
  };
  live: { id: string; advisor_user_id: string | null; advisor_name: string | null; level: GrowthLevel; reason: string; set_by_name: string | null; effective_from: string; source: string }[];
  history: { id: string; advisor_name: string | null; level: GrowthLevel; reason: string; is_safety: boolean; source: string; set_by_name: string | null; effective_from: string; effective_to: string | null }[];
  settings: LevelSettingsRow | null;
  can_change: boolean;
}

export interface LevelPreviewResult {
  from: GrowthLevel;
  to: GrowthLevel;
  lines: string[];
  cooldown: { ok: boolean; until: string | null; days: number };
  live_id: string | null;
}

/** Row 7.10a: Results (screen 9). The shapes are the server's own. */
export type { ResultsResponse, GoalResult, PieceResult, RollupResult, ChannelResult, BandRow, PersonScoreRow } from "@shared/marketing/results";
export type { GoalLearningView, GoalScoreboard };
export type { CampaignHoldoutResult } from "@shared/marketing/holdouts";
export type { PaidPieceResult, PaidKind, PaidAdjustAction, OutcomeInfluenceRow, PaidHelp } from "@shared/marketing/paid-results";
export type { LeaderboardResponse, LeaderboardCompany, LeaderboardPerson, LeaderboardPeriod } from "@shared/marketing/leaderboard";
export type { RecognitionDraft, RecognitionSendResponse } from "@shared/marketing/recognition";
export type { BudgetMeterWire, MarketingRuleKey, RuleChangeResponse, RuleHistoryEntry, RuleView, SettingsHomeBudgets, SettingsHomeResponse } from "@shared/marketing/marketing-rules";
export type { WorkRow, WorkButton } from "@shared/marketing/work-list";
/** Row 7.9a: the approval queue (screen 7). */
export interface ApprovalQueueResponse {
  rows: QueueRow[];
  total: number;
  /** Null for a signer, who sees only the rows waiting on them. */
  header: (HeaderNumbers & { line: string }) | null;
  ran_under_parent: { total: number; rows: { what: string; detail: string }[] } | null;
  /** Row 7.9b: approved pieces that missed their window or wait on a reason; null for a signer. */
  not_sending: { piece_id: string; series_id: string; what: string; state: "held" | "waiting"; reason: string; href: string }[] | null;
  seat: "grant" | "reader" | "signer";
  can_act: boolean;
  signs_as: SignerRole[];
  /** Row 7.9b: Grant's "Send what's due now"; null for anyone else. */
  sending?: { enabled: boolean; quiet: boolean; sentence: string } | null;
}

export interface SeriesSendRunResult {
  slid: number;
  missed_window: number;
  quiet_hours: boolean;
  firm_stop: boolean;
  emails_sent: number;
  people_refused: number;
  drafts: number;
  tasks: number;
  families_refused: number;
  pieces_done: number;
  pieces_waiting: { piece_id: string; reason: string }[];
}

export type ApprovalDetailResponse = QueueDetail & { can_act: boolean; signs_as: SignerRole[] };

export interface GoalPrepared {
  goal_id: string;
  campaign_id: string;
  sent_to_grant: boolean;
  waiting: string | null;
  stew_note: string | null;
  held: { series: string; position: number; sentence: string; reason: string }[];
  /** Row 11.5a: a sentence for every piece Prepare could not make, or made with a gap. */
  notes?: { kind: string; sentence: string }[];
}

// ─── Row 8.2: the social composer (screen 16) ───────────────────────

export type SocialNetworkKey = "facebook" | "instagram" | "linkedin" | "twitter" | "youtube" | "tiktok" | "googlebusiness";

export interface SocialComposerTarget {
  account_id: string;
  network: SocialNetworkKey;
  label: string;
  whose: "firm" | "own";
  health: "healthy" | "needs_reconnection" | "disconnected";
}

export interface SocialPieceScreen {
  piece: {
    id: string;
    author_user_id: string;
    companies: string[];
    base_text: string;
    overrides: Partial<Record<SocialNetworkKey, string>>;
    targets: string[];
    media_item_id: string | null;
    marketing_media_id: string | null;
    instagram_media_id: string | null;
    /** Row 8.7: 1 when the picture carries words. */
    words_on_picture: number;
    alt_text: string | null;
    scheduled_for: string | null;
    campaign_id: string | null;
    /** Row 10.9: a series of posts (null: one post). */
    repeat_cadence: "weekly" | "every_other_week" | "monthly" | null;
    repeat_count: number | null;
    piece_state: "draft" | "approval" | "scheduled" | "taken_back";
    submitted_at: string | null;
    approved_at: string | null;
    version_number: number;
  };
  author_name: string | null;
  /** "Stewardship Insurance", "Mortgage and Realty", "Stewardship as a whole". */
  speaks_for: string;
  targets: SocialComposerTarget[];
  not_connected: { network: SocialNetworkKey; word: string }[];
  limits: Record<SocialNetworkKey, number>;
  tightest: { network: SocialNetworkKey; limit: number } | null;
  problems: { network: SocialNetworkKey | null; reason: string }[];
  footer: { words: string; text: string | null; held: string | null };
  media: { source: "yours" | "team"; id: string; name: string; media_type: "image" | "video"; kind: "upload" | "made" | "team"; width: number | null; height: number | null; marketing_ok?: boolean; picked_reason?: string | null } | null;
  instagram_cut: { id: string; name: string; width: number | null; height: number | null } | null;
  suggested: { at: string; why: string };
  signers: { role: string; name: string }[];
  high: boolean;
  sent_back: { note: string | null; at: string; by_name: string | null } | null;
  campaigns: { id: string; name: string; company_slug: string }[];
  posts: { id: string; account_id: string; label: string; network?: string | null; piece_state: string; scheduled_at: string | null; published_at: string | null; held_reason: string | null; platform_url: string | null; boost?: PostBoost | null }[];
  history: { version_n: number; event: string; reason_text: string | null; actor_user_id: string | null; created_at: string }[];
  can_save: boolean;
  can_take_back: boolean;
  viewer_desk: string[] | "all";
  viewer_is_founder: boolean;
  /** Row 8.7: Stew's read per network, kept per version. */
  stew_read: ContentRead | null;
  read_networks: SocialNetworkKey[];
  read_how: string[];
  read_for_makers: boolean;
}

export interface SocialPieceEdit {
  base_text?: string;
  overrides?: Partial<Record<SocialNetworkKey, string | null>>;
  targets?: string[];
  companies?: string[];
  media_item_id?: string | null;
  marketing_media_id?: string | null;
  instagram_media_id?: string | null;
  words_on_picture?: boolean;
  alt_text?: string | null;
  scheduled_for?: string | null;
  campaign_id?: string | null;
  /** Row 10.9: repeat on a cadence, or null for one post. */
  repeat?: { cadence: "weekly" | "every_other_week" | "monthly"; count: number } | null;
}

export interface SocialPieceListItem {
  id: string;
  base_text: string;
  companies: string[];
  piece_state: SocialPieceScreen["piece"]["piece_state"];
  scheduled_for: string | null;
  author_user_id: string;
  updated_at: string;
}

// ─── Row 8.6: landing pages ──────────────────────────────────────────

export interface LandingContentOut {
  heading: string;
  intro: string;
  items: { title: string; note: string }[];
  askLabel: string;
}

export interface LandingPageScreen {
  page: {
    id: string;
    campaign_id: string | null;
    blog_post_id: string | null;
    author_user_id: string;
    advisor_user_id: string;
    company_slug: string | null;
    title: string;
    slug: string;
    content: LandingContentOut;
    address: string;
    link_code: string | null;
    destination: string | null;
    destination_page_id: string | null;
    preview_url: string | null;
    published_url: string | null;
    /** 1 the site kept the paid-version script, 0 it removed it, null not read back yet (slice 11 audit, decision 4). */
    version_script_kept: 0 | 1 | null;
    written_at: string | null;
    write_attempts: number;
    version_number: number;
  };
  site: string;
  public_url: string;
  advisor: { id: string; name: string | null; title: string | null; phone: string | null; active: boolean };
  company_word: string;
  readiness: { problems: string[]; footer_text: string | null; disclaimer: string | null; medicare: boolean };
  counts: { visits: number; asks: number };
  recent_asks: { id: string; name: string; question: string; arrived_at: string; match_status: string; household_id: string | null; inbox_item_id: string | null }[];
  link: { code: string; url: string } | null;
  advisors: { id: string; name: string }[];
  destination: { kind: string; word: string; configured: boolean; problem: string | null; site: string | null };
  history: { version_n: number; event: string; reason_text: string | null; actor_name: string | null; created_at: string }[];
  can_save: boolean;
  can_write: boolean;
  viewer_is_founder: boolean;
}

export type LandingParentRef = { campaign_id: string } | { blog_post_id: string };

// ─── Row 8.5: the blog builder ──────────────────────────────────────

export type BlogBlockOut =
  | { type: "section"; heading: string; text: string }
  | { type: "image"; media_item_id: string | null; marketing_media_id: string | null; alt: string }
  | { type: "quote"; text: string; byline: string }
  | { type: "button"; label: string; url: string }
  | { type: "table"; rows: string[][] };

export type BlogPieceStateKey = "draft" | "approval" | "scheduled" | "sent" | "failed" | "held";

export interface BlogCheckOut {
  key: string;
  ok: boolean;
  blocking: boolean;
  text: string;
  small?: string;
}

export interface BlogPostScreen {
  post: {
    id: string;
    author_user_id: string;
    byline_user_id: string;
    companies: string[];
    title: string;
    slug: string;
    search_phrases: string;
    blocks: BlogBlockOut[];
    media_item_id: string | null;
    marketing_media_id: string | null;
    alt_text: string | null;
    brief: string | null;
    voice: "warm" | "plain" | "short" | "story" | null;
    campaign_id: string | null;
    publish_on: string | null;
    piece_state: BlogPieceStateKey;
    held_reason: string | null;
    failure_reason: string | null;
    submitted_at: string | null;
    approved_at: string | null;
    destination: string | null;
    destination_draft_id: string | null;
    preview_url: string | null;
    destination_post_id: string | null;
    published_url: string | null;
    published_at: string | null;
    publish_attempts: number;
    version_number: number;
    updated_at: string;
  };
  author_name: string | null;
  byline_name: string | null;
  speaks_for: string;
  /** "/learn/planning/medicare-101". */
  address: string;
  /** "https://stewardship.pro". */
  site: string;
  checks: BlogCheckOut[];
  footer: { words: string; text: string | null };
  disclaimer: string | null;
  medicare: boolean;
  read: { words: number; minutes: number };
  media: { source: "yours" | "team"; id: string; name: string; media_type: "image" | "video"; kind: "upload" | "made" | "team"; width: number | null; height: number | null; marketing_ok?: boolean; picked_reason?: string | null } | null;
  signers: { role: string; name: string }[];
  high: boolean;
  sent_back: { note: string | null; at: string; by_name: string | null } | null;
  campaigns: { id: string; name: string; company_slug: string }[];
  bylines: { id: string; name: string }[];
  voices: { voice: "warm" | "plain" | "short" | "story"; word: string }[];
  draft_options: readonly string[];
  block_words: Record<BlogBlockOut["type"], string>;
  state_word: string;
  cuts: { kind: "email" | "post"; id: string; title: string; state: string; open_to: string }[];
  history: { version_n: number; event: string; reason_text: string | null; actor_user_id: string | null; actor_name: string | null; created_at: string }[];
  destination: { kind: "wordpress" | "none"; word: string; configured: boolean; problem: string | null; site: string | null; status: "draft" | "publish" };
  /** Row 8.7: Stew's read (found, read, act), kept per version. */
  stew_read: ContentRead | null;
  read_sections: Record<string, string>;
  read_how: string[];
  read_for_makers: boolean;
  can_save: boolean;
  can_take_back: boolean;
  can_preview: boolean;
  can_cut: boolean;
  viewer_desk: string[] | "all";
  viewer_is_founder: boolean;
}

export interface BlogPostEdit {
  title?: string;
  slug?: string;
  search_phrases?: string;
  blocks?: BlogBlockOut[];
  companies?: string[];
  byline_user_id?: string;
  media_item_id?: string | null;
  marketing_media_id?: string | null;
  alt_text?: string | null;
  publish_on?: string | null;
  campaign_id?: string | null;
  brief?: string | null;
  voice?: "warm" | "plain" | "short" | "story" | null;
}

export interface BlogPostListItem {
  id: string;
  title: string;
  companies: string[];
  piece_state: BlogPieceStateKey;
  state_word: string;
  publish_on: string | null;
  published_url: string | null;
  author_user_id: string;
  updated_at: string;
}

// ─── Row 8.4: the Calendar and the Content library ──────────────────

export type CalendarKindKey = "email" | "advisor_email" | "text_task" | "mail_task" | "post" | "social_piece" | "blog" | "boost" | "search" | "event";
export type CalendarChannelKey = "email" | "advisor" | "social" | "blog" | "gbp" | "event" | "task" | "paid";
export type CalendarStateKey = "draft" | "approval" | "scheduled" | "sent" | "failed" | "held";
export type CalendarStatusFilter = "all" | "waiting" | "scheduled" | "sent" | "failed_held";

export interface CalendarItemOut {
  kind: CalendarKindKey;
  id: string;
  channel: CalendarChannelKey;
  at: string;
  day: string;
  title: string;
  state: CalendarStateKey;
  companies: string[];
  company_word: string;
  author_user_id: string;
  can_move: boolean;
  reason: string | null;
  open_to: string;
  parent: string | null;
}

export interface CalendarScopeOut {
  companies: string[] | null;
  label: string;
  offered: string[];
  can_widen: boolean;
}

export interface CalendarCampaignChoice {
  id: string;
  name: string;
  company_slug: string;
  starts_on: string;
  ends_on: string | null;
  has_audience: boolean;
}

export interface CalendarNewMenuEntry {
  key: "email" | "post" | "post_series" | "series" | "advisor_batch" | "blog" | "event" | "task" | "ad" | "search";
  label: string;
  small: string;
  opens: "campaign_email" | "composer" | "composer_series" | "series" | "blog" | "event" | "boost" | "new_ad" | "landing" | null
  arrives: string | null;
}

export interface CalendarResponse {
  now: string;
  scope: CalendarScopeOut;
  range: { from: string; to: string; status: CalendarStatusFilter };
  items: CalendarItemOut[];
  total: number;
  truncated: boolean;
  heat: {
    weeks: { start: string; end: string; label: string }[];
    rows: { channel: CalendarChannelKey; word: string; counts: number[] }[];
    arriving: { channel: string; sentence: string }[];
  };
  attention: { rows: { kind: "failed" | "awaiting" | "held" | "missed"; word: string; item: CalendarItemOut; sentence: string }[]; total: number };
  new_menu: CalendarNewMenuEntry[];
  campaigns: CalendarCampaignChoice[];
  can_write: boolean;
}

export type LibraryTabKey = "all" | "emails" | "posts" | "blog" | "ads_events" | "auto_send" | "media";

export interface LibraryCardOut {
  kind: "template" | "email" | "post" | "social_piece" | "blog" | "boost" | "search" | "event" | "media";
  id: string;
  channel: CalendarChannelKey;
  label: string;
  title: string;
  meta: string;
  state: { tone: "good" | "warn" | "bad" | "navy" | "none"; word: string };
  results: string | null;
  action: { kind: "use" | "reuse" | "open" | "media"; to: string | null };
  companies: string[];
  company_word: string;
}

export interface LibraryResponse {
  scope: CalendarScopeOut;
  tab: LibraryTabKey;
  cards: LibraryCardOut[];
  totals: { templates: number; emails: number; posts: number; social_pieces: number; blog_posts: number; ads_events: number; photos: number; uploads: number };
  arriving: { word: string; arrives: string }[];
  campaigns: CalendarCampaignChoice[];
  can_write: boolean;
}

export interface EmailPieceScreen {
  piece: EmailPieceRow;
  series: { id: string; name: string; piece_count: number; state: CampaignSeries["state"]; cadence: CadenceWord; send_time: string; starts_on: string; version_number: number; brief: string | null };
  campaign: { id: string; name: string; company_slug: string; company_name: string; mode: "sprint" | "ongoing"; ends_on: string | null };
  siblings: { id: string; position: number; subject: string; piece_state: PieceStateWord }[];
  audience: { name: string; relevant: number; eligible: number; excluded_by: Record<"paused" | "at_ceiling" | "nobody_reachable", number>; people: number; no_first_name: number; families: { id: string; name: string }[] } | null;
  checks: { ok: boolean; text: string; small?: string }[];
  from_options: { kind: "company" | "own"; label: string; email: string }[];
  from_current: { email: string; name: string | null; as_company: boolean; mine: boolean };
  drafts: { id: string; version: number; state: string; held_reason: string | null; held_sentence: string | null; created_at: string }[];
  testimonials: { review_id: string; body: string; byline: string; submitted_at: string }[];
  library: { id: string; text: string }[];
  facts: { kind: "renewal" | "note_rate" | "window"; word: string; tokens: { token: string; word: string }[] }[];
  merge_fields: { token: string; word: string }[];
  /** Whether "Send a test to me" can work on this server, said before the press. */
  test_send: { available: true } | { available: false; code: string; why: string };
  voices: { voice: "warm" | "plain" | "short" | "story"; word: string }[];
  options: { option: "draft" | "rewrite" | "shorter" | "story"; word: string }[];
  block_words: Record<EmailBlockT["type"], string>;
  versions: { version_number: number; event: string; actor_name: string | null; created_at: string }[];
  can_save: boolean;
  can_approve: boolean;
  /** Stew's read (row 7.7): the email's makers only; null for anyone else and before the first read. */
  read: EmailRead | null;
  read_for_makers: boolean;
  read_how: string[];
  read_questions: Record<QuestionRead["question"], string>;
}

export interface EmailRead {
  id: string;
  piece_version: number;
  questions: QuestionRead[];
  suggestions: ReadSuggestions;
  held: { key: string; sentence: string; reason: string }[];
  applied: { ids: string[]; by: string; by_name: string | null; at: string; version: number }[];
  stew_model: string | null;
  stew_note: string | null;
  dismissed_at: string | null;
  computed_by_name: string | null;
  computed_at: string;
}

/** Row 8.7: Stew's read of a blog post (three questions) or a social post (one section per network). */
export type { ReadFinding, ReadSection } from "@shared/marketing/content-lift";
export interface ContentRead<S = Record<string, unknown>> {
  id: string;
  channel: "social" | "blog";
  piece_version: number;
  sections: import("@shared/marketing/content-lift").ReadSection[];
  suggestions: S;
  held: { key: string; sentence: string; reason: string }[];
  applied: { ids: string[]; by: string; by_name: string | null; at: string; version: number }[];
  stew_model: string | null;
  stew_note: string | null;
  dismissed_at: string | null;
  computed_by_name: string | null;
  computed_at: string;
}

export interface WhatWorksView {
  id: string;
  channel: "email" | "social" | "blog" | "paid" | "goal";
  platform: string;
  feature: string;
  feature_word: string;
  pattern: string;
  pattern_word: string;
  lift: number;
  sample: number;
  baseline_sample: number;
  since: string;
  mode: "own" | "blended" | "benchmark";
  mode_word: string;
  pin_kind: "keep" | "drop" | null;
  pin_note: string | null;
  pinned_by_name: string | null;
  pinned_at: string | null;
  rebuilt_at: string;
}

export interface WhatWorksResponse {
  email: WhatWorksView[];
  social: WhatWorksView[];
  /** Row 8.7: the blog rows and what each half counts. */
  blog: WhatWorksView[];
  /** Row 11.9c: each paid version's words against the other versions in its batch. */
  paid: WhatWorksView[];
  paid_rebuilt_at: string | null;
  /** Slice 11 audit, finding 3: what each finished goal taught, by product and channel. */
  goal: WhatWorksView[];
  goal_rebuilt_at: string | null;
  social_counts: { platform: string; posts: number }[];
  blog_counts: { company_slug: string | null; posts: number }[];
  blog_rebuilt_at: string | null;
  email_counts: { company_slug: string | null; emails: number; families: number }[];
  email_lookback_days: number;
  own_min: number;
  blended_min: number;
  email_rebuilt_at: string | null;
  social_rebuilt_at: string | null;
  pin_note_max: number;
}

export interface EmailPreview {
  subject: string;
  html: string;
  text: string;
  household: { id: string; name: string } | null;
  facts: { kind: string; word: string; as_of: string | null }[];
  footer_held: boolean;
  medicare: boolean;
  disclaimer_held: boolean;
}

export interface SeriesForm {
  name: string;
  kind: SeriesKindWord;
  count: number;
  cadence: CadenceWord;
  send_time: string;
  starts_on: string;
  audience_id: string | null;
  brief: string | null;
}

/** Row 9a.1: one platform rule as the Platform rules screen reads it (screen 63, modal 40). */
export interface PlatformRule {
  id: string;
  platform: "meta" | "google_ads" | "google_business_profile" | "google_search_console" | "linkedin" | "x" | "youtube" | "tiktok" | "ses" | "other";
  rule_key: string;
  capability: string | null;
  capability_label: string | null;
  account_slug: string | null;
  purpose: "inclusion" | "exclusion" | "observation" | null;
  status: "allowed" | "restricted" | "disabled" | "review";
  fallback: "ad_refused" | "exclusion_only" | "pause_company" | "none" | null;
  value_json: string;
  applies_to_companies_json: string | null;
  verified_at: string | null;
  verified_by_user_id: string | null;
  verified_by_name: string | null;
  source_url: string | null;
  notes: string | null;
  next_review_at: string | null;
  page_hash: string | null;
  page_checked_at: string | null;
  review_flagged_at: string | null;
  review_reason: string | null;
  version_number: number;
  created_at: string;
  updated_at: string;
  /** The decision a builder would read today: the lapse and status rules applied. */
  decision: { ok: boolean; state: "allowed" | "restricted" | "disabled" | "review" | "unverified" | "lapsed" | "missing"; sentence: string };
}
export interface PlatformRulesResponse {
  today: string;
  can_act: boolean;
  rules: PlatformRule[];
  waiting_on_grant: number;
  watch: { last_started_at: string | null; last_succeeded_at: string | null; last_failed_at: string | null; last_error: string | null };
}
export interface PlatformRuleVersion {
  id: string;
  policy_id: string;
  change_kind: string;
  prior_snapshot_json: string | null;
  reason_text: string | null;
  changed_by_user_id: string | null;
  changed_by_name: string | null;
  changed_at: string;
}
export interface PlatformRuleEditBody {
  status: PlatformRule["status"];
  fallback: PlatformRule["fallback"];
  next_review_at: string;
  notes: string;
  source_url: string;
  verification_note: string;
  verified_today: boolean;
  expected_version_number: number;
}

/** Who owns one desk (shared/db/marketing-desk-owners.ts): a setting Grant manages. */
export interface DeskOwnerRow {
  desk_key: "insurance" | "mortgage" | "financial" | "planning" | "realty" | "mortgage:private_money";
  label: string;
  owner: { user_id: string; name: string } | null;
  owner_from: "setting" | "fallback";
  setting: { id: string; note: string | null; effective_from: string; set_by_user_id: string | null } | null;
  choices: { user_id: string; name: string }[];
}
export interface CapacitySettingsResponse {
  today: string;
  week_start: string;
  desks: DeskSetting[];
  desk_owners: DeskOwnerRow[];
  can_edit: boolean;
}

/** Row 9a.2: GET /api/marketing/settings/budgets (screen 62's caps and meters). */
export interface BudgetsResponse {
  today: string;
  month: string;
  month_words: string;
  can_act: boolean;
  companies: BudgetMeterWire[];
  firm: BudgetMeterWire;
  /** True once any company has a provider-confirmed figure this month (row 9a.4). */
  measured_any: boolean;
  /** Confirmed spend no company owns this month, counted in the firm ceiling (S9-29). */
  unattributed?: { dollars: number; as_of: string } | null;
}
export interface BudgetVersion {
  id: string;
  budget_id: string;
  change_kind: string;
  prior_snapshot_json: string | null;
  reason_text: string | null;
  changed_by_user_id: string | null;
  changed_by_name: string | null;
  changed_at: string;
}
/** Row 9a.4: a provider object the OS may act on (an ad set, a search campaign, the Local Services campaign). */
export interface AdObjectView {
  id: string;
  provider: "meta" | "google_ads" | "google_lsa";
  connection_id: string;
  company_slug: string;
  kind: "ad_set" | "campaign" | "lsa_campaign";
  provider_object_id: string;
  provider_account_id: string;
  provider_budget_id: string | null;
  name: string | null;
  state: "live" | "paused" | "retired";
  provider_status: string | null;
  provider_status_at: string | null;
  budget_dollars: number | null;
  budget_period: string | null;
  campaign_id: string | null;
}
/** Row 9a.4: one command row in the execution outbox. */
export interface OutboxRowView {
  id: string;
  command: "create" | "pause" | "resume" | "budget" | "cap";
  provider: "meta" | "google_ads" | "google_lsa";
  object_id: string | null;
  provider_account_id: string;
  company_slug: string | null;
  payload_json: string;
  state: "queued" | "sending" | "confirmed" | "refused" | "interrupted";
  attempts: number;
  problem: string | null;
  batch_id: string | null;
  created_at: string;
  sent_at: string | null;
  confirmed_at: string | null;
}
export interface PaidSwitchResult {
  batch_id: string;
  rows: number;
  by_provider: { provider: "meta" | "google_ads" | "google_lsa"; rows: number; confirmed: number; refused: number; pending: number; skipped?: number }[];
}
export interface ExecutionResponse {
  can_act: boolean;
  kill_switch: { last_used_at: string | null; last_used_by_name: string | null; last_used_batch_id: string | null; last_tested_at: string | null; last_tested_by_name: string | null; last_result: PaidSwitchResult | null; last_result_words: string | null };
  /** Every object not retired in the viewer's scope: what the switch pauses (finding 3). */
  objects_to_pause: number;
  /** Of those, the ones the OS last read as running. */
  live_objects: number;
  objects: AdObjectView[];
  commands: OutboxRowView[];
  /** The objects "Resume this one" may start: paused by the switch while running (decision 3). */
  resumable_object_ids: string[];
  /** The last nightly spend read (Grant and system admins only; null for a company's desk). */
  last_pull: {
    as_of: string | null;
    mismatches: { provider: "meta" | "google_ads" | "google_lsa"; provider_account_id: string; provider_dollars: number; os_dollars: number; difference: number }[];
    providers: { provider: "meta" | "google_ads" | "google_lsa"; ok: boolean; rows: number; problem: string | null }[];
  } | null;
  assignments: { provider: "meta" | "google_ads"; provider_account_id: string; company_slug: string }[];
  runner_enabled: boolean;
}
/** Modal 41's body. */
export interface BudgetEditBody {
  cap_dollars: number;
  alert_pct: number;
  reason: string;
  expected_version_number: number;
}

/** The query the Today page sends (all optional; the server validates each). */
export interface MarketingTodayParams {
  company?: string;
  owner?: string;
  product?: string;
  band?: string;
  urgency?: string;
  moment?: string;
  filter_company?: string;
  advisor?: string;
  goes_away_days?: number;
  status?: string;
  sort?: string;
  limit?: number;
  offset?: number;
  export?: "1";
  /** Row 2.6: a firm viewer asking for a desk instead of the firm view. */
  view?: "desk";
}

/** The Re-run control's preview, as the server builds it (type only: nothing
 *  from the server module reaches the browser bundle). */
export type InsuranceRerunPreview = import("@shared/comp/insurance-month-rerun-types").RerunPreview;

/** Monthly Mortgage Summary shapes (types only; see the import-free types file). */
export type MortgageSummaryAdvisor = import("@shared/comp/mortgage-monthly-summary-types").SummaryAdvisor;
export type MortgageMonthlySummaryRec = import("@shared/comp/mortgage-monthly-summary-types").MortgageMonthlySummary;
export type MortgageSummaryEmailRec = import("@shared/comp/mortgage-monthly-summary-types").MortgageSummaryEmail;

export class ApiError extends Error {
  status: number;
  code: string | null;
  /** The full parsed JSON error body, when the server sent one. Lets a
   *  caller read structured detail (e.g. the active-attachments counts on
   *  a 409) without a bespoke fetch. Null when the body wasn't JSON. */
  data: unknown;

  constructor(status: number, message: string, code: string | null = null, data: unknown = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

function acaQuotePath(kind: AcaQuoteKind, id: string, year: number): string {
  return `/api/aca-quotes/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/${year}`;
}

function medicareQuotePath(kind: MedicareQuoteKind, id: string, year: number): string {
  return `/api/medicare-quotes/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/${year}`;
}

/** A failed file download (CSV, PDF) as an ApiError that keeps the
 *  server's JSON body, so a caller can recognize the health tier's
 *  refusal and draw the locked door instead of a generic "couldn't". */
async function fileDownloadError(resp: Response, fallback: string): Promise<ApiError> {
  let data: unknown = null;
  let code: string | null = null;
  try {
    const body = (await resp.json()) as { error?: string };
    data = body;
    code = body.error ?? null;
  } catch {
    // Not JSON: keep the fallback message.
  }
  return new ApiError(resp.status, fallback, code, data);
}

/** A stage move a person may still make with a reason: the 409
 *  `stage_blocked` body when it says `overridable`. Null for any other
 *  error, including a block nobody may override. */
export interface StageBlock {
  message: string;
  overrideLabel: string;
}

export function readStageBlock(err: unknown): StageBlock | null {
  if (!(err instanceof ApiError) || err.status !== 409 || err.code !== "stage_blocked") return null;
  const data = err.data as { overridable?: unknown; override_label?: unknown } | null;
  if (!data || data.overridable !== true || typeof data.override_label !== "string") return null;
  return { message: err.message, overrideLabel: data.override_label };
}

/**
 * The audit-file download found documents it can't read from storage.
 *
 * Its own type because the card needs the LIST and the offer, not just a
 * message: Grant hit "5 documents on this loan couldn't be read, so the
 * package wasn't built" on 2026-08-13 — true, and a dead end, because it
 * named neither which five nor what to do next.
 */
export class AuditFileUnavailableError extends Error {
  /** Checklist names + why each one failed. Never household names. */
  readonly documents: string[];
  /** True when the server will build the package without them. */
  readonly canBuildWithout: boolean;

  constructor(message: string, documents: string[], canBuildWithout: boolean) {
    super(message);
    this.name = "AuditFileUnavailableError";
    this.documents = documents;
    this.canBuildWithout = canBuildWithout;
  }
}

/** Mirror of shared/db/households.ts HouseholdActiveAttachments — the
 *  live records a household archive would strand, returned in the 409
 *  body so the escalate UI can show the consequence. */
export interface HouseholdActiveAttachments {
  activeServiceRecords: number;
  openOpportunities: number;
  openTransactions: number;
  activeRhythms: number;
  total: number;
}

// ─── Global session-expiry signal ──────────────────────────────────────
//
// Any authenticated API call can come back 401 when the Better Auth
// session cookie has expired or been invalidated server-side (idle
// timeout, the 8-hour window lapsing, an admin revoke). Without a global
// hook, each page handles that 401 on its own — and most don't, so the
// page strands on a dead spinner with no way forward. That's exactly the
// "My Households just kept loading and I had to re-sign-in" failure from
// Joey's first-impressions walkthrough (2026-06-26): the list fetch
// 401'd mid-session, the rows never rendered (so nothing was clickable),
// and the filter — which depends on a now-failing call — felt broken.
//
// The fix is one global handler: request() reports an unauthenticated
// 401 to a single listener that the SessionProvider registers. The
// provider flips the app to signed-out, which makes RequireAuth bounce
// to /sign-in with a friendly "session expired" notice AND the current
// location preserved, so re-auth returns the user exactly where they
// were instead of dumping them on a stuck screen.
//
// Auth/session/invite endpoints are EXCLUDED: a 401 from /api/session is
// the normal "not signed in" answer, and a 401 from /api/auth/sign-in is
// a bad-password attempt SignInPage already surfaces. Treating either as
// an expiry would loop the user back through sign-in.

type UnauthorizedListener = () => void;
let unauthorizedListener: UnauthorizedListener | null = null;

/** Register (or clear, with null) the app-wide expired-session handler. */
export function setUnauthorizedHandler(fn: UnauthorizedListener | null): void {
  unauthorizedListener = fn;
}

// Graph-disconnected signal — the sibling of the 401 handler above, for the
// OTHER silent-failure mode. When any Graph-backed endpoint answers 412 with
// code "not_connected" (a token that expired / failed to refresh / was
// revoked mid-session), we notify a single listener the app-wide
// ReconnectOffice365Banner registers, so a dropped Microsoft connection
// surfaces a "reconnect" prompt the moment it bites — WITHOUT looking like a
// logout (that's the 401 path). The listener re-checks the real connection
// status before deciding to show, so a genuinely never-connected user (who
// also gets 412 here) is handled by the gentle connect nudge instead.
type GraphConnectionLostListener = () => void;
let graphConnectionLostListener: GraphConnectionLostListener | null = null;

/** Register (or clear, with null) the app-wide Graph-disconnected handler. */
export function setGraphConnectionLostHandler(fn: GraphConnectionLostListener | null): void {
  graphConnectionLostListener = fn;
}

// Transient session-verification blip (Mike's "Couldn't verify your
// session just now" dead-end, 2026-07-23). require-auth.ts returns 503
// `session_unavailable` when Better Auth's getSession THROWS on a
// momentary D1 overload — deliberately 503 (retryable), NOT 401 (would
// log the user out). It fires BEFORE any route handler, so no mutation
// ever ran; retrying any method is safe. We retry a few times with
// backoff at the transport layer so a one-second blip self-heals instead
// of surfacing as a raw error the user has to react to.
const SESSION_UNAVAILABLE_RETRIES = 3;
const SESSION_UNAVAILABLE_BACKOFF_MS = [400, 900, 1600];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * A request that must not be allowed to hang forever (2026-09-12).
 *
 * There is deliberately NO blanket timeout on `request` — exports, PDF
 * builds and whole-book reports legitimately run long, and cutting them
 * off would break working features to fix a different problem. This is
 * opt-in, for calls where hanging is worse than failing.
 *
 * The case it was written for: saving an edit to an automation pauses the
 * rule, then writes. The write recomputes the rule's audience over the
 * whole book, so on a large rule it can outlive the request — and when it
 * never returns, the caller's catch never runs, the rule is left sitting
 * PAUSED, and the button sits on "Saving…" with nothing to click. Andrea
 * Middleton hit exactly that on 2026-09-12: twenty minutes on a dead
 * button, a rule switched off, and her edits lost. A request that fails is
 * recoverable; one that hangs is not.
 */
export class RequestTimeoutError extends Error {
  constructor(public readonly timeoutMs: number) {
    super("The server didn't answer in time, so this save was given up on.");
    this.name = "RequestTimeoutError";
  }
}

/** Long enough that a slow-but-working save still lands, short enough that
 *  a person is not left guessing. */
const SAVE_TIMEOUT_MS = 90_000;

/** Run `fn` with an abort signal that fires after `ms`, turning a hang into
 *  a typed failure the caller can recover from. */
async function withTimeout<T>(ms: number, fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fn(controller.signal);
  } catch (err) {
    if (controller.signal.aborted) throw new RequestTimeoutError(ms);
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response!: Response;
  for (let attempt = 0; ; attempt++) {
    response = await fetch(apiUrl(path), {
      ...init,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(init.headers ?? {}),
      },
    });
    // Retry ONLY the transient pre-handler session blip, and only a few
    // times. Peek at the body via a clone so the normal error path below
    // still reads the original response untouched.
    if (response.status === 503 && attempt < SESSION_UNAVAILABLE_RETRIES) {
      let transient = false;
      try {
        const peek = (await response.clone().json()) as { error?: string };
        transient = peek.error === "session_unavailable";
      } catch {
        transient = false;
      }
      if (transient) {
        await sleep(
          SESSION_UNAVAILABLE_BACKOFF_MS[attempt] ??
            SESSION_UNAVAILABLE_BACKOFF_MS[SESSION_UNAVAILABLE_BACKOFF_MS.length - 1]!,
        );
        continue;
      }
    }
    break;
  }

  // Feed the idle-warning clock. Any answered request means the session
  // cookie was accepted and the server's idle window slid forward, so this
  // is the honest definition of "the user is still working" — including
  // error responses, which reached a handler behind the auth gate just the
  // same. The one exception is the expiry 401 handled below.
  if (!shouldSignalSessionExpiry(path, response.status)) {
    recordActivity();
  }

  if (!response.ok) {
    // Signal an expired/invalid session globally BEFORE throwing, so the
    // app can redirect to sign-in even if the calling page swallows the
    // error. The throw still happens so existing local handlers behave
    // exactly as before.
    if (shouldSignalSessionExpiry(path, response.status)) {
      unauthorizedListener?.();
    }
    let code: string | null = null;
    let message = response.statusText || `HTTP ${response.status}`;
    let data: unknown = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
      };
      data = body;
      code = body.error ?? null;
      if (body.message) message = body.message;
    } catch {
      // Response wasn't JSON — keep the statusText message.
    }
    // A Graph-backed endpoint reporting the connection is down. Signal the
    // reconnect banner (it re-checks status before showing). Code is the
    // precise, Graph-only marker; the 412 guard keeps it from misfiring on
    // an unrelated conflict/precondition failure that happens to lack a code.
    if (response.status === 412 && code === "not_connected") {
      graphConnectionLostListener?.();
    }
    throw new ApiError(response.status, message, code, data);
  }

  return (await response.json()) as T;
}

// ─── Writes that must get through a network blip ───────────────────────
//
// Nate, 2026-09-22: a typed note came back "couldn't save." The notes code
// never saw the request — the hop between the browser and the API was
// down for moments at a time (the uptime monitor logged Cloudflare 530s
// and timeouts at the same minutes). One try and a red line is the wrong
// answer for a note; the person already did the work.
//
// This retries ONLY failures where the request plausibly never reached a
// handler, or reached one that told us to come back: the network itself
// failing, a timeout, and the gateway / "busy" statuses. A 4xx is an
// answer, not a blip, and is never retried.
//
// It is only safe for writes that are idempotent. The note endpoints are:
// the composer mints the note id up front and sends the same id on every
// attempt, so an attempt that landed but whose reply was lost comes back
// as "already saved" instead of a second copy (saveTypedNote on the
// server). Do not point a non-idempotent write at this helper.
const DELIVERY_RETRY_BACKOFF_MS = [600, 1500, 3000, 5000];
const DELIVERY_ATTEMPT_TIMEOUT_MS = 20_000;
const TRANSIENT_DELIVERY_STATUSES = new Set([408, 502, 503, 504, 520, 521, 522, 523, 524, 530]);

/** True when a failed request is worth sending again unchanged. */
export function isTransientDeliveryFailure(err: unknown): boolean {
  if (err instanceof RequestTimeoutError) return true;
  if (err instanceof ApiError) return TRANSIENT_DELIVERY_STATUSES.has(err.status);
  // fetch() rejects with a TypeError when the network itself fails
  // ("Failed to fetch", "Load failed", "NetworkError when attempting…").
  return err instanceof TypeError;
}

async function requestDelivered<T>(
  path: string,
  init: RequestInit,
  opts: { backoffMs?: readonly number[]; attemptTimeoutMs?: number } = {},
): Promise<T> {
  const backoff = opts.backoffMs ?? DELIVERY_RETRY_BACKOFF_MS;
  const timeoutMs = opts.attemptTimeoutMs ?? DELIVERY_ATTEMPT_TIMEOUT_MS;
  for (let attempt = 0; ; attempt++) {
    try {
      return await withTimeout(timeoutMs, (signal) => request<T>(path, { ...init, signal }));
    } catch (err) {
      if (attempt >= backoff.length || !isTransientDeliveryFailure(err)) throw err;
      await sleep(backoff[attempt]!);
    }
  }
}

/** Test seam: the delivery retry with a caller-chosen schedule. */
export const __requestDeliveredForTest = requestDelivered;

/** A fresh id for a note about to be saved. The composer holds on to it
 *  across retries of the same text (see requestDelivered). */
export function newNoteId(): string | undefined {
  try {
    return globalThis.crypto?.randomUUID?.();
  } catch {
    return undefined;
  }
}

/** The message a note composer shows when every retry failed. Says the
 *  one thing the person needs: the words are safe and one tap resends. */
export function noteSaveFailureMessage(err: unknown): string {
  if (isTransientDeliveryFailure(err)) {
    return "Couldn't reach the server, even after a few tries. Your note is still here. Tap Try again.";
  }
  if (err instanceof ApiError && err.message) {
    return `${err.message} Your note is still here.`;
  }
  return "Couldn't save the note. Your note is still here. Tap Try again.";
}

// Cascade-archive preview manifest (mirrors the server planner shape).
export interface ArchiveManifestGroupResponse {
  kind: string;
  label: string;
  count: number;
  live_count: number;
  transferable: boolean;
  sample: Array<{ kind: string; id: string; label: string; live: boolean }>;
}
export interface ArchiveManifestResponse {
  entity_kind: string;
  entity_id: string;
  groups: ArchiveManifestGroupResponse[];
  total: number;
  live_total: number;
  /** Contact cascades only (Grant 2026-07-30): set when this contact is
   *  the household's LAST live member, so archiving them would leave an
   *  orphaned shell. The dialog offers to archive it in the same action.
   *  `active_attachment_count > 0` means the household can't be archived
   *  (its guard refuses) — the dialog explains instead of offering. */
  orphaned_household?: {
    id: string;
    display_name: string;
    remaining_member_count: number;
    active_attachment_count: number;
  };
}

// ─── Financial source-of-truth import (Wave 1+2 of cross-source plan) ───

export type FinancialImportDecisionKind =
  | "matched_existing_appended_snapshot"
  | "matched_existing_updated_portfolio_and_snapshot"
  | "created_new_with_household_match"
  | "created_new_with_quarantined_household"
  | "skipped_duplicate_snapshot"
  | "skipped_missing_external_account_id"
  | "errored";

export interface FinancialImportDecision {
  source: string;
  external_account_id: string;
  kind: FinancialImportDecisionKind;
  holder_name_raw: string | null;
  total_balance_cents: number | null;
  portfolio_id: string | null;
  household_id: string | null;
  note: string | null;
}

export interface FinancialImportResult {
  rows_processed: number;
  rows_inserted: number;
  rows_skipped: Record<string, number>;
  rows_errored: number;
  snapshots_inserted: number;
  inserts: Array<{
    source: string;
    external_account_id: string;
    portfolio_id: string;
    service_record_id: string;
    snapshot_id: string;
    household_id: string;
  }>;
  decisions: FinancialImportDecision[];
  decisions_by_kind: Record<FinancialImportDecisionKind, number>;
  dry_run: boolean;
  /** Present when a ByAllAccounts CSV was part of the run: the rows the
   *  server refused to import as balances (alarmed / never aggregated). */
  byallaccounts_skipped?: ByAllAccountsSkippedRow[];
}

// ── Monthly advisory fee review (Financial compliance) ──────────────

export interface AltruistInvoiceUpload {
  householdLabel: string;
  invoiceNumber: string | null;
  days: number | null;
  totalAdbCents: number | null;
  totalFeeCents: number | null;
  accounts: {
    accountLabel: string;
    adbCents: number;
    feeCents: number;
    days: number | null;
  }[];
  accountHolders?: {
    holderName: string;
    accountType: string;
    maskedNumber: string;
    feeCents: number;
  }[];
}

export interface FeeReviewRequestBody {
  period: string; // YYYY-MM
  betterment_csv?: string;
  altruist_invoices?: AltruistInvoiceUpload[];
  /** Browser-side file-read problems (unreadable PDFs, empty ZIP) — the
   *  server persists these with the run so the saved record is honest
   *  about coverage. */
  client_warnings?: string[];
}

export interface FeeReviewComputedResult {
  household_id: string | null;
  label: string;
  custodian: "betterment" | "altruist_schwab";
  account_count: number;
  accounts: {
    client: string;
    account_label: string;
    adb_cents: number;
    fee_cents: number;
  }[];
  adb_cents: number;
  expected_fee_cents: number;
  actual_fee_cents: number;
  variance_cents: number;
  outcome: "pass" | "fail" | "waived";
}

export interface FeeReviewComputationResponse {
  period: string;
  days_in_period: number;
  totals: {
    household_count: number;
    passed_count: number;
    failed_count: number;
    waived_count: number;
    total_adb_cents: number;
    total_expected_cents: number;
    total_actual_cents: number;
  };
  results: FeeReviewComputedResult[];
  unmatched_clients: {
    client_name: string;
    custodian: "betterment" | "altruist_schwab";
    adb_cents: number;
    fee_cents: number;
    /** Households the name could belong to (a known person in several
     *  households) — rendered as one-click link choices. */
    candidates: { household_id: string; label: string }[];
  }[];
  warnings: string[];
  fee_schedule: {
    source: "comp_rules" | "default_fallback";
    tiers: { up_to_cents: number | null; rate: number }[];
  };
  sources: ("betterment" | "altruist")[];
  advicepay: AdvicePayCheckRec;
}

/** The $189/month planning-fee comparison — evidence, not a verdict. */
export interface AdvicePayCheckRec {
  roster: { household_id: string; label: string; adb_cents: number }[];
  expected_monthly_cents: number;
  excluded_unmatched_count: number;
  /** Fee-waived households left off the roster (absent on runs saved
   *  before this field existed). */
  excluded_waived_count?: number;
  /** null = no AdvicePay deposit recorded for the month (different from $0). */
  deposits_total_cents: number | null;
  deposit_count: number | null;
}

export interface FeeReviewRunSummaryRec {
  id: string;
  period: string;
  days_in_period: number;
  superseded_at: string | null;
  household_count: number;
  passed_count: number;
  failed_count: number;
  waived_count: number;
  unmatched_client_count: number;
  total_adb_cents: number;
  total_expected_cents: number;
  total_actual_cents: number;
  sources_json: string;
  created_by_user_id: string | null;
  created_at: string;
}

export interface FeeReviewRunDetail {
  run: FeeReviewRunSummaryRec & {
    fee_schedule_json: string;
    advicepay_json: string | null;
    /** JSON array of the warnings shown at save time; null on runs saved
     *  before warnings were persisted (unknown, not "none"). */
    warnings_json: string | null;
  };
  results: {
    id: string;
    review_id: string;
    household_id: string | null;
    external_label: string;
    custodian: "betterment" | "altruist_schwab";
    account_count: number;
    accounts_json: string;
    adb_cents: number;
    expected_fee_cents: number;
    actual_fee_cents: number;
    variance_cents: number;
    outcome: "pass" | "fail" | "waived";
    note: string | null;
  }[];
  unmatched: {
    client_name: string;
    custodian: "betterment" | "altruist_schwab";
    adb_cents: number;
    fee_cents: number;
    /** Live resolution against today's data (the saved list is a
     *  snapshot): the household this name matches NOW, if any. */
    resolves_now: { household_id: string; label: string } | null;
    candidates: { household_id: string; label: string }[];
  }[];
}

export interface FinancialImportRequestBody {
  period_start: string;
  period_end: string;
  schwab_balance_rows?: Array<Record<string, unknown>>;
  altruist_household_rows?: Array<Record<string, unknown>>;
  betterment_fees_csv?: string;
  betterment_account_rows?: Array<Record<string, unknown>>;
  betterment_trust_rows?: Array<Record<string, unknown>>;
  /** Morningstar ByAllAccounts — AccountView Accounts grid CSV, raw text. */
  byallaccounts_accounts_csv?: string;
}

/** A ByAllAccounts row the server refused to import as a balance. */
export interface ByAllAccountsSkippedRow {
  institution: string;
  name: string;
  number_last_four: string;
  reason: "not_aggregated" | "unknown_institution" | "missing_number";
  last_aggregated: string | null;
}

// ── Financial retention backfill (Bug B follow-up, 2026-04-29) ───────

export type FinancialRetentionDecisionKind =
  | "household_unmatched"
  | "no_hcr_row_for_household"
  | "would_create_hcr"
  | "no_change"
  | "would_set_client_since"
  | "would_set_relationship_ended"
  | "would_set_both";

export interface FinancialRetentionDecision {
  household_id: string | null;
  ac_contact_email: string | null;
  current_relationship_state: string | null;
  computed_client_since: string | null;
  computed_relationship_ended_at: string | null;
  has_open_deal: boolean;
  ac_deal_count: number;
  kind: FinancialRetentionDecisionKind;
  note: string | null;
}

export interface FinancialRetentionBackfillResult {
  rows_in_csv: number;
  rows_in_pipeline: number;
  households_resolved: number;
  households_unresolved: number;
  decisions: FinancialRetentionDecision[];
  decisions_by_kind: Record<FinancialRetentionDecisionKind, number>;
  hcr_rows_updated: number;
  errors: number;
  dry_run: boolean;
}

export interface FinancialRetentionBackfillRequestBody {
  ac_deal_rows: Array<Record<string, unknown>>;
  pipeline_filter?: string;
}

// ── Betterment household mapping import (PR #267) ────────────────────

export type BettermentMappingDecisionKind =
  | "would_set_alias"
  | "would_overwrite_existing"
  | "no_change"
  | "no_contact_match"
  | "ambiguous_multiple_contacts"
  | "ambiguous_multiple_households"
  | "unparseable_client"
  | "missing_fields"
  | "errored";

export type BettermentMappingMatchMethod =
  | "full_name"
  | "last_name_only"
  | "household_hint_disambiguation"
  | null;

export interface BettermentMappingDecision {
  client: string | null;
  household_label: string | null;
  parsed_first_name: string | null;
  parsed_last_name: string | null;
  match_method: BettermentMappingMatchMethod;
  resolved_household_id: string | null;
  resolved_contact_id: string | null;
  current_alias_on_contact: string | null;
  kind: BettermentMappingDecisionKind;
  note: string | null;
}

export interface BettermentMappingResult {
  rows_in_csv: number;
  rows_processed: number;
  decisions: BettermentMappingDecision[];
  decisions_by_kind: Record<BettermentMappingDecisionKind, number>;
  contacts_updated: number;
  errors: number;
  dry_run: boolean;
}

export interface BettermentMappingRequestBody {
  rows: Array<Record<string, unknown>>;
}

// Financial advisor attribution backfill — re-process AC contacts CSV
// to populate household_company_relationships.current_advisor_user_id
// for households tagged with the Jake/Brian Clients-Family tags.
export type FinancialAdvisorAttributionDecisionKind =
  | "would_create_hcr"
  | "would_set_advisor"
  | "would_overwrite_advisor"
  | "no_change"
  | "ambiguous_household"
  | "unmatched_email"
  | "no_household_for_contact"
  | "no_relevant_tag"
  | "missing_email"
  | "errored";

export interface FinancialAdvisorAttributionDecision {
  email: string | null;
  matched_tag: string | null;
  proposed_advisor_user_id: string | null;
  proposed_advisor_name: string | null;
  resolved_household_id: string | null;
  household_display_name: string | null;
  current_hcr_advisor_user_id: string | null;
  kind: FinancialAdvisorAttributionDecisionKind;
  note: string | null;
}

export interface FinancialAdvisorAttributionResult {
  rows_in_csv: number;
  rows_with_relevant_tag: number;
  unique_households_resolved: number;
  ambiguous_households: number;
  decisions: FinancialAdvisorAttributionDecision[];
  decisions_by_kind: Record<FinancialAdvisorAttributionDecisionKind, number>;
  hcr_rows_inserted: number;
  hcr_rows_updated: number;
  errors: number;
  dry_run: boolean;
}

export interface FinancialAdvisorAttributionRequestBody {
  ac_contact_rows: Array<Record<string, unknown>>;
}

// ─── API surface ────────────────────────────────────────────────────────

// ── Unified Cleanup System (B1 read surface) ─────────────────────────

// Grant, 2026-09-10 — the cleanup dashboard's coverage panel. Mirrors
// shared/db/cleanup-detector-health.ts's DetectorHealthRow.
export type CleanupDetectorHealthStatus =
  | "ok"
  | "no-issues"
  | "stale"
  | "stalled"
  | "erroring"
  | "never-run";

export interface CleanupDetectorHealthRow {
  detectorSlug: string;
  title: string;
  issueFamily: string;
  entityKind: string;
  status: CleanupDetectorHealthStatus;
  statusLabel: string;
  lastSweptAt: string | null;
  lastCandidates: number | null;
  lastDurationMs: number | null;
  lastError: string | null;
  lastErrorAt: string | null;
  possiblyTruncated: boolean;
}

export interface CleanupDetectorHealthResponse {
  detectors: CleanupDetectorHealthRow[];
}

export interface CleanupSummaryResponse {
  burndown: {
    totalIssues: number;
    open: number;
    resolved: number;
    dismissed: number;
    autoResolved: number;
    snoozed: number;
    reopened: number;
  };
  byTier: Record<string, number>;
  byFamily: Record<string, number>;
  myOpen: number;
  /** B3 — human decisions in the trailing windows (actor-scoped;
   *  auto-resolves never count). */
  decisions7d: number;
  decisions30d: number;
  // F1 — feedback-loop telemetry (UX rule #8). Global all-time.
  feedback: {
    decisions: number;
    accepted: number;
    overridden: number;
    rolledBack: number;
    autoResolved: number;
    reopened: number;
    avgDecisionMinutes: number | null;
    avgDecisionSeconds: number | null;
  };
}

// Donna's resolver (2026-07-23) — GET /api/cleanup/issues/:id/quarantine-context.
export interface QuarantineResolveContext {
  quarantine_id: string;
  resolution: string;
  source: string;
  source_file: string | null;
  imported: {
    name: string | null;
    spouse_name_raw: string | null;
    email: string | null;
    phone: string | null;
  };
  primary_household: {
    household_id: string;
    display_name: string;
    members: string[];
  } | null;
  candidates: Array<{
    household_id: string;
    display_name: string;
    members: string[];
    city: string | null;
    state: string | null;
    zip: string | null;
    active_services: number;
    is_primary_household: boolean;
  }>;
}

export interface CleanupLane {
  id: string;
  label: string;
  description: string;
  count: number;
  filter: {
    family?: string;
    tier?: string;
    state?: string;
    assignedToMe?: boolean;
    followUpMine?: boolean;
    /** Global operators only (system_admin/compliance) — see
     *  unownedWhere() in shared/db/cleanup-lanes.ts. */
    unowned?: boolean;
  };
  tone: "auto" | "standard" | "attention" | "history";
}

export interface CleanupIssueSummary {
  id: string;
  issueKey: string;
  detectorSlug: string;
  issueFamily: string;
  entityKind: string;
  owningCompanySlug: string | null;
  decisionQuestion: string;
  flagExplanation: string;
  confidence: number;
  risk: number;
  tier: string;
  state: string;
  /** The change this issue asks for, declared by its detector (see
   *  shared/cleanup/fix.ts). The card renders the button from THIS, never
   *  from the detector's name. Null until the detector declares one. */
  fix?: CleanupFix | null;
  /** One plain sentence on how the data got this way. */
  howItHappened?: string | null;
  /** The detector's plain-language reasons (already on the wire — the
   *  server returns the full parsed issue). Strings in practice. */
  flagReasons?: unknown[];
  recommendation: {
    verb: string;
    label: string;
    reason: string;
    // Detector-supplied hints for the decide rail (e.g., for merge
    // verbs: `survivor_id` to disambiguate which side of a 2-record
    // cluster is the intended survivor). Permissive shape because
    // each verb's params bag is its own contract; see
    // shared/db/cleanup-detectors/shared.ts MergeRecommendationParams
    // for the merge convention.
    params?: Record<string, unknown>;
  } | null;
  affectedRecords: Array<{
    kind: string;
    id: string;
    label: string;
    /** Household refs only: "Tom & Carolyn Jones" — which Jones this
     *  is, attached server-side at list time. */
    members_label?: string | null;
    /** Household refs only: true when that household has since been
     *  archived or merged away. Attached server-side at list time (the
     *  frozen affected-records list can't know). Absent means "not
     *  provably gone", never "confirmed live". */
    record_gone?: boolean;
  }>;
  assignedOwnerUserId: string | null;
  // Follow-up parking (Donna's request). Set when this issue is parked
  // in the "Waiting on follow-up" lane; null otherwise. Already on the
  // wire (the server returns the full parsed CleanupIssue).
  followupAt?: string | null;
  followupByUserId?: string | null;
  followupReason?: string | null;
  lastSeenAt: string;
  // F3 — already on the wire (parseCleanupIssue maps
  // first_detected_at); typed now so the queue can render an
  // SLA/age tag. ISO timestamp of when the issue was first detected.
  firstDetectedAt: string;
}

export interface CleanupIssueListResponse {
  total: number;
  /** Per-answer-shape tallies over the whole filtered set (not narrowed by
   *  `shape` itself), counted server-side. The filter chips must never be
   *  counted off the loaded page. */
  shapeCounts: Record<AnswerShape, number>;
  items: CleanupIssueSummary[];
}

// P0-2 — the per-group "Resolve all" preview: what the bulk button will
// DO, in plain English, per detector/verb group.
export interface CleanupAutoResolveGroup {
  detector_slug: string;
  verb: string;
  label: string;
  count: number;
}

// C1: response of GET /api/cleanup/for-record/:kind/:id — the open
// cleanup issues affecting one record, for the in-record drawer. The
// server returns full CleanupIssue objects (a structural superset of
// CleanupIssueSummary); the drawer's issue list only needs the
// summary fields, and ResearchPane re-fetches full detail per issue.
export interface CleanupForRecordResponse {
  issues: CleanupIssueSummary[];
  count: number;
}

// C3 — body for POST /api/cleanup/manual-issue ("Flag a problem").
// problem_type is a ManualProblemType (see
// shared/db/cleanup-manual-issue.ts — the picker imports the
// definitions from there directly).
export interface CleanupManualIssueBody {
  entity_kind: string;
  entity_id: string;
  entity_label: string;
  problem_type: string;
  note: string;
  assigned_owner_user_id?: string | null;
}

// B4 — the actor's authorization decision for an issue's primary
// record. `allowed=false` ⇒ the decide rail shows a view-only banner
// (reason) + forward paths (pathForward) instead of action buttons
// that would 403. Single source of truth: server-side canActOnEntity.
export interface CleanupAuthz {
  allowed: boolean;
  companySlug: string | null;
  reason: string;
  pathForward?: string;
}

export interface CleanupIssueDetailResponse {
  issue: CleanupIssueSummary & {
    flagReasons: unknown[];
    evidence: Record<string, unknown>;
    survivorship: unknown[] | null;
  };
  notes: Array<{
    id: string;
    issueId: string;
    authorUserId: string | null;
    // Author display name, joined server-side from users. Null for a
    // system-authored note or a since-deleted author.
    authorName: string | null;
    body: string;
    createdAt: string;
  }>;
}

export interface DataNeededQueueItem {
  id: string;
  detector_slug: string;
  issue_family: string;
  entity_kind: string;
  owning_company_slug: string | null;
  decision_question: string;
  flag_explanation: string;
  tier: string;
  primary_record_id: string | null;
  primary_record_label: string | null;
  /** "Tom & Carolyn Jones" for household-grain rows (Nate 2026-07-02). */
  primary_record_members: string | null;
}
export interface DataNeededQueue {
  mode: "universal" | "company";
  companies: string[];
  total: number;
  by_family: Array<{ family: string; count: number }>;
  items: DataNeededQueueItem[];
}

/** The page-size cap the server enforces on the Data Needed worklist —
 *  the value the "All" page-size option resolves to (mirrors
 *  MY_QUEUE_MAX_LIMIT in shared/db/cleanup-lanes.ts). */
export const DATA_NEEDED_QUEUE_MAX_LIMIT = 500;

// S3 — Data Needed judgment items (the Category-6 worklist, Zone 1 of
// /data-needed). Mirrors shared/db/data-needed.ts.
export type JudgmentItemType =
  | "fit_assessment"
  | "opportunity_numbers"
  | "transaction_numbers"
  | "borrower_tier"
  | "what_matters_most"
  // (History: preferred_name, referral_source, marital_status, and an email
  // opt-in confirm were all contact-identity items here; retired
  // 2026-07-08/07-10/07-15 — marital status was the biggest noise source on
  // advisor Data Needed dashboards and is factual intake data, not an advisor
  // call. All stay ordinary editable contact fields. Mirrors
  // shared/judgment/catalog.ts; see the RETIRED notes in
  // shared/db/cleanup-detectors/contact-completeness.ts.)
  // Client-since — scoreboard-only (its worklist stays in the routed
  // cleanup lane); appears on /data-needed/team + snapshots only.
  | "client_since"
  // Planning cases in force (Grant, 2026-10-20). Mirrors shared/judgment/catalog.ts.
  | "planning_case_revenue"
  | "planning_case_advisor";

/** Planning case facts on the two case items. Mirrors shared/db/data-needed.ts. */
export interface PlanningCaseContext {
  case_type: string;
  carrier_name: string | null;
  product_name: string | null;
  policy_number: string | null;
  annual_premium: number | null;
  monthly_premium: number | null;
  effective_date: string | null;
  estimated_revenue_year_1: number | null;
  advisor_user_id: string | null;
  advisor_name: string | null;
  advisor_active: boolean;
  /** What the Planning rate on file says the first year pays (dollars), or null with the reason in suggested_words. */
  suggested_revenue?: number | null;
  suggested_words?: string | null;
}

export interface MyJudgmentItemNumbers {
  expected_close_date: string | null;
  product_metric: number | null;
  estimated_revenue_year_1: number | null;
}

// Relationship evidence on fit cards. Mirrors shared/db/data-needed.ts.
export interface FitJudgmentContext {
  client_since: string | null;
  active_service_count: number;
  active_services: string[];
  other_active_companies: string[];
  last_touch_at: string | null;
  referral_source: string | null;
  what_matters_most: string | null;
  /** A sibling company's recorded fit — a one-click proposal, never
   *  written for the advisor (mirrors shared/db/data-needed.ts). */
  sibling_fit: { company_slug: string; fit: "ideal_fit" | "typical" | "poor_fit" } | null;
}

// "Which of this household's records is this?" facts on a workflow-record
// item (Nate 2026-07-29). Mirrors shared/db/data-needed.ts.
export interface JudgmentRecordContext {
  property_address: string | null;
  property_link_missing: boolean;
  closed_on: string | null;
  started_on: string | null;
  stage: string | null;
  company_record_count: number;
  /** Realty + linked property only: BrokerMint commission breakdown
   *  already on the property record (dollars). Prefill, never re-asked. */
  realty_commission: {
    office_gross: number | null;
    agent_net: number | null;
    office_net: number | null;
  } | null;
  /** Sale price / Product Metric — known whether or not there's an
   *  address on file; a fallback disambiguator (Nate 2026-08-04). */
  sale_price: number | null;
  /** Current advisor's name, when assigned. */
  advisor_name: string | null;
}

export interface MyJudgmentItem {
  issue_id: string;
  item_type: JudgmentItemType;
  /** Whose list this is on; null when unrouted. Shown only in the admin
   *  "Everyone" view of Data Needed. */
  routed_to: { user_id: string; name: string | null } | null;
  label: string;
  question: string;
  explanation: string;
  company_slug: string | null;
  record: { kind: string; id: string; label: string };
  /** "Tom & Carolyn Jones" for household-grain items (Nate 2026-07-02). */
  household_members: string | null;
  numbers: MyJudgmentItemNumbers | null;
  /** Transaction items only — the disambiguators (dates, stage, count). */
  record_context: JudgmentRecordContext | null;
  fit_context: FitJudgmentContext | null;
  /** Planning case items only: what is on file for the case. */
  planning_case?: PlanningCaseContext | null;
  first_detected_at: string;
}

export interface MyJudgmentList {
  total: number;
  by_type: Array<{ item_type: JudgmentItemType; label: string; count: number }>;
  items: MyJudgmentItem[];
  /** Full outstanding count from the latest scoreboard snapshot (the
   *  worklist is capped, so the page shows "first N of M"). */
  scoreboard_pending: number | null;
}

// S3 PR 6 — the team judgment-completion scoreboard (all employees).
export interface JudgmentCompletionCell {
  item_type: JudgmentItemType;
  applicable: number;
  completed: number;
}

export interface JudgmentTeamResponse {
  as_of: string;
  overall: JudgmentCompletionCell[];
  by_company: Array<{
    company_slug: string | null;
    cells: JudgmentCompletionCell[];
  }>;
  by_advisor: Array<{
    user_id: string | null;
    name: string | null;
    email: string | null;
    cells: JudgmentCompletionCell[];
  }>;
  trend: Array<{
    captured_on: string;
    item_type: JudgmentItemType;
    applicable: number;
    completed: number;
  }>;
}

// S3 PR 9 — AI relationship summaries (the record-top cliff-notes card).
// Upcoming automated communications (2026-07-03).
export interface UpcomingCommunicationItem {
  source: "automation" | "ongoing_care" | "deferred";
  automation_id: string | null;
  rule_name: string;
  rhythm_instance_id: string | null;
  touchpoint_id: string | null;
  entity_type: string;
  entity_id: string;
  contact_id: string | null;
  household_id: string | null;
  recipient_label: string;
  recipient_email: string | null;
  sender_user_id: string | null;
  sender_name: string | null;
  mode: "draft" | "send";
  channel: "outlook" | "bulk";
  subject_preview: string | null;
  expected_date: string | null;
  date_kind: "exact" | "next_run";
  /** False for Ongoing Care rows: the care tracker PLANS the touch, but
   *  nothing sends it (the touchpoint engine was retired 2026-07-19), so
   *  a human does. The UI must not present these as automatic. */
  will_fire_automatically: boolean;
}

/** An automated task a rule is going to create (2026-08-02). */
export interface UpcomingTaskItem {
  source: "automation" | "deferred" | "ongoing_care";
  automation_id: string | null;
  rule_name: string;
  rhythm_instance_id: string | null;
  touchpoint_id: string | null;
  entity_type: string;
  entity_id: string;
  contact_id: string | null;
  household_id: string | null;
  assignee_user_id: string | null;
  assignee_name: string | null;
  title: string;
  action_type: "create_task" | "create_reminder" | "flag_for_review" | "notify_teammate";
  priority: string;
  due_date: string | null;
  about_label: string;
  expected_date: string | null;
  date_kind: "exact" | "next_run";
  will_fire_automatically: boolean;
}

export interface UpcomingCommunicationsResult {
  items: UpcomingCommunicationItem[];
  tasks: UpcomingTaskItem[];
  capped_rules: string[];
}

export interface UpcomingCommunicationsSummary {
  horizon_days: number;
  to_my_clients: number;
  from_me: number;
  my_company: number;
  tasks_for_me: number;
  tasks_to_my_clients: number;
  tasks_my_company: number;
  capped_rules: string[];
  /** When the firm-wide half of these counts was worked out. The tile
   *  shows it: these numbers are refreshed on a schedule, and a cached
   *  number presented as live is a promise the OS did not keep. */
  computed_at: string | null;
}

/** Event-triggered rules that could fire on a record (2026-08-02). */
export interface StandingAutomationRow {
  id: string;
  display_name: string;
  sentence: string | null;
  state: string;
  event_type: string | null;
}

export interface StandingAutomationsResponse {
  automations: StandingAutomationRow[];
}

/** "Which automations run on my clients?" (2026-08-02). */
export type BookRuleReason = "scheduled" | "recent_activity" | "standing";

export interface BookRuleSummary {
  id: string;
  display_name: string;
  sentence: string | null;
  state: string;
  scope: string;
  scope_company: string | null;
  trigger_type: string;
  owner_user_id: string | null;
  owner_name: string | null;
  reasons: BookRuleReason[];
  scheduled_emails: number;
  scheduled_tasks: number;
  scheduled_households: number;
  last_acted_at: string | null;
}

export interface BookRulesResult {
  rules: BookRuleSummary[];
  horizon_days: number;
  capped_rules: string[];
  /** When the firm-wide half was worked out (the 15-minute snapshot the
   *  dashboard tiles share). The screen shows it: a cached number
   *  presented as live is the accuracy rule failing quietly. */
  computed_at: string | null;
}

export interface UpcomingCommunicationPreview {
  subject: string;
  body_html: string;
  mode: "draft" | "send";
  channel: "outlook" | "bulk";
  sender_user_id: string | null;
  sender_name: string | null;
  recipient_email: string | null;
  template_missing: boolean;
}

export interface AiRelationshipSummaryResponse {
  summary_text: string;
  model: string;
  generated_at: string;
  refreshed: boolean;
  refresh_error: boolean;
}

// ─── Bulk email (SES) campaigns — PR-SES-4 ──────────────────────────

/** Result of GET /api/communications/ses-check (admin diagnostic). */
export interface SesConnectionCheck {
  ok: boolean;
  /** SES region the check ran against, e.g. "us-east-2". */
  region: string;
  /** Present when ok — the account's sending posture. */
  account?: {
    productionAccessEnabled: boolean | null;
    sendingEnabled: boolean | null;
    enforcementStatus: string | null;
    maxSendPerDay: number | null;
    sentLast24Hours: number | null;
    maxSendRatePerSecond: number | null;
  };
  /** Present when !ok — plain-English cause naming what to fix. */
  message?: string;
  error?: string;
  aws_type?: string | null;
}

export interface EmailInfraStatus {
  bounce_wiring: {
    /** True when sends carry a configuration set AND the webhook knows
     *  its topic — i.e. bounces feed the suppression list automatically. */
    wired: boolean;
    configuration_set: string | null;
    sns_topic_arn: string | null;
    wired_at: string | null;
    env_override: boolean;
  };
  suppressions_by_reason: Record<string, number>;
}

export interface WireBounceResult {
  ok: boolean;
  steps: { step: string; result: string }[];
  configuration_set?: string;
  sns_topic_arn?: string;
  /** Addresses imported from Amazon's account-level bounce list. */
  imported?: number;
  message: string;
  error?: string;
  aws_type?: string | null;
}

export interface BulkCampaign {
  id: string;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  from_email: string;
  from_name: string | null;
  reply_to: string | null;
  audience_filter: string; // JSON
  status: "draft" | "sending" | "sent" | "failed" | "cancelled";
  total_recipients: number;
  sent_count: number;
  suppressed_count: number;
  failed_count: number;
  configuration_set: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  /** ISO instant a scheduled blast goes out, or null. Still a draft. */
  scheduled_send_at: string | null;
  scheduled_by_user_id: string | null;
  /** Marketing Hub row 6.1: the company whose sending subdomain the blast
   *  goes out From, or null for the sender's own stewardship.pro address. */
  sending_company_slug?: string | null;
  /** Why a send door declined this draft, or null (gate 23). */
  send_blocked_reason?: string | null;
  /** Program audit PR 1 (decision SPA-4): where the draft stands with
   *  Grant. Set on the list; absent on older responses. */
  approval_state?: BlastApprovalState;
  approval_requested_at?: string | null;
  approved_at?: string | null;
  /** Bulk-email click tracking: people who clicked a link in this sent
   *  blast, one each, real browsers only (the count Results shows). null:
   *  the blast carried no counted link. Set on the list only. */
  people_clicked?: number | null;
}
/** Where a blast stands with Grant (shared/db/blast-approval.ts). */
export type BlastApprovalState = "not_asked" | "waiting" | "approved" | "changed_since";
/** A company the viewer may send a blast as (row 6.1). */
export interface SendingCompanyChoice {
  slug: string;
  name: string;
  domain: string;
}
/** One GoDaddy record still owed, in GoDaddy's own words. */
export interface SendingDnsStep {
  slug: string;
  type: "MX" | "TXT";
  name: string;
  value: string;
  priority?: number;
  why: string;
}
export interface SendingSubdomainView {
  slug: string;
  domain: string;
  mail_from_domain: string;
  readiness: "ready" | "needs_records" | "needs_setup" | "not_in_ses" | "unknown";
  can_send: boolean;
  sentence: string;
  dkim: string | null;
  mail_from: string | null;
  dmarc_record: string | null;
  records_owed: SendingDnsStep[];
}
/** Bulk-email click tracking on Email Health: the switch and the last
 *  30 days of follows by verdict. */
export interface ClickTrackingReport {
  on: boolean;
  /** Grant's setting, the Worker's default, or neither (off). */
  source: "setting" | "environment" | "default";
  updated_at: string | null;
  window_days: number;
  counts: { person: number; robot_agent: number; scanner_burst: number };
  can_change: boolean;
}
/** Row 6.2: Google Postmaster for stewardship.pro (Email Health). */
export interface PostmasterReport {
  domain: string;
  connected: boolean;
  latest_read: { read_at: string; outcome: string; days_written: number; sentence: string } | null;
  current_spam_rate: { rate: number; date: string } | null;
  stop_rate: number;
  watch_rate: number;
  days: {
    stat_date: string;
    spam_rate: number | null;
    spf_success_rate: number | null;
    dkim_success_rate: number | null;
    dmarc_success_rate: number | null;
    delivery_error_rate: number | null;
  }[];
  sends: { id: string; subject: string; sent_at: string | null; sent: number; complaints: number; unsubscribes: number }[];
}
export interface SendingDomainsReport {
  ses_configured: boolean;
  checked_at: string;
  dmarc_value: string;
  can_set_up: boolean;
  subdomains: SendingSubdomainView[];
}
export interface BulkAudienceSummary {
  eligible: number;
  suppressed: number;
  /** Families paused for marketing (row 2.5); left out of the send. */
  paused?: number;
  will_receive: number;
}
/** What the composer sends as `audience_filter`: the quick picks as
 *  strings, an explicit contact-id list, and/or the registry-driven
 *  condition tree the Contacts list uses. */
export type BulkAudienceFilterPayload = Record<string, string | string[] | FilterSet>;
export interface BulkCampaignSendResult {
  /** "sent" once fully drained, "sending" while a long tail finishes in
   *  the background (the hourly drain cron completes it). */
  status?: string;
  total: number;
  sent: number;
  failed: number;
  /** Recipients still queued (background/cron will send them). */
  remaining?: number;
  truncated: number;
}

// ─── Outlook paced batch sends ("Email selected", 2026-08-01) ────────
export type OutlookBatchPacingMode = "spread_4h" | "next_business_day";
export interface OutlookBatchSend {
  id: string;
  sender_user_id: string;
  subject: string;
  body_html: string;
  pacing_mode: OutlookBatchPacingMode;
  window_start_at: string;
  window_end_at: string;
  status: "sending" | "sent" | "partial" | "failed" | "cancelled";
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}
export interface OutlookBatchRecipient {
  id: string;
  batch_id: string;
  contact_id: string;
  email: string;
  first_name: string | null;
  household_id: string | null;
  send_after: string;
  status: "pending" | "sent" | "failed" | "skipped";
  status_detail: string | null;
  sent_at: string | null;
}
export type BatchSendVerdict =
  | "ok"
  | "not_found"
  | "archived"
  | "no_email"
  | "do_not_email"
  | "duplicate_email";
export interface BatchSendPreviewRecipient {
  contact_id: string;
  name: string;
  email: string | null;
  household_id: string | null;
  verdict: BatchSendVerdict;
}
export interface BatchSendPreview {
  recipients: BatchSendPreviewRecipient[];
  counts: Record<BatchSendVerdict, number>;
  will_send: number;
  windows: Record<OutlookBatchPacingMode, { startAtIso: string; endAtIso: string }>;
  max: number;
}

// ─── Personal cockpit tools — rocks + team check-ins (Mike PR2) ──────
export type RockStatus = "on_track" | "at_risk" | "off_track" | "done";
export interface AdvisorRock {
  id: string;
  user_id: string;
  title: string;
  detail: string | null;
  quarter: string | null;
  status: RockStatus;
  sort_order: number;
  created_at: string;
  updated_at: string;
}
export interface TeamCheckin {
  id: string;
  owner_user_id: string;
  person_name: string;
  person_org: string | null;
  teammate_user_id: string | null;
  cadence_days: number;
  last_checkin_at: string | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  /** Derived on read by the server. */
  is_overdue: boolean;
  days_since: number | null;
}

/** What GET /api/transactions/:id/move-back-preview returns. */
export interface MoveBackPreview {
  eligible: true;
  company_slug: string;
  product_label: string;
  reopens_existing: boolean;
  opportunity_id: string | null;
  suggested_stage: string;
  suggested_stage_label: string;
  stages: { slug: string; label: string }[];
  clears_subject_property: boolean;
  archives_property_record: boolean;
  /** Realty: the record is parked and reused, not closed (2026-09-21). */
  parks_record: boolean;
  /** Realty: the accepted offer that will be marked "Contract cancelled". */
  accepted_offer_id: string | null;
  fell_through_reasons: { value: string; label: string }[];
  consequences: string[];
}

export const api = {
  async getSession(): Promise<SessionResponse> {
    return request<SessionResponse>("/api/session");
  },

  // ─── Profile photo (avatar) ────────────────────────────────────────
  // Uploads a new profile image and returns the app-internal URL the UI
  // should render (also persisted to the session on the next refresh).
  // Multipart, so it can't go through request() (that forces JSON).
  async uploadAvatar(file: File): Promise<{ image: string }> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(apiUrl("/api/me/avatar"), {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      let payload: { error?: string; message?: string } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        // non-JSON error body — fall back to a generic message
      }
      throw new ApiError(
        res.status,
        payload.message ?? payload.error ?? `upload_failed_${res.status}`,
        payload.error ?? null,
      );
    }
    return (await res.json()) as { image: string };
  },

  // Removes the profile image; UI falls back to the initials circle.
  async removeAvatar(): Promise<{ image: null }> {
    return request<{ image: null }>("/api/me/avatar", { method: "DELETE" });
  },

  // Team avatar directory — {user_id, name, image} for every active
  // employee. Loaded once + cached so advisor-avatar render sites can
  // show teammates' profile photos.
  async getTeamAvatars(): Promise<{ avatars: TeamAvatar[] }> {
    return request<{ avatars: TeamAvatar[] }>("/api/team/avatars");
  },

  // ─── Personal cockpit tools (Mike PR2) — rocks + team check-ins ────
  async listRocks(): Promise<{ rocks: AdvisorRock[] }> {
    return request<{ rocks: AdvisorRock[] }>("/api/me/rocks");
  },
  async createRock(input: {
    title: string;
    detail?: string | null;
    quarter?: string | null;
    status?: RockStatus;
  }): Promise<{ rock: AdvisorRock }> {
    return request<{ rock: AdvisorRock }>("/api/me/rocks", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateRock(
    id: string,
    patch: Partial<{
      title: string;
      detail: string | null;
      quarter: string | null;
      status: RockStatus;
      sort_order: number;
    }>,
  ): Promise<{ rock: AdvisorRock }> {
    return request<{ rock: AdvisorRock }>(`/api/me/rocks/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  async deleteRock(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/me/rocks/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  async listTeamCheckins(): Promise<{ checkins: TeamCheckin[] }> {
    return request<{ checkins: TeamCheckin[] }>("/api/me/team-checkins");
  },
  async createTeamCheckin(input: {
    person_name: string;
    person_org?: string | null;
    cadence_days?: number;
    last_checkin_at?: string | null;
    notes?: string | null;
  }): Promise<{ checkin: TeamCheckin }> {
    return request<{ checkin: TeamCheckin }>("/api/me/team-checkins", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async logTeamCheckin(id: string): Promise<{ checkin: TeamCheckin }> {
    return request<{ checkin: TeamCheckin }>(
      `/api/me/team-checkins/${encodeURIComponent(id)}/log`,
      { method: "POST" },
    );
  },
  async deleteTeamCheckin(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/me/team-checkins/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  // ─── Microsoft Bookings sync ───────────────────────────────────────
  // Status is readable by any signed-in employee; running the sweep and
  // toggling a page are admin-gated server-side.
  async getBookingsSyncStatus(): Promise<BookingsSyncStatus> {
    return request<BookingsSyncStatus>("/api/integrations/bookings/status");
  },

  async runBookingsSyncNow(pageId?: string): Promise<{ summary: BookingsSweepSummaryDto }> {
    return request<{ summary: BookingsSweepSummaryDto }>("/api/integrations/bookings/sync-now", {
      method: "POST",
      body: JSON.stringify(pageId ? { page_id: pageId } : {}),
    });
  },

  async setBookingsPageEnabled(
    pageId: string,
    enabled: boolean,
  ): Promise<{ ok: true; page_id: string; enabled: boolean }> {
    return request<{ ok: true; page_id: string; enabled: boolean }>(
      `/api/integrations/bookings/pages/${encodeURIComponent(pageId)}/enabled`,
      { method: "POST", body: JSON.stringify({ enabled }) },
    );
  },

  async getCleanupSummary(company?: string | null): Promise<CleanupSummaryResponse> {
    const qs = company ? `?company=${encodeURIComponent(company)}` : "";
    return request<CleanupSummaryResponse>(`/api/cleanup/summary${qs}`);
  },

  // The cleanup dashboard's coverage panel (Grant, 2026-09-10) —
  // system_admin/compliance only server-side. Worst-first per-detector
  // status so a stopped/erroring/truncated detector is visible instead
  // of silently showing an empty or shrunken queue.
  async getCleanupDetectorHealth(): Promise<CleanupDetectorHealthResponse> {
    return request<CleanupDetectorHealthResponse>(`/api/cleanup/detector-health`);
  },

  async getDataNeededQueue(
    params: { limit?: number; offset?: number } = {},
  ): Promise<DataNeededQueue> {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return request<DataNeededQueue>(
      // Zone 2 of /data-needed. Moved off /api/data-accuracy/my-queue
      // (2026-07-31): the worklist outlives the pre-cutover sprint board.
      `/api/data-needed/queue${q ? `?${q}` : ""}`,
    );
  },
  // The two numbers behind the sidebar's Data Needed badge: the caller's
  // open judgment calls + the cleanup items in their lane (disjoint sets).
  // `total` is added on the SERVER. Render it; never re-add the halves
  // in the browser — two browser-side adders reading separate fetches is
  // exactly how the badge (746) and the page header (983) ended up
  // disagreeing on one screen (2026-09-15).
  async getDataNeededCounts(
    scope: { mine?: boolean; company?: string | null; routedTo?: string | null } = {},
  ): Promise<{ judgment_open: number; my_lane_open: number; total: number }> {
    const qs = new URLSearchParams();
    if (scope.mine) qs.set("mine", "true");
    if (scope.company) qs.set("company", scope.company);
    if (scope.routedTo) qs.set("routed_to", scope.routedTo);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ judgment_open: number; my_lane_open: number; total: number }>(
      `/api/data-needed/counts${suffix}`,
    );
  },
  // B1 (2026-07-23) — the canonical open-backlog count the nav badge
  // polls. {open, mine}, scoped to the viewer.
  async getCleanupOpenCount(): Promise<{ open: number; mine: number }> {
    return request<{ open: number; mine: number }>("/api/cleanup/open-count");
  },
  // Donna's resolver (2026-07-23) — live context for an unmatched
  // import row: who came in, which family their email resolves to, and
  // every candidate household with distinguishing info.
  async getQuarantineContext(issueId: string): Promise<QuarantineResolveContext> {
    return request<QuarantineResolveContext>(
      `/api/cleanup/issues/${encodeURIComponent(issueId)}/quarantine-context`,
    );
  },
  // B6 (2026-07-23) — in-record start-date prompt write path (replaced
  // the retired sr-missing-effective-date decide-rail branch). Pass
  // {effective_date} to set, or {unknown:true} for the durable
  // "not recoverable — stop asking" escape (0203 marker).
  async setServiceRecordEffectiveDate(
    serviceRecordId: string,
    input: { effective_date: string } | { unknown: true },
  ): Promise<{ id: string; effective_date: string | null; unknown: boolean }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/effective-date`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  // S3 — my Category-6 judgment items. limit=0 → counts only (the
  // sidebar badge's cheap call).
  async getMyJudgmentItems(
    limit?: number,
    // Admin "Everyone" view (system_admin / compliance; gated server-side).
    scope?: { everyone?: boolean; company?: string | null; routedTo?: string | null; shape?: AnswerShape | null },
  ): Promise<MyJudgmentList> {
    const qs = new URLSearchParams();
    if (limit !== undefined) qs.set("limit", String(limit));
    if (scope?.shape) qs.set("shape", scope.shape);
    if (scope?.everyone) {
      qs.set("scope", "everyone");
      if (scope.company) qs.set("company", scope.company);
      if (scope.routedTo) qs.set("routed_to", scope.routedTo);
    }
    const q = qs.toString();
    return request<MyJudgmentList>(`/api/data-needed/judgment${q ? `?${q}` : ""}`);
  },
  // S3 — the team judgment-completion scoreboard (visible to all
  // employees per the transparency doctrine).
  async getJudgmentTeam(): Promise<JudgmentTeamResponse> {
    return request<JudgmentTeamResponse>("/api/data-needed/team");
  },
  // S3 — the Category-6 fast-entry writes (judgment inline editors).
  async setFitAssessment(
    householdId: string,
    companySlug: string,
    fit: "ideal_fit" | "typical" | "poor_fit" | "unassessed" | "advisor_departed",
    reason?: string,
  ): Promise<{
    household_company_relationship_id: string;
    fit_assessment: string;
    previous_assessment: string;
    changed: boolean;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/companies/${encodeURIComponent(companySlug)}/fit`,
      {
        method: "PATCH",
        body: JSON.stringify(reason ? { fit_assessment: fit, reason } : { fit_assessment: fit }),
      },
    );
  },
  // Stage of Wealth — Control / Margin / Impact, one per FAMILY
  // (2026-09-18). Any employee may record it: a household carries no
  // company, so there is no scope to check, and the protection is the
  // history every one of these writes leaves behind.
  //
  // `stage: null` clears it back to unanswered, which is what makes the
  // strip's undo real after a first set.
  async setWealthStage(
    householdId: string,
    stage: "control" | "margin" | "impact" | null,
    reason?: string,
  ): Promise<{
    previous_stage: "control" | "margin" | "impact" | null;
    stage: "control" | "margin" | "impact" | null;
    direction: "set" | "advance" | "regress" | null;
    changed: boolean;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/wealth-stage`, {
      method: "PATCH",
      body: JSON.stringify(reason ? { wealth_stage: stage, reason } : { wealth_stage: stage }),
    });
  },
  /** Record the honest "we can't say yet" (the strip's escape hatch). */
  async markWealthStageUnknown(householdId: string): Promise<{ changed: boolean }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/wealth-stage/mark-unknown`, {
      method: "POST",
    });
  },
  /** Undo that — the family becomes genuinely unanswered again. */
  async clearWealthStageUnknown(householdId: string): Promise<{ changed: boolean }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/wealth-stage/clear-unknown`,
      { method: "POST" },
    );
  },
  // Admin: bulk "advisor departed" fit cleanup (system_admin only).
  async previewFitDeparted(args: {
    company_slug?: string | null;
    target: "departed_advisor" | "no_advisor" | "both" | "inherited_book";
  }): Promise<{
    matched: number;
    sample: { household_id: string; household_name: string | null; company_slug: string }[];
  }> {
    return request(`/api/admin/fit-cleanup/preview`, {
      method: "POST",
      body: JSON.stringify(args),
    });
  },
  async applyFitDeparted(args: {
    company_slug?: string | null;
    target: "departed_advisor" | "no_advisor" | "both" | "inherited_book";
    limit?: number;
  }): Promise<{ applied: number; remaining: number }> {
    return request(`/api/admin/fit-cleanup/apply`, {
      method: "POST",
      body: JSON.stringify(args),
    });
  },
  // WMM bulk "don't know this family" — inherited-book backlog clear
  // (sibling of fit-cleanup; system_admin only).
  async previewWmmCleanup(): Promise<{
    matched: number;
    sample: { household_id: string; household_name: string | null }[];
  }> {
    return request(`/api/admin/wmm-cleanup/preview`, { method: "POST" });
  },
  async applyWmmCleanup(): Promise<{ applied: number; remaining: number }> {
    return request(`/api/admin/wmm-cleanup/apply`, { method: "POST" });
  },
  async patchOpportunityNumbers(
    id: string,
    patch: Partial<MyJudgmentItemNumbers>,
  ): Promise<MyJudgmentItemNumbers & { id: string; judgment_complete: boolean }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/numbers`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  /** Data Needed "Planning case revenue": the case's first-year revenue (dollars). */
  async patchPlanningCaseRevenue(id: string, estimated_revenue_year_1: number): Promise<{ estimated_revenue_year_1: number | null; revenue_none: boolean }> {
    return request(`/api/service-records/${encodeURIComponent(id)}/case-revenue`, {
      method: "PATCH",
      body: JSON.stringify({ estimated_revenue_year_1 }),
    });
  },

  /** "Stewardship earns nothing on this case" (or take it back with undo=true). */
  async markPlanningCaseRevenueNone(id: string, undo = false): Promise<{ estimated_revenue_year_1: number | null; revenue_none: boolean }> {
    return request(`/api/service-records/${encodeURIComponent(id)}/case-revenue/none`, { method: undo ? "DELETE" : "POST" });
  },

  /** Who may advise a Planning case (current advisors only). */
  async listPlanningCaseAdvisorCandidates(): Promise<{ users: { id: string; name: string | null }[] }> {
    return request(`/api/planning/case-advisor-candidates`);
  },

  /** Keep or change the advisor on ONE Planning case. */
  async setPlanningCaseAdvisor(id: string, advisor_user_id: string): Promise<{ undo_version_id: string; prior_advisor_user_id: string | null; advisor_user_id: string }> {
    return request(`/api/service-records/${encodeURIComponent(id)}/case-advisor`, {
      method: "POST",
      body: JSON.stringify({ advisor_user_id }),
    });
  },

  async undoPlanningCaseAdvisor(id: string, undo_version_id: string): Promise<{ advisor_user_id: string | null }> {
    return request(`/api/service-records/${encodeURIComponent(id)}/case-advisor/undo`, {
      method: "POST",
      body: JSON.stringify({ undo_version_id }),
    });
  },

  async patchTransactionNumbers(
    id: string,
    patch: Partial<MyJudgmentItemNumbers> & {
      /** Realty only (Grant 2026-07-29): BrokerMint's three commission
       *  metrics, written through to the linked property record. */
      realty_commission?: {
        office_gross?: number | null;
        agent_net?: number | null;
        office_net?: number | null;
      };
    },
  ): Promise<MyJudgmentItemNumbers & { id: string; judgment_complete: boolean }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/numbers`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  // "Not known / N/A" terminal answer for a deal-numbers item (migration
  // 0195) — clears the item for the whole team, unlike the per-user "Not
  // sure" skip. Reversible via the DELETE variant.
  async markOpportunityNumbersUnknown(
    id: string,
  ): Promise<{ id: string; numbers_unknown_reason: string | null }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/numbers/mark-unknown`, {
      method: "POST",
    });
  },
  async markTransactionNumbersUnknown(
    id: string,
  ): Promise<{ id: string; numbers_unknown_reason: string | null }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/numbers/mark-unknown`, {
      method: "POST",
    });
  },
  async clearOpportunityNumbersUnknown(
    id: string,
  ): Promise<{ id: string; numbers_unknown_reason: string | null }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/numbers/mark-unknown`, {
      method: "DELETE",
    });
  },
  async clearTransactionNumbersUnknown(
    id: string,
  ): Promise<{ id: string; numbers_unknown_reason: string | null }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/numbers/mark-unknown`, {
      method: "DELETE",
    });
  },
  // ─── Board Setup: the task lists and date rules behind the workspace ─
  // One board at a time (company + workflow + product). Both saves send
  // the WHOLE list — the server adds, updates and retires by comparing it
  // with what is stored, and reports back how many of each.
  async getBoardSetup(
    company: string,
    workflow: "transaction" | "opportunity",
    type: string,
  ): Promise<BoardSetup> {
    return request(
      `/api/board-setup/${encodeURIComponent(company)}/${encodeURIComponent(workflow)}/${encodeURIComponent(type)}`,
    );
  },
  async saveBoardSetupTasks(
    company: string,
    workflow: "transaction" | "opportunity",
    type: string,
    tasks: BoardSetupTaskDraft[],
  ): Promise<{ added: number; updated: number; removed: number }> {
    return request(
      `/api/board-setup/${encodeURIComponent(company)}/${encodeURIComponent(workflow)}/${encodeURIComponent(type)}/tasks`,
      { method: "PUT", body: JSON.stringify({ tasks }) },
    );
  },
  async saveBoardSetupDates(
    company: string,
    workflow: "transaction" | "opportunity",
    type: string,
    dates: BoardSetupDateDraft[],
  ): Promise<{ added: number; updated: number; removed: number }> {
    return request(
      `/api/board-setup/${encodeURIComponent(company)}/${encodeURIComponent(workflow)}/${encodeURIComponent(type)}/dates`,
      { method: "PUT", body: JSON.stringify({ dates }) },
    );
  },

  // ─── The Processing workspace: stage tasks + key dates ──────────────
  // One assembled view — the resolved deadlines, the timeline, the stage
  // ladder, the next action. Every mutation below returns the WHOLE
  // workspace back, because one change can move several computed dates and
  // the due dates that chase them; re-rendering from the server's answer
  // is how the page never shows a stale one.
  async getProcessingWorkspace(transactionId: string): Promise<{ workspace: ProcessingWorkspace }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/workspace`);
  },
  async addProcessingTask(
    transactionId: string,
    body: {
      stage_slug: string;
      title: string;
      owner_kind?: StageTaskOwnerKind;
      due_date?: string | null;
      note?: string | null;
    },
  ): Promise<{ workspace: ProcessingWorkspace | null }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/tasks`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // `taskRef` is a materialized task's id, or — for a task nobody has
  // touched yet — its template task_key. The server materializes on the
  // first edit; the page does not have to know which it is holding.
  async updateProcessingTask(
    transactionId: string,
    taskRef: string,
    body: {
      title?: string;
      done?: boolean;
      due_date?: string | null;
      note?: string | null;
      owner_kind?: StageTaskOwnerKind;
      stage_slug?: string;
    },
  ): Promise<{ workspace: ProcessingWorkspace | null }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/tasks/${encodeURIComponent(taskRef)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  },
  async deleteProcessingTask(
    transactionId: string,
    taskRef: string,
  ): Promise<{ workspace: ProcessingWorkspace | null }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/tasks/${encodeURIComponent(taskRef)}`,
      { method: "DELETE" },
    );
  },
  // Only a date the company set up as one a person sets accepts a value;
  // a computed date answers 409 naming the rule that owns it.
  /**
   * Set a user-owned key date, clear it, or mark it as not applying to
   * this record. Passing `notApplicableReason` (even null) marks it N/A;
   * omitting the argument entirely sets or clears the date as before.
   */
  async setProcessingKeyDate(
    transactionId: string,
    dateKey: string,
    value: string | null,
    notApplicableReason?: string | null,
  ): Promise<{ workspace: ProcessingWorkspace | null }> {
    const markingNotApplicable = notApplicableReason !== undefined;
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/key-dates/${encodeURIComponent(dateKey)}`,
      {
        method: "PUT",
        body: JSON.stringify(
          markingNotApplicable
            ? { not_applicable: true, not_applicable_reason: notApplicableReason }
            : { value },
        ),
      },
    );
  },
  /**
   * Put a removed deadline back on this record (2026-09-22). Works on a
   * computed date too — the server stores the flag off, which is also how
   * it knows a person overruled an automatic removal. `value` restores a
   * user-set date that the removal cleared (the undo path); null for a
   * computed one.
   */
  async restoreProcessingKeyDate(
    transactionId: string,
    dateKey: string,
    value: string | null,
  ): Promise<{ workspace: ProcessingWorkspace | null }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/key-dates/${encodeURIComponent(dateKey)}`,
      {
        method: "PUT",
        body: JSON.stringify({ not_applicable: false, value }),
      },
    );
  },

  // ─── Realty transaction ↔ property-record linking (Nate 2026-07-06) ──
  // Picker candidates: unlinked realty property records (any household),
  // filtered by `q` against address + household name.
  async listLinkableServiceRecords(
    transactionId: string,
    q: string,
  ): Promise<{ candidates: LinkableServiceRecord[] }> {
    const query = q ? `?q=${encodeURIComponent(q)}` : "";
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/linkable-service-records${query}`,
    );
  },
  // Addresses the OS already knows for this file — the offers written on
  // it and the homes logged for the household (Nate, 2026-09-03). These
  // prefill the create form; nothing here links or creates anything on
  // its own.
  async listPropertySuggestions(transactionId: string): Promise<PropertySuggestionsResponse> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/property-suggestions`);
  },
  // Link a transaction to an existing property record OR create one inline
  // (buy/sell side + address). Reversible via unlinkTransactionServiceRecord.
  async linkTransactionServiceRecord(
    transactionId: string,
    body:
      | { service_record_id: string }
      | {
          create: {
            side: "listing" | "home_purchase";
            property_address_line_1: string;
            property_address_line_2?: string | null;
            property_city: string;
            property_state: string;
            property_zip: string;
          };
        },
  ): Promise<LinkServiceRecordResult> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/link-service-record`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // Detach a link (undo target for the link toast + a first-class action).
  async unlinkTransactionServiceRecord(
    transactionId: string,
    body?: {
      restore_service_record_id?: string | null;
      delete_service_record_id?: string | null;
      restore_numbers?: {
        product_metric?: number | null;
        estimated_revenue_year_1?: number | null;
      } | null;
    },
  ): Promise<{ ok: boolean; linked_service_record_id: string | null }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/unlink-service-record`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  // Edit the linked property address (upserts the subtype row).
  async patchTransactionPropertyAddress(
    transactionId: string,
    address: {
      property_address_line_1: string;
      property_address_line_2?: string | null;
      property_city: string;
      property_state: string;
      property_zip: string;
    },
  ): Promise<{
    ok: boolean;
    service_record_id: string;
    property_address: string | null;
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/property-address`, {
      method: "PATCH",
      body: JSON.stringify(address),
    });
  },
  // Correct the actual close date.
  async patchTransactionActualCloseDate(
    transactionId: string,
    actualCloseDate: string | null,
  ): Promise<{
    ok: boolean;
    actual_close_date: string | null;
    previous: string | null;
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/actual-close-date`, {
      method: "PATCH",
      body: JSON.stringify({ actual_close_date: actualCloseDate }),
    });
  },
  // Correct a mis-filed listing/purchase (Nate 2026-08-04). Moves the
  // linked property record to the other side's table; its own inverse —
  // calling it again with the other side switches back.
  async switchTransactionRealtySide(
    transactionId: string,
    side: "listing" | "home_purchase",
  ): Promise<{
    ok: boolean;
    realty_record_id: string;
    previous_side: "listing" | "purchase";
    next_side: "listing" | "purchase";
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/switch-side`, {
      method: "POST",
      body: JSON.stringify({ side }),
    });
  },
  // Set/clear a mortgage opportunity's prequal letter expiration date.
  // Cross-company writable (mortgage + realty scope) — see
  // workers/api/src/routes/prequal.ts.
  async patchOpportunityPrequalExpiration(
    id: string,
    prequalExpiresAt: string | null,
  ): Promise<{ id: string; prequal_expires_at: string | null }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/prequal-expiration`, {
      method: "PATCH",
      body: JSON.stringify({ prequal_expires_at: prequalExpiresAt }),
    });
  },
  // Per-record Estimated Revenue edit — broad-access (any employee, no
  // company-scope gate). The single editable revenue source of truth;
  // aggregates stay derived. Reversibility-first: server records a version
  // snapshot + audit row. 409 when the record is locked (deal finalized /
  // opportunity closed).
  async patchTransactionEstimatedRevenue(
    id: string,
    estimatedRevenueYear1: number | null,
  ): Promise<{
    transaction_id: string;
    estimated_revenue_year_1: number | null;
    previous_estimated_revenue_year_1: number | null;
    changed: boolean;
  }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/estimated-revenue`, {
      method: "PATCH",
      body: JSON.stringify({
        estimated_revenue_year_1: estimatedRevenueYear1,
      }),
    });
  },
  async patchOpportunityEstimatedRevenue(
    id: string,
    estimatedRevenueYear1: number | null,
  ): Promise<{
    opportunity_id: string;
    estimated_revenue_year_1: number | null;
    previous_estimated_revenue_year_1: number | null;
    changed: boolean;
  }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/estimated-revenue`, {
      method: "PATCH",
      body: JSON.stringify({
        estimated_revenue_year_1: estimatedRevenueYear1,
      }),
    });
  },
  // Financial Advisor Workspace v2, O1 — "The opportunity" card's
  // plain-words advisor summary. Empty string clears it.
  async patchOpportunitySummary(
    id: string,
    summary: string | null,
  ): Promise<{ opportunity_id: string; advisor_summary: string | null; changed: boolean }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/summary`, {
      method: "PATCH",
      body: JSON.stringify({ summary }),
    });
  },
  async patchBorrowerTier(
    serviceRecordId: string,
    tier: string | null,
    notes?: string,
  ): Promise<{
    service_record_id: string;
    borrower_tier: string | null;
    previous_borrower_tier: string | null;
  }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/borrower-tier`, {
      method: "PATCH",
      body: JSON.stringify(notes ? { borrower_tier: tier, notes } : { borrower_tier: tier }),
    });
  },
  // S3 PR 9 — AI relationship summaries. GET serves cached + rewrites
  // server-side when the data changed; POST refresh forces a rewrite.
  // ─── Services Check (Jake Norton, 2026-09-12) ──────────────────────
  async getHouseholdServiceCheck(householdId: string): Promise<HouseholdServiceCheckResponse> {
    return request<HouseholdServiceCheckResponse>(
      `/api/households/${encodeURIComponent(householdId)}/services-check`,
    );
  },
  async saveServiceCheckAnswer(
    householdId: string,
    companySlug: string,
    serviceKey: string,
    body: {
      state: ServiceCheckStoredState;
      provider_name?: string | null;
      set_up_year?: number | null;
      revisit_month?: string | null;
      note?: string | null;
      /** The day the family said it (YYYY-MM-DD), when it was not today. */
      told_us_on?: string | null;
      /** Where it came from: advisor, client, check_in, review, declaration_page. */
      source?: string;
    },
  ): Promise<{
    id: string;
    /** The history row this save wrote; the 60-second undo names it. */
    history_id: string;
    state: string;
    previous_state: string | null;
    changed: boolean;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/services-check/${encodeURIComponent(companySlug)}/${encodeURIComponent(serviceKey)}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  },
  /** Take back a save made in the last minute (Marketing Hub modal 26). */
  /** Row 10.4: Stew reads a family's declaration page (a household
   *  document already uploaded) and proposes Services Check lines. */
  async readDeclarationPage(householdId: string, documentId: string): Promise<{ reading: FamilyDecPageReading; document: { id: string; filename: string } | null }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/services-check/declaration-page/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ document_id: documentId }),
    });
  },
  /**
   * Row 10.4 (#3703 F1): the page itself goes to Stew. It is read in memory
   * and filed in the family's documents only when it is not a health page;
   * `document` is null on a refusal, because nothing was kept.
   */
  async readDeclarationPageFile(householdId: string, file: File): Promise<{ reading: FamilyDecPageReading; document: { id: string; filename: string } | null }> {
    const fd = new FormData();
    fd.append("file", file);
    // Not request(): it sets Content-Type to JSON, and a FormData body needs
    // the browser's own multipart boundary (same as uploadDocumentSharePoint).
    const res = await fetch(apiUrl(`/api/households/${encodeURIComponent(householdId)}/services-check/declaration-page/read`), { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) {
      let payload: { error?: string; message?: string } = {};
      try {
        payload = (await res.json()) as { error?: string; message?: string };
      } catch {
        // ignore parse errors
      }
      throw new ApiError(res.status, payload.message ?? payload.error ?? `read_failed_${res.status}`, payload.error ?? null);
    }
    return (await res.json()) as { reading: FamilyDecPageReading; document: { id: string; filename: string } | null };
  },
  /** Row 10.4: the advisor accepts one proposed line (edited or not). */
  async acceptDeclarationLine(
    householdId: string,
    body: {
      document_id: string;
      service_key: string;
      carrier: string | null;
      policy_label: string | null;
      effective_on: string | null;
      renews_on: string | null;
      premium_cents: number | null;
      premium_frequency: string | null;
    },
  ): Promise<{ id: string; history_id: string; revisit_month: string | null }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/services-check/declaration-page/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async undoServiceCheckSave(householdId: string, historyId: string): Promise<{ ok: true; restored_state: string | null }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/services-check/undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ history_id: historyId }),
    });
  },
  /** Modal 25: the question for one service on one household, filled in. */
  async getAskOne(householdId: string, companySlug: string, serviceKey: string): Promise<AskOneComposer> {
    return request<AskOneComposer>(
      `/api/marketing/households/${encodeURIComponent(householdId)}/ask-one?company=${encodeURIComponent(companySlug)}&service=${encodeURIComponent(serviceKey)}`,
    );
  },
  async sendAskOne(
    householdId: string,
    body: { company_slug: string; service_key: string; mode: TodayEmailMode; contact_ids: string[]; subject: string; body: string },
  ): Promise<{ ok: true; mode: TodayEmailMode; web_link: string | null; touch_written: boolean }> {
    return request(`/api/marketing/households/${encodeURIComponent(householdId)}/ask-one`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async holdAskOne(
    householdId: string,
    body: { company_slug: string; service_key: string; way: "digest" | "note"; contact_ids?: string[]; body: string },
  ): Promise<{ ok: true; way: "digest" | "note"; ids?: string[] }> {
    return request(`/api/marketing/households/${encodeURIComponent(householdId)}/ask-one/hold`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async undoAskOneHold(householdId: string, ids: string[]): Promise<{ ok: true; withdrawn: number }> {
    return request(`/api/marketing/households/${encodeURIComponent(householdId)}/ask-one/hold/undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  },
  async suggestServiceCheckProviders(
    serviceKey: string,
  ): Promise<{ suggestions: Array<{ provider_name: string; households: number }> }> {
    return request(`/api/services-check/providers?service_key=${encodeURIComponent(serviceKey)}`);
  },
  // The book-wide list: docs/archive/mockups/services-check-book-list-v1.html,
  // approved by Jake 2026-09-12.
  async getServiceCheckList(params: ServiceCheckListParams): Promise<ServiceCheckListResponse> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    if (params.owner) qs.set("owner", params.owner);
    if (params.state) qs.set("state", params.state);
    if (params.service_key) qs.set("service_key", params.service_key);
    if (params.due_only) qs.set("due_only", "1");
    if (params.due_within_months != null) {
      qs.set("due_within_months", String(params.due_within_months));
    }
    if (params.search) qs.set("search", params.search);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return request<ServiceCheckListResponse>(`/api/services-check/list${query ? `?${query}` : ""}`);
  },
  // Marketing Hub, Today (row 2.3a). The server reads the viewer's scope
  // from the session and refuses a company outside it (403 with
  // allowed_companies); the page falls back and prints the scope the server
  // says it applied.
  async getMarketingToday(params: MarketingTodayParams): Promise<TodayResponse> {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") qs.set(key, String(value));
    }
    const query = qs.toString();
    return request<TodayResponse>(`/api/marketing/today${query ? `?${query}` : ""}`);
  },
  /** Row 2.7a: the financial brief for acting on a Today row ("Is this
   *  worth it?"). Opening it stores the brief, or returns the stored one. */
  async getMarketingDecisionBrief(decisionId: string): Promise<{ brief: StoredBrief; versions: number; desk: { slots: number | null; used: number } | null }> {
    return request(`/api/marketing/decisions/${encodeURIComponent(decisionId)}/brief`);
  },
  /** Program audit M7: the brief a goal stored when it was set, opened from
   *  Results. Same shape as a Today row's brief; there is no desk line. */
  async getMarketingGoalBrief(goalId: string): Promise<{ brief: StoredBrief; versions: number; desk: null }> {
    return request(`/api/marketing/goals/${encodeURIComponent(goalId)}/brief`);
  },
  /** Row 2.7b: the brief's settings. Grant saves; everyone else reads. */
  async getMarketingEconomics(): Promise<EconomicsSettingsResponse> {
    return request("/api/marketing/settings/economics");
  },
  async saveMarketingIncomeOverride(key: string, body: { income_y1: number; lifetime: number | null; effective_from: string; reason: string }): Promise<{ ok: true; id: string }> {
    return request(`/api/marketing/settings/economics/income/${encodeURIComponent(key)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  /** Take an income override back to the comp rule (nothing is deleted; the history shows both). */
  async removeMarketingIncomeOverride(key: string, reason: string | null): Promise<{ ok: true; removed_id: string; restored_id: string | null }> {
    return request(`/api/marketing/settings/economics/income/${encodeURIComponent(key)}/remove`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });
  },
  async getMarketingBenchmarks(): Promise<BenchmarkSettingsResponse> {
    return request("/api/marketing/settings/benchmarks");
  },
  async saveMarketingBenchmark(id: string, body: { low: number; mid: number | null; high: number | null; source: string | null; checked_at: string; reason?: string | null }): Promise<{ ok: true; assumed: boolean }> {
    return request(`/api/marketing/settings/benchmarks/${encodeURIComponent(id)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  /** Row 6.4: the seasonal calendar (plan 5.4). Everyone reads; Grant edits. */
  async getSeasonalCalendar(): Promise<SeasonalCalendarResponse> {
    return request("/api/marketing/settings/seasonal-calendar");
  },
  async saveSeason(id: string | null, body: SeasonalEditBody): Promise<{ ok: true; id: string; changed: string[] }> {
    return request(`/api/marketing/settings/seasonal-calendar${id ? `/${encodeURIComponent(id)}` : ""}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  /** Row 6.5: the month's digest verdicts as counts (Grant only). */
  async getDigestPreview(month?: string): Promise<DigestPreviewResponse> {
    return request(`/api/marketing/settings/digest${month ? `?month=${encodeURIComponent(month)}` : ""}`);
  },
  async recomputeDigests(): Promise<{ ok: true; month: string; households: number; earned: number; silent: number; failed_readers: string[] }> {
    return request("/api/marketing/settings/digest/recompute", { method: "POST" });
  },
  async listMarketingCampaigns(): Promise<{ campaigns: MarketingCampaign[]; save_companies: readonly string[] | "all" }> {
    return request("/api/marketing/campaigns");
  },
  async createMarketingCampaign(body: { company: string; name: string; mode: "sprint" | "ongoing"; starts_on: string; ends_on: string | null; audience_id: string | null }): Promise<{ campaign: MarketingCampaign }> {
    return request("/api/marketing/campaigns", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getMarketingCampaign(id: string, householdId?: string | null): Promise<CampaignScreen> {
    return request(`/api/marketing/campaigns/${encodeURIComponent(id)}${householdId ? `?household=${encodeURIComponent(householdId)}` : ""}`);
  },
  async setCampaignHoldout(id: string, on: boolean, expectedVersion: number): Promise<{ ok: true; campaign: MarketingCampaign }> {
    return request(`/api/marketing/campaigns/${encodeURIComponent(id)}/holdout`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ on, expected_version: expectedVersion }) });
  },
  async editMarketingCampaign(id: string, body: { name?: string; starts_on?: string; ends_on?: string | null; audience_id?: string | null; expected_version: number }): Promise<{ ok: true; campaign: MarketingCampaign }> {
    return request(`/api/marketing/campaigns/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async pauseMarketingCampaign(id: string, action: "pause" | "resume", expectedVersion: number): Promise<{ ok: true; held?: number; released?: number }> {
    return request(`/api/marketing/campaigns/${encodeURIComponent(id)}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version: expectedVersion }) });
  },
  async createCampaignSeries(campaignId: string, body: SeriesForm): Promise<{ series: CampaignSeries }> {
    return request(`/api/marketing/campaigns/${encodeURIComponent(campaignId)}/series`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getCampaignSeries(id: string): Promise<SeriesScreen> {
    return request(`/api/marketing/series/${encodeURIComponent(id)}`);
  },
  async editCampaignSeries(id: string, body: Omit<SeriesForm, "kind"> & { expected_version: number }): Promise<{ series: CampaignSeries }> {
    return request(`/api/marketing/series/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async seriesAction(id: string, action: "submit" | "approve" | "pause" | "resume", expectedVersion: number): Promise<{ series?: CampaignSeries }> {
    return request(`/api/marketing/series/${encodeURIComponent(id)}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version: expectedVersion }) });
  },
  async saveSeriesPiece(carrier: "email" | "advisor", id: string, body: { subject: string; body: string; expected_version: number }): Promise<{ piece: SeriesPiece }> {
    return request(`/api/marketing/pieces/${carrier}/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getEmailPiece(id: string): Promise<EmailPieceScreen> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}`);
  },
  async saveEmailPiece(id: string, body: { subject: string; preview: string; blocks: EmailBlockT[]; from?: "company" | "own"; expected_version: number }): Promise<{ piece: EmailPieceRow }> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async readEmailPiece(id: string): Promise<{ read: EmailRead }> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/read`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async applyEmailRead(id: string, readId: string, body: { ids: string[]; rescore: boolean }): Promise<{ piece: EmailPieceRow; read: EmailRead; applied: string[]; could_not: { id: string; reason: string }[] }> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/read/${encodeURIComponent(readId)}/apply`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async keepEmailAsWritten(id: string, readId: string): Promise<{ read: EmailRead }> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/read/${encodeURIComponent(readId)}/keep`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async getWhatWorks(): Promise<WhatWorksResponse> {
    return request(`/api/marketing/settings/what-works`);
  },
  async pinWhatWorks(id: string, body: { kind: "keep" | "drop"; note: string }): Promise<{ row: WhatWorksView }> {
    return request(`/api/marketing/settings/what-works/${encodeURIComponent(id)}/pin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async unpinWhatWorks(id: string): Promise<{ row: WhatWorksView | null }> {
    return request(`/api/marketing/settings/what-works/${encodeURIComponent(id)}/unpin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Clear Ask Stew's hand-off once its sentence is placed; fire-and-forget. */
  async clearGoalHandoff(id: string): Promise<{ ok: boolean }> {
    return request(`/api/marketing/goals/handoff/clear`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) });
  },
  async getGoalAsk(gap: string | null, audience?: string | null, mode?: string | null, from?: string | null): Promise<GoalAsk> {
    const q = new URLSearchParams();
    if (gap) q.set("gap", gap);
    if (from) q.set("from", from);
    if (audience) q.set("audience", audience);
    if (mode) q.set("mode", mode);
    const qs = q.toString();
    return request(`/api/marketing/goals/new${qs ? `?${qs}` : ""}`);
  },
  async readGoalSentence(sentence: string, answers?: Partial<GoalAnswerBody>): Promise<GoalReadResult> {
    return request(`/api/marketing/goals/read`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sentence, ...(answers ?? {}) }) });
  },
  async proposeGoal(body: { company: string; service_key: string; audience: string; mode: string; weeks: number; reason: string; target: number | null; lighter: boolean; conditions?: FilterCondition[] } & Partial<GoalAnswerBody>): Promise<{ reading: GoalReading; plan: GoalPlanView }> {
    return request(`/api/marketing/goals/propose`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async goalAudience(body: { company: string; service_key: string; audience: string; mode: string; weeks: number; reason: string; target: number | null; audience_words: string; conditions?: FilterCondition[] }): Promise<GoalAudienceView> {
    return request(`/api/marketing/goals/audience`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async saveGoalPreset(id: string, body: { service_key: string; sentence: string; audience_words: string; audience_mode: string; outcome: OutcomeMeasure; pace: GoalPace; weeks: number; expected_version_number: number }): Promise<{ preset: GoalPreset }> {
    return request(`/api/marketing/goals/presets/${encodeURIComponent(id)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async requestGoalSetup(body: { what: string; company: string | null; sentence: string }): Promise<{ task_id: string; owner_user_id: string; already_asked: boolean }> {
    return request(`/api/marketing/goals/setup-request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async prepareGoal(body: { sentence: string; company: string; service_key: string; audience: string; mode: string; weeks: number; reason: string; target: number | null; lighter: boolean; gap_key: string | null; preset_id?: string | null; conditions?: FilterCondition[]; variation?: Variation | null; confirm_whole_audience?: boolean } & Partial<GoalAnswerBody>): Promise<GoalPrepared> {
    return request(`/api/marketing/goals`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getGoal(id: string): Promise<GoalScreen> {
    return request(`/api/marketing/goals/${encodeURIComponent(id)}`);
  },
  async getPlaybooks(): Promise<{ playbooks: PlaybookSummary[]; research: { cap: number; used_today: number } }> {
    return request(`/api/marketing/playbooks`);
  },
  async getPlaybook(company: string, family: string): Promise<PlaybookScreen> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(company)}/${encodeURIComponent(family)}`);
  },
  async savePlaybook(id: string, body: PlaybookSaveBody): Promise<{ playbook: PlaybookDetail; approval_reset: boolean }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async approvePlaybook(id: string, expectedVersion: number): Promise<{ playbook: PlaybookDetail }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version_number: expectedVersion }) });
  },
  async withdrawPlaybookApproval(id: string, expectedVersion: number): Promise<{ playbook: PlaybookDetail }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}/withdraw`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version_number: expectedVersion }) });
  },
  async draftPlaybook(id: string): Promise<{ fields: PlaybookFields; saved: false }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}/draft`, { method: "POST" });
  },
  async researchPlaybook(id: string, body: { topic: ResearchTopic; note: string }): Promise<{ run: ResearchRun; kept: number }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}/research`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getPlays(): Promise<{ plays: PlaySummary[]; financial_switch: boolean; save_companies: readonly string[] | "all"; is_founder: boolean }> {
    return request(`/api/marketing/plays`);
  },
  async getPlay(id: string): Promise<PlayScreen> {
    return request(`/api/marketing/plays/${encodeURIComponent(id)}`);
  },
  async savePlay(id: string, body: PlaySaveBody): Promise<{ play: PlayDetail; approval_reset: boolean; note: string | null }> {
    return request(`/api/marketing/plays/${encodeURIComponent(id)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async draftPlay(id: string, body: { channels: PlayChannel[]; email_count: number; name: string }): Promise<{ words: PlayWords; saved: false }> {
    return request(`/api/marketing/plays/${encodeURIComponent(id)}/draft`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async playAction(id: string, action: "approve" | "withdraw" | "retire" | "sign", expectedVersion: number): Promise<{ play: PlayDetail }> {
    return request(`/api/marketing/plays/${encodeURIComponent(id)}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version_number: expectedVersion }) });
  },
  async launchPlay(id: string, expectedVersion: number): Promise<PlayLaunchResult> {
    return request(`/api/marketing/plays/${encodeURIComponent(id)}/launch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version_number: expectedVersion }) });
  },
  async undoPlayLaunch(goalId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/goals/${encodeURIComponent(goalId)}/undo-launch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async releasePlayLaunch(goalId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/goals/${encodeURIComponent(goalId)}/release-launch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async researchAdLibraries(id: string, note: string): Promise<{ run: ResearchRun; kept: number; refreshed?: number }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}/ad-libraries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note }) });
  },
  async addSeenFinding(id: string, body: SeenFindingBody): Promise<{ id: string; already: boolean }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}/findings`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async suggestFinding(id: string, words: string): Promise<{ read: { advertiser: string; hook: string; offer: string; format: MarketAdFormat } | null; saved: false }> {
    return request(`/api/marketing/playbooks/${encodeURIComponent(id)}/findings/suggest`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ words }) });
  },
  async setMarketFindingAside(id: string, aside: boolean, reason: string | null): Promise<{ ok: true }> {
    return request(`/api/marketing/market-findings/${encodeURIComponent(id)}/${aside ? "discard" : "restore"}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) });
  },
  async setOutsideClaimAside(id: string, aside: boolean, reason: string | null): Promise<{ ok: true }> {
    return request(`/api/marketing/outside-claims/${encodeURIComponent(id)}/${aside ? "discard" : "restore"}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) });
  },
  async getLevels(): Promise<LevelsResponse> {
    return request(`/api/marketing/levels`);
  },
  async getLevel(company: string, family: string): Promise<LevelDetail> {
    return request(`/api/marketing/levels/${encodeURIComponent(company)}/${encodeURIComponent(family)}`);
  },
  async previewLevel(company: string, family: string, body: { to: GrowthLevel; advisor_user_id: string | null; safety: string | null }): Promise<LevelPreviewResult> {
    return request(`/api/marketing/levels/${encodeURIComponent(company)}/${encodeURIComponent(family)}/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async setLevel(company: string, family: string, body: { to: GrowthLevel; advisor_user_id: string | null; safety: string | null; reason: string; expected_live_id: string | null }): Promise<{ row: { id: string; level: GrowthLevel }; paused: number; held: number }> {
    return request(`/api/marketing/levels/${encodeURIComponent(company)}/${encodeURIComponent(family)}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async undoLevel(company: string, family: string, body: { live_id: string; advisor_user_id: string | null }): Promise<{ row: { id: string; level: GrowthLevel } }> {
    return request(`/api/marketing/levels/${encodeURIComponent(company)}/${encodeURIComponent(family)}/undo`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async setLevelCooldown(company: string, days: number): Promise<{ settings: LevelSettingsRow | null }> {
    return request(`/api/marketing/level-settings/${encodeURIComponent(company)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ cooldown_days: days }) });
  },
  async goalAction(id: string, action: "send" | "approve" | "withdraw"): Promise<Record<string, unknown>> {
    return request(`/api/marketing/goals/${encodeURIComponent(id)}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async draftEmailPiece(id: string, body: { brief: string; voice: string; option: string; expected_version: number }): Promise<{ piece: EmailPieceRow | null; held: { sentence: string; reason: string } | null }> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/draft`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async previewEmailPiece(id: string, householdId: string | null): Promise<EmailPreview> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/preview${householdId ? `?household=${encodeURIComponent(householdId)}` : ""}`);
  },
  /** The preview of the email as edited, before it is saved (nothing is written). */
  async previewEmailDraft(id: string, householdId: string | null, draft: { subject: string; preview: string; blocks: EmailBlockT[] }): Promise<EmailPreview> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ household: householdId, ...draft }) });
  },
  async testEmailPiece(id: string, householdId: string | null): Promise<{ ok: true; sent_to: string }> {
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/test`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ household_id: householdId }) });
  },
  /** A library photo (`team`) or, since row 8.1, an upload or a made image. */
  async pickEmailImage(id: string, picked: { source: "team" | "upload" | "made"; id: string }): Promise<{ image_id: string; url: string | null; name: string }> {
    const body = picked.source === "team" ? { media_id: picked.id } : { marketing_media_id: picked.id };
    return request(`/api/marketing/pieces/email/${encodeURIComponent(id)}/image`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async writeSeries(seriesId: string, body: { brief: string; voice: string }): Promise<{ written: number[]; held: { position: number; sentence: string; reason: string }[]; skipped: number[] }> {
    return request(`/api/marketing/series/${encodeURIComponent(seriesId)}/write`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async newCampaignEmail(campaignId: string, body: { name: string; starts_on: string; send_time: string }): Promise<{ series_id: string; piece_id: string | null }> {
    return request(`/api/marketing/campaigns/${encodeURIComponent(campaignId)}/email`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async listAudiences(includeArchived = false): Promise<AudiencesResponse> {
    return request(`/api/marketing/audiences${includeArchived ? "?archived=1" : ""}`);
  },
  async countAudience(draft: AudienceDraft): Promise<AudienceCountResponse> {
    return request("/api/marketing/audiences/count", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
  },
  async audienceHousehold(householdId: string, draft: AudienceDraft): Promise<AudienceHouseholdResponse> {
    return request(`/api/marketing/audiences/household/${encodeURIComponent(householdId)}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
  },
  async getAudience(id: string): Promise<{ audience: SavedAudience; versions: AudienceVersion[] }> {
    return request(`/api/marketing/audiences/${encodeURIComponent(id)}`);
  },
  async createAudience(draft: AudienceDraft & { name: string }): Promise<{ audience: SavedAudience }> {
    return request("/api/marketing/audiences", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
  },
  async editAudience(id: string, draft: AudienceDraft & { name: string; expected_version: number }): Promise<{ audience: SavedAudience }> {
    return request(`/api/marketing/audiences/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
  },
  async archiveAudience(id: string, body: { archived: boolean; expected_version: number }): Promise<{ audience: SavedAudience }> {
    return request(`/api/marketing/audiences/${encodeURIComponent(id)}/archive`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async undoAudience(id: string, expectedVersion: number): Promise<{ audience: SavedAudience }> {
    return request(`/api/marketing/audiences/${encodeURIComponent(id)}/undo`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version: expectedVersion }) });
  },
  /** Row 9a.1: the platform rules table (Grant acts; system_admin reads). */
  async getPlatformRules(): Promise<PlatformRulesResponse> {
    return request("/api/marketing/settings/platform-rules");
  },
  async getPlatformRuleHistory(id: string): Promise<{ id: string; history: PlatformRuleVersion[] }> {
    return request(`/api/marketing/settings/platform-rules/${encodeURIComponent(id)}/history`);
  },
  async savePlatformRule(id: string, body: PlatformRuleEditBody): Promise<{ ok: true; rule: PlatformRule; message: string }> {
    return request(`/api/marketing/settings/platform-rules/${encodeURIComponent(id)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async runPlatformRulesWatch(): Promise<{ ok: true; pages: number; baselined: number; flagged: number; unchanged: number; unreadable: number; skipped: number }> {
    return request("/api/marketing/settings/platform-rules/watch", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 9a.2: the month's caps and meters (Grant acts; system_admin reads all; a desk reads its own). */
  async getBudgets(): Promise<BudgetsResponse> {
    return request("/api/marketing/settings/budgets");
  },
  async getBudgetHistory(id: string): Promise<{ id: string; history: BudgetVersion[] }> {
    return request(`/api/marketing/settings/budgets/${encodeURIComponent(id)}/history`);
  },
  async saveBudgetCap(id: string, body: BudgetEditBody): Promise<{ ok: true; row: NonNullable<BudgetMeterWire["cap_row"]>; companies: BudgetMeterWire[]; firm: BudgetMeterWire; message: string }> {
    return request(`/api/marketing/settings/budgets/${encodeURIComponent(id)}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Row 9a.4: screen 62's switch, objects and commands. */
  async getPaidExecution(): Promise<ExecutionResponse> {
    return request("/api/marketing/settings/paid/execution");
  },
  async pressKillSwitch(confirm: string): Promise<{ ok: true; result: PaidSwitchResult; message: string; state: ExecutionResponse["kill_switch"] }> {
    return request("/api/marketing/settings/paid/kill-switch", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ confirm }) });
  },
  async testKillSwitch(): Promise<{ ok: true; tested: boolean; by_provider: { provider: string; account: string | null; ok: boolean; word: string }[]; message: string; state: ExecutionResponse["kill_switch"] }> {
    return request("/api/marketing/settings/paid/kill-switch/test", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async resumeAdObject(id: string): Promise<{ ok: true; command: OutboxRowView; message: string }> {
    return request(`/api/marketing/settings/paid/objects/${encodeURIComponent(id)}/resume`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async setProviderCaps(): Promise<{ ok: true; queued: { provider_account_id: string; company_slug: string; cap_dollars: number; outbox_id: string }[]; refused: { provider_account_id: string; message: string }[]; google: string; message: string }> {
    return request("/api/marketing/settings/paid/provider-caps", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async setLocalServicesBudget(): Promise<{ ok: true; outbox_id: string; dollars: number; period: string; state: string; problem: string | null; message: string }> {
    return request("/api/marketing/settings/paid/local-services-budget", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async assignAdAccountCompany(provider: "meta" | "google_ads", accountId: string, companySlug: string): Promise<{ ok: true; message: string }> {
    return request(`/api/marketing/settings/paid/ad-accounts/${provider}/${encodeURIComponent(accountId)}/company`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ company_slug: companySlug }) });
  },
  // Row 9b.2: the Meta ad builder.
  async createAd(body: { post_id: string; company_slug?: string }): Promise<{ ok: true; id: string; href: string }> {
    return request("/api/marketing/ads", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** "Write a new ad": a draft with no post, for a company and the service it is about. */
  async createNewAd(body: { company_slug: string; product_family: string }): Promise<{ ok: true; id: string; href: string }> {
    return request("/api/marketing/ads/new", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Stew's draft of a new ad's words; nothing is saved. */
  async draftNewAdWords(id: string, body: { note: string }): Promise<{ ok: true; body: string; versions: { hook: string; first_line: string }[] }> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}/draft-words`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getAd(id: string): Promise<AdBuilderResponse> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}`);
  },
  async saveAd(id: string, body: AdDraftBody): Promise<AdBuilderResponse> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async saveAdVersions(id: string, body: { versions: MetaVersionBody[]; landing_page_id: string | null; body?: string; expected_version_number: number }): Promise<AdBuilderResponse> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}/versions`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async estimateAd(id: string): Promise<AdBuilderResponse> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}/estimate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async sendAd(id: string): Promise<AdBuilderResponse & { message: string }> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async takeBackAd(id: string): Promise<AdBuilderResponse & { message: string }> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}/take-back`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async pauseAd(id: string): Promise<{ ok: true; state: string; message: string }> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}/pause`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async lowerAdBudget(id: string, dollars: number): Promise<{ ok: true; state: string; message: string }> {
    return request(`/api/marketing/ads/${encodeURIComponent(id)}/budget`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ dollars }) });
  },
  // Row 9b.4: Realty's Local Services leads and ad.
  async localServices(): Promise<LocalServicesView> {
    return request("/api/marketing/settings/local-services");
  },
  async setLocalServicesLeadOwner(userId: string): Promise<{ ok: true; message: string }> {
    return request("/api/marketing/settings/local-services/lead-owner", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ user_id: userId }) });
  },
  async readLocalServicesLeads(): Promise<{ ok: boolean; message: string }> {
    return request("/api/marketing/settings/local-services/read", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async startLocalServices(words: string): Promise<{ ok: boolean; message: string }> {
    return request("/api/marketing/settings/local-services/start", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ words }) });
  },
  async pauseLocalServices(): Promise<{ ok: boolean; message: string }> {
    return request("/api/marketing/settings/local-services/pause", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  // Row 9b.6: Meta lead forms.
  async metaLeads(): Promise<MetaLeadsView> {
    return request("/api/marketing/settings/meta-leads");
  },
  async subscribeMetaLeads(): Promise<{ ok: true; message: string }> {
    return request("/api/marketing/settings/meta-leads/subscribe", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async setMetaLeadFormCompany(formId: string, companySlug: string): Promise<{ ok: true; message: string }> {
    return request(`/api/marketing/settings/meta-leads/forms/${encodeURIComponent(formId)}/company`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ company_slug: companySlug }) });
  },
  // Row 9b.5: conversion feedback.
  async conversionFeedback(): Promise<ConversionFeedbackView> {
    return request("/api/marketing/settings/conversions");
  },
  async setConversionSwitch(provider: "meta" | "google_ads", on: boolean): Promise<{ ok: true; message: string }> {
    return request("/api/marketing/settings/conversions/switch", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ provider, on }) });
  },
  async setConversionDestination(providerAccountId: string, conversionActionId: string): Promise<{ ok: true; message: string }> {
    return request("/api/marketing/settings/conversions/destination", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ provider_account_id: providerAccountId, conversion_action_id: conversionActionId }) });
  },
  // Row 9b.3: the Google search campaign builder.
  async createSearchCampaign(landingPageId: string): Promise<{ ok: true; id: string; href: string }> {
    return request("/api/marketing/search-campaigns", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ landing_page_id: landingPageId }) });
  },
  async getSearchCampaign(id: string): Promise<SearchBuilderResponse> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}`);
  },
  // Row 11.10: short clips.
  async listClips(): Promise<ClipsResponse> {
    return request(`/api/marketing/clips`);
  },
  async createClip(body: { company: ClipCompany; source_media_id?: string; duration_seconds?: number; audio_base64?: string; content_source_id?: string }): Promise<{ clip: ClipView }> {
    return request(`/api/marketing/clips`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getClip(id: string): Promise<{ clip: ClipView }> {
    return request(`/api/marketing/clips/${encodeURIComponent(id)}`);
  },
  async suggestClipCuts(id: string): Promise<{ clip: ClipView }> {
    return request(`/api/marketing/clips/${encodeURIComponent(id)}/suggest`, { method: "POST" });
  },
  async saveClipCut(id: string, body: { start: number; end: number; caption: string; expected_version_number: number }): Promise<{ clip: ClipView }> {
    return request(`/api/marketing/clips/${encodeURIComponent(id)}/cut`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async makeClip(id: string, expectedVersion: number): Promise<{ clip: ClipView }> {
    return request(`/api/marketing/clips/${encodeURIComponent(id)}/make`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version_number: expectedVersion }) });
  },
  async checkClip(id: string): Promise<{ clip: ClipView }> {
    return request(`/api/marketing/clips/${encodeURIComponent(id)}/check`, { method: "POST" });
  },
  async getCaptionStyles(): Promise<CaptionStylesResponse> {
    return request(`/api/marketing/settings/caption-styles`);
  },
  /** A null template id takes the company's caption style back to none. */
  async setCaptionStyle(company: ClipCompany, templateId: string | null): Promise<{ ok: true; template_name: string | null }> {
    return request(`/api/marketing/settings/caption-styles/${encodeURIComponent(company)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ template_id: templateId }) });
  },
  async saveSearchVersions(id: string, body: { versions: { headlines: string[]; descriptions: string[] }[]; expected_version_number: number }): Promise<SearchBuilderResponse> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}/versions`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async saveSearchCampaign(id: string, body: SearchDraftBody): Promise<SearchBuilderResponse> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async readSearchFigures(id: string): Promise<SearchBuilderResponse> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}/figures`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async sendSearchCampaign(id: string): Promise<SearchBuilderResponse & { message: string }> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async takeBackSearchCampaign(id: string): Promise<SearchBuilderResponse & { message: string }> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}/take-back`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 11.9c: Grant's "Switch these off" on a batch's versions behind; the server checks them again. */
  async switchOffVersions(kind: "search" | "boost", id: string, versionIds: string[]): Promise<{ ok: true; switched: string[]; message: string }> {
    return request(`/api/marketing/results/versions/switch-off`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind, id, version_ids: versionIds }) });
  },
  async pauseSearchCampaign(id: string): Promise<{ ok: true; state: string; message: string }> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}/pause`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async stepUpSearchCampaign(id: string): Promise<{ ok: true; state: string; message: string }> {
    return request(`/api/marketing/search-campaigns/${encodeURIComponent(id)}/step-up`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async addNeverFor(phrase: string): Promise<{ ok: true }> {
    return request("/api/marketing/search-campaigns/never-for", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phrase }) });
  },
  async removeNeverFor(id: string): Promise<{ ok: true }> {
    return request(`/api/marketing/search-campaigns/never-for/${encodeURIComponent(id)}/remove`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  // Row 9b.1: the ad audience lists. Grant creates, switches and syncs; a system admin and a company admin read counts.
  async adLists(): Promise<AdListsResponse> {
    return request("/api/marketing/settings/paid/lists");
  },
  async createAdList(body: { provider: "meta" | "google_ads"; company_slug: string; purpose: "exclusion" | "inclusion"; audience_id: string | null }): Promise<{ ok: true; id: string; message: string }> {
    return request("/api/marketing/settings/paid/lists", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async switchAdList(id: string, enabled: boolean): Promise<{ ok: true; message: string }> {
    return request(`/api/marketing/settings/paid/lists/${encodeURIComponent(id)}/switch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled }) });
  },
  async syncAdList(id: string): Promise<{ ok: true; message: string }> {
    return request(`/api/marketing/settings/paid/lists/${encodeURIComponent(id)}/sync`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async getMarketingCapacity(): Promise<CapacitySettingsResponse> {
    return request("/api/marketing/settings/capacity");
  },
  async saveMarketingDesk(companyId: string, body: { slots_per_week: number; processing_ceiling: number | null; ceiling_label: string; note: string; from_proposal?: boolean }): Promise<{ ok: true; id: string }> {
    return request(`/api/marketing/settings/capacity/${encodeURIComponent(companyId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  /** Who owns a desk (Grant only): the person its new business goes to. */
  async saveMarketingDeskOwner(body: { desk_key: DeskOwnerRow["desk_key"]; owner_user_id: string; note: string }): Promise<{ ok: true; id: string; owner_name: string | null }> {
    return request("/api/marketing/settings/desk-owners", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async clearMarketingDeskOwner(deskKey: DeskOwnerRow["desk_key"]): Promise<{ ok: true; cleared_id: string }> {
    return request("/api/marketing/settings/desk-owners/clear", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ desk_key: deskKey }) });
  },
  async undoMarketingDeskOwner(deskKey: DeskOwnerRow["desk_key"], settingId: string): Promise<{ ok: true; restored_id: string | null }> {
    return request("/api/marketing/settings/desk-owners/undo", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ desk_key: deskKey, setting_id: settingId }) });
  },
  /** Clear a desk's number (Grant only); the toast's Undo saves the old numbers again. */
  async clearMarketingDesk(companyId: string): Promise<{ ok: true; cleared_id: string }> {
    return request(`/api/marketing/settings/capacity/${encodeURIComponent(companyId)}/clear`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 7.3: take back the last change to one desk (Grant only). */
  async undoMarketingDesk(companyId: string, settingId: string): Promise<{ ok: true; restored_id: string | null }> {
    return request(`/api/marketing/settings/capacity/${encodeURIComponent(companyId)}/undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ setting_id: settingId }),
    });
  },
  /** Row 3.2: the household Marketing tab (service map, referrals, signals,
   *  the decision with every moment it weighed). */
  /** Row 3.4: "Where we could help", the gaps counted four ways. */
  async getMarketingOpportunities(): Promise<OpportunitiesResponse> {
    return request<OpportunitiesResponse>(`/api/marketing/opportunities`);
  },
  /** Row 8.8: the search opportunity list, read beside the gaps. */
  async getSearchDemand(): Promise<SearchDemandResponse> {
    return request<SearchDemandResponse>(`/api/marketing/search-demand`);
  },
  /** Row 8.11: the viewer's mail and card tasks (their own, or everyone's in their companies). */
  async listMailTasks(scope: "mine" | "everyone"): Promise<MailTaskListResponse> {
    return request<MailTaskListResponse>(`/api/marketing/mail?scope=${scope}`);
  },
  async markMailTaskMailed(sendId: string): Promise<{ ok: true; activity_id: string; mailed_at: string; undo_seconds: number }> {
    return request(`/api/marketing/mail/${encodeURIComponent(sendId)}/mailed`, { method: "POST", body: JSON.stringify({}) });
  },
  async undoMailTaskMailed(sendId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/mail/${encodeURIComponent(sendId)}/undo`, { method: "POST", body: JSON.stringify({}) });
  },
  async markHouseholdMailReturned(householdId: string, reason: "returned" | "vacant"): Promise<{ ok: true; word: string }> {
    return request(`/api/marketing/mail/households/${encodeURIComponent(householdId)}/returned`, { method: "POST", body: JSON.stringify({ reason }) });
  },
  async clearHouseholdMailReturned(householdId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/mail/households/${encodeURIComponent(householdId)}/returned/clear`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Row 8.12: a household's video messages, who may get one, and whether this person's Outlook is connected. */
  async listHouseholdVideos(householdId: string): Promise<HouseholdVideoResponse> {
    return request<HouseholdVideoResponse>(`/api/marketing/households/${encodeURIComponent(householdId)}/video`);
  },
  /** The advisor's own still, for the tab's thumbnail (the family's page has its own door). */
  householdVideoStillUrl(videoId: string): string {
    return apiUrl(`/api/marketing/video/${encodeURIComponent(videoId)}/still`);
  },
  /** Multipart (the clip and its still frame), so it can't go through request(). */
  async recordHouseholdVideo(
    householdId: string,
    args: { clip: Blob; still: Blob; durationSeconds: number; companySlug: string },
  ): Promise<{ video: { id: string; duration_seconds: number; state: "recorded"; company_id: string } }> {
    const fd = new FormData();
    fd.append("clip", args.clip, "video");
    fd.append("still", args.still, "still");
    fd.append("duration_seconds", String(args.durationSeconds));
    fd.append("company", args.companySlug);
    const res = await fetch(apiUrl(`/api/marketing/households/${encodeURIComponent(householdId)}/video`), { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) {
      let payload: { message?: string } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        payload = {};
      }
      throw new Error(payload.message ?? "The video did not upload. Try again.");
    }
    return (await res.json()) as { video: { id: string; duration_seconds: number; state: "recorded"; company_id: string } };
  },
  async sendVideoMessage(videoId: string, body: { contact_ids: string[]; subject: string; text: string }): Promise<{ ok: true; sent: number; recipients: { recipient_id: string; contact_id: string }[] }> {
    return request(`/api/marketing/video/${encodeURIComponent(videoId)}/send`, { method: "POST", body: JSON.stringify(body) });
  },
  async removeVideoMessage(videoId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/video/${encodeURIComponent(videoId)}/remove`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Row 8.10: events, ahead and past, for the viewer's companies. */
  async listMarketingEvents(): Promise<EventsListResponse> {
    return request<EventsListResponse>(`/api/marketing/events`);
  },
  async createMarketingEvent(body: {
    company: string;
    kind: string;
    title: string;
    description: string | null;
    starts_at: string;
    place: string;
    seats: number;
    presenter_user_id: string | null;
    service_key: string | null;
    cash_cost: number;
    audience_id: string | null;
    /** Row 11.5a: the goal that planned this event. */
    goal_id?: string | null;
  }): Promise<{ event: MarketingEventRow }> {
    return request(`/api/marketing/events`, { method: "POST", body: JSON.stringify(body) });
  },
  async getMarketingEvent(id: string): Promise<EventScreen> {
    return request<EventScreen>(`/api/marketing/events/${encodeURIComponent(id)}`);
  },
  async addEventRegistration(id: string, body: { name: string; email: string; guests: number }): Promise<{ ok: true; registration: EventRegistration; created: boolean; created_person: boolean }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/registrations`, { method: "POST", body: JSON.stringify(body) });
  },
  async undoCheckInEventRegistration(id: string, rid: string): Promise<{ ok: true; registration: EventRegistration }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/registrations/${encodeURIComponent(rid)}/undo-check-in`, { method: "POST", body: JSON.stringify({}) });
  },
  async checkInEventRegistration(id: string, rid: string): Promise<{ ok: true; registration: EventRegistration }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/registrations/${encodeURIComponent(rid)}/check-in`, { method: "POST", body: JSON.stringify({}) });
  },
  async cancelEventRegistration(id: string, rid: string): Promise<{ ok: true; registration: EventRegistration }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/registrations/${encodeURIComponent(rid)}/cancel`, { method: "POST", body: JSON.stringify({}) });
  },
  async submitMarketingEvent(id: string): Promise<{ ok: true; submitted: number; message: string }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/submit`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Row 8.13: "Remind by text": one task per registered household with the words; the OS never sends a text. */
  async remindEventByText(id: string): Promise<{ ok: true; made: number; refused: Record<string, number>; message: string }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/remind-by-text`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Row 8.13: "They asked us to stop texting" on a person; every company's text tasks honor it. */
  async recordTextStop(contactId: string): Promise<{ ok: true; stopped_at: string; closed_tasks: number; line: string | null; undo_seconds: number }> {
    return request(`/api/marketing/text-stop/${encodeURIComponent(contactId)}`, { method: "POST", body: JSON.stringify({}) });
  },
  async clearTextStop(contactId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/text-stop/${encodeURIComponent(contactId)}/clear`, { method: "POST", body: JSON.stringify({}) });
  },
  async remindEventNow(id: string): Promise<{ ok: true; families: number; ran: { drafts: number; refused: number } | null; message: string }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/remind-now`, { method: "POST", body: JSON.stringify({}) });
  },
  async setMarketingEventState(id: string, state: "open" | "closed" | "cancelled", expectedVersion: number): Promise<{ ok: true; event: MarketingEventRow }> {
    return request(`/api/marketing/events/${encodeURIComponent(id)}/state`, { method: "POST", body: JSON.stringify({ state, expected_version: expectedVersion }) });
  },
  /** Row 8.9: the firm's Google reviews, the viewer's companies' and the firm's. */
  async getGoogleReviews(params: { concerns?: boolean } = {}): Promise<GoogleReviewsResponse> {
    return request<GoogleReviewsResponse>(`/api/marketing/reviews${params.concerns ? "?concerns=1" : ""}`);
  },
  /** Save a reply as a draft, or send it to Grant (`send`). Nothing posts to Google here. */
  async saveReviewReply(reviewId: string, body: { text: string; send: boolean; expected_version: number | null }): Promise<{ ok: true; reply: ReviewReplyRow; review: GoogleReviewRow; message: string }> {
    return request(`/api/marketing/reviews/${encodeURIComponent(reviewId)}/reply`, { method: "POST", body: JSON.stringify(body) });
  },
  async takeBackReviewReply(reviewId: string): Promise<{ ok: true; reply: ReviewReplyRow; review: GoogleReviewRow }> {
    return request(`/api/marketing/reviews/${encodeURIComponent(reviewId)}/reply/take-back`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Stew shortens the draft with the draft's own words; null text when it could not without adding one. */
  async shortenReviewReply(reviewId: string, text: string): Promise<{ ok: boolean; text: string | null; message: string }> {
    return request(`/api/marketing/reviews/${encodeURIComponent(reviewId)}/reply/shorten`, { method: "POST", body: JSON.stringify({ text }) });
  },
  /** Modal 23 opening: the sentence, the row's counts, Stew's first estimate. */
  /** `countsAsOf` is the row's own "counted at": the server refuses when
   *  the counts have refreshed since, so the window never shows numbers the
   *  row did not (gate 11). */
  async getOpportunityEstimate(
    gapKey: string,
    countsAsOf?: string,
  ): Promise<{ gap: OpportunityGap; four: FourWay; counts_as_of: string; sentence: string; hint: string; estimate: QuickEstimate }> {
    const q = countsAsOf ? `?counts_as_of=${encodeURIComponent(countsAsOf)}` : "";
    return request(`/api/marketing/opportunities/${encodeURIComponent(gapKey)}/estimate${q}`);
  },
  /** Row 10.8: "Suggest priorities", the ranked rows with their reasons; one snapshot kept. */
  async suggestOpportunityPriorities(): Promise<PrioritiesResponse> {
    return request(`/api/marketing/opportunities/priorities`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async saveOpportunityGoal(gapKey: string, body: { audience: GoalAudience; mode: GoalMode; counts_as_of?: string; reason?: string | null }): Promise<{ ok: true; id: string }> {
    return request(`/api/marketing/opportunities/${encodeURIComponent(gapKey)}/goal`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async withdrawOpportunityGoal(id: string): Promise<{ ok: true }> {
    return request(`/api/marketing/opportunities/goals/${encodeURIComponent(id)}/withdraw`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  /** Row 3.6: top the gap's open "never asked" questions up to ten, each a
   *  task for the household's own advisor. A person's press, never a schedule. */
  async queueOpportunityQuestions(gapKey: string): Promise<QueueResult & { message: string }> {
    return request(`/api/marketing/opportunities/${encodeURIComponent(gapKey)}/queue-questions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  async undoOpportunityQuestions(batchId: string): Promise<{ ok: true; withdrawn: number }> {
    return request(`/api/marketing/opportunities/queue-questions/${encodeURIComponent(batchId)}/undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  async undoOpportunityTell(taskId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/opportunities/tell/${encodeURIComponent(taskId)}/undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  async tellOpportunityOwner(gapKey: string): Promise<{ ok: true; task_id: string }> {
    return request(`/api/marketing/opportunities/${encodeURIComponent(gapKey)}/tell`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  /** The privacy notice log (screen 65, row 3.5): Grant and Baker only. */
  async getPrivacyNoticeLog(): Promise<PrivacyNoticeLog> {
    return request<PrivacyNoticeLog>(`/api/marketing/settings/privacy-notice`);
  },
  /** Row 5.0: Connected accounts (screen 64) and the firm's claims. Grant
   *  acts; a system admin reads. */
  async getConnectedAccounts(): Promise<ConnectedAccountsResponse> {
    return request<ConnectedAccountsResponse>(`/api/marketing/settings/accounts`);
  },
  async disconnectSocialAccount(id: string, body: { confirm: string; reason: string }): Promise<{ ok: true; held: number }> {
    return request(`/api/marketing/settings/accounts/${encodeURIComponent(id)}/disconnect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async reconnectSocialAccount(id: string): Promise<{ ok: true }> {
    return request(`/api/marketing/settings/accounts/${encodeURIComponent(id)}/reconnect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  async setSocialAccountOwner(id: string, body: { scope: "personal" | "firm"; owner_user_id?: string }): Promise<{ ok: true }> {
    return request(`/api/marketing/settings/accounts/${encodeURIComponent(id)}/owner`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async approveFirmClaim(id: string, reason: string): Promise<{ ok: true; claim: FirmClaim }> {
    return request(`/api/marketing/settings/claims/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });
  },
  /** Audit S3-H7: take a claim's approval back; the words stay, it returns to draft. */
  async unapproveFirmClaim(id: string, reason: string | null): Promise<{ ok: true; claim: FirmClaim }> {
    return request(`/api/marketing/settings/claims/${encodeURIComponent(id)}/unapprove`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });
  },
  /** Row 5.1: the photo library picker (modal 37's read half). */
  async getMediaLibrary(args: { q?: string | undefined; limit?: number; offset?: number; flag?: PhotoFlagFilter } = {}): Promise<MediaLibraryResponse> {
    const p = new URLSearchParams();
    if (args.q) p.set("q", args.q);
    if (args.flag && args.flag !== "all") p.set("flag", args.flag);
    if (args.limit) p.set("limit", String(args.limit));
    if (args.offset) p.set("offset", String(args.offset));
    const qs = p.toString();
    return request<MediaLibraryResponse>(`/api/marketing/media${qs ? `?${qs}` : ""}`);
  },
  /** The thumbnail is streamed by the Worker from Graph; an <img> loads it
   *  with the session cookie like the avatar does. */
  mediaThumbnailUrl(id: string): string {
    return apiUrl(`/api/marketing/media/${encodeURIComponent(id)}/thumbnail`);
  },
  /** Row 8.4: the Calendar and the Content library. */
  async getMarketingCalendar(args: { from: string; to: string; status?: CalendarStatusFilter; companies?: string[] | null }): Promise<CalendarResponse> {
    const p = new URLSearchParams({ from: args.from, to: args.to });
    if (args.status && args.status !== "all") p.set("status", args.status);
    if (args.companies && args.companies.length > 0) p.set("companies", args.companies.join(","));
    return request<CalendarResponse>(`/api/marketing/calendar?${p.toString()}`);
  },
  async moveCalendarItem(body: { kind: CalendarKindKey; id: string; day: string }): Promise<{ item: CalendarItemOut }> {
    return request(`/api/marketing/calendar/move`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getContentLibrary(args: { tab?: LibraryTabKey; companies?: string[] | null; q?: string }): Promise<LibraryResponse> {
    const p = new URLSearchParams();
    if (args.q && args.q.trim()) p.set("q", args.q.trim());
    if (args.tab && args.tab !== "all") p.set("tab", args.tab);
    if (args.companies && args.companies.length > 0) p.set("companies", args.companies.join(","));
    const qs = p.toString();
    return request<LibraryResponse>(`/api/marketing/content/library${qs ? `?${qs}` : ""}`);
  },
  async scheduleContentTemplate(body: { template_id: string; campaign_id: string; day: string; send_time: string }): Promise<{ series_id: string; piece_id: string; day: string }> {
    return request(`/api/marketing/content/use`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async reuseContentPiece(body: { kind: "email" | "post" | "blog"; id: string; day: string }): Promise<{ open_to: string }> {
    return request(`/api/marketing/content/reuse`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Row 8.2: the social composer. */
  async listSocialPieces(): Promise<{ items: SocialPieceListItem[]; total: number; can_write: boolean }> {
    return request(`/api/marketing/pieces/post`);
  },
  async createSocialPiece(companies: string[] = []): Promise<{ piece: { id: string } }> {
    return request(`/api/marketing/pieces/post`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ companies }) });
  },
  async getSocialPiece(id: string): Promise<SocialPieceScreen> {
    return request(`/api/marketing/pieces/post/${encodeURIComponent(id)}`);
  },
  async saveSocialPiece(id: string, edit: SocialPieceEdit & { expected_version: number }): Promise<SocialPieceScreen> {
    return request(`/api/marketing/pieces/post/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(edit) });
  },
  async submitSocialPiece(id: string, expectedVersion: number): Promise<SocialPieceScreen> {
    return request(`/api/marketing/pieces/post/${encodeURIComponent(id)}/submit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version: expectedVersion }) });
  },
  async takeBackSocialPiece(id: string): Promise<SocialPieceScreen & { held: number }> {
    return request(`/api/marketing/pieces/post/${encodeURIComponent(id)}/take-back`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 8.5: the blog builder. */
  async listBlogPosts(): Promise<{ items: BlogPostListItem[]; total: number; can_write: boolean }> {
    return request(`/api/marketing/pieces/blog`);
  },
  async createBlogPost(body: { companies?: string[]; day?: string; campaign_id?: string; title?: string; search_phrases?: string } = {}): Promise<{ post: { id: string } }> {
    return request(`/api/marketing/pieces/blog`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getBlogPost(id: string): Promise<BlogPostScreen> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}`);
  },
  async saveBlogPost(id: string, edit: BlogPostEdit & { expected_version: number }): Promise<BlogPostScreen> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(edit) });
  },
  async draftBlogPost(id: string, body: { outline: string; voice: string; option: string; expected_version: number }): Promise<BlogPostScreen & { held: { sentence: string; reason: string } | null }> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/draft`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async submitBlogPost(id: string, expectedVersion: number): Promise<BlogPostScreen> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/submit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_version: expectedVersion }) });
  },
  async takeBackBlogPost(id: string): Promise<BlogPostScreen> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/take-back`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async previewBlogPost(id: string): Promise<BlogPostScreen> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async cutFromBlogPost(id: string, kind: "email" | "post"): Promise<{ kind: "email" | "post"; id: string; open_to: string; cuts: BlogPostScreen["cuts"] }> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/cut`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind }) });
  },
  /** Row 8.6: landing pages, one per campaign or blog post. */
  async findLandingPage(parent: LandingParentRef): Promise<{ page: LandingPageScreen | null; can_make: boolean }> {
    const q = "campaign_id" in parent ? `campaign_id=${encodeURIComponent(parent.campaign_id)}` : `blog_post_id=${encodeURIComponent(parent.blog_post_id)}`;
    return request(`/api/marketing/landing-pages?${q}`);
  },
  async createLandingPage(parent: LandingParentRef): Promise<LandingPageScreen> {
    return request(`/api/marketing/landing-pages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(parent) });
  },
  async getLandingPage(id: string): Promise<LandingPageScreen> {
    return request(`/api/marketing/landing-pages/${encodeURIComponent(id)}`);
  },
  async saveLandingPage(id: string, edit: { title?: string; slug?: string; content?: LandingContentOut; advisor_user_id?: string; expected_version: number }): Promise<LandingPageScreen> {
    return request(`/api/marketing/landing-pages/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(edit) });
  },
  async writeLandingPage(id: string): Promise<LandingPageScreen> {
    return request(`/api/marketing/landing-pages/${encodeURIComponent(id)}/write`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  landingPagePreviewUrl(id: string): string {
    return `/api/marketing/landing-pages/${encodeURIComponent(id)}/preview`;
  },
  /** Row 8.7: Stew's read of a blog post and of a social post. */
  async readBlogPost(id: string): Promise<{ read: ContentRead }> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/read`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async applyBlogRead(id: string, readId: string, body: { ids: string[]; rescore: boolean }): Promise<{ read: ContentRead; applied: string[]; could_not: { id: string; reason: string }[]; screen: BlogPostScreen }> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/read/${encodeURIComponent(readId)}/apply`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async keepBlogAsWritten(id: string, readId: string): Promise<{ read: ContentRead }> {
    return request(`/api/marketing/pieces/blog/${encodeURIComponent(id)}/read/${encodeURIComponent(readId)}/keep`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async readSocialPiece(id: string): Promise<{ read: ContentRead }> {
    return request(`/api/marketing/pieces/post/${encodeURIComponent(id)}/read`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async applySocialRead(id: string, readId: string, body: { ids: string[]; rescore: boolean }): Promise<{ read: ContentRead; applied: string[]; could_not: { id: string; reason: string }[]; screen: SocialPieceScreen }> {
    return request(`/api/marketing/pieces/post/${encodeURIComponent(id)}/read/${encodeURIComponent(readId)}/apply`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async keepSocialAsWritten(id: string, readId: string): Promise<{ read: ContentRead }> {
    return request(`/api/marketing/pieces/post/${encodeURIComponent(id)}/read/${encodeURIComponent(readId)}/keep`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 8.5: Grant names where blog posts publish. */
  /** Row 9a.3: Grant enters the Meta system-user token once; it is read back, stored encrypted, and never returned. */
  async connectMetaAds(token: string): Promise<{ ok: true; connection: AdConnectionView; message: string }> {
    return request(`/api/marketing/settings/ad-connections/meta`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }) });
  },
  async rereadAdConnection(id: string): Promise<{ ok: true; connection: AdConnectionView; message: string }> {
    return request(`/api/marketing/settings/ad-connections/${encodeURIComponent(id)}/reread`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async holdAdConnection(id: string, body: { confirm: string; reason: string }): Promise<{ ok: true; connection: AdConnectionView; message: string }> {
    return request(`/api/marketing/settings/ad-connections/${encodeURIComponent(id)}/hold`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async releaseAdConnection(id: string): Promise<{ ok: true; connection: AdConnectionView; message: string }> {
    return request(`/api/marketing/settings/ad-connections/${encodeURIComponent(id)}/release`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  async setBlogDestination(kind: "wordpress" | "none"): Promise<{ ok: true; kind: string; word: string }> {
    return request(`/api/marketing/settings/blog-destination`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind }) });
  },
  /** The large (800px) thumbnail, the composer's Instagram crop source. */
  mediaLargeThumbnailUrl(id: string): string {
    return apiUrl(`/api/marketing/media/${encodeURIComponent(id)}/thumbnail?size=large`);
  },
  /** "OK for marketing" on one photo; `expectedVersion` is the version the person saw (a stale one is a 409). */
  async setPhotoMarketingOk(id: string, ok: boolean, expectedVersion?: number): Promise<{ id: string; marketing_ok: boolean; marketing_ok_at: string | null; marketing_flag_version: number }> {
    return request(`/api/marketing/media/${encodeURIComponent(id)}/marketing-ok`, { method: "POST", body: JSON.stringify({ ok, ...(expectedVersion === undefined ? {} : { expected_version: expectedVersion }) }) });
  },
  async bulkSetPhotoMarketingOk(ids: string[], ok: boolean): Promise<PhotoBulkFlagResult> {
    return request<PhotoBulkFlagResult>(`/api/marketing/media/marketing-ok/bulk`, { method: "POST", body: JSON.stringify({ ids, ok }) });
  },
  async undoPhotoMarketingOk(bulkActionId: string): Promise<{ restored: number; left_alone: number }> {
    return request(`/api/marketing/media/marketing-ok/undo`, { method: "POST", body: JSON.stringify({ bulk_action_id: bulkActionId }) });
  },
  /** Row 8.1: the Yours and Made by Stew tabs. */
  async getMarketingMedia(args: { tab: "yours" | "made"; q?: string | undefined; limit?: number; offset?: number; context?: MediaContext; imagesOnly?: boolean }): Promise<MarketingMediaPage> {
    const p = new URLSearchParams({ tab: args.tab });
    if (args.q) p.set("q", args.q);
    if (args.limit) p.set("limit", String(args.limit));
    if (args.offset) p.set("offset", String(args.offset));
    if (args.context) p.set("context", args.context);
    if (args.imagesOnly) p.set("images_only", "1");
    return request<MarketingMediaPage>(`/api/marketing/media/library?${p.toString()}`);
  },
  marketingMediaFileUrl(id: string): string {
    return apiUrl(`/api/marketing/media/file/${encodeURIComponent(id)}`);
  },
  /** Multipart, so it can't go through request() (that forces JSON). */
  async uploadMarketingMedia(file: File, extra: { width?: number; height?: number; durationSeconds?: number } = {}): Promise<{ item: MarketingMediaItem }> {
    const fd = new FormData();
    fd.append("file", file);
    if (extra.width) fd.append("width", String(extra.width));
    if (extra.height) fd.append("height", String(extra.height));
    if (extra.durationSeconds) fd.append("duration_seconds", String(extra.durationSeconds));
    const res = await fetch(apiUrl("/api/marketing/media/upload"), { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) {
      let payload: { message?: string } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        payload = {};
      }
      throw new Error(payload.message ?? "The upload did not go through. Try again.");
    }
    return (await res.json()) as { item: MarketingMediaItem };
  },
  async makeMarketingImage(args: { brief: string; context: MediaContext; companySlug?: string | null; fresh?: boolean }): Promise<{ item: MarketingMediaItem; reused: boolean }> {
    return request(`/api/marketing/media/make`, {
      method: "POST",
      body: JSON.stringify({ brief: args.brief, context: args.context, company_slug: args.companySlug ?? null, fresh: args.fresh === true }),
    });
  },
  async removeMarketingMedia(id: string): Promise<{ removed: boolean }> {
    return request(`/api/marketing/media/${encodeURIComponent(id)}/remove`, { method: "POST", body: "{}" });
  },
  async reindexMediaLibrary(): Promise<PhotoIndexResult> {
    return request<PhotoIndexResult>(`/api/marketing/media/reindex`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 5.2: Founder Studio (Grant by user id). */
  async getFounderStudio(): Promise<FounderStudioResponse> {
    return request<FounderStudioResponse>(`/api/marketing/studio/founder`);
  },
  async createStudioSource(body: { kind: "voice" | "video" | "story"; transcript_text: string; title?: string; file_name?: string; segments?: TranscribedSegment[]; marketing_media_id?: string }): Promise<{ source: StudioSource }> {
    return request(`/api/marketing/sources`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Step 1: Stew pulls the story, the lesson, the services and the claims used. */
  async packageStudioSource(id: string): Promise<{ source: StudioSource; kinds: { kind: StudioDerivativeKind; label: string }[] }> {
    return request(`/api/marketing/sources/${encodeURIComponent(id)}/package`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Step 2: one derivative, one model call; remake after a source edit. */
  async makeStudioDerivative(id: string, kind: StudioDerivativeKind, body: { remake?: boolean; option?: StudioRerunOption } = {}): Promise<{ asset: StudioAsset; kept: boolean; model_called: boolean }> {
    return request(`/api/marketing/sources/${encodeURIComponent(id)}/derivatives/${encodeURIComponent(kind)}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async editStudioSource(id: string, body: { edited_source_text: string }): Promise<{ source: StudioSource }> {
    return request(`/api/marketing/sources/${encodeURIComponent(id)}/edit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async editStudioAsset(id: string, body: { body: string }): Promise<{ asset: StudioAsset; memories?: number; check?: { flags: number; sentence: string; reason: string } | null }> {
    return request(`/api/marketing/assets/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async acceptStudioAsset(id: string): Promise<{ asset: StudioAsset }> {
    return request(`/api/marketing/assets/${encodeURIComponent(id)}/accept`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Audit S2-H1 round 2: "Check again" on a held piece; no model call. */
  async recheckStudioAsset(id: string): Promise<{ asset: StudioAsset; changed: boolean }> {
    return request(`/api/marketing/assets/${encodeURIComponent(id)}/recheck`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 5.3: "Not my voice" on a piece; the other memory kinds Stew learns from edits. */
  async writeStudioMemory(assetId: string, body: { kind: "not_my_voice"; text: string }): Promise<{ ok: true; written: boolean }> {
    return request(`/api/marketing/assets/${encodeURIComponent(assetId)}/memory`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Row 5.3: an accepted personal piece cut to a company page, in approval, with the footer. */
  async cutStudioAssetToCompany(assetId: string, body: { social_account_id: string; company_slugs: string[] }): Promise<{ post: StudioPost }> {
    return request(`/api/marketing/assets/${encodeURIComponent(assetId)}/cut-to-company`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  // Row 5.4: publishing to Grant's accounts, the archive.
  async postStudioAsset(assetId: string, body: { social_account_id: string; media_item_id?: string | null; marketing_media_id?: string | null; when: "now" | string }): Promise<StudioPublishResponse> {
    return request(`/api/marketing/assets/${encodeURIComponent(assetId)}/post`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async publishStudioPost(postId: string): Promise<StudioPublishResponse> {
    return request(`/api/marketing/posts/${encodeURIComponent(postId)}/publish`, { method: "POST" });
  },
  async scheduleStudioPost(postId: string, body: { scheduled_at: string }): Promise<{ ok: true; post: StudioPost }> {
    return request(`/api/marketing/posts/${encodeURIComponent(postId)}/schedule`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async unscheduleStudioPost(postId: string): Promise<{ ok: true; post: StudioPost }> {
    return request(`/api/marketing/posts/${encodeURIComponent(postId)}/unschedule`, { method: "POST" });
  },
  async editStudioPost(postId: string, body: { rendered_text?: string; media_item_id?: string | null; marketing_media_id?: string | null }): Promise<{ ok: true; post: StudioPost }> {
    return request(`/api/marketing/posts/${encodeURIComponent(postId)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async approveStudioPost(postId: string, body: { scheduled_at: string }): Promise<{ ok: true; message: string; post: StudioPost }> {
    return request(`/api/marketing/posts/${encodeURIComponent(postId)}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async markStudioPostDeletedOnPlatform(postId: string): Promise<{ ok: true; post: StudioPost }> {
    return request(`/api/marketing/posts/${encodeURIComponent(postId)}/deleted-on-platform`, { method: "POST" });
  },
  async getStudioPostVersions(postId: string): Promise<{ post_id: string; chain: { ok: boolean; versions: number; brokenAt: number | null }; versions: StudioPostVersion[] }> {
    return request(`/api/marketing/posts/${encodeURIComponent(postId)}/versions`);
  },
  async editFirmClaimText(id: string, body: { template: string; reason: string }): Promise<{ ok: true; claim: FirmClaim }> {
    return request(`/api/marketing/settings/claims/${encodeURIComponent(id)}/text`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async getHouseholdMarketing(householdId: string): Promise<HouseholdMarketingResponse> {
    return request<HouseholdMarketingResponse>(`/api/households/${encodeURIComponent(householdId)}/marketing`);
  },
  /** Slice 6 audit M4: turn one company's marketing email back on for a
   *  person who asked, with the reason. */
  async restartCompanyEmail(householdId: string, body: { contact_id: string; company: string; reason: string }): Promise<{ ok: true; cleared: boolean }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/marketing/company-email`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  /** Row 3.7: a fresh personal check-in link for one adult, to copy. */
  async logTextReply(householdId: string, body: { contact_id: string; company: string; words: string }): Promise<{ activity_id: string; response: { intent: string; action: string; opportunity_id: string | null } | null }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/marketing/text-reply`, { method: "POST", body: JSON.stringify(body) });
  },
  async makeCheckinLink(householdId: string, contactId: string): Promise<{ url: string; last_day: string }> {
    return request<{ url: string; last_day: string }>(`/api/households/${encodeURIComponent(householdId)}/marketing/checkin-link`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId }),
    });
  },
  /** Row 3.1: the household card (what the OS decided about this family
   *  today, why, the last conversation, an open hand-raise, the history). */
  async getHouseholdMarketingCard(householdId: string): Promise<HouseholdCardResponse> {
    return request<HouseholdCardResponse>(`/api/marketing/households/${encodeURIComponent(householdId)}/card`);
  },
  /** Row 10.1: the one-tap verdict on an outside signal; `null` is the undo. */
  async setOutsideSignalVerdict(
    householdId: string,
    signalId: string,
    verdict: DecisionVerdict | null,
  ): Promise<{ ok: true; verdict: DecisionVerdict | null; previous: DecisionVerdict | null }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/marketing/signals/${encodeURIComponent(signalId)}/verdict`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ verdict }),
    });
  },
  /** Row 10.2: a listing a Realty advisor saw (S10-5). */
  async addListingSnapshot(
    householdId: string,
    input: { mls_number: string; listed_on: string; list_city: string | null },
  ): Promise<{ ok: true; signal_id: string; already_on_file: boolean }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/marketing/listing-snapshot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  },
  /** Row 10.2: take a typed listing back (its author or a system admin). */
  async takeBackListingSnapshot(householdId: string, signalId: string): Promise<{ ok: true }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/marketing/listing-snapshot/${encodeURIComponent(signalId)}/take-back`, {
      method: "POST",
    });
  },
  /** Row 10.1: Grant turns one outside feed on or off. */
  /** Row 10.5: Grant records (or clears) counsel's confirmation on the
   *  Mortgage inquiry watch. */
  async recordInquiryCounsel(confirmedOn: string, confirmedBy: string): Promise<{ ok: true; sources: OutsideSourceSwitch[] }> {
    return request(`/api/marketing/settings/outside-sources/inquiry/counsel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirmed_on: confirmedOn, confirmed_by: confirmedBy }),
    });
  },
  async clearInquiryCounsel(): Promise<{ ok: true; sources: OutsideSourceSwitch[] }> {
    return request(`/api/marketing/settings/outside-sources/inquiry/counsel`, { method: "DELETE" });
  },
  /** Row 10.5: the Mortgage watch (Mortgage only; a 403 for everyone else). */
  async getMortgageWatch(): Promise<MortgageWatchResponse> {
    return request(`/api/mortgage-watch`);
  },
  async setOutsideSourceSwitch(kind: string, enabled: boolean, reason?: string): Promise<{ ok: true; sources: OutsideSourceSwitch[] }> {
    return request(`/api/marketing/settings/outside-sources/${encodeURIComponent(kind)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled, ...(reason ? { reason } : {}) }),
    });
  },
  /** A one-tap verdict on a Today row; `null` is the undo. */
  async setMarketingDecisionVerdict(
    decisionId: string,
    verdict: DecisionVerdict | null,
  ): Promise<{ ok: true; verdict: DecisionVerdict | null; previous: DecisionVerdict | null }> {
    return request(`/api/marketing/decisions/${encodeURIComponent(decisionId)}/verdict`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ verdict }),
    });
  },
  /** A Today row button that changes something (2.3b-i). */
  async actOnMarketingDecision(
    decisionId: string,
    body:
      | { action: "call"; talked: boolean; note?: string | null }
      | { action: "tell" }
      | { action: "text" }
      | { action: "snooze"; revisit_month: string | null; note?: string },
  ): Promise<{ ok: true; touch_written?: boolean; task_id?: string; title?: string; description?: string; target_slug?: string; revisit_month?: string | null }> {
    return request(`/api/marketing/decisions/${encodeURIComponent(decisionId)}/act`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  /** Take back the last button press on a Today row. */
  /** "Waiting on you" (row 2.4): close an item, or undo that. */
  async setInboxItemHandled(
    itemId: string,
    undo: boolean,
    opts: { notInterested?: boolean } = {},
  ): Promise<{ ok: true; state: "open" | "handled"; not_interested?: true }> {
    return request(`/api/marketing/inbox/${encodeURIComponent(itemId)}/handled`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ undo, ...(opts.notInterested ? { not_interested: true } : {}) }),
    });
  },
  /** People held for an advisor to confirm (decision D7): a name a client typed on the check-in, a walk-in typed at an event door. */
  async listHeldPeople(opts: { eventId?: string; state?: "held" | "dismissed" } = {}): Promise<HeldPeopleResponse> {
    const q = new URLSearchParams();
    if (opts.eventId) q.set("event_id", opts.eventId);
    if (opts.state) q.set("state", opts.state);
    return request(`/api/marketing/held-people${q.toString() ? `?${q.toString()}` : ""}`);
  },
  async confirmHeldPerson(id: string, matchContactId: string | null): Promise<{ ok: true; resolution: "created_new" | "matched_existing"; household_id: string | null; ref: string | null }> {
    return request(`/api/marketing/held-people/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(matchContactId ? { match_contact_id: matchContactId } : {}),
    });
  },
  async dismissHeldPerson(id: string, note: string): Promise<{ ok: true }> {
    return request(`/api/marketing/held-people/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    });
  },
  async reopenHeldPerson(id: string): Promise<{ ok: true }> {
    return request(`/api/marketing/held-people/${encodeURIComponent(id)}/reopen`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  /** Row 2.5: pause a family's marketing until someone lifts it. */
  async markMarketingDoNotMarket(
    decisionId: string,
    why: { reason_code: string; reason?: string },
  ): Promise<{ ok: true; pause_id: string }> {
    return request(`/api/marketing/decisions/${encodeURIComponent(decisionId)}/do-not-market`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(why),
    });
  },
  /** Row 2.5: lift a family's pause (also the Undo of "Do not market"). */
  async liftMarketingPause(pauseId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/pauses/${encodeURIComponent(pauseId)}/lift`, { method: "POST" });
  },

  /** Row 2.6, "Thank personally": record that a person mailed the card,
   *  made the call, or skipped with a reason. Nothing is sent by the OS. */
  async recordThankYou(
    subject: ThankYouSubject,
    id: string,
    side: ThankYouSide,
    body: { act: "mailed" | "called" | "texted" } | { act: "skipped"; reason: string },
  ): Promise<{ ok: true; act: string; touch_written: boolean }> {
    return request(`/api/marketing/thank-yous/${subject}/${encodeURIComponent(id)}/${side}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  /** Undo a recorded thank-you: it is owed again and its touch is taken back
   *  (a draft is deleted from Outlook; a sent email refuses). */
  async undoThankYou(subject: ThankYouSubject, id: string, side: ThankYouSide): Promise<{ ok: true }> {
    return request(`/api/marketing/thank-yous/${subject}/${encodeURIComponent(id)}/${side}/undo`, { method: "POST" });
  },
  /** Row 4.3: the thank-you email composer, filled in (the named person first). */
  async getThankYouEmail(subject: ThankYouSubject, id: string, side: ThankYouSide): Promise<ThankYouEmailComposer> {
    return request<ThankYouEmailComposer>(`/api/marketing/thank-yous/${subject}/${encodeURIComponent(id)}/${side}/email`);
  },
  /** Row 4.3: save the thank-you as a draft in the presser's own Outlook, or send it from there. */
  async sendThankYouEmail(
    subject: ThankYouSubject,
    id: string,
    side: ThankYouSide,
    body: { mode: TodayEmailMode; contact_ids: string[]; subject: string; body: string },
  ): Promise<{ ok: true; mode: TodayEmailMode; web_link: string | null; touch_written: boolean }> {
    return request(`/api/marketing/thank-yous/${subject}/${encodeURIComponent(id)}/${side}/email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  /** Row 4.3: "Text" is a task with the words for the presser (the OS never sends a text). */
  async createThankYouTextTask(
    subject: ThankYouSubject,
    id: string,
    side: ThankYouSide,
  ): Promise<{ ok: true; task_id: string; title: string; description: string }> {
    return request(`/api/marketing/thank-yous/${subject}/${encodeURIComponent(id)}/${side}/text-task`, { method: "POST" });
  },
  // ─── The approval queue (row 7.9a, screen 7, modals 32 and 35) ──────
  async getApprovals(): Promise<ApprovalQueueResponse> {
    return request(`/api/marketing/approvals`);
  },
  async getApproval(kind: QueueKind, id: string): Promise<ApprovalDetailResponse> {
    return request(`/api/marketing/approvals/${kind}/${encodeURIComponent(id)}`);
  },
  async approvalAction(
    kind: QueueKind,
    id: string,
    action: "approve" | "hold" | "send-back" | "sign",
    body: { note?: string; expected_version?: number | null; verdicts?: Record<string, "keep" | "reject"> } = {},
  ): Promise<{ ok: true; signoffs?: SignoffState[]; sent_back?: boolean; message?: string }> {
    return request(`/api/marketing/approvals/${kind}/${encodeURIComponent(id)}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Split 2 audit M7: Grant takes a wording's approval back (the toast's Undo). */
  async takeBackTemplateApproval(slug: string): Promise<{ ok: true }> {
    return request(`/api/marketing/approvals/template/${encodeURIComponent(slug)}/take-back`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Split 2 audit M7: a prepared goal's missing brief, written from the plan as prepared. */
  async rebuildGoalBrief(goalId: string): Promise<{ ok: true }> {
    return request(`/api/marketing/goals/${encodeURIComponent(goalId)}/rebuild-brief`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 7.9b: Grant's "Send what's due now", one run of the series runner. */
  async sendDueSeriesNow(): Promise<{ ok: true; result: SeriesSendRunResult }> {
    return request(`/api/marketing/approvals/send-now`, { method: "POST" });
  },
  /** Row 4.3: Grant approves a wording template for sending from the OS. */
  async approveWording(slug: string, reason?: string): Promise<{ ok: true; slug: string; approved: true }> {
    return request(`/api/marketing/wording/${encodeURIComponent(slug)}/approve`, reason ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) } : { method: "POST" });
  },
  /** Audit S3-H7: take a wording's approval back; the words stay. */
  async withdrawWording(slug: string): Promise<{ ok: true; slug: string; approved: false }> {
    return request(`/api/marketing/wording/${encodeURIComponent(slug)}/withdraw`, { method: "POST" });
  },

  // ─── The referral record (Marketing Hub row 4.1, modal 27) ──────────
  /** What the modal opens with: the household, its people, every company's
   *  default owner and thank-you rule line, and the clock's length. */
  async getReferralRecordDefaults(householdId: string | null): Promise<ReferralRecordDefaults> {
    const qs = householdId ? `?household_id=${encodeURIComponent(householdId)}` : "";
    return request<ReferralRecordDefaults>(`/api/marketing/referrals/defaults${qs}`);
  },
  /** The facts about a picked sender (second-generation credit included). */
  /** Row 10.9: the company's published blog posts a family can share as a guide. */
  async getShareGuides(company: string): Promise<{ guides: ShareGuide[] }> {
    return request(`/api/marketing/referrals/share/guides?company=${encodeURIComponent(company)}`);
  },
  /** Row 10.9: "Share an introduction": the family's booking link, its last day and the words. Nothing is sent. */
  async makeIntroductionShare(body: { household_id: string; company: string; guide_id: string | null }): Promise<IntroductionShare> {
    return request(`/api/marketing/referrals/share`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getReferralSenderFacts(sender: ReferralSenderPick): Promise<ReferralSenderFacts> {
    const qs = "contact_id" in sender ? `contact_id=${encodeURIComponent(sender.contact_id)}` : `vendor_id=${encodeURIComponent(sender.vendor_id)}`;
    return request<ReferralSenderFacts>(`/api/marketing/referrals/sender?${qs}`);
  },
  /** "Record it and start the clock". */
  async recordReferral(body: {
    sender: ReferralSenderPick;
    to_household_id: string;
    to_contact_id?: string | null;
    company_slug: string;
    kind: "asked" | "unsolicited";
    note: string | null;
    owner_user_id?: string | null;
  }): Promise<{ ok: true; id: string; owner_user_id: string; clock_due_at: string; inbox_item_id: string; edge_id: string | null }> {
    return request(`/api/marketing/referrals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  // ─── The Referrals screen (row 4.2, drawing screen 5) ───────────────
  async getMarketingReferrals(params: { company?: string | null; owner?: string | null } = {}): Promise<ReferralsScreenResponse> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    if (params.owner) qs.set("owner", params.owner);
    const tail = qs.toString();
    return request<ReferralsScreenResponse>(`/api/marketing/referrals${tail ? `?${tail}` : ""}`);
  },
  /** "Call the <family>" (a phone conversation, status to contacted) or
   *  "Text instead" (a task for the owner with the words; the OS never texts). */
  async markReferralContacted(id: string, body: { way: "call" | "text"; note?: string | null }): Promise<{ ok: true; status: string; activity_id: string | null; task_id: string | null }> {
    return request(`/api/marketing/referrals/${encodeURIComponent(id)}/contacted`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** "Not this quarter" (no date: +90 days) or "Queue for <date>". */
  async setReferralNextAsk(id: string, date: string | null): Promise<{ ok: true; next_ask_after: string }> {
    return request(`/api/marketing/referrals/${encodeURIComponent(id)}/next-ask`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(date ? { date } : {}) });
  },
  /** "Skip this file": no introduction ask, with the reason. */
  async skipReferralAsk(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/marketing/referrals/${encodeURIComponent(id)}/skip-ask`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) });
  },
  /** The undo of a queued date or a skip. */
  async clearReferralAsk(id: string): Promise<{ ok: true }> {
    return request(`/api/marketing/referrals/${encodeURIComponent(id)}/clear-ask`, { method: "POST" });
  },
  /** "Call him": a touch on a center of influence. */
  async logCenterOfInfluenceCall(vendorId: string, note: string | null): Promise<{ ok: true; activity_id: string }> {
    return request(`/api/marketing/referrals/coi/${encodeURIComponent(vendorId)}/called`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note }) });
  },
  /** One referral row, for the modal's edit mode. */
  async getReferralRecord(id: string): Promise<ReferralRecordView> {
    return request<ReferralRecordView>(`/api/marketing/referrals/${encodeURIComponent(id)}`);
  },
  /** Name the sender on a row a form or a backfill left unnamed. */
  async nameReferralSender(id: string, sender: ReferralSenderPick): Promise<{ ok: true; edge_id: string | null }> {
    return request(`/api/marketing/referrals/${encodeURIComponent(id)}/sender`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sender }),
    });
  },

  /** Give a "Waiting on you" item to a teammate; the deadline stays. */
  async handInboxItemTo(itemId: string, userId: string): Promise<{ ok: true; owner_user_id: string }> {
    return request(`/api/marketing/inbox/${encodeURIComponent(itemId)}/hand-to`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    });
  },
  /** Row 5.5: the unified inbox, scoped to the viewer's desk by default. */
  async getMarketingInbox(params: { view?: "mine" | "company" | "firm"; company?: string | null } = {}): Promise<MarketingInboxResponse> {
    const q = new URLSearchParams();
    if (params.view) q.set("view", params.view);
    if (params.company) q.set("company", params.company);
    const qs = q.toString();
    return request<MarketingInboxResponse>(`/api/marketing/inbox${qs ? `?${qs}` : ""}`);
  },
  /** Row 5.5: "This is …": link a comment's handle to a person; every
   *  unattributed comment or message from that handle is matched now. */
  async linkInboxHandle(engagementId: string, contactId: string): Promise<{ ok: true; handle: { id: string; platform: string; handle: string }; matched: number; hand_raises: number }> {
    return request(`/api/marketing/inbox/unattributed/${encodeURIComponent(engagementId)}/link`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contact_id: contactId }),
    });
  },
  /** Row 8.3: answer a comment on the post or privately, from the account
   *  it arrived on. A refusal's message says why and, when the vendor cannot
   *  reply there, the platform link is on the error body. */
  async replyToInboxItem(itemId: string, way: ReplyWay, text: string): Promise<{ ok: true; reply_id: string; handled: boolean; message: string }> {
    return request(`/api/marketing/inbox/${encodeURIComponent(itemId)}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ way, text }),
    });
  },
  /** Row 8.3: "Not them": the handle was linked to the wrong person. */
  async unlinkInboxHandle(itemId: string): Promise<{ ok: true; handles: number; engagements: number; touches: number; items: number; platform: string; handle: string | null }> {
    return request(`/api/marketing/inbox/${encodeURIComponent(itemId)}/unlink`, { method: "POST" });
  },
  /** Row 5.6: the advisor share packages for the viewer's companies plus the firm's. */
  async getSharePackages(): Promise<SharePackagesResponse> {
    return request<SharePackagesResponse>("/api/marketing/studio/share");
  },
  /** Row 5.6: "Copy the text": the advisor took the words. */
  async recordShareCopied(assetId: string): Promise<{ ok: true; package: SharePackage }> {
    return request(`/api/marketing/studio/share/${encodeURIComponent(assetId)}/copied`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 5.6: "Share to your LinkedIn": a post by the advisor to their own LinkedIn through the vendor. */
  async shareToLinkedin(assetId: string): Promise<{ ok: true; message: string; post: { id: string; platform_post_url: string | null; piece_state: string }; package: SharePackage }> {
    return request(`/api/marketing/studio/share/${encodeURIComponent(assetId)}/linkedin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
  },
  /** Row 7.10a: Results, each goal against its brief, for the viewer's companies or the firm. */
  async getResults(scope: "mine" | "all" = "mine"): Promise<ResultsResponse> {
    return request<ResultsResponse>(`/api/marketing/results?scope=${scope}`);
  },
  /** Row 7.11b: the team leaderboard (screen 9a), for the viewer's companies or the firm. */
  async getLeaderboard(scope: "mine" | "all" = "mine", period: LeaderboardPeriod = "quarter"): Promise<LeaderboardResponse> {
    return request<LeaderboardResponse>(`/api/marketing/leaderboard?scope=${scope}&period=${period}`);
  },
  /** Audit S2-H12: is this company's footer held? `held` is the reason, or null when it can print. */
  async getFooterStatus(company: string): Promise<{ company: string; held: string | null }> {
    return request<{ company: string; held: string | null }>(`/api/marketing/footer-status/${encodeURIComponent(company)}`);
  },
  /** Row 7.11c: Stew's note in Grant's voice about one teammate's result (Grant only). */
  // Row 7.12a: the Settings home and the marketing rules (Grant changes them).
  async getSettingsHome(): Promise<SettingsHomeResponse> {
    return request<SettingsHomeResponse>(`/api/marketing/settings/home`);
  },
  async getRuleHistory(key: MarketingRuleKey): Promise<{ key: MarketingRuleKey; history: RuleHistoryEntry[] }> {
    return request<{ key: MarketingRuleKey; history: RuleHistoryEntry[] }>(`/api/marketing/settings/rules/${encodeURIComponent(key)}/history`);
  },
  async changeMarketingRule(key: MarketingRuleKey, body: { value: number; reason: string; expected_row_id: string | null }): Promise<RuleChangeResponse> {
    return request<RuleChangeResponse>(`/api/marketing/settings/rules/${encodeURIComponent(key)}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getRecognitionDraft(body: { user_id: string; company_slug: string; period: LeaderboardPeriod }): Promise<RecognitionDraft> {
    return request<RecognitionDraft>(`/api/marketing/recognition/draft`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Row 7.11c: send the note (email from Grant's Outlook, the Everyone chat, or said in person). */
  async sendRecognition(body: { user_id: string; company_slug: string; period: LeaderboardPeriod; note: string; edited: boolean; channels: RecognitionChannel[] }): Promise<RecognitionSendResponse> {
    return request<RecognitionSendResponse>(`/api/marketing/recognition`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Row 7.10b: "Send the remaining", the viewer's own drafts from their own Outlook. */
  async sendRemainingDrafts(ids: readonly string[]): Promise<{ ok: true; sent: number; gone: number; failed: number; message: string }> {
    return request(`/api/marketing/results/drafts/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids }) });
  },
  /** Row 7.10b: "Pause it" on a goal behind its brief (the Campaign screen's pause). */
  async pauseResultsGoal(goalId: string): Promise<{ ok: true; held: number; message: string }> {
    return request(`/api/marketing/results/pause`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ goal_id: goalId }) });
  },
  /** Row 7.10b: "Check again <date>" or "Keep" on a goal's row. */
  async resultsFollowup(body: { goal_id: string; row_kind: "behind_pace" | "window_open"; decision: "check_again" | "keep"; check_on: string | null }): Promise<{ ok: true }> {
    return request(`/api/marketing/results/followups`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  /** Row 5.7: the Results Content tab for the viewer's scope. */
  /** Row 9b.7: the influence ledger, per outcome ("By outcome"). */
  async getResultsOutcomes(scope: "mine" | "all" = "mine"): Promise<{ scope: { label: string; widened: boolean }; as_of: string; cap: number; rows: OutcomeInfluenceRow[]; total: number }> {
    return request(`/api/marketing/results/outcomes?scope=${scope}`);
  },
  /** Row 9b.7: modal 45, pause or lower one search campaign or boost (Grant only); refused when the figures moved since it opened. */
  async adjustPaid(body: { kind: PaidKind; id: string; action: PaidAdjustAction; to?: number; since_iso: string; seen: { spend: number | null; spend_as_of: string | null } }): Promise<{ ok: boolean; state: string; message: string }> {
    return request(`/api/marketing/results/paid/adjust`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  },
  async getContentResults(days = 30): Promise<ContentTabResponse> {
    return request<ContentTabResponse>(`/api/marketing/results/content?days=${encodeURIComponent(String(days))}`);
  },
  /** "Write the email" on a Today row: the composer, already filled in. */
  async getMarketingDecisionEmail(decisionId: string): Promise<TodayEmailComposer> {
    return request<TodayEmailComposer>(`/api/marketing/decisions/${encodeURIComponent(decisionId)}/email`);
  },
  /** Save it as a draft in the presser's own Outlook, or send it from there. */
  async sendMarketingDecisionEmail(
    decisionId: string,
    body: { mode: TodayEmailMode; contact_ids: string[]; subject: string; body: string },
  ): Promise<{ ok: true; mode: TodayEmailMode; web_link: string | null; touch_written: boolean }> {
    return request(`/api/marketing/decisions/${encodeURIComponent(decisionId)}/email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async undoMarketingDecisionAction(decisionId: string): Promise<{ ok: true; undone: string | null }> {
    return request(`/api/marketing/decisions/${encodeURIComponent(decisionId)}/act/undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  },
  async getAiSummary(
    kind: "household" | "contact",
    id: string,
  ): Promise<AiRelationshipSummaryResponse> {
    return request<AiRelationshipSummaryResponse>(
      `/api/ai-summary/${kind}/${encodeURIComponent(id)}`,
    );
  },
  async refreshAiSummary(
    kind: "household" | "contact",
    id: string,
  ): Promise<AiRelationshipSummaryResponse> {
    return request<AiRelationshipSummaryResponse>(
      `/api/ai-summary/${kind}/${encodeURIComponent(id)}/refresh`,
      { method: "POST" },
    );
  },
  // Upcoming automated communications (2026-07-03) — record panels,
  // dashboard tile, drill-in list, rendered draft preview.
  async getUpcomingCommunicationsForRecord(
    kind: "household" | "contact",
    id: string,
    days?: number,
  ): Promise<UpcomingCommunicationsResult> {
    const qs = days ? `?days=${days}` : "";
    return request<UpcomingCommunicationsResult>(
      `/api/records/${kind}/${encodeURIComponent(id)}/upcoming-communications${qs}`,
    );
  },
  async getUpcomingCommunicationsSummary(days?: number): Promise<UpcomingCommunicationsSummary> {
    const qs = days ? `?days=${days}` : "";
    return request<UpcomingCommunicationsSummary>(`/api/upcoming-communications/summary${qs}`);
  },
  async listUpcomingCommunications(
    scope: "book" | "mine" | "company",
    days?: number,
  ): Promise<UpcomingCommunicationsResult> {
    const params = new URLSearchParams({ scope });
    if (days) params.set("days", String(days));
    return request<UpcomingCommunicationsResult>(
      `/api/upcoming-communications/list?${params.toString()}`,
    );
  },
  /** `days` is optional and normally omitted: the server defaults to the
   *  horizon its 15-minute snapshot is warmed for, so the common call
   *  reads that instead of recomputing the firm-wide upcoming set. Pass a
   *  number only when a caller genuinely needs a different window, and
   *  expect it to compute live. */
  async listAutomationsActingOnMyClients(
    scope: "book" | "company",
    days?: number,
  ): Promise<BookRulesResult> {
    const params = new URLSearchParams({ scope });
    if (days) params.set("days", String(days));
    return request<BookRulesResult>(`/api/automations/acting-on-my-clients?${params.toString()}`);
  },
  async getUpcomingCommunicationPreview(args: {
    contactId: string;
    automationId?: string | null;
    rhythmInstanceId?: string | null;
    touchpointId?: string | null;
  }): Promise<UpcomingCommunicationPreview> {
    const params = new URLSearchParams({ contact_id: args.contactId });
    if (args.automationId) params.set("automation_id", args.automationId);
    if (args.rhythmInstanceId) {
      params.set("rhythm_instance_id", args.rhythmInstanceId);
    }
    if (args.touchpointId) params.set("touchpoint_id", args.touchpointId);
    return request<UpcomingCommunicationPreview>(
      `/api/upcoming-communications/preview?${params.toString()}`,
    );
  },
  async listCleanupLanes(
    company?: string | null,
    // Owner=Mine narrows the lane counts to match the mine-filtered queue.
    mine?: boolean,
  ): Promise<{ lanes: CleanupLane[] }> {
    const qs = new URLSearchParams();
    if (company) qs.set("company", company);
    if (mine) qs.set("mine", "true");
    const q = qs.toString();
    return request<{ lanes: CleanupLane[] }>(`/api/cleanup/lanes${q ? `?${q}` : ""}`);
  },

  // P0-2 — plain-English per-group preview behind the "Resolve all"
  // confirmation (same filtered set the bulk loop pages through).
  async getCleanupAutoResolvePreview(params: {
    company?: string | null;
    mine?: boolean;
  }): Promise<{ groups: CleanupAutoResolveGroup[] }> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    if (params.mine) qs.set("mine", "true");
    const q = qs.toString();
    return request<{ groups: CleanupAutoResolveGroup[] }>(
      `/api/cleanup/auto-resolve-preview${q ? `?${q}` : ""}`,
    );
  },

  async listCleanupIssues(
    params: {
      family?: string;
      tier?: string;
      state?: string;
      assignedToMe?: boolean;
      followUpMine?: boolean;
      unowned?: boolean;
      company?: string;
      shape?: AnswerShape;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<CleanupIssueListResponse> {
    const qs = new URLSearchParams();
    if (params.family) qs.set("family", params.family);
    if (params.tier) qs.set("tier", params.tier);
    if (params.state) qs.set("state", params.state);
    if (params.assignedToMe) qs.set("assigned_to_me", "true");
    if (params.followUpMine) qs.set("followup_mine", "true");
    if (params.unowned) qs.set("unowned", "true");
    if (params.company) qs.set("company", params.company);
    if (params.shape) qs.set("shape", params.shape);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return request<CleanupIssueListResponse>(`/api/cleanup/issues${q ? `?${q}` : ""}`);
  },

  async getCleanupIssue(id: string): Promise<CleanupIssueDetailResponse> {
    return request<CleanupIssueDetailResponse>(`/api/cleanup/issues/${encodeURIComponent(id)}`);
  },

  // Save the answers a Cleanup / Data Needed card collected for a
  // detector-declared `type-it-in` fields ask (shared/cleanup/fix.ts).
  // The writer names the record's OWN endpoint (answer-writers.ts) — the
  // one the record page saves through — so scope, validation and audit
  // are that route's. `answers` holds only the fields the person touched,
  // in control wire format; encodeAnswer turns each into what the
  // endpoint takes. Nothing here is a second write path.
  async answerCleanupFields(
    writer: CleanupFieldWriter,
    writerId: string,
    fields: readonly CleanupAskedField[],
    answers: Readonly<Record<string, string>>,
  ): Promise<{ ok: boolean; changed: boolean }> {
    const w = ANSWER_WRITERS[writer];
    const body: Record<string, string | number | null> = {};
    for (const f of fields) {
      if (f.key in answers) body[f.key] = encodeAnswer(f, answers[f.key] ?? "");
    }
    return request<{ ok: boolean; changed: boolean }>(w.path(writerId), {
      method: w.method,
      body: JSON.stringify(body),
    });
  },

  // C1: the open cleanup issues affecting ONE record. Powers the
  // in-record CleanupActionsButton drawer (the co-equal entry point
  // to the /cleanup workspace). `kind` is a CleanupEntityKind
  // (contact | household | transaction | opportunity |
  // service_record | rhythm_instance | vendor | policy | activity).
  // Returns active-state issues only; out-of-scope workflow issues
  // are included (shown read-only in the drawer — UX rule #5).
  async getCleanupIssuesForRecord(kind: string, id: string): Promise<CleanupForRecordResponse> {
    return request<CleanupForRecordResponse>(
      `/api/cleanup/for-record/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
    );
  },

  // C3: flag a problem on any record (incl. 0-issue records). Creates
  // a manual CleanupIssue (state 'new') that flows through the same
  // engine. Returns the new issue id so the drawer can refresh and
  // select it.
  async createCleanupManualIssue(
    body: CleanupManualIssueBody,
  ): Promise<{ ok: boolean; issue_id: string }> {
    return request<{ ok: boolean; issue_id: string }>("/api/cleanup/manual-issue", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // B4: can the current user act on this issue? Drives the decide
  // rail's pre-emptive view-only banner + forward paths (no dead end).
  async getCleanupIssueAuthz(issueId: string): Promise<{ authz: CleanupAuthz }> {
    return request<{ authz: CleanupAuthz }>(
      `/api/cleanup/issues/${encodeURIComponent(issueId)}/authz`,
    );
  },

  // B4: add to an issue's note thread. Universal (collaboration is
  // never scope-gated) — a forward path on restricted issues + the
  // general note thread.
  async addCleanupIssueNote(
    issueId: string,
    body: string,
  ): Promise<{ ok: boolean; note_id: string }> {
    return request<{ ok: boolean; note_id: string }>(
      `/api/cleanup/issues/${encodeURIComponent(issueId)}/notes`,
      { method: "POST", body: JSON.stringify({ body }) },
    );
  },

  // B4: hand an issue to its domain owner. Universal — assigning an
  // out-of-scope issue to the right owner is THE no-dead-end path.
  async assignCleanupIssue(
    issueId: string,
    assignedOwnerUserId: string | null,
  ): Promise<{ ok: boolean; changed: boolean }> {
    return request<{ ok: boolean; changed: boolean }>(
      `/api/cleanup/issues/${encodeURIComponent(issueId)}/assign`,
      {
        method: "POST",
        body: JSON.stringify({
          assigned_owner_user_id: assignedOwnerUserId,
        }),
      },
    );
  },

  // Park an issue in the "Waiting on follow-up" lane (Donna's request).
  // Universal, like assign — it touches no records, just moves the
  // issue out of its active lane while the steward waits on an answer.
  async parkCleanupIssueFollowup(
    issueId: string,
    body: { reason?: string; check_back_at?: string } = {},
  ): Promise<{ ok: boolean; changed: boolean }> {
    return request<{ ok: boolean; changed: boolean }>(
      `/api/cleanup/issues/${encodeURIComponent(issueId)}/followup`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  // Bring a parked issue back to the open queue.
  async resumeCleanupIssueFollowup(issueId: string): Promise<{ ok: boolean; changed: boolean }> {
    return request<{ ok: boolean; changed: boolean }>(
      `/api/cleanup/issues/${encodeURIComponent(issueId)}/resume`,
      { method: "POST" },
    );
  },

  async decideCleanupIssue(body: {
    issue_id: string;
    verb: string;
    params?: Record<string, unknown>;
    reason_code?: string;
    reason_text?: string;
    overrode_recommendation?: boolean;
  }): Promise<{
    ok: boolean;
    verb: string;
    undo:
      | { available: true; kind: string; refs: Record<string, unknown>; expires_at: string }
      | { available: false };
  }> {
    return request("/api/cleanup/decide", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async undoCleanupDecision(body: {
    issue_id: string;
    kind: string;
    refs?: Record<string, unknown>;
  }): Promise<{ ok: boolean; undone: string }> {
    return request("/api/cleanup/undo", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Close an issue whose records were already merged via the
  // cluster/field-level merge wizard (the merge ran through the
  // contact-merge substrate; this just transitions the issue so it
  // leaves the queue immediately instead of lingering until the next
  // reconcile sweep with a now-merged-away record that 404s).
  // Data Needed "won, nothing created" card (2026-10-03): reopen the stray
  // win so the ordinary Mark Won / Mark Lost can run. The server refuses a
  // win that has a Processing record behind it.
  async reopenStrayWin(issueId: string, opportunityId: string): Promise<{ ok: true; reopened: boolean }> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/reopen-stray-win`, {
      method: "POST",
      body: JSON.stringify({ opportunity_id: opportunityId }),
    });
  },
  // The undo for "It didn't really close" on the same card.
  async reopenJustLost(issueId: string, opportunityId: string): Promise<{ ok: true }> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/reopen-just-lost`, {
      method: "POST",
      body: JSON.stringify({ opportunity_id: opportunityId }),
    });
  },
  async markCleanupIssueResolved(issueId: string): Promise<{ ok: boolean; changed: boolean }> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/mark-resolved`, {
      method: "POST",
    });
  },

  // Audit 6.1 — the permanent inverse of resolve/dismiss (no 60s
  // window; the dismiss copy promises "you can reopen it later").
  // Re-queues the issue; touches no records.
  async reopenCleanupIssue(issueId: string): Promise<{ ok: boolean; changed: boolean }> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/reopen`, { method: "POST" });
  },

  // Admin/compliance only (server enforces requireAdminAccess). Runs
  // every detector against the live data and populates issues. Used
  // by the first-run "Run a scan now" affordance; also fires every
  // ~6h on the cron + on import-completion + on contact/household
  // edits.
  async runCleanupReconcile(): Promise<{
    ok: boolean;
    totals: {
      candidates: number;
      created: number;
      updated: number;
      autoResolved: number;
      reopened: number;
      errored: number;
    };
  }> {
    return request("/api/cleanup/reconcile", { method: "POST" });
  },

  // Financial source-of-truth uploads. Preview runs the importer in
  // dry-run mode (no DB writes); apply actually persists.
  async previewFinancialImport(body: FinancialImportRequestBody): Promise<FinancialImportResult> {
    return request("/api/admin/data-imports/financial/preview", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async applyFinancialImport(body: FinancialImportRequestBody): Promise<FinancialImportResult> {
    return request("/api/admin/data-imports/financial/apply", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Monthly advisory fee review (Jake's Financial compliance duty).
  // Preview computes without writing; apply persists the run as a
  // permanent compliance record.
  async previewFeeReview(body: FeeReviewRequestBody): Promise<FeeReviewComputationResponse> {
    return request("/api/admin/financial/fee-review/preview", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async applyFeeReview(
    body: FeeReviewRequestBody,
  ): Promise<FeeReviewComputationResponse & { review_id: string }> {
    return request("/api/admin/financial/fee-review/apply", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async listFeeReviewRuns(includeSuperseded = false): Promise<{ runs: FeeReviewRunSummaryRec[] }> {
    return request(
      `/api/admin/financial/fee-review/runs${includeSuperseded ? "?include_superseded=1" : ""}`,
    );
  },

  async getFeeReviewRun(id: string): Promise<FeeReviewRunDetail> {
    return request(`/api/admin/financial/fee-review/runs/${encodeURIComponent(id)}`);
  },

  async saveFeeReviewNote(body: {
    household_id: string | null;
    external_label: string | null;
    custodian: "betterment" | "altruist_schwab";
    note: string;
  }): Promise<{ ok: true }> {
    return request("/api/admin/financial/fee-review/notes", {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  /** Settle an unmatched billed name for either custodian: records the
   *  name → household link the resolver reads first (audited; for
   *  Betterment also writes the contact alias and clears any other
   *  contact carrying it). */
  async linkCustodianName(body: {
    custodian: "betterment" | "altruist_schwab";
    alias: string;
    household_id: string;
  }): Promise<{
    ok: true;
    household_label: string;
    contact_name: string | null;
    previous_household_id: string | null;
    cleared_from: { contact_id: string; contact_name: string }[];
  }> {
    return request("/api/admin/financial/fee-review/link-household", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Financial retention backfill — drop the AC deals CSV, populate
  // client_since_this_company + relationship_ended_at on HCR rows.
  async previewFinancialRetentionBackfill(
    body: FinancialRetentionBackfillRequestBody,
  ): Promise<FinancialRetentionBackfillResult> {
    return request("/api/admin/data-imports/financial/retention-backfill/preview", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async applyFinancialRetentionBackfill(
    body: FinancialRetentionBackfillRequestBody,
  ): Promise<FinancialRetentionBackfillResult> {
    return request("/api/admin/data-imports/financial/retention-backfill/apply", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async previewBettermentMapping(
    body: BettermentMappingRequestBody,
  ): Promise<BettermentMappingResult> {
    return request("/api/admin/data-imports/financial/betterment-mapping/preview", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async applyBettermentMapping(
    body: BettermentMappingRequestBody,
  ): Promise<BettermentMappingResult> {
    return request("/api/admin/data-imports/financial/betterment-mapping/apply", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Financial advisor attribution backfill — re-process the AC contacts
  // CSV to populate household_company_relationships.current_advisor_user_id
  // for households tagged "FINANCIAL: Jake Norton Clients - Family" or
  // "FINANCIAL: Brian Ream Clients - Family".
  async previewFinancialAdvisorAttribution(
    body: FinancialAdvisorAttributionRequestBody,
  ): Promise<FinancialAdvisorAttributionResult> {
    return request("/api/admin/data-imports/financial/advisor-attribution/preview", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async applyFinancialAdvisorAttribution(
    body: FinancialAdvisorAttributionRequestBody,
  ): Promise<FinancialAdvisorAttributionResult> {
    return request("/api/admin/data-imports/financial/advisor-attribution/apply", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async signIn(
    email: string,
    password: string,
  ): Promise<{ token?: string; twoFactorRedirect?: boolean }> {
    return request<{ token?: string; twoFactorRedirect?: boolean }>("/api/auth/sign-in/email", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  // ── Two-factor auth (MFA) ──────────────────────────────────────────
  async getMfaStatus(): Promise<{ enabled: boolean }> {
    return request<{ enabled: boolean }>("/api/me/mfa-status");
  },
  async enableTwoFactor(password: string): Promise<{ totpURI: string; backupCodes: string[] }> {
    return request<{ totpURI: string; backupCodes: string[] }>("/api/auth/two-factor/enable", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },
  async verifyTotp(code: string): Promise<{ token?: string }> {
    return request<{ token?: string }>("/api/auth/two-factor/verify-totp", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
  async verifyBackupCode(code: string): Promise<{ token?: string }> {
    return request<{ token?: string }>("/api/auth/two-factor/verify-backup-code", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
  async disableTwoFactor(password: string): Promise<{ status: boolean }> {
    return request<{ status: boolean }>("/api/auth/two-factor/disable", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },

  // ── Passkeys (Face ID / Touch ID sign-in) ──────────────────────────
  // Thin wrappers over the @better-auth/passkey endpoints. The WebAuthn
  // browser ceremony between the two halves of each pair lives in
  // web/src/lib/passkeys.ts — these only move JSON. The options payloads
  // are the WebAuthn JSON types; kept as unknown-shaped records here and
  // typed at the ceremony boundary, where @simplewebauthn owns them.
  async passkeyRegisterOptions(name: string): Promise<Record<string, unknown>> {
    return request<Record<string, unknown>>(
      `/api/auth/passkey/generate-register-options?name=${encodeURIComponent(name)}`,
    );
  },
  async passkeyVerifyRegistration(body: {
    response: unknown;
    name: string;
  }): Promise<{ id?: string }> {
    return request<{ id?: string }>("/api/auth/passkey/verify-registration", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async passkeyAuthOptions(): Promise<Record<string, unknown>> {
    return request<Record<string, unknown>>("/api/auth/passkey/generate-authenticate-options");
  },
  async passkeyVerifyAuthentication(body: { response: unknown }): Promise<{ session?: unknown }> {
    return request<{ session?: unknown }>("/api/auth/passkey/verify-authentication", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async listPasskeys(): Promise<
    {
      id: string;
      name?: string | null;
      deviceType: string;
      backedUp: boolean;
      createdAt: string;
    }[]
  > {
    return request<
      {
        id: string;
        name?: string | null;
        deviceType: string;
        backedUp: boolean;
        createdAt: string;
      }[]
    >("/api/auth/passkey/list-user-passkeys");
  },
  async deletePasskey(id: string): Promise<{ status: boolean }> {
    return request<{ status: boolean }>("/api/auth/passkey/delete-passkey", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  // Invite acceptance flow. The CLI-issued URL lands at
  // /accept-invite?token=…; the page calls `lookupInvite` to decide
  // whether to show a password form and then `acceptInvite` to set
  // the credential. Self-signup is closed — there is deliberately no
  // api.signUp() surface.
  async lookupInvite(
    token: string,
  ): Promise<{ email: string; role_slug: string; expires_at: string }> {
    return request<{ email: string; role_slug: string; expires_at: string }>(
      `/api/invites/lookup?token=${encodeURIComponent(token)}`,
    );
  },

  async acceptInvite(
    token: string,
    password: string,
  ): Promise<{ success: boolean; email: string; purpose: string }> {
    return request<{
      success: boolean;
      email: string;
      purpose: string;
    }>("/api/invites/accept", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  },

  // Password reset (admin-initiated). The Team page issues a reset via
  // resetUserPassword(); the user redeems it on /reset-password?token=…
  // which calls lookupPasswordReset() then acceptPasswordReset().
  async resetUserPassword(userId: string): Promise<{
    user_id: string;
    email: string;
    expires_at: string;
    reset_url: string;
    email_sent: boolean;
    console_message: string | null;
  }> {
    return request(`/api/admin/team/users/${encodeURIComponent(userId)}/reset-password`, {
      method: "POST",
      body: "{}",
    });
  },

  // Admin-initiated MFA reset (restricted recovery). Clears the target
  // user's TOTP enrollment so the enforcement gate walks them back through
  // fresh enrollment on their next login. system_admin only; self-target
  // blocked server-side. `reason` is recorded on the audit row.
  async resetUserMfa(
    userId: string,
    reason?: string,
  ): Promise<{
    user_id: string;
    email: string;
    mfa_was_enabled: boolean;
    two_factor_rows_deleted: number;
  }> {
    return request(`/api/admin/team/users/${encodeURIComponent(userId)}/reset-mfa`, {
      method: "POST",
      body: JSON.stringify(reason ? { reason } : {}),
    });
  },

  // Self-service "Forgot password?" request. Enumeration-safe: the API
  // always returns { ok: true } whether or not the email maps to an
  // account, so this resolves on any well-formed request.
  async requestPasswordReset(email: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>("/api/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  async lookupPasswordReset(token: string): Promise<{ email: string; expires_at: string }> {
    return request<{ email: string; expires_at: string }>(
      `/api/password-reset/lookup?token=${encodeURIComponent(token)}`,
    );
  },

  async acceptPasswordReset(token: string, password: string): Promise<{ success: boolean }> {
    return request<{ success: boolean }>("/api/password-reset/accept", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  },

  async signOut(): Promise<{ success: boolean }> {
    // Better Auth sign-out requires an explicit Origin header, which the
    // browser sends automatically on same-origin / CORS-credentialed
    // requests. The empty {} body is required.
    return request<{ success: boolean }>("/api/auth/sign-out", {
      method: "POST",
      body: "{}",
    });
  },

  async getHousehold(id: string): Promise<HouseholdDetail> {
    return request<HouseholdDetail>(`/api/households/${encodeURIComponent(id)}`);
  },

  // Service Records PR 4 — Household 360.
  async getHouseholdPortfolio(id: string): Promise<HouseholdPortfolioResponse> {
    return request<HouseholdPortfolioResponse>(
      `/api/households/${encodeURIComponent(id)}/portfolio`,
    );
  },

  // PR3 (2026-09-06) — the household's "Forms" tab: every form this
  // household has submitted, across every template.
  async getHouseholdFormSubmissions(
    id: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<RecordFormSubmissionsResponse> {
    const params = new URLSearchParams();
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.offset) params.set("offset", String(opts.offset));
    const qs = params.toString();
    return request<RecordFormSubmissionsResponse>(
      `/api/households/${encodeURIComponent(id)}/form-submissions${qs ? `?${qs}` : ""}`,
    );
  },

  // The household's "Payment info" card (Brandon, 2026-10-09): who has a
  // bank account or card on file (masks only) and what a client submitted
  // that is still waiting to be filed. Values are never in this response;
  // Reveal is the audited contact endpoint.
  async getHouseholdPaymentInfo(id: string): Promise<HouseholdPaymentInfo> {
    return request<HouseholdPaymentInfo>(
      `/api/households/${encodeURIComponent(id)}/payment-info`,
    );
  },

  // S4 — requote worksheet (andrea-planning-ops.md §4 step 3).
  //
  // `opportunityId` opens it for a health/Medicare opportunity instead of
  // a case — the new-business path (Andrea, 2026-09-09). The household's
  // own cases still come back either way.
  async getRequoteWorksheet(
    householdId: string,
    opportunityId?: string | null,
    access?: HealthAccessReason | null,
  ): Promise<RequoteWorksheet> {
    const qs = opportunityId ? `?opportunity_id=${encodeURIComponent(opportunityId)}` : "";
    return request<RequoteWorksheet>(
      withHealthAccess(
        `/api/households/${encodeURIComponent(householdId)}/requote-worksheet${qs}`,
        access,
      ),
    );
  },

  // The income, subsidy and eligibility answers a marketplace quote is
  // run on, held on the opportunity until the case row exists.
  async saveOpportunityHealthQuote(
    opportunityId: string,
    body: {
      has_subsidy: boolean | null;
      aca_income: number | null;
      subsidy_amount: number | null;
      eligibility: Record<string, boolean | null>;
    },
  ): Promise<{ id: string; quote: HealthQuoteFacts }> {
    return request<{ id: string; quote: HealthQuoteFacts }>(
      `/api/opportunities/${encodeURIComponent(opportunityId)}/health-quote`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  },

  // S15 phase 2 — ACA formulary/network coverage check for one requote
  // worksheet case. Called only for ACA cases, after the base worksheet
  // has loaded (this does real lookup work; the worksheet itself must not).
  async getRequoteAcaCoverage(
    householdId: string,
    serviceRecordId: string,
    access?: HealthAccessReason | null,
  ): Promise<RequoteCoverageResult> {
    return request<RequoteCoverageResult>(
      withHealthAccess(
        `/api/households/${encodeURIComponent(householdId)}/requote-worksheet/${encodeURIComponent(serviceRecordId)}/aca-coverage`,
        access,
      ),
    );
  },

  // S15 phase 3 — the side-by-side plan comparison for one case + plan
  // year. `comparison: null` means nobody has built one yet, which the
  // page renders as an empty builder rather than an error.
  async getPlanComparison(
    householdId: string,
    target: ComparisonTargetRef,
    planYear: number,
  ): Promise<{ comparison: PlanComparison | null }> {
    return request<{ comparison: PlanComparison | null }>(
      `${comparisonBasePath(householdId, target)}/comparison?plan_year=${planYear}`,
    );
  },

  async savePlanComparison(
    householdId: string,
    target: ComparisonTargetRef,
    body: {
      plan_year: number;
      client_note: string | null;
      plans: ComparisonPlanInput[];
      expected_version_number: number | null;
    },
  ): Promise<{ comparison: PlanComparison }> {
    return request<{ comparison: PlanComparison }>(
      `${comparisonBasePath(householdId, target)}/comparison`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  },

  // S15 — prepare the recommendation: composes from the editable
  // template and leaves a DRAFT in the advisor's Outlook. Nothing is
  // sent; the advisor reads it and clicks Send himself.
  async recommendPlanComparison(
    householdId: string,
    target: ComparisonTargetRef,
    planYear: number,
  ): Promise<{
    recommended_at: string;
    draft_mailbox_upn: string;
    draft_web_link: string | null;
    recipients: string[];
  }> {
    return request(`${comparisonBasePath(householdId, target)}/comparison/recommend`, {
      method: "POST",
      body: JSON.stringify({ plan_year: planYear }),
    });
  },

  // At-Risk Scoring v2 PR 3 — Relationship Health card.
  async getHouseholdAtRisk(id: string): Promise<HouseholdAtRiskResponse> {
    return request<HouseholdAtRiskResponse>(`/api/households/${encodeURIComponent(id)}/at-risk`);
  },

  // Relationship Engagement (migration 0193) — the two-axis Engagement
  // card on the household page.
  async getHouseholdEngagement(id: string): Promise<HouseholdEngagementResponse> {
    return request<HouseholdEngagementResponse>(
      `/api/households/${encodeURIComponent(id)}/engagement`,
    );
  },

  // At-Risk Scoring v2 PR 4 — typed retention-touch logging.
  async logHouseholdTouch(
    id: string,
    body: {
      activity_type: string;
      note?: string;
      occurred_at?: string;
      /** The record the note box was opened on, so the touch counts for
       *  that record and its company (relevant touches, 2026-09-22). */
      record_type?: "service_record" | "opportunity" | "transaction" | "rhythm_instance";
      record_id?: string;
    },
  ): Promise<{ ok: true; activity_id: string; occurred_at: string }> {
    return request(`/api/households/${encodeURIComponent(id)}/touch`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // At-Risk Scoring v2 PR 8 — calibration (admin).
  async getAtRiskCalibration(): Promise<AtRiskCalibrationReport> {
    return request<AtRiskCalibrationReport>("/api/admin/at-risk/calibration");
  },

  async runAtRiskBatchNow(): Promise<{ ok: true; households_scored: number }> {
    return request("/api/admin/at-risk/run-now", { method: "POST" });
  },

  // Watchlist Rules (2026-09-22) — every number the at-risk engine scores
  // with. A 409 with code "version_conflict" carries what the rule says now
  // (current_value, current_version_number) in ApiError.data.
  async listWatchlistRules(): Promise<{
    groups: Array<{ key: string; label: string }>;
    rules: WatchlistRuleRec[];
  }> {
    return request("/api/admin/at-risk/rules");
  },

  async listWatchlistRuleHistory(id: string): Promise<{ history: WatchlistRuleHistoryRec[] }> {
    return request(`/api/admin/at-risk/rules/${encodeURIComponent(id)}/history`);
  },

  async setWatchlistRule(
    id: string,
    input: { value: number; reason: string | null; expected_version_number: number },
  ): Promise<{ ok: true; changed: boolean; previous: number; next: number }> {
    return request(`/api/admin/at-risk/rules/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },

  // At-Risk Scoring v2 PR 7 — life events + concern flags.
  async listHouseholdSignals(
    id: string,
    openOnly = false,
  ): Promise<{ signals: HouseholdSignal[] }> {
    return request(
      `/api/households/${encodeURIComponent(id)}/signals${openOnly ? "?open=true" : ""}`,
    );
  },

  async createHouseholdSignal(
    id: string,
    body: { signal_type: "life_event" | "concern"; subtype?: string; details?: string },
  ): Promise<{ ok: true; signal: HouseholdSignal }> {
    return request(`/api/households/${encodeURIComponent(id)}/signals`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async resolveHouseholdSignal(
    id: string,
    signalId: string,
    resolutionNote: string,
  ): Promise<{ ok: true }> {
    return request(
      `/api/households/${encodeURIComponent(id)}/signals/${encodeURIComponent(signalId)}/resolve`,
      {
        method: "POST",
        body: JSON.stringify({ resolution_note: resolutionNote }),
      },
    );
  },

  // Service Records PR 13 — Contact 360.
  async getContactPortfolio(id: string): Promise<ContactPortfolioResponse> {
    return request<ContactPortfolioResponse>(`/api/contacts/${encodeURIComponent(id)}/portfolio`);
  },

  // PR3 (2026-09-06) — the contact's "Forms" tab: every form this
  // contact has personally submitted, across every template.
  async getContactFormSubmissions(
    id: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<RecordFormSubmissionsResponse> {
    const params = new URLSearchParams();
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.offset) params.set("offset", String(opts.offset));
    const qs = params.toString();
    return request<RecordFormSubmissionsResponse>(
      `/api/contacts/${encodeURIComponent(id)}/form-submissions${qs ? `?${qs}` : ""}`,
    );
  },

  // PR3 (2026-09-06) — the single shared "Form Results" view.
  async getFormResults(
    submissionId: string,
    access?: HealthAccessReason | null,
  ): Promise<{ results: FormResultsDetail }> {
    return request<{ results: FormResultsDetail }>(
      withHealthAccess(`/api/form-submissions/${encodeURIComponent(submissionId)}/results`, access),
    );
  },

  // PR3 (2026-09-06) — "waiting for your review" status for a household's
  // own columns.
  async getHouseholdFormReviewStatus(id: string): Promise<{ columns: PendingReviewColumn[] }> {
    return request<{ columns: PendingReviewColumn[] }>(
      `/api/households/${encodeURIComponent(id)}/form-review-status`,
    );
  },

  // PR3 (2026-09-06) — same, for a contact's own columns.
  async getContactFormReviewStatus(id: string): Promise<{ columns: PendingReviewColumn[] }> {
    return request<{ columns: PendingReviewColumn[] }>(
      `/api/contacts/${encodeURIComponent(id)}/form-review-status`,
    );
  },

  // Service Records PR 17 — Workload Planner.
  async getWorkloadCapacity(months: number = 6): Promise<WorkloadCapacityResponse> {
    return request<WorkloadCapacityResponse>(
      `/api/admin/workload-planner/capacity?months=${months}`,
    );
  },

  // Service Records PR 18 — Weekly + annual + team heatmap.
  async getWorkloadWeekly(weeks: number = 8): Promise<WorkloadWeeklyResponse> {
    return request<WorkloadWeeklyResponse>(`/api/admin/workload-planner/weekly?weeks=${weeks}`);
  },
  async getWorkloadAnnual(): Promise<WorkloadAnnualResponse> {
    return request<WorkloadAnnualResponse>(`/api/admin/workload-planner/annual`);
  },

  // Forms Platform F0 — the active intake-form catalog (slug + title),
  // feeding the automation builder's "Which form?" picker.
  async listIntakeFormTemplates(): Promise<{
    templates: { slug: string; title: string; company_slug: string | null }[];
  }> {
    return request<{
      templates: { slug: string; title: string; company_slug: string | null }[];
    }>(`/api/intake-form-templates`);
  },

  // The whole active document-type catalog, feeding the automation
  // builder's "a document is signed → which document type?" picker.
  // Lives beside the intake-form picker above because it is the same
  // kind of thing: a builder options source on the `api` object, not on
  // apiV11 (where the per-record e-sign helpers live).
  async listEsignDocumentTypeCatalog(): Promise<{
    types: Array<{ id: string; name: string; company: string; sort_order: number }>;
  }> {
    return request<{
      types: Array<{ id: string; name: string; company: string; sort_order: number }>;
    }>(`/api/esign/document-types`);
  },

  // Forms Platform F2 — the builder + Forms section.
  async listForms(
    scope: FormsListScope,
  ): Promise<{ scope: FormsListScope; forms: FormSummary[]; counts: FormsScopeCounts }> {
    return request(`/api/forms?scope=${encodeURIComponent(scope)}`);
  },
  async createForm(body: {
    title: string;
    company_slug: string;
    definition?: import("@shared/intake/definition").IntakeDefinition;
  }): Promise<{ ok: true; slug: string }> {
    return request(`/api/forms`, { method: "POST", body: JSON.stringify(body) });
  },
  /** The form's side of a form↔meeting connection, saved on its own —
   *  no definition, no draft edits. Pass null to disconnect. */
  async setFormBookAfter(
    slug: string,
    handoff: { company_slug: string; appointment_type_name: string } | null,
  ): Promise<{ ok: true }> {
    return request(`/api/forms/${encodeURIComponent(slug)}/book-after`, {
      method: "POST",
      body: JSON.stringify(handoff ? { enabled: true, ...handoff } : { enabled: false }),
    });
  },
  /** "Describe it to Stew": question rows drafted from a sentence. */
  async draftFormQuestions(description: string): Promise<{
    ok: true;
    questions: import("@shared/forms/connected-experience").QuickQuestion[];
  }> {
    return request(`/api/forms/draft-questions`, {
      method: "POST",
      body: JSON.stringify({ description }),
    });
  },
  async getForm(slug: string): Promise<{ form: FormDetail }> {
    return request(`/api/forms/${encodeURIComponent(slug)}`);
  },
  async saveFormDraft(
    slug: string,
    body: {
      definition: import("@shared/intake/definition").IntakeDefinition;
      title?: string;
      settings?: Record<string, unknown>;
      expected_draft_updated_at?: string | null;
    },
  ): Promise<{ ok: true; draft_updated_at: string }> {
    return request(`/api/forms/${encodeURIComponent(slug)}/draft`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  async publishForm(slug: string): Promise<{ ok: true; version: number }> {
    return request(`/api/forms/${encodeURIComponent(slug)}/publish`, {
      method: "POST",
    });
  },
  async archiveForm(slug: string): Promise<{ ok: true }> {
    return request(`/api/forms/${encodeURIComponent(slug)}/archive`, {
      method: "POST",
    });
  },
  async createFormLink(
    slug: string,
    body: {
      label?: string;
      kind?: "public" | "personalized" | "single_use";
      household_id?: string;
      contact_id?: string;
      expires_days?: number;
      /** The record the link is about, when it's sent from one (a
       *  long-term care case). The server checks it's this household's. */
      record_type?: "opportunity" | "transaction" | "service_record";
      record_id?: string;
      /** "Send a fresh link": also revoke this household's older usable
       *  links for this form, so only the new one opens. */
      replace_existing?: boolean;
    },
  ): Promise<{ ok: true; id: string; url: string; replaced_count?: number }> {
    return request(`/api/forms/${encodeURIComponent(slug)}/links`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  /** "Fill in a form with the client": the person's own personal link,
   *  marked with the employee filling it in, opened in a new tab. Nothing
   *  is sent to the client. A health form passes the health gate first, so
   *  a stated reason (break glass) rides on the path when one is given. */
  async createAssistedFormLink(
    slug: string,
    body: {
      household_id: string;
      contact_id: string;
      record_type?: "opportunity" | "transaction" | "service_record";
      record_id?: string;
    },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; id: string; url: string }> {
    return request(withHealthAccess(`/api/forms/${encodeURIComponent(slug)}/assisted-links`, access), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  /** Public links always come first and in full; personal links are the
   *  newest `page_size` matching the search, with real totals in
   *  `counts` (never measured off the returned list). */
  async listFormLinks(
    slug: string,
    params: { q?: string; show?: "live" | "all" } = {},
  ): Promise<{ links: FormLinkRow[]; counts: FormLinkCounts; page_size: number }> {
    const qs = new URLSearchParams();
    if (params.q?.trim()) qs.set("q", params.q.trim());
    if (params.show === "live") qs.set("show", "live");
    const q = qs.toString();
    return request(`/api/forms/${encodeURIComponent(slug)}/links${q ? `?${q}` : ""}`);
  },
  async listHouseholdFormLinks(
    householdId: string,
  ): Promise<{ links: HouseholdFormLinkRow[]; truncated: boolean }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/form-links`);
  },
  async revokeFormLink(id: string): Promise<{ ok: true }> {
    return request(`/api/form-links/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
    });
  },
  async listFormSubmissions(
    slug: string,
    params: { limit?: number; offset?: number; needsReviewOnly?: boolean; search?: string } = {},
    access?: HealthAccessReason | null,
  ): Promise<{ submissions: FormSubmissionRow[]; total: number; limit: number; offset: number }> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.needsReviewOnly) qs.set("needs_review", "1");
    if (params.search?.trim()) qs.set("q", params.search.trim());
    const q = qs.toString();
    return request(
      withHealthAccess(`/api/forms/${encodeURIComponent(slug)}/submissions${q ? `?${q}` : ""}`, access),
    );
  },
  /** Server-built CSV with the actual answers (audit fix — the old
   *  client export shipped metadata only and silently truncated). */
  async exportFormSubmissionsCsv(slug: string, access?: HealthAccessReason | null): Promise<string> {
    const resp = await fetch(
      apiUrl(withHealthAccess(`/api/forms/${encodeURIComponent(slug)}/submissions/export`, access)),
      { credentials: "include" },
    );
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't export.");
    return resp.text();
  },
  /** Mark or unmark a form as health information (Planning only). */
  async setFormHealthClassification(
    slug: string,
    classified: boolean,
  ): Promise<{ ok: true; health_classified: boolean }> {
    return request(`/api/forms/${encodeURIComponent(slug)}/health-classification`, {
      method: "POST",
      body: JSON.stringify({ classified }),
    });
  },
  async getFormAnalytics(slug: string): Promise<{ versions: FormVersionFunnel[] }> {
    return request(`/api/forms/${encodeURIComponent(slug)}/analytics`);
  },
  async getFormSubmission(
    id: string,
    access?: HealthAccessReason | null,
  ): Promise<{ submission: FormSubmissionDetail }> {
    return request(withHealthAccess(`/api/form-submissions/${encodeURIComponent(id)}`, access));
  },
  /** The filled questionnaire as a PDF — one submission, its pinned
   *  definition version, ready to send to a vendor (e.g. Meagan's
   *  estate-plan questionnaire). */
  async downloadFormSubmissionPdf(
    id: string,
    access?: HealthAccessReason | null,
  ): Promise<{ blob: Blob; filename: string }> {
    const resp = await fetch(
      apiUrl(withHealthAccess(`/api/form-submissions/${encodeURIComponent(id)}/pdf`, access)),
      { credentials: "include" },
    );
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't download the PDF.");
    const disposition = resp.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await resp.blob(), filename: match?.[1] ?? `${id}.pdf` };
  },
  /** The read-only twin for a submission sent before this form moved
   *  onto the Forms Platform (source: "legacy"). */
  async getLegacyFormSubmission(
    id: string,
    access?: HealthAccessReason | null,
  ): Promise<{ submission: FormSubmissionDetail }> {
    return request(
      withHealthAccess(`/api/legacy-form-submissions/${encodeURIComponent(id)}`, access),
    );
  },
  async getSubmissionApplyPlan(id: string, access?: HealthAccessReason | null): Promise<{ plan: FormApplyPlan }> {
    return request(
      withHealthAccess(`/api/form-submissions/${encodeURIComponent(id)}/apply-plan`, access),
    );
  },
  // Which record a submission's answers are about, when the OS couldn't
  // tell on its own (2026-09-08): the household's candidates under the
  // form's destination rule, and the reviewer's pick.
  async getSubmissionRecord(id: string): Promise<{ record: FormSubmissionRecordState }> {
    return request(`/api/form-submissions/${encodeURIComponent(id)}/record`);
  },
  async attachSubmissionRecord(
    id: string,
    recordId: string,
  ): Promise<{ ok: true; record_id: string; apply_state: string }> {
    return request(`/api/form-submissions/${encodeURIComponent(id)}/record`, {
      method: "PATCH",
      body: JSON.stringify({ record_id: recordId }),
    });
  },

  // Protected answers (SSN / bank / licence) a submission could save onto the
  // contact record (Joy, 2026-08-03). Masks only — the values themselves never
  // reach the browser, and applying is a ciphertext move on the server.
  async getSubmissionSensitivePlan(id: string): Promise<{ plan: FormSensitiveApplyPlan }> {
    return request(`/api/form-submissions/${encodeURIComponent(id)}/sensitive-plan`);
  },
  async applySubmissionSensitive(
    id: string,
    families: string[],
  ): Promise<{ ok: true; applied: string[] }> {
    return request(`/api/form-submissions/${encodeURIComponent(id)}/apply-sensitive`, {
      method: "POST",
      body: JSON.stringify({ families }),
    });
  },
  // Doctor + medication lists from the Planning open-enrollment
  // questionnaire (S9). The plan is the diff Andrea reads; applying it
  // writes real contact_providers / contact_medications rows, each
  // versioned and undoable.
  async getSubmissionHealthPlan(
    id: string,
    access?: HealthAccessReason | null,
  ): Promise<{ plan: FormHealthWriteBackPlan }> {
    return request(
      withHealthAccess(`/api/form-submissions/${encodeURIComponent(id)}/health-plan`, access),
    );
  },
  async applySubmissionHealth(
    id: string,
    operations: string[],
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; applied: string[] }> {
    return request(
      withHealthAccess(`/api/form-submissions/${encodeURIComponent(id)}/apply-health`, access),
      { method: "POST", body: JSON.stringify({ operations }) },
    );
  },
  async markSubmissionSpam(id: string): Promise<{ ok: true }> {
    return request(`/api/form-submissions/${encodeURIComponent(id)}/spam`, {
      method: "POST",
    });
  },
  /** Explicit "I've read this, nothing more to do" -- for a submission
   *  whose only pending reason is a free-text note, which has no apply
   *  action of its own to trigger the automatic turns-to-submitted
   *  transition. */
  async markSubmissionReviewed(id: string): Promise<{ ok: true }> {
    return request(`/api/form-submissions/${encodeURIComponent(id)}/mark-reviewed`, {
      method: "POST",
    });
  },
  async applySubmission(
    id: string,
    targets: string[],
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; applied: string[] }> {
    return request(withHealthAccess(`/api/form-submissions/${encodeURIComponent(id)}/apply`, access), {
      method: "POST",
      body: JSON.stringify({ targets }),
    });
  },

  // Service Records PR 19 — Communication templates.
  async listCommunicationTemplates(
    scope?: TemplateScope,
  ): Promise<{ templates: CommunicationTemplate[] }> {
    const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
    return request<{ templates: CommunicationTemplate[] }>(`/api/communication-templates${qs}`);
  },
  async createCommunicationTemplate(
    body: CreateCommunicationTemplateBody,
  ): Promise<{ id: string; slug: string }> {
    return request<{ id: string; slug: string }>(`/api/admin/communication-templates`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async updateCommunicationTemplate(
    id: string,
    body: UpdateCommunicationTemplateBody,
  ): Promise<{ id: string; changes_count: number; approval_cleared?: boolean }> {
    return request<{ id: string; changes_count: number; approval_cleared?: boolean }>(
      `/api/admin/communication-templates/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  },
  async deleteCommunicationTemplate(id: string): Promise<{ id: string; deleted: boolean }> {
    return request<{ id: string; deleted: boolean }>(
      `/api/admin/communication-templates/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },

  // Service Records PR 9b — Donna's visitor prep.
  async getHouseholdComingInToday(id: string): Promise<ComingInTodayResponse> {
    return request<ComingInTodayResponse>(
      `/api/households/${encodeURIComponent(id)}/coming-in-today`,
    );
  },

  // "What runs by itself" (2026-08-11) — the system-jobs registry+health
  // merge. `scope: "all"` widens past the viewer's default company scope.
  // ── PM Guidelines (Private Money) ──
  // Mortgage company scope + the two global roles; the server checks on
  // every one of these three, not once at page load.
  // ── PM underwriting reports (Private Money) ──
  // Same mortgage-scope gate as the guidelines, checked on every route.
  // Costs money: ATTOM bills per call and this makes two. Same mortgage
  // gate as everything else here.
  // ── The KlinkLoans fund ──
  // Readings are computed server-side from the live book and the live
  // limits; nothing is stored, because both halves move independently.
  async getPmFund(): Promise<{
    upload: PmFundUploadRec | null;
    loans: PmFundLoanRec[];
    exposure: PmFundExposureRec | null;
    /** Mike's rules, sent so the read-only thresholds card prints the same
     *  bands the PM Guidelines screen does. */
    guidelines: PmGuidelineRec[];
  }> {
    return request("/api/pm-fund");
  },
  /** The upload history — what the numbers looked like before, and proof
   *  a bad export can be traced back. */
  async listPmFundUploads(): Promise<{ uploads: PmFundUploadRec[] }> {
    return request("/api/pm-fund/uploads");
  },
  async uploadPmFund(input: {
    csv: string;
    as_of_date: string;
    filename: string | null;
    /** Set only after the person has seen which columns are missing and
     *  chosen to go ahead — without it such a file is refused. */
    import_anyway?: boolean;
    /** KlinkLoans' Total Current Assets, in dollars, plus its date. */
    fund_size?: string | null;
    fund_size_as_of?: string | null;
  }): Promise<{
    upload: PmFundUploadRec;
    skipped: Array<{ row: number; reason: string }>;
    unrecognised_headers: string[];
    missing_columns: string[];
  }> {
    return request("/api/pm-fund/upload", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /** Put an earlier export back in force. `expected_live_upload_id` is the
   *  export this screen believes is live — a 409 means someone changed it. */
  async restorePmFundUpload(
    uploadId: string,
    input: { expected_live_upload_id: string | null; reason?: string },
  ): Promise<{ upload: PmFundUploadRec; replaced_upload_id: string | null }> {
    return request(`/api/pm-fund/uploads/${encodeURIComponent(uploadId)}/restore`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async lookupPmProperty(
    subject: { subject_type: "opportunity" | "transaction"; subject_id: string },
    address: {
      line1: string;
      city: string | null;
      state: string | null;
      zip: string;
    },
  ): Promise<{
    property: PmPropertyFactsRec;
    lookup_id: string;
    county: { in_maricopa: boolean | null; source: PmCountySource };
  }> {
    return request("/api/pm-underwriting/property-lookup", {
      method: "POST",
      body: JSON.stringify({ ...address, ...subject }),
    });
  },

  async getPmAttomAllowance(): Promise<{
    used: number;
    ceiling: number;
    left: number;
    max_calls_for_subdivision: number;
    resets_on: string;
  }> {
    return request("/api/pm-underwriting/attom-allowance");
  },

  async comparePmSubdivision(
    lookupId: string,
  ): Promise<{ property: PmPropertyFactsRec; calls_used: number }> {
    return request(
      `/api/pm-underwriting/property-lookup/${encodeURIComponent(lookupId)}/subdivision`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  async addPmRentcastValue(lookupId: string): Promise<{ property: PmPropertyFactsRec }> {
    return request(
      `/api/pm-underwriting/property-lookup/${encodeURIComponent(lookupId)}/rentcast-value`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  async getPmBorrowerExperience(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
  ): Promise<PmBorrowerExperienceRec> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/experience`,
    );
  },
  // Stew's draft comes back to the page and is NOT stored until approved.
  async draftPmExperienceSummary(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
  ): Promise<{ draft: string | null; message?: string }> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/experience/draft`,
      { method: "POST", body: "{}" },
    );
  },
  async approvePmExperienceSummary(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
    input: { text: string; drafted_by_stew: boolean },
  ): Promise<{ summary: PmExperienceSummaryRec }> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/experience`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  async removePmExperienceSummary(id: string): Promise<{ ok: true }> {
    return request(`/api/pm-underwriting/experience-summaries/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  async getPmBorrowerCredit(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
  ): Promise<PmBorrowerCreditRec> {
    return request(`/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/credit`);
  },
  // A POST because it appends a pull; nothing is overwritten.
  async recordPmCreditPull(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
    input: { score: number; pulled_on: string; source_note?: string | null },
  ): Promise<{ pull: PmCreditPullRec }> {
    return request(`/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/credit`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async removePmCreditPull(id: string): Promise<{ ok: true }> {
    return request(`/api/pm-underwriting/credit-pulls/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  async getPmLoanApplication(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
  ): Promise<{
    application: PmLoanApplicationRec | null;
    /** A Private Money loan with no application: its (blank) terms, so they
     *  can still be set. Absent when there is an application. */
    bare_terms?: { terms: PmLoanTermsRec };
  }> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/application`,
    );
  },
  /** Which protected details the loan has on file (the entity's EIN, the
   *  wire destination) — masks and presence only, never a value. */
  async getPmProtectedDetails(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
  ): Promise<{ protected: PmProtectedDetailsRec }> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/protected`,
    );
  },
  /** Reveal ONE protected detail on a Private Money loan. Same rules as
   *  fetchSensitiveField: MFA, audited server-side, hourly budget, and the
   *  value lives only in the calling component's local state. */
  async fetchPmLoanSensitiveField(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
    field: PmLoanSensitiveFieldName,
  ): Promise<{ field: PmLoanSensitiveFieldName; value: string }> {
    return request(
      `/api/pm-sensitive/${subjectType}/${encodeURIComponent(subjectId)}/${field}`,
    );
  },
  /** Set the loan's rate and origination fee; null clears one back to the
   *  lender's standing value. `previous` is what Undo puts back. */
  async setPmLoanTerms(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
    body: PmLoanTermsSave,
  ): Promise<{
    terms: PmLoanTermsRec | null;
    previous: PmLoanTermsSave;
    changed: boolean;
  }> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/terms`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  },
  /** The loan's terms as a draft email to the borrower. Sends nothing; the
   *  body holds a form-link marker the caller replaces with a real link. */
  async composePmTermsEmail(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
  ): Promise<PmTermsEmailDraft> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/terms-email`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },
  async getPmDisclosure(transactionId: string): Promise<{ disclosure: PmDisclosureRec }> {
    return request(
      `/api/pm-underwriting/transaction/${encodeURIComponent(transactionId)}/disclosure`,
    );
  },
  async preparePmDisclosure(
    transactionId: string,
  ): Promise<{ document_id: string; filename: string; web_url: string }> {
    return request(
      `/api/pm-underwriting/transaction/${encodeURIComponent(transactionId)}/disclosure/prepare`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },
  /** An uploaded disclosure is the signed copy; `undo` takes it back. */
  async markPmDisclosureSigned(
    transactionId: string,
    documentId: string,
    opts?: { undo?: boolean },
  ): Promise<{ disclosure: PmDisclosureRec }> {
    return request(
      `/api/pm-underwriting/transaction/${encodeURIComponent(transactionId)}/disclosure/${opts?.undo ? "unmark-signed" : "mark-signed"}`,
      { method: "POST", body: JSON.stringify({ document_id: documentId }) },
    );
  },
  async sendPmDisclosure(
    transactionId: string,
    body: { message: string | null },
  ): Promise<{
    envelope_id: string;
    document_id: string;
    signers: Array<{ name: string; email: string; sign_url: string; emailed: boolean }>;
  }> {
    return request(
      `/api/pm-underwriting/transaction/${encodeURIComponent(transactionId)}/disclosure/send`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  /** Every report on the loan — including those run on the Opportunity a
   *  Processing record came from. `identity` adds who/what the loan is. */
  async listPmUnderwritingReports(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
    opts?: { identity?: boolean },
  ): Promise<{
    reports: PmUnderwritingReportRec[];
    opportunity_id?: string | null;
    /** What changed after the latest report ran. */
    changed_since_latest?: string[];
    subject?: PmUnderwritingSubjectRec;
    fund_checks_not_asked?: string;
  }> {
    return request(
      `/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}${opts?.identity ? "?identity=1" : ""}`,
    );
  },
  /** Take back a lien check just marked (the marker, within 5 minutes). */
  async undoPmUnderwritingLien(reportId: string): Promise<{ report: PmUnderwritingReportRec }> {
    return request(`/api/pm-underwriting/reports/${encodeURIComponent(reportId)}/verify-lien`, {
      method: "DELETE",
    });
  },
  /** "Your review". 409 version_conflict carries the current report. */
  async setPmUnderwritingReview(
    reportId: string,
    input: PmUnderwritingReviewInput & { expected_review_version: number },
  ): Promise<{
    report: PmUnderwritingReportRec;
    changed: boolean;
    previous: PmUnderwritingReviewInput;
  }> {
    return request(`/api/pm-underwriting/reports/${encodeURIComponent(reportId)}/review`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  // A POST because it APPENDS a permanent version to a lending file — it
  // reads like a calculation but it is a write.
  async runPmUnderwritingReport(
    subjectType: "opportunity" | "transaction",
    subjectId: string,
    input: PmUnderwritingRunInput,
  ): Promise<{ report: PmUnderwritingReportRec }> {
    return request(`/api/pm-underwriting/${subjectType}/${encodeURIComponent(subjectId)}/run`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async verifyPmUnderwritingLien(
    reportId: string,
    note: string | null,
  ): Promise<{ report: PmUnderwritingReportRec }> {
    return request(`/api/pm-underwriting/reports/${encodeURIComponent(reportId)}/verify-lien`, {
      method: "POST",
      body: JSON.stringify({ note }),
    });
  },

  // The lenders ride along: §6.3 puts each lender's defaults on this
  // screen, and the screen is drawn whole.
  // `now` is the fund's reading per Target/Limit prefix, from the live
  // KlinkLoans export; a prefix absent from it was not measured.
  async listPmGuidelines(): Promise<{
    guidelines: PmGuidelineRec[];
    lenders: PmLenderProfileRec[];
    now?: Record<string, number>;
    fund_as_of_date?: string | null;
  }> {
    return request("/api/pm-guidelines");
  },
  async listPmGuidelineHistory(id: string): Promise<{ history: PmGuidelineHistoryRec[] }> {
    return request<{ history: PmGuidelineHistoryRec[] }>(
      `/api/pm-guidelines/${encodeURIComponent(id)}/history`,
    );
  },
  // `expected_version_number` is the version this client last read. Leaving
  // it out would make the write last-wins from the user's point of view —
  // see the note on setPmGuidelineValue in shared/db/pm-guidelines.ts. A
  // 409 with code "version_conflict" carries what the row says now.
  async setPmGuidelineValue(
    id: string,
    input: {
      value: number | null;
      /** Required — it goes in the number's history. */
      reason: string;
      expected_version_number: number;
    },
  ): Promise<{ ok: true; changed: boolean; guideline: PmGuidelineRec | null }> {
    return request(`/api/pm-guidelines/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },

  /** A lender's standing rate, fee, balloon, prepayment and extension
   *  terms (PM Guidelines). 409 version_conflict when someone else
   *  changed it first. */
  async setPmLenderDefaults(
    id: string,
    input: {
      default_interest_rate: number | null;
      default_origination_fee_cents: number | null;
      default_balloon_months: number | null;
      default_prepayment_terms: string | null;
      default_extension_terms: string | null;
      reason: string | null;
      expected_version_number: number;
    },
  ): Promise<{
    changed: boolean;
    previous: {
      default_interest_rate: number | null;
      default_origination_fee_cents: number | null;
      default_balloon_months: number | null;
      default_prepayment_terms: string | null;
      default_extension_terms: string | null;
    };
    lender: PmLenderProfileRec | null;
  }> {
    return request(`/api/pm-guidelines/lenders/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },

  async listSystemJobs(opts?: { scope?: "all" }): Promise<SystemJobsResponse> {
    const qs = opts?.scope === "all" ? "?scope=all" : "";
    return request<SystemJobsResponse>(`/api/system-jobs${qs}`);
  },

  // Slow-query telemetry (speed program PR 7) — admin diagnostic over
  // slow_query_log: which database queries ran slow (≥500ms) or heavy
  // (≥10k rows scanned) in the window.
  async getEmailHealth(): Promise<EmailHealthReport> {
    return request<EmailHealthReport>(`/api/admin/email-health`);
  },

  // Staging outbox (2026-11-02). Staging only; system_admin only.
  async listStagingOutbox(before?: { created_at: string; id: string } | null): Promise<StagingOutboxPage> {
    const qs = before
      ? `?before_created_at=${encodeURIComponent(before.created_at)}&before_id=${encodeURIComponent(before.id)}`
      : "";
    return request<StagingOutboxPage>(`/api/admin/staging-outbox${qs}`);
  },
  async getStagingOutboxEmail(id: string): Promise<StagingOutboxEmail> {
    return request<StagingOutboxEmail>(`/api/admin/staging-outbox/${encodeURIComponent(id)}`);
  },

  // Row 6.1: the five per-company sending subdomains, read live.
  async getSendingDomains(): Promise<SendingDomainsReport> {
    return request<SendingDomainsReport>(`/api/admin/email-health/sending-domains`);
  },
  async setUpSendingDomains(): Promise<{ ok: true; steps: { slug: string; result: string }[]; subdomains: SendingSubdomainView[] }> {
    return request(`/api/admin/email-health/sending-domains/setup`, { method: "POST" });
  },
  async getPostmaster(): Promise<PostmasterReport> {
    return request<PostmasterReport>(`/api/admin/email-health/postmaster`);
  },
  async readPostmasterNow(): Promise<{ outcome: string; days_written: number; sentence: string }> {
    return request(`/api/admin/email-health/postmaster/read-now`, { method: "POST" });
  },
  async getClickTracking(): Promise<ClickTrackingReport> {
    return request<ClickTrackingReport>(`/api/admin/email-health/click-tracking`);
  },
  async setClickTracking(on: boolean): Promise<Pick<ClickTrackingReport, "on" | "source" | "updated_at">> {
    return request(`/api/admin/email-health/click-tracking`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ on }),
    });
  },
  async listSendingCompanies(): Promise<{ companies: SendingCompanyChoice[] }> {
    return request<{ companies: SendingCompanyChoice[] }>(`/api/communications/campaigns/sending-companies`);
  },

  async runEmailHealthCheckNow(): Promise<EmailHealthCheckNowResult> {
    return request<EmailHealthCheckNowResult>(`/api/admin/email-health/check-now`, {
      method: "POST",
    });
  },

  // Mortgage OS loan file (A1a API, A1b screen). The server opens these
  // only to Mortgage scope, system admin and firm-wide compliance, and
  // audits every read.
  async ensureLoanFile(ref: { transaction_id: string } | { opportunity_id: string }): Promise<{ file: LoanFileRow; created: boolean }> {
    return request(`/api/mortgage-loan-files/ensure`, { method: "POST", body: JSON.stringify(ref) });
  },
  async getLoanFile(fileId: string): Promise<LoanFileView> {
    return request<LoanFileView>(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}`);
  },
  async getLoanFileDemographics(fileId: string): Promise<{ demographics: DemographicsRow[] }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/demographics`);
  },
  // A1c: editing. Every change is a new claim (never an overwrite);
  // expected_claim_id is the claim the screen showed, so a stale screen
  // gets a 409 and its entry is kept as a claim waiting for a decision.
  async recordLoanClaim(
    fileId: string,
    body: {
      subject_type: string;
      subject_id: string;
      field: string;
      value: string | number | boolean | null;
      expected_claim_id: string | null;
      source_kind?: "employee" | "stated" | "document" | "lender" | "computed";
      source_reference?: string;
      source_date?: string;
      method_note?: string;
    },
  ): Promise<{ claim_id: string; qualifying: boolean }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/claims`, { method: "POST", body: JSON.stringify(body) });
  },
  async listLoanClaims(fileId: string, q: { subject_type: string; subject_id: string; field: string }): Promise<{ claims: LoanClaimListItem[]; total: number; cap: number }> {
    const params = new URLSearchParams(q);
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/claims?${params.toString()}`);
  },
  async chooseLoanClaim(fileId: string, claimId: string, expectedClaimId: string | null): Promise<{ claim_id: string; qualifying: boolean }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/claims/${encodeURIComponent(claimId)}/choose`, {
      method: "POST",
      body: JSON.stringify({ expected_claim_id: expectedClaimId }),
    });
  },
  async addLoanRow(
    fileId: string,
    body: { subject_type: string; loan_borrower_id?: string | null; contact_id?: string; role?: "primary" | "co_borrower"; values: Record<string, string | number | boolean | null> },
  ): Promise<{ id: string; claim_ids: string[] }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/rows`, { method: "POST", body: JSON.stringify(body) });
  },
  async removeLoanRow(fileId: string, subject: string, rowId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/rows/${encodeURIComponent(subject)}/${encodeURIComponent(rowId)}/remove`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async restoreLoanRow(fileId: string, subject: string, rowId: string): Promise<{ ok: true }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/rows/${encodeURIComponent(subject)}/${encodeURIComponent(rowId)}/restore`, {
      method: "POST",
      body: "{}",
    });
  },
  async recordLoanDemographics(fileId: string, borrowerId: string, answers: Record<string, unknown>): Promise<{ id: string }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/demographics/${encodeURIComponent(borrowerId)}`, {
      method: "POST",
      body: JSON.stringify(answers),
    });
  },

  // Mortgage OS A6a: the loan file's credit report. Behind the loan file's
  // Mortgage-only gate; every call is audited. The XML is read and dropped.
  async getLoanCredit(fileId: string): Promise<LoanCreditSummary> {
    return request<LoanCreditSummary>(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/credit`);
  },
  // Multipart, so it can't go through request() (that forces JSON).
  async importLoanCreditReport(fileId: string, xml: File, pdf: File | null, pullKind: "hard" | "soft"): Promise<LoanCreditImportResult> {
    const fd = new FormData();
    fd.append("xml", xml);
    if (pdf) fd.append("pdf", pdf);
    fd.append("pull_kind", pullKind);
    const res = await fetch(apiUrl(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/credit-reports`), {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      let payload: { error?: string; message?: string } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        // non-JSON error body: fall back to a generic message
      }
      throw new ApiError(res.status, payload.message ?? payload.error ?? `import_failed_${res.status}`, payload.error ?? null);
    }
    return (await res.json()) as LoanCreditImportResult;
  },
  async resolveLoanCreditAlert(fileId: string, alertId: string, note: string): Promise<{ ok: true }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/credit-alerts/${encodeURIComponent(alertId)}/resolve`, {
      method: "POST",
      body: JSON.stringify({ note }),
    });
  },
  async decideLoanCreditProposal(fileId: string, liabilityId: string, decision: "approve" | "dismiss", reason?: string): Promise<{ ok: true }> {
    return request(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/credit-proposals/${encodeURIComponent(liabilityId)}`, {
      method: "POST",
      body: JSON.stringify({ decision, reason }),
    });
  },
  async downloadLoanCreditPdf(fileId: string, reportId: string): Promise<{ blob: Blob; filename: string }> {
    const resp = await fetch(apiUrl(`/api/mortgage-loan-files/${encodeURIComponent(fileId)}/credit-reports/${encodeURIComponent(reportId)}/pdf`), {
      credentials: "include",
    });
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't download the report.");
    const disposition = resp.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await resp.blob(), filename: match?.[1] ?? "credit-report.pdf" };
  },

  // Mortgage OS A5a: a loan's bank data, the loan officer's side. Opened
  // only to the Mortgage-only lock and audited on every call. Asking sends
  // nothing to Plaid; the borrower agrees on my.stewardship.pro.
  async getLoanBankData(fileId: string): Promise<LoanBankDataView> {
    return request<LoanBankDataView>(`/api/loan-bank-data/${encodeURIComponent(fileId)}`);
  },
  async askLoanBankData(fileId: string, loanBorrowerId: string): Promise<{ ask: LoanBankDataAsk; email_sent: boolean }> {
    return request(`/api/loan-bank-data/${encodeURIComponent(fileId)}/ask`, {
      method: "POST",
      body: JSON.stringify({ loan_borrower_id: loanBorrowerId }),
    });
  },
  async withdrawLoanBankDataAsk(fileId: string, askId: string, reason: string): Promise<{ withdrawn: boolean }> {
    return request(`/api/loan-bank-data/${encodeURIComponent(fileId)}/asks/${encodeURIComponent(askId)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async fetchLoanBankReport(fileId: string, askId: string): Promise<{ report_id: string }> {
    return request(`/api/loan-bank-data/${encodeURIComponent(fileId)}/asks/${encodeURIComponent(askId)}/fetch-report`, { method: "POST", body: "{}" });
  },
  async refreshLoanBankReport(fileId: string, askId: string): Promise<{ requested: boolean; message: string }> {
    return request(`/api/loan-bank-data/${encodeURIComponent(fileId)}/asks/${encodeURIComponent(askId)}/refresh`, { method: "POST", body: "{}" });
  },
  async endLoanBankConnection(fileId: string, askId: string, reason: LoanBankEndReason): Promise<{ ended: boolean }> {
    return request(`/api/loan-bank-data/${encodeURIComponent(fileId)}/asks/${encodeURIComponent(askId)}/end`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  /** A saved report's PDF, through the Mortgage-only lock (audited). */
  async downloadLoanBankReportPdf(fileId: string, reportId: string): Promise<{ blob: Blob; filename: string }> {
    const resp = await fetch(apiUrl(`/api/loan-bank-data/${encodeURIComponent(fileId)}/reports/${encodeURIComponent(reportId)}/pdf`), {
      credentials: "include",
    });
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't download the report.");
    const disposition = resp.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await resp.blob(), filename: match?.[1] ?? "home-lending-report.pdf" };
  },

  async listClientAccess(): Promise<ClientAccessList> {
    return request<ClientAccessList>(`/api/client-access`);
  },
  async inviteClient(contactId: string): Promise<ClientInviteResponse> {
    return request<ClientInviteResponse>(`/api/client-access/invite`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId }),
    });
  },
  async resendClientInvite(userId: string): Promise<{ invitation: "sent" | "not_sent"; sign_in_url: string | null }> {
    return request(`/api/client-access/${encodeURIComponent(userId)}/resend`, { method: "POST", body: "{}" });
  },
  async setClientAccess(
    userId: string,
    action: "disable" | "enable",
    body: { reason?: string; expected_version: number },
  ): Promise<{ ok: true; version_number: number }> {
    return request(`/api/client-access/${encodeURIComponent(userId)}/${action}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async getPlaidLiveTest(): Promise<PlaidLiveTestStatus> {
    return request<PlaidLiveTestStatus>(`/api/admin/plaid-live-test`);
  },

  async startPlaidLiveTest(
    consentVersion: string,
    identity: PlaidLiveTestIdentity,
  ): Promise<{ link_token: string; plaid_calls: PlaidCallSummary[] }> {
    return request(`/api/admin/plaid-live-test/start`, {
      method: "POST",
      body: JSON.stringify({ consent_version: consentVersion, agreed: true, identity }),
    });
  },

  async resumePlaidLiveTest(): Promise<{ link_token: string }> {
    return request(`/api/admin/plaid-live-test/resume`);
  },

  async completePlaidLiveTest(publicToken: string): Promise<{
    connection_id: string;
    /** True when this finished a "Reconnect your bank" repair. */
    repaired?: boolean;
    fresh_report_requested?: boolean;
    plaid_calls: PlaidCallSummary[];
  }> {
    return request(`/api/admin/plaid-live-test/complete`, {
      method: "POST",
      body: JSON.stringify({ public_token: publicToken }),
    });
  },

  async choosePlaidLiveTestUpload(): Promise<{ recorded: boolean; message: string; plaid_calls: PlaidCallSummary[] }> {
    return request(`/api/admin/plaid-live-test/choose-upload`, { method: "POST" });
  },

  async checkPlaidLiveTestConnection(id: string): Promise<{
    banks: {
      institution_id: string | null;
      institution_name: string | null;
      error_code: string | null;
      consent_expiration_time: string | null;
    }[];
    plaid_calls: PlaidCallSummary[];
  }> {
    return request(`/api/admin/plaid-live-test/connections/${encodeURIComponent(id)}/check`, { method: "POST" });
  },

  /** Link in repair mode for a connection that needs the person (fresh passkey). */
  async reconnectPlaidLiveTestConnection(
    id: string,
  ): Promise<{ link_token: string; expiration: string | null; banks_to_repair: number; plaid_calls: PlaidCallSummary[] }> {
    return request(`/api/admin/plaid-live-test/connections/${encodeURIComponent(id)}/reconnect`, { method: "POST" });
  },

  /** Ask Plaid for a fresh report; it saves itself when Plaid's notice arrives. */
  async refreshPlaidLiveTestReport(
    id: string,
  ): Promise<{ requested: boolean; message: string; plaid_calls: PlaidCallSummary[] }> {
    return request(`/api/admin/plaid-live-test/connections/${encodeURIComponent(id)}/refresh`, { method: "POST" });
  },

  async fetchPlaidLiveTestReport(id: string): Promise<{ report: PlaidLiveTestReport; plaid_calls: PlaidCallSummary[] }> {
    return request(`/api/admin/plaid-live-test/connections/${encodeURIComponent(id)}/report`, { method: "POST" });
  },

  /** A saved report's PDF, through the Mortgage-only lock (audited). */
  async downloadPlaidLiveTestReportPdf(reportId: string): Promise<{ blob: Blob; filename: string }> {
    const resp = await fetch(apiUrl(`/api/admin/plaid-live-test/reports/${encodeURIComponent(reportId)}/pdf`), {
      credentials: "include",
    });
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't download the report.");
    const disposition = resp.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    return { blob: await resp.blob(), filename: match?.[1] ?? "home-lending-report.pdf" };
  },

  async endPlaidLiveTestConnection(id: string): Promise<{ ended: boolean; plaid_calls: PlaidCallSummary[] }> {
    return request(`/api/admin/plaid-live-test/connections/${encodeURIComponent(id)}/end`, { method: "POST" });
  },

  async deletePlaidLiveTestConnection(id: string, confirm: string): Promise<{ deleted: boolean }> {
    return request(`/api/admin/plaid-live-test/connections/${encodeURIComponent(id)}/delete`, {
      method: "POST",
      body: JSON.stringify({ confirm }),
    });
  },

  async getDatabaseHealth(days: number): Promise<DatabaseHealthReport> {
    return request<DatabaseHealthReport>(
      `/api/admin/database-health?days=${encodeURIComponent(String(days))}`,
    );
  },

  async getSlowQueryReport(days: number): Promise<SlowQueryReport> {
    return request<SlowQueryReport>(
      `/api/admin/slow-queries?days=${encodeURIComponent(String(days))}`,
    );
  },

  // Service Records PR 12 — Automation Workspace.
  async listAutomations(): Promise<{ automations: AutomationListRow[] }> {
    return request<{ automations: AutomationListRow[] }>(`/api/admin/automations`);
  },
  async getAutomationDetail(id: string): Promise<AutomationDetailResponse> {
    return request<AutomationDetailResponse>(`/api/admin/automations/${encodeURIComponent(id)}`);
  },
  async listAffectingAutomations(
    entityType: string,
    entityId: string,
  ): Promise<{ automations: AffectingAutomationRow[] }> {
    return request<{ automations: AffectingAutomationRow[] }>(
      `/api/records/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/affecting-automations`,
    );
  },
  async listScheduledAutomations(
    entityType: string,
    entityId: string,
  ): Promise<ScheduledAutomationsResponse> {
    return request<ScheduledAutomationsResponse>(
      `/api/records/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/scheduled-automations`,
    );
  },
  async listStandingAutomations(
    entityType: string,
    entityId: string,
  ): Promise<StandingAutomationsResponse> {
    return request<StandingAutomationsResponse>(
      `/api/records/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/standing-automations`,
    );
  },
  // Ongoing Care → Automations, step 3: the rules acting on a whole board,
  // grouped by the stage each targets (derived from trigger + conditions).
  async listPipelineAutomations(
    board: PipelineBoard,
    company?: string | null,
  ): Promise<PipelineAutomations> {
    const qs = company ? `?company=${encodeURIComponent(company)}` : "";
    return request<PipelineAutomations>(
      `/api/pipelines/${encodeURIComponent(board)}/automations${qs}`,
    );
  },

  // Service Records PR 15 — Automation builder wizard.
  async createAutomation(
    body: CreateAutomationBody,
  ): Promise<{ id: string; state: AutomationState }> {
    return request<{ id: string; state: AutomationState }>(`/api/admin/automations`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async updateAutomation(
    id: string,
    body: UpdateAutomationBody,
  ): Promise<{ id: string; changes_count: number }> {
    // Timed: this is the write that pauses-then-saves, and a hang here is
    // what leaves a rule switched off with no way back. See withTimeout.
    return withTimeout(SAVE_TIMEOUT_MS, (signal) =>
      request<{ id: string; changes_count: number }>(
        `/api/admin/automations/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(body), signal },
      ),
    );
  },
  // Build item B — the pre-save impact of a proposed edit. Body carries
  // the PROPOSED compiled definition; nothing is persisted.
  async getAutomationEditImpact(
    id: string,
    body: {
      trigger_type?: "event" | "schedule" | "record_condition";
      trigger_config?: unknown;
      conditions?: ConditionNode | null;
      actions?: CompiledAction[];
      // A proposed scope change (2026-08-03), so the delta reflects the
      // records the move would add or drop.
      scope?: "personal" | "company" | "global";
      scope_company?: string | null;
    },
  ): Promise<EditImpactResponse> {
    return request<EditImpactResponse>(
      `/api/admin/automations/${encodeURIComponent(id)}/edit-impact`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async setAutomationState(
    id: string,
    state: Exclude<AutomationState, "error">,
    // Required by the server when state === "active" (safety patch
    // 2026-07-03): the impact numbers the consequence modal displayed,
    // echoed back so the acknowledgement is recorded server-side.
    impact?: AutomationImpactAcknowledgement,
  ): Promise<{ id: string; state: AutomationState }> {
    // Timed (2026-09-14): this is the PAUSE step that runs before
    // updateAutomation's write, and the RESTORE-on-failed-save step after
    // it — a hang here is what left Andrea's edits never even reaching the
    // write call. See withTimeout.
    return withTimeout(SAVE_TIMEOUT_MS, (signal) =>
      request<{ id: string; state: AutomationState }>(
        `/api/admin/automations/${encodeURIComponent(id)}/state`,
        {
          method: "POST",
          body: JSON.stringify({ state, ...(impact ? { impact } : {}) }),
          signal,
        },
      ),
    );
  },
  // Owner reassignment (E, 2026-07-03) — matrix-gated server-side.
  async reassignAutomationOwner(
    id: string,
    ownerUserId: string,
  ): Promise<{ id: string; owner_user_id: string }> {
    return request<{ id: string; owner_user_id: string }>(
      `/api/admin/automations/${encodeURIComponent(id)}/owner`,
      { method: "POST", body: JSON.stringify({ owner_user_id: ownerUserId }) },
    );
  },
  async buildAutomationFromText(input: { message: string; company?: string | null }): Promise<{
    selection: RuleSelection;
    sentence: string;
    /** Model-suggested rule name (the user's own name, verbatim, when they gave one). */
    suggested_name?: string | null;
    /** Plain English: parts of the request Stew could NOT include, with reasons. */
    unmapped?: string[];
    /** Requested per-rule send time (migration 0186) — canonical "HH:MM" or
     *  null. Prefills the builder's send-time control. */
    send_time_local?: string | null;
    /** "personal" when the request was plainly about the asker's own
     *  work; null otherwise. Only ever narrows the rule's audience. */
    scope?: "personal" | null;
  }> {
    return request(`/api/admin/automations-ai/build`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // Automations v2 PR13 (v1.5) — global "Ask Stew about your
  // automations" tool-use loop. Read-only; any state change comes back
  // as a `proposed` the user must confirm (routed through
  // setAutomationState).
  async engageAutomationStew(message: string): Promise<AutomationEngageResponse> {
    return request<AutomationEngageResponse>(`/api/admin/automations-ai/engage`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
  },
  async rollbackAutomationRun(
    id: string,
    runId: string,
  ): Promise<{
    run_id: string;
    reversed: { kind: string; detail: string }[];
    irreversible: { kind: string; detail: string }[];
  }> {
    return request(
      `/api/admin/automations/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}/rollback`,
      { method: "POST" },
    );
  },
  async previewAutomation(body: AutomationPreviewBody): Promise<AutomationPreviewResponse> {
    return request<AutomationPreviewResponse>(`/api/admin/automations/preview`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // "Send me a test" in the builder's email editor — sends the current
  // subject/body (or a saved template) to the caller's own mailbox.
  // 409 not_connected means their Outlook isn't connected yet.
  async sendAutomationTestEmail(body: {
    subject: string;
    body: string;
    /** "html" when the body came from the rich-text editor. Omitted for
     *  plain-text bodies, matching the stored action config. */
    body_format?: "html";
    template_slug?: string;
    /** The rule's "Link text" override for {{form.link}} — matches what
     *  the real send renders instead of falling back to the bare URL. */
    link_text?: string;
    /** Same, for {{booking.link}} (its own words since 2026-09-04). */
    booking_link_text?: string;
  }): Promise<{ ok: boolean; sent_to: string; signature_appended: boolean }> {
    return request<{
      ok: boolean;
      sent_to: string;
      signature_appended: boolean;
    }>(`/api/admin/automations/test-send`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async automationAudiencePreview(body: {
    entity_type: AutomationPreviewEntityType;
    conditions: ConditionNode | null;
    automation_id?: string;
  }): Promise<AutomationAudiencePreview> {
    return request<AutomationAudiencePreview>(`/api/admin/automations/audience-preview`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async addAutomationExclusion(
    id: string,
    body: { contact_id: string; household_id?: string | null; reason?: string },
  ): Promise<{ ok: boolean; created: boolean }> {
    return request<{ ok: boolean; created: boolean }>(
      `/api/admin/automations/${encodeURIComponent(id)}/exclusions`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async removeAutomationExclusion(
    id: string,
    contactId: string,
  ): Promise<{ ok: boolean; removed: boolean }> {
    return request<{ ok: boolean; removed: boolean }>(
      `/api/admin/automations/${encodeURIComponent(id)}/exclusions/${encodeURIComponent(contactId)}`,
      { method: "DELETE" },
    );
  },
  // Per-contact controls (2026-07-03): manual inclusion + skip-once +
  // always-draft-for-this-person. Same permission model as exclusions.
  async addAutomationInclusion(
    id: string,
    body: { contact_id: string; reason?: string },
  ): Promise<{ ok: boolean; created: boolean; removed_exclusion: boolean }> {
    return request<{ ok: boolean; created: boolean; removed_exclusion: boolean }>(
      `/api/admin/automations/${encodeURIComponent(id)}/inclusions`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async removeAutomationInclusion(
    id: string,
    contactId: string,
  ): Promise<{ ok: boolean; removed: boolean }> {
    return request<{ ok: boolean; removed: boolean }>(
      `/api/admin/automations/${encodeURIComponent(id)}/inclusions/${encodeURIComponent(contactId)}`,
      { method: "DELETE" },
    );
  },
  async setAutomationContactOverride(
    id: string,
    contactId: string,
    body: { always_draft?: boolean; skip_next?: boolean },
  ): Promise<{ ok: boolean; always_draft: boolean; skip_next_pending: boolean }> {
    return request<{
      ok: boolean;
      always_draft: boolean;
      skip_next_pending: boolean;
    }>(
      `/api/admin/automations/${encodeURIComponent(id)}/contact-overrides/${encodeURIComponent(contactId)}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  },
  async detectAutomationConflicts(
    body: DetectAutomationConflictsBody,
  ): Promise<{ conflicts: AutomationConflict[] }> {
    // Timed (2026-09-14): the third of the three activation gates walked by
    // activateThroughGates — see withTimeout.
    return withTimeout(SAVE_TIMEOUT_MS, (signal) =>
      request<{ conflicts: AutomationConflict[] }>(`/api/admin/automations/conflicts`, {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      }),
    );
  },
  // Service Records PR 22 — Impact preview + safe activation.
  async getAutomationImpact(
    id: string,
    entityType?: AutomationPreviewEntityType,
    cursor?: string,
  ): Promise<ImpactScanResponse> {
    const params = new URLSearchParams();
    if (entityType) params.set("entity_type", entityType);
    if (cursor) params.set("cursor", cursor);
    const qs = params.size > 0 ? `?${params.toString()}` : "";
    // Timed (2026-09-14): one chunk of activateThroughGates' whole-book
    // test walk (up to SCAN_CHUNK_GUARD round trips) — each chunk gets its
    // own budget rather than the walk as a whole. See withTimeout.
    return withTimeout(SAVE_TIMEOUT_MS, (signal) =>
      request<ImpactScanResponse>(`/api/admin/automations/${encodeURIComponent(id)}/impact${qs}`, {
        signal,
      }),
    );
  },
  // Missed marks + catch-up (Grant, 2026-08-10). Exact-day firing means a
  // record whose day passed unserved stays unserved until someone acts —
  // these two calls are the seeing and the fixing.
  async getAutomationMissedMarks(id: string, lookbackDays?: number): Promise<MissedMarksResponse> {
    const qs = lookbackDays && lookbackDays > 0 ? `?lookback_days=${lookbackDays}` : "";
    return request<MissedMarksResponse>(
      `/api/admin/automations/${encodeURIComponent(id)}/missed-marks${qs}`,
    );
  },
  async catchUpAutomation(id: string, entityIds: string[]): Promise<CatchUpResponse> {
    return request<CatchUpResponse>(`/api/admin/automations/${encodeURIComponent(id)}/catch-up`, {
      method: "POST",
      body: JSON.stringify({ entity_ids: entityIds }),
    });
  },
  async getAutomationActivationStatus(id: string): Promise<ActivationGateStatusResponse> {
    return request<ActivationGateStatusResponse>(
      `/api/admin/automations/${encodeURIComponent(id)}/activation-status`,
    );
  },
  async acknowledgeAutomationGate(
    id: string,
    gate: ActivationGate,
    notes?: string | null,
  ): Promise<{ acknowledgement: ActivationAcknowledgement }> {
    // Timed (2026-09-14): one of the three gate-recording calls
    // activateThroughGates makes on the way to turning a rule on. See
    // withTimeout.
    return withTimeout(SAVE_TIMEOUT_MS, (signal) =>
      request<{ acknowledgement: ActivationAcknowledgement }>(
        `/api/admin/automations/${encodeURIComponent(id)}/acknowledge-gate`,
        {
          method: "POST",
          body: JSON.stringify({ gate, notes: notes ?? null }),
          signal,
        },
      ),
    );
  },
  async activateAutomation(id: string): Promise<{ id: string; state: AutomationState }> {
    // Timed (2026-09-14): the last step of activateThroughGates — the call
    // that actually flips the rule on. See withTimeout.
    return withTimeout(SAVE_TIMEOUT_MS, (signal) =>
      request<{ id: string; state: AutomationState }>(
        `/api/admin/automations/${encodeURIComponent(id)}/activate`,
        { method: "POST", signal },
      ),
    );
  },
  // Service Records PR 23 — Automation versioning.
  async listAutomationVersions(id: string): Promise<{ versions: AutomationVersion[] }> {
    return request<{ versions: AutomationVersion[] }>(
      `/api/admin/automations/${encodeURIComponent(id)}/versions`,
    );
  },
  async rollbackAutomationVersion(
    id: string,
    versionNumber: number,
  ): Promise<{ id: string; state: AutomationState; restored_from_version: number }> {
    return request<{
      id: string;
      state: AutomationState;
      restored_from_version: number;
    }>(`/api/admin/automations/${encodeURIComponent(id)}/versions/${versionNumber}/rollback`, {
      method: "POST",
    });
  },
  // Service Records PR 24 — Kill switch + ownership policy.
  async invokeAutomationKillSwitch(body: KillSwitchRequest): Promise<KillSwitchResult> {
    return request<KillSwitchResult>(`/api/admin/automations/kill-switch`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // Automations v2 page — global kill switch. GET is readable by anyone who
  // can open the workspace (the banner must show for everyone when armed);
  // POST is system_admin only.
  async getAutomationGlobalPause(): Promise<GlobalPauseState> {
    return request<GlobalPauseState>(`/api/admin/automations/global-pause`);
  },
  async setAutomationGlobalPause(
    paused: boolean,
    reason?: string | null,
  ): Promise<GlobalPauseState> {
    return request<GlobalPauseState>(`/api/admin/automations/global-pause`, {
      method: "POST",
      body: JSON.stringify({ paused, reason: reason ?? null }),
    });
  },
  async recordAutomationOwnershipReview(
    id: string,
  ): Promise<{ id: string; last_ownership_review_at: string }> {
    return request<{ id: string; last_ownership_review_at: string }>(
      `/api/admin/automations/${encodeURIComponent(id)}/ownership-review`,
      { method: "POST" },
    );
  },
  async getAutomationOwnershipHealth(): Promise<{
    rules: OwnershipHealthRow[];
  }> {
    return request<{ rules: OwnershipHealthRow[] }>(`/api/admin/automations/ownership-health`);
  },
  async sweepAutomationOwnership(): Promise<OwnershipSweepResult> {
    return request<OwnershipSweepResult>(`/api/admin/automations/sweep-ownership`, {
      method: "POST",
    });
  },
  // Service Records PR 26 — Drag-to-rebalance.
  async listWorkloadItems(args: {
    user_id: string;
    week_start: string;
    week_end_exclusive: string;
  }): Promise<{ items: WorkloadItem[] }> {
    const qs = new URLSearchParams({
      user_id: args.user_id,
      week_start: args.week_start,
      week_end_exclusive: args.week_end_exclusive,
    });
    return request<{ items: WorkloadItem[] }>(`/api/admin/workload-planner/items?${qs.toString()}`);
  },
  async listWorkloadCandidates(args: {
    exclude_user_id: string;
    item_type?: WorkloadItemType;
    item_id?: string;
  }): Promise<{ candidates: AdvisorCandidate[] | RankedCandidate[] }> {
    const qs = new URLSearchParams({ exclude_user_id: args.exclude_user_id });
    if (args.item_type) qs.set("item_type", args.item_type);
    if (args.item_id) qs.set("item_id", args.item_id);
    return request<{ candidates: AdvisorCandidate[] | RankedCandidate[] }>(
      `/api/admin/workload-planner/candidates?${qs.toString()}`,
    );
  },
  async reassignWorkloadItem(args: {
    item_type: WorkloadItemType;
    item_id: string;
    to_user_id: string;
  }): Promise<ReassignResult> {
    return request<ReassignResult>(`/api/admin/workload-planner/reassign`, {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async getHouseholdCompanyDetail(
    householdId: string,
    companySlug: string,
  ): Promise<HouseholdCompanyDetailResponse> {
    return request<HouseholdCompanyDetailResponse>(
      `/api/households/${encodeURIComponent(householdId)}/companies/${encodeURIComponent(companySlug)}`,
    );
  },

  // Set the current advisor for a household × company. Writes to BOTH
  // household_company_relationships.current_advisor_user_id and the
  // active service_records' current_advisor_user_id. `null` clears
  // the assignment.
  async setHouseholdCompanyAdvisor(
    householdId: string,
    companySlug: string,
    advisorUserId: string | null,
  ): Promise<{
    household_company_relationship_id: string;
    current_advisor_user_id: string | null;
    service_records_updated: number;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/companies/${encodeURIComponent(companySlug)}/advisor`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ advisor_user_id: advisorUserId }),
      },
    );
  },

  // Set (or clear, with null) the per-company client-since date on a
  // household × company relationship. Nate feedback 2026-07-02 — the
  // field previously had no write path anywhere in the product.
  async setHouseholdCompanyClientSince(
    householdId: string,
    companySlug: string,
    clientSince: string | null,
  ): Promise<{
    household_company_relationship_id: string;
    client_since_this_company: string | null;
    previous: string | null;
    changed: boolean;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/companies/${encodeURIComponent(companySlug)}/client-since`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_since: clientSince }),
      },
    );
  },

  // "Not a client here" (Grant 2026-07-23, the Mike Brandt case): clear
  // a false-active AC-migration artifact — active_client on a recurring
  // company with zero service records. Server verifies eligibility
  // against the same predicate as the admin bulk sweep; 409s when the
  // pair has real product history.
  async markNotAClient(
    householdId: string,
    companySlug: string,
    reason?: string,
  ): Promise<{
    household_company_relationship_id: string;
    previous_state: "active_client";
    new_state: "prospect" | "engaged_lost";
    changed: boolean;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/companies/${encodeURIComponent(companySlug)}/not-a-client`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reason ? { reason } : {}),
      },
    );
  },

  // Undo for markNotAClient. Guarded server-side (no 60s window): only
  // restores a pair still in the post-fix zero-service-record shape.
  async undoNotAClient(
    householdId: string,
    companySlug: string,
  ): Promise<{
    household_company_relationship_id: string;
    previous_state: "prospect" | "engaged_lost";
    new_state: "active_client";
    changed: boolean;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/companies/${encodeURIComponent(companySlug)}/not-a-client/undo`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      },
    );
  },

  async listHouseholds(params: HouseholdListParams = {}): Promise<HouseholdListResponse> {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.sort) qs.set("sort", params.sort);
    if (params.order) qs.set("order", params.order);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    if (params.archived) qs.set("archived", "true");
    if (params.includeQuarantined) qs.set("include_quarantined", "true");
    if (params.company) qs.set("company", params.company);
    if (params.advisor) qs.set("advisor", params.advisor);
    for (const condition of params.conditions ?? []) {
      qs.append("f", serializeCondition(condition));
    }
    const query = qs.toString();
    return request<HouseholdListResponse>(`/api/households${query ? `?${query}` : ""}`, {
      ...(params.signal ? { signal: params.signal } : {}),
    });
  },

  // The people on a "might be the same person" cleanup card (2026-10-02).
  async getCleanupIssuePeople(issueId: string): Promise<{ people: DuplicatePerson[] }> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/people`);
  },

  async getCleanupIssueHouseholds(issueId: string): Promise<{ households: DuplicateHousehold[] }> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/households`);
  },

  async getCleanupIssueMissingDocuments(issueId: string): Promise<MissingDocumentsState> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/missing-documents`);
  },

  async markCleanupDocumentNotNeeded(
    issueId: string,
    body: { definition_id: string; reason: string },
  ): Promise<MissingDocumentsState & { waiver_id: string; resolved: boolean }> {
    return request(`/api/cleanup/issues/${encodeURIComponent(issueId)}/missing-documents/not-needed`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async withdrawCleanupDocumentNotNeeded(issueId: string, waiverId: string): Promise<MissingDocumentsState> {
    return request(
      `/api/cleanup/issues/${encodeURIComponent(issueId)}/missing-documents/not-needed/${encodeURIComponent(waiverId)}/withdraw`,
      { method: "POST" },
    );
  },

  async getContactDetail(id: string): Promise<ContactDetailResponse> {
    return request<ContactDetailResponse>(`/api/contacts/${encodeURIComponent(id)}/detail`);
  },

  // Cross-household connections (migration 0160). Creates the edge
  // "otherContactId is the {relationshipType} of contactId" — i.e. the
  // picked person relates to the contact whose page we're on.
  async createContactConnection(
    contactId: string,
    body: {
      other_contact_id: string;
      relationship_type: ContactRelationshipType;
      note?: string | null;
    },
  ): Promise<{ id: string }> {
    return request<{ id: string }>(`/api/contacts/${encodeURIComponent(contactId)}/connections`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async deleteContactConnection(contactId: string, connectionId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/contacts/${encodeURIComponent(contactId)}/connections/${encodeURIComponent(connectionId)}`,
      { method: "DELETE" },
    );
  },

  // Any employee. Sends only the fields that actually changed (callers
  // compute the diff). Returns the updated contact + the number of
  // fields the server applied — useful for surfacing "saved 3 changes"
  // toasts vs no-op saves.
  //
  // `expectedVersion` is the contact's version_number when the form was
  // opened. When sent, a save on top of someone else's newer save is
  // refused with 409 stale_record (carrying latest_snapshot) instead of
  // silently overwriting it. Callers that edit a field from a form
  // should always send it.
  async updateContact(
    id: string,
    patch: Partial<EditableContactFields>,
    expectedVersion?: number,
  ): Promise<UpdateContactResponse> {
    const body =
      typeof expectedVersion === "number"
        ? { ...patch, expected_version_number: expectedVersion }
        : patch;
    return request<UpdateContactResponse>(`/api/contacts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  // "Communicates via" (2026-09-04 Medicare edge case) — a dedicated
  // pair of endpoints, not part of updateContact's patch: the field
  // lives in a side table (contacts is at D1's 100-column cap) with its
  // own DB-backed validation (self-reference, chaining, target must be a
  // live person). A 400 with error "invalid_communicates_via" comes back
  // on a bad pick; the message is written to be shown to the user as-is.
  async setContactCommunicatesVia(
    id: string,
    input: { via_contact_id: string; note?: string | null },
  ): Promise<{ contact: SafeContact }> {
    return request<{ contact: SafeContact }>(
      `/api/contacts/${encodeURIComponent(id)}/communicates-via`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  async clearContactCommunicatesVia(id: string): Promise<{ contact: SafeContact }> {
    return request<{ contact: SafeContact }>(
      `/api/contacts/${encodeURIComponent(id)}/communicates-via`,
      { method: "DELETE" },
    );
  },

  // Wave D.1 (April 2026): dedicated narrow endpoint for the navy
  // What Matters Most banner edit. Any authenticated employee can
  // call this — it's an advisor-judgment field, not admin-gated.
  // Empty string clears the field.
  async updateContactWhatMattersMost(
    id: string,
    nextValue: string | null,
  ): Promise<{ contact: SafeContact; changes_count: number }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/what-matters-most`, {
      method: "PATCH",
      body: JSON.stringify({ what_matters_most: nextValue }),
    });
  },

  // Recent audit_log rows scoped to one contact, newest-first. Read
  // access is requireAuth (any authenticated employee), matching the
  // contact detail bundle. Powers the Change History panel.
  async getContactHistory(id: string): Promise<ContactHistoryResponse> {
    return request<ContactHistoryResponse>(`/api/contacts/${encodeURIComponent(id)}/history`);
  },

  // P2b — "was this record's name overwritten and now collides with a
  // household-mate?" Returns { overwrite: null } when there's nothing to flag.
  async checkContactIdentityOverwrite(id: string): Promise<{
    overwrite: null | {
      from_name: string;
      to_name: string;
      changed_at: string;
      target_version_id: string;
      colliding_member_name: string;
    };
  }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/identity-overwrite-check`);
  },

  // system_admin-gated. Same per-field-delta pattern as updateContact.
  // Returns 422 with `error: "display_name_required"` if the patch
  // would clear display_name (NOT NULL in schema).
  async updateHousehold(
    id: string,
    // expected_version_number is optional: when sent, a concurrent edit
    // returns 409 stale_record instead of silently overwriting it.
    patch: Partial<EditableHouseholdFields> & { expected_version_number?: number },
  ): Promise<UpdateHouseholdResponse> {
    return request<UpdateHouseholdResponse>(`/api/households/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  // Jake 2026-10-16 — copy the household's mailing address onto the members
  // the user ticked; the returned bulk_action_id is the undo handle.
  async applyAddressToMembers(
    householdId: string,
    contactIds: string[],
  ): Promise<{
    ok: true;
    bulk_action_id: string;
    updated: Array<{ contact_id: string }>;
    skipped: Array<{ contact_id: string; reason: string }>;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/apply-address-to-members`, {
      method: "POST",
      body: JSON.stringify({ contact_ids: contactIds }),
    });
  },
  async undoApplyAddressToMembers(
    householdId: string,
    bulkActionId: string,
  ): Promise<{ ok: true; restored: number }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/apply-address-to-members/${encodeURIComponent(bulkActionId)}/undo`,
      { method: "POST" },
    );
  },

  // Nate 2026-07-09 — additional household addresses + read-only linked
  // deal properties.
  async getHouseholdAddresses(householdId: string): Promise<{
    addresses: HouseholdAddress[];
    linked_properties: LinkedProperty[];
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/addresses`);
  },

  async addHouseholdAddress(
    householdId: string,
    input: HouseholdAddressInput,
  ): Promise<{ ok: true; address: HouseholdAddress }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/addresses`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async updateHouseholdAddress(
    householdId: string,
    addressId: string,
    input: HouseholdAddressInput,
  ): Promise<{ ok: true; address: HouseholdAddress }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/addresses/${encodeURIComponent(addressId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  async deleteHouseholdAddress(householdId: string, addressId: string): Promise<{ ok: true }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/addresses/${encodeURIComponent(addressId)}`,
      { method: "DELETE" },
    );
  },

  // Wave D.1 (April 2026): dedicated narrow endpoint for the navy
  // ─── Contact-identity judgment writes (#2) ───────────────────────
  async patchContactPreferredName(
    id: string,
    preferredName: string | null,
  ): Promise<{ changes_count: number }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/judgment/preferred-name`, {
      method: "PATCH",
      body: JSON.stringify({ preferred_name: preferredName }),
    });
  },
  async patchContactMaritalStatus(
    id: string,
    maritalStatus: string,
  ): Promise<{ changes_count: number }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/judgment/marital-status`, {
      method: "PATCH",
      body: JSON.stringify({ marital_status: maritalStatus }),
    });
  },
  async patchContactReferralSource(
    id: string,
    referralSourceType: string,
    internalReferredFromCompanyId?: string | null,
  ): Promise<{ changes_count: number }> {
    const body: Record<string, unknown> = {
      referral_source_type: referralSourceType,
    };
    // Only send the company field when the caller actually passed one —
    // matches the API's "absence means don't touch it" contract.
    if (internalReferredFromCompanyId !== undefined) {
      body.internal_referred_from_company_id = internalReferredFromCompanyId;
    }
    return request(`/api/contacts/${encodeURIComponent(id)}/judgment/referral-source`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  async judgmentNotSure(issueId: string): Promise<{ ok: boolean; skipped: boolean }> {
    return request(`/api/data-needed/judgment/${encodeURIComponent(issueId)}/not-sure`, {
      method: "POST",
    });
  },
  async judgmentUndoNotSure(issueId: string): Promise<{ ok: boolean; skipped: boolean }> {
    return request(`/api/data-needed/judgment/${encodeURIComponent(issueId)}/not-sure`, {
      method: "DELETE",
    });
  },

  // What Matters Most banner edit on the household page. Any
  // authenticated employee can call.
  async updateHouseholdWhatMattersMost(
    id: string,
    nextValue: string | null,
  ): Promise<{ household: Household; changes_count: number }> {
    return request(`/api/households/${encodeURIComponent(id)}/what-matters-most`, {
      method: "PATCH",
      body: JSON.stringify({ what_matters_most: nextValue }),
    });
  },
  // Record "we don't know this family well enough" — the honest escape
  // hatch that clears the What Matters Most Data Needed item without a
  // fake value. Reversible via clearHouseholdWmmUnknown.
  async markHouseholdWmmUnknown(id: string): Promise<{ household: Household; changed: boolean }> {
    return request(`/api/households/${encodeURIComponent(id)}/what-matters-most/mark-unknown`, {
      method: "POST",
    });
  },
  async clearHouseholdWmmUnknown(id: string): Promise<{ household: Household; changed: boolean }> {
    return request(`/api/households/${encodeURIComponent(id)}/what-matters-most/clear-unknown`, {
      method: "POST",
    });
  },

  // Same shape as getContactHistory — entity-agnostic ChangeHistory
  // component consumes both via { events } responses.
  async getHouseholdHistory(id: string): Promise<HouseholdHistoryResponse> {
    return request<HouseholdHistoryResponse>(`/api/households/${encodeURIComponent(id)}/history`);
  },

  // ─── Archive / restore ────────────────────────────────────────────
  //
  // All four are system_admin-gated. Idempotent: hitting an already-
  // archived (or already-active) record returns 200 with a sentinel
  // flag instead of an error so the UI can display the right state
  // without an extra fetch.
  async archiveContact(
    id: string,
    reason: string,
  ): Promise<{ contact: SafeContact; was_archived: false } | { already_archived: true }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  async restoreContact(
    id: string,
  ): Promise<{ contact: SafeContact; was_restored: true } | { not_archived: true }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/restore`, {
      method: "POST",
    });
  },

  // ─── Mark / unmark deceased ─────────────────────────────────────────
  //
  // requireAuth (any employee) — same reversible "confirm"-tier
  // permission level as archive/restore. Marking sets do_not_email
  // automatically and, when the contact was the household's primary,
  // returns a suggested replacement so the caller can offer a one-click
  // promote via api.updateHousehold({ primary_contact_id }).
  async markContactDeceased(
    id: string,
    input?: { deceased_at?: string; reason?: string },
  ): Promise<
    | {
        contact: SafeContact;
        was_marked: true;
        household_id: string | null;
        was_household_primary: boolean;
        suggested_new_primary: { id: string; name: string } | null;
      }
    | { already_deceased: true }
  > {
    return request(`/api/contacts/${encodeURIComponent(id)}/mark-deceased`, {
      method: "POST",
      body: JSON.stringify(input ?? {}),
    });
  },

  async unmarkContactDeceased(
    id: string,
  ): Promise<{ contact: SafeContact; was_restored: true } | { not_deceased: true }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/unmark-deceased`, { method: "POST" });
  },

  // Bare contact fetch — used by the merge wizard to load the OTHER
  // contact (the one being merged in) given a UUID.
  async findDuplicateCandidatesForContact(id: string): Promise<{
    contact_id: string;
    anchor: {
      email: string | null;
      phone_primary_normalized: string | null;
      legal_first_name: string | null;
      legal_last_name: string | null;
      zip: string | null;
    };
    matches: Array<{
      contact_id: string;
      legal_first_name: string | null;
      legal_last_name: string | null;
      preferred_name: string | null;
      email: string | null;
      phone_primary: string | null;
      household_id: string | null;
      household_display_name: string | null;
      match_signals: Array<"email" | "phone" | "name_zip">;
    }>;
  }> {
    return request(`/api/contacts/${encodeURIComponent(id)}/duplicate-candidates`);
  },

  async getContact(id: string): Promise<{ contact: SafeContact }> {
    return request(`/api/contacts/${encodeURIComponent(id)}`);
  },

  // List merged-away contacts so admins can find ones to unmerge.
  async listMergedContacts(limit = 50, offset = 0): Promise<MergedContactsResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return request(`/api/admin/data-hygiene/merged-contacts?${qs.toString()}`);
  },

  // Partial unmerge — restores a merged-away contact to active state
  // and reverses field overrides on the primary. FK rewrites stay.
  async unmergeContact(mergedContactId: string): Promise<UnmergeContactResponse> {
    return request(`/api/contacts/${encodeURIComponent(mergedContactId)}/unmerge`, {
      method: "POST",
    });
  },

  // PR #311 — cluster unmerge. :id is the SURVIVOR contact's id
  // (the primary that absorbed N merged contacts). Reverses every
  // pair pointing at this survivor in time-reverse order.
  async unmergeCluster(primaryContactId: string): Promise<{
    primary_contact_id: string;
    restored: Array<{
      restored_contact_id: string;
      reverted_fields: Array<{
        field_name: string;
        reverted_to_value: unknown;
        was_value: unknown;
      }>;
    }>;
    failed: Array<{ contact_id: string; error: string }>;
  }> {
    return request(`/api/contacts/${encodeURIComponent(primaryContactId)}/unmerge-cluster`, {
      method: "POST",
    });
  },

  // Merge :mergedId INTO :primaryId. The primary's id stays; the
  // merged contact is sidelined (merged_into_contact_id +
  // deleted_at). Per-field overrides control which side's value
  // wins on each editable field.
  async mergeContacts(
    primaryId: string,
    mergedId: string,
    body: {
      field_overrides: Record<string, "primary" | "merged">;
      /**
       * Cross-column promotions for the "Keep both" affordance.
       * When `email: true`, the backend copies merged.email →
       * primary.secondary_email. When `phone: true`, the backend
       * copies merged.phone_primary → primary.phone_secondary.
       * Subject to the same blank-protection guard as field_overrides.
       */
      secondary_promotions?: { email?: boolean; phone?: boolean };
      reason: string;
      /**
       * Phase D2: canonical reason code from change_reason_codes
       * (filtered to action="merge"). Optional for back-compat with
       * older callers; the merge wizard sends it on every merge.
       */
      reason_code?: string;
    },
  ): Promise<{
    primary_contact: SafeContact;
    merged_contact_id: string;
    applied_field_overrides: Array<{
      field_name: string;
      from_value: unknown;
      to_value: unknown;
    }>;
    /**
     * Field overrides the caller asked for that were silently dropped
     * because the merged-side value was blank (null / "" / undefined)
     * and the primary already had a populated value. Per the
     * blank-override protection guard in shared/db/contact-merge.ts.
     * The merge wizard surfaces this as "Protected from blank: …" so
     * the admin sees what was preserved.
     */
    skipped_blank_overrides: Array<{
      field_name: string;
      from_value: unknown;
      to_value: unknown;
    }>;
    counts: Record<string, number>;
  }> {
    return request(
      `/api/contacts/${encodeURIComponent(primaryId)}/merge-from/${encodeURIComponent(mergedId)}`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  async archiveHousehold(
    id: string,
    reason: string,
    opts?: { allowActiveAttachments?: boolean },
  ): Promise<{ household: Household; was_archived: false } | { already_archived: true }> {
    // On a 409 the server returns { error: "active_attachments",
    // attachments }. request() throws an ApiError whose `.data` carries
    // that body, so the caller can render the escalate UI. Passing
    // allowActiveAttachments=true is the deliberate (confirm + audited)
    // override.
    return request(`/api/households/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      body: JSON.stringify({
        reason,
        ...(opts?.allowActiveAttachments ? { allow_active_attachments: true } : {}),
      }),
    });
  },

  async restoreHousehold(
    id: string,
  ): Promise<{ household: Household; was_restored: true } | { not_archived: true }> {
    return request(`/api/households/${encodeURIComponent(id)}/restore`, {
      method: "POST",
    });
  },

  // Clear the migration's fuzzy-match quarantine flag on a household.
  // Used by the data-hygiene action bar's "Clear flag — it's fine"
  // path. system_admin-gated; idempotent.
  async clearHouseholdQuarantine(
    id: string,
  ): Promise<{ household: Household; was_cleared: true } | { not_quarantined: true }> {
    return request(`/api/households/${encodeURIComponent(id)}/clear-quarantine`, {
      method: "POST",
    });
  },

  // Service-record detail — PR3 of the data-hygiene revamp.
  // GET /api/service-records/:id returns the SR plus its joined
  // company / household / advisor / finalized-by display data plus
  // active rhythm instances linked to this SR.
  async getServiceRecordDetail(id: string): Promise<ServiceRecordDetailResponse> {
    return request<ServiceRecordDetailResponse>(`/api/service-records/${encodeURIComponent(id)}`);
  },

  // PATCH /api/service-records/:id — advisor-facing lifecycle write.
  // Sets status (active / inactive / replaced / past) + optional
  // termination date + optional change reason. Company-scoped
  // server-side. Backs the "Change status" control on the SR detail
  // page and the household service portfolio.
  async setServiceRecordStatus(
    id: string,
    input: {
      status: "active" | "inactive" | "replaced" | "past";
      termination_date?: string | null;
      /** The "deal closed on this day" date (migration 0195). Same
       *  tri-state as termination_date — omit to leave, null/"" to clear,
       *  ISO string to set. Powers the Realty "mark closed" control. */
      closed_date?: string | null;
      reason_code?: string;
      reason_text?: string;
      /** Re-submit with this after a 409 open_work_on_record to end the
       *  record anyway. The server refuses to assume it (a stale client
       *  must not be able to skip the warning), so it is only ever set by
       *  a human who has read what is still open. */
      acknowledge_open_work?: boolean;
    },
  ): Promise<{
    id: string;
    status: string;
    termination_date: string | null;
    closed_date: string | null;
    previous_status: string;
    changed: boolean;
    /** Care plans (Renewal Plan / Rate Watch cycles) ended alongside a
     *  move away from 'active'. */
    rhythms_ended: number;
    /** The household's per-company standing flipped by this change —
     *  e.g. the last active Planning case ended → Past Client. null
     *  when the standing already matched (the common case). */
    relationship_state_change: {
      previous_state: string | null;
      new_state: string;
      company_display_name: string;
    } | null;
  }> {
    return request(`/api/service-records/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  },

  // ── People on a record (0232, Grant 2026-08-03) ──
  // "Whose record is this" — borrower / insured / account holder /
  // seller. Company-scoped server-side; the last person can never be
  // removed (a record cannot exist without a person).
  async addServiceRecordPerson(
    serviceRecordId: string,
    input: { contact_id: string; make_primary?: boolean },
  ): Promise<{ ok: true; person: ServiceRecordPerson }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/people`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async removeServiceRecordPerson(
    serviceRecordId: string,
    contactId: string,
  ): Promise<{ ok: true; new_primary_contact_id: string | null }> {
    return request(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/people/${encodeURIComponent(contactId)}`,
      { method: "DELETE" },
    );
  },

  async setServiceRecordPrimaryPerson(
    serviceRecordId: string,
    contactId: string,
  ): Promise<{ ok: true }> {
    return request(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/people/${encodeURIComponent(contactId)}/primary`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  // A human confirms a system-attributed ('defaulted'/'backfill'/'import')
  // person is right (0252) — flips provenance to 'user' so the cleanup
  // confirm-lane stops asking.
  async confirmServiceRecordPerson(
    serviceRecordId: string,
    contactId: string,
  ): Promise<{ ok: true; already_confirmed: boolean }> {
    return request(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/people/${encodeURIComponent(contactId)}/confirm`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  // ─── Another household on the same purchase (2026-09-23) ───────────
  async listSharedPurchases(householdId: string): Promise<{ purchases: SharedPurchase[] }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/shared-purchases`);
  },

  async listRecordCoHouseholds(
    kind: CoHouseholdRecordKind,
    recordId: string,
  ): Promise<{ households: RecordCoHousehold[] }> {
    return request(coHouseholdBase(kind, recordId));
  },

  async addRecordCoHousehold(
    kind: CoHouseholdRecordKind,
    recordId: string,
    householdId: string,
  ): Promise<{ ok: true; households: RecordCoHousehold[] }> {
    return request(coHouseholdBase(kind, recordId), {
      method: "POST",
      body: JSON.stringify({ household_id: householdId }),
    });
  },

  async removeRecordCoHousehold(
    kind: CoHouseholdRecordKind,
    recordId: string,
    householdId: string,
  ): Promise<{ ok: true; households: RecordCoHousehold[] }> {
    return request(`${coHouseholdBase(kind, recordId)}/${encodeURIComponent(householdId)}`, {
      method: "DELETE",
    });
  },

  // ─── Whose record is this — workflow records (0252) ────────────────
  // Same verbs as the service-record people methods above, for
  // Opportunities and (unlinked) Processing records. kind picks the
  // route family: /api/opportunities/:id/people… or
  // /api/transactions/:id/people….
  async addWorkflowRecordPerson(
    kind: "opportunity" | "transaction",
    recordId: string,
    input: { contact_id: string; make_primary?: boolean },
  ): Promise<{ ok: true; person: ServiceRecordPerson }> {
    return request(
      `/api/${kind === "opportunity" ? "opportunities" : "transactions"}/${encodeURIComponent(recordId)}/people`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  async removeWorkflowRecordPerson(
    kind: "opportunity" | "transaction",
    recordId: string,
    contactId: string,
  ): Promise<{ ok: true; new_primary_contact_id: string | null }> {
    return request(
      `/api/${kind === "opportunity" ? "opportunities" : "transactions"}/${encodeURIComponent(recordId)}/people/${encodeURIComponent(contactId)}`,
      { method: "DELETE" },
    );
  },

  async setWorkflowRecordPrimaryPerson(
    kind: "opportunity" | "transaction",
    recordId: string,
    contactId: string,
  ): Promise<{ ok: true }> {
    return request(
      `/api/${kind === "opportunity" ? "opportunities" : "transactions"}/${encodeURIComponent(recordId)}/people/${encodeURIComponent(contactId)}/primary`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  async confirmWorkflowRecordPerson(
    kind: "opportunity" | "transaction",
    recordId: string,
    contactId: string,
  ): Promise<{ ok: true; already_confirmed: boolean }> {
    return request(
      `/api/${kind === "opportunity" ? "opportunities" : "transactions"}/${encodeURIComponent(recordId)}/people/${encodeURIComponent(contactId)}/confirm`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  // POST /api/service-records/:id/archive — soft-remove a mis-created or
  // junk service record. Reversible via restoreServiceRecord (undo toast)
  // and from History. Company-scoped server-side. Reason optional.
  async archiveServiceRecord(
    id: string,
    input?: { reason_code?: string; reason_text?: string },
  ): Promise<{ id: string; archived: boolean; already_archived: boolean }> {
    return request(`/api/service-records/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input ?? {}),
    });
  },

  // POST /api/service-records/:id/restore — undo an archive.
  async restoreServiceRecord(
    id: string,
  ): Promise<{ id: string; restored: boolean; not_archived: boolean }> {
    return request(`/api/service-records/${encodeURIComponent(id)}/restore`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
  },

  // PATCH /api/service-records/:id/planning-case — product-detail write
  // for Planning cases (carrier, policy #, plan name, plan tier / life
  // type, dates, premiums, death benefit, ACA subsidy). Company-scoped
  // server-side; creates the planning_cases subtype row when the SR
  // lacks one.
  async updatePlanningCase(
    serviceRecordId: string,
    patch: Partial<
      Record<
        | "carrier_name"
        | "policy_number"
        | "product_name"
        | "plan_tier"
        | "life_type"
        | "effective_date"
        | "expiration_date"
        | "annual_premium"
        | "monthly_premium"
        | "death_benefit"
        | "cash_value"
        | "has_subsidy"
        | "aca_income"
        | "subsidy_amount",
        string | number | null
      >
    >,
  ): Promise<{
    id: string;
    planning_case_id: string;
    changed: boolean;
    created: boolean;
  }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/planning-case`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
  },

  // PATCH /api/transactions/:id/captured-product-facts — S18. What Ryan
  // already knows about the product before it exists (carrier, policy #,
  // plan name, requested effective date), captured on the Processing
  // record itself. Company-scoped server-side.
  async updateTransactionCapturedFacts(
    transactionId: string,
    patch: Partial<
      Record<
        | "captured_carrier_vendor_id"
        | "captured_carrier_name"
        | "captured_policy_number"
        | "captured_product_name"
        | "captured_effective_date"
        // ACA / health / Medicare application facts + estate execution
        // facts (migration 20260829004952).
        | "captured_plan_tier"
        | "captured_monthly_premium"
        | "captured_annual_premium"
        | "captured_has_subsidy"
        | "captured_aca_income"
        | "captured_subsidy_amount"
        | "captured_estate_plan_type"
        | "captured_signed_at"
        | "captured_attorney_name"
        | "captured_estate_drafting_channel"
        | "captured_enrollment_service",
        string | number | null
      >
    >,
  ): Promise<{ id: string; changed: boolean }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/captured-product-facts`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      },
    );
  },

  // Migration cleanup progress — PR5 of the data-hygiene revamp.
  // Returns per-metric baseline + current counts plus 7-day pace
  // and ETA for the top progress card on the data-hygiene dashboard.
  async getMigrationCleanupProgress(): Promise<MigrationCleanupProgressResponse> {
    return request<MigrationCleanupProgressResponse>("/api/admin/data-hygiene/cleanup-progress");
  },

  // POST /api/admin/data-hygiene/cleanup-progress/snapshot — captures
  // current counts as the baseline. system_admin-gated, idempotent
  // (UPSERT on metric_slug). Optional notes string for the audit trail.
  async snapshotMigrationCleanupBaseline(
    notes: string | null,
  ): Promise<{ baselines: MigrationCleanupMetric[] }> {
    return request<{ baselines: MigrationCleanupMetric[] }>(
      "/api/admin/data-hygiene/cleanup-progress/snapshot",
      {
        method: "POST",
        body: JSON.stringify({ notes }),
      },
    );
  },

  // ─── Household membership ─────────────────────────────────────────
  //
  // Two endpoints, both system_admin-gated:
  //   POST   /api/households/:id/members              — add a member
  //   DELETE /api/households/:id/members/:contactId   — remove a member
  //
  // Add has two modes: create a brand-new contact, or move an existing
  // contact in. Remove just clears household_id + household_role on the
  // contact (and clears the household's primary_contact_id if they were
  // the primary).
  async addHouseholdMemberCreate(
    householdId: string,
    body: {
      role: "primary" | "spouse" | "child" | "other" | "member";
      contact: {
        legal_first_name?: string | null;
        legal_middle_name?: string | null;
        legal_last_name?: string | null;
        preferred_name?: string | null;
        email?: string | null;
        secondary_email?: string | null;
        phone_primary?: string | null;
        phone_secondary?: string | null;
        birthday?: string | null;
        // Driver's license (quoting tier). Encrypted server-side via the
        // sensitive-field cipher; never stored or returned in plaintext.
        drivers_license?: string | null;
        drivers_license_state?: string | null;
      };
    },
  ): Promise<{
    contact: SafeContact;
    mode: "create_new";
    // Present + true only when a driver's license was submitted but the
    // caller lacked sensitive-field access for this household. The member
    // was still created; the DL was not saved.
    drivers_license_denied?: boolean;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/members`, {
      method: "POST",
      body: JSON.stringify({ mode: "create_new", ...body }),
    });
  },

  async addHouseholdMemberMove(
    householdId: string,
    body: {
      contact_id: string;
      role: "primary" | "spouse" | "child" | "other" | "member";
    },
  ): Promise<{
    contact: SafeContact;
    mode: "move_existing";
    vacated_primary_household_id: string | null;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/members`, {
      method: "POST",
      body: JSON.stringify({ mode: "move_existing", ...body }),
    });
  },

  async removeHouseholdMember(
    householdId: string,
    contactId: string,
  ): Promise<{
    contact: SafeContact;
    vacated_primary: boolean;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/members/${encodeURIComponent(contactId)}`,
      { method: "DELETE" },
    );
  },

  // Set ONE member's role in place (Donna, 2026-07-31 — the Cleanup
  // drawer told her to "set its role to spouse" and no such control
  // existed anywhere in the OS). Picking "primary" behaves exactly like
  // "Make primary": the previous primary is demoted for you, and comes
  // back in demoted_contact_ids so the caller can say so out loud.
  //
  // Throws ApiError 422 `is_designated_primary` when the target IS the
  // household's primary — demoting them would leave the household with
  // none. The message names the way forward.
  async setHouseholdMemberRole(
    householdId: string,
    contactId: string,
    role: HouseholdRole,
  ): Promise<{
    contact: SafeContact;
    changed: boolean;
    from_role: string | null;
    to_role: HouseholdRole;
    demoted_contact_ids: string[];
    promoted: boolean;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/members/${encodeURIComponent(contactId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ role }),
      },
    );
  },

  // One-step "remove into their own household" (divorce case): the
  // contact leaves this household and lands in a fresh single-member
  // household in the same write — no orphan contact.
  async splitOutHouseholdMember(
    householdId: string,
    contactId: string,
    reason?: string,
  ): Promise<{
    new_household_id: string;
    new_household_name: string;
    /** Parent/child edges written back to the household they left, so the
     *  two stay visibly one family (Joy + Brandon, 2026-08-19). */
    family_links_created?: number;
    /** Records that NAME the person and are still on the old household —
     *  offered to follow them. Nothing has moved. */
    movable_records?: SplitMovableRecord[];
    /** The split's bulk action. Records that follow pass this back so
     *  they undo WITH the split rather than one at a time. */
    bulk_action_id?: string;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/members/${encodeURIComponent(contactId)}/split-out`,
      {
        method: "POST",
        body: JSON.stringify(reason ? { reason } : {}),
      },
    );
  },

  // "Which records still on this household name any of these people?"
  // Read-only. Built for the Reorganize-members flow, which moves several
  // people at once (unlike split-out, which handles this inline for its
  // one mover) — see shared/db/household-split-records.ts.
  async listMovableRecordsForContacts(
    householdId: string,
    contactIds: string[],
  ): Promise<{ movable_records: SplitMovableRecord[] }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/movable-records`, {
      method: "POST",
      body: JSON.stringify({ contact_ids: contactIds }),
    });
  },

  // "Was this household created by a split, and what would undoing it
  // do?" Returns split_origin: null for the vast majority of households.
  async getHouseholdSplitOrigin(
    householdId: string,
  ): Promise<{ split_origin: SplitOrigin | null; reason?: string }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/split-origin`);
  },

  // Reverse a household split: the people go back with their original
  // roles, anything that followed them comes back, and the household the
  // split created is dissolved.
  async undoHouseholdSplit(
    householdId: string,
    reason?: string,
  ): Promise<{
    source_household_id: string;
    dissolved_household_id: string;
    moved_back: Record<string, number>;
    roles_restored: number;
    records_returned: number;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/undo-split`, {
      method: "POST",
      body: JSON.stringify(reason ? { reason } : {}),
    });
  },

  // Data Quality Dashboard — aggregate counts for /admin/data-hygiene.
  // Admin-gated (system_admin OR compliance).
  async getDataHygieneSummary(): Promise<DataHygieneSummary> {
    return request("/api/admin/data-hygiene/summary");
  },

  // Spouse review queue — Bug B drill-down. Households with one
  // person contact, oldest first by default.
  async listSpouseReviewQueue(
    limit = 50,
    offset = 0,
    sort: SpouseReviewSort = "oldest_first",
  ): Promise<SpouseReviewQueueResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
    });
    return request(`/api/admin/data-hygiene/queues/spouse-review?${qs.toString()}`);
  },

  // Boolean quarantines queue — households flagged is_quarantined=1
  // by migration. Hidden from default list views.
  async listBooleanQuarantineQueue(
    limit = 50,
    offset = 0,
    sort: BooleanQuarantineSort = "oldest_first",
  ): Promise<BooleanQuarantineQueueResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
    });
    return request(`/api/admin/data-hygiene/queues/boolean-quarantines?${qs.toString()}`);
  },

  // Vendor-lookalikes queue — person contacts whose name suggests
  // they're really a business entity. Each row has a "Retag as
  // vendor" action that flips contact_type via the existing PATCH
  // /api/contacts/:id endpoint.
  async listVendorLookalikesQueue(
    limit = 50,
    offset = 0,
    sort: VendorLookalikesSort = "oldest_first",
  ): Promise<VendorLookalikeQueueResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
    });
    return request(`/api/admin/data-hygiene/queues/vendor-lookalikes?${qs.toString()}`);
  },

  // Convenience wrapper for the retag action — calls the existing
  // contact PATCH endpoint with just contact_type changed. Returns
  // the updated contact + the audit-recorded change list.
  async retagContactAsVendor(
    contactId: string,
  ): Promise<{ contact: SafeContact; changes: unknown[] }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}`, {
      method: "PATCH",
      body: JSON.stringify({ contact_type: "vendor" }),
    });
  },

  // Duplicate candidates queue — pairs matched on email / phone /
  // (name + zip + different household). Sort defaults to email > phone
  // > name_zip, then match_value. Filter by match_type to focus the
  // queue on one category at a time.
  async listDuplicateCandidatesQueue(
    limit = 50,
    offset = 0,
    sort: DuplicateCandidatesSort = "default",
    matchType?: DuplicateMatchType,
  ): Promise<DuplicateCandidatesQueueResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
    });
    if (matchType) qs.set("match_type", matchType);
    return request(`/api/admin/data-hygiene/queues/duplicate-candidates?${qs.toString()}`);
  },

  // Phase B2 (May 2026 — data hygiene gap recovery). Pre-save dup
  // detection for the new-contact form. Hits the same signals as the
  // duplicate-candidates queue but takes raw fields so the form can
  // call BEFORE save.
  async duplicatePrecheck(input: {
    email?: string | null;
    phone?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    zip?: string | null;
  }): Promise<{
    matches: DuplicatePrecheckMatch[];
    signals_run: DuplicateMatchType[];
  }> {
    const qs = new URLSearchParams();
    if (input.email) qs.set("email", input.email);
    if (input.phone) qs.set("phone", input.phone);
    if (input.first_name) qs.set("first_name", input.first_name);
    if (input.last_name) qs.set("last_name", input.last_name);
    if (input.zip) qs.set("zip", input.zip);
    const query = qs.toString();
    return request(`/api/contacts/duplicate-precheck${query ? `?${query}` : ""}`);
  },

  // ─── Ambassador referral intake (Ambassador program PR 2) ──────────
  //
  // The one working surface the ambassador role has. Every response is
  // deliberately THIN — the precheck answers "do we already know this
  // person / do they already work with Stewardship" and nothing more
  // (no email/phone/household of the matched contact; which companies
  // serve them is PR 3's disclosure).
  async ambassadorPrecheck(input: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
  }): Promise<{ match: AmbassadorPrecheckMatch | null }> {
    return request("/api/ambassador/referrals/precheck", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async ambassadorCreateReferral(input: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    existing_contact_id?: string | null;
    /** Which company they need (migration 20260814223351). Defaults server-side to realty. */
    company?: string | null;
    need?: string | null;
    timeline?: string | null;
    area?: string | null;
    price_range?: string | null;
    situation?: string | null;
    lender_status?: string | null;
    how_known?: string | null;
    note?: string | null;
  }): Promise<{
    referral_id: string | null;
    contact_name: string;
    contact_existed: boolean;
    company: string | null;
    company_label: string | null;
    assigned_to_advisor: boolean;
  }> {
    return request("/api/ambassador/referrals", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async ambassadorListReferrals(): Promise<{
    referrals: AmbassadorReferralRow[];
  }> {
    return request("/api/ambassador/referrals");
  },

  // ─── The referral fee (Ambassador program PR 6) ────────────────────

  /** The ambassador's OWN earnings. Their fees, their totals, nothing else. */
  async ambassadorEarnings(): Promise<AmbassadorEarningsResponse> {
    return request("/api/ambassador/earnings");
  },

  /** The admin ledger: every fee, plus totals summed over the whole table. */
  async adminAmbassadorFees(status?: "owed" | "paid" | "void"): Promise<{
    fees: AmbassadorFeeAdminRow[];
    totals: {
      owed_count: number;
      owed_cents: number;
      paid_count: number;
      paid_cents: number;
    };
    settings_in_force: AmbassadorProgramSettings | null;
    /** Every company's live policy (migration 20260814223351). */
    settings_by_company: AmbassadorProgramSettings[];
    /**
     * Won referrals into a company whose rate is not decided yet. These
     * owe something and deliberately carry NO amount — inventing one
     * would be a wrong number on a compensation screen.
     */
    awaiting_rate: AmbassadorReferralAwaitingRate[];
  }> {
    return request(`/api/admin/ambassador-fees${status ? `?status=${status}` : ""}`);
  },

  /** Re-scan closed Realty deals for fees. Idempotent — safe to press twice. */
  async adminResolveAmbassadorFees(): Promise<{
    created: number;
    updated: number;
    skipped: Array<{ referral_id: string; realty_entity_id: string; reason: string }>;
  }> {
    return request("/api/admin/ambassador-fees/resolve", { method: "POST" });
  },

  async adminRecordTitlePayment(feeId: string): Promise<{ id: string }> {
    return request(`/api/admin/ambassador-fees/${encodeURIComponent(feeId)}/title-payment`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  async adminPayAmbassadorFee(
    feeId: string,
    input: { method?: string | null; reference?: string | null } = {},
  ): Promise<{ id: string; status: string }> {
    return request(`/api/admin/ambassador-fees/${encodeURIComponent(feeId)}/pay`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async adminVoidAmbassadorFee(
    feeId: string,
    reason: string,
  ): Promise<{ id: string; status: string }> {
    return request(`/api/admin/ambassador-fees/${encodeURIComponent(feeId)}/void`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  async adminAmbassadorSettings(): Promise<{
    in_force: AmbassadorProgramSettings | null;
    history: AmbassadorProgramSettings[];
  }> {
    return request("/api/admin/ambassador-settings");
  },

  /**
   * Change the policy. The server CLOSES the current window and OPENS a
   * new one — it never edits the live row, so fees already earned keep
   * the terms they were earned under.
   */
  async adminSaveAmbassadorSettings(input: {
    rate_bps: number;
    fee_basis: "office_gross" | "office_net";
    first_deal_only: boolean;
    exclude_existing_realty_client: boolean;
    require_title_payment_before_paid: boolean;
    effective_from?: string;
    notes?: string | null;
  }): Promise<{ in_force: AmbassadorProgramSettings | null }> {
    return request("/api/admin/ambassador-settings", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  /** Null for the overwhelming majority of deals — no panel renders. */
  async serviceRecordAmbassadorFee(
    serviceRecordId: string,
  ): Promise<{ ambassador_fee: ServiceRecordAmbassadorFee | null }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/ambassador-fee`);
  },

  // ─── Ambassador calendar access (Ambassador program PR 5) ──────────
  //
  // Nate and Joey only, free/busy only, and booking is scoped to one of
  // the caller's OWN referrals (hence referral_id, not household_id —
  // the ambassador never names a household).
  async ambassadorListAdvisors(): Promise<{
    advisors: AmbassadorBookableAdvisor[];
  }> {
    return request("/api/ambassador/advisors");
  },

  async ambassadorAdvisorAvailability(
    userId: string,
    params: { date?: string; days?: number } = {},
  ): Promise<AmbassadorAvailabilityResponse> {
    const qs = new URLSearchParams();
    if (params.date) qs.set("date", params.date);
    if (params.days !== undefined) qs.set("days", String(params.days));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request(`/api/ambassador/advisors/${encodeURIComponent(userId)}/availability${suffix}`);
  },

  async ambassadorBookAppointment(
    userId: string,
    input: {
      referral_id: string;
      kind: "phone" | "video" | "in_office";
      startIso: string;
      endIso: string;
      subject?: string | null;
      notes?: string | null;
    },
  ): Promise<{
    event_id: string;
    web_link: string | null;
    online_meeting_url: string | null;
    start_iso: string;
    end_iso: string;
    subject: string;
    advisor: AmbassadorBookableAdvisor;
    referral_id: string;
  }> {
    return request(`/api/ambassador/advisors/${encodeURIComponent(userId)}/appointments`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Nate 2026-07-11 — free-text contact search backing the reusable
  // ContactPicker (replaces raw-UUID text inputs on the household page).
  // Explicitly safe row shape — no *_encrypted field, ever.
  async searchContacts(q: string, limit = 20): Promise<{ contacts: ContactPickerResult[] }> {
    const qs = new URLSearchParams({ q });
    if (limit) qs.set("limit", String(limit));
    return request(`/api/contacts/search?${qs.toString()}`);
  },

  // Phase C2 (May 2026 — data hygiene gap recovery). Three structural-
  // integrity queues whose backends shipped in PR 13 / wave 13; UI
  // pages never built. All three lists return contact / household
  // rows with deep-link metadata.
  async listOrphanHouseholds(
    limit = 50,
    offset = 0,
  ): Promise<{
    total: number;
    items: Array<{
      household_id: string;
      display_name: string;
      contact_count: number;
      updated_at: string;
    }>;
  }> {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    return request(`/api/admin/data-hygiene/queues/orphan-households?${qs.toString()}`);
  },

  async listOrphanContacts(
    limit = 50,
    offset = 0,
  ): Promise<{
    total: number;
    items: Array<{
      contact_id: string;
      legal_first_name: string | null;
      legal_last_name: string | null;
      preferred_name: string | null;
      email: string | null;
      phone_primary: string | null;
      updated_at: string;
    }>;
  }> {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    return request(`/api/admin/data-hygiene/queues/orphan-contacts?${qs.toString()}`);
  },

  async listStaleWhatMattersMost(
    limit = 50,
    offset = 0,
    days = 365,
  ): Promise<{
    total: number;
    threshold_days: number;
    items: Array<{
      contact_id: string;
      legal_first_name: string | null;
      legal_last_name: string | null;
      household_id: string | null;
      household_display_name: string | null;
      what_matters_most_updated_at: string | null;
      days_since_wmm_update: number | null;
    }>;
  }> {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    qs.set("days", String(days));
    return request(`/api/admin/data-hygiene/queues/stale-what-matters-most?${qs.toString()}`);
  },

  // Phase C1 (May 2026 — data hygiene gap recovery). Capitalization-
  // inconsistencies queue. Backend shipped in PR 12 / wave 12; UI never
  // built. Surfaces clusters of contacts with identical lowercased
  // name+ZIP but different stored capitalizations ("Bob Smith" /
  // "BOB SMITH" / "bob smith"). Almost always real duplicates AND
  // mechanically-cleanable (Title Case the name, run merge).
  async listCapitalizationInconsistencies(
    limit = 50,
    offset = 0,
  ): Promise<{
    total: number;
    items: Array<{
      normalized_key: string;
      members: Array<{
        contact_id: string;
        legal_first_name: string | null;
        legal_last_name: string | null;
        zip: string | null;
        household_id: string | null;
        household_display_name: string | null;
      }>;
    }>;
  }> {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    return request(
      `/api/admin/data-hygiene/queues/capitalization-inconsistencies?${qs.toString()}`,
    );
  },

  // Phase D3 (May 2026 — reversibility-first architecture). Reads
  // entity_versions for the History tab on every record. The list
  // call is lightweight (no full snapshot); per-row expansion calls
  // getEntityVersion to fetch the snapshot for diff rendering.
  async listEntityVersions(
    entityType:
      | "contact"
      | "household"
      | "transaction"
      | "opportunity"
      | "service_record"
      | "rhythm_instance",
    entityId: string,
    limit = 50,
    offset = 0,
  ): Promise<{
    total: number;
    items: Array<{
      id: string;
      version_n: number;
      source: string;
      reason_code: string | null;
      reason_text: string | null;
      created_at: string;
      created_by_user_id: string | null;
      /** Display name joined from users. null when there was no human
       *  actor (a cron job / importer / automation) or the account is
       *  gone — the two are distinguishable via created_by_user_id. */
      created_by_name: string | null;
    }>;
  }> {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    return request(
      `/api/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/versions?${qs.toString()}`,
    );
  },

  async getEntityVersion(
    entityType: string,
    entityId: string,
    versionN: number,
  ): Promise<{
    id: string;
    entity_type: string;
    entity_id: string;
    version_n: number;
    prior_version_id: string | null;
    source: string;
    reason_code: string | null;
    reason_text: string | null;
    created_at: string;
    created_by_user_id: string | null;
    change_event_id: string | null;
    snapshot: Record<string, unknown> | null;
  }> {
    return request(
      `/api/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/versions/${versionN}`,
    );
  },

  // Phase D5 (May 2026 — reversibility-first architecture). Rolls an
  // entity back to a prior version by writing a NEW compensating
  // entity_versions row whose snapshot equals the target version.
  // History is never deleted. Auth: system_admin only (per the
  // current endpoint requireSystemAdmin gate).
  async rollbackEntityToVersion(
    entityType:
      | "contact"
      | "household"
      | "transaction"
      | "opportunity"
      | "service_record"
      | "rhythm_instance",
    entityId: string,
    args: {
      target_version_id: string;
      reason_code?: string;
      reason_text?: string;
    },
  ): Promise<{
    success: true;
    rollback_log_id: string;
    compensating_version_id: string;
    compensating_version_n: number;
    /**
     * Phase D5 finish: number of live-row columns actually written by
     * the rollback. Null when the entity type doesn't support live-row
     * materialization yet (e.g., transaction / opportunity).
     */
    live_row_fields_applied: number | null;
  }> {
    return request(
      `/api/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/rollback-to-version`,
      {
        method: "POST",
        body: JSON.stringify(args),
      },
    );
  },

  // Phase D6 (May 2026 — reversibility-first architecture).
  // Per-user recent destructive actions, 5-minute TTL window.
  // The UndoToast surfaces the newest entry as a floating toast
  // with a one-click rollback (via api.rollbackEntityToVersion).
  async listRecentActions(): Promise<{
    entries: Array<{
      id: string;
      entity_type:
        | "contact"
        | "household"
        | "transaction"
        | "opportunity"
        | "service_record"
        | "rhythm_instance";
      entity_id: string;
      label: string;
      prior_version_id: string;
      occurred_at: string;
      action_class: "merge" | "archive" | "delete" | "move" | "edit" | "rollback";
    }>;
  }> {
    return request(`/api/recent-actions`);
  },

  /** Phase D6: drop a recent-action entry (after Undo or explicit
   *  dismiss). Idempotent — second call with the same id no-ops. */
  async dismissRecentAction(id: string): Promise<{ removed: boolean }> {
    return request(`/api/recent-actions/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  // Phase G3 (May 2026 — reversibility-first architecture).
  // pending_conflicts admin queue — source-priority conflicts get
  // queued here when a synced value's source priority is lower than
  // the current source's priority. The admin queue page surfaces
  // them for human resolution.
  async listPendingConflicts(
    limit = 50,
    offset = 0,
  ): Promise<{
    total: number;
    items: Array<{
      id: string;
      entity_type: string;
      entity_id: string;
      field_name: string;
      current_source: string;
      current_value_hash: string | null;
      proposed_source: string;
      proposed_value_hash: string | null;
      current_value_label: string | null;
      proposed_value_label: string | null;
      proposed_source_record_id: string | null;
      detected_at: string;
      resolved_at: string | null;
      resolved_by_user_id: string | null;
      resolution: string | null;
    }>;
  }> {
    const qs = new URLSearchParams();
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));
    return request(`/api/admin/pending-conflicts/active?${qs.toString()}`);
  },

  /** Resolve a pending source-priority conflict. */
  async resolvePendingConflict(
    id: string,
    resolution: "keep_current" | "accept_proposed" | "manual_value" | "dismiss",
  ): Promise<{ resolved: boolean }> {
    return request(`/api/admin/pending-conflicts/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
      body: JSON.stringify({ resolution }),
    });
  },

  // Phase G1/G2 (May 2026 — reversibility-first architecture).
  // Per-(entity, field) provenance map. The hover-tooltip wrapper
  // calls this once per record-page open to render "Source X on
  // Date Y" for every editable field that has a recorded source.
  async getEntityProvenance(
    entityType:
      | "contact"
      | "household"
      | "transaction"
      | "opportunity"
      | "service_record"
      | "rhythm_instance",
    entityId: string,
  ): Promise<{
    provenance: Record<
      string,
      {
        source: string;
        set_at: string;
        set_by_user_id: string | null;
        /** Display name, joined server-side. Null when a system source
         *  set the value or the account is gone. */
        set_by_name: string | null;
        source_record_id?: string | null;
      }
    >;
  }> {
    return request(
      `/api/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/provenance`,
    );
  },

  // Phase F3 (May 2026 — reversibility-first architecture).
  // Active (unresolved) health alerts. The admin dashboard banner
  // surfaces these so the team sees spike-detection cron output
  // (mass-merge, mass-archive, etc.) in one place.
  async listActiveHealthAlerts(): Promise<{
    items: Array<{
      id: string;
      alert_kind: string;
      severity: "info" | "warning" | "critical";
      headline: string;
      detail: string | null;
      context_json: string;
      fired_at: string;
      resolved_at: string | null;
      resolved_by: "cron_clear" | "admin_dismiss" | null;
    }>;
  }> {
    return request(`/api/admin/health-alerts/active`);
  },

  // Phase F3: dismiss an active alert. Records resolved_by='admin_dismiss'
  // + resolved_at on the row. Surfaces no errors when called twice.
  async dismissHealthAlert(
    id: string,
  ): Promise<{ success: true } | { success: false; error: string }> {
    return request(`/api/admin/health-alerts/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
    });
  },

  // Phase D2 (May 2026 — reversibility-first architecture). Canonical
  // reason-code catalog. DestructiveActionConfirm's picker populates
  // from this. Filtering by action class ensures
  // 'married_couple_consolidation' shows on merges/moves but not on
  // archives, etc.
  async listChangeReasonCodes(action?: "merge" | "archive" | "delete" | "move" | "edit"): Promise<{
    codes: Array<{
      code: string;
      display_label: string;
      description: string;
      applies_to: Array<"merge" | "archive" | "delete" | "move" | "edit">;
      requires_text: boolean;
      sort_order: number;
    }>;
  }> {
    const qs = action ? `?action=${encodeURIComponent(action)}` : "";
    return request(`/api/change-reason-codes${qs}`);
  },

  // Mark a duplicate cluster as "not actually a duplicate" so it
  // stops surfacing in the queue. Idempotent — calling twice with the
  // same (match_type, match_value) returns the existing dismissal.
  async dismissDuplicateCluster(
    matchType: DismissalMatchType,
    matchValue: string,
    reason: string | null = null,
  ): Promise<{ dismissal: DuplicateDismissal }> {
    return request(`/api/admin/data-hygiene/duplicate-dismissals`, {
      method: "POST",
      body: JSON.stringify({
        match_type: matchType,
        match_value: matchValue,
        reason,
      }),
    });
  },

  // Phase 11.x (May 2026): vendor management surface for the
  // "Link as vendor co-workers" action on the duplicate-candidates
  // queue. Three endpoints — search / create / link.
  async searchVendors(q: string, limit = 20, type?: VendorType): Promise<{ vendors: Vendor[] }> {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("limit", String(limit));
    if (type) qs.set("type", type);
    return request(`/api/vendors/search?${qs.toString()}`);
  },

  async createVendor(input: {
    display_name: string;
    type: VendorType;
    portal_url?: string | null;
    primary_contact_name?: string | null;
    primary_contact_email?: string | null;
    primary_contact_phone?: string | null;
    primary_contact_address?: string | null;
    notes?: string | null;
  }): Promise<{ vendor: Vendor }> {
    return request(`/api/vendors`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // ── Manual insurance policy add + cancel (PR-Q2) ──
  // Per Grant 2026-05-13: Joy can add/remove insurance policies on a
  // household directly in OS instead of maintaining an external
  // spreadsheet. Backed by /workers/api/src/routes/insurance-policies.ts.
  async addInsurancePolicy(
    householdId: string,
    input: {
      policy_type: string;
      carrier_display_name: string;
      policy_number?: string | null;
      effective_date?: string | null;
      expiration_date?: string | null;
      /** 12 unless told otherwise. Every carrier import sets this; a
       *  hand-added policy had nowhere to record it before 2026-08-11. */
      term_length_months?: number | null;
      /** Supplying only one derives the other from term_length_months
       *  server-side — see shared/insurance/policy-term-premium.ts. */
      annual_premium?: number | null;
      term_premium?: number | null;
      payment_method?: string | null;
      payment_frequency?: string | null;
      notes?: string | null;
      extra?: Record<string, unknown> | null;
      /** Whose record this is — household member contact ids, first =
       *  primary (0232). */
      for_contact_ids?: string[];
      /** "Add it anyway", after the 409 named the policy already on this
       *  household under the same number. */
      allow_duplicate_policy_number?: boolean;
    },
  ): Promise<{
    service_record_id: string;
    insurance_policy_id: string;
    rhythm_instance_id: string | null;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/insurance-policies`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Attach a FIRST insurance_policies row to an existing insurance
  // service record that has none — the create-on-first-save fix for the
  // record-creation gap Mylah hit on Jeannie Olsen's Auto + Umbrella
  // records (2026-08-12): a won-Opportunity conversion or a completed
  // Processing record can leave a "policy family" SR with nowhere to
  // type carrier/policy number/premium. Same shape as addInsurancePolicy
  // but targets an SR that already exists instead of minting a new one.
  // Create-only — 409s if the record already has a policy; from then on
  // edit via updateInsurancePolicy. Backed by POST
  // /api/service-records/:id/insurance-policy.
  async attachInsurancePolicy(
    serviceRecordId: string,
    input: {
      policy_type: string;
      carrier_display_name: string;
      policy_number?: string | null;
      effective_date?: string | null;
      expiration_date?: string | null;
      term_length_months?: number | null;
      annual_premium?: number | null;
      term_premium?: number | null;
      payment_method?: string | null;
      payment_frequency?: string | null;
      reason_text?: string | null;
      /** "Add it anyway", after the 409 named the policy already on this
       *  household under the same number. */
      allow_duplicate_policy_number?: boolean;
    },
  ): Promise<{
    service_record_id: string;
    insurance_policy_id: string;
    retyped: boolean;
    new_service_type: string | null;
    new_display_label: string | null;
  }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/insurance-policy`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Manually record a loan the household already has (record-creation-gap
  // program — the mortgage twin of addInsurancePolicy).
  async addMortgageLoan(
    householdId: string,
    input: {
      service_type: string;
      lender_name?: string | null;
      loan_number?: string | null;
      origination_amount?: number | null;
      interest_rate?: number | null;
      funded_date?: string | null;
      notes?: string | null;
      /** Whose record this is — household member contact ids, first =
       *  primary (0232). */
      for_contact_ids?: string[];
    },
  ): Promise<{
    service_record_id: string;
    mortgage_loan_id: string;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/mortgage-loans`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Manually record a portfolio the household already has (record-
  // creation-gap program — the financial twin of addInsurancePolicy).
  async addPortfolio(
    householdId: string,
    input: {
      custodian: string;
      account_number?: string | null;
      account_type?: string | null;
      account_title?: string | null;
      balance?: number | null;
      opened_date?: string | null;
      notes?: string | null;
      /** Whose record this is — household member contact ids, first =
       *  primary (0232). */
      for_contact_ids?: string[];
    },
  ): Promise<{
    service_record_id: string;
    portfolio_id: string;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/portfolios`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // ─── H4 "Client moved assets out" (Stewardship Financial) ─────────
  //
  // The household-level action that ends a Financial relationship: closes
  // every active Financial account as transferred out, ends the Financial
  // care cycles, and records where the money went. One bulk_action_id
  // undoes all of it. Company-scope gated server-side on "financial".

  async previewFinancialAssetsTransfer(
    householdId: string,
  ): Promise<FinancialAssetsTransferPreview> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/financial-assets-transfer/preview`,
    );
  },

  async transferFinancialAssetsOut(
    householdId: string,
    input: {
      destination: string;
      reason_text?: string | null;
      transfer_date?: string | null;
    },
  ): Promise<FinancialAssetsTransferResult> {
    return request(`/api/households/${encodeURIComponent(householdId)}/financial-assets-transfer`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async undoFinancialAssetsTransfer(
    householdId: string,
    bulkActionId: string,
  ): Promise<{
    ok: boolean;
    accounts_reopened: number;
    care_plans_resumed: number;
    message?: string;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/financial-assets-transfer/${encodeURIComponent(bulkActionId)}/undo`,
      { method: "POST" },
    );
  },

  // Manually record a planning case the household already has (record-
  // creation-gap program — the planning twin of addInsurancePolicy).
  // Product-detail keys beyond service_type/notes are validated
  // server-side by the same validator the case detail page uses.
  async addPlanningCase(
    householdId: string,
    input: {
      service_type: string;
      /** Medicare type (MAPD / PDP / Supplement) or marketplace tier —
       *  settable at create since 2026-08-03 (Ryan's ask). Same
       *  validator as the case detail page's edit. */
      plan_tier?: string | null;
      carrier_name?: string | null;
      /** The vendors-backed carrier link (S5) — sent alongside
       *  carrier_name when the advisor picks from the dropdown. */
      carrier_vendor_id?: string | null;
      product_name?: string | null;
      policy_number?: string | null;
      effective_date?: string | null;
      expiration_date?: string | null;
      annual_premium?: number | null;
      monthly_premium?: number | null;
      notes?: string | null;
      /** Whose record this is — household member contact ids, first =
       *  primary (0232). */
      for_contact_ids?: string[];
    },
  ): Promise<{
    service_record_id: string;
    planning_case_id: string;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/planning-cases`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Manually record a completed (or in-progress) listing / purchase the
  // household already has (record-creation-gap program — the realty twin
  // of addInsurancePolicy).
  async addRealtyRecord(
    householdId: string,
    input: {
      side: "listing" | "home_purchase";
      property_address_line_1: string;
      property_address_line_2?: string | null;
      property_city: string;
      property_state: string;
      property_zip: string;
      sale_price?: number | null;
      closed_date?: string | null;
      mls_number?: string | null;
      notes?: string | null;
      /** Whose record this is — household member contact ids, first =
       *  primary (0232). */
      for_contact_ids?: string[];
      /** A lease, not a sale (2026-09-22). `side` still names who we
       *  represent: listing = landlord, home_purchase = tenant. */
      rental?: boolean;
      monthly_rent?: number | null;
      lease_start_date?: string | null;
      lease_end_date?: string | null;
    },
  ): Promise<{
    service_record_id: string;
    realty_record_id: string;
    status: string;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/realty-records`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Directly create a Processing (transaction) record — no precursor
  // Opportunity required (Nate 2026-08-04: "I have no way of adding a new
  // "Attach to a policy they already have" — the opt-in companion to
  // automatic product materialization (2026-08-17 program). Realty is
  // excluded server-side; it has its own picker.
  async linkableProductRecords(
    transactionId: string,
    q: string,
  ): Promise<{
    candidates: Array<{
      service_record_id: string;
      service_type: string;
      service_type_label: string;
      service_family: string;
      display_label: string | null;
      status: string;
      effective_date: string | null;
      likely_match: boolean;
    }>;
  }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/linkable-product-records?q=${encodeURIComponent(q)}`,
    );
  },

  async linkProductRecord(
    transactionId: string,
    serviceRecordId: string,
  ): Promise<{
    ok: boolean;
    service_record_id: string;
    undo: { previous_linked_service_record_id: string | null };
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/link-product-record`, {
      method: "POST",
      body: JSON.stringify({ service_record_id: serviceRecordId }),
    });
  },

  /**
   * "The policy exists — record it now." Creates the product record this
   * Processing file delivers, ahead of the board stage that would create
   * it automatically. `created: false` means one already existed (someone
   * else, or the trigger stage, got there first) — not an error.
   */
  async createProductRecord(
    transactionId: string,
    effectiveDate?: string | null,
  ): Promise<{
    ok: boolean;
    created: boolean;
    service_record_id: string;
    undo: { previous_linked_service_record_id: string | null };
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/create-product-record`, {
      method: "POST",
      body: JSON.stringify(effectiveDate ? { effective_date: effectiveDate } : {}),
    });
  },

  async unlinkProductRecord(transactionId: string): Promise<{ ok: boolean }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/unlink-product-record`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  // transaction"). Creates the linked service_record too, entering the
  // company's board at its first stage.
  async createTransaction(
    householdId: string,
    input: {
      company_slug: string;
      service_type: string;
      expected_close_date?: string | null;
      product_metric?: number | null;
      owner_user_id?: string | null;
      for_contact_ids?: string[];
      notes?: string | null;
      // Realty only (company_slug === 'realty').
      property_address_line_1?: string;
      property_address_line_2?: string | null;
      property_city?: string;
      property_state?: string;
      property_zip?: string;
      sale_price?: number | null;
      closed_date?: string | null;
      mls_number?: string | null;
      // Realty rentals only (service_type home_rental / rental_listing).
      monthly_rent?: number | null;
      lease_start_date?: string | null;
      lease_end_date?: string | null;
    },
  ): Promise<{
    ok: boolean;
    service_record_id: string;
    transaction_id: string;
    realty_record_id: string | null;
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/transactions`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Change a service record's type within its company (record-creation-
  // gap program — fixes e.g. a planning case typed health_case that was
  // really a term-life sale). Server enforces same-company, same data
  // shape, and the care-cycle guard.
  async retypeServiceRecord(
    serviceRecordId: string,
    input: { service_type: string; reason_text?: string | null },
  ): Promise<{
    id: string;
    changed: boolean;
    old_service_type?: string;
    new_service_type?: string;
    new_display_label?: string | null;
  }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/retype`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Move a Processing record to a different board within its own company
  // (Ryan, 2026-08-08 — won life cases filed on the estate-plan board with
  // no way to move them). The transaction-side twin of retypeServiceRecord:
  // this changes which BOARD the record sits on, not what kind of case or
  // policy it delivers.
  async changeTransactionBoard(
    transactionId: string,
    input: {
      transaction_type: string;
      reason_text?: string | null;
      /** A person's reason for going past the Private Money disclosure
       *  check (409 stage_blocked — read it with readStageBlock). */
      override_reason?: string | null;
    },
  ): Promise<{
    ok: boolean;
    transaction_id: string;
    changed: boolean;
    old_transaction_type?: string;
    new_transaction_type?: string;
    new_board_label?: string;
    old_stage?: string;
    new_stage?: string;
    stage_carried_over?: boolean;
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/change-board`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Change what a Processing record is INTENDED to deliver, before its
  // product record exists (Ryan Delviken, 2026-08-29 — declined by one
  // carrier, applying with another, and no way to say so on the record).
  // The third correction control alongside retypeServiceRecord (a
  // product already exists) and changeTransactionBoard (right product,
  // wrong board) — refused with 409 if a product record is already
  // linked, since that case belongs to retypeServiceRecord instead.
  async retypeTransactionIntent(
    transactionId: string,
    input: { service_type: string; reason_text?: string | null },
  ): Promise<{
    ok: boolean;
    transaction_id: string;
    changed: boolean;
    old_intended_service_type?: string | null;
    new_intended_service_type?: string;
    board_changed?: boolean;
    old_transaction_type?: string;
    new_transaction_type?: string;
    new_board_label?: string;
    old_stage?: string;
    new_stage?: string;
    stage_carried_over?: boolean;
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/retype-intent`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Rename a service record — change the title it renders under, not
  // what kind of record it is (Joy, 2026-08-06). Server enforces company
  // scope: the Insurance team renames insurance records, each company's
  // team renames its own. An empty label clears it, restoring the title
  // derived from the record's type.
  async renameServiceRecord(
    serviceRecordId: string,
    input: { display_label: string | null; reason_text?: string | null },
  ): Promise<{
    id: string;
    changed: boolean;
    old_display_label?: string | null;
    new_display_label?: string | null;
  }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/rename`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async cancelInsurancePolicy(
    serviceRecordId: string,
    input: {
      cancellation_reason: string;
      cancelled_at?: string | null;
      cancellation_notes?: string | null;
    },
  ): Promise<{
    ok: true;
    service_record_id: string;
    insurance_policy_id: string;
    cancellation_reason: string;
    cancelled_at: string;
    rhythms_cancelled: number;
  }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/cancel-insurance`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Policy cancellation request (2026-09-24) — the letter a client signs to
  // ask the carrier to cancel, sent from the policy through OS e-sign.
  // Replaces the website form whose signNow hookup was retired.
  async getCancellationRequestOptions(serviceRecordId: string): Promise<CancellationRequestOptions> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/cancellation-request`);
  },

  async sendCancellationRequest(
    serviceRecordId: string,
    input: {
      service_record_ids: string[];
      signer_contact_ids: string[];
      effective_date: string;
      reason: string;
      reason_other: string | null;
      requote_next_year: boolean;
      message: string | null;
    },
  ): Promise<{
    request_id: string;
    envelope_id: string;
    document_id: string;
    signers: Array<{ name: string; email: string; sign_url: string; emailed: boolean; email_error?: string | null }>;
  }> {
    return request(`/api/service-records/${encodeURIComponent(serviceRecordId)}/cancellation-request`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Joy feedback PR 3 — set/clear the carrier's quoted renewal premium
  // on a policy (in-flow capture while reviewing the renewal quote).
  async setRenewalPremiumOffer(
    insurancePolicyId: string,
    renewalPremiumOffer: number | null,
  ): Promise<{
    ok: true;
    insurance_policy_id: string;
    service_record_id: string;
    renewal_premium_offer: number | null;
  }> {
    return request(
      `/api/insurance-policies/${encodeURIComponent(insurancePolicyId)}/renewal-offer`,
      {
        method: "PATCH",
        body: JSON.stringify({ renewal_premium_offer: renewalPremiumOffer }),
      },
    );
  },

  // Mylah, 2026-08-06 — once the renewal takes effect, carry the
  // confirmed renewal_premium_offer into annual_premium (the number
  // everyone else reads as "the premium") and clear the offer. 409 when
  // no offer is confirmed yet.
  //
  // Mylah, 2026-08-11 — rolls the WHOLE term forward, not just the
  // premium: effective/expiration dates and term_number move too
  // (shared/insurance/renewal-term-rollforward.ts computes the default;
  // the optional overrides let the carrier's actual dates win, since
  // the OS isn't authoritative for when a term really starts).
  async applyRenewalPremium(
    insurancePolicyId: string,
    dateOverrides?: { effective_date?: string; expiration_date?: string },
  ): Promise<{
    ok: true;
    insurance_policy_id: string;
    service_record_id: string;
    annual_premium: number;
    term_premium: number;
    effective_date: string;
    expiration_date: string;
    term_number: number | null;
    pre_renewal_premium: number | null;
  }> {
    return request(
      `/api/insurance-policies/${encodeURIComponent(insurancePolicyId)}/apply-renewal-premium`,
      { method: "POST", body: JSON.stringify(dateOverrides ?? {}) },
    );
  },

  // Edit the Carrier Data card on an existing policy (policy number,
  // carrier, term dates, premiums, payment). Partial — only the keys
  // present are changed. Versioned server-side (History tab). Backed by
  // PATCH /api/insurance-policies/:id.
  async updateInsurancePolicy(
    insurancePolicyId: string,
    input: {
      policy_number?: string | null;
      carrier_display_name?: string | null;
      carrier_vendor_id?: string | null;
      policy_type?: string;
      effective_date?: string | null;
      expiration_date?: string | null;
      /** Supplying only one of annual_premium/term_premium derives the
       *  other from term_length_months (the value set in this same
       *  PATCH, or the policy's existing one) — see
       *  shared/insurance/policy-term-premium.ts. */
      term_length_months?: number | null;
      annual_premium?: number | null;
      term_premium?: number | null;
      payment_method?: string | null;
      payment_frequency?: string | null;
      vehicles?: string | null;
      /** Coverage elections — true / false / null, where null means
       *  "back to unrecorded". Sending false is a real answer. */
      um_uim_rejected?: boolean | null;
      has_scheduled_jewelry?: boolean | null;
      has_good_student_discount?: boolean | null;
      reason_text?: string | null;
    },
  ): Promise<{
    ok: true;
    insurance_policy_id: string;
    service_record_id: string;
    updated_fields: string[];
  }> {
    return request(`/api/insurance-policies/${encodeURIComponent(insurancePolicyId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },

  async linkContactsToVendor(
    vendorId: string,
    contactIds: string[],
    context: string,
  ): Promise<{
    created: number;
    skipped: number;
    invalid_contact_ids: string[];
  }> {
    return request(`/api/vendors/${encodeURIComponent(vendorId)}/links`, {
      method: "POST",
      body: JSON.stringify({ contact_ids: contactIds, context }),
    });
  },

  /** Take one person off a vendor (the contact record is kept). Returns the contexts removed, for Undo. */
  async unlinkContactFromVendor(
    vendorId: string,
    contactId: string,
  ): Promise<{ removed_contexts: string[] }> {
    return request(
      `/api/vendors/${encodeURIComponent(vendorId)}/links/${encodeURIComponent(contactId)}`,
      { method: "DELETE" },
    );
  },

  /** Edit a vendor's contact details, portal link, relationship owner and notes. Null clears a field. */
  async updateVendorDetails(
    vendorId: string,
    patch: VendorDetailsPatch,
  ): Promise<{
    vendor: VendorSummary;
    owner_name: string | null;
    changes: Partial<Record<keyof VendorDetailsPatch, { from: string | null; to: string | null }>>;
  }> {
    return request(`/api/vendors/${encodeURIComponent(vendorId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  // ── Bug B Phase 5: spouse-pass quarantine review queue ─────────────
  // List pending entries that the spouse-pass conservatively skipped
  // because the matcher returned 2+ candidate spouse households.
  async listSpousePassQuarantine(): Promise<SpousePassQuarantineList> {
    return request(`/api/admin/quarantine/spouse-pass`);
  },

  // Resolve a spouse-pass quarantine entry. Two actions:
  //   * "merge"   — merges the chosen suggested household INTO the AC
  //                 primary's current household via mergeHouseholds().
  //   * "dismiss" — marks the entry resolved with no merge.
  async resolveSpousePassQuarantine(
    id: string,
    body: { action: "merge"; target_household_id: string } | { action: "dismiss"; note?: string },
  ): Promise<SpousePassQuarantineResolveResponse> {
    return request(`/api/admin/quarantine/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // List dismissed clusters (most recent first). includeRestored=true
  // also includes ones that were later un-dismissed.
  async listDuplicateDismissals(
    limit = 50,
    offset = 0,
    includeRestored = false,
  ): Promise<ListDismissalsResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      include_restored: includeRestored ? "true" : "false",
    });
    return request(`/api/admin/data-hygiene/duplicate-dismissals?${qs.toString()}`);
  },

  // Soft un-dismiss so the cluster surfaces again.
  async restoreDuplicateDismissal(
    id: string,
    reason: string | null = null,
  ): Promise<{ dismissal: DuplicateDismissal }> {
    return request(
      `/api/admin/data-hygiene/duplicate-dismissals/${encodeURIComponent(id)}/restore`,
      {
        method: "POST",
        body: JSON.stringify({ reason }),
      },
    );
  },

  // Bulk-dismiss phone clusters that look like married couples sharing
  // a phone (same household, all members have distinct names).
  // dry_run=true previews the count + sample without writing.
  async autoSuppressCoupleClusters(dryRun: boolean): Promise<AutoSuppressCouplesResult> {
    return request("/api/admin/data-hygiene/duplicate-dismissals/auto-suppress-couples", {
      method: "POST",
      body: JSON.stringify({ dry_run: dryRun }),
    });
  },

  // Bulk-merge phone clusters where every member has the same
  // (legal_first_name, legal_last_name) — true duplicates from the AC
  // migration. Survivor is picked by population_score (most-populated
  // record wins, tiebreak by oldest created_at). dry_run=true previews
  // count + sample without writing.
  async autoMergeSameNameClusters(dryRun: boolean): Promise<AutoMergeSameNameResult> {
    return request("/api/admin/data-hygiene/duplicate-candidates/auto-merge-same-name-phone", {
      method: "POST",
      body: JSON.stringify({ dry_run: dryRun }),
    });
  },

  // Financial firm dashboard — aggregations from portfolios +
  // service_records + investment_account_snapshots.
  async getFinancialFirmDashboard(opts?: {
    topHouseholdsLimit?: number;
    recentSnapshotsLimit?: number;
  }): Promise<FinancialFirmDashboardResult> {
    const qs = new URLSearchParams();
    if (opts?.topHouseholdsLimit) qs.set("top_households_limit", String(opts.topHouseholdsLimit));
    if (opts?.recentSnapshotsLimit)
      qs.set("recent_snapshots_limit", String(opts.recentSnapshotsLimit));
    const url = "/api/admin/financial/firm-dashboard" + (qs.toString() ? "?" + qs.toString() : "");
    return request(url);
  },

  // Referral Network — new-contact volume AND won-revenue by source, the
  // internal cross-company from→to flow matrix, and the referring-advisor
  // leaderboard. Served from a KV cache; system_admin/compliance only.
  async getReferralNetworkReport(params: { company?: string | null } = {}): Promise<ReferralNetworkReport> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    const tail = qs.toString();
    return request<ReferralNetworkReport>(`/api/marketing/referral-network${tail ? `?${tail}` : ""}`);
  },

  // Fin-G2 — READ-ONLY trust→family re-attachment preview. Returns
  // the proposed family household for every quarantined trust
  // account (or a flag: needs split / needs review / no family).
  // Performs zero writes; the apply is a separate Grant-approved
  // reversible step.
  async getTrustReattachmentPreview(): Promise<TrustReattachmentPreviewResult> {
    return request<TrustReattachmentPreviewResult>(
      "/api/admin/financial/trust-reattachment-preview",
    );
  },

  // Fin-G2 APPLY — gated, reversible. Re-attaches ONE quarantined
  // trust account-household to its real family household via the
  // shared audited mergeHouseholds service (unmerge-able). The server
  // re-validates the match; multi_account_collapsed is refused.
  async applyTrustReattachment(quarantinedHouseholdId: string): Promise<TrustReattachApplyResult> {
    return request<TrustReattachApplyResult>("/api/admin/financial/trust-reattachment/apply", {
      method: "POST",
      body: JSON.stringify({
        quarantined_household_id: quarantinedHouseholdId,
      }),
    });
  },

  async getInsuranceDashboard(): Promise<InsuranceDashboardResponse> {
    return request<InsuranceDashboardResponse>("/api/dashboards/insurance");
  },

  async getMortgageDashboard(): Promise<MortgageDashboardResponse> {
    return request<MortgageDashboardResponse>("/api/dashboards/mortgage");
  },

  async getRealtyDashboard(): Promise<RealtyDashboardResponse> {
    return request<RealtyDashboardResponse>("/api/dashboards/realty");
  },

  // Bad-data alerts queue — placeholder / malformed email or
  // placeholder phone values.
  async listBadDataAlertsQueue(
    limit = 50,
    offset = 0,
    sort: BadDataAlertsSort = "default",
    matchType?: BadDataMatchType,
  ): Promise<BadDataAlertsQueueResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
    });
    if (matchType) qs.set("match_type", matchType);
    return request(`/api/admin/data-hygiene/queues/bad-data-alerts?${qs.toString()}`);
  },

  // Stale fields queue — active person contacts whose updated_at is
  // older than the threshold (default 365 days, override with days).
  async listStaleFieldsQueue(
    limit = 50,
    offset = 0,
    days = 365,
    sort: StaleFieldsSort = "oldest_first",
  ): Promise<StaleFieldsQueueResult> {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      days: String(days),
      sort,
    });
    return request(`/api/admin/data-hygiene/queues/stale-fields?${qs.toString()}`);
  },

  // ─── Sensitive field decryption ──────────────────────────────────────
  // Every call triggers server-side audit logging and consumes rate
  // limit budget. The returned `value` is ephemeral — the UI layer MUST
  // NOT persist it to Redux/Zustand/localStorage/sessionStorage. Only
  // local component state.
  async fetchSensitiveField(
    contactId: string,
    field: SensitiveFieldName,
  ): Promise<SensitiveFieldResponse> {
    return request<SensitiveFieldResponse>(
      `/api/contacts/${encodeURIComponent(contactId)}/sensitive/${field}`,
    );
  },

  // Set or clear a contact's SSN. Encrypted server-side; pass null/"" to
  // clear. Returns the refreshed SafeContact (masked last-four only — never
  // the SSN). Revealing the value still goes through fetchSensitiveField.
  async updateSsn(
    contactId: string,
    input: { ssn: string | null },
  ): Promise<{ contact: SafeContact | null }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/ssn`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  // Set or clear the SPOUSE's SSN on a contact. Same encryption, gate,
  // audit, and rate limit as updateSsn — a spouse's number is the same tier
  // of data as the contact's own. Planning needs it for joint tax work.
  async updateSpouseSsn(
    contactId: string,
    input: { spouse_ssn: string | null },
  ): Promise<{ contact: SafeContact | null }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/spouse-ssn`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  // Set or clear the CO-BORROWER's SSN on a contact. Same handling again;
  // Mortgage needs it whenever a loan carries a second borrower.
  async updateCoborrowerSsn(
    contactId: string,
    input: { coborrower_ssn: string | null },
  ): Promise<{ contact: SafeContact | null }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/coborrower-ssn`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  // Set or clear a contact's bank / ACH details (Joy, 2026-08-03 — the
  // record could display these but never capture them, so payment info was
  // ending up in notes). Account number, routing number, and account name are
  // encrypted server-side; account type and the ACH flag are plain metadata.
  // Pass null/"" to clear a key, omit a key to leave it unchanged. Returns
  // the refreshed SafeContact (masked last-four only — never the numbers).
  async updateBankDetails(
    contactId: string,
    input: {
      bank_account_number?: string | null;
      bank_routing_number?: string | null;
      bank_account_name?: string | null;
      bank_account_type?: "checking" | "savings" | null;
      bank_name?: string | null;
      ach_on_file?: boolean;
    },
  ): Promise<{ contact: SafeContact | null }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/bank`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  // Set or clear a contact's payment card. Number/CVV/name are encrypted
  // server-side; expiry + billing ZIP are plaintext metadata. Pass null/""
  // to clear a key, omit a key to leave it unchanged. Clearing the CVV
  // ("clear security code" after carrier setup) is card_cvv: null. Returns
  // the refreshed safe projection (masked values only).
  async updateCardDetails(
    contactId: string,
    input: {
      card_number?: string | null;
      card_cvv?: string | null;
      card_name?: string | null;
      card_expiration?: string | null;
      card_billing_zip?: string | null;
    },
  ): Promise<{ contact: SafeContact | null }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/card`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  // Set or clear a contact's driver's license (the insurance quoting
  // tier). The number is encrypted server-side; pass null/"" to clear a
  // key, omit a key to leave it unchanged. Returns the refreshed
  // SafeContact (masked last-four + state only — never the number).
  // Revealing the value still goes through fetchSensitiveField.
  async updateDriversLicense(
    contactId: string,
    input: {
      drivers_license?: string | null;
      drivers_license_state?: string | null;
    },
  ): Promise<{ contact: SafeContact | null }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/drivers-license`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  // ── Medicare + health lists (Planning workspace) ──────────────────
  // Save Medicare fields. The MBI is encrypted server-side; pass null to
  // clear a field, omit a key to leave it unchanged. Returns the refreshed
  // SafeContact (masked last-four + dates only — never the MBI).
  async updateMedicare(
    contactId: string,
    input: {
      medicare_beneficiary_id?: string | null;
      medicare_part_a_start_date?: string | null;
      medicare_part_b_start_date?: string | null;
    },
  ): Promise<{ contact: SafeContact | null }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/medicare`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  async listProviders(contactId: string, access?: HealthAccessReason | null): Promise<ContactProvider[]> {
    const r = await request<{ providers: ContactProvider[] }>(
      withHealthAccess(`/api/contacts/${encodeURIComponent(contactId)}/providers`, access),
    );
    return r.providers;
  },
  async addProvider(
    contactId: string,
    input: {
      provider_name: string;
      specialty?: string | null;
      phone?: string | null;
      npi?: string | null;
      street_address?: string | null;
      notes?: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<ContactProvider> {
    const r = await request<{ provider: ContactProvider }>(
      withHealthAccess(`/api/contacts/${encodeURIComponent(contactId)}/providers`, access),
      { method: "POST", body: JSON.stringify(input) },
    );
    return r.provider;
  },
  async updateProvider(
    contactId: string,
    providerId: string,
    patch: Partial<{
      provider_name: string;
      specialty: string | null;
      phone: string | null;
      npi: string | null;
      street_address: string | null;
      notes: string | null;
    }>,
    access?: HealthAccessReason | null,
  ): Promise<ContactProvider> {
    const r = await request<{ provider: ContactProvider }>(
      withHealthAccess(
        `/api/contacts/${encodeURIComponent(contactId)}/providers/${encodeURIComponent(providerId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    return r.provider;
  },
  async deleteProvider(
    contactId: string,
    providerId: string,
    access?: HealthAccessReason | null,
  ): Promise<void> {
    await request(
      withHealthAccess(
        `/api/contacts/${encodeURIComponent(contactId)}/providers/${encodeURIComponent(providerId)}`,
        access,
      ),
      { method: "DELETE" },
    );
  },

  // ─── The household's health lists, including members with no profile ──
  //
  // The WRITE methods below only ever reach rows that belong to nobody in
  // particular (an ACA dependent). A named person's doctors and
  // medications are edited through the contact methods above — the server
  // enforces that split, this is just where it is visible.

  /** The long-term care prequals that belong to one case (LTC health
   *  intake, PR 5). Health tier: a refusal is a 403 the locked door reads. */
  async getLtcCaseIntake(
    kind: "opportunity" | "transaction",
    id: string,
    access?: HealthAccessReason | null,
  ): Promise<LtcCaseIntake> {
    return request<LtcCaseIntake>(
      withHealthAccess(
        `/api/ltc-cases/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/intake`,
        access,
      ),
    );
  },
  /** Record that a long-term care prequal's limited permission was
   *  withdrawn, or undo that record (PR 12). */
  async setLtcPrequalPermission(
    kind: "opportunity" | "transaction",
    id: string,
    submissionId: string,
    input: { action: "withdraw" | "restore"; note: string | null },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true }> {
    return request(
      withHealthAccess(
        `/api/ltc-cases/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/prequals/${encodeURIComponent(submissionId)}/permission`,
        access,
      ),
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  /** Who opened this person's health information (LTC PR 11): the
   *  health decisions logged for them and their household. Metadata only. */
  async getHealthReads(
    contactId: string,
    access?: HealthAccessReason | null,
  ): Promise<HealthReadsLog> {
    return request<HealthReadsLog>(
      withHealthAccess(`/api/contacts/${encodeURIComponent(contactId)}/health-reads`, access),
    );
  },
  /** The case's "About the person" and "This application" panels (PR 9).
   *  Health information: gated like the intake read. */
  async getLtcCaseSummary(
    kind: "opportunity" | "transaction",
    id: string,
    access?: HealthAccessReason | null,
  ): Promise<LtcCaseSummary> {
    return request<LtcCaseSummary>(
      withHealthAccess(
        `/api/ltc-cases/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/summary`,
        access,
      ),
    );
  },
  /** The long-term care carrier trail (PR 8). Health-derived, so every
   *  call carries the admin's stated reason when one is in effect. */
  async addLtcCarrier(
    kind: "opportunity" | "transaction",
    id: string,
    input: LtcCarrierInput,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: LtcCaseCarrier }> {
    return request(
      withHealthAccess(
        `/api/ltc-cases/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/carriers`,
        access,
      ),
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  async updateLtcCarrier(
    kind: "opportunity" | "transaction",
    id: string,
    rowId: string,
    input: LtcCarrierInput & { version_number: number },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: LtcCaseCarrier }> {
    return request(
      withHealthAccess(
        `/api/ltc-cases/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/carriers/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async removeLtcCarrier(
    kind: "opportunity" | "transaction",
    id: string,
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: LtcCaseCarrier }> {
    return request(
      withHealthAccess(
        `/api/ltc-cases/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/carriers/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "DELETE" },
    );
  },
  async undoLtcCarrier(
    kind: "opportunity" | "transaction",
    id: string,
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: LtcCaseCarrier }> {
    return request(
      withHealthAccess(
        `/api/ltc-cases/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/carriers/${encodeURIComponent(rowId)}/undo`,
        access,
      ),
      { method: "POST" },
    );
  },
  // ─── The ACA quote ───────────────────────────────────────────────────
  // Every call is health information: the stated reason, when an admin gave
  // one, rides along on each.
  async getAcaQuote(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    access?: HealthAccessReason | null,
  ): Promise<AcaQuoteResponse> {
    return request<AcaQuoteResponse>(withHealthAccess(acaQuotePath(kind, id, year), access));
  },
  async uploadAcaQuoteFile(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    input: {
      file: File;
      file_kind: AcaQuoteFile["file_kind"];
      read_json: string | null;
      reader_version: string | null;
      /** A carrier screenshot's carrier ("Oscar"). */
      carrier_name?: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: AcaQuoteFile }> {
    const fd = new FormData();
    fd.append("file", input.file);
    fd.append("file_kind", input.file_kind);
    if (input.read_json) fd.append("read_json", input.read_json);
    if (input.reader_version) fd.append("reader_version", input.reader_version);
    if (input.carrier_name) fd.append("carrier_name", input.carrier_name);
    const resp = await fetch(
      apiUrl(withHealthAccess(`${acaQuotePath(kind, id, year)}/files`, access)),
      { method: "POST", body: fd, credentials: "include" },
    );
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't upload that file.");
    return (await resp.json()) as { ok: true; row: AcaQuoteFile };
  },
  async downloadAcaQuoteFile(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    fileId: string,
    access?: HealthAccessReason | null,
  ): Promise<Blob> {
    const resp = await fetch(
      apiUrl(
        withHealthAccess(
          `${acaQuotePath(kind, id, year)}/files/${encodeURIComponent(fileId)}`,
          access,
        ),
      ),
      { credentials: "include" },
    );
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't open that file.");
    return resp.blob();
  },
  /** Search healthcare.gov (Stage 2). A ZIP in two counties answers 409
   *  `pick_county` with the choices in ApiError.data.counties. */
  async searchAcaPlans(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    input: {
      zip: string;
      county_fips: string | null;
      income: number | null;
      married_couple: boolean;
      people: { contact_id: string | null; label: string | null; age: number; uses_tobacco: boolean }[];
    },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: AcaQuoteSearch }> {
    return request(withHealthAccess(`${acaQuotePath(kind, id, year)}/searches`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async addAcaQuotePlan(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    input: AcaQuotePlanInput,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: AcaQuotePlan }> {
    return request(withHealthAccess(`${acaQuotePath(kind, id, year)}/plans`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateAcaQuotePlan(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    planId: string,
    input: AcaQuotePlanInput & { version_number: number },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: AcaQuotePlan }> {
    return request(
      withHealthAccess(
        `${acaQuotePath(kind, id, year)}/plans/${encodeURIComponent(planId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async getAcaQuoteEmailOptions(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    access?: HealthAccessReason | null,
  ): Promise<AcaQuoteEmailOptions> {
    return request(withHealthAccess(`${acaQuotePath(kind, id, year)}/email-options`, access));
  },
  async draftAcaQuoteEmail(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    input: {
      sender_user_id: string;
      booking_url: string | null;
      why_recommended: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<{ draft_mailbox_upn: string; draft_web_link: string | null }> {
    return request(withHealthAccess(`${acaQuotePath(kind, id, year)}/draft-email`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async getAcaQuoteCoverage(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    access?: HealthAccessReason | null,
  ): Promise<AcaQuoteCoverage> {
    return request<AcaQuoteCoverage>(
      withHealthAccess(`${acaQuotePath(kind, id, year)}/coverage`, access),
    );
  },
  /** "Find a doctor" on a quote: the year's loaded lists, or healthcare.gov
   *  near the household's ZIP when the year isn't loaded. Planning scope. */
  async findAcaQuoteDoctor(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    name: string,
    access?: HealthAccessReason | null,
  ): Promise<{
    source: "carrier_files" | "healthcare_gov";
    zip: string | null;
    more_exist: boolean;
    matches: ProviderMatch[];
  }> {
    return request(withHealthAccess(`${acaQuotePath(kind, id, year)}/find-doctor`, access), {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  },
  async addAcaCoverageCheck(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    input: AcaCoverageCheckInput,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: AcaCoverageCheck }> {
    return request(withHealthAccess(`${acaQuotePath(kind, id, year)}/coverage-checks`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateAcaCoverageCheck(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    checkId: string,
    input: AcaCoverageCheckInput & { version_number: number },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: AcaCoverageCheck }> {
    return request(
      withHealthAccess(
        `${acaQuotePath(kind, id, year)}/coverage-checks/${encodeURIComponent(checkId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async addAcaQuoteHospital(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    input: AcaQuoteHospitalInput,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true }> {
    return request(withHealthAccess(`${acaQuotePath(kind, id, year)}/hospitals`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async removeAcaQuoteRow(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    what: "files" | "plans" | "hospitals" | "coverage-checks",
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true }> {
    return request(
      withHealthAccess(
        `${acaQuotePath(kind, id, year)}/${what}/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "DELETE" },
    );
  },
  async undoAcaQuoteRow(
    kind: AcaQuoteKind,
    id: string,
    year: number,
    what: "files" | "plans" | "hospitals" | "coverage-checks",
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true }> {
    return request(
      withHealthAccess(
        `${acaQuotePath(kind, id, year)}/${what}/${encodeURIComponent(rowId)}/undo`,
        access,
      ),
      { method: "POST" },
    );
  },
  // ─── The Medicare quote (Quote tab step 3) ───────────────────────────
  // Every call is health information: the stated reason, when an admin
  // gave one, rides along on each.
  async getMedicareQuote(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    access?: HealthAccessReason | null,
  ): Promise<MedicareQuoteResponse> {
    return request<MedicareQuoteResponse>(
      withHealthAccess(medicareQuotePath(kind, id, year), access),
    );
  },
  /** Upload a printout with what the browser read from it. Multipart, so
   *  it cannot go through request(). */
  async uploadMedicareQuoteFile(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    input: {
      file: File;
      file_kind: MedicareQuoteFile["file_kind"];
      read_json: string | null;
      reader_version: string | null;
      /** A carrier doctor-search record's carrier ("Humana"). */
      carrier_name?: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: MedicareQuoteFile }> {
    const fd = new FormData();
    fd.append("file", input.file);
    fd.append("file_kind", input.file_kind);
    if (input.read_json) fd.append("read_json", input.read_json);
    if (input.reader_version) fd.append("reader_version", input.reader_version);
    if (input.carrier_name) fd.append("carrier_name", input.carrier_name);
    const resp = await fetch(
      apiUrl(withHealthAccess(`${medicareQuotePath(kind, id, year)}/files`, access)),
      { method: "POST", body: fd, credentials: "include" },
    );
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't upload that file.");
    return (await resp.json()) as { ok: true; row: MedicareQuoteFile };
  },
  /** One stored printout, as a PDF to open. */
  async downloadMedicareQuoteFile(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    fileId: string,
    access?: HealthAccessReason | null,
  ): Promise<Blob> {
    const resp = await fetch(
      apiUrl(
        withHealthAccess(
          `${medicareQuotePath(kind, id, year)}/files/${encodeURIComponent(fileId)}`,
          access,
        ),
      ),
      { credentials: "include" },
    );
    if (!resp.ok) throw await fileDownloadError(resp, "Couldn't open that file.");
    return resp.blob();
  },
  async addMedicareQuotePlan(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    input: MedicareQuotePlanInput,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: MedicareQuotePlan }> {
    return request(withHealthAccess(`${medicareQuotePath(kind, id, year)}/plans`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateMedicareQuotePlan(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    planId: string,
    input: MedicareQuotePlanInput & { version_number: number },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: MedicareQuotePlan }> {
    return request(
      withHealthAccess(
        `${medicareQuotePath(kind, id, year)}/plans/${encodeURIComponent(planId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async addMedicareQuoteDoctorCheck(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    input: MedicareQuoteDoctorCheckInput,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: MedicareQuoteDoctorCheck }> {
    return request(withHealthAccess(`${medicareQuotePath(kind, id, year)}/doctor-checks`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateMedicareQuoteDoctorCheck(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    checkId: string,
    input: MedicareQuoteDoctorCheckInput & { version_number: number },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; row: MedicareQuoteDoctorCheck }> {
    return request(
      withHealthAccess(
        `${medicareQuotePath(kind, id, year)}/doctor-checks/${encodeURIComponent(checkId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async getMedicareQuoteEmailOptions(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    access?: HealthAccessReason | null,
  ): Promise<MedicareQuoteEmailOptions> {
    return request(withHealthAccess(`${medicareQuotePath(kind, id, year)}/email-options`, access));
  },
  /** Save the stay / switch email as an Outlook draft. Nothing is sent. */
  async draftMedicareQuoteEmail(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    input: {
      sender_user_id: string;
      pharmacy_name: string | null;
      include_biggest_drug: boolean;
      booking_url: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true; kind: "stay" | "switch"; draft_mailbox_upn: string; draft_web_link: string | null; recipients: number }> {
    return request(withHealthAccess(`${medicareQuotePath(kind, id, year)}/draft-email`, access), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /** Remove a plan or a file from the quote (soft delete), or undo that. */
  async removeMedicareQuoteRow(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    what: "plans" | "files" | "doctor-checks",
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true }> {
    return request(
      withHealthAccess(
        `${medicareQuotePath(kind, id, year)}/${what}/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "DELETE" },
    );
  },
  async undoMedicareQuoteRow(
    kind: MedicareQuoteKind,
    id: string,
    year: number,
    what: "plans" | "files" | "doctor-checks",
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<{ ok: true }> {
    return request(
      withHealthAccess(
        `${medicareQuotePath(kind, id, year)}/${what}/${encodeURIComponent(rowId)}/undo`,
        access,
      ),
      { method: "POST" },
    );
  },
  async listHouseholdHealthLists(
    householdId: string,
    access?: HealthAccessReason | null,
  ): Promise<HouseholdHealthPerson[]> {
    const r = await request<{ people: HouseholdHealthPerson[] }>(
      withHealthAccess(`/api/households/${encodeURIComponent(householdId)}/health-lists`, access),
    );
    return r.people;
  },
  async addHouseholdProvider(
    householdId: string,
    input: {
      person_label: string;
      provider_name: string;
      specialty?: string | null;
      phone?: string | null;
      npi?: string | null;
      street_address?: string | null;
      notes?: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<ContactProvider> {
    const r = await request<{ provider: ContactProvider }>(
      withHealthAccess(`/api/households/${encodeURIComponent(householdId)}/providers`, access),
      { method: "POST", body: JSON.stringify(input) },
    );
    return r.provider;
  },
  async updateHouseholdProvider(
    householdId: string,
    rowId: string,
    patch: Partial<{
      person_label: string | null;
      provider_name: string;
      specialty: string | null;
      phone: string | null;
      npi: string | null;
      street_address: string | null;
      notes: string | null;
    }>,
    access?: HealthAccessReason | null,
  ): Promise<ContactProvider> {
    const r = await request<{ provider: ContactProvider }>(
      withHealthAccess(
        `/api/households/${encodeURIComponent(householdId)}/providers/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    return r.provider;
  },
  async deleteHouseholdProvider(
    householdId: string,
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<void> {
    await request(
      withHealthAccess(
        `/api/households/${encodeURIComponent(householdId)}/providers/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "DELETE" },
    );
  },
  async addHouseholdMedication(
    householdId: string,
    input: {
      person_label: string;
      medication_name: string;
      dosage?: string | null;
      frequency?: string | null;
      notes?: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<ContactMedication> {
    const r = await request<{ medication: ContactMedication }>(
      withHealthAccess(`/api/households/${encodeURIComponent(householdId)}/medications`, access),
      { method: "POST", body: JSON.stringify(input) },
    );
    return r.medication;
  },
  async updateHouseholdMedication(
    householdId: string,
    rowId: string,
    patch: Partial<{
      person_label: string | null;
      medication_name: string;
      dosage: string | null;
      frequency: string | null;
      notes: string | null;
    }>,
    access?: HealthAccessReason | null,
  ): Promise<ContactMedication> {
    const r = await request<{ medication: ContactMedication }>(
      withHealthAccess(
        `/api/households/${encodeURIComponent(householdId)}/medications/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    return r.medication;
  },
  async deleteHouseholdMedication(
    householdId: string,
    rowId: string,
    access?: HealthAccessReason | null,
  ): Promise<void> {
    await request(
      withHealthAccess(
        `/api/households/${encodeURIComponent(householdId)}/medications/${encodeURIComponent(rowId)}`,
        access,
      ),
      { method: "DELETE" },
    );
  },
  /** Give a profile-less row to a real person. One click; the row keeps
   *  its id and its whole history. */
  async designateHealthRow(
    householdId: string,
    input: { list_kind: "provider" | "medication"; row_id: string; contact_id: string },
    access?: HealthAccessReason | null,
  ): Promise<void> {
    await request(
      withHealthAccess(
        `/api/households/${encodeURIComponent(householdId)}/health-lists/designate`,
        access,
      ),
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // ─── Three-year income history (S7) ──────────────────────────────────
  //
  // PUT rather than POST: the write is an upsert keyed on the calendar
  // year, so re-entering 2024 is a correction, not a duplicate.

  async listIncomeYears(contactId: string): Promise<ContactIncomeYear[]> {
    const r = await request<{ income_years: ContactIncomeYear[] }>(
      `/api/contacts/${encodeURIComponent(contactId)}/income-years`,
    );
    return r.income_years;
  },
  async saveIncomeYear(
    contactId: string,
    input: { income_year: number; annual_income?: number | null; notes?: string | null },
  ): Promise<ContactIncomeYear> {
    const r = await request<{ income_year: ContactIncomeYear }>(
      `/api/contacts/${encodeURIComponent(contactId)}/income-years`,
      { method: "PUT", body: JSON.stringify(input) },
    );
    return r.income_year;
  },
  async deleteIncomeYear(contactId: string, rowId: string): Promise<void> {
    await request(
      `/api/contacts/${encodeURIComponent(contactId)}/income-years/${encodeURIComponent(rowId)}`,
      { method: "DELETE" },
    );
  },

  // ─── Schedule of assets (S7, estate cases) ───────────────────────────

  async listCaseAssets(serviceRecordId: string): Promise<PlanningCaseAsset[]> {
    const r = await request<{ assets: PlanningCaseAsset[] }>(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/schedule-of-assets`,
    );
    return r.assets;
  },
  async addCaseAsset(
    serviceRecordId: string,
    input: PlanningCaseAssetInput,
  ): Promise<PlanningCaseAsset> {
    const r = await request<{ asset: PlanningCaseAsset }>(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/schedule-of-assets`,
      { method: "POST", body: JSON.stringify(input) },
    );
    return r.asset;
  },
  async updateCaseAsset(
    serviceRecordId: string,
    assetId: string,
    patch: Partial<PlanningCaseAssetInput>,
  ): Promise<PlanningCaseAsset> {
    const r = await request<{ asset: PlanningCaseAsset }>(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/schedule-of-assets/${encodeURIComponent(assetId)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    return r.asset;
  },
  async deleteCaseAsset(serviceRecordId: string, assetId: string): Promise<void> {
    await request(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/schedule-of-assets/${encodeURIComponent(assetId)}`,
      { method: "DELETE" },
    );
  },

  async getRecentReviews(opts?: {
    days?: number;
    company?: string;
  }): Promise<RecentReviewsResponse> {
    const params = new URLSearchParams();
    if (opts?.days) params.set("days", String(opts.days));
    if (opts?.company) params.set("company", opts.company);
    const qs = params.toString();
    return request<RecentReviewsResponse>(`/api/reviews/recent${qs ? `?${qs}` : ""}`);
  },

  async listMedications(contactId: string, access?: HealthAccessReason | null): Promise<ContactMedication[]> {
    const r = await request<{ medications: ContactMedication[] }>(
      withHealthAccess(`/api/contacts/${encodeURIComponent(contactId)}/medications`, access),
    );
    return r.medications;
  },
  async addMedication(
    contactId: string,
    input: {
      medication_name: string;
      dosage?: string | null;
      frequency?: string | null;
      notes?: string | null;
    },
    access?: HealthAccessReason | null,
  ): Promise<ContactMedication> {
    const r = await request<{ medication: ContactMedication }>(
      withHealthAccess(`/api/contacts/${encodeURIComponent(contactId)}/medications`, access),
      { method: "POST", body: JSON.stringify(input) },
    );
    return r.medication;
  },
  async updateMedication(
    contactId: string,
    medicationId: string,
    patch: Partial<{
      medication_name: string;
      dosage: string | null;
      frequency: string | null;
      notes: string | null;
    }>,
    access?: HealthAccessReason | null,
  ): Promise<ContactMedication> {
    const r = await request<{ medication: ContactMedication }>(
      withHealthAccess(
        `/api/contacts/${encodeURIComponent(contactId)}/medications/${encodeURIComponent(medicationId)}`,
        access,
      ),
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    return r.medication;
  },
  async deleteMedication(
    contactId: string,
    medicationId: string,
    access?: HealthAccessReason | null,
  ): Promise<void> {
    await request(
      withHealthAccess(
        `/api/contacts/${encodeURIComponent(contactId)}/medications/${encodeURIComponent(medicationId)}`,
        access,
      ),
      { method: "DELETE" },
    );
  },

  // CEO Cockpit dashboard — the single-payload endpoint that powers
  // Grant's dashboard. Response is cached server-side for 5 minutes;
  // clients can call it freely and expect sub-50ms on cache hits.
  async getGrantDashboard(): Promise<GrantDashboardResponse> {
    return request<GrantDashboardResponse>("/api/dashboard/grant");
  },

  // Brandon's Insurance dashboard — book-of-business intelligence for
  // Stewardship Insurance. Same 5-minute server-side cache as Grant.
  async getBrandonDashboard(): Promise<BrandonDashboardResponse> {
    return request<BrandonDashboardResponse>("/api/dashboard/brandon");
  },

  // Jake's Financial advisor dashboard. Same 5-min server-side cache.
  async getJakeDashboard(): Promise<JakeDashboardResponse> {
    return request<JakeDashboardResponse>("/api/dashboard/jake");
  },

  // Brian Ream's Financial advisor dashboard (relationship briefing).
  // Same 5-min server-side cache.
  async getBrianDashboard(): Promise<BrianDashboardResponse> {
    return request<BrianDashboardResponse>("/api/dashboard/brian");
  },

  // Brian's funds/indices markets card — live quotes via the keyless
  // feed (15-min server cache). Best-effort: ok=false means the card
  // hides itself rather than breaking the briefing.
  async getBrianFunds(symbols?: string[]): Promise<BrianFundsResponse> {
    const qs =
      symbols && symbols.length > 0 ? `?symbols=${encodeURIComponent(symbols.join(","))}` : "";
    return request<BrianFundsResponse>(`/api/dashboard/brian-funds${qs}`);
  },

  // Ryan Delviken's Planning dashboard re-cut (program S2): gross
  // income + households-served tiles, the needs-attention rail, and
  // last-week's reviews.
  async getRyanPlanningDashboard(): Promise<RyanPlanningDashboardResponse> {
    return request<RyanPlanningDashboardResponse>("/api/dashboard/ryan-planning");
  },

  // Andrea Middleton's Planning Ops queues (program S3): estate-plan +
  // open-enrollment queues, and the this-week zone.
  async getAndreaPlanningOps(): Promise<AndreaPlanningOpsResponse> {
    return request<AndreaPlanningOpsResponse>("/api/dashboard/andrea-planning-ops");
  },

  // Cambria's mortgage close-out cockpit: the files parked at
  // Closed - Compliance, the loans closing soon, and her own close-out
  // tasks. One request, because the three are one screen — see the
  // route's comment for why they are not three endpoints.
  async getMortgageCloseOutCockpit(): Promise<MortgageCloseOutCockpit> {
    return request<MortgageCloseOutCockpit>("/api/dashboards/mortgage/close-out");
  },

  // Mike's (or Cole's, later) own Private Money loan desk — new
  // requests + files in Processing, one request. See
  // shared/db/private-money-loan-desk.ts for the two zones.
  async getPrivateMoneyLoanDesk(): Promise<PmLoanDesk> {
    return request<PmLoanDesk>("/api/dashboards/mortgage/private-money-desk");
  },

  // ── Voice-to-notes ────────────────────────────────────────────────
  // Record audio → Whisper transcript → Claude-structured note →
  // approvable follow-up tasks. Audio is sent base64 in JSON so it
  // rides the standard request helper; raw audio is never stored.
  async transcribeVoiceNote(audioBase64: string): Promise<{ transcript: string; segments?: TranscribedSegment[] }> {
    return request<{ transcript: string; segments?: TranscribedSegment[] }>("/api/voice-notes/transcribe", {
      method: "POST",
      body: JSON.stringify({ audio_base64: audioBase64 }),
    });
  },
  async structureVoiceNote(
    transcript: string,
    opts?: { mode?: "cleanup" | "summary" },
  ): Promise<{ structured: boolean; title: string | null; body: string }> {
    return request("/api/voice-notes/structure", {
      method: "POST",
      body: JSON.stringify({
        transcript,
        ...(opts?.mode ? { mode: opts.mode } : {}),
      }),
    });
  },
  async createVoiceNote(input: {
    entity_type: HouseholdNoteKind;
    entity_id: string;
    title: string | null;
    body: string;
  }): Promise<{ note: VoiceNote; suggestions: VoiceNoteSuggestion[] }> {
    return request("/api/voice-notes", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Generic advisor home aggregator — powers the role-dashboard
  // archetypes (Relationship Briefing + Operational Cockpit). One call,
  // scoped to the caller's BOOK by default. A servicer / back-office
  // cockpit (Mylah, Cambria, Andrea) passes { scope: "company", company }
  // to scope every zone to their company instead of an empty personal
  // book; the server clamps the company to the caller's entitlement.
  async getDashboardHome(opts?: {
    scope?: "company";
    company?: string;
  }): Promise<DashboardHomeResponse> {
    const qs = new URLSearchParams();
    if (opts?.scope === "company") {
      qs.set("scope", "company");
      if (opts.company) qs.set("company", opts.company);
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<DashboardHomeResponse>(`/api/dashboard/home${suffix}`);
  },
  // The caller's ACTIVE realty deals, split list side vs buy side, each
  // with a derived stage + next step + days-on-market. Powers the realty
  // cockpit's deal-status panel (Nate/Mark). Lazy-loaded apart from /home.
  async getRealtyDeals(): Promise<RealtyDealsResponse> {
    return request<RealtyDealsResponse>("/api/dashboard/realty-deals");
  },
  // Listing engagement — the "is this home getting engagement?" snapshots
  // an advisor captures each morning (Zillow / Facebook / showings / email).
  async getListingEngagement(listingId: string): Promise<ListingEngagement> {
    return request<ListingEngagement>(`/api/realty-listings/${listingId}/engagement`);
  },
  async recordListingEngagement(
    listingId: string,
    metrics: ListingEngagementInput,
  ): Promise<{ snapshot: EngagementSnapshot }> {
    return request<{ snapshot: EngagementSnapshot }>(
      `/api/realty-listings/${listingId}/engagement`,
      { method: "POST", body: JSON.stringify(metrics) },
    );
  },
  // A weekly seller-update DRAFT (email + text) for a listing, assembled
  // from the listing + latest engagement. The advisor reviews + sends 1:1.
  async getSellerUpdate(listingId: string): Promise<SellerUpdateDraft> {
    return request<SellerUpdateDraft>(`/api/realty-listings/${listingId}/seller-update`);
  },
  // Realty market update — latest Cromford brief + per-city developments.
  async getMarketUpdate(): Promise<MarketUpdate> {
    return request<MarketUpdate>("/api/realty-market");
  },
  async recordMarketNote(input: {
    kind: "city_development" | "cromford";
    city?: string | null;
    title?: string | null;
    body: string;
  }): Promise<{ note: MarketNote }> {
    return request<{ note: MarketNote }>("/api/realty-market", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // Expeditions — shared, collaborator-enabled planning notes (Nate's
  // "current expedition" card). Owner + collaborators co-edit one note.
  async getExpeditions(): Promise<{ expeditions: Expedition[] }> {
    return request<{ expeditions: Expedition[] }>("/api/expeditions");
  },
  async getExpeditionTeammates(): Promise<{ teammates: ExpeditionTeammate[] }> {
    return request<{ teammates: ExpeditionTeammate[] }>("/api/expeditions/teammates");
  },
  async createExpedition(input: {
    title: string;
    note?: string | null;
    event_date?: string | null;
  }): Promise<{ expedition: Expedition }> {
    return request<{ expedition: Expedition }>("/api/expeditions", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateExpedition(
    id: string,
    patch: {
      title?: string;
      note?: string | null;
      event_date?: string | null;
      status?: "active" | "done" | "archived";
    },
  ): Promise<{ expedition: Expedition }> {
    return request<{ expedition: Expedition }>(`/api/expeditions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  async addExpeditionCollaborator(id: string, userId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/expeditions/${id}/collaborators`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    });
  },
  async removeExpeditionCollaborator(id: string, userId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/expeditions/${id}/collaborators/${userId}`, {
      method: "DELETE",
    });
  },
  // Planning book grouped by line of business (Health / Life-DI-LTC /
  // Medicare / Estate). Powers Ryan's "Lines of business" snapshot.
  // S20 — the Planning book of business. `scope: "mine"` narrows to the
  // caller's own serviced cases; the default measures the whole Planning
  // company, which is what Ryan asked for.
  async getPlanningBook(scope: "everyone" | "mine"): Promise<PlanningBookOfBusiness> {
    return request<PlanningBookOfBusiness>(
      `/api/dashboard/planning-book${scope === "mine" ? "?scope=mine" : ""}`,
    );
  },

  async getPlanningLob(): Promise<PlanningLobSummary> {
    return request<PlanningLobSummary>("/api/dashboard/planning-lob");
  },
  // PR 15 — Ryan's "Today's 5": the caller's at-risk Planning households,
  // ranked, with primary-contact phone/email for one-click call/email.
  async getTodaysFive(): Promise<{ items: TodaysFiveItem[] }> {
    return request<{ items: TodaysFiveItem[] }>("/api/dashboard/todays-five");
  },
  // AI "before you advise" brief for one household (Haiku + rule-based
  // fallback). Re-callable; never throws on AI-off (returns rule_based).
  async getMeetingPrep(householdId: string): Promise<MeetingPrepResponse> {
    return request<MeetingPrepResponse>("/api/dashboard/meeting-prep", {
      method: "POST",
      body: JSON.stringify({ household_id: householdId }),
    });
  },
  async markHouseholdContacted(
    householdId: string,
  ): Promise<{ ok: boolean; activity_id: string; occurred_at: string }> {
    return request("/api/dashboard/jake/mark-contacted", {
      method: "POST",
      body: JSON.stringify({ household_id: householdId }),
    });
  },
  async undoMarkContacted(activityId: string): Promise<{ ok: boolean }> {
    return request("/api/dashboard/jake/mark-contacted/undo", {
      method: "POST",
      body: JSON.stringify({ activity_id: activityId }),
    });
  },

  // Baker's compliance cockpit — 4-zone retreat demo dashboard.
  async getBakerDashboard(): Promise<BakerDashboardResponse> {
    return request<BakerDashboardResponse>("/api/dashboard/baker");
  },

  async listComplianceIssues(
    company: string,
  ): Promise<{ items: BakerComplianceIssue[]; total: number }> {
    return request<{ items: BakerComplianceIssue[]; total: number }>(
      `/api/compliance/company/${encodeURIComponent(company)}`,
    );
  },

  async resolveComplianceIssue(id: string, notes?: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/compliance/issue/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    });
  },

  async completeComplianceTask(
    id: string,
    body: { completion_notes?: string; evidence_url?: string } = {},
  ): Promise<{ ok: true; override: boolean }> {
    return request<{ ok: true; override: boolean }>(
      `/api/compliance/tasks/${encodeURIComponent(id)}/complete`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  async createManualTask(body: {
    title: string;
    description?: string;
    company?: string;
    due_date?: string;
    priority?: "low" | "normal" | "high" | "urgent";
  }): Promise<{ ok: true; id: string }> {
    return request<{ ok: true; id: string }>(`/api/compliance/manual-tasks`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async completeManualTask(id: string, notes?: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/compliance/manual-tasks/${encodeURIComponent(id)}/complete`,
      { method: "POST", body: JSON.stringify({ notes }) },
    );
  },

  async listChecklistInstances(
    params: { company?: string; status?: "active" | "completed" | "cancelled" } = {},
  ): Promise<{ items: BakerChecklistInstance[]; total: number }> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    if (params.status) qs.set("status", params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ items: BakerChecklistInstance[]; total: number }>(
      `/api/compliance/checklists${suffix}`,
    );
  },

  async getChecklistInstance(
    id: string,
  ): Promise<{ instance: BakerChecklistInstance; tasks: BakerChecklistTask[] }> {
    return request<{ instance: BakerChecklistInstance; tasks: BakerChecklistTask[] }>(
      `/api/compliance/checklists/${encodeURIComponent(id)}`,
    );
  },

  async listComplianceRules(params: { company?: string; active?: "all" } = {}): Promise<{
    items: BakerComplianceRule[];
    total: number;
  }> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    if (params.active) qs.set("active", params.active);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ items: BakerComplianceRule[]; total: number }>(
      `/api/admin/compliance-rules${suffix}`,
    );
  },

  async listChecklistTemplates(params: { company?: string } = {}): Promise<{
    items: BakerChecklistTemplate[];
    total: number;
  }> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ items: BakerChecklistTemplate[]; total: number }>(
      `/api/admin/checklist-templates${suffix}`,
    );
  },

  // ─── Admin / team management ────────────────────────────────────────
  // Every admin endpoint is role-gated server-side (requireSystemAdmin
  // or requireAdminAccess). The UI additionally hides the surfaces,
  // but the API is the real security boundary.

  async listAdminUsers(): Promise<{ users: AdminTeamUser[] }> {
    return request<{ users: AdminTeamUser[] }>("/api/admin/team/users");
  },

  /** Polish #42 — update an advisor's buffer_percent and/or
   *  skill_tags so the workload planner has correct staffing
   *  signal. system_admin only. */
  async updateUserStaffing(
    userId: string,
    input: { buffer_percent?: number; skill_tags?: string[] },
  ): Promise<{ id: string; updated: number }> {
    return request<{ id: string; updated: number }>(
      `/api/admin/team/users/${encodeURIComponent(userId)}/staffing`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
  },

  /** Edit who a teammate IS — the directory half of the Edit user
   *  panel. Cosmetic: nothing here changes what they can reach, which
   *  is why it is a separate call from updateUserAccess below.
   *  Omitted keys are left alone; sending "" clears an optional field.
   *  system_admin only. */
  async updateUserProfile(
    userId: string,
    input: {
      name?: string;
      job_title?: string | null;
      phone?: string | null;
      employment_type?: "w2" | "contractor";
      employment_started_at?: string | null;
      employment_ended_at?: string | null;
    },
  ): Promise<{ id: string; updated: string[] }> {
    return request<{ id: string; updated: string[] }>(
      `/api/admin/team/users/${encodeURIComponent(userId)}/profile`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  /** Edit what a teammate can REACH — role and company scope. Separate
   *  from the profile call on purpose: this one is audited as its own
   *  event and carries the guards (no self role change, last admin
   *  protected, ambassadors excluded). company_scope of null means
   *  every company; [] means none. system_admin only. */
  async updateUserAccess(
    userId: string,
    input: { role_slug?: string; company_scope?: string[] | null },
  ): Promise<{ id: string; updated: string[] }> {
    return request<{ id: string; updated: string[] }>(
      `/api/admin/team/users/${encodeURIComponent(userId)}/access`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  /** Set another user's profile photo. Same 2 MB cap and format rules
   *  as uploadAvatar (they share one implementation server-side).
   *  Multipart, so it can't go through request(). system_admin only. */
  async uploadUserAvatar(userId: string, file: File): Promise<{ image: string }> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(apiUrl(`/api/admin/team/users/${encodeURIComponent(userId)}/avatar`), {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      let payload: { error?: string; message?: string } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        // non-JSON error body — fall back to a generic message
      }
      throw new ApiError(
        res.status,
        payload.message ?? payload.error ?? `upload_failed_${res.status}`,
        payload.error ?? null,
      );
    }
    return (await res.json()) as { image: string };
  },

  /** Remove another user's profile photo (back to initials). */
  async removeUserAvatar(userId: string): Promise<{ image: null }> {
    return request<{ image: null }>(`/api/admin/team/users/${encodeURIComponent(userId)}/avatar`, {
      method: "DELETE",
    });
  },

  async listAdminInvites(): Promise<{ invites: AdminTeamInvite[] }> {
    return request<{ invites: AdminTeamInvite[] }>("/api/admin/team/invites");
  },

  async createAdminInvite(input: CreateAdminInviteInput): Promise<CreateAdminInviteResponse> {
    return request<CreateAdminInviteResponse>("/api/admin/invites", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Creates the ambassador's account AND sends their invite. The
  // ordinary invite endpoint cannot do this — it attaches a password to
  // a user row that already exists, and ambassadors have none until
  // this runs. Server hardcodes role, Realty scope, and contractor
  // status; there is deliberately no way to pass them in.
  async createAmbassador(input: {
    name: string;
    email: string;
  }): Promise<CreateAmbassadorResponse> {
    return request<CreateAmbassadorResponse>("/api/admin/team/ambassadors", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async resendAdminInvite(inviteId: string): Promise<CreateAdminInviteResponse> {
    return request<CreateAdminInviteResponse>(
      `/api/admin/team/invites/${encodeURIComponent(inviteId)}/resend`,
      { method: "POST", body: "{}" },
    );
  },

  async revokeAdminInvite(inviteId: string): Promise<void> {
    await request<null>(`/api/admin/team/invites/${encodeURIComponent(inviteId)}/revoke`, {
      method: "POST",
      body: "{}",
    });
  },

  async deactivateAdminUser(userId: string): Promise<void> {
    await request<null>(`/api/admin/team/users/${encodeURIComponent(userId)}/deactivate`, {
      method: "POST",
      body: "{}",
    });
  },

  async reactivateAdminUser(userId: string): Promise<void> {
    await request<null>(`/api/admin/team/users/${encodeURIComponent(userId)}/reactivate`, {
      method: "POST",
      body: "{}",
    });
  },

  async setCommissionNotificationPreference(
    userId: string,
    preference: CommissionNotificationPreference,
  ): Promise<{ ok: true; preference: CommissionNotificationPreference }> {
    return request<{ ok: true; preference: CommissionNotificationPreference }>(
      `/api/admin/team/users/${encodeURIComponent(userId)}/commission-notification-preference`,
      { method: "POST", body: JSON.stringify({ preference }) },
    );
  },

  // ─── Pulse Detail ──────────────────────────────────────────────────
  async getPulseDetail(metric: PulseMetricSlug, window: PulseWindow): Promise<PulseDetailResponse> {
    return request<PulseDetailResponse>(
      `/api/dashboard/pulse-detail?metric=${encodeURIComponent(metric)}&window=${encodeURIComponent(window)}`,
    );
  },

  // ─── Metric contributors (Wave E "What Moved The Number") ─────────
  async getMetricContributors(
    metric: PulseMetricSlug,
    fromMonth: string,
    toMonth: string,
  ): Promise<{ contributors: MetricContributor[] }> {
    return request<{ contributors: MetricContributor[] }>(
      `/api/dashboard/contributors?metric=${encodeURIComponent(metric)}&from=${encodeURIComponent(fromMonth)}&to=${encodeURIComponent(toMonth)}`,
    );
  },
  async listAllMetricContributors(): Promise<{
    contributors: MetricContributor[];
  }> {
    return request<{ contributors: MetricContributor[] }>("/api/admin/metric-contributors");
  },
  async createMetricContributor(input: {
    metric_slug: PulseMetricSlug;
    contribution_month: string;
    title: string;
    description: string | null;
    magnitude: "major" | "moderate" | "minor";
    direction: "positive" | "negative" | "neutral";
    related_entity_type?: string | null;
    related_entity_id?: string | null;
    source?: "manual" | "claude_suggested" | "claude_accepted";
  }): Promise<{ contributor: MetricContributor }> {
    return request<{ contributor: MetricContributor }>("/api/admin/metric-contributors", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateMetricContributor(
    id: string,
    input: Partial<{
      metric_slug: string;
      contribution_month: string;
      title: string;
      description: string | null;
      magnitude: "major" | "moderate" | "minor";
      direction: "positive" | "negative" | "neutral";
    }>,
  ): Promise<{ contributor: MetricContributor }> {
    return request<{ contributor: MetricContributor }>(
      `/api/admin/metric-contributors/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async deleteMetricContributor(id: string): Promise<void> {
    await request<void>(`/api/admin/metric-contributors/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  async suggestMetricContributors(input: {
    metric_slug: string;
    contribution_month: string;
    movement_summary: string;
    context_signals?: string[];
  }): Promise<{ candidates: MetricContributorCandidate[] }> {
    return request<{ candidates: MetricContributorCandidate[] }>(
      "/api/admin/metric-contributors/suggest",
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // ─── Cross-sell reasoning (Wave E) ─────────────────────────────────
  async getCrossSellReasoning(
    householdId: string,
    targetCompanySlug: string,
  ): Promise<CrossSellReasoningResponse> {
    return request<CrossSellReasoningResponse>("/api/watchlist/cross-sell-reasoning", {
      method: "POST",
      body: JSON.stringify({
        household_id: householdId,
        target_company_slug: targetCompanySlug,
      }),
    });
  },

  // ─── View preferences (Wave F) ──────────────────────────────────────
  async getViewPreference(surfaceKey: string): Promise<ResolvedViewPreference> {
    return request<ResolvedViewPreference>(
      `/api/view-preferences/${encodeURIComponent(surfaceKey)}`,
    );
  },
  async setViewPreference(
    surfaceKey: string,
    preferencesJson: string,
  ): Promise<{ preference: UserViewPreferenceRow }> {
    return request<{ preference: UserViewPreferenceRow }>(
      `/api/view-preferences/${encodeURIComponent(surfaceKey)}`,
      {
        method: "PUT",
        body: JSON.stringify({ preferences_json: preferencesJson }),
      },
    );
  },
  async clearViewPreference(surfaceKey: string): Promise<void> {
    await request<void>(`/api/view-preferences/${encodeURIComponent(surfaceKey)}`, {
      method: "DELETE",
    });
  },
  async listAllViewPreferences(): Promise<{
    preferences: UserViewPreferenceRow[];
  }> {
    return request<{ preferences: UserViewPreferenceRow[] }>("/api/view-preferences/all");
  },
  async listAllViewDefaults(): Promise<{
    defaults: RoleViewDefaultRow[];
  }> {
    return request<{ defaults: RoleViewDefaultRow[] }>("/api/admin/view-defaults");
  },
  async listViewDefaultsForRole(roleSlug: string): Promise<{ defaults: RoleViewDefaultRow[] }> {
    return request<{ defaults: RoleViewDefaultRow[] }>(
      `/api/admin/view-defaults/${encodeURIComponent(roleSlug)}`,
    );
  },
  async setViewDefault(
    roleSlug: string,
    surfaceKey: string,
    preferencesJson: string,
  ): Promise<{ default: RoleViewDefaultRow }> {
    return request<{ default: RoleViewDefaultRow }>(
      `/api/admin/view-defaults/${encodeURIComponent(roleSlug)}/${encodeURIComponent(surfaceKey)}`,
      {
        method: "PUT",
        body: JSON.stringify({ preferences_json: preferencesJson }),
      },
    );
  },
  async clearViewDefault(roleSlug: string, surfaceKey: string): Promise<void> {
    await request<void>(
      `/api/admin/view-defaults/${encodeURIComponent(roleSlug)}/${encodeURIComponent(surfaceKey)}`,
      { method: "DELETE" },
    );
  },

  // ─── Pulse auto-narrative + AI banner (Wave E) ─────────────────────
  async getPulseNarrative(
    metric: PulseMetricSlug,
    window: PulseWindow,
  ): Promise<PulseNarrativeResponse> {
    return request<PulseNarrativeResponse>("/api/dashboard/pulse-narrative", {
      method: "POST",
      body: JSON.stringify({ metric, window }),
    });
  },

  // ─── Dashboard FY targets (Wave D.2) ────────────────────────────────
  async getDashboardTarget(
    metric: PulseMetricSlug,
    fiscalYear?: number,
  ): Promise<{ target: DashboardTarget } | null> {
    const fy = fiscalYear ?? new Date().getUTCFullYear();
    const url = `/api/dashboard/targets?metric=${encodeURIComponent(metric)}&fy=${fy}`;
    try {
      return await request<{ target: DashboardTarget }>(url);
    } catch (err) {
      // 404 = no target set; surface as null so caller doesn't have
      // to try/catch. Other errors re-throw.
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  async listDashboardTargetsForFY(
    fiscalYear?: number,
  ): Promise<{ fiscal_year: number; targets: DashboardTarget[] }> {
    const fy = fiscalYear ?? new Date().getUTCFullYear();
    return request<{ fiscal_year: number; targets: DashboardTarget[] }>(
      `/api/dashboard/targets?fy=${fy}`,
    );
  },
  async listAllDashboardTargets(): Promise<{ targets: DashboardTarget[] }> {
    return request<{ targets: DashboardTarget[] }>("/api/admin/dashboard-targets");
  },
  async createDashboardTarget(input: {
    metric_slug: PulseMetricSlug;
    fiscal_year: number;
    target_value: number;
    notes: string | null;
  }): Promise<{ target: DashboardTarget }> {
    return request<{ target: DashboardTarget }>("/api/admin/dashboard-targets", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateDashboardTarget(
    id: string,
    input: Partial<{
      metric_slug: string;
      fiscal_year: number;
      target_value: number;
      notes: string | null;
    }>,
  ): Promise<{ target: DashboardTarget }> {
    return request<{ target: DashboardTarget }>(
      `/api/admin/dashboard-targets/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async deleteDashboardTarget(id: string): Promise<void> {
    await request<void>(`/api/admin/dashboard-targets/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  // ─── Dashboard event annotations (Wave D.3) ─────────────────────────
  async getDashboardEvents(
    metric: PulseMetricSlug,
    fromIso: string,
    toIso: string,
  ): Promise<{ events: DashboardEvent[] }> {
    return request<{ events: DashboardEvent[] }>(
      `/api/dashboard/events?metric=${encodeURIComponent(metric)}&from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
    );
  },
  async listAllDashboardEvents(): Promise<{ events: DashboardEvent[] }> {
    return request<{ events: DashboardEvent[] }>("/api/admin/dashboard-events/events");
  },
  async createDashboardEvent(input: {
    event_date: string;
    metric_slug: string | null;
    title: string;
    description: string | null;
    category: DashboardEventCategory;
  }): Promise<{ event: DashboardEvent }> {
    return request<{ event: DashboardEvent }>("/api/admin/dashboard-events/events", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateDashboardEvent(
    id: string,
    input: Partial<{
      event_date: string;
      metric_slug: string | null;
      title: string;
      description: string | null;
      category: DashboardEventCategory;
    }>,
  ): Promise<{ event: DashboardEvent }> {
    return request<{ event: DashboardEvent }>(
      `/api/admin/dashboard-events/events/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async deleteDashboardEvent(id: string): Promise<void> {
    await request<void>(`/api/admin/dashboard-events/events/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  // ─── Watchlist ─────────────────────────────────────────────────────
  // `total` is the TRUE count over the same WHERE as the (capped) list. null
  // when the server could not produce a trustworthy one (at-risk scores not
  // populated); the page then says nothing about a total rather than guess.
  async getAtRiskHouseholds(
    qs = "",
  ): Promise<{ households: AtRiskHousehold[]; total: number | null }> {
    return request<{ households: AtRiskHousehold[]; total: number | null }>(
      `/api/watchlist/at-risk-households${qs ? `?${qs}` : ""}`,
    );
  },
  async getCrossSellOpportunities(
    qs = "",
  ): Promise<{ households: CrossSellOpportunity[]; total: number }> {
    return request<{ households: CrossSellOpportunity[]; total: number }>(
      `/api/watchlist/cross-sell-opportunities${qs ? `?${qs}` : ""}`,
    );
  },
  // The notification feed (AI nudges + assigned tasks) for the bell panel
  // and the /notifications history page. Maps the API's snake_case
  // time_label to the timeLabel the UI renders.
  async listNotifications(): Promise<MockNotification[]> {
    const resp = await request<{
      items: Array<{
        id: string;
        kind: MockNotification["kind"];
        title: string;
        context: string;
        time_label: string;
        href: string;
        read?: boolean;
      }>;
    }>("/api/notifications");
    return resp.items.map((i) => ({
      id: i.id,
      kind: i.kind,
      title: i.title,
      context: i.context,
      timeLabel: i.time_label,
      href: i.href,
      read: i.read === true,
    }));
  },
  // Save that the signed-in person read these notifications, so the bell
  // still knows after a reload (Brandon, 2026-09-28: "9+" on every reload).
  async markNotificationsRead(ids: string[]): Promise<{ marked: number }> {
    return request<{ marked: number }>("/api/notifications/read", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
  },
  async getStuckDeals(qs = ""): Promise<{ deals: StuckDeal[]; total: number }> {
    return request<{ deals: StuckDeal[]; total: number }>(
      `/api/watchlist/stuck-deals${qs ? `?${qs}` : ""}`,
    );
  },
  async getTeamCapacity(qs = ""): Promise<{ advisors: TeamCapacityAdvisor[] }> {
    return request<{ advisors: TeamCapacityAdvisor[] }>(
      `/api/watchlist/team-capacity${qs ? `?${qs}` : ""}`,
    );
  },
  async getWatchlistSummary(): Promise<WatchlistSummary> {
    return request<WatchlistSummary>("/api/watchlist/summary");
  },
  // Relationship Engagement zones (the two-axis complement to at-risk).
  async getWeOweHouseholds(qs = ""): Promise<{ households: EngagementZoneHousehold[] }> {
    return request<{ households: EngagementZoneHousehold[] }>(
      `/api/watchlist/engagement/we-owe${qs ? `?${qs}` : ""}`,
    );
  },
  async getGoingQuietHouseholds(qs = ""): Promise<{ households: EngagementZoneHousehold[] }> {
    return request<{ households: EngagementZoneHousehold[] }>(
      `/api/watchlist/engagement/going-quiet${qs ? `?${qs}` : ""}`,
    );
  },
  async getEngagementZoneSummary(qs = ""): Promise<EngagementZoneSummary> {
    return request<EngagementZoneSummary>(`/api/watchlist/engagement/summary${qs ? `?${qs}` : ""}`);
  },

  // ─── List views ───────────────────────────────────────────────────
  async listOpportunities(
    qs: string,
    signal?: AbortSignal,
  ): Promise<ListResponse<OpportunityListItem>> {
    return request<ListResponse<OpportunityListItem>>(
      `/api/opportunities/list${qs ? `?${qs}` : ""}`,
      { ...(signal ? { signal } : {}) },
    );
  },
  async listTransactions(
    qs: string,
    signal?: AbortSignal,
  ): Promise<ListResponse<TransactionListItem>> {
    return request<ListResponse<TransactionListItem>>(
      `/api/transactions/list${qs ? `?${qs}` : ""}`,
      { ...(signal ? { signal } : {}) },
    );
  },
  // Processing redesign (PR2) — exception-strip counts for the list.
  async getTransactionExceptions(qs: string): Promise<TransactionExceptionCounts> {
    return request<TransactionExceptionCounts>(`/api/transactions/exceptions${qs ? `?${qs}` : ""}`);
  },
  // Processing redesign (fix-PR10, M2) — true per-stage totals for the
  // Board column headers. Returns the open-transaction count per stage
  // over the same base population as the list.
  async getTransactionStageCounts(qs: string): Promise<{ stage_counts: TransactionStageCount[] }> {
    return request<{ stage_counts: TransactionStageCount[] }>(
      `/api/transactions/stage-counts${qs ? `?${qs}` : ""}`,
    );
  },

  // ── Editable pipeline ("board") templates ──────────────────────────
  // Resolved pipelines (config defaults overlaid with DB edits + custom
  // pipelines) for the board's Board selector + the admin editor.
  async listPipelines(
    workflow: PipelineWorkflow = "transaction",
    opts?: { onlyEditable?: boolean },
  ): Promise<{ pipelines: ResolvedPipeline[] }> {
    const qs = opts?.onlyEditable ? "&onlyEditable=1" : "";
    return request<{ pipelines: ResolvedPipeline[] }>(
      `/api/pipelines?workflow=${encodeURIComponent(workflow)}${qs}`,
    );
  },
  // Single resolved pipeline (config default overlaid with any DB edit) —
  // used by the New Opportunity modal's "Starting stage" picker so it
  // always shows the SAME columns the board renders, including a domain
  // owner's own edits in the Pipeline boards editor.
  async getPipeline(
    company: string,
    workflow: PipelineWorkflow,
    type: string,
  ): Promise<{
    company_slug: string;
    workflow: PipelineWorkflow;
    type: string;
    source: "custom" | "config";
    stages: PipelineStage[];
    can_edit: boolean;
  }> {
    return request(
      `/api/pipelines/${encodeURIComponent(company)}/${encodeURIComponent(
        workflow,
      )}/${encodeURIComponent(type)}`,
    );
  },
  async savePipeline(
    company: string,
    workflow: PipelineWorkflow,
    type: string,
    input: { stages: PipelineStage[]; display_name: string | null },
  ): Promise<{
    ok: true;
    id: string;
    version_number: number;
    created: boolean;
    stages: PipelineStage[];
  }> {
    return request(
      `/api/pipelines/${encodeURIComponent(company)}/${encodeURIComponent(
        workflow,
      )}/${encodeURIComponent(type)}`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  async revertPipeline(
    company: string,
    workflow: PipelineWorkflow,
    type: string,
  ): Promise<{ ok: true; reverted: boolean; source: string; stages: PipelineStage[] }> {
    return request(
      `/api/pipelines/${encodeURIComponent(company)}/${encodeURIComponent(
        workflow,
      )}/${encodeURIComponent(type)}`,
      { method: "DELETE" },
    );
  },
  // Ongoing Care bulk "Assign owner" (Andrea, 2026-09-30). One reversible
  // run; the returned bulk_action_id is the undo handle.
  async bulkAssignPlanOwner(body: {
    plan_ids: string[];
    owner_user_id: string | null;
  }): Promise<BulkAssignPlanOwnerResult> {
    return request<BulkAssignPlanOwnerResult>(`/api/ongoing-care/bulk-assign-owner`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async undoBulkAssignPlanOwner(
    bulkActionId: string,
  ): Promise<{ ok: true; restored: number; left_alone: number }> {
    return request<{ ok: true; restored: number; left_alone: number }>(
      `/api/ongoing-care/bulk-assign-owner/${encodeURIComponent(bulkActionId)}/undo`,
      { method: "POST" },
    );
  },
  async listRhythms(qs: string, signal?: AbortSignal): Promise<ListResponse<RhythmListItem>> {
    return request<ListResponse<RhythmListItem>>(
      `/api/rhythm-instances/list${qs ? `?${qs}` : ""}`,
      {
        ...(signal ? { signal } : {}),
      },
    );
  },
  // True per-stage totals for the staged renewal board's column pills —
  // counted over the whole filtered population, never the loaded page
  // (2026-08-20; the renewal twin of getTransactionStageCounts).
  async getRhythmStageCounts(
    qs: string,
    signal?: AbortSignal,
  ): Promise<{ stage_counts: { stage: string | null; count: number }[] }> {
    return request(`/api/rhythm-instances/stage-counts${qs ? `?${qs}` : ""}`, {
      ...(signal ? { signal } : {}),
    });
  },
  // Per-column "soonest N" cards for the staged renewal/care board
  // (2026-09-15 — Joy: a column reading 30 showed only 3, because the
  // board used to ask /list for ONE page of 200 cards for the WHOLE
  // board instead of asking per column). stageKeys is the pipeline's
  // column slugs in order (entry column first) — the caller already
  // has this list to render the columns, so it's also the one place
  // that can tell the server which slug is the entry column.
  async listRhythmsBoard(
    qs: string,
    stageKeys: readonly string[],
    perColumn: number,
    signal?: AbortSignal,
  ): Promise<{ items: RhythmListItem[] }> {
    const params = new URLSearchParams(qs);
    params.set("stage_keys", stageKeys.join(","));
    params.set("per_column", String(perColumn));
    return request(`/api/rhythm-instances/board?${params.toString()}`, {
      ...(signal ? { signal } : {}),
    });
  },
  // Brandon's carrier-switch flow (2026-08-21): create the Processing
  // record for an in-house switch from this renewal cycle. Idempotent —
  // a cycle with an open switch returns it instead of creating a twin.
  async startRenewalSwitch(
    rhythmInstanceId: string,
  ): Promise<{ transaction_id: string; existing: boolean }> {
    return request(`/api/ongoing-care/${encodeURIComponent(rhythmInstanceId)}/start-switch`, {
      method: "POST",
    });
  },
  async listContactsForView(
    qs: string,
    // Optional cancellation for keystroke-superseded searches — same
    // D1-stability rationale as listHouseholds.signal.
    signal?: AbortSignal,
  ): Promise<ListResponse<ContactListItem>> {
    return request<ListResponse<ContactListItem>>(`/api/contacts-list/list${qs ? `?${qs}` : ""}`, {
      ...(signal ? { signal } : {}),
    });
  },
  // Service Records PR 5a — cross-company SR list with text search
  // across SR + subtype-table fields (carrier name, policy number,
  // product noun).
  async listServiceRecords(
    qs: string,
    signal?: AbortSignal,
  ): Promise<ListResponse<ServiceRecordListItem>> {
    return request<ListResponse<ServiceRecordListItem>>(
      `/api/service-records/list${qs ? `?${qs}` : ""}`,
      { ...(signal ? { signal } : {}) },
    );
  },

  // Assign / reassign the advisor on ONE service record (Joy, 2026-07-01).
  // Writes through advisor_history (close prior / open new); company-scope
  // enforced server-side. `advisor_user_id: null` clears the assignment.
  async assignServiceRecordAdvisor(
    serviceRecordId: string,
    body: {
      advisor_user_id: string | null;
      reason_code?: string;
      reason_text?: string;
    },
  ): Promise<AssignServiceRecordAdvisorResult> {
    return request<AssignServiceRecordAdvisorResult>(
      `/api/service-records/${encodeURIComponent(serviceRecordId)}/advisor`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );
  },

  // Assign the advisor on a Processing (transaction) record FROM the deal
  // page (Nate 2026-07-14, N12). Routes server-side through the canonical
  // SR-advisor path so the SR advisor + this transaction's owner move
  // together. Returns which SR (if any) it routed through.
  async assignTransactionAdvisor(
    transactionId: string,
    body: {
      advisor_user_id: string | null;
      reason_code?: string;
      reason_text?: string;
    },
  ): Promise<{
    ok: true;
    transaction_id: string;
    routed_through_service_record: string | null;
    new_advisor_user_id: string | null;
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/advisor`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  // Assign an advisor to a batch of service records in one reversible run.
  // Returns a bulk_action_id the caller can pass to undo.
  async bulkAssignServiceRecordAdvisor(body: {
    service_record_ids: string[];
    advisor_user_id: string | null;
    reason_code?: string;
    reason_text?: string;
  }): Promise<BulkAssignAdvisorResult> {
    return request<BulkAssignAdvisorResult>(`/api/service-records/bulk-assign-advisor`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  // Undo a bulk advisor assignment (compensating restore of the prior
  // advisor on every affected relationship).
  async undoBulkAssignServiceRecordAdvisor(
    bulkActionId: string,
  ): Promise<UndoBulkAssignAdvisorResult> {
    return request<UndoBulkAssignAdvisorResult>(
      `/api/service-records/bulk-assign-advisor/${encodeURIComponent(bulkActionId)}/undo`,
      { method: "POST" },
    );
  },

  // Service Records PR 5b — per-user + per-role saved views for list
  // pages. The frontend merges these with the hardcoded config
  // savedViews in ListView.
  async listSavedViews(entityType: string): Promise<{ views: ServerSavedView[] }> {
    return request<{ views: ServerSavedView[] }>(
      `/api/saved-views?entity_type=${encodeURIComponent(entityType)}`,
    );
  },
  async createSavedView(input: {
    entity_type: string;
    slug: string;
    label: string;
    audience?: "personal" | "company" | "global";
    company_slug?: string;
    filters: { key: string; value: string }[];
    conditions?: FilterCondition[];
    columns?: string[] | null;
    density?: "comfortable" | "compact" | "condensed" | null;
    sort_by?: string | null;
    sort_dir?: "asc" | "desc" | null;
    is_pinned?: boolean;
  }): Promise<{ view: ServerSavedView }> {
    return request<{ view: ServerSavedView }>(`/api/saved-views`, {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    });
  },
  async updateSavedView(
    id: string,
    patch: {
      label?: string;
      filters?: { key: string; value: string }[];
      conditions?: FilterCondition[];
      columns?: string[] | null;
      density?: "comfortable" | "compact" | "condensed" | null;
      sort_by?: string | null;
      sort_dir?: "asc" | "desc" | null;
    },
  ): Promise<{ view: ServerSavedView }> {
    return request<{ view: ServerSavedView }>(`/api/saved-views/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
      headers: { "content-type": "application/json" },
    });
  },
  // Resolve one visible view by slug — powers /saved-views/:slug.
  async getSavedViewBySlug(slug: string): Promise<{ view: ServerSavedView }> {
    return request<{ view: ServerSavedView }>(
      `/api/saved-views/by-slug/${encodeURIComponent(slug)}`,
    );
  },
  async deleteSavedView(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/saved-views/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  async pinSavedView(id: string, isPinned: boolean): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/saved-views/${encodeURIComponent(id)}/pin`, {
      method: "POST",
      body: JSON.stringify({ is_pinned: isPinned }),
      headers: { "content-type": "application/json" },
    });
  },

  // ─── Ad audiences (Marketing & Ads Phase 0; system_admin) ─────────
  // Definitions + counts only cross this client — the hashed CSV comes
  // back as a blob download and never enters app state.
  async listAdAudiences(): Promise<{ audiences: AdAudienceSummaryDto[] }> {
    return request<{ audiences: AdAudienceSummaryDto[] }>("/api/admin/integrations/ad-audiences");
  },
  async createAdAudience(input: {
    label: string;
    saved_view_id?: string | null;
    conditions: FilterCondition[];
    platforms: string[];
    purpose: "inclusion" | "exclusion" | "lookalike_seed";
    ad_category?: "standard" | "credit" | "housing";
    company_slug?: string | null;
    include_value_column?: boolean;
  }): Promise<{ audience: AdAudienceSummaryDto }> {
    return request<{ audience: AdAudienceSummaryDto }>("/api/admin/integrations/ad-audiences", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async previewAdAudience(id: string): Promise<{
    summary: {
      matched: number;
      eligible: number;
      suppressed: number;
      paused?: number;
      will_export: number;
      warnings: string[];
    };
    capabilities: Record<string, { customAudience: boolean; lookalike: boolean; note: string }>;
  }> {
    return request(`/api/admin/integrations/ad-audiences/${encodeURIComponent(id)}/preview`);
  },
  async deleteAdAudience(id: string): Promise<{ deleted: boolean }> {
    return request<{ deleted: boolean }>(
      `/api/admin/integrations/ad-audiences/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },
  async exportAdAudienceCsv(
    id: string,
    platform: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(
      apiUrl(
        `/api/admin/integrations/ad-audiences/${encodeURIComponent(id)}/export?platform=${encodeURIComponent(platform)}`,
      ),
      { credentials: "include", headers: { Accept: "text/csv" } },
    );
    if (!response.ok) {
      let code: string | null = null;
      let message = response.statusText || `HTTP ${response.status}`;
      try {
        const body = (await response.json()) as {
          error?: string;
          message?: string;
        };
        code = body.error ?? null;
        if (body.message) message = body.message;
      } catch {
        // ignore — fall back to statusText
      }
      throw new ApiError(response.status, message, code);
    }
    const blob = await response.blob();
    const cd = response.headers.get("Content-Disposition") ?? "";
    const filenameMatch = /filename="([^"]+)"/.exec(cd);
    const filename = filenameMatch?.[1] ?? `audience-${platform}.csv`;
    return { blob, filename };
  },

  // ─── Bulk email (SES) campaigns ───────────────────────────────────
  async previewCampaign(
    // string values for segment filters; string[] for contact_ids
    // (the explicit-list audience from the Email-selected wizard);
    // FilterSet for the registry-driven condition tree.
    audienceFilter: BulkAudienceFilterPayload,
    // Row 6.3: a blast sent as a company counts without that company's opt-outs.
    sendingCompanySlug: string | null = null,
  ): Promise<{ summary: BulkAudienceSummary; dropped?: string[] }> {
    return request<{ summary: BulkAudienceSummary; dropped?: string[] }>(
      `/api/communications/campaigns/preview`,
      {
        method: "POST",
        body: JSON.stringify({ audience_filter: audienceFilter, sending_company_slug: sendingCompanySlug }),
      },
    );
  },
  async createCampaign(input: {
    subject: string;
    body_html?: string;
    body_text?: string;
    from_email?: string;
    reply_to?: string;
    audience_filter: BulkAudienceFilterPayload;
    sending_company_slug?: string | null;
  }): Promise<{ campaign: BulkCampaign }> {
    return request<{ campaign: BulkCampaign }>(`/api/communications/campaigns`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // "Send a test to me" — mails the exact subject/body through the same
  // SES render path a real send uses, to the requesting user's own
  // address only. No campaign id required — works on whatever is
  // currently in the composer, saved or not.
  async testSendCampaign(input: {
    subject: string;
    body_html?: string;
    body_text?: string;
    from_email?: string;
    sending_company_slug?: string | null;
  }): Promise<{ ok: true; sent_to: string }> {
    return request<{ ok: true; sent_to: string }>(`/api/communications/campaigns/test-send`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // "Check SES connection" — admin diagnostic for a refused send. Asks
  // Amazon for this account's sending posture using the exact
  // credentials a blast uses; sends no email to anyone. Always resolves
  // (a rejection comes back as ok:false with the reason), so the caller
  // renders the answer rather than catching an error.
  async checkSesConnection(): Promise<SesConnectionCheck> {
    return request<SesConnectionCheck>(`/api/communications/ses-check`);
  },
  // Bounce-handling wiring (admin). Status says whether Amazon is set up
  // to report bounces/complaints back to the OS; wire performs that
  // AWS-side setup idempotently with the credentials already on file.
  async getEmailInfraStatus(): Promise<EmailInfraStatus> {
    return request<EmailInfraStatus>(`/api/communications/email-infra/status`);
  },
  async wireBounceHandling(): Promise<WireBounceResult> {
    return request<WireBounceResult>(`/api/communications/email-infra/wire-bounces`, {
      method: "POST",
    });
  },
  // Edit a DRAFT. Every field is optional and only what's sent is
  // changed — omitting `audience_filter` deliberately leaves the stored
  // audience alone, which is how a draft aimed at a hand-picked list of
  // contacts survives an edit made in a composer that can't show one.
  async updateCampaign(
    id: string,
    input: {
      subject?: string;
      body_html?: string;
      body_text?: string;
      from_email?: string;
      reply_to?: string;
      audience_filter?: BulkAudienceFilterPayload;
      sending_company_slug?: string | null;
    },
  ): Promise<{ campaign: BulkCampaign }> {
    return request<{ campaign: BulkCampaign }>(
      `/api/communications/campaigns/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  // Put a time on a draft, or pass null to clear it. A scheduled blast
  // stays a draft — editable and cancellable right up until it fires.
  async scheduleCampaign(
    id: string,
    sendAtIso: string | null,
  ): Promise<{ campaign: BulkCampaign }> {
    return request<{ campaign: BulkCampaign }>(
      `/api/communications/campaigns/${encodeURIComponent(id)}/schedule`,
      { method: "POST", body: JSON.stringify({ send_at: sendAtIso }) },
    );
  },
  async listCampaigns(): Promise<{ campaigns: BulkCampaign[] }> {
    return request<{ campaigns: BulkCampaign[] }>(`/api/communications/campaigns`);
  },
  /** Ask Grant to approve a blast (decision SPA-4). Grant's own ask approves it. */
  async requestBlastApproval(id: string): Promise<{ campaign: BulkCampaign; approval: BlastApprovalState }> {
    return request<{ campaign: BulkCampaign; approval: BlastApprovalState }>(
      `/api/communications/campaigns/${encodeURIComponent(id)}/request-approval`,
      { method: "POST" },
    );
  },
  async sendCampaign(id: string): Promise<{ result: BulkCampaignSendResult }> {
    return request<{ result: BulkCampaignSendResult }>(
      `/api/communications/campaigns/${encodeURIComponent(id)}/send`,
      { method: "POST" },
    );
  },
  async cancelCampaign(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/communications/campaigns/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
    });
  },

  // ─── Outlook paced batch sends ("Email selected", 2026-08-01) ──────
  async previewBatchSend(contactIds: string[]): Promise<BatchSendPreview> {
    return request<BatchSendPreview>(`/api/email/batch-send/preview`, {
      method: "POST",
      body: JSON.stringify({ contactIds }),
    });
  },
  async createBatchSend(input: {
    contactIds: string[];
    subject: string;
    bodyHtml: string;
    pacingMode: OutlookBatchPacingMode;
  }): Promise<{ batch: OutlookBatchSend; skipped: BatchSendPreviewRecipient[] }> {
    return request<{
      batch: OutlookBatchSend;
      skipped: BatchSendPreviewRecipient[];
    }>(`/api/email/batch-send`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async listBatchSends(): Promise<{ batches: OutlookBatchSend[] }> {
    return request<{ batches: OutlookBatchSend[] }>(`/api/email/batch-sends`);
  },
  async getBatchSend(
    id: string,
  ): Promise<{ batch: OutlookBatchSend; recipients: OutlookBatchRecipient[] }> {
    return request<{
      batch: OutlookBatchSend;
      recipients: OutlookBatchRecipient[];
    }>(`/api/email/batch-sends/${encodeURIComponent(id)}`);
  },
  async cancelBatchSend(id: string): Promise<{ batch: OutlookBatchSend }> {
    return request<{ batch: OutlookBatchSend }>(
      `/api/email/batch-sends/${encodeURIComponent(id)}/cancel`,
      { method: "POST" },
    );
  },

  // ─── Detail endpoints (v1.x phase 11.16) ───────────────────────────
  async getOpportunityDetail(id: string): Promise<{
    opportunity: OpportunityDetail;
    also_happening: AlsoHappeningItem[];
  }> {
    return request<{
      opportunity: OpportunityDetail;
      also_happening: AlsoHappeningItem[];
    }>(`/api/opportunities/${encodeURIComponent(id)}`);
  },
  async getTransactionDetail(id: string): Promise<{
    transaction: TransactionDetail;
    also_happening: AlsoHappeningItem[];
  }> {
    return request<{
      transaction: TransactionDetail;
      also_happening: AlsoHappeningItem[];
    }>(`/api/transactions/${encodeURIComponent(id)}`);
  },
  // Restore a single archived transaction directly (the id-scoped
  // counterpart of restoreContact/restoreHousehold above). Archiving goes
  // through archiveExecute (kind: "transaction") for the plain-English
  // preview + bulk-undo toast; this is for restoring later, from the
  // ArchivedBanner on the record itself.
  // POST /api/opportunities/:id/restore: bring an archived opportunity back
  // (the ArchivedBanner on the record page).
  async restoreOpportunity(id: string): Promise<{ was_restored: true } | { not_archived: true }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/restore`, {
      method: "POST",
    });
  },
  async restoreTransaction(id: string): Promise<{ was_restored: true } | { not_archived: true }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/restore`, {
      method: "POST",
    });
  },
  async getRhythmInstanceDetail(id: string): Promise<{ rhythm: RhythmInstanceDetail }> {
    return request<{ rhythm: RhythmInstanceDetail }>(
      `/api/rhythm-instances/${encodeURIComponent(id)}`,
    );
  },
  // PR 4 — Ongoing Care Home command-center aggregates.
  async getOngoingCarePortfolio(): Promise<{
    portfolio: OngoingCarePortfolio;
  }> {
    return request<{ portfolio: OngoingCarePortfolio }>(`/api/ongoing-care/portfolio`);
  },
  async getOngoingCareCalendar(): Promise<{
    buckets: OngoingCareCalendarBucket[];
  }> {
    return request<{ buckets: OngoingCareCalendarBucket[] }>(`/api/ongoing-care/calendar`);
  },
  // The full active working set — one fetch feeds the redesigned
  // calendar / timeline / workload / renewal-board views.
  // Financial Ongoing Care as a book (Brian Ream, approved 2026-09-23):
  // households by last conversation, review status from facts. `owner`
  // omitted = the viewer's own book; "all" = everyone's.
  async getFinancialCareBook(params: {
    owner?: string | undefined;
    status?: FinancialBookStatusFilter | undefined;
    q?: string | undefined;
    sort?: FinancialBookSort | undefined;
  }): Promise<{ book: FinancialBook; owners: FinancialBookOwner[]; owner: string }> {
    const qs = new URLSearchParams();
    if (params.owner) qs.set("owner", params.owner);
    if (params.status) qs.set("status", params.status);
    if (params.q) qs.set("q", params.q);
    if (params.sort) qs.set("sort", params.sort);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request(`/api/ongoing-care/financial-book${suffix}`);
  },
  async getFinancialCareTrail(householdId: string): Promise<{ trail: FinancialCareTrail }> {
    return request(`/api/ongoing-care/financial-book/${encodeURIComponent(householdId)}/trail`);
  },
  async listCarePlans(): Promise<{ plans: CarePlan[] }> {
    return request<{ plans: CarePlan[] }>(`/api/ongoing-care/plans`);
  },
  // The Table view's KPI strip — the SAME filtered population its
  // ListView is showing, not the firm-wide set above. `qs` is the raw
  // `f` (and only `f`) query string ListView already has on the URL;
  // see OngoingCareHome's use of this for why pagination/sort/search
  // are deliberately not forwarded (server ignores them anyway).
  async listCarePlansForKpi(qs: string): Promise<{ plans: CarePlan[] }> {
    return request<{ plans: CarePlan[] }>(`/api/rhythm-instances/kpi-plans${qs ? `?${qs}` : ""}`);
  },
  async getOngoingCareMonth(
    yyyymm: string,
    opts: { company?: string | null } = {},
  ): Promise<{ month: string; plans: MonthPlanItem[] }> {
    const qs = opts.company ? `?company=${encodeURIComponent(opts.company)}` : "";
    return request<{ month: string; plans: MonthPlanItem[] }>(
      `/api/ongoing-care/month/${encodeURIComponent(yyyymm)}${qs}`,
    );
  },
  // In-record enrollment (Ryan, 2026-09-01) — "put THIS record on a care
  // plan", the path that didn't exist when a case slipped past automatic
  // enrollment.
  async getCarePlanOptionsForRecord(
    serviceRecordId: string,
  ): Promise<{ options: RecordCarePlanOption[] }> {
    return request<{ options: RecordCarePlanOption[] }>(
      `/api/ongoing-care/for-record/${encodeURIComponent(serviceRecordId)}`,
    );
  },
  async enrollRecordInCarePlan(
    serviceRecordId: string,
    rhythmTemplateId: string,
  ): Promise<{ ok: true; rhythm_instance_id: string }> {
    return request<{ ok: true; rhythm_instance_id: string }>(
      `/api/ongoing-care/for-record/${encodeURIComponent(serviceRecordId)}`,
      { method: "POST", body: JSON.stringify({ rhythm_template_id: rhythmTemplateId }) },
    );
  },
  /** Every plan a family could be on, each On / Not yet / Skipped
   *  (Ongoing Care v2, step 4). `record` narrows to one record's plans. */
  async getHouseholdCarePlans(
    householdId: string,
    opts: { record?: string | null } = {},
  ): Promise<HouseholdCarePlans> {
    const q = opts.record ? `?record=${encodeURIComponent(opts.record)}` : "";
    return request<HouseholdCarePlans>(`/api/ongoing-care/for-household/${encodeURIComponent(householdId)}${q}`);
  },
  /** "Add" on a plan with one cycle per household. */
  async addHouseholdToCarePlan(
    householdId: string,
    rhythmTemplateId: string,
  ): Promise<{ ok: true; rhythm_instance_id: string }> {
    return request(`/api/ongoing-care/for-household/${encodeURIComponent(householdId)}`, {
      method: "POST",
      body: JSON.stringify({ rhythm_template_id: rhythmTemplateId }),
    });
  },
  // PR 7 — create / edit / lifecycle / activation.
  async getCreatableCareTemplates(): Promise<{ templates: CreatableCareTemplate[] }> {
    return request<{ templates: CreatableCareTemplate[] }>(`/api/ongoing-care/creatable-templates`);
  },
  async createOngoingCarePlan(body: {
    rhythm_template_id: string;
    household_id: string;
    cycle_anchor_date: string;
    linked_service_record_id?: string | null;
    owner_user_id?: string | null;
    plan_name?: string | null;
    is_live?: boolean;
    reason_text?: string | null;
  }): Promise<CareCreateResult> {
    return request<CareCreateResult>(`/api/ongoing-care`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async updateOngoingCarePlan(
    id: string,
    body: {
      plan_name?: string | null;
      owner_user_id?: string | null;
      cycle_anchor_date?: string;
      expected_version_number?: number;
      reason_text?: string | null;
    },
  ): Promise<CareMutationResult> {
    return request<CareMutationResult>(`/api/ongoing-care/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  // ── Plan definitions (user-owned care plans, 2026-07-23) ──
  async listCarePlanDefinitions(): Promise<{ definitions: CarePlanDefinition[] }> {
    return request<{ definitions: CarePlanDefinition[] }>(`/api/ongoing-care/plan-definitions`);
  },
  /** The Plans list with each active plan's On / Not yet / Skipped counts. */
  async listCarePlanDefinitionsWithCounts(): Promise<{ definitions: CarePlanDefinition[] }> {
    return request<{ definitions: CarePlanDefinition[] }>(
      `/api/ongoing-care/plan-definitions?with_counts=1`,
    );
  },
  async getCarePlanOverview(id: string, scope: "mine" | "all" = "all"): Promise<CarePlanOverview> {
    return request<CarePlanOverview>(
      `/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/overview?scope=${scope}`,
    );
  },
  async listCarePlanMembers(
    id: string,
    state: CarePlanMemberState,
    opts: { scope?: "mine" | "all"; limit?: number; offset?: number } = {},
  ): Promise<CarePlanMemberPage> {
    const sp = new URLSearchParams({
      state,
      scope: opts.scope ?? "all",
      limit: String(opts.limit ?? 50),
      offset: String(opts.offset ?? 0),
    });
    return request<CarePlanMemberPage>(
      `/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/members?${sp.toString()}`,
    );
  },
  async getCarePlanHistory(id: string): Promise<{ entries: CarePlanHistoryEntry[] }> {
    return request(`/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/history`);
  },
  async skipForCarePlan(
    id: string,
    body: { household_id: string; service_record_id: string | null; reason: string; note?: string | null },
  ): Promise<{ ok: true; skip: { id: string }; cancelledRhythmInstanceId: string | null }> {
    return request(`/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/skips`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async unskipForCarePlan(id: string, skipId: string): Promise<{ ok: true }> {
    return request(
      `/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/skips/${encodeURIComponent(skipId)}`,
      { method: "DELETE" },
    );
  },
  async clearCareUnfinishedFlag(rhythmInstanceId: string): Promise<{ ok: true; cleared: boolean }> {
    return request(`/api/ongoing-care/${encodeURIComponent(rhythmInstanceId)}/clear-unfinished`, {
      method: "POST",
    });
  },
  /** Create screen: how many records a plan with these answers would
   *  cover today, and the stages it would use. Writes nothing. */
  async getCarePlanDraftPreview(q: {
    company: string;
    service_type: string | null;
    grain: "service_record" | "household";
  }): Promise<{
    matches: number;
    stages: { slug: string; label: string }[];
    suggested_window: { start: string; end: string; label: string } | null;
  }> {
    const params = new URLSearchParams({ company: q.company, grain: q.grain });
    if (q.service_type) params.set("service_type", q.service_type);
    return request(`/api/ongoing-care/plan-definitions/draft-preview?${params.toString()}`);
  },
  async getCarePlanRestartPreview(id: string): Promise<{
    restart_now: { cycles: number; would_flag_unfinished: number };
    next_restart_date: string | null;
  }> {
    return request(`/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/restart-preview`);
  },
  async restartCarePlanNow(
    id: string,
  ): Promise<{ ok: true; bulk_action_id: string | null; restarted: number; flagged_unfinished: number }> {
    return request(`/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/restart`, {
      method: "POST",
      body: JSON.stringify({ confirm: "restart" }),
    });
  },
  async undoCarePlanRestart(
    id: string,
    runId: string,
  ): Promise<{ ok: true; reverted: number; skipped_drifted: number; errors: number }> {
    return request(
      `/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/restart-undo/${encodeURIComponent(runId)}`,
      { method: "POST" },
    );
  },
  async getCarePlanDefinition(id: string): Promise<CarePlanDefinitionDetail> {
    return request<CarePlanDefinitionDetail>(
      `/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}`,
    );
  },
  async createCarePlanDefinition(body: {
    display_name: string;
    company_slug: string;
    service_type?: string | null;
    anchor_mode: "fixed_window" | "record_date" | "all_year";
    anchor_window_start?: string | null;
    anchor_window_end?: string | null;
    auto_enroll?: boolean;
    description?: string | null;
    enrollment_grain?: "service_record" | "household";
    owner_user_id?: string | null;
    backup_owner_user_id?: string | null;
    restart_month_day?: string | null;
    /** Bypasses the "a plan already covers this" warning (409
     *  duplicate_shape) once the caller has seen it and chosen to build
     *  a second plan on the same product/window anyway. An exact-name
     *  collision (409 duplicate_name) has no such override. */
    allow_duplicate_shape?: boolean;
  }): Promise<{ ok: true; definition: CarePlanDefinition }> {
    return request(`/api/ongoing-care/plan-definitions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async updateCarePlanDefinition(
    id: string,
    body: {
      display_name?: string;
      anchor_window_start?: string | null;
      anchor_window_end?: string | null;
      auto_enroll?: boolean;
      description?: string | null;
      archived?: boolean;
      archived_reason?: string | null;
      restart_month_day?: string | null;
      restart_stage_slug?: string | null;
      unfinished_handling?: "flag" | "reset";
      owner_user_id?: string | null;
      backup_owner_user_id?: string | null;
      skip_reasons?: string[] | null;
    },
  ): Promise<{ ok: true; definition: CarePlanDefinition }> {
    return request(`/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  async previewCarePlanEnrollment(id: string): Promise<{ preview: CareEnrollmentPreview }> {
    return request(
      `/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/enrollment-preview`,
    );
  },
  // scope:"mine" enrolls only the caller's own book; omitted (or "all")
  // enrolls the plan's whole company cohort, as before.
  async enrollCarePlanCohort(
    id: string,
    body: {
      owner_user_id?: string | null;
      is_live?: boolean;
      scope?: "mine" | "all";
    } = {},
  ): Promise<{
    ok: true;
    bulk_action_id: string;
    created: number;
    skipped_missing_date: number;
    skipped_on_purpose: number;
  }> {
    return request(`/api/ongoing-care/plan-definitions/${encodeURIComponent(id)}/enroll`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async undoCarePlanEnrollment(bulkActionId: string): Promise<{ ok: true; restored: number }> {
    return request(
      `/api/ongoing-care/plan-definitions/enroll-undo/${encodeURIComponent(bulkActionId)}`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },

  // Joy PR 11 — write a note on a household (the first note write
  // path; the AI follow-up chips run off this save).
  async addHouseholdNote(
    householdId: string,
    input: { body: string; title?: string | null; important?: boolean; id?: string },
  ): Promise<{ ok: true; note: Note }> {
    // Retry-safe: the same id rides every attempt (see requestDelivered).
    const body = { ...input, id: input.id ?? newNoteId() };
    return requestDelivered(`/api/households/${encodeURIComponent(householdId)}/notes`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Typed note on a contact — parity with addHouseholdNote (the contact
  // Notes tab offers type-or-speak, same as households).
  async addContactNote(
    contactId: string,
    input: { body: string; title?: string | null; important?: boolean; id?: string },
  ): Promise<{ ok: true; note: Note }> {
    // Retry-safe: the same id rides every attempt (see requestDelivered).
    const body = { ...input, id: input.id ?? newNoteId() };
    return requestDelivered(`/api/contacts/${encodeURIComponent(contactId)}/notes`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Nate 2026-07-09 — edit a note (author or system_admin; enforced
  // server-side). Only the passed fields change.
  async updateHouseholdNote(
    householdId: string,
    noteId: string,
    input: {
      body?: string;
      title?: string | null;
      important?: boolean;
      pinned?: boolean;
    },
  ): Promise<{ ok: true; note: Note }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/notes/${encodeURIComponent(noteId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  // Soft-delete a note (reversible via deleted_at). Author or admin.
  async deleteHouseholdNote(householdId: string, noteId: string): Promise<{ ok: true }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/notes/${encodeURIComponent(noteId)}`,
      { method: "DELETE" },
    );
  },

  // Every note in the household this record belongs to, EXCEPT this
  // record's own (those are already listed above wherever this is used).
  // Backs both the Household Note Center and the compact in-record
  // "more notes elsewhere in this household" module — one endpoint, so
  // the two can never disagree about what exists.
  async listHouseholdNotes(
    anchor: { entityType: HouseholdNoteKind; entityId: string },
    opts: {
      kinds?: HouseholdNoteKind[];
      q?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<HouseholdNotesPage> {
    const params = new URLSearchParams({
      entity_type: anchor.entityType,
      entity_id: anchor.entityId,
    });
    if (opts.kinds && opts.kinds.length > 0) params.set("kinds", opts.kinds.join(","));
    if (opts.q) params.set("q", opts.q);
    if (opts.limit != null) params.set("limit", String(opts.limit));
    if (opts.offset != null) params.set("offset", String(opts.offset));
    return request(`/api/household-notes?${params.toString()}`);
  },

  // The Financial-advisor lens's "Notes & meetings" feed (H3): notes,
  // meeting activities, and logged emails for a household, merged into one
  // newest-first, continuously-paginated stream. Gated client-side by
  // isFinancialLensUser; the endpoint itself reads at the same "any
  // authenticated employee" level as every other notes/activity read.
  // H2b — the household notes summary (Financial lens). GET serves the
  // cached paragraph and rewrites it server-side when a note or meeting
  // changed; POST forces a rewrite from the card's Refresh button.
  async getHouseholdNotesSummary(householdId: string): Promise<HouseholdNotesSummaryResponse> {
    return request<HouseholdNotesSummaryResponse>(
      `/api/households/${encodeURIComponent(householdId)}/notes-summary`,
    );
  },

  async refreshHouseholdNotesSummary(householdId: string): Promise<HouseholdNotesSummaryResponse> {
    return request<HouseholdNotesSummaryResponse>(
      `/api/households/${encodeURIComponent(householdId)}/notes-summary/refresh`,
      { method: "POST" },
    );
  },

  // H2 — household action items (Financial lens). The GET may propose new
  // ones (that is the AI half); only the decision call below makes one
  // real, and every decision is audit-logged server-side.
  async listHouseholdActionItems(householdId: string): Promise<HouseholdActionItemsResponse> {
    return request<HouseholdActionItemsResponse>(
      `/api/households/${encodeURIComponent(householdId)}/action-items`,
    );
  },

  async rescanHouseholdActionItems(householdId: string): Promise<HouseholdActionItemsResponse> {
    return request<HouseholdActionItemsResponse>(
      `/api/households/${encodeURIComponent(householdId)}/action-items/rescan`,
      { method: "POST" },
    );
  },

  async decideHouseholdActionItem(
    householdId: string,
    itemId: string,
    decision: HouseholdActionItemDecision,
    opts: { close_task?: boolean } = {},
  ): Promise<{ item: HouseholdActionItem }> {
    return request<{ item: HouseholdActionItem }>(
      `/api/households/${encodeURIComponent(householdId)}/action-items/${encodeURIComponent(itemId)}`,
      { method: "POST", body: JSON.stringify({ decision, ...opts }) },
    );
  },

  // "Add to my To Do" on an "ours" item. Only ever on an advisor's click.
  async addHouseholdActionItemToTodo(
    householdId: string,
    itemId: string,
    assignedToUserId?: string,
  ): Promise<{ item: HouseholdActionItem; task_id: string }> {
    return request<{ item: HouseholdActionItem; task_id: string }>(
      `/api/households/${encodeURIComponent(householdId)}/action-items/${encodeURIComponent(itemId)}/task`,
      {
        method: "POST",
        body: JSON.stringify(assignedToUserId ? { assigned_to_user_id: assignedToUserId } : {}),
      },
    );
  },

  // The "Gifts sent" list on the contact page (Donna, 2026-09-29).
  async listHouseholdGifts(householdId: string, limit = 10): Promise<HouseholdGiftsPage> {
    return request<HouseholdGiftsPage>(
      `/api/households/${encodeURIComponent(householdId)}/gifts?limit=${limit}`,
    );
  },

  // Brian Ream's ask, 2026-09-29: add your own follow-ups, and edit any item
  // (including the AI's suggestions). Shared with everyone who sees the card.
  async addHouseholdActionItem(
    householdId: string,
    input: { side: HouseholdActionItemSide; title: string; promised_on?: string | null },
  ): Promise<{ item: HouseholdActionItem }> {
    return request<{ item: HouseholdActionItem }>(
      `/api/households/${encodeURIComponent(householdId)}/action-items`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  async editHouseholdActionItem(
    householdId: string,
    itemId: string,
    patch: HouseholdActionItemInput & { expected_updated_at?: string },
  ): Promise<{ item: HouseholdActionItem }> {
    return request<{ item: HouseholdActionItem }>(
      `/api/households/${encodeURIComponent(householdId)}/action-items/${encodeURIComponent(itemId)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },

  // Edit a logged gift's wording and/or date (any employee).
  async updateHouseholdGift(
    householdId: string,
    activityId: string,
    patch: { description?: string; occurred_at?: string },
  ): Promise<{ ok: true; gift: HouseholdGiftEntry }> {
    return request<{ ok: true; gift: HouseholdGiftEntry }>(
      `/api/households/${encodeURIComponent(householdId)}/gifts/${encodeURIComponent(activityId)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },

  async listHouseholdNotesMeetingsFeed(
    householdId: string,
    opts: {
      filter?: NotesMeetingsFeedFilter;
      q?: string;
      limit?: number;
      offset?: number;
      /** "mine" (the server default) narrows to the viewer's own
       *  company_scope; "all" widens to every company. */
      company?: "mine" | "all";
    } = {},
  ): Promise<NotesMeetingsFeedPage> {
    const params = new URLSearchParams();
    if (opts.filter) params.set("filter", opts.filter);
    if (opts.q) params.set("q", opts.q);
    if (opts.limit != null) params.set("limit", String(opts.limit));
    if (opts.offset != null) params.set("offset", String(opts.offset));
    if (opts.company) params.set("company", opts.company);
    const qs = params.toString();
    return request(
      `/api/households/${encodeURIComponent(householdId)}/notes-meetings-feed${qs ? `?${qs}` : ""}`,
    );
  },

  // Notes on opportunities / processing (transactions) / service records /
  // ongoing care (rhythm instances) — Ryan Delviken, 2026-08-05: these had
  // no notes doorway at all (only households and contacts did), even
  // though the polymorphic `notes` table has supported them since
  // migration 0011. Same CRUD shape as the household/contact methods
  // above; `kind` picks the API prefix.
  /** `household_id` (2026-09-10) is the household the record belongs to
   *  — what the Notes dock needs to log a TOUCH from a record page, since
   *  a touch is always written on the household. Null only for a contact
   *  with no household. */
  async listEntityNotes(
    kind: NoteEntitySegment,
    entityId: string,
  ): Promise<{ notes: Note[]; household_id?: string | null }> {
    return request(`/api/${kind}/${encodeURIComponent(entityId)}/notes`);
  },

  async addEntityNote(
    kind: NoteEntitySegment,
    entityId: string,
    input: { body: string; title?: string | null; important?: boolean; id?: string },
  ): Promise<{ ok: true; note: Note }> {
    // Retry-safe: the same id rides every attempt (see requestDelivered).
    const body = { ...input, id: input.id ?? newNoteId() };
    return requestDelivered(`/api/${kind}/${encodeURIComponent(entityId)}/notes`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async updateEntityNote(
    kind: NoteEntitySegment,
    entityId: string,
    noteId: string,
    input: { body?: string; title?: string | null; important?: boolean; pinned?: boolean },
  ): Promise<{ ok: true; note: Note }> {
    return request(
      `/api/${kind}/${encodeURIComponent(entityId)}/notes/${encodeURIComponent(noteId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  async deleteEntityNote(
    kind: NoteEntitySegment,
    entityId: string,
    noteId: string,
  ): Promise<{ ok: true }> {
    return request(
      `/api/${kind}/${encodeURIComponent(entityId)}/notes/${encodeURIComponent(noteId)}`,
      { method: "DELETE" },
    );
  },

  async updateContactNote(
    contactId: string,
    noteId: string,
    input: {
      body?: string;
      title?: string | null;
      important?: boolean;
      pinned?: boolean;
    },
  ): Promise<{ ok: true; note: Note }> {
    return request(
      `/api/contacts/${encodeURIComponent(contactId)}/notes/${encodeURIComponent(noteId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  async deleteContactNote(contactId: string, noteId: string): Promise<{ ok: true }> {
    return request(
      `/api/contacts/${encodeURIComponent(contactId)}/notes/${encodeURIComponent(noteId)}`,
      { method: "DELETE" },
    );
  },

  // Joy PRs 10-12 — AI follow-up suggestions.
  async extractFollowupSuggestions(
    sourceEntityType: "note" | "logged_email",
    sourceEntityId: string,
  ): Promise<{ suggestions: FollowupSuggestion[]; skipped?: string }> {
    return request("/api/followup-suggestions/extract", {
      method: "POST",
      body: JSON.stringify({
        source_entity_type: sourceEntityType,
        source_entity_id: sourceEntityId,
      }),
    });
  },

  async listFollowupSuggestions(
    status: "proposed" | "approved" | "dismissed" = "proposed",
  ): Promise<{ suggestions: FollowupSuggestion[] }> {
    return request(`/api/followup-suggestions?status=${status}`);
  },

  async approveFollowupSuggestion(
    id: string,
    edits: { title?: string; due_date?: string | null } = {},
  ): Promise<{ ok: true; task_id: string; suggestion_id: string }> {
    return request(`/api/followup-suggestions/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify(edits),
    });
  },

  async dismissFollowupSuggestion(id: string): Promise<{ ok: true; suggestion_id: string }> {
    return request(`/api/followup-suggestions/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  // Joy PR 9 — set/clear a household-company relationship's coverage
  // wishes (the interim intake highlight on the insurance card).
  async createIntakeForm(
    householdId: string,
    input: { template_slug: string; rhythm_instance_id?: string | null },
  ): Promise<CreateIntakeFormResponse> {
    return request(`/api/households/${encodeURIComponent(householdId)}/intake-forms`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async setCoveragePreferences(
    householdId: string,
    companySlug: string,
    coveragePreferences: string | null,
  ): Promise<{
    ok: true;
    coverage_preferences: string | null;
    coverage_preferences_updated_at: string | null;
  }> {
    return request(
      `/api/households/${encodeURIComponent(householdId)}/companies/${encodeURIComponent(companySlug)}/coverage-preferences`,
      {
        method: "PATCH",
        body: JSON.stringify({ coverage_preferences: coveragePreferences }),
      },
    );
  },

  // Joy PR 6b — move a renewal cycle across the 8-stage workflow.
  // 409 not_a_renewal_plan for non-renewal plans; 409 stale_record on
  // version conflicts (same contract as the other care mutations).
  async setRenewalCycleStage(
    id: string,
    renewalStage: string,
    opts: { expectedVersionNumber?: number; reasonText?: string | null } = {},
  ): Promise<CareMutationResult> {
    return request<CareMutationResult>(
      `/api/ongoing-care/${encodeURIComponent(id)}/renewal-stage`,
      {
        method: "PATCH",
        body: JSON.stringify({
          renewal_stage: renewalStage,
          expected_version_number: opts.expectedVersionNumber,
          reason_text: opts.reasonText ?? null,
        }),
      },
    );
  },

  async pauseOngoingCarePlan(
    id: string,
    body: { paused_reason?: string | null; expected_version_number?: number },
  ): Promise<CareMutationResult> {
    return request<CareMutationResult>(`/api/ongoing-care/${encodeURIComponent(id)}/pause`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async resumeOngoingCarePlan(
    id: string,
    body: { expected_version_number?: number },
  ): Promise<CareMutationResult> {
    return request<CareMutationResult>(`/api/ongoing-care/${encodeURIComponent(id)}/resume`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async cancelOngoingCarePlan(
    id: string,
    body: {
      cancel_reason?: string | null;
      cycle_outcome?: string | null;
      expected_version_number?: number;
    },
  ): Promise<CareMutationResult> {
    return request<CareMutationResult>(`/api/ongoing-care/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async activateOngoingCarePlan(
    id: string,
    body: { expected_version_number?: number },
  ): Promise<CareMutationResult> {
    return request<CareMutationResult>(`/api/ongoing-care/${encodeURIComponent(id)}/activate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async deactivateOngoingCarePlan(
    id: string,
    body: { expected_version_number?: number },
  ): Promise<CareMutationResult> {
    return request<CareMutationResult>(`/api/ongoing-care/${encodeURIComponent(id)}/deactivate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // Convert an open Opportunity to Won — atomically creates a service
  // record + a Processing transaction (see opportunity-transitions.ts).
  // Returns the new ids so the caller can route to the Processing record.
  async transitionOpportunityToWon(
    id: string,
    body?: {
      outcome_reason?: string | null;
      /** Planning wins pick which case the client bought (validated
       *  server-side against the company's service-type catalog). */
      service_type_override?: string;
      /**
       * The Realty offer this win came from accepting. When set, that
       * offer is marked accepted, pointed at the new Processing record,
       * and its terms are carried onto that record's contract card.
       * Ignored by every other company.
       */
      accepted_offer_id?: string | null;
    },
  ): Promise<{
    ok: true;
    opportunity_id: string;
    service_record_id: string;
    transaction_id: string;
    accepted_offer_id?: string | null;
    carried_terms?: Record<string, string | number> | null;
    /** Every term the offer carried that could NOT reach the contract
     *  card, each with a sentence saying why. Never silent. */
    uncarried_terms?: UncarriedOfferTermDto[];
  }> {
    return request<{
      ok: true;
      opportunity_id: string;
      service_record_id: string;
      transaction_id: string;
      accepted_offer_id?: string | null;
      carried_terms?: Record<string, string | number> | null;
      uncarried_terms?: UncarriedOfferTermDto[];
    }>(`/api/opportunities/${encodeURIComponent(id)}/transition-to-won`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  // Close an open Opportunity as Lost (no Processing record created).
  async transitionOpportunityToLost(
    id: string,
    body?: { outcome_reason?: string | null },
  ): Promise<{ ok: true; opportunity_id: string }> {
    return request<{ ok: true; opportunity_id: string }>(
      `/api/opportunities/${encodeURIComponent(id)}/transition-to-lost`,
      { method: "POST", body: JSON.stringify(body ?? {}) },
    );
  },
  // "Already closed? Link it" — the records on this household, for this
  // opportunity's company, that the card could be linked to instead of
  // Mark Won creating a new Processing record (Grant, 2026-09-29).
  async linkableExistingRecords(id: string): Promise<{ candidates: LinkableExistingRecordDto[] }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/linkable-existing-records`);
  },
  // Mark the opportunity Won by linking it to a record that already
  // exists. Creates nothing; "Undo this win" reverses it by unlinking.
  async linkOpportunityToExistingRecord(
    id: string,
    target: { kind: "transaction" | "service_record"; id: string },
  ): Promise<{
    ok: true;
    opportunity_id: string;
    transaction_id: string | null;
    service_record_id: string | null;
    won_at: string;
  }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/link-existing-record`, {
      method: "POST",
      body: JSON.stringify(target),
    });
  },
  // Set or switch a Realty opportunity's side: "sell" (a listing) or
  // "buy" (a buyer). Rewrites opportunity_type; versioned and audited,
  // reversed by choosing the other side. Open Realty opportunities only.
  async setOpportunityRealtySide(
    id: string,
    side: "sell" | "buy",
  ): Promise<{
    opportunity_id: string;
    opportunity_type: string;
    realty_side: "sell" | "buy";
    changed: boolean;
  }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/realty-side`, {
      method: "POST",
      body: JSON.stringify({ side }),
    });
  },

  // Reverse a Won transition: reopens the opportunity and soft-deletes the
  // service record + Processing transaction the win created. This is how a
  // Processing record that should never have existed gets removed — the
  // reason Joey couldn't clear his duplicate Stuart Cruz record.
  async undoOpportunityWon(
    id: string,
    body?: { reason_text?: string | null },
  ): Promise<{
    ok: true;
    opportunity_id: string;
    reverted_service_record_id: string | null;
    reverted_transaction_id: string | null;
    /** True when the win was a link: nothing was removed, only unlinked. */
    was_link?: boolean;
  }> {
    return request<{
      ok: true;
      opportunity_id: string;
      reverted_service_record_id: string | null;
      reverted_transaction_id: string | null;
      was_link?: boolean;
    }>(`/api/opportunities/${encodeURIComponent(id)}/undo-won`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  // Reopen an opportunity marked Lost by mistake. Clears the outcome and
  // leaves the stage as it was; versioned server-side.
  async undoOpportunityLost(
    id: string,
    body?: { reason_text?: string | null },
  ): Promise<{ ok: true; opportunity_id: string }> {
    return request<{ ok: true; opportunity_id: string }>(
      `/api/opportunities/${encodeURIComponent(id)}/undo-lost`,
      { method: "POST", body: JSON.stringify(body ?? {}) },
    );
  },
  // Move an open Opportunity to a different (typically adjacent) stage
  // from the kanban board. Server validates the opportunity is open and
  // the target stage belongs to its company's template, records an
  // entity_versions snapshot, and is company-scope gated.
  async moveOpportunityStage(
    id: string,
    toStage: string,
  ): Promise<{ ok: true; opportunity_id: string; stage: string }> {
    return request<{ ok: true; opportunity_id: string; stage: string }>(
      `/api/opportunities/${encodeURIComponent(id)}/move-stage`,
      { method: "POST", body: JSON.stringify({ stage: toStage }) },
    );
  },
  // Processing board — between-stage move behind the board's move
  // controls + drag-and-drop. Reversible/audited server-side (records an
  // entity_versions snapshot); the board's Undo toast issues the inverse
  // move. Mirrors moveOpportunityStage.
  //
  // A 409 `stage_blocked` means the record owes something first (a
  // Private Money loan's signed Disclosure to Private Investor). When its
  // body says `overridable`, a person may resend with `overrideReason`;
  // readStageBlock() below reads that body off the ApiError.
  async moveTransactionStage(
    id: string,
    toStage: string,
    overrideReason?: string,
  ): Promise<{
    ok: true;
    transaction_id: string;
    stage: string;
  }> {
    return request<{
      ok: true;
      transaction_id: string;
      stage: string;
    }>(`/api/transactions/${encodeURIComponent(id)}/move-stage`, {
      method: "POST",
      body: JSON.stringify(
        overrideReason ? { stage: toStage, override_reason: overrideReason } : { stage: toStage },
      ),
    });
  },
  // Correct WHEN a record entered its current stage (fix for an accidental
  // stage move that corrupted the days-in-stage clock). Does NOT move the
  // stage. Reversible/audited server-side; the response's
  // prior_stage_entered_at powers the caller's 60s compensating-forward undo
  // (re-call with stage_entered_at = the prior value).
  async correctOpportunityStageClock(
    id: string,
    input: {
      stage_entered_at: string;
      reason_code: string;
      reason_text?: string | null;
    },
  ): Promise<{
    ok: true;
    opportunity_id: string;
    stage: string;
    prior_stage_entered_at: string;
    new_stage_entered_at: string;
  }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/correct-stage-clock`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async correctTransactionStageClock(
    id: string,
    input: {
      stage_entered_at: string;
      reason_code: string;
      reason_text?: string | null;
    },
  ): Promise<{
    ok: true;
    transaction_id: string;
    stage: string;
    prior_stage_entered_at: string;
    new_stage_entered_at: string;
  }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/correct-stage-clock`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // Create a new opportunity (the "+ New Opportunity" flow).
  async createOpportunity(input: {
    household_id: string;
    company_slug: string;
    opportunity_type: string;
    // Optional starting stage — must be a real slug on this (company,
    // opportunity_type) board; the server rejects anything else with
    // `invalid_stage`. Omit to land on the board's first column.
    stage?: string;
    owner_user_id?: string | null;
    product_metric?: number | null;
    expected_close_date?: string | null;
    // Source attribution (migration 0207).
    source_type?: string | null;
    source_detail?: string | null;
    source_referred_by_contact_id?: string | null;
    // Row 4.1: the center of influence, when the source is "Referral Partner".
    source_referred_by_vendor_id?: string | null;
    internal_referred_from_company_slug?: string | null;
    internal_referrer_user_id?: string | null;
    is_cross_sell?: boolean;
    // Whose opportunity this is (0252) — household members chosen in the
    // modal. Omit to let the server default (lone member / household
    // primary, provenance-marked for cleanup confirmation).
    for_contact_ids?: string[];
  }): Promise<{ ok: true; id: string }> {
    return request<{ ok: true; id: string }>("/api/opportunities", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Create a new contact. When household_id is set the contact joins
  // that household; otherwise a fresh single-person household is
  // created with this contact as primary.
  //
  // When household_id is NOT set, the server checks for a strong
  // duplicate (same name + same email or phone as an existing
  // contact) and throws ApiError(409, ..., "duplicate_contact") with
  // `err.data.matches: DuplicatePrecheckMatch[]` instead of creating a
  // twin. Pass allow_duplicate: true to create anyway once the caller
  // has confirmed this is really a different person.
  async createContact(input: {
    legal_first_name?: string | null;
    legal_last_name?: string | null;
    preferred_name?: string | null;
    email?: string | null;
    phone_primary?: string | null;
    household_id?: string | null;
    allow_duplicate?: boolean;
  }): Promise<{
    ok: true;
    id: string;
    household_id: string;
    created_household: boolean;
  }> {
    return request<{
      ok: true;
      id: string;
      household_id: string;
      created_household: boolean;
    }>("/api/contacts", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Processing redesign (PR4) — wire the outcome buttons.
  // "complete" delivers the transaction: flips the linked service
  // record active + auto-starts the right Ongoing Care cycle (server
  // side). Conditions are warn-never-block — the UI confirms before
  // calling, the server never hard-blocks on open conditions.
  async transitionTransactionToCompleted(
    id: string,
    body?: { outcome_reason?: string | null; override_reason?: string | null },
  ): Promise<{
    ok: true;
    transaction_id: string;
    service_record_id: string;
    rhythm_instance_id: string | null;
  }> {
    return request<{
      ok: true;
      transaction_id: string;
      service_record_id: string;
      rhythm_instance_id: string | null;
    }>(`/api/transactions/${encodeURIComponent(id)}/transition-to-completed`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  // "fell_through" / "cancelled" close the transaction WITHOUT
  // delivering — no service record flip, no rhythm. reason_code is the
  // structured reporting value; reason_text an optional note.
  async transitionTransactionToClosed(
    id: string,
    body: {
      outcome: "fell_through" | "cancelled";
      reason_code?: string | null;
      reason_text?: string | null;
      /** The date the file ended (yyyy-mm-dd); the server defaults to today. */
      final_disposition_date?: string | null;
      /** Mortgage only — how the application ended, in NMLS terms;
       *  the server defaults to "withdrawn". */
      nmls_action_taken?: string | null;
    },
  ): Promise<{ ok: true; transaction_id: string; outcome: string }> {
    return request<{ ok: true; transaction_id: string; outcome: string }>(
      `/api/transactions/${encodeURIComponent(id)}/transition-to-closed`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  // Reopen (reversibility-first): the inverse of the two close paths.
  // undo-completed reverses a delivered transaction (soft-deletes the
  // spawned rhythm + restores the SR's prior status); undo-closed
  // reverses a fell_through/cancelled close (no satellite rows). Both
  // append a compensating version and are requireAuth (same blast
  // radius as the forward action they reverse).
  async undoTransactionCompleted(
    id: string,
    reasonText?: string | null,
  ): Promise<{
    ok: true;
    transaction_id: string;
    reverted_rhythm_instance_id: string | null;
    restored_service_record_status: string | null;
  }> {
    return request<{
      ok: true;
      transaction_id: string;
      reverted_rhythm_instance_id: string | null;
      restored_service_record_status: string | null;
    }>(`/api/transactions/${encodeURIComponent(id)}/undo-completed`, {
      method: "POST",
      body: JSON.stringify({ reason_text: reasonText ?? null }),
    });
  },
  // ── Move a dead Mortgage / Realty file back to Opportunities ──────
  //
  // The Wagner action (2026-09-14): the deal died with the house, the
  // household is still ours. Preview first — the modal shows what the
  // write will do, computed by the same planner the write uses.
  async moveBackPreview(id: string): Promise<MoveBackPreview> {
    return request<MoveBackPreview>(
      `/api/transactions/${encodeURIComponent(id)}/move-back-preview`,
    );
  },

  async moveTransactionBackToOpportunity(
    id: string,
    body: {
      reason_code?: string | null;
      reason_text?: string | null;
      stage?: string | null;
    },
  ): Promise<{
    ok: true;
    transaction_id: string;
    opportunity_id: string;
    opportunity_created: boolean;
    stage: string;
  }> {
    return request<{
      ok: true;
      transaction_id: string;
      opportunity_id: string;
      opportunity_created: boolean;
      stage: string;
    }>(`/api/transactions/${encodeURIComponent(id)}/move-back-to-opportunity`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async undoTransactionClosed(
    id: string,
    reasonText?: string | null,
  ): Promise<{ ok: true; transaction_id: string; reopened: boolean }> {
    return request<{ ok: true; transaction_id: string; reopened: boolean }>(
      `/api/transactions/${encodeURIComponent(id)}/undo-closed`,
      {
        method: "POST",
        body: JSON.stringify({ reason_text: reasonText ?? null }),
      },
    );
  },

  // Service Records PR 14 — Transaction linked-work panel.
  async getTransactionLinkedWork(id: string): Promise<TransactionLinkedWorkResponse> {
    return request<TransactionLinkedWorkResponse>(
      `/api/transactions/${encodeURIComponent(id)}/linked-work`,
    );
  },

  // NMLS MCR prep worksheet (all loan types combined). Omit params for
  // the most recently completed quarter.
  async getMcrWorksheet(year?: number, quarter?: number): Promise<{ worksheet: McrWorksheetRec }> {
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    if (quarter) params.set("quarter", String(quarter));
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<{ worksheet: McrWorksheetRec }>(`/api/mortgage/call-report${qs}`);
  },

  // NMLS application classification (0249) — how a fell-through /
  // cancelled mortgage application resolved, in the MCR's vocabulary.
  async setTransactionNmlsAction(
    transactionId: string,
    action: string | null,
  ): Promise<{ ok: true; nmls_action_taken: string | null }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/nmls-action`, {
      method: "POST",
      body: JSON.stringify({ action }),
    });
  },

  // Insurance commission reconciliation (Grant, 2026-08-28) — expected
  // vs. what carriers said they paid vs. what QuickBooks booked. Shared
  // between Grant and Brandon, so the server gate is system_admin /
  // compliance / company_admin rather than admin-only.
  async getCommissionReconciliation(fromMonth?: string): Promise<CommissionReconciliationRec> {
    const qs = fromMonth ? `?from_month=${encodeURIComponent(fromMonth)}` : "";
    return request<CommissionReconciliationRec>(
      `/api/admin/commission-reconciliation/summary${qs}`,
    );
  },

  // The commission exception working list (2026-09-12) — every unpaid,
  // short-paid, over-paid and unplaceable case as one flat row, so it can
  // be worked carrier by carrier and exported to Excel. Same access gate
  // as the summary above.
  async getCommissionExceptions(): Promise<CommissionExceptionsRec> {
    return request<CommissionExceptionsRec>("/api/admin/commission-reconciliation/exceptions");
  },

  // Closed Loan Report (close-out program PR 5) — a year of closed
  // traditional loans straight from the loan records. Omit year for the
  // latest year with data.
  async getClosedLoanReport(
    year?: number,
    loanType?: "traditional" | "private_money",
  ): Promise<{ report: ClosedLoanReportRec }> {
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    if (loanType) params.set("type", loanType);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<{ report: ClosedLoanReportRec }>(`/api/mortgage/closed-loan-report${qs}`);
  },
  // The cancelled tab of the same report — Processing records that ended
  // without funding, by the year the file ended.
  async getCancelledApplicationsReport(
    year?: number,
    loanType?: "traditional" | "private_money",
  ): Promise<{ report: CancelledApplicationsReportRec }> {
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    if (loanType) params.set("type", loanType);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<{ report: CancelledApplicationsReportRec }>(
      `/api/mortgage/closed-loan-report/cancelled${qs}`,
    );
  },

  // The auditor's Loan Log — both loan types in one sheet, rolling 24
  // months to the last complete month. No parameters: the window is the
  // report's definition, not a choice.
  // GET /api/mortgage/loan-officers — the Mortgage team names the
  // "Loan officer" field in Edit loan details offers.
  async listMortgageLoanOfficers(): Promise<{ names: string[] }> {
    return request<{ names: string[] }>("/api/mortgage/loan-officers");
  },
    async getLoanLog(): Promise<{ log: LoanLogRec }> {
    return request<{ log: LoanLogRec }>(`/api/mortgage/loan-log`);
  },

  // Mortgage file close-out (Cambria + the assigned LO) — the
  // audit-file checklist on a traditional mortgage Processing record.
  async getTransactionCloseOut(id: string): Promise<MortgageCloseOutResponse> {
    return request<MortgageCloseOutResponse>(
      `/api/transactions/${encodeURIComponent(id)}/close-out`,
    );
  },

  /**
   * Record that this loan was checked against the closing package.
   *
   * An attestation — "I looked" — not a claim the figures agree. The
   * loan's own values are snapshotted server-side, so the card can later
   * say whether the record moved after the check.
   */
  async verifyAgainstClosingPackage(
    transactionId: string,
    documentId?: string | null,
  ): Promise<{ ok: boolean; verification: ClosingPackageVerificationRec | null }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/closing-package-verification`,
      {
        method: "POST",
        body: JSON.stringify(documentId ? { document_id: documentId } : {}),
      },
    );
  },

  /** Mark a lender document "not needed on this file" (reason required). */
  async markCloseOutNotNeeded(
    transactionId: string,
    definitionId: string,
    reason: string,
  ): Promise<{ ok: boolean; waiver_id: string }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/close-out/waivers/${encodeURIComponent(definitionId)}`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
  },

  /** Put that document back on this file's checklist. */
  async clearCloseOutNotNeeded(
    transactionId: string,
    definitionId: string,
  ): Promise<{ ok: boolean; removed: boolean }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/close-out/waivers/${encodeURIComponent(definitionId)}`,
      { method: "DELETE" },
    );
  },

  /** Take the attestation back — a stamp on the wrong file is a mistake
   *  a person must be able to undo. Audited like the stamp itself. */
  async clearClosingPackageVerification(
    transactionId: string,
  ): Promise<{ ok: boolean; removed: boolean; verification: null }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/closing-package-verification`,
      { method: "DELETE" },
    );
  },

  /**
   * The whole loan as one archive, for handing to an auditor (Baker,
   * 2026-08-12). Named for the borrower and ordered by the checklist.
   *
   * The server streams the archive rather than building it, so this
   * cannot report progress — but it also never holds the package in
   * memory at either end. The blob lands here and goes straight to a
   * Blob URL → <a download>, the same shape as the Stew CSV export.
   *
   * Throws ApiError with the server's own words on a loan whose
   * documents can't be read, or one with nothing filed yet.
   */
  async downloadMortgageAuditFile(
    id: string,
    opts?: { skipUnreadable?: boolean },
  ): Promise<{
    blob: Blob;
    filename: string;
    documentCount: number;
    missingCount: number;
    unreadableCount: number;
  }> {
    const response = await fetch(
      apiUrl(
        `/api/transactions/${encodeURIComponent(id)}/audit-file${
          opts?.skipUnreadable ? "?skip_unreadable=1" : ""
        }`,
      ),
      { method: "GET", credentials: "include", headers: { Accept: "application/zip" } },
    );
    if (!response.ok) {
      let code: string | null = null;
      let message = response.statusText || `HTTP ${response.status}`;
      let documents: string[] = [];
      let canBuildWithout = false;
      try {
        const body = (await response.json()) as {
          error?: string;
          message?: string;
          documents?: string[];
          can_build_without?: boolean;
        };
        code = body.error ?? null;
        if (body.message) message = body.message;
        if (Array.isArray(body.documents)) documents = body.documents;
        canBuildWithout = body.can_build_without === true;
      } catch {
        // Not JSON — fall back to the status text.
      }
      // The unreadable-documents case carries a list and an offer, and
      // the card needs both to say anything useful (Grant, 2026-08-13:
      // a bare count with no way forward is a dead end). ApiError can't
      // hold them, so it gets its own error type.
      if (code === "documents_unavailable") {
        throw new AuditFileUnavailableError(message, documents, canBuildWithout);
      }
      throw new ApiError(response.status, message, code);
    }
    const blob = await response.blob();
    const cd = response.headers.get("Content-Disposition") ?? "";
    // Prefer the RFC 5987 form so a non-ASCII surname survives.
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    const plain = /filename="([^"]+)"/.exec(cd);
    const filename = encoded ? decodeURIComponent(encoded[1]!) : (plain?.[1] ?? "Audit File.zip");
    return {
      blob,
      filename,
      documentCount: Number(response.headers.get("X-Audit-File-Documents") ?? "0"),
      missingCount: Number(response.headers.get("X-Audit-File-Missing") ?? "0"),
      unreadableCount: Number(response.headers.get("X-Audit-File-Unreadable") ?? "0"),
    };
  },

  /**
   * The loan's own SharePoint folder — for "Open this loan's folder
   * in SharePoint" (Baker's part (b), 2026-08-13). `exists: false`
   * means nothing has ever been uploaded there yet; the card hides
   * the button in that case rather than opening an empty page.
   */
  async getTransactionFolder(
    id: string,
  ): Promise<{ exists: false; folder_path: string } | { exists: true; url: string }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/folder`);
  },

  /**
   * Which documents behind this loan sit outside its own SharePoint
   * folder — read-only preview for the "Gather this loan's documents"
   * confirm step. Empty array means nothing is scattered.
   */
  async listStrayDocuments(id: string): Promise<{
    strays: { document_id: string; filename: string; currently_filed_under: string }[];
  }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/stray-documents`);
  },

  /**
   * Moves every stray document into the loan's own folder. Nothing is
   * renamed and no record's entity_type changes — this is a physical
   * reorganization only. The response is what `undoGatherDocuments`
   * needs to put everything back, so the caller should hold onto it.
   */
  async gatherLoanDocuments(id: string): Promise<{
    moved: { document_id: string; filename: string; from_folder: string }[];
    failed: { document_id: string; filename: string; reason: string }[];
  }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/gather-documents`, {
      method: "POST",
    });
  },

  /** Reverses a gatherLoanDocuments call using its own response. */
  async undoGatherLoanDocuments(
    id: string,
    moves: { document_id: string; from_folder: string }[],
  ): Promise<{ restored: string[]; failed: { document_id: string; reason: string }[] }> {
    return request(`/api/transactions/${encodeURIComponent(id)}/gather-documents/undo`, {
      method: "POST",
      body: JSON.stringify({ moves }),
    });
  },

  // Processing redesign (PR3) — conditions tracker.
  async listTransactionConditions(id: string): Promise<{ conditions: TransactionCondition[] }> {
    return request<{ conditions: TransactionCondition[] }>(
      `/api/transactions/${encodeURIComponent(id)}/conditions`,
    );
  },
  async createTransactionCondition(
    id: string,
    body: {
      label: string;
      condition_type: ConditionType;
      owner_kind?: ConditionOwnerKind | null;
      due_date?: string | null;
    },
  ): Promise<{ ok: true; condition: TransactionCondition }> {
    return request<{ ok: true; condition: TransactionCondition }>(
      `/api/transactions/${encodeURIComponent(id)}/conditions`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  // Bring a record's checklist in line with its current board's template
  // (Ryan, 2026-08-10): adds what's missing, and removes untouched items
  // left over from a different board. Items already acted on (satisfied /
  // blocked / N/A) are never removed — reported in kept_touched instead.
  async syncTransactionConditionsToTemplate(id: string): Promise<{
    ok: true;
    added: TransactionCondition[];
    removed: TransactionCondition[];
    already_present: number;
    kept_touched: TransactionCondition[];
  }> {
    return request<{
      ok: true;
      added: TransactionCondition[];
      removed: TransactionCondition[];
      already_present: number;
      kept_touched: TransactionCondition[];
    }>(`/api/transactions/${encodeURIComponent(id)}/conditions/sync-template`, {
      method: "POST",
    });
  },
  // Joey program PR5 — Stew-suggested loan conditions (read-only AI).
  async getLoanConditionSuggestions(id: string): Promise<LoanConditionSuggestionsResponse> {
    return request<LoanConditionSuggestionsResponse>(
      `/api/transactions/${encodeURIComponent(id)}/condition-suggestions`,
      { method: "POST" },
    );
  },
  async updateTransactionCondition(
    id: string,
    cid: string,
    patch: {
      status?: ConditionStatus;
      owner_user_id?: string | null;
      due_date?: string | null;
      document_id?: string | null;
      blocked_reason?: string | null;
    },
  ): Promise<{ ok: true; condition: TransactionCondition }> {
    return request<{ ok: true; condition: TransactionCondition }>(
      `/api/transactions/${encodeURIComponent(id)}/conditions/${encodeURIComponent(cid)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },

  // Processing redesign (PR6) — "Vendors & systems" panel. These rows
  // index the external systems a delivery touches (deep-link + staleness
  // stamp); there is no real vendor API sync in v1.
  async listTransactionVendors(id: string): Promise<{ vendors: TransactionVendor[] }> {
    return request<{ vendors: TransactionVendor[] }>(
      `/api/transactions/${encodeURIComponent(id)}/vendors`,
    );
  },
  async createTransactionVendor(
    id: string,
    body: {
      system_label: string;
      system_kind: TransactionVendorKind;
      external_ref?: string | null;
      portal_url?: string | null;
      vendor_id?: string | null;
    },
  ): Promise<{ ok: true; vendor: TransactionVendor }> {
    return request<{ ok: true; vendor: TransactionVendor }>(
      `/api/transactions/${encodeURIComponent(id)}/vendors`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async updateTransactionVendor(
    id: string,
    vid: string,
    patch: {
      system_label?: string;
      system_kind?: TransactionVendorKind;
      external_ref?: string | null;
      portal_url?: string | null;
      status?: TransactionVendorStatus;
      mark_synced?: boolean;
    },
  ): Promise<{ ok: true; vendor: TransactionVendor }> {
    return request<{ ok: true; vendor: TransactionVendor }>(
      `/api/transactions/${encodeURIComponent(id)}/vendors/${encodeURIComponent(vid)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },
  async deleteTransactionVendor(id: string, vid: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/transactions/${encodeURIComponent(id)}/vendors/${encodeURIComponent(vid)}`,
      { method: "DELETE" },
    );
  },

  // ─── Realty party roster (migration 0205, Nate's N26) ─────────────
  // The parties at a deal (client + co-client derived from the household,
  // plus the title / escrow / loan officer / other agent / TC recorded
  // here). The panel multi-selects them and emails via the Graph 1:1
  // composer. GET is open to any employee; writes are company-scoped.
  async listTransactionParties(id: string): Promise<{ parties: TransactionPartyEntry[] }> {
    return request<{ parties: TransactionPartyEntry[] }>(
      `/api/transactions/${encodeURIComponent(id)}/parties`,
    );
  },
  async createTransactionParty(
    id: string,
    body: {
      party_role: TransactionPartyRole;
      side?: TransactionPartySide;
      contact_id?: string | null;
      vendor_id?: string | null;
      display_name?: string | null;
      email?: string | null;
    },
  ): Promise<{ ok: true; party: unknown }> {
    return request<{ ok: true; party: unknown }>(
      `/api/transactions/${encodeURIComponent(id)}/parties`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async updateTransactionParty(
    id: string,
    pid: string,
    patch: {
      party_role?: TransactionPartyRole;
      side?: TransactionPartySide;
      display_name?: string | null;
      email?: string | null;
    } & Partial<TransactionPartyCredentials>,
  ): Promise<{ ok: true; party: unknown }> {
    return request<{ ok: true; party: unknown }>(
      `/api/transactions/${encodeURIComponent(id)}/parties/${encodeURIComponent(pid)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },
  async deleteTransactionParty(id: string, pid: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/transactions/${encodeURIComponent(id)}/parties/${encodeURIComponent(pid)}`,
      { method: "DELETE" },
    );
  },
  // S19 (ryan-planning-views.md §10) — sends the CMS Scope of
  // Appointment form for e-signature, client-then-advisor.
  async sendScopeOfAppointment(
    householdId: string,
    body: {
      contact_id: string;
      product_lines: string[];
      message?: string | null;
    },
  ): Promise<{
    ok: true;
    envelope_id: string;
    signers: Array<{ email: string; name: string; sign_url: string; emailed: boolean }>;
    missing: string[];
  }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/scope-of-appointment/send`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // S21 (andrea-planning-ops.md §6) — fills the Arizona Health Care
  // Directives Registry form for one person on an estate-plan Processing
  // record from what the OS already knows, saves it as a document on the
  // record, and reports what was left blank so the requester knows what
  // to gather before printing.
  async fillAzHdrForm(
    id: string,
    contactId: string,
  ): Promise<{ document_id: string; web_url: string; missing: string[] }> {
    return request<{ document_id: string; web_url: string; missing: string[] }>(
      `/api/transactions/${encodeURIComponent(id)}/az-hdr-form`,
      { method: "POST", body: JSON.stringify({ contact_id: contactId }) },
    );
  },
  // Opportunity party roster (migration 0261) — the household's own
  // contacts (derived) plus stored co-advisors / referral partners /
  // ad-hoc people. Same shape + same pattern as the transaction parties
  // methods above. GET is open to any employee; writes are company-scoped.
  async listOpportunityParties(id: string): Promise<{ parties: OpportunityPartyEntry[] }> {
    return request<{ parties: OpportunityPartyEntry[] }>(
      `/api/opportunities/${encodeURIComponent(id)}/parties`,
    );
  },
  async createOpportunityParty(
    id: string,
    body: {
      party_role: OpportunityPartyRole;
      side?: OpportunityPartySide;
      contact_id?: string | null;
      vendor_id?: string | null;
      user_id?: string | null;
      display_name?: string | null;
      email?: string | null;
    },
  ): Promise<{ ok: true; party: unknown }> {
    return request<{ ok: true; party: unknown }>(
      `/api/opportunities/${encodeURIComponent(id)}/parties`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async updateOpportunityParty(
    id: string,
    pid: string,
    patch: {
      party_role?: OpportunityPartyRole;
      side?: OpportunityPartySide;
      display_name?: string | null;
      email?: string | null;
    },
  ): Promise<{ ok: true; party: unknown }> {
    return request<{ ok: true; party: unknown }>(
      `/api/opportunities/${encodeURIComponent(id)}/parties/${encodeURIComponent(pid)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },
  async deleteOpportunityParty(id: string, pid: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/opportunities/${encodeURIComponent(id)}/parties/${encodeURIComponent(pid)}`,
      { method: "DELETE" },
    );
  },
  // Realty one-click message drafts (Nate, 2026-08-11 — items 6 & 7).
  // Read-only: this returns filled text for the composer, and never sends.
  async getRealtyMessageDrafts(id: string): Promise<RealtyMessageDraftsResponse> {
    return request<RealtyMessageDraftsResponse>(
      `/api/transactions/${encodeURIComponent(id)}/message-drafts`,
    );
  },

  // "Request CLUE report" (Nate, 2026-08-11 #5; changed to an email draft
  // 2026-09-23, also Nate, relaying Joy). Read-only, same as
  // getRealtyMessageDrafts — it renders text for the composer and never
  // sends. The buyer/seller choice is the advisor's, made fresh on each
  // request, so it's a query param rather than something computed once.
  async getRealtyClueDraft(
    id: string,
    args: { subject_party: "buyer" | "seller"; note?: string },
  ): Promise<{
    assignee: { id: string; name: string | null; email: string } | null;
    property: string;
    party: "buyer" | "seller";
    template_slug: string;
    subject: string;
    body: string;
    source: "template" | "built_in";
  }> {
    const params = new URLSearchParams({ party: args.subject_party });
    if (args.note?.trim()) params.set("note", args.note.trim());
    return request(
      `/api/transactions/${encodeURIComponent(id)}/clue-request?${params.toString()}`,
    );
  },

  // Realty contract terms — the contract card's write path (Nate's
  // 2026-08-24 spec §6). Carries concessions + Program ID (N27 / N32),
  // the inspection period (Nate, 2026-08-11), and the six terms his
  // contract card added on 2026-08-25.
  //
  // Any subset: a per-field pencil sends one key, "Edit all fields"
  // sends several, and the reply always echoes the record's full
  // contract state so the card re-renders from one answer.
  //
  // `due_diligence_starts_at` is day zero, not a column: the server
  // turns it into an end date.
  async patchTransactionRealtyFields(
    id: string,
    patch: {
      concessions_amount?: number | null;
      program_id?: string | null;
      earnest_money?: number | null;
      loan_type?: string | null;
      loan_amount?: number | null;
      down_payment?: number | null;
      possession?: string | null;
      repair_credit?: number | null;
      /** Percentage-of-price pins (2026-09-22): the server works out the
       *  matching dollars. Sending the dollars alone clears the pin. */
      earnest_money_pct?: number | null;
      down_payment_pct?: number | null;
      concessions_pct?: number | null;
      repair_credit_pct?: number | null;
      commission_credit_amount?: number | null;
      commission_credit_to?: string | null;
      other_party_name?: string | null;
      cda_sent_to_title_at?: string | null;
      /** The day the buyer signed the purchase contract (2026-10-14). */
      contract_dated?: string | null;
      due_diligence_starts_at?: string | null;
      due_diligence_days?: number | null;
      due_diligence_ends_at?: string | null;
    },
  ): Promise<RealtyContractFields> {
    return request<RealtyContractFields>(
      `/api/transactions/${encodeURIComponent(id)}/realty-fields`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },

  // ─── Admin / Gusto connection (read only; pay-runs program 9.4) ─────
  async getGustoStatus(): Promise<{ connection: GustoConnectionView }> {
    return request<{ connection: GustoConnectionView }>("/api/gusto/status");
  },
  async disconnectGusto(): Promise<{ connection: GustoConnectionView }> {
    return request<{ connection: GustoConnectionView }>("/api/gusto/disconnect", { method: "POST" });
  },

  // ─── Admin / QuickBooks integration ─────────────────────────────────
  // Status shows one row per Stewardship company (always 6 rows, fixed
  // order). `connect` is a 302 redirect through the browser — the UI
  // opens via window.location to trigger the full OAuth dance.
  async getQuickBooksStatus(): Promise<QuickBooksStatusResponse> {
    return request<QuickBooksStatusResponse>("/api/admin/integrations/quickbooks/status");
  },
  async disconnectQuickBooks(stewardshipCompany: string): Promise<QuickBooksDisconnectResponse> {
    return request<QuickBooksDisconnectResponse>("/api/admin/integrations/quickbooks/disconnect", {
      method: "POST",
      body: JSON.stringify({ stewardship_company: stewardshipCompany }),
    });
  },

  async refreshQuickBooks(stewardshipCompany: string): Promise<QuickBooksRefreshResponse> {
    return request<QuickBooksRefreshResponse>("/api/admin/integrations/quickbooks/refresh", {
      method: "POST",
      body: JSON.stringify({ company: stewardshipCompany }),
    });
  },

  async backfillQuickBooks(
    input: { company?: string; months?: number } = {},
  ): Promise<QuickBooksBackfillResponse> {
    return request<QuickBooksBackfillResponse>("/api/admin/integrations/quickbooks/backfill", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Phase B — re-parse existing raw_response_json into the
  // per-account projection. Zero Intuit API calls. Idempotent.
  async backfillQuickBooksAccountSnapshots(
    input: { company?: string } = {},
  ): Promise<QuickBooksAccountSnapshotsBackfillResponse> {
    return request<QuickBooksAccountSnapshotsBackfillResponse>(
      "/api/admin/integrations/quickbooks/backfill-account-snapshots",
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },

  async getQuickBooksLastRefreshed(): Promise<QuickBooksLastRefreshedResponse> {
    return request<QuickBooksLastRefreshedResponse>(
      "/api/admin/integrations/quickbooks/last-refreshed",
    );
  },

  async getQuickBooksTransactionHistory(): Promise<QuickBooksTransactionHistoryResponse> {
    return request<QuickBooksTransactionHistoryResponse>(
      "/api/admin/integrations/quickbooks/transaction-history",
    );
  },

  // ─── Microsoft Graph (Office 365) integration — per-advisor ────────
  // Each advisor connects their OWN mailbox via /preferences/email.
  // The connect endpoint redirects to Microsoft via window.location;
  // status/disconnect are normal JSON. The system_admin roster lives
  // at getMicrosoftGraphAdminRoster (below) and is read-only.

  async getMicrosoftGraphStatus(): Promise<MicrosoftGraphStatusResponse> {
    return request<MicrosoftGraphStatusResponse>("/api/integrations/microsoft-graph/status");
  },

  async disconnectMicrosoftGraph(): Promise<MicrosoftGraphDisconnectResponse> {
    return request<MicrosoftGraphDisconnectResponse>(
      "/api/integrations/microsoft-graph/disconnect",
      { method: "POST", body: "{}" },
    );
  },

  // v1.x phase 11.16 (Tier A item 5) — admin-only one-time migration
  // of SharePoint files from the legacy folder shape to the new
  // household-rooted hierarchy. The endpoint is idempotent — re-runs
  // only act on still-old rows.
  async runSharePointMigration(input: {
    dry_run: boolean;
    limit?: number;
    offset?: number;
  }): Promise<SharePointMigrationResponse> {
    return request<SharePointMigrationResponse>("/api/admin/sharepoint/migrate", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // 2026-08-11 — lift documents stranded in the legacy R2 store into
  // SharePoint. Separate from runSharePointMigration above: that one
  // reshuffles folders for files ALREADY in SharePoint; this one
  // rescues files that never got there (everything the old shared
  // FileUpload component wrote before the same-day fix). Copies, never
  // moves — the R2 original is retained as the undo handle.
  async runSharePointR2Lift(input: {
    dry_run: boolean;
    limit?: number;
  }): Promise<SharePointR2LiftResponse> {
    return request<SharePointR2LiftResponse>("/api/admin/sharepoint/lift-r2", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async undoSharePointR2Lift(batchId: string): Promise<SharePointR2LiftUndoResponse> {
    return request<SharePointR2LiftUndoResponse>("/api/admin/sharepoint/lift-r2/undo", {
      method: "POST",
      body: JSON.stringify({ batch_id: batchId }),
    });
  },

  async getMicrosoftGraphAdminRoster(): Promise<MicrosoftGraphAdminRosterResponse> {
    return request<MicrosoftGraphAdminRosterResponse>(
      "/api/admin/integrations/microsoft-graph/status",
    );
  },

  // ─── Email — Flow B (live-proxy of the advisor's mailbox) ──────────
  // Phase 4: GET /api/email/folders returns the advisor's full
  // Outlook folder tree (to ~4 levels deep). No persistence — fetched
  // fresh on every call.

  // ─── Email snooze (account-stored, 2026-08-15) ─────────────────────
  // Snooze is the OS's own "hide until later" record — nothing here
  // touches the real mailbox, so a snoozed message still shows in
  // Outlook. `mailboxUpn` selects which mailbox the id belongs to
  // (Graph message ids are per-mailbox); null = the user's own.
  async listEmailSnoozes(
    opts: { mailboxUpn?: string | null } = {},
  ): Promise<{ snoozes: EmailSnooze[] }> {
    const qs = new URLSearchParams();
    if (opts.mailboxUpn) qs.set("mailbox", opts.mailboxUpn);
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<{ snoozes: EmailSnooze[] }>(`/api/email/snoozes${tail}`);
  },
  async snoozeEmailMessage(
    messageId: string,
    snoozeUntilIso: string,
    opts: { mailboxUpn?: string | null } = {},
  ): Promise<void> {
    await request(`/api/email/snoozes/${encodeURIComponent(messageId)}`, {
      method: "PUT",
      body: JSON.stringify({
        snooze_until: snoozeUntilIso,
        mailbox: opts.mailboxUpn ?? null,
      }),
    });
  },
  async unsnoozeEmailMessage(
    messageId: string,
    opts: { mailboxUpn?: string | null } = {},
  ): Promise<void> {
    const qs = new URLSearchParams();
    if (opts.mailboxUpn) qs.set("mailbox", opts.mailboxUpn);
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    await request(`/api/email/snoozes/${encodeURIComponent(messageId)}${tail}`, {
      method: "DELETE",
    });
  },
  async adoptLegacyEmailSnoozes(
    entries: { message_id: string; snooze_until: string }[],
    opts: { mailboxUpn?: string | null } = {},
  ): Promise<{ adopted: number }> {
    return request<{ adopted: number }>("/api/email/snoozes/adopt", {
      method: "POST",
      body: JSON.stringify({ entries, mailbox: opts.mailboxUpn ?? null }),
    });
  },

  async getEmailFolders(opts: { mailboxUpn?: string | null } = {}): Promise<EmailFoldersResponse> {
    const qs = new URLSearchParams();
    if (opts.mailboxUpn) qs.set("mailbox", opts.mailboxUpn);
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<EmailFoldersResponse>(`/api/email/folders${tail}`);
  },

  // Lazy-fetch one folder's own subfolders — the level getEmailFolders()
  // doesn't reach for folders nested two-plus levels deep (e.g. an
  // advisor's per-client folders inside a custom top-level folder).
  async getEmailFolderChildren(
    folderId: string,
    opts: { mailboxUpn?: string | null } = {},
  ): Promise<EmailFoldersResponse> {
    const qs = new URLSearchParams();
    if (opts.mailboxUpn) qs.set("mailbox", opts.mailboxUpn);
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<EmailFoldersResponse>(
      `/api/email/folders/${encodeURIComponent(folderId)}/children${tail}`,
    );
  },

  // v1.x phase 11.7 (Tier 5): list of known shared mailboxes the
  // sidebar's mailbox switcher renders. The backend hardcodes the
  // registry so we don't ship the list of UPNs in the JS bundle.
  async getSharedMailboxes(): Promise<{
    mailboxes: Array<{
      upn: string;
      display_name: string;
      description: string;
      // S26 — false for a specific person's own mailbox (never a valid
      // compose "From" choice, only readable). See the route's own
      // comment for why.
      sendable: boolean;
    }>;
  }> {
    return request("/api/email/shared-mailboxes");
  },

  async getEmailMessages(
    folderId: string,
    opts: {
      top?: number;
      next?: string | null;
      // v1.x phase 11.7: shared mailbox UPN. When set the request
      // hits /users/{upn}/mailFolders/{id}/messages instead of
      // /me/mailFolders/.... Server-side validates against a
      // registry; unknown UPNs return 400 without forwarding to
      // Microsoft.
      mailboxUpn?: string | null;
    } = {},
  ): Promise<EmailMessagesResponse> {
    const qs = new URLSearchParams();
    if (opts.top) qs.set("top", String(opts.top));
    if (opts.next) qs.set("next", opts.next);
    if (opts.mailboxUpn) qs.set("mailbox", opts.mailboxUpn);
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return request<EmailMessagesResponse>(
      `/api/email/folders/${encodeURIComponent(folderId)}/messages${suffix}`,
    );
  },

  async getEmailMessage(
    messageId: string,
    opts: { mailboxUpn?: string | null } = {},
  ): Promise<EmailSingleMessageResponse> {
    const qs = new URLSearchParams();
    if (opts.mailboxUpn) qs.set("mailbox", opts.mailboxUpn);
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return request<EmailSingleMessageResponse>(
      `/api/email/messages/${encodeURIComponent(messageId)}${suffix}`,
    );
  },

  async getEmailConversation(
    conversationId: string,
    opts: { mailboxUpn?: string | null } = {},
  ): Promise<EmailConversationResponse> {
    const qs = new URLSearchParams();
    if (opts.mailboxUpn) qs.set("mailbox", opts.mailboxUpn);
    const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
    return request<EmailConversationResponse>(
      `/api/email/conversations/${encodeURIComponent(conversationId)}/messages${suffix}`,
    );
  },

  async searchEmail(q: string, opts: { top?: number } = {}): Promise<EmailSearchResponse> {
    const qs = new URLSearchParams();
    qs.set("q", q);
    if (opts.top) qs.set("top", String(opts.top));
    return request<EmailSearchResponse>(`/api/email/search?${qs.toString()}`);
  },

  // Outlook redesign Phase 3: To/Cc/Bcc autocomplete. Optional signal
  // aborts a keystroke-superseded request so it stops burning D1 CPU
  // server-side (same rationale as HouseholdListParams.signal).
  async getEmailSuggestions(
    q: string,
    limit?: number,
    signal?: AbortSignal,
  ): Promise<EmailRecipientSuggestionsResponse> {
    const qs = new URLSearchParams();
    qs.set("q", q);
    if (limit) qs.set("limit", String(limit));
    return request<EmailRecipientSuggestionsResponse>(
      `/api/contacts/email-suggestions?${qs.toString()}`,
      { ...(signal ? { signal } : {}) },
    );
  },

  // v1.x: Microsoft Graph thread-aware reply / replyAll / forward.
  // Use these instead of plain sendEmail() when there's an original
  // message to thread against — Microsoft handles In-Reply-To +
  // References + the RE: subject prefix + the quoted thread block.
  // mailboxUpn: reply FROM a registered shared mailbox (e.g.
  // insurance@stewardship.pro) the message was read from. Omit / null =
  // the advisor's own mailbox. The message id is scoped to the mailbox
  // it was read from, so this must match the mailbox the reading pane
  // is viewing or Graph 404s. v1.x phase 11.8 send-from.
  async replyToEmail(
    messageId: string,
    bodyHtml: string,
    mailboxUpn?: string | null,
  ): Promise<{ ok: boolean; mode: string }> {
    const q = mailboxUpn ? `?mailbox=${encodeURIComponent(mailboxUpn)}` : "";
    return request<{ ok: boolean; mode: string }>(
      `/api/email/messages/${encodeURIComponent(messageId)}/reply${q}`,
      { method: "POST", body: JSON.stringify({ bodyHtml }) },
    );
  },
  async replyAllToEmail(
    messageId: string,
    bodyHtml: string,
    mailboxUpn?: string | null,
  ): Promise<{ ok: boolean; mode: string }> {
    const q = mailboxUpn ? `?mailbox=${encodeURIComponent(mailboxUpn)}` : "";
    return request<{ ok: boolean; mode: string }>(
      `/api/email/messages/${encodeURIComponent(messageId)}/replyAll${q}`,
      { method: "POST", body: JSON.stringify({ bodyHtml }) },
    );
  },
  async forwardEmail(
    messageId: string,
    bodyHtml: string,
    to: string[],
    cc: string[] = [],
    bcc: string[] = [],
    mailboxUpn?: string | null,
  ): Promise<{ ok: boolean; mode: string }> {
    const q = mailboxUpn ? `?mailbox=${encodeURIComponent(mailboxUpn)}` : "";
    return request<{ ok: boolean; mode: string }>(
      `/api/email/messages/${encodeURIComponent(messageId)}/forward${q}`,
      {
        method: "POST",
        body: JSON.stringify({
          bodyHtml,
          to,
          ...(cc.length > 0 ? { cc } : {}),
          ...(bcc.length > 0 ? { bcc } : {}),
        }),
      },
    );
  },

  // Outlook redesign Phase 6: per-message actions.
  async markEmailRead(
    messageId: string,
    isRead: boolean,
  ): Promise<{ ok: boolean; isRead: boolean }> {
    return request<{ ok: boolean; isRead: boolean }>(
      `/api/email/messages/${encodeURIComponent(messageId)}/read`,
      { method: "PATCH", body: JSON.stringify({ isRead }) },
    );
  },
  // v1.x phase 11.17 (Tier B-1): toggle Microsoft's flag status on a
  // message. The pill in the message-list row + the Flag toggle in
  // the reading-pane header both call this with optimistic UI.
  async setEmailFlag(
    messageId: string,
    flagged: boolean,
  ): Promise<{ ok: boolean; flagged: boolean }> {
    return request<{ ok: boolean; flagged: boolean }>(
      `/api/email/messages/${encodeURIComponent(messageId)}/flag`,
      { method: "PATCH", body: JSON.stringify({ flagged }) },
    );
  },
  // Files a message into one of the caller's own mailbox folders.
  // Graph issues a new id on move, so callers keep newId for Undo.
  async moveEmail(
    messageId: string,
    destinationFolderId: string,
  ): Promise<{ ok: boolean; newId: string }> {
    return request<{ ok: boolean; newId: string }>(
      `/api/email/messages/${encodeURIComponent(messageId)}/move`,
      { method: "POST", body: JSON.stringify({ destinationFolderId }) },
    );
  },
  async deleteEmail(messageId: string): Promise<{ ok: boolean; newId: string }> {
    return request<{ ok: boolean; newId: string }>(
      `/api/email/messages/${encodeURIComponent(messageId)}`,
      { method: "DELETE" },
    );
  },

  async getEmailSignature(): Promise<EmailSignatureResponse> {
    return request<EmailSignatureResponse>("/api/email/signature");
  },

  async saveEmailSignature(html: string): Promise<EmailSignatureSaveResponse> {
    return request<EmailSignatureSaveResponse>("/api/email/signature", {
      method: "POST",
      body: JSON.stringify({ html }),
    });
  },

  async sendEmail(input: SendEmailInput): Promise<SendEmailResponse> {
    return request<SendEmailResponse>("/api/email/send", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Upload one big attachment ahead of the send.
  //
  // XMLHttpRequest rather than fetch, for one reason: it reports
  // upload progress. A 15 MB file on a home connection is a real wait,
  // and a chip that sits there saying nothing reads as broken — which
  // is roughly how the 2.8 MB refusal read to Nate. The bytes stream;
  // the File is handed over as-is and never materialized in JS.
  async stageEmailAttachment(
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<StagedEmailAttachment> {
    const qs = new URLSearchParams({
      filename: file.name,
      size: String(file.size),
    });
    return new Promise<StagedEmailAttachment>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", apiUrl(`/api/email/attachments/staged?${qs.toString()}`), true);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && e.total > 0) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      xhr.onload = () => {
        let payload: unknown = null;
        try {
          payload = JSON.parse(xhr.responseText) as unknown;
        } catch {
          // handled below
        }
        if (xhr.status >= 200 && xhr.status < 300 && payload) {
          resolve(payload as StagedEmailAttachment);
          return;
        }
        const err =
          payload && typeof payload === "object"
            ? (payload as { error?: string; message?: string })
            : {};
        reject(
          new ApiError(
            xhr.status,
            err.message ?? err.error ?? `upload_failed_${xhr.status}`,
            err.error ?? null,
          ),
        );
      };
      xhr.onerror = () => reject(new ApiError(0, "That file didn't finish uploading.", null));
      xhr.onabort = () => reject(new ApiError(0, "Upload cancelled.", "aborted"));
      xhr.send(file);
    });
  },

  // Removing the chip. Best-effort — an unclaimed staged file is swept
  // within a day either way, so a failure here isn't worth a message.
  async deleteStagedEmailAttachment(id: string): Promise<void> {
    await fetch(apiUrl(`/api/email/attachments/staged/${id}`), {
      method: "DELETE",
      credentials: "include",
    }).catch(() => undefined);
  },

  // v1.x: schedule a draft for future delivery. Outlook holds the
  // queue (PR_DEFERRED_DELIVERY_TIME); the user sees the message in
  // their Drafts folder until the timestamp hits and Microsoft flushes.
  async sendScheduledEmail(input: SendScheduledEmailInput): Promise<SendScheduledEmailResponse> {
    return request<SendScheduledEmailResponse>("/api/email/send-scheduled", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // v1.x phase 11.12: save the composer's content as a draft in the
  // advisor's Outlook Drafts folder. Microsoft does NOT auto-send;
  // the advisor opens Outlook (via webLink in the response) to
  // review + click Send themselves. No activity row is written
  // until the actual send is captured by mailbox-sync.
  async saveEmailDraft(input: SaveEmailDraftInput): Promise<SaveEmailDraftResponse> {
    return request<SaveEmailDraftResponse>("/api/email/draft", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // v1.x: email history surfaced on contact / household / transaction
  // record views. Excludes activities tied to a contact whose
  // email_history_private flag is set (admin-managed).
  async getEmailHistoryForEntity(
    entityType:
      | "household"
      | "contact"
      | "opportunity"
      | "transaction"
      | "service_record"
      | "rhythm_instance",
    entityId: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<{
    items: EmailHistoryItem[];
    limit: number;
    offset: number;
  }> {
    const qs = new URLSearchParams();
    qs.set("entity_type", entityType);
    qs.set("entity_id", entityId);
    if (opts.limit) qs.set("limit", String(opts.limit));
    if (opts.offset) qs.set("offset", String(opts.offset));
    return request<{ items: EmailHistoryItem[]; limit: number; offset: number }>(
      `/api/email/history?${qs.toString()}`,
    );
  },

  // The OS's saved copy of a logged email (readable by every employee).
  async getSavedEmail(activityId: string): Promise<{ email: SavedEmail }> {
    return request<{ email: SavedEmail }>(
      `/api/email/activities/${encodeURIComponent(activityId)}`,
    );
  },

  // v1.x phase 11.6: advisor-initiated email-activity deletion.
  // Soft-delete; only the advisor whose mailbox the email traversed
  // OR a system_admin can call. Audit row written on success.
  async deleteEmailActivity(
    activityId: string,
  ): Promise<{ ok: boolean; activity_id?: string; already_deleted?: boolean }> {
    return request(`/api/email/activities/${encodeURIComponent(activityId)}`, { method: "DELETE" });
  },

  // v1.x: admin-only toggle for whether a contact's emails should be
  // hidden from cross-team email-history feeds. Use case: Grant
  // flagging personal contacts whose emails shouldn't surface in
  // shared record views.
  async setContactEmailHistoryPrivate(
    contactId: string,
    isPrivate: boolean,
  ): Promise<{ contact_id: string; email_history_private: boolean }> {
    return request(`/api/contacts/${encodeURIComponent(contactId)}/email-history-private`, {
      method: "PATCH",
      body: JSON.stringify({ email_history_private: isPrivate }),
    });
  },

  // v1.x: per-user To Do list picker. GET returns the current pick
  // + the full list of available To Do lists so the picker UI can
  // render. POST persists the pick (or clears it with both fields
  // null to revert to the default "Stewardship OS" find-or-create).
  // ─── Board event labels (Donna's Color Studio) ─────────────────────
  // The caller's own post-it labels on front-desk-board events. Strictly
  // per-user server-side; the labels' names + colours live in the
  // front_desk_board view preference.
  async lookupBoardEventLabels(keys: string[]): Promise<{ labels: Record<string, string> }> {
    return request<{ labels: Record<string, string> }>("/api/dashboard/donna/board-labels/lookup", {
      method: "POST",
      body: JSON.stringify({ keys }),
    });
  },
  async setBoardEventLabel(visitKey: string, labelId: string): Promise<{ label_id: string }> {
    return request<{ label_id: string }>(
      `/api/dashboard/donna/board-labels/${encodeURIComponent(visitKey)}`,
      { method: "PUT", body: JSON.stringify({ label_id: labelId }) },
    );
  },
  async clearBoardEventLabel(visitKey: string): Promise<void> {
    await request<{ cleared: boolean }>(
      `/api/dashboard/donna/board-labels/${encodeURIComponent(visitKey)}`,
      { method: "DELETE" },
    );
  },
  async clearBoardLabelApplications(labelId: string): Promise<void> {
    await request<{ cleared: boolean }>(
      `/api/dashboard/donna/board-labels/of-label/${encodeURIComponent(labelId)}`,
      { method: "DELETE" },
    );
  },

  async getTodoListPreference(): Promise<TodoListPreferenceResponse> {
    return request<TodoListPreferenceResponse>("/api/preferences/todo-list");
  },
  async setTodoListPreference(input: { list_id: string; list_name: string } | null): Promise<{
    preferred_list_id: string | null;
    preferred_list_name: string | null;
  }> {
    return request("/api/preferences/todo-list", {
      method: "POST",
      body: JSON.stringify(input ?? { list_id: null, list_name: null }),
    });
  },

  // Contact-sync-to-phone: per-user "sync my OS contacts to my phone"
  // setting. GET returns the current setting + connection/scope state;
  // PUT updates it; sync-now runs the reconcile immediately.
  async getContactSyncPreference(): Promise<ContactSyncPreferenceResponse> {
    return request<ContactSyncPreferenceResponse>("/api/preferences/contact-sync");
  },
  async setContactSyncPreference(input: {
    enabled?: boolean;
    scope?: ContactSyncScopeValue;
  }): Promise<ContactSyncPreferenceResponse> {
    return request<ContactSyncPreferenceResponse>("/api/preferences/contact-sync", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
  async runContactSyncNow(): Promise<
    ContactSyncPreferenceResponse & { ran: ContactSyncRunResult }
  > {
    return request("/api/preferences/contact-sync/sync-now", {
      method: "POST",
    });
  },

  // v1.x: read-only proxy of the advisor's Microsoft 365 calendar.
  // The window is a PARAMETER, not a fixed 7 days:
  //   * { start, end } (ISO) — any window the UI renders (the agenda
  //     pager's 7-day window, the month grid's 42-day range). The
  //     server clamps the span (60 days max).
  //   * { days } — legacy today→today+N convenience for callers that
  //     just want "the next week" (dashboards). Ignored when start+end
  //     are present.
  async getUpcomingCalendarEvents(
    opts: { days?: number; start?: string; end?: string } = {},
  ): Promise<UpcomingCalendarResponse> {
    const qs = new URLSearchParams();
    if (opts.start && opts.end) {
      qs.set("start", opts.start);
      qs.set("end", opts.end);
    } else if (opts.days) {
      qs.set("days", String(opts.days));
    }
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<UpcomingCalendarResponse>(`/api/calendar/upcoming${tail}`);
  },

  // Weekly Digest — the signed-in user's week ahead: deals they own
  // closing in the next 7 days + tasks due in the next 7 days. (The
  // calendar half is fetched via getUpcomingCalendarEvents so this works
  // even without an Office 365 connection.)
  async getMyWeek(): Promise<MyWeekResponse> {
    return request<MyWeekResponse>("/api/dashboard/my-week");
  },

  // Joey Ellis program PR4 — mortgage family view. Active mortgage loans
  // for a household with current rate, computed current LTV, and the
  // advisor-entered income/DTI snapshot.
  /** How much of the traditional book the county has been checked for
   *  (win-back). Mortgage-scoped users only; 403 otherwise. Pass an advisor
   *  id to count that advisor's book. */
  async getCountyCoverage(advisorUserId?: string | null): Promise<CountyCoverage> {
    const q = advisorUserId ? `?advisor=${encodeURIComponent(advisorUserId)}` : "";
    return request<CountyCoverage>(`/api/service-records/county-coverage${q}`);
  },
  async getMortgageSummary(householdId: string): Promise<MortgageSummaryResponse> {
    return request<MortgageSummaryResponse>(
      `/api/households/${encodeURIComponent(householdId)}/mortgage-summary`,
    );
  },
  // Joey program PR6 — mortgage rate ESTIMATE (benchmark + adjustments).
  async estimateMortgageRate(
    input: MortgageRateEstimateInput,
  ): Promise<MortgageRateEstimateResponse> {
    return request<MortgageRateEstimateResponse>("/api/mortgage/rate-estimate", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // PATCH /api/mortgage-loans/:id/details — advisor-entered loan details
  // from the record page's Loan details card: amount earned (Stewardship's
  // fee), loan amount, lender, closing date, property address, borrower
  // tier. Money fields are integer CENTS. Only provided keys are written.
  // Server enforces mortgage company scope + audits before/after.
  async updateMortgageLoanDetails(
    loanId: string,
    patch: Partial<
      Record<
        | "total_amount_earned_cents"
        | "loan_amount_cents"
        | "lender_name"
        | "loan_officer_name"
        | "closing_date"
        | "property_address_line_1"
        | "property_city"
        | "property_state"
        | "property_zip"
        | "borrower_tier"
        | "lien_status"
        | "hoepa_status"
        | "qm_status",
        string | number | null
      >
    >,
  ): Promise<{
    ok: boolean;
    mortgage_loan_id: string;
    changed: boolean;
    updated: Record<string, string | number | null>;
  }> {
    return request(`/api/mortgage-loans/${encodeURIComponent(loanId)}/details`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  /**
   * Save the loan's numbers from a Processing record, without the caller
   * needing to know whether a funded loan exists yet.
   *
   * A mortgage file below Funded has no loan row (deliberately — it is
   * an application, not a loan), so the server routes this to the
   * application record and carries the values into the loan when it is
   * created. Both paths validate and audit identically.
   */
  async updateTransactionLoanFacts(
    transactionId: string,
    patch: Partial<Record<MortgageLoanFactField, string | number | null>>,
  ): Promise<{
    ok: boolean;
    wrote_to: "loan" | "application";
    mortgage_loan_id: string | null;
    changed: boolean;
    updated: Record<string, string | number | null>;
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/loan-facts`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  // Fetch a single Realty subtype record (property + sale/commission
  // numbers) by its own id — used to embed the same editable card
  // (RealtyProductDetails) on the Processing/transaction detail page,
  // not just the service-record page (Nate 2026-08-04: the sale price
  // he needed to correct had no visible edit affordance from Processing).
  // ─── Realty offers ─────────────────────────────────────────────────
  //
  // Offers our buyer writes and offers a listing receives, with their
  // statuses and counter chains. Every path is Realty-scoped server-side
  // and reachable only through the record the offer hangs off — there is
  // deliberately no "list every offer" endpoint, so the record's own gate
  // is the only door.
  async listRealtyOffers(
    parentKind: "opportunity" | "transaction",
    parentId: string,
  ): Promise<RealtyOffersResponse> {
    return request(`/api/realty-offers/for/${parentKind}/${encodeURIComponent(parentId)}`);
  },
  async getRealtyOffer(offerId: string): Promise<RealtyOfferDetailResponse> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}`);
  },
  async createRealtyOffer(
    parentKind: "opportunity" | "transaction",
    parentId: string,
    input: RealtyOfferWrite & { direction?: "outgoing" | "incoming" },
  ): Promise<{ offer: RealtyOfferDto }> {
    return request(`/api/realty-offers/for/${parentKind}/${encodeURIComponent(parentId)}`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /** The signed-in agent's preferred title / escrow officer — a vendor
   *  row, or null when they haven't picked one. */
  async getPreferredEscrowOfficer(): Promise<{ vendor: Vendor | null }> {
    return request(`/api/realty-offers/escrow-preference`);
  },
  async setPreferredEscrowOfficer(vendorId: string | null): Promise<{ vendor: Vendor | null }> {
    return request(`/api/realty-offers/escrow-preference`, {
      method: "PUT",
      body: JSON.stringify({ vendor_id: vendorId }),
    });
  },
  async updateRealtyOffer(
    offerId: string,
    input: Partial<RealtyOfferWrite>,
  ): Promise<{ offer: RealtyOfferDto | null }> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  // Deliberately cannot set 'accepted' — accepting an offer is Mark Won,
  // which creates the Processing record and carries the terms onto its
  // contract card. The server refuses it here and names the action that
  // does it properly.
  //
  // Ending an ACCEPTED offer — cancelled, withdrawn or rejected after the
  // contract existed — also moves its Processing record back to
  // Opportunities in the same call; `reason_code` and `stage` feed that
  // move (same vocabulary as moveTransactionBackToOpportunity) and the
  // response says where the record went.
  async setRealtyOfferStatus(
    offerId: string,
    status: RealtyOfferStatus,
    note?: string | null,
    moveBack?: { reason_code?: string | null; stage?: string | null },
  ): Promise<{ offer: RealtyOfferDto | null; moved_back: RealtyOfferMovedBack | null }> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/status`, {
      method: "POST",
      body: JSON.stringify({ status, note: note ?? null, ...(moveBack ?? {}) }),
    });
  },
  async counterRealtyOffer(
    offerId: string,
    input: { label?: string | null; notes?: string | null; expires_at?: string | null },
  ): Promise<{ counter: RealtyOfferDto }> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/counter`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // Soft delete. The response carries the undo handle; the pane surfaces
  // it as an undo toast (reversibility-first).
  async archiveRealtyOffer(
    offerId: string,
    reason?: string | null,
  ): Promise<{ ok: true; restore_offer_id: string; released_parent_id: string | null }> {
    const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}${qs}`, { method: "DELETE" });
  },
  // ─── The offer summary box ─────────────────────────────────────────
  //
  // Two calls, and the split between them IS the safety property, not an
  // API-design preference: `read` costs a model call and writes NOTHING —
  // not the summary, not a proposal. `apply` writes only values that
  // arrive carrying a provenance saying a human accepted them. There is
  // deliberately no call that does both.
  async readOfferSummary(offerId: string, summary: string): Promise<OfferSummaryReadResponse> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/summary/read`, {
      method: "POST",
      body: JSON.stringify({ summary }),
    });
  },
  async applyOfferSummary(
    offerId: string,
    input: {
      summary: string;
      input_method: "voice" | "typed";
      values: {
        slot_key: string;
        value: string;
        provenance: "ai_accepted" | "ai_edited" | "typed";
        source_quote?: string | null;
      }[];
    },
  ): Promise<{ ok: true; written: number; replaced: string[]; summary_id: string | null }> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/summary/apply`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async getOfferSummary(offerId: string): Promise<OfferSummaryStateResponse> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/summary`);
  },
  // Accepting an offer on a PROCESSING record: the file already exists,
  // so this marks the offer accepted and carries its terms onto the
  // contract card. An offer on an OPPORTUNITY is refused here with
  // `use_mark_won` — there accepting IS Mark Won, and it rides the
  // existing transition rather than forking a second copy of it.
  async acceptRealtyOffer(offerId: string): Promise<RealtyOfferAcceptResponse> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/accept`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  // Hang the signing package (and the documents that went into it) off
  // the offer it belongs to. The server refuses an envelope from a
  // different record — an id off a stale screen would otherwise show
  // another household's contract as this client's.
  async attachToRealtyOffer(
    offerId: string,
    input: { envelope_id?: string; document_ids?: string[] },
  ): Promise<{ ok: true; envelope_attached: boolean; documents_attached: number }> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/attach`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // Seed a seller net-proceeds scenario from an offer that came in.
  // Sell-side only, server-enforced: a buyer nets nothing, they pay.
  async runNetSheetOnOffer(offerId: string): Promise<OfferNetSheetResponse> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/net-sheet`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  async restoreRealtyOffer(offerId: string): Promise<{ offer: RealtyOfferDto | null }> {
    return request(`/api/realty-offers/${encodeURIComponent(offerId)}/restore`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
  // ─── Seller net proceeds ───────────────────────────────────────────
  //
  // Every path is realty-scoped + sell-side-gated server-side; the UI
  // gates too, but the server is the enforcement.
  async listNetProceedsScenarios(
    parentKind: "opportunity" | "transaction",
    parentId: string,
  ): Promise<NetProceedsListResponse> {
    return request(`/api/net-proceeds/${parentKind}/${encodeURIComponent(parentId)}`);
  },
  async createNetProceedsScenario(
    parentKind: "opportunity" | "transaction",
    parentId: string,
    input: NetProceedsScenarioWrite,
  ): Promise<NetProceedsScenarioDto> {
    return request(`/api/net-proceeds/${parentKind}/${encodeURIComponent(parentId)}`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateNetProceedsScenario(
    scenarioId: string,
    input: NetProceedsScenarioWrite,
  ): Promise<NetProceedsScenarioDto> {
    return request(`/api/net-proceeds/scenarios/${encodeURIComponent(scenarioId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  // Soft delete. The response carries the undo path; the card surfaces it
  // as an undo toast (reversibility-first).
  async deleteNetProceedsScenario(
    scenarioId: string,
  ): Promise<{ id: string; deleted: boolean; undo: string }> {
    return request(`/api/net-proceeds/scenarios/${encodeURIComponent(scenarioId)}`, {
      method: "DELETE",
    });
  },
  async restoreNetProceedsScenario(scenarioId: string): Promise<NetProceedsScenarioDto> {
    return request(`/api/net-proceeds/scenarios/${encodeURIComponent(scenarioId)}/restore`, {
      method: "POST",
    });
  },
  // Builds the seller-facing email. Returns text; sends nothing.
  async getNetProceedsDraft(scenarioId: string): Promise<NetProceedsDraftResponse> {
    return request(`/api/net-proceeds/scenarios/${encodeURIComponent(scenarioId)}/draft`);
  },
  // The subject property on a Realty listing opportunity (Grant,
  // 2026-08-19). Send every field blank to clear it.
  async updateSubjectProperty(
    opportunityId: string,
    input: {
      subject_property_line_1: string | null;
      subject_property_line_2: string | null;
      subject_property_city: string | null;
      subject_property_state: string | null;
      subject_property_zip: string | null;
      subject_property_square_feet: number | null;
    },
  ): Promise<SubjectPropertyResponse> {
    return request(`/api/opportunities/${encodeURIComponent(opportunityId)}/subject-property`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  async getRealtyRecord(realtyRecordId: string): Promise<RealtyRecordDetail> {
    return request(`/api/realty-records/${encodeURIComponent(realtyRecordId)}`);
  },
  // The closing-address question on a Realty Processing record (Nate,
  // 2026-09-19). The server composes the question AND decides what each
  // answer writes — the client sends an answer, never an address to store,
  // except for the one answer whose point is that a human typed it. See
  // shared/realty/closing-address-decision.ts.
  async getClosingAddressDecision(transactionId: string): Promise<ClosingAddressDecisionState> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/closing-address`);
  },
  async answerClosingAddressDecision(
    transactionId: string,
    input: {
      answer: ClosingAddressAnswer;
      /** Only for "replace_with_new" when the advisor typed one. */
      address?: {
        street_line_1: string | null;
        street_line_2: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
      };
    },
  ): Promise<{
    id: string;
    answer: ClosingAddressAnswer;
    household_fields_changed: string[];
    state: ClosingAddressDecisionState;
  }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/closing-address`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // What the agent's share should be on this deal per the configured
  // rates, so the commission calculator can prefill it. A suggestion:
  // nothing is stored, and typing over it is the per-transaction custom
  // split. Mirrors RealtyRecordCommissionSuggestion in
  // shared/db/realty-record-commission.ts.
  async getRealtyRecordCommissionSplit(
    realtyRecordId: string,
  ): Promise<RealtyCommissionSuggestion> {
    return request(`/api/realty-records/${encodeURIComponent(realtyRecordId)}/commission-split`);
  },
  // Write advisor-entered Realty details (sale price + BrokerMint's three
  // commission metrics + close date + property address, plus the contract
  // terms Nate's contract card edits). Money in DOLLARS; the two _pct
  // fields are PERCENTAGES (2.5 means 2.5%). Server enforces realty
  // company scope and audits before/after.
  //
  // `listing_side_pct` is listing-side only — a purchase answers 400
  // saying so, because we are the buyer's agent there and no such
  // number exists.
  async updateRealtyRecordDetails(
    realtyRecordId: string,
    patch: Partial<
      Record<
        | "sale_price"
        | "office_gross"
        | "agent_net"
        | "office_net"
        | "closed_at"
        | "property_address_line_1"
        | "property_city"
        | "property_state"
        | "property_zip"
        | "list_price"
        | "listing_side_pct"
        | "buyer_broker_pct"
        | "listing_side_flat"
        | "buyer_broker_flat"
        | "home_warranty"
        | "under_contract_at"
        | "agreement_signed_at"
        | "agreement_expires_at"
        // The listing agreement's own terms (2026-10-14), listing side only.
        | "original_list_price"
        | "listing_period_starts_at"
        | "has_leased_items"
        | "leased_items_detail"
        | "items_not_conveying"
        // The title company's file number, both sides (2026-10-26).
        | "escrow_number"
        // Property facts the AAR contracts require (2026-08-28).
        // `in_unincorporated_area` takes a boolean because "no" and
        // "nobody has checked" are different answers and 0 vs null in a
        // JSON body is too easy to get wrong.
        | "apn"
        | "legal_description"
        | "county"
        | "subdivision"
        | "lot_size_sqft"
        | "year_built"
        | "sewer_type"
        | "water_source"
        | "hoa_status"
        | "hoa_name"
        | "solar_status"
        | "in_unincorporated_area"
        // "No ARMLS" build (2026-08-30): which provenance source these
        // facts came from. Optional — omit for a hand-typed edit, which
        // is every existing caller. See the route's own comment for why
        // this is validated server-side against an allowlist.
        | "fact_source",
        string | number | boolean | null
      >
    >,
  ): Promise<{
    ok: boolean;
    realty_record_id: string;
    changed: boolean;
    updated: Record<string, string | number | null>;
    record: RealtyRecordDetail | null;
  }> {
    return request(`/api/realty-records/${encodeURIComponent(realtyRecordId)}/details`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  // "No ARMLS" tier 2 (2026-08-30): look up county-record facts by
  // address via RentCast. Nothing is saved by this call — the confirm UI
  // writes kept facts through updateRealtyRecordDetails with
  // fact_source: "rentcast_sync", same as tier 1's document reader.
  // Feature-gated server-side; a 503 with `error: "disabled"` or
  // `"no_api_key"` means the RentCast plan upgrade hasn't been turned on
  // yet, not a bug.
  async lookupRealtyPropertyFacts(
    realtyRecordId: string,
    address?: string,
  ): Promise<{ ok: true; address: string; facts: ExtractedPropertyFactsDto; found_count: number }> {
    return request(
      `/api/realty-records/${encodeURIComponent(realtyRecordId)}/lookup-property-facts`,
      {
        method: "POST",
        body: JSON.stringify(address ? { address } : {}),
      },
    );
  },
  // Household address cards' "Check address" (2026-10-28): the Census finds
  // the standard spelling, ZIP and county. A suggestion only; nothing is
  // saved until the advisor presses Save on the card.
  async checkAddress(address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  }): Promise<{
    ok: true;
    matched_line: string;
    found: { street: string; city: string; state: string; zip: string; county: string | null };
  }> {
    return request("/api/address-check", { method: "POST", body: JSON.stringify(address) });
  },
  // "No ARMLS" tier 3 (2026-08-30): properties a buyer has viewed.
  async listPropertyViewings(
    householdId: string,
  ): Promise<{ viewings: RealtyPropertyViewingDto[] }> {
    return request(`/api/realty/property-viewings?household_id=${encodeURIComponent(householdId)}`);
  },
  async logPropertyViewing(body: {
    household_id: string;
    property_address_line_1: string;
    property_city?: string;
    property_state?: string;
    property_zip?: string;
    viewed_at: string;
    notes?: string;
  }): Promise<{ ok: true; viewing: RealtyPropertyViewingDto }> {
    return request(`/api/realty/property-viewings`, { method: "POST", body: JSON.stringify(body) });
  },
  async deletePropertyViewing(id: string): Promise<{ ok: true }> {
    return request(`/api/realty/property-viewings/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // Candidates read the CALLER'S OWN Outlook calendar (server-resolved —
  // there is no advisor id to pass). 404/412-shaped errors from
  // requireGraphActive mean "connect your Outlook first", same as every
  // other Graph-gated read in this file.
  async listPropertyViewingCandidates(): Promise<{ candidates: ViewingCandidateDto[] }> {
    return request(`/api/realty/property-viewings/candidates`);
  },
  async confirmPropertyViewingCandidate(body: {
    graph_event_id: string;
    household_id: string;
    property_address_line_1: string;
    property_city?: string;
    property_state?: string;
    property_zip?: string;
    viewed_at: string;
    notes?: string;
  }): Promise<{ ok: true; viewing: RealtyPropertyViewingDto }> {
    return request(`/api/realty/property-viewings/candidates/confirm`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async dismissPropertyViewingCandidate(graphEventId: string): Promise<{ ok: true }> {
    return request(`/api/realty/property-viewings/candidates/dismiss`, {
      method: "POST",
      body: JSON.stringify({ graph_event_id: graphEventId }),
    });
  },
  /** One company's firm block — the broker identity every contract asks
   *  for near the signatures. Readable by anyone scoped to the company,
   *  since it prints on documents they prepare; `may_edit` says whether
   *  THIS person may change it, so the screen can show it read-only
   *  rather than offering a save the server will refuse. */
  async getFirmProfile(companySlug: string): Promise<FirmProfileResponse> {
    return request(`/api/firm-profile/${encodeURIComponent(companySlug)}`);
  },

  /** Save an edit. Only the fields sent are touched. */
  async updateFirmProfile(
    companySlug: string,
    edit: Partial<Record<FirmProfileField, string | null>>,
  ): Promise<FirmProfileResponse> {
    return request(`/api/firm-profile/${encodeURIComponent(companySlug)}`, {
      method: "PUT",
      body: JSON.stringify(edit),
    });
  },

  // Realty Forms Library admin screen: every current form's link status
  // plus the Realty templates available to link.
  async getContractFormsAdmin(): Promise<ContractFormsAdminResponse> {
    return request(`/api/admin/contract-forms`);
  },

  /** Industry Documents: one company's blank-form library, whichever
   *  shape backs it (Realty's AAR catalog, or the generic template
   *  catalog every other company uses). `includeArchived` also returns
   *  documents removed from the library, for the "Show removed" view —
   *  the generic (non-Realty) shape only; Realty's own removal is the
   *  existing "Remove the blank PDF" unlink, a different action. */
  async getIndustryDocuments(
    company: string,
    options?: { includeArchived?: boolean },
  ): Promise<IndustryDocumentsResponse> {
    const params = new URLSearchParams({ company });
    if (options?.includeArchived) params.set("include_archived", "1");
    return request(`/api/admin/industry-documents?${params.toString()}`);
  },

  /** Add a Stewardship-authored document to the forms catalog. Creates
   *  the catalog row only — the blank PDF is attached afterward through
   *  the same upload-and-link control every unlinked row already has. */
  async createContractForm(input: {
    display_name: string;
    revision_label?: string;
    category: "disclosure" | "optional_addendum";
    applies_to_side: "listing" | "purchase" | "both";
  }): Promise<{ form: ContractFormSummary }> {
    return request(`/api/admin/contract-forms`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  /** Rename a Realty library form. The catalog name and its blank's
   *  e-sign name change together (2026-10-27). */
  async renameContractForm(
    formId: string,
    displayName: string,
  ): Promise<{ ok: boolean; form: ContractFormSummary }> {
    return request(`/api/admin/contract-forms/${encodeURIComponent(formId)}`, {
      method: "PATCH",
      body: JSON.stringify({ display_name: displayName }),
    });
  },

  /** Attach (or, with null, detach) a form's blank document. */
  async linkContractFormTemplate(
    formId: string,
    esignTemplateId: string | null,
  ): Promise<{ ok: boolean; form: ContractFormSummary }> {
    return request(`/api/admin/contract-forms/${encodeURIComponent(formId)}`, {
      method: "PATCH",
      body: JSON.stringify({ esign_template_id: esignTemplateId }),
    });
  },

  // The complete, current list of forms a Realty deal needs — base
  // contract, the disclosures that ride along, and the conditional
  // addenda the property's own facts make mandatory. Every entry names
  // the revision that is CURRENT, which is a database constraint rather
  // than a convention (see contract_forms).
  async getContractPacket(realtyRecordId: string): Promise<ContractPacketResponse> {
    return request(`/api/realty-records/${encodeURIComponent(realtyRecordId)}/contract-packet`);
  },
  // Write the income/DTI snapshot. Server enforces mortgage company scope.
  async updateMortgageLoanFinancials(
    loanId: string,
    input: {
      gross_monthly_income: number | null;
      dti_percent: number | null;
      financials_as_of?: string | null;
    },
  ): Promise<MortgageFinancialsUpdateResponse> {
    return request<MortgageFinancialsUpdateResponse>(
      `/api/mortgage-loans/${encodeURIComponent(loanId)}/financials`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  // v1.x phase 11.10: create a new calendar event. When
  // input.conflictCheck is true, returns existing overlapping events
  // without creating; the caller commits with a follow-up call where
  // conflictCheck=false (or omitted).
  async createCalendarEvent(input: CreateCalendarEventInput): Promise<CreateCalendarEventResponse> {
    return request<CreateCalendarEventResponse>("/api/calendar/events", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // v1.x phase 11.16 (Tier B item 7) — calendar event mutations.
  async updateCalendarEvent(
    eventId: string,
    input: UpdateCalendarEventInput,
  ): Promise<CalendarEventMutationResponse> {
    return request<CalendarEventMutationResponse>(
      `/api/calendar/events/${encodeURIComponent(eventId)}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
  },
  async cancelCalendarEvent(
    eventId: string,
    input: { comment?: string } = {},
  ): Promise<{ kind: "cancelled"; event_id: string }> {
    return request<{ kind: "cancelled"; event_id: string }>(
      `/api/calendar/events/${encodeURIComponent(eventId)}/cancel`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },
  async respondToCalendarEvent(
    eventId: string,
    input: {
      response: "accept" | "tentative" | "decline";
      comment?: string;
      sendResponse?: boolean;
    },
  ): Promise<{
    kind: "responded";
    event_id: string;
    response: "accept" | "tentative" | "decline";
  }> {
    return request(`/api/calendar/events/${encodeURIComponent(eventId)}/respond`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // v1.x phase 11.16 (Tier B item 8) — Donna's dashboard aggregate.
  async getDonnaDashboard(): Promise<DonnaDashboardResponse> {
    return request<DonnaDashboardResponse>("/api/dashboard/donna");
  },

  // All-advisor office schedule. Separate from the aggregate above
  // because the Graph fan-out is slower (~one calendar read per
  // connected advisor) — the page loads it independently so tasks +
  // households render immediately. view: 'clients' (matched households
  // only, default) or 'all' (every event; front_desk/admin roles).
  // date: Phoenix-local YYYY-MM-DD, defaults to today.
  // fresh: bypass the server's per-day schedule cache and re-read every
  // advisor's Outlook calendar. Used by the Refresh button, the board's
  // auto-refresh, and immediately after a correction, so a human who
  // asks for the truth always gets it (see office-schedule-cache.ts).
  async getDonnaOfficeSchedule(
    opts: { date?: string; view?: "clients" | "all"; fresh?: boolean } = {},
  ): Promise<OfficeScheduleResponse> {
    const params = new URLSearchParams();
    if (opts.date) params.set("date", opts.date);
    if (opts.view) params.set("view", opts.view);
    if (opts.fresh) params.set("fresh", "1");
    const qs = params.toString();
    return request<OfficeScheduleResponse>(
      `/api/dashboard/donna/office-schedule${qs ? `?${qs}` : ""}`,
    );
  },

  // Front-desk check-in on an office visit. status 'clear' undoes.
  async checkinOfficeVisit(
    visitKey: string,
    body: {
      date: string;
      status: "checked_in" | "done" | "clear";
      notify_advisor_user_ids?: string[] | undefined;
      visit_label?: string | undefined;
    },
  ): Promise<{ checkin: OfficeVisitCheckin | null }> {
    return request(`/api/dashboard/donna/office-visits/${encodeURIComponent(visitKey)}/checkin`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Manually associate a calendar event with a household (sticks for
  // recurrences). DELETE undoes a mis-link.
  async linkOfficeVisitHousehold(
    visitKey: string,
    householdId: string,
  ): Promise<{ linked: boolean }> {
    return request(
      `/api/dashboard/donna/office-visits/${encodeURIComponent(visitKey)}/link-household`,
      { method: "POST", body: JSON.stringify({ household_id: householdId }) },
    );
  },

  async unlinkOfficeVisitHousehold(
    visitKey: string,
    householdId: string,
  ): Promise<{ linked: boolean }> {
    return request(
      `/api/dashboard/donna/office-visits/${encodeURIComponent(visitKey)}/link-household/${encodeURIComponent(householdId)}`,
      { method: "DELETE" },
    );
  },

  // Donna's correction to the automatic office/phone/video/off-site
  // guess. Stewardship-side only — never edits the Outlook event.
  // Sticks for recurrences (undated on the backend).
  async setOfficeVisitKind(
    visitKey: string,
    kind: OfficeMeetingKindOverride,
  ): Promise<{ kind: OfficeMeetingKindOverride }> {
    return request(`/api/dashboard/donna/office-visits/${encodeURIComponent(visitKey)}/kind`, {
      method: "POST",
      body: JSON.stringify({ kind }),
    });
  },

  // Reset to the automatic guess.
  async clearOfficeVisitKind(visitKey: string): Promise<{ kind: null }> {
    return request(`/api/dashboard/donna/office-visits/${encodeURIComponent(visitKey)}/kind`, {
      method: "DELETE",
    });
  },

  // Rooms: each tracked room's day (busy blocks + free/busy-until).
  async getDonnaRooms(opts: { date?: string } = {}): Promise<RoomScheduleResponse> {
    const qs = opts.date ? `?date=${encodeURIComponent(opts.date)}` : "";
    return request<RoomScheduleResponse>(`/api/dashboard/donna/rooms${qs}`);
  },

  async addDonnaRoom(body: { name: string; email: string }): Promise<{ room: RoomDay | null }> {
    return request(`/api/dashboard/donna/rooms`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async hideDonnaRoom(roomId: string): Promise<{ hidden: boolean }> {
    return request(`/api/dashboard/donna/rooms/${encodeURIComponent(roomId)}/hide`, {
      method: "POST",
    });
  },

  // Books a real Outlook meeting from the caller's own mailbox with the
  // room invited (the room auto-accepts, landing on its own calendar).
  async bookDonnaRoom(body: {
    room_email: string;
    room_name?: string | undefined;
    subject: string;
    start_iso: string;
    end_iso: string;
  }): Promise<{ booked: boolean; event_web_link: string | null }> {
    return request(`/api/dashboard/donna/rooms/book`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Team birthdays card (every home screen). userId null = my own.
  async getTeamBirthdays(): Promise<TeamBirthdaysResponse> {
    return request("/api/team/birthdays");
  },
  async setTeamBirthdayPrefs(prefs: { day_before: boolean; day_of: boolean }): Promise<unknown> {
    return request("/api/team/birthdays/prefs", { method: "PUT", body: JSON.stringify(prefs) });
  },
  async setTeamBirthday(userId: string | null, body: { month: number; day: number }): Promise<unknown> {
    return request(`/api/team/birthdays/${encodeURIComponent(userId ?? "me")}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  async removeTeamBirthday(userId: string | null): Promise<unknown> {
    return request(`/api/team/birthdays/${encodeURIComponent(userId ?? "me")}`, { method: "DELETE" });
  },

  // Cancel booking: held for five minutes, then sent to Outlook.
  async cancelDonnaRoomBooking(
    roomId: string,
    body: { event_id: string; date: string },
  ): Promise<{ cancellation: RoomCancellationTicket }> {
    return request(`/api/dashboard/donna/rooms/${encodeURIComponent(roomId)}/bookings/cancel`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async undoDonnaRoomCancellation(id: string): Promise<{ undone: boolean }> {
    return request(`/api/dashboard/donna/rooms/cancellations/${encodeURIComponent(id)}/undo`, {
      method: "POST",
    });
  },
  async sendDonnaRoomCancellation(id: string): Promise<{ status: string }> {
    return request(`/api/dashboard/donna/rooms/cancellations/${encodeURIComponent(id)}/send`, {
      method: "POST",
    });
  },

  // Yesterday's office visits awaiting a follow-up note.
  // Onboarding: list Donna's open cases.
  async getDonnaOnboarding(): Promise<{ cases: OnboardingCase[] }> {
    return request<{ cases: OnboardingCase[] }>("/api/dashboard/donna/onboarding");
  },

  // Onboarding: advisor hands a household off to Donna.
  async sendToOnboarding(
    householdId: string,
    companyId?: string,
  ): Promise<{ ok: true; onboarding_case: OnboardingCase }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/onboarding`, {
      method: "POST",
      body: JSON.stringify(companyId ? { company_id: companyId } : {}),
    });
  },

  // Onboarding: update CEA status and/or case status.
  async patchOnboardingCase(
    caseId: string,
    input: { cea_status?: OnboardingCeaStatus; status?: OnboardingCaseStatus },
  ): Promise<{ ok: true; onboarding_case: OnboardingCase }> {
    return request(`/api/onboarding/${encodeURIComponent(caseId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },

  // Onboarding: toggle a checklist step.
  async toggleOnboardingStep(
    caseId: string,
    stepKey: string,
    done: boolean,
  ): Promise<{ ok: true; onboarding_case: OnboardingCase }> {
    return request(
      `/api/onboarding/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepKey)}`,
      { method: "PATCH", body: JSON.stringify({ done }) },
    );
  },

  // Monthly checklist: this month's items for the logged-in user.
  async getMonthlyChecklist(): Promise<MonthlyChecklist> {
    return request<MonthlyChecklist>("/api/dashboard/donna/monthly-checklist");
  },

  async toggleMonthlyChecklistItem(itemKey: string, done: boolean): Promise<MonthlyChecklist> {
    return request<MonthlyChecklist>(
      `/api/dashboard/donna/monthly-checklist/${encodeURIComponent(itemKey)}/toggle`,
      { method: "POST", body: JSON.stringify({ done }) },
    );
  },

  // ── Advisor scheduling (Donna, 2026-08-04) ────────────────────────

  // One advisor's day (or run of days), their booking links, and
  // whether this viewer may book. `days` is clamped 1..14 server-side.
  async getAdvisorSchedule(
    userId: string,
    opts: { date?: string; days?: number } = {},
  ): Promise<AdvisorScheduleResponse> {
    const params = new URLSearchParams();
    if (opts.date) params.set("date", opts.date);
    if (opts.days) params.set("days", String(opts.days));
    const qs = params.toString();
    return request<AdvisorScheduleResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/schedule${qs ? `?${qs}` : ""}`,
    );
  },

  // The Appointments list — "who booked with me?" (CALENDAR →
  // Appointments). scope "office" is honored only for the board roles;
  // anyone else is force-downgraded server-side and the response's
  // `scope` echoes what's actually in effect.
  async listAppointments(
    opts: { scope?: "mine" | "office"; days?: number; pastDays?: number } = {},
  ): Promise<AppointmentsListResponse> {
    const params = new URLSearchParams();
    if (opts.scope) params.set("scope", opts.scope);
    if (opts.days) params.set("days", String(opts.days));
    if (opts.pastDays) params.set("past_days", String(opts.pastDays));
    const qs = params.toString();
    return request<AppointmentsListResponse>(`/api/appointments${qs ? `?${qs}` : ""}`);
  },

  // Mark an appointment completed or no-show (Appointments tab → Past).
  async closeAppointment(
    id: string,
    outcome: "completed" | "no_show",
  ): Promise<{ ok: true; id: string; status: string }> {
    return request(`/api/appointments/${encodeURIComponent(id)}/close`, {
      method: "POST",
      body: JSON.stringify({ outcome }),
    });
  },

  // Clear a client-cancelled booking off the "needs attention" strip.
  async dismissCancelledAppointment(id: string): Promise<{ ok: true; id: string }> {
    return request(`/api/appointments/${encodeURIComponent(id)}/dismiss-cancelled`, {
      method: "POST",
    });
  },

  // Undo a cancel within its grace window — recreates the Outlook event
  // and restores the OS row (Appointments tab's cancel toast).
  async undoCancelAppointment(
    id: string,
  ): Promise<{ ok: true; id: string; status: string; graph_web_link: string | null }> {
    return request(`/api/appointments/${encodeURIComponent(id)}/undo-cancel`, {
      method: "POST",
    });
  },

  // Put an appointment on an advisor's calendar. Front desk + admins
  // only (and anyone booking their own calendar) — enforced server-side.
  async createAdvisorAppointment(
    userId: string,
    input: CreateAdvisorAppointmentInput,
  ): Promise<CreateAdvisorAppointmentResponse> {
    return request<CreateAdvisorAppointmentResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/appointments`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // Cancel a meeting on an advisor's calendar — booked through the OS
  // or the advisor's own Outlook entry, either way. Same audience as
  // booking. `date` is the board day the drawer was showing, so a
  // raw Outlook event (no OS appointment row to read a date from) still
  // gets its cached board day invalidated.
  async cancelAdvisorCalendarEvent(
    userId: string,
    eventId: string,
    input: { reason?: string; date?: string; notify_client?: boolean } = {},
  ): Promise<{ ok: true; appointment_id: string | null }> {
    return request(
      `/api/advisors/${encodeURIComponent(userId)}/calendar-events/${encodeURIComponent(eventId)}/cancel`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // Move a meeting on an advisor's calendar to a new time. `current_start_iso`
  // lets the server exclude this same meeting from its own conflict
  // pre-check when there's no OS appointment row to read the current
  // time from (a raw Outlook entry).
  async rescheduleAdvisorCalendarEvent(
    userId: string,
    eventId: string,
    input: {
      start_iso: string;
      end_iso: string;
      current_start_iso?: string;
      date?: string;
    },
  ): Promise<{
    ok: true;
    appointment_id: string | null;
    start_iso: string;
    end_iso: string;
  }> {
    return request(
      `/api/advisors/${encodeURIComponent(userId)}/calendar-events/${encodeURIComponent(eventId)}/reschedule`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // An advisor's own booking parameters. Anyone who may view their
  // calendar may read them; only the advisor (or a system admin) may
  // save — enforced server-side.
  async getAdvisorBookingRules(userId: string): Promise<AdvisorBookingRulesResponse> {
    return request<AdvisorBookingRulesResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/booking-rules`,
    );
  },

  async saveAdvisorBookingRules(
    userId: string,
    input: Omit<AdvisorBookingRules, "is_configured">,
  ): Promise<AdvisorBookingRulesResponse> {
    return request<AdvisorBookingRulesResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/booking-rules`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },

  // The user's other Outlook calendars, live from Microsoft — the
  // Booking rules screen's "block these too" checklist. Self or
  // system_admin only (enforced server-side).
  async getAdvisorCalendars(userId: string): Promise<AdvisorCalendarsResponse> {
    return request<AdvisorCalendarsResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/calendars`,
    );
  },

  // ── Native scheduling Phase B1 (2026-08-14) ───────────────────────

  // An advisor's appointment types. Read: anyone who may view their
  // calendar. Save: the advisor themselves or system_admin (server-
  // enforced) — the whole list at once, absence meaning deletion.
  async getAdvisorAppointmentTypes(userId: string): Promise<AdvisorAppointmentTypesResponse> {
    return request<AdvisorAppointmentTypesResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/appointment-types`,
    );
  },

  // Every appointment type in the firm with its owner — the search index
  // behind Settings -> Appointment types. Loaded once per tab open.
  async listAppointmentTypeDirectory(): Promise<{
    types: AppointmentTypeDirectoryEntry[];
    can_edit_others: boolean;
  }> {
    return request<{ types: AppointmentTypeDirectoryEntry[]; can_edit_others: boolean }>(
      "/api/advisors/appointment-types/directory",
    );
  },

  // The connected experience (2026-09-05): set the MEETING side of a
  // form↔meeting connection on every type of one name the viewer may
  // edit (null form_slug clears it), and create one named meeting for
  // several advisors at once from the form builder.
  async connectFormToMeeting(body: {
    appointment_type_name: string;
    form_slug: string | null;
    advisor_user_ids?: string[];
  }): Promise<ConnectFormToMeetingResult> {
    return request<ConnectFormToMeetingResult>("/api/advisors/appointment-types/connect-form", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async createSharedMeeting(body: CreateSharedMeetingInput): Promise<{
    ok: true;
    name: string;
    created_advisor_ids: string[];
    existing_advisor_ids: string[];
  }> {
    return request("/api/advisors/appointment-types/create-shared", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Who the viewer may open a booking page for. 403 for everyone but
  // the front desk and admins, which is how the switcher decides not to
  // render rather than needing a second permission flag.
  async listBookingDirectory(): Promise<{
    can_book: boolean;
    advisors: BookingDirectoryEntry[];
  }> {
    return request<{ can_book: boolean; advisors: BookingDirectoryEntry[] }>(
      "/api/advisors/booking-directory",
    );
  },

  async saveAdvisorAppointmentTypes(
    userId: string,
    types: AppointmentTypeInput[],
  ): Promise<AdvisorAppointmentTypesResponse> {
    return request<AdvisorAppointmentTypesResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/appointment-types`,
      { method: "PUT", body: JSON.stringify({ types }) },
    );
  },

  // An advisor's booking profile (bio, needs served, audience). Same
  // read/save split as appointment types.
  async getAdvisorBookingProfile(userId: string): Promise<AdvisorBookingProfileResponse> {
    return request<AdvisorBookingProfileResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/booking-profile`,
    );
  },

  async saveAdvisorBookingProfile(
    userId: string,
    input: {
      public_bio: string | null;
      title: string | null;
      serves_needs: string[];
      audience: "any" | "new" | "existing";
    },
  ): Promise<AdvisorBookingProfileResponse> {
    return request<AdvisorBookingProfileResponse>(
      `/api/advisors/${encodeURIComponent(userId)}/booking-profile`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },

  // The /book routing registry (needs, rules, pools, live coverage).
  // Read: any employee. Save: system_admin only (server-enforced).
  async getBookingRouting(): Promise<BookingRoutingResponse> {
    return request<BookingRoutingResponse>("/api/admin/booking-routing");
  },

  async saveBookingRouting(input: BookingRoutingSaveInput): Promise<BookingRoutingResponse> {
    return request<BookingRoutingResponse>("/api/admin/booking-routing", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  // Mint a personal booking link for a known contact (Phase B2's
  // zero-typing golden path). Front desk + admins + the advisor
  // themselves — enforced server-side.
  async mintBookingInvite(
    advisorUserId: string,
    input: {
      contact_id: string;
      appointment_type_id?: string | null;
      context_note?: string | null;
      /** "household" addresses the link to the couple (2026-09-09);
       *  absent means one person, as every link before this was. */
      audience?: "person" | "household";
    },
  ): Promise<{ invite_url: string; expires_at: string; audience?: "person" | "household" }> {
    return request<{ invite_url: string; expires_at: string; audience?: "person" | "household" }>(
      `/api/advisors/${encodeURIComponent(advisorUserId)}/booking-invites`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // The household's contactable people, for the booking form's
  // "who's coming?" picker.
  async getHouseholdAttendees(householdId: string): Promise<{ contacts: HouseholdAttendee[] }> {
    return request<{ contacts: HouseholdAttendee[] }>(
      `/api/advisors/household-attendees?household_id=${encodeURIComponent(householdId)}`,
    );
  },

  // v1.x phase 11.16 (Tier B item 16) — voice brief. `date` is
  // YYYY-MM-DD; defaults server-side to today UTC when omitted.
  // `kind` accepts "daily" (the focal day) or "weekly" (the 7 days
  // ending on the focal date — Grant's weekend recap use case).
  async getVoiceBrief(
    opts: { date?: string; kind?: "daily" | "weekly" } = {},
  ): Promise<VoiceBriefResponse> {
    const qs = new URLSearchParams();
    if (opts.date) qs.set("date", opts.date);
    if (opts.kind) qs.set("kind", opts.kind);
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<VoiceBriefResponse>(`/api/dashboard/donna/brief${tail}`);
  },

  // v1.x: save an email attachment directly to a record. Server pulls
  // the bytes from Graph, stores them in SharePoint (R2 only when
  // SharePoint isn't configured), and creates the documents row in
  // one shot — no client-side two-step.
  //
  // 2026-08-05: the workflow record types joined household/contact so
  // an emailed document can be filed on the transaction it belongs to
  // (Nate's ask).
  async saveEmailAttachmentToEntity(
    messageId: string,
    attachmentId: string,
    input: {
      entity_type: SaveAttachmentEntityType;
      entity_id: string;
      /** Optional: the checklist document item this attachment fulfils
       *  (Processing / Opportunity records only, 2026-10-15). */
      checklist_task_id?: string;
    },
  ): Promise<{
    document_id: string;
    entity_type: string;
    entity_id: string;
    size_bytes: number;
    filename: string;
    /** Present only when a checklist item was named: whether it was
     *  checked off (false if it moved on before the save finished). */
    checklist_item_linked?: boolean;
  }> {
    return request(
      `/api/email/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}/save-to-entity`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // The email ITSELF as a PDF onto a record (Cambria, 2026-10-28): the
  // OS version of "print the email to PDF and save it to the loan file".
  // Same record types and optional checklist item as an attachment save.
  // `mailboxUpn` is the shared mailbox the email is open in, if any.
  async saveEmailAsPdfToEntity(
    messageId: string,
    input: {
      entity_type: SaveAttachmentEntityType;
      entity_id: string;
      checklist_task_id?: string;
    },
    mailboxUpn?: string | null,
  ): Promise<{
    document_id: string;
    entity_type: string;
    entity_id: string;
    size_bytes: number;
    filename: string;
    checklist_item_linked?: boolean;
  }> {
    const mailboxQuery = mailboxUpn ? `?mailbox=${encodeURIComponent(mailboxUpn)}` : "";
    return request(
      `/api/email/messages/${encodeURIComponent(messageId)}/save-as-pdf${mailboxQuery}`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // The household's OPEN Processing + Opportunities, in the small
  // shape the Save-attachment dialog's second step needs. Feeds
  // "…or file it on one of this household's active records."
  async listHouseholdWorkRecords(householdId: string): Promise<HouseholdWorkRecordsResponse> {
    return request<HouseholdWorkRecordsResponse>(
      `/api/households/${encodeURIComponent(householdId)}/work-records`,
    );
  },

  // v1.x: Microsoft Graph search folders (mailSearchFolder). Used to
  // pin saved searches like "All email with John Smith" or "All
  // unread across my whole mailbox" into the EMAIL sidebar.
  async listSearchFolders(): Promise<{ folders: SearchFolderSummary[] }> {
    return request<{ folders: SearchFolderSummary[] }>("/api/email/search-folders");
  },
  async createSearchFolder(
    input: CreateSearchFolderInput,
  ): Promise<{ folder: SearchFolderSummary }> {
    return request<{ folder: SearchFolderSummary }>("/api/email/search-folders", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async deleteSearchFolder(folderId: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/api/email/search-folders/${encodeURIComponent(folderId)}`, {
      method: "DELETE",
    });
  },

  // v1.x: Microsoft To Do — Flow B (live proxy). The advisor sees
  // every task list in their To Do account; writes go straight to
  // Graph with no DB persistence on our side.
  async listAllTodoLists(): Promise<{ lists: TodoTaskList[] }> {
    return request<{ lists: TodoTaskList[] }>("/api/tasks/all-todo/lists");
  },
  async listTasksInTodoList(
    listId: string,
    opts: { top?: number; next?: string | null } = {},
  ): Promise<{ tasks: TodoTask[]; nextLink: string | null }> {
    const qs = new URLSearchParams();
    if (opts.top) qs.set("top", String(opts.top));
    if (opts.next) qs.set("next", opts.next);
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<{ tasks: TodoTask[]; nextLink: string | null }>(
      `/api/tasks/all-todo/lists/${encodeURIComponent(listId)}/tasks${tail}`,
    );
  },
  async createTodoTask(listId: string, input: CreateTodoTaskInput): Promise<{ task: TodoTask }> {
    return request<{ task: TodoTask }>(
      `/api/tasks/all-todo/lists/${encodeURIComponent(listId)}/tasks`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  async updateTodoTask(
    listId: string,
    taskId: string,
    patch: UpdateTodoTaskPatch,
  ): Promise<{ task: TodoTask }> {
    return request<{ task: TodoTask }>(
      `/api/tasks/all-todo/lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },
  async completeTodoTask(listId: string, taskId: string): Promise<{ task: TodoTask }> {
    return request<{ task: TodoTask }>(
      `/api/tasks/all-todo/lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}/complete`,
      { method: "POST" },
    );
  },
  async deleteTodoTask(listId: string, taskId: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(
      `/api/tasks/all-todo/lists/${encodeURIComponent(listId)}/tasks/${encodeURIComponent(taskId)}`,
      { method: "DELETE" },
    );
  },

  // v1.x: Stewardship Tasks — Flow A (mirror via /api/tasks).
  // Local manual_tasks rows that ALSO push to the user's
  // "Stewardship OS" Microsoft To Do list. Each write fires a
  // background Graph push (executionCtx.waitUntil); the local row
  // commits before the API response returns, so the UI is snappy.
  // v1.x phase 11.14: list active employees the current user can
  // assign tasks to. Powers the assignee picker in AddTaskModal +
  // EditTaskModal.
  async listAssignableUsers(): Promise<{ users: AssignableUser[] }> {
    return request<{ users: AssignableUser[] }>("/api/users/assignable");
  },

  // The advisors who may own one company's records (2026-10-02): the
  // cleanup "who is the advisor?" picker. Same shape as the list above.
  async listCompanyAdvisors(company: string): Promise<{ users: AssignableUser[] }> {
    return request<{ users: AssignableUser[] }>(
      `/api/users/assignable?company=${encodeURIComponent(company)}`,
    );
  },

  // Who may OWN an opportunity in one company (2026-10-19): the company's
  // advisors plus system_admin and compliance. Wider than the advisor list.
  async listCompanyOwnerCandidates(company: string): Promise<{ users: AssignableUser[] }> {
    return request<{ users: AssignableUser[] }>(
      `/api/users/assignable?company=${encodeURIComponent(company)}&owners=1`,
    );
  },

  // Hand an open opportunity to another owner (2026-10-19). `expected` is the
  // owner the person saw; a 409 `stale_owner` means someone changed it first.
  // `undo: true` is the 60-second Undo (recorded as a rollback).
  async changeOpportunityOwner(
    opportunityId: string,
    input: {
      owner_user_id: string;
      expected_owner_user_id: string | null;
      reason_code?: string;
      reason_text?: string;
      undo?: boolean;
    },
  ): Promise<{
    ok: true;
    opportunity_id: string;
    no_op: boolean;
    prior_owner_user_id: string | null;
    owner_user_id: string;
    owner_name: string | null;
  }> {
    return request(`/api/opportunities/${encodeURIComponent(opportunityId)}/change-owner`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // The automation builder's appointment selector (2026-08-16): step 1
  // is "a user or a service", step 2 is that target's live appointment
  // types. Both are fetched (never cached in a constant) because
  // appointment types are advisor-owned rows that change whenever an
  // advisor edits their own booking settings.
  async listAutomationBookingTargets(): Promise<{ options: BuilderPickerOption[] }> {
    return request<{ options: BuilderPickerOption[] }>(
      "/api/admin/automation-options/booking-targets",
    );
  },

  async listAutomationAppointmentTypes(
    target: string,
  ): Promise<{ options: BuilderPickerOption[] }> {
    return request<{ options: BuilderPickerOption[] }>(
      `/api/admin/automation-options/appointment-types?target=${encodeURIComponent(target)}`,
    );
  },

  /** A company's contract deadlines, for the key-date trigger's picker.
   *  Dependent on the chosen company, same as appointment types are on
   *  their booking target. */
  async listAutomationKeyDates(company: string): Promise<{ options: BuilderPickerOption[] }> {
    return request<{ options: BuilderPickerOption[] }>(
      `/api/admin/automation-options/key-dates?company=${encodeURIComponent(company)}`,
    );
  },

  // Active Ambassador Agents — the "which ambassador sent them?" picker
  // shown when an employee marks a referral's source as Ambassador Agent
  // (migration 20260814223351). Loaded lazily, only once that source is picked.
  async listAmbassadors(): Promise<{ ambassadors: AmbassadorOption[] }> {
    return request<{ ambassadors: AmbassadorOption[] }>("/api/users/ambassadors");
  },

  /** Where an existing opportunity came from (and who, if an ambassador). */
  async getOpportunitySource(id: string): Promise<OpportunitySource> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/source`);
  },

  /**
   * Set it. Naming an ambassador is what credits them — the server
   * refuses the Ambassador Agent source without one, and refuses to
   * un-credit someone whose fee has already been worked out.
   */
  async setOpportunitySource(
    id: string,
    body: {
      source_type: string | null;
      source_detail?: string | null;
      internal_referrer_user_id?: string | null;
      internal_referred_from_company_slug?: string | null;
    },
  ): Promise<{
    ok: true;
    opportunity_id: string;
    source_type: string | null;
    internal_referrer_user_id: string | null;
    ambassador_referral_id: string | null;
  }> {
    return request(`/api/opportunities/${encodeURIComponent(id)}/source`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  // Cross-company referral tracking (Ryan's feedback, 2026-07-09):
  // Stewardship's companies (incl. Stewardship Productions), for the
  // referral-source company picker on Data Needed + the contact edit form.
  async listCompanies(): Promise<{ companies: CompanyOption[] }> {
    return request<{ companies: CompanyOption[] }>("/api/companies");
  },

  // Admin — Shared Mailbox Access. Grant/revoke which employees see a
  // shared mailbox in the picker (on top of the role/company defaults).
  async getSharedMailboxAccess(): Promise<{
    mailboxes: SharedMailboxAccessRow[];
  }> {
    return request<{ mailboxes: SharedMailboxAccessRow[] }>("/api/admin/shared-mailboxes");
  },

  async grantSharedMailbox(input: {
    mailbox_upn: string;
    employee_user_id: string;
  }): Promise<{ grant_id: string; created: boolean }> {
    return request<{ grant_id: string; created: boolean }>("/api/admin/shared-mailboxes/grants", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // Connect a shared mailbox by address (system_admin). The server probes
  // Microsoft first; a missing FullAccess grant comes back as a 403 whose
  // `message` + `data.path_forward` say what to ask for.
  async connectSharedMailbox(input: {
    upn: string;
    display_name?: string;
    description?: string;
  }): Promise<{ id: string; upn: string }> {
    return request<{ id: string; upn: string }>("/api/email/shared-mailboxes/connect", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async disconnectSharedMailbox(upn: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/admin/shared-mailboxes/connected/${encodeURIComponent(upn)}`,
      { method: "DELETE" },
    );
  },

  // Outlook categories (colored tags) for the user's mailbox, or a shared
  // mailbox they may see.
  async getEmailCategories(
    mailboxUpn?: string | null,
  ): Promise<{ categories: OutlookCategoryRow[] }> {
    const qs = mailboxUpn ? `?mailbox=${encodeURIComponent(mailboxUpn)}` : "";
    return request<{ categories: OutlookCategoryRow[] }>(`/api/email/categories${qs}`);
  },

  async revokeSharedMailbox(grantId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/admin/shared-mailboxes/grants/${encodeURIComponent(grantId)}`,
      { method: "DELETE" },
    );
  },

  // "+ Filter" builder dynamic choice lists (Saved-Filter Builder
  // PR 5): stages, carriers, service types/families, plan templates.
  async listFilterOptions(
    source: string,
    entity: string,
  ): Promise<{ options: { value: string; label: string }[] }> {
    const qs = new URLSearchParams({ source, entity });
    return request<{ options: { value: string; label: string }[] }>(
      `/api/filter-options?${qs.toString()}`,
    );
  },

  // Plain English → validated filter conditions (PR 8). The server
  // runs the registry validation; what comes back is exactly what the
  // list endpoints can execute.
  async nlFilter(
    entity: string,
    message: string,
  ): Promise<{ conditions: FilterCondition[]; dropped_reasons: string[] }> {
    return request<{ conditions: FilterCondition[]; dropped_reasons: string[] }>(
      "/api/lists/nl-filter",
      {
        method: "POST",
        body: JSON.stringify({ entity, message }),
        headers: { "content-type": "application/json" },
      },
    );
  },

  // Full-result audited CSV export of a filtered list (PR 7). Returns
  // the raw CSV text — the caller downloads it. Throws with the
  // server's plain-English message on 4xx.
  async exportList(input: {
    entity: string;
    filters: { key: string; value: string }[];
    conditions: FilterCondition[];
    sort_by: string | null;
    sort_dir: "asc" | "desc";
    search: string;
    columns: string[];
    row_cap?: number;
    reason_code?: string;
    reason_note?: string;
    // Returns the CSV plus what the server actually shipped, so the caller
    // can tell the user the truth when the export hit its ceiling
    // (Grant 2026-07-26, accuracy sweep).
  }): Promise<{
    csv: string;
    rowCount: number | null;
    totalMatching: number | null;
    truncated: boolean;
  }> {
    const response = await fetch(apiUrl("/api/list-export"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      let message = `Export failed (${response.status}).`;
      try {
        const parsed = (await response.json()) as { message?: string };
        if (parsed.message) message = parsed.message;
      } catch {
        // keep the generic message
      }
      throw new Error(message);
    }
    const num = (h: string): number | null => {
      const raw = response.headers.get(h);
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    };
    return {
      csv: await response.text(),
      rowCount: num("x-export-row-count"),
      totalMatching: num("x-export-total-matching"),
      truncated: response.headers.get("x-export-truncated") === "true",
    };
  },

  // v1.x phase 11.14 follow-up: unified record search for the LINK
  // TO picker (and, since S2 PR 1, every ranked-search consumer).
  // Server caps results at 8 per type. Empty q (or q < 2 chars)
  // returns an empty array. opts.types narrows which entity types
  // the server searches — e.g. the task modals request only the
  // task-linkable set (manual_tasks' CHECK doesn't allow 'vendor').
  async searchRecords(
    q: string,
    opts: { types?: ReadonlyArray<RecordSearchResult["entity_type"]> } = {},
  ): Promise<{ results: RecordSearchResult[]; notice?: string | null }> {
    const params = new URLSearchParams({ q });
    if (opts.types && opts.types.length > 0) {
      params.set("types", opts.types.join(","));
    }
    // `notice` (Grant 2026-07-16) explains an empty/degraded result in
    // plain English — too-short query, over-long query that got clamped,
    // or a genuine no-match. Optional; typeahead callers may ignore it,
    // the global search surface shows it.
    return request<{ results: RecordSearchResult[]; notice?: string | null }>(
      `/api/search/records?${params.toString()}`,
    );
  },

  // ── Cascade archive (preview / execute / undo) ──────────────────────
  async archivePreview(kind: string, id: string): Promise<{ manifest: ArchiveManifestResponse }> {
    const params = new URLSearchParams({ kind, id });
    return request<{ manifest: ArchiveManifestResponse }>(
      `/api/archive/preview?${params.toString()}`,
    );
  },

  async archiveExecute(body: {
    kind: string;
    id: string;
    reason: string;
    reason_code: string;
    member_ids?: string[];
    transfers?: Array<{
      kind: string;
      id: string;
      target_kind: string;
      target_id: string;
    }>;
    /** Contact cascades: also archive the household this leaves empty. */
    also_archive_household?: boolean;
  }): Promise<{
    bulk_action_id: string;
    archived_count: number;
    transferred_count: number;
    emptied_household?: {
      id: string;
      archived: boolean;
      blocked_reason?: "has_live_members" | "active_attachments";
    };
  }> {
    return request(`/api/archive/execute`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async archiveUndo(bulkActionId: string): Promise<{ restored: number }> {
    return request(`/api/archive/undo`, {
      method: "POST",
      body: JSON.stringify({ bulk_action_id: bulkActionId }),
    });
  },

  // P1-core: move a single workflow record (deal/policy/case/plan) onto
  // another household. Reversible — undo with archiveUndo(bulk_action_id).
  async moveRecordToHousehold(
    kind: "service_record" | "opportunity" | "transaction" | "rhythm_instance",
    id: string,
    body: {
      target_household_id: string;
      reason_code: string;
      reason?: string;
      /** Join an existing bulk action (a household split) so this record
       *  unwinds with it rather than needing its own undo. */
      bulk_action_id?: string;
    },
  ): Promise<{
    status: "moved";
    bulk_action_id: string;
    from_household_id: string | null;
    to_household_id: string;
    /** What travelled with the record (see shared/db/record-household-move.ts). */
    care_plans_moved?: number;
    processing_moved?: number;
    client_status_recomputed?: number;
  }> {
    return request(
      `/api/records/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/move-household`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  },

  // gs follow-ons A1 — the vendors reference surface.
  // ── Employers (employer groups; workers/api/src/routes/employers.ts) ──
  async listEmployers(params: { q?: string; scope?: "mine" | "all"; limit?: number; offset?: number } = {}): Promise<EmployerListResponse> {
    const qs = new URLSearchParams();
    if (params.q?.trim()) qs.set("q", params.q.trim());
    if (params.scope === "all") qs.set("scope", "all");
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.offset) qs.set("offset", String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return request<EmployerListResponse>(`/api/employers${tail}`);
  },
  async getEmployer(id: string): Promise<EmployerDetailResponse> {
    return request<EmployerDetailResponse>(`/api/employers/${encodeURIComponent(id)}`);
  },
  async createEmployer(input: EmployerCreateInput): Promise<{ ok: true; id: string }> {
    return request(`/api/employers`, { method: "POST", body: JSON.stringify(input) });
  },
  async updateEmployer(
    id: string,
    patch: Partial<EmployerCreateInput> & { expected_version_number: number },
  ): Promise<{ ok: true; version_number: number }> {
    return request(`/api/employers/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  async linkEmployerPerson(id: string, contactId: string, role: EmployerPersonRole): Promise<{ ok: true; id: string; created: boolean }> {
    return request(`/api/employers/${encodeURIComponent(id)}/people`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId, role }),
    });
  },
  async setEmployerWorkDetails(
    employerId: string,
    contactId: string,
    body: {
      work_email: string | null;
      work_phone: string | null;
      job_title: string | null;
      expected_version_number: number;
    },
  ): Promise<{ ok: true; version_number: number; changed: string[] }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/people/${encodeURIComponent(contactId)}/work-details`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  },
  async unlinkEmployerPerson(id: string, linkId: string, reason?: string): Promise<{ ok: true; ended: boolean }> {
    return request(`/api/employers/${encodeURIComponent(id)}/people/${encodeURIComponent(linkId)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason: reason ?? null }),
    });
  },
  async openEmployerGroupSale(
    id: string,
    input: { for_contact_ids: string[]; owner_user_id?: string; expected_close_date?: string },
  ): Promise<{ ok: true; id: string }> {
    return request(`/api/employers/${encodeURIComponent(id)}/opportunities`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateGroupPlan(
    employerId: string,
    planId: string,
    patch: GroupPlanEdit,
  ): Promise<{ ok: true; version_number: number; changed: string[] }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },
  async getGroupPlanHistory(
    employerId: string,
    planId: string,
  ): Promise<{ history: GroupPlanHistoryEntry[] }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/history`,
    );
  },
  async getGroupPlanRoster(employerId: string, planId: string): Promise<{ roster: GroupPlanRoster }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/members`,
    );
  },
  /** Expected vs received commission on the plan's certificates. 403 for
   *  anyone outside Planning, compliance and system admins. */
  async getGroupPlanCommissions(employerId: string, planId: string): Promise<{ commissions: GroupPlanCommissions }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/commissions`,
    );
  },
  async previewBringIn(
    employerId: string,
    planId: string,
    rows: EnrollmentRowInput[],
  ): Promise<{ preview: { rows: BringInPreviewRow[]; counts: Record<string, number> } }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/bring-in/preview`,
      { method: "POST", body: JSON.stringify({ rows }) },
    );
  },
  async applyBringIn(
    employerId: string,
    planId: string,
    body: {
      bulk_action_id: string | null;
      items: { row: EnrollmentRowInput; decision: BringInDecision }[];
    },
  ): Promise<{ ok: true; bulk_action_id: string; results: BringInRowResult[] }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/bring-in/apply`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async undoBringIn(
    employerId: string,
    planId: string,
    bulkActionId: string,
  ): Promise<{ ok: true; certificates_archived: number; people_archived: number; households_archived: number }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/bring-in/undo`,
      { method: "POST", body: JSON.stringify({ bulk_action_id: bulkActionId }) },
    );
  },
  /** Record that someone left the employer: kept the coverage on their own
   *  ("portable") or it ended. Planning only; undoable. */
  async recordGroupMemberLeft(
    employerId: string,
    planId: string,
    memberId: string,
    body: { outcome: "portable" | "ended"; left_on: string; reason: string | null; expected_version_number: number },
  ): Promise<{ ok: true; status: string }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/members/${encodeURIComponent(memberId)}/left`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async undoGroupMemberLeft(
    employerId: string,
    planId: string,
    memberId: string,
    expectedVersion: number,
  ): Promise<{ ok: true; status: string }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/members/${encodeURIComponent(memberId)}/left/undo`,
      { method: "POST", body: JSON.stringify({ expected_version_number: expectedVersion }) },
    );
  },
  async endGroupPlan(
    employerId: string,
    planId: string,
    body: { ended_on: string; reason: string | null; expected_version_number: number },
  ): Promise<{ ok: true; version_number: number }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/end`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  async reopenGroupPlan(
    employerId: string,
    planId: string,
    expectedVersion: number,
  ): Promise<{ ok: true; version_number: number }> {
    return request(
      `/api/employers/${encodeURIComponent(employerId)}/group-plans/${encodeURIComponent(planId)}/reopen`,
      { method: "POST", body: JSON.stringify({ expected_version_number: expectedVersion }) },
    );
  },
  async listVendors(q = ""): Promise<{ vendors: VendorSummary[] }> {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    const tail = params.toString() ? `?${params.toString()}` : "";
    return request<{ vendors: VendorSummary[] }>(`/api/vendors${tail}`);
  },

  // Vendor merge (Baker, 2026-11-02). `id` is always the duplicate; `keep`
  // is the vendor that stays. A 404 from getVendorDetail carries
  // `merged_away` in ApiError.data when the vendor was merged into another.
  async previewVendorMerge(id: string, keep: string): Promise<VendorMergePreview> {
    return request<VendorMergePreview>(
      `/api/vendors/${encodeURIComponent(id)}/merge-preview?keep=${encodeURIComponent(keep)}`,
    );
  },
  async mergeVendor(
    id: string,
    body: { keep_vendor_id: string; reason_code: string; reason_text: string | null; confirm?: string },
  ): Promise<VendorMergeResult> {
    return request<VendorMergeResult>(`/api/vendors/${encodeURIComponent(id)}/merge`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async undoVendorMerge(bulkActionId: string): Promise<{ ok: true; rows_restored: number; skipped_drifted: number }> {
    return request(`/api/vendors/merges/${encodeURIComponent(bulkActionId)}/undo`, { method: "POST" });
  },

  async getVendorDetail(id: string): Promise<VendorDetailResponse> {
    return request<VendorDetailResponse>(`/api/vendors/${encodeURIComponent(id)}`);
  },
  /** Row 4.1: the two facts typed on a center of influence's page. */
  async updateVendorCenterOfInfluence(
    id: string,
    patch: { reciprocal_cadence_months?: number | null; last_referred_to_at?: string | null },
  ): Promise<{ ok: true; center_of_influence: CenterOfInfluenceFacts }> {
    return request(`/api/vendors/${encodeURIComponent(id)}/center-of-influence`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
  },

  // The VA LGY Hub PIN card (Mortgage only; Grant, 2026-10-26). The GET
  // carries dates and names, never the PIN; reveal returns it and is
  // audited. Anyone outside Mortgage gets a 403.
  async getVaLgyPin(): Promise<CompanyCredentialStatus> {
    return request<CompanyCredentialStatus>("/api/company-credentials/va-lgy-pin");
  },

  async revealVaLgyPin(): Promise<{ credential_id: string; pin: string }> {
    return request("/api/company-credentials/va-lgy-pin/reveal", { method: "POST" });
  },

  async setVaLgyPin(pin: string): Promise<CompanyCredentialStatus> {
    return request("/api/company-credentials/va-lgy-pin", {
      method: "PUT",
      body: JSON.stringify({ pin }),
    });
  },

  async reuseVaLgyPin(credentialId: string): Promise<CompanyCredentialStatus> {
    return request(`/api/company-credentials/va-lgy-pin/reuse/${encodeURIComponent(credentialId)}`, {
      method: "POST",
    });
  },

  // The Stewardship Mortgage approved-lender roster, with the account
  // executive at each lender.
  async listApprovedLenders(): Promise<{ lenders: ApprovedLender[] }> {
    return request<{ lenders: ApprovedLender[] }>("/api/vendors/approved-lenders");
  },

  // Editing the roster (Grant, 2026-09-24). Mortgage-scoped server-side:
  // anyone else gets a 403. A 409 carries the existing lender / contact
  // on ApiError.data so the caller can offer it instead of a twin.
  async createApprovedLender(input: {
    display_name: string;
    portal_url?: string | null;
    notes?: string | null;
  }): Promise<{ lender: RosterLender }> {
    return request(`/api/vendors/approved-lenders`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async updateApprovedLender(
    vendorId: string,
    patch: {
      display_name?: string;
      portal_url?: string | null;
      notes?: string | null;
      approval_status?: string | null;
      programs?: LenderProgram[];
    },
  ): Promise<{ lender: RosterLender; changes: Record<string, { from: unknown; to: unknown }> }> {
    return request(`/api/vendors/approved-lenders/${encodeURIComponent(vendorId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  async addLenderAccountExecutive(
    vendorId: string,
    input:
      | { contact_id: string }
      | {
          first_name?: string | null;
          last_name?: string | null;
          email?: string | null;
          phone_primary?: string | null;
          phone_secondary?: string | null;
        },
  ): Promise<{ kind: "created" | "linked"; contact_id: string; restored?: boolean }> {
    return request(
      `/api/vendors/approved-lenders/${encodeURIComponent(vendorId)}/account-executives`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  async removeLenderAccountExecutive(
    vendorId: string,
    contactId: string,
    opts: { archive?: boolean } = {},
  ): Promise<{ unlinked: boolean; archived: boolean }> {
    const qs = opts.archive ? "?archive=1" : "";
    return request(
      `/api/vendors/approved-lenders/${encodeURIComponent(vendorId)}/account-executives/${encodeURIComponent(contactId)}${qs}`,
      { method: "DELETE" },
    );
  },

  // Comp plans: what each payer pays Stewardship (comp_schedules,
  // commissions build program A4). 403 for anyone who is not a system
  // admin, a named person for that company, or compliance; the lender
  // list then shows without its rates.
  async getCompSchedules(company: string): Promise<CompSchedulesResponse> {
    return request<CompSchedulesResponse>(`/api/comp-schedules/${encodeURIComponent(company)}`);
  },

  // The Carriers view (Vendors → Carriers). Every employee gets the list;
  // rates only come back for the people who may see that company's comp
  // plans (rates_visible).
  async getCarriers(company: "insurance" | "planning"): Promise<CarriersResponse> {
    return request<CarriersResponse>(`/api/comp-schedules/${company}/carriers`);
  },

  // Planning's carrier statements (commissions build program C6, part 3):
  // Health Values Association and LTCPG. 403 for anyone outside the
  // Planning team, system admins and compliance; the Carriers view then
  // leaves the section out.
  async getPlanningStatements(vendorId: string): Promise<PlanningStatementsResponse> {
    return request<PlanningStatementsResponse>(`/api/planning-statements/${encodeURIComponent(vendorId)}`);
  },
  /** The file goes up, is kept, and Stew reads it into a draft for the grid. Nothing reaches the ledger yet. */
  async readPlanningStatement(vendorId: string, file: File): Promise<PlanningStatementDraft> {
    const fd = new FormData();
    fd.append("file", file);
    // Not request(): a FormData body needs the browser's own multipart boundary.
    const res = await fetch(apiUrl(`/api/planning-statements/${encodeURIComponent(vendorId)}/read`), { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) {
      let payload: { error?: string; message?: string } = {};
      try {
        payload = (await res.json()) as { error?: string; message?: string };
      } catch {
        // ignore parse errors
      }
      throw new ApiError(res.status, payload.message ?? payload.error ?? `read_failed_${res.status}`, payload.error ?? null);
    }
    return (await res.json()) as PlanningStatementDraft;
  },
  async savePlanningStatement(
    vendorId: string,
    body: { sha256: string; draft: Record<string, unknown>; payable_typed?: boolean },
  ): Promise<PlanningStatementSaved> {
    return request<PlanningStatementSaved>(`/api/planning-statements/${encodeURIComponent(vendorId)}/save`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async withdrawPlanningStatement(vendorId: string, batchId: string, reason: string): Promise<{ withdrawn: true; statements: PlanningStatementView[] }> {
    return request(`/api/planning-statements/${encodeURIComponent(vendorId)}/${encodeURIComponent(batchId)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  /** Planning's deposits and the carrier-and-month check (commissions step C6, part 2). */
  async getPlanningDeposits(month?: string): Promise<PlanningDepositsResponse> {
    return request<PlanningDepositsResponse>(`/api/planning-deposits${month ? `?month=${encodeURIComponent(month)}` : ""}`);
  },
  async getPlanningDepositCandidates(receiptId: string): Promise<{ deposit: PlanningDeposit; candidates: PlanningCandidate[] }> {
    return request(`/api/planning-deposits/${encodeURIComponent(receiptId)}/candidates`);
  },
  async placePlanningDeposit(
    receiptId: string,
    placements: { obligation_id: string; amount_cents: number }[],
  ): Promise<{ ok: true; placements: { cash_allocation_id: string; obligation_id: string; amount_cents: number; amended: boolean }[] }> {
    return request(`/api/planning-deposits/${encodeURIComponent(receiptId)}/place`, { method: "POST", body: JSON.stringify({ placements }) });
  },
  async undoPlanningPlacement(allocationId: string): Promise<{ ok: true; pay_line_approved_stays: boolean }> {
    return request(`/api/planning-deposits/placements/${encodeURIComponent(allocationId)}/undo`, { method: "POST" });
  },
  planningStatementFileUrl(vendorId: string, batchId: string): string {
    return apiUrl(`/api/planning-statements/${encodeURIComponent(vendorId)}/${encodeURIComponent(batchId)}/file`);
  },

  async createCompSchedule(
    company: string,
    // payer_vendor_id null: a Planning rate for any carrier, or the estate price list.
    input: { payer_vendor_id: string | null; service_line: string; product: string; version: CompVersionDraft },
  ): Promise<{ schedule_id: string; version_id: string; version_number: number }> {
    return request(`/api/comp-schedules/${encodeURIComponent(company)}`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // The Enter rates screen: a column of typed lender rates, previewed
  // (preview: true writes nothing) or saved in one press.
  async saveLenderRates(input: {
    rates: Array<{
      payer_vendor_id: string;
      product: string;
      percent: string;
      expected_version_number: number;
      effective_from?: string;
    }>;
    effective_from: string;
    source_note?: string;
    reason?: string;
    preview?: boolean;
  }): Promise<{ rows: RatePlanRow[]; applied: number; repriced: number | null }> {
    return request(`/api/comp-schedules/mortgage/rates`, { method: "POST", body: JSON.stringify(input) });
  },

  async addCompScheduleVersion(
    company: string,
    scheduleId: string,
    input: { expected_version_number: number; version: CompVersionDraft },
  ): Promise<{ schedule_id: string; version_id: string; version_number: number }> {
    return request(
      `/api/comp-schedules/${encodeURIComponent(company)}/${encodeURIComponent(scheduleId)}/versions`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  // gs follow-ons B5 — on-demand document content search (Microsoft's
  // index over the SharePoint drive; 503 when not configured).
  async searchDocuments(q: string): Promise<{
    documents: Array<{
      id: string;
      name: string;
      web_url: string;
      last_modified: string | null;
    }>;
  }> {
    return request(`/api/search/documents?q=${encodeURIComponent(q)}`);
  },

  // S2 PR 2 — recently-viewed records for the palette's empty state.
  // recordRecentVisit is a fire-and-forget beacon from record detail
  // pages: it must never block or break a page view, so callers
  // intentionally ignore the promise and we swallow failures here.
  async recordRecentVisit(input: {
    entity_type: RecordSearchResult["entity_type"];
    entity_id: string;
    label: string;
    sub_label?: string | null;
  }): Promise<void> {
    try {
      await request<{ ok: boolean }>("/api/recent-visits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
    } catch {
      // Beacon — a failed visit record is invisible by design.
    }
  },

  async listRecentVisits(): Promise<{ visits: RecentVisit[] }> {
    return request<{ visits: RecentVisit[] }>("/api/recent-visits");
  },

  async listStewardshipTasks(
    opts: { includeCompleted?: boolean } = {},
  ): Promise<{ tasks: StewardshipTask[] }> {
    const qs = new URLSearchParams();
    if (opts.includeCompleted) qs.set("includeCompleted", "1");
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<{ tasks: StewardshipTask[] }>(`/api/tasks${tail}`);
  },
  // Nate feedback 2026-07-01 — the Tasks page's "Transaction tasks"
  // section: the caller's open Processing records + their open/blocked
  // conditions.
  async listTransactionWork(): Promise<{
    transactions: TransactionWorkItem[];
  }> {
    return request<{ transactions: TransactionWorkItem[] }>("/api/tasks/transaction-work");
  },
  // Single Stewardship task fetch — used by the Today dashboard tile to
  // load the full task shape before opening the shared EditTaskModal (the
  // tile itself only carries the lightweight FollowupTask).
  async getStewardshipTask(taskId: string): Promise<{ task: StewardshipTask }> {
    return request<{ task: StewardshipTask }>(`/api/tasks/${encodeURIComponent(taskId)}`);
  },
  // Nate feedback 2026-08-19 — EditTaskModal's "Who and where" panel:
  // resolves a task's linked record down to its household, members,
  // and mailing address so a card/letter task shows who it's for and
  // where it goes instead of a raw record id.
  async getStewardshipTaskLinkContext(
    taskId: string,
  ): Promise<{ context: TaskLinkContext | null }> {
    return request<{ context: TaskLinkContext | null }>(
      `/api/tasks/${encodeURIComponent(taskId)}/link-context`,
    );
  },
  // Today tile — the advisor's Microsoft To-Do items due today or
  // overdue. A SEPARATE live-Graph fetch (like getUpcomingCalendarEvents)
  // so the fast dashboard aggregator never depends on Graph. Soft-fails:
  // `connected:false` when Office 365 isn't linked (never throws for that
  // case), so the tile renders a quiet connect nudge.
  // opts.list_ids, when passed (even as []), scopes the live To-Do read
  // to those lists — [] deliberately means "show nothing" (the advisor
  // unchecked every list), distinct from omitting the option entirely.
  async getTodayTodo(opts?: { list_ids?: string[] }): Promise<TodayTodoResponse> {
    const qs =
      opts?.list_ids !== undefined
        ? `?list_ids=${encodeURIComponent(opts.list_ids.join(","))}`
        : "";
    return request<TodayTodoResponse>(`/api/dashboard/today-todo${qs}`);
  },
  async createStewardshipTask(
    input: CreateStewardshipTaskInput,
  ): Promise<{ task: StewardshipTask }> {
    return request<{ task: StewardshipTask }>("/api/tasks", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // ── Advisor Requests Hub ───────────────────────────────────────────
  // Baker's inbox (GET) + the recipient actions. Backend:
  // workers/api/src/routes/advisor-requests.ts (auth: any employee; the
  // inbox is scoped to the caller server-side). Powers the "Requests
  // From Advisors" widget on Baker's dashboard.
  async listAdvisorRequestInbox(): Promise<{
    rows: AdvisorRequestInboxRow[];
    open_count: number;
  }> {
    return request<{ rows: AdvisorRequestInboxRow[]; open_count: number }>(
      "/api/advisor-requests/inbox",
    );
  },
  async acceptAdvisorRequest(id: string): Promise<void> {
    await request<{ ok: boolean }>(`/api/advisor-requests/${encodeURIComponent(id)}/accept`, {
      method: "POST",
    });
  },
  async declineAdvisorRequest(id: string): Promise<void> {
    await request<{ ok: boolean }>(`/api/advisor-requests/${encodeURIComponent(id)}/decline`, {
      method: "POST",
    });
  },
  async replyAdvisorRequest(id: string, responseBody: string): Promise<void> {
    await request<{ ok: boolean }>(`/api/advisor-requests/${encodeURIComponent(id)}/reply`, {
      method: "POST",
      body: JSON.stringify({ response_body: responseBody }),
    });
  },

  /**
   * Ask Stew PR #5 — execute a Stew-proposed Task Preview action.
   * The Rule D routing decision (Personal=To Do / Workflow=OS only /
   * Cross-cutting=both) is made server-side based on (assignee,
   * workflow link). Returns the routing decision + (PR #6) the
   * pending_action_id + reversible_until so the panel can render
   * the UndoToast.
   */
  async applyStewCreateTask(input: {
    title: string;
    body?: string | null;
    due_date?: string | null;
    assignee_user_id?: string | null;
    entity_type?: "household" | "contact" | "transaction" | "opportunity" | "service_record" | null;
    entity_id?: string | null;
    stew_session_id?: string | null;
  }): Promise<{
    routing: "to_do_only" | "os_only" | "both";
    task_id: string;
    reason: string;
    pending_action_id: string;
    reversible_until: string;
    reversible_window_seconds: number;
  }> {
    return request<{
      routing: "to_do_only" | "os_only" | "both";
      task_id: string;
      reason: string;
      pending_action_id: string;
      reversible_until: string;
      reversible_window_seconds: number;
    }>("/api/stew/apply", {
      method: "POST",
      body: JSON.stringify({
        action_type: "create_task_flow_a",
        task: input,
      }),
    });
  },

  /**
   * Ask Stew PR #6 — undo a Stew-applied action within the 60s
   * reversibility window. Returns ms_since_apply + rollback note so
   * the caller can surface the appropriate toast. Throws ApiError on
   * 4xx (not_found / wrong_user / not_applied / expired).
   */
  async undoStewAction(pendingActionId: string): Promise<{
    ok: true;
    ms_since_apply: number;
    rollback: { ok: boolean; note: string };
  }> {
    return request<{
      ok: true;
      ms_since_apply: number;
      rollback: { ok: boolean; note: string };
    }>("/api/stew/undo", {
      method: "POST",
      body: JSON.stringify({ pending_action_id: pendingActionId }),
    });
  },

  /**
   * Ask Stew V1.5.4 — list active proactive nudges for the current
   * user. Returns up to 10 newest-first. Cheap (single indexed query)
   * — safe to call on panel open + on a 5-minute heartbeat for the
   * badge count.
   */
  async listStewNudges(): Promise<{
    items: ReadonlyArray<{
      id: string;
      kind: string;
      generated_at: string;
      title: string;
      sub: string | null;
      rationale: string | null;
      related_entity_type: string | null;
      related_entity_id: string | null;
      href: string | null;
    }>;
    count: number;
  }> {
    return request("/api/stew/nudges");
  },

  /** Dismiss a nudge — won't re-fire for 24h. */
  async dismissStewNudge(id: string): Promise<{ ok: true; id: string }> {
    return request(`/api/stew/nudges/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      body: "{}",
    });
  },

  /** Signal that the user clicked through on a nudge. */
  async actOnStewNudge(id: string): Promise<{ ok: true; id: string }> {
    return request(`/api/stew/nudges/${encodeURIComponent(id)}/act`, {
      method: "POST",
      body: "{}",
    });
  },

  /**
   * Ask Stew V1.5.3 — create a calendar event via Microsoft Graph.
   * Direct-apply (no pending → confirm two-step) since the user
   * already reviewed the draft on the CalendarEventDraft card.
   * Returns the Graph event id + webLink so the panel can offer
   * "Open in Outlook".
   *
   * Throws ApiError on:
   *   * 412 graph_not_connected — user needs to connect Office 365
   *   * 403 scope_not_granted — connection lacks Calendars.ReadWrite
   *     (user needs disconnect + reconnect)
   *   * 502 create_failed — Graph returned a non-2xx
   */
  async applyStewCalendarEvent(input: {
    subject: string;
    start_iso: string;
    end_iso: string;
    body?: string | null;
    location?: string | null;
    attendees?: string[];
    is_online_meeting?: boolean;
    household_id?: string;
    stew_session_id?: string;
  }): Promise<{
    ok: true;
    event_id: string;
    web_link: string | null;
    pending_action_id: string;
    reversible_until: string;
    reversible_window_seconds: number;
  }> {
    return request<{
      ok: true;
      event_id: string;
      web_link: string | null;
      pending_action_id: string;
      reversible_until: string;
      reversible_window_seconds: number;
    }>("/api/stew/apply", {
      method: "POST",
      body: JSON.stringify({
        action_type: "create_calendar_event",
        event: input,
      }),
    });
  },

  /**
   * Ask Stew PR #7 — confirm a staged customize_view action. chat.ts
   * already validated the LLM patch + dry-ran it against the user's
   * current preferences_json + wrote ask_stew_pending_actions with
   * status='pending'. This endpoint flips status='applied', writes
   * the new preferences_json via setUserViewPreference, and returns
   * the undo handle so the panel can render UndoToast.
   */
  async applyStewCustomize(
    pendingActionId: string,
    reasonNote?: string | null,
  ): Promise<{
    ok: true;
    surface_key: string;
    pending_action_id: string;
    reversible_until: string;
    reversible_window_seconds: number;
  }> {
    return request<{
      ok: true;
      surface_key: string;
      pending_action_id: string;
      reversible_until: string;
      reversible_window_seconds: number;
    }>("/api/stew/apply", {
      method: "POST",
      body: JSON.stringify({
        action_type: "customize_view",
        pending_action_id: pendingActionId,
        // V1.5.1 audit fix — when DestructiveActionConfirm captured a
        // reason (modal's reasonRequired=true OR the user typed
        // optionally), thread it through to the apply route so it
        // persists into the audit row's context.reason_note.
        ...(reasonNote ? { reason_note: reasonNote } : {}),
      }),
    });
  },

  /**
   * Ask Stew Wave 5 — apply a staged proposal (one of the six stew_*
   * action types). chat.ts staged the row when the propose_action tool
   * ran; this flips it to applied, executes exactly what was previewed,
   * and returns the undo handle (+ the booking link, when one was minted).
   */
  async applyStewProposal(
    actionType: string,
    pendingActionId: string,
    reasonNote?: string | null,
  ): Promise<{
    ok: true;
    action_type: string;
    pending_action_id: string;
    reversible_until: string;
    reversible_window_seconds: number;
    message: string;
    link_url?: string;
  }> {
    return request<{
      ok: true;
      action_type: string;
      pending_action_id: string;
      reversible_until: string;
      reversible_window_seconds: number;
      message: string;
      link_url?: string;
    }>("/api/stew/apply", {
      method: "POST",
      body: JSON.stringify({
        action_type: actionType,
        pending_action_id: pendingActionId,
        ...(reasonNote ? { reason_note: reasonNote } : {}),
      }),
    });
  },

  /**
   * Ask Stew PR #9 — /admin/stew-feedback queue. Returns recent
   * rows + 7d/30d trend counts grouped by outcome_kind. Requires
   * system_admin OR compliance role (server-enforced).
   */
  async listStewFeedback(input: {
    limit?: number;
    follow_up_status?: string;
    outcome_kind?: string;
  }): Promise<{
    rows: ReadonlyArray<{
      id: string;
      user_id: string;
      user_name: string | null;
      user_email: string | null;
      outcome_kind: string;
      request_text: string | null;
      mode: string | null;
      surface_key: string | null;
      route: string | null;
      intent_classified: string | null;
      follow_up_status: string;
      cause_code: string | null;
      created_at: string;
      session_id: string | null;
      message_id: string | null;
    }>;
    trend: ReadonlyArray<{
      outcome_kind: string;
      last_7d: number;
      last_30d: number;
      all_time: number;
    }>;
  }> {
    const params = new URLSearchParams();
    if (input.limit !== undefined) {
      params.set("limit", String(input.limit));
    }
    if (input.follow_up_status) {
      params.set("follow_up_status", input.follow_up_status);
    }
    if (input.outcome_kind) {
      params.set("outcome_kind", input.outcome_kind);
    }
    const qs = params.toString();
    return request(`/api/admin/stew-feedback${qs ? `?${qs}` : ""}`);
  },

  /**
   * Ask Stew PR #9 — top off-schema customization asks, grouped by
   * normalized request_text. Drives the "what would the team want
   * us to ship next" roadmap-input chart.
   */
  async listTopStewCustomizationAsks(input: { limit?: number } = {}): Promise<{
    rows: ReadonlyArray<{
      normalized_text: string;
      count: number;
      most_recent: string;
      sample_route: string | null;
    }>;
  }> {
    const qs = input.limit !== undefined ? `?limit=${input.limit}` : "";
    return request(`/api/admin/stew-feedback/top-asks${qs}`);
  },

  /**
   * Ask Stew PR #9 — triage a feedback row. Server requires
   * system_admin (the mutation gate); admin OR compliance can
   * READ via listStewFeedback above.
   */
  async triageStewFeedback(input: {
    id: string;
    follow_up_status?: "new" | "triaged" | "accepted_into_roadmap" | "dismissed";
    /** Ask Stew v3, PR 1 — why the answer missed, one word from STEW_CAUSE_CODES. */
    cause_code?: string | null;
  }): Promise<{
    ok: true;
    id: string;
    follow_up_status: string;
    previous_status: string;
    cause_code: string | null;
  }> {
    return request(`/api/admin/stew-feedback/${encodeURIComponent(input.id)}`, {
      method: "PATCH",
      body: JSON.stringify({
        ...(input.follow_up_status ? { follow_up_status: input.follow_up_status } : {}),
        ...(input.cause_code !== undefined ? { cause_code: input.cause_code } : {}),
      }),
    });
  },

  /**
   * Ask Stew v3, PR 1 — "Stew answers": every reply's trace (from the
   * audit row each turn already writes), joined to the question, the
   * reply snippet and any thumb. Admin or compliance may read.
   */
  async listStewAnswers(input: { limit?: number; thumb?: "up" | "down" } = {}): Promise<{
    rows: ReadonlyArray<{
      trace_id: string;
      occurred_at: string;
      actor_user_id: string | null;
      actor_name: string | null;
      session_id: string | null;
      stew_message_id: string | null;
      question: string | null;
      reply: string | null;
      route: string | null;
      kind: string | null;
      model: string | null;
      tools: string[];
      model_calls: number | null;
      tool_calls: number | null;
      wall_ms: number | null;
      tokens: number | null;
      cache_read_tokens: number | null;
      db_queries: number | null;
      db_rows_read: number | null;
      hit_turn_limit: boolean;
      failure: string | null;
      failure_detail: string | null;
      thumb: "up" | "down" | null;
      feedback_id: string | null;
      cause_code: string | null;
      follow_up_status: string | null;
    }>;
  }> {
    const params = new URLSearchParams();
    if (input.limit !== undefined) params.set("limit", String(input.limit));
    if (input.thumb) params.set("thumb", input.thumb);
    const qs = params.toString();
    return request(`/api/admin/stew-feedback/answers${qs ? `?${qs}` : ""}`);
  },

  // ── Ask Stew Stage 2 PR 7: the verified-question library, the eval-case
  // queue, answer time per class, and "export this conversation" ──
  async listStewVerifiedQuestions(input: { all?: boolean; limit?: number } = {}): Promise<{
    rows: ReadonlyArray<StewVerifiedQuestionRow>;
  }> {
    const params = new URLSearchParams();
    if (input.all) params.set("all", "1");
    if (input.limit !== undefined) params.set("limit", String(input.limit));
    const qs = params.toString();
    return request(`/api/admin/stew-feedback/verified${qs ? `?${qs}` : ""}`);
  },
  async createStewVerifiedQuestion(input: {
    trace_id?: string;
    question?: string;
    synonyms?: string[];
    notes?: string | null;
    company_scope?: string[] | null;
  }): Promise<{ ok: true; id: string }> {
    return request("/api/admin/stew-feedback/verified", { method: "POST", body: JSON.stringify(input) });
  },
  async updateStewVerifiedQuestion(input: {
    id: string;
    question?: string;
    synonyms?: string[];
    notes?: string | null;
    company_scope?: string[] | null;
    active?: boolean;
  }): Promise<{ ok: true }> {
    const { id, ...patch } = input;
    return request(`/api/admin/stew-feedback/verified/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  async listStewEvalCases(input: { status?: "proposed" | "written" | "retired"; limit?: number } = {}): Promise<{
    rows: ReadonlyArray<StewEvalCaseRow>;
    counts: { proposed: number; written: number; retired: number };
  }> {
    const params = new URLSearchParams();
    if (input.status) params.set("status", input.status);
    if (input.limit !== undefined) params.set("limit", String(input.limit));
    const qs = params.toString();
    return request(`/api/admin/stew-feedback/eval-cases${qs ? `?${qs}` : ""}`);
  },
  async createStewEvalCase(input: {
    feedback_id?: string;
    trace_id?: string;
    question?: string;
    cause_code?: string | null;
    expected_note?: string | null;
  }): Promise<{ ok: true; id: string }> {
    return request("/api/admin/stew-feedback/eval-cases", { method: "POST", body: JSON.stringify(input) });
  },
  async setStewEvalCaseStatus(input: { id: string; status: "proposed" | "written" | "retired" }): Promise<{ ok: true }> {
    return request(`/api/admin/stew-feedback/eval-cases/${encodeURIComponent(input.id)}`, { method: "PATCH", body: JSON.stringify({ status: input.status }) });
  },
  async getStewLatency(input: { days?: number } = {}): Promise<{
    days: number;
    since: string;
    total: number;
    sampled: boolean;
    rows: ReadonlyArray<{ intent_class: string; answers: number; p50_ms: number | null; p95_ms: number | null }>;
  }> {
    const qs = input.days !== undefined ? `?days=${input.days}` : "";
    return request(`/api/admin/stew-feedback/latency${qs}`);
  },
  /** The conversation as the server stored it, as a Markdown file (own conversations only). */
  async exportStewConversation(sessionId: string): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(apiUrl(`/api/stew/sessions/${encodeURIComponent(sessionId)}/export`), {
      credentials: "include",
      headers: { Accept: "text/markdown" },
    });
    if (!response.ok) {
      let message = response.statusText || `HTTP ${response.status}`;
      let code: string | null = null;
      try {
        const body = (await response.json()) as { error?: string; message?: string };
        code = body.error ?? null;
        if (body.message) message = body.message;
      } catch {
        // fall back to statusText
      }
      throw new ApiError(response.status, message, code);
    }
    const blob = await response.blob();
    const cd = response.headers.get("Content-Disposition") ?? "";
    const m = /filename="([^"]+)"/.exec(cd);
    return { blob, filename: m?.[1] ?? "ask-stew-conversation.md" };
  },

  /**
   * Ask Stew PR #9 — fetch the user's role-aware suggestion chips
   * for a given mode. Returns the resolved persona key + label so
   * the RoleIndicatorPill can render the persona-tuned name (e.g.
   * "Insurance advisor" instead of just "advisor").
   *
   * Cheap (no LLM, no DB heavy lookup). Safe to call per-mode-
   * switch.
   */
  /**
   * Ask Stew v3 PR 4 — the inline brief for a household or record page:
   * the call-prep packet in sections, each with its sources, read live.
   */
  async getStewBrief(input: {
    kind: "household" | "contact" | "opportunity" | "transaction" | "service_record" | "rhythm_instance";
    id: string;
    sections?: string;
  }): Promise<{
    ok: true;
    packet: {
      kind: string;
      id: string;
      label: string;
      household_id: string | null;
      household_label: string | null;
      sections: Array<{ key: string; title: string; lines: string[]; sources: string[] }>;
      generated_at: string;
    };
    narration: StewBriefNarration | null;
  }> {
    const q = new URLSearchParams({ kind: input.kind, id: input.id });
    if (input.sections) q.set("sections", input.sections);
    return request(`/api/stew/brief?${q.toString()}`);
  },

  // Stage 2 PR 6 — "Narrate now" on the brief band. Serves the stored
  // narration when the facts have not changed (refreshed: false).
  async narrateStewBrief(input: {
    kind: "household" | "contact" | "opportunity" | "transaction" | "service_record" | "rhythm_instance";
    id: string;
    force?: boolean;
  }): Promise<{ ok: true; narration: StewBriefNarration; refreshed: boolean }> {
    return request("/api/stew/brief/narrate", { method: "POST", body: JSON.stringify(input) });
  },

  async getStewSuggestions(input: { mode: string }): Promise<{
    role: string | null;
    persona_key:
      | "system_admin"
      | "compliance"
      | "front_desk"
      | "back_office"
      | "company_admin"
      | "advisor"
      | "advisor:insurance"
      | "advisor:mortgage"
      | "advisor:financial"
      | "advisor:planning"
      | "advisor:realty"
      | "generic";
    persona_label: string;
    mode: string;
    suggestions: string[];
  }> {
    return request(`/api/stew/suggestions?mode=${encodeURIComponent(input.mode)}`);
  },

  /**
   * Ask Stew PR #8 — Rule F CSV export. Re-runs the named list
   * query server-side (the client never controls the column set
   * or projection) and returns the CSV as a Blob the caller can
   * pipe through Blob URL → <a download>. Throws ApiError on
   * unknown query_id / reason-required-but-missing / row-cap
   * violations.
   *
   * Per Rule F: every export writes an audit_log row with action
   * 'export' + context: {query_id, row_count, column_list_hash,
   * surface_key} BEFORE the bytes ship.
   */
  async exportStewCsv(input: {
    query_id: string;
    row_cap?: number;
    reason_code?: string;
    reason_note?: string;
  }): Promise<{
    blob: Blob;
    filename: string;
    rowCount: number;
    totalCount: number;
  }> {
    const response = await fetch(apiUrl("/api/stew/export-csv"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/csv",
      },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      let code: string | null = null;
      let message = response.statusText || `HTTP ${response.status}`;
      try {
        const body = (await response.json()) as {
          error?: string;
          message?: string;
        };
        code = body.error ?? null;
        if (body.message) message = body.message;
      } catch {
        // ignore — fall back to statusText
      }
      throw new ApiError(response.status, message, code);
    }
    const blob = await response.blob();
    // Pull the filename from Content-Disposition; fall back to a
    // sensible default.
    const cd = response.headers.get("Content-Disposition") ?? "";
    const filenameMatch = /filename="([^"]+)"/.exec(cd);
    const filename = filenameMatch?.[1] ?? `${input.query_id}.csv`;
    const rowCount = Number(response.headers.get("X-Stew-Row-Count") ?? "0");
    const totalCount = Number(response.headers.get("X-Stew-Total-Count") ?? "0");
    return { blob, filename, rowCount, totalCount };
  },

  /**
   * Ask Stew PR #6 — capture a feedback signal. Used by:
   *   * CardFeedback thumbs-down ("manual_thumbs_down")
   *   * useStewSession low-confidence-abandoned auto-capture
   *   * PR #7+ off-schema-customization escape hatch
   * Fire-and-forget; the caller typically doesn't wait on the
   * round-trip. Server returns the new feedback row id.
   */
  async recordStewFeedback(input: {
    outcome_kind:
      | "off_schema_customization"
      | "apply_then_undo_under_5s"
      | "permission_denied_retried"
      | "low_confidence_abandoned"
      | "manual_thumbs_down"
      | "manual_thumbs_up"
      | "escape_hatch_other";
    request_text?: string | null;
    session_id?: string | null;
    message_id?: string | null;
    mode?: string | null;
    surface_key?: string | null;
    route?: string | null;
    intent_classified?: string | null;
  }): Promise<{ ok: true; feedback_id: string }> {
    return request<{ ok: true; feedback_id: string }>("/api/stew/feedback", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async updateStewardshipTask(
    taskId: string,
    patch: UpdateStewardshipTaskPatch,
  ): Promise<{ task: StewardshipTask }> {
    return request<{ task: StewardshipTask }>(`/api/tasks/${encodeURIComponent(taskId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  async completeStewardshipTask(
    taskId: string,
    notes?: string | null,
  ): Promise<{ task: StewardshipTask }> {
    return request<{ task: StewardshipTask }>(`/api/tasks/${encodeURIComponent(taskId)}/complete`, {
      method: "POST",
      body: JSON.stringify(notes ? { notes } : {}),
    });
  },
  async reopenStewardshipTask(taskId: string): Promise<{ task: StewardshipTask }> {
    return request<{ task: StewardshipTask }>(`/api/tasks/${encodeURIComponent(taskId)}/reopen`, {
      method: "POST",
    });
  },
  async deleteStewardshipTask(taskId: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`/api/tasks/${encodeURIComponent(taskId)}`, {
      method: "DELETE",
    });
  },

  // Phase 10: AI compose
  async getAiPreferences(): Promise<AiPreferencesResponse> {
    return request<AiPreferencesResponse>("/api/preferences/ai");
  },
  async saveAiPreferences(input: {
    custom_instructions?: string | null;
    model_preference?: "sonnet" | "opus";
  }): Promise<AiPreferencesResponse> {
    return request<AiPreferencesResponse>("/api/preferences/ai", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async composeAssist(input: ComposeAssistInput): Promise<ComposeAssistResponse> {
    return request<ComposeAssistResponse>("/api/email/compose-assist", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  // v1.x phase 11.16 (Tier B item 14) — send the compose mode along
  // with the accept/reject so per-mode counters stay in sync. Mode
  // is optional for backwards compat; the server falls back to
  // global-only counter updates when omitted.
  async submitComposeFeedback(accepted: boolean, mode?: string): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>("/api/email/compose-assist/feedback", {
      method: "POST",
      body: JSON.stringify({ accepted, ...(mode ? { mode } : {}) }),
    });
  },
  async suggestEmailSubject(input: SubjectSuggestInput): Promise<SubjectSuggestResponse> {
    return request<SubjectSuggestResponse>("/api/email/compose-assist/subject", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // ─── Impersonation ("view as") ─────────────────────────────────────
  //
  // Every request round-trips the session cookie; the server joins the
  // signed-in user against impersonation_sessions to decide what to
  // return. These endpoints are the ONLY surface the UI uses to start
  // / stop / inspect an impersonation — the session swap flows
  // through the /api/session response naturally.

  async getImpersonationStatus(): Promise<ImpersonationStatusResponse> {
    return request<ImpersonationStatusResponse>("/api/admin/impersonate/status");
  },

  async startImpersonation(targetUserId: string): Promise<ImpersonationStartResponse> {
    return request<ImpersonationStartResponse>("/api/admin/impersonate/start", {
      method: "POST",
      body: JSON.stringify({ target_user_id: targetUserId }),
    });
  },

  async stopImpersonation(): Promise<{
    ended: boolean;
    session_id: string | null;
  }> {
    return request<{ ended: boolean; session_id: string | null }>("/api/admin/impersonate/stop", {
      method: "POST",
      body: "{}",
    });
  },

  async listImpersonationHistory(
    params: { impersonator_id?: string; target_id?: string; limit?: number } = {},
  ): Promise<{ sessions: ImpersonationHistoryRow[] }> {
    const qs = new URLSearchParams();
    if (params.impersonator_id) qs.set("impersonator_id", params.impersonator_id);
    if (params.target_id) qs.set("target_id", params.target_id);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ sessions: ImpersonationHistoryRow[] }>(
      `/api/admin/impersonate/history${suffix}`,
    );
  },

  async getImpersonationSessionDetail(sessionId: string): Promise<ImpersonationSessionDetail> {
    return request<ImpersonationSessionDetail>(
      `/api/admin/impersonate/history/${encodeURIComponent(sessionId)}`,
    );
  },

  // ─── Connections Health (admins) ──────────────────────────────────
  async getConnectionsHealth(): Promise<ConnectionsHealthDto> {
    return request<ConnectionsHealthDto>("/api/admin/connections-health");
  },

  // ─── Integration usage and cost (system_admin only) ───────────────
  // The report and its writes. A non-system-admin gets 403, which the
  // Integrations page reads as "show status only, no money columns".
  async getIntegrationCosts(month?: string): Promise<IntegrationCostReportDto> {
    const q = month ? `?month=${encodeURIComponent(month)}` : "";
    return request<IntegrationCostReportDto>(`/api/admin/integration-costs${q}`);
  },
  async integrationCostWrite<T = unknown>(
    method: "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    body?: Record<string, unknown>,
  ): Promise<{ ok: true; row: T }> {
    return request<{ ok: true; row: T }>(`/api/admin/integration-costs${path}`, {
      method,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  },

  // ─── IVANS carrier feed (Phase A — the file rail) ─────────────────
  // Metadata only. Raw file bytes live in private R2 and never cross
  // this client; filenames are carrier/mailbox codes, not client names.
  async getIvansFeedStats(): Promise<IvansFeedStatsDto> {
    return request<IvansFeedStatsDto>("/api/ivans/stats");
  },
  async listIvansFiles(params: { limit?: number; offset?: number } = {}): Promise<{
    files: IvansFileDto[];
    total: number;
  }> {
    const query = new URLSearchParams();
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.offset != null) query.set("offset", String(params.offset));
    const qs = query.toString();
    return request<{ files: IvansFileDto[]; total: number }>(
      `/api/ivans/files${qs ? `?${qs}` : ""}`,
    );
  },
  async getIvansUploaderConfig(): Promise<IvansUploaderConfigDto> {
    return request<IvansUploaderConfigDto>("/api/ivans/config");
  },
  // ByAllAccounts feed (the VM exporter for Schwab + Betterment).
  async getByAllAccountsFeedStats(): Promise<ByAllAccountsFeedStatsDto> {
    return request<ByAllAccountsFeedStatsDto>("/api/byallaccounts/stats");
  },
  async getByAllAccountsUploaderConfig(): Promise<ByAllAccountsUploaderConfigDto> {
    return request<ByAllAccountsUploaderConfigDto>("/api/byallaccounts/config");
  },
  /** Drop a ByAllAccounts positions or transactions CSV (or an accounts
   *  CSV) straight onto the feed rail. Raw text body; the server reads
   *  the header to decide which table it is. */
  async uploadByAllAccountsFile(
    csvText: string,
    filename: string,
  ): Promise<ByAllAccountsManualUploadResult> {
    return request<ByAllAccountsManualUploadResult>("/api/byallaccounts/manual-upload", {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-ByAllAccounts-Filename": filename,
      },
      body: csvText,
    });
  },
  async rotateByAllAccountsUploadToken(): Promise<{
    endpoint: string;
    token: string | null;
    token_generation: number;
  }> {
    return request("/api/byallaccounts/rotate-token", { method: "POST" });
  },
  // Rotation is an access-control change: the previous token stops
  // working the moment this returns, and the carrier feed is stopped
  // until someone pastes the new one on the VM. The caller must have
  // said so before calling.
  async rotateIvansUploadToken(): Promise<{
    endpoint: string;
    token: string | null;
    token_generation: number;
  }> {
    return request("/api/ivans/rotate-token", { method: "POST" });
  },
  async reclassifyIvansFiles(): Promise<IvansReclassifyResultDto> {
    return request<IvansReclassifyResultDto>("/api/ivans/reclassify", {
      method: "POST",
    });
  },
  async getIvansParseStats(): Promise<IvansParseStatsDto> {
    return request<IvansParseStatsDto>("/api/ivans/parse-stats");
  },
  async listIvansFailedFiles(limit = 50): Promise<{ files: IvansFileDto[]; total: number }> {
    return request<{ files: IvansFileDto[]; total: number }>(
      `/api/ivans/files?processing_status=failed&limit=${limit}`,
    );
  },
  async runIvansParseNow(): Promise<IvansParsePassSummaryDto> {
    return request<IvansParsePassSummaryDto>("/api/ivans/parse-now", {
      method: "POST",
    });
  },

  // ─── IVANS matching preview (Phase C1 — verdicts only) ────────────
  // These read and write OPINIONS about where a transaction belongs.
  // Nothing here applies anything to a policy, household, or contact.
  async getIvansMatchStats(): Promise<IvansMatchStatsDto> {
    return request<IvansMatchStatsDto>("/api/ivans/match-stats");
  },
  async listIvansMatchExamples(
    status: IvansMatchStatusDto,
    limit = 25,
  ): Promise<{ examples: IvansMatchExampleDto[]; total: number }> {
    return request<{ examples: IvansMatchExampleDto[]; total: number }>(
      `/api/ivans/match-examples?status=${encodeURIComponent(status)}&limit=${limit}`,
    );
  },
  async runIvansMatchNow(
    reevaluate = false,
    /** The ISO timestamp this whole re-evaluate sweep began. Send the
     *  SAME value on every request of one sweep — it is how the server
     *  knows which rows it has already covered and when to say stop. */
    sweep?: string,
  ): Promise<IvansMatchPassSummaryDto> {
    const params = new URLSearchParams();
    if (reevaluate) params.set("reevaluate", "1");
    if (sweep) params.set("sweep", sweep);
    const qs = params.toString();
    return request<IvansMatchPassSummaryDto>(`/api/ivans/match-now${qs ? `?${qs}` : ""}`, {
      method: "POST",
    });
  },

  // ─── IVANS match review (Insurance-scoped grading queue) ──────────
  // Grades are opinions about the matcher's opinions. Nothing here
  // changes a policy, household, or contact.
  async getIvansReviewNext(
    bucket?: IvansMatchStatusDto,
  ): Promise<{ item: IvansReviewItemDto | null }> {
    return request<{ item: IvansReviewItemDto | null }>(
      `/api/ivans-review/next${bucket ? `?bucket=${encodeURIComponent(bucket)}` : ""}`,
    );
  },
  // The undo: the last transaction this reviewer graded, with the grade
  // she gave it, so a misclick is recoverable.
  async getIvansReviewLast(): Promise<{ item: IvansGradedItemDto | null }> {
    return request<{ item: IvansGradedItemDto | null }>("/api/ivans-review/last");
  },
  async getIvansReviewProgress(): Promise<IvansReviewProgressDto> {
    return request<IvansReviewProgressDto>("/api/ivans-review/progress");
  },
  async getIvansReviewSummary(): Promise<IvansReviewSummaryDto> {
    return request<IvansReviewSummaryDto>("/api/ivans-review/summary");
  },
  async gradeIvansMatch(input: {
    ivans_transaction_id: string;
    verdict: "looks_right" | "wrong" | "cant_tell";
    correction_text?: string | null;
    note?: string | null;
  }): Promise<{ status: "recorded" | "updated"; review_id: string }> {
    return request("/api/ivans-review/grade", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // ─── Carrier activity (Insurance-scoped, read-only) ───────────────
  // The log of what the carriers actually sent. Reads only — there is
  // no write endpoint on this surface at all.
  async getCarrierActivitySummary(): Promise<CarrierActivitySummaryResponseDto> {
    return request<CarrierActivitySummaryResponseDto>("/api/ivans-activity/summary");
  },
  async getCarrierActivityDays(
    filters: CarrierActivityQuery = {},
  ): Promise<{ days: CarrierActivityDayDto[] }> {
    return request<{ days: CarrierActivityDayDto[] }>(
      `/api/ivans-activity/days${carrierActivityQuery(filters)}`,
    );
  },
  async getCarrierActivityTransactions(
    filters: CarrierActivityQuery = {},
  ): Promise<CarrierActivityPageDto> {
    return request<CarrierActivityPageDto>(
      `/api/ivans-activity/transactions${carrierActivityQuery(filters)}`,
    );
  },
};

// ─── Carrier activity types ──────────────────────────────────────

export interface CarrierActivityQuery {
  carrier?: string | null;
  cycle?: string | null;
  q?: string | null;
  needsPerson?: boolean;
  day?: string | null;
  limit?: number;
  offset?: number;
}

/** Only non-empty filters are sent, so a blank box never narrows a query. */
function carrierActivityQuery(f: CarrierActivityQuery): string {
  const params = new URLSearchParams();
  if (f.carrier) params.set("carrier", f.carrier);
  if (f.cycle) params.set("cycle", f.cycle);
  if (f.q && f.q.trim()) params.set("q", f.q.trim());
  if (f.needsPerson) params.set("needs_person", "1");
  if (f.day) params.set("day", f.day);
  if (f.limit !== undefined) params.set("limit", String(f.limit));
  if (f.offset !== undefined) params.set("offset", String(f.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export interface CarrierActivityRowDto {
  id: string;
  transaction_date: string | null;
  /** When the OS received it — the feed sends no time of day. */
  created_at: string;
  /** The Phoenix-local day it reached us — what the log groups by. */
  received_day: string;
  carrier_name: string | null;
  carrier_code: string | null;
  policy_number: string | null;
  cycle_code: string | null;
  lob_code: string | null;
  named_insured: string | null;
  effective_date: string | null;
  match_status: string | null;
  matched_household_id: string | null;
  matched_household_name: string | null;
  matched_insurance_policy_id: string | null;
  matched_service_record_id: string | null;
  /** Set when the matcher left this for a person and the team has since
   *  added the policy (the number now leads to exactly one live policy). */
  resolved_household_id: string | null;
  resolved_household_name: string | null;
}

export interface CarrierActivityPageDto {
  rows: CarrierActivityRowDto[];
  /** COUNT(*) over the same filters — never rows.length. */
  total: number;
  truncated: boolean;
}

export interface CarrierActivityDayDto {
  day: string;
  total: number;
  needs_person: number;
}

export interface CarrierFreshnessDto {
  carrier_code: string;
  carrier_name: string | null;
  /** The Phoenix-local day this carrier last sent us anything. */
  last_received_day: string | null;
  transactions_last_24h: number;
  transactions_last_7d: number;
  days_since: number | null;
  quiet: boolean;
}

export interface CarrierActivitySummaryResponseDto {
  summary: {
    latest_day: string | null;
    latest_day_total: number;
    last_7d_total: number;
    needs_person_total: number;
    carriers_reporting: number;
    carriers_quiet: number;
  };
  carriers: CarrierFreshnessDto[];
}

// ─── IVANS match review types ────────────────────────────────────

export interface IvansReviewItemDto {
  ivans_transaction_id: string;
  file_content_sha256: string;
  txn_index: number;
  policy_number: string | null;
  named_insured: string | null;
  /** JSON array of the OTHER insureds on a jointly-held policy. */
  named_insured_additional: string | null;
  carrier_name: string | null;
  cycle_code: string | null;
  lob_code: string | null;
  /** The date the carrier stamped on THIS transaction — what lines a
   *  card up against the IVANS download list. Not the term, and not when
   *  we received the file. */
  transaction_date: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  insured_address_line1: string | null;
  insured_address_line2: string | null;
  insured_city: string | null;
  insured_state: string | null;
  insured_zip: string | null;
  premium_cents: number | null;
  match_status: string | null;
  match_confidence: string | null;
  match_method: string | null;
  match_evidence: string | null;
  matched_household_id: string | null;
  matched_household_name: string | null;
  matched_insurance_policy_id: string | null;
  matched_policy_number: string | null;
  matched_policy_carrier_name: string | null;
}

/** A transaction this reviewer already graded, plus her grade — what
 *  the "go back" button re-opens. */
export interface IvansGradedItemDto extends IvansReviewItemDto {
  my_verdict: "looks_right" | "wrong" | "cant_tell";
  my_correction_text: string | null;
  my_note: string | null;
}

export interface IvansReviewProgressDto {
  graded_by_me: number;
  gradable_total: number;
  remaining_for_me: number;
}

export interface IvansReviewSummaryDto {
  total_graded: number;
  by_bucket: {
    match_status: string;
    graded: number;
    looks_right: number;
    wrong: number;
    cant_tell: number;
    accuracy_percent: number | null;
  }[];
  wrong_items: {
    snapshot_policy_number: string | null;
    snapshot_named_insured: string | null;
    snapshot_carrier_name: string | null;
    snapshot_cycle_code: string | null;
    graded_match_status: string | null;
    graded_match_method: string | null;
    correction_text: string | null;
    note: string | null;
    reviewed_at: string;
    reviewer_name: string | null;
  }[];
  /** COUNT(*) of every 'wrong' — wrong_items above is capped, this is
   *  not, and the page must show this number rather than a list length. */
  wrong_total: number;
  wrong_items_truncated: boolean;
  cant_tell_count: number;
  reviewers: { user_id: string; name: string | null; graded: number }[];
  latest_reviewed_at: string | null;
  /** Grades whose transaction no longer matches what was graded. */
  drifted_grades: number;
}

// ─── IVANS matching preview types ────────────────────────────────

export type IvansMatchStatusDto =
  | "matched_policy"
  | "suggested_household"
  | "ambiguous"
  | "unmatched";

export interface IvansMatchStatsDto {
  policy_transactions: number;
  not_evaluated: number;
  matched_policy: number;
  suggested_household: number;
  ambiguous: number;
  unmatched: number;
  matched_high: number;
  matched_medium: number;
  /** Null until something has been evaluated — a rate over an empty
   *  denominator is a lie, not a zero. */
  match_rate_percent: number | null;
  by_method: Record<string, number>;
  latest_matched_at: string | null;
}

export interface IvansMatchExampleDto {
  id: string;
  policy_number: string | null;
  carrier_name: string | null;
  cycle_code: string | null;
  named_insured: string | null;
  insured_city: string | null;
  insured_state: string | null;
  effective_date: string | null;
  match_status: string | null;
  match_confidence: string | null;
  match_method: string | null;
  match_evidence: string | null;
  matched_household_id: string | null;
  matched_insurance_policy_id: string | null;
  matched_household_name: string | null;
}

export interface IvansMatchPassSummaryDto {
  evaluated: number;
  matched_policy: number;
  suggested_household: number;
  ambiguous: number;
  unmatched: number;
  more_pending: boolean;
  /** Left to judge in this sweep after this batch; null when no sweep. */
  remaining: number | null;
}

// ─── Pulse Detail types ───────────────────────────────────────────────

export type PulseMetricSlug =
  | "revenue_ttm"
  | "households_served"
  | "cross_sell_rate"
  | "ideal_client_mix"
  | "net_op_profit"
  | "cash_on_hand";

export type PulseWindow = "12m" | "24m" | "36m";

export type PulseMetricKind = "currency" | "count" | "percent";

export interface PulseMonthlyBucket {
  month: string;
  value: number;
  per_company: {
    mortgage: number;
    insurance: number;
    planning: number;
    realty: number;
    financial: number;
  };
}

// Wave D.5 (April 2026): MoM/YoY/YTD chip group below the hero.
// `absolute` is the delta in the metric's native units (count, %, $).
// `percent` is the percent change vs prior, in whole-percent units
// (e.g., 8.9 = +8.9%). Null when prior period was 0 or the helper
// had insufficient history for that comparison — UI renders "—".
export interface PulseAggregate {
  absolute: number;
  percent: number | null;
}

export interface PulseDetailAggregates {
  mom: PulseAggregate | null;
  yoy: PulseAggregate | null;
  ytd: PulseAggregate | null;
}

// Wave F (April 2026): per-user + per-role view preferences.
export const KNOWN_SURFACE_KEYS = [
  "household_detail",
  "contact_detail",
  "household_list",
  "contact_list",
  "dashboard_cockpit",
  "pulse_detail",
  "opportunity_list",
  "transaction_list",
  "rhythm_list",
  // These three were added to shared/db/view-preferences.ts's list
  // (the server-authoritative one the API validates against) without
  // a matching update here, so their consumers passed the surface key
  // as a raw string literal rather than importing SurfaceKey. Backfilled
  // 2026-08-29 while fixing the S11 additions below, so this list is a
  // true mirror of the backend's again — a divergence a test now
  // guards against (tests/web/use-view-preference.test.ts).
  "cleanup-coachmarks",
  "front_desk_board",
  "user_settings",
  // S11 (2026-08-29) — record-view customization for the four
  // Service-Record-family detail pages. Keep in sync with
  // shared/db/view-preferences.ts's KNOWN_SURFACE_KEYS (the API-side
  // list this one duplicates for typed frontend callers).
  "opportunity_detail",
  "transaction_detail",
  "rhythm_detail",
  "service_record_detail",
] as const;
export type SurfaceKey = (typeof KNOWN_SURFACE_KEYS)[number];

export interface ResolvedViewPreference {
  surface_key: string;
  source: "user" | "role" | "baseline";
  /** Null when no user/role row exists; UI applies its baseline. */
  preferences_json: string | null;
  role_slug: string | null;
}

export interface UserViewPreferenceRow {
  id: string;
  user_id: string;
  surface_key: string;
  preferences_json: string;
  created_at: string;
  updated_at: string;
}

export interface RoleViewDefaultRow {
  id: string;
  role_slug: string;
  surface_key: string;
  preferences_json: string;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// Wave E (April 2026): "What Moved The Number" — metric contributors.
export interface MetricContributor {
  id: string;
  metric_slug: string;
  contribution_month: string;
  title: string;
  description: string | null;
  magnitude: "major" | "moderate" | "minor";
  direction: "positive" | "negative" | "neutral";
  related_entity_type: string | null;
  related_entity_id: string | null;
  source: "manual" | "claude_suggested" | "claude_accepted";
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface MetricContributorCandidate {
  title: string;
  description: string;
  magnitude: "major" | "moderate" | "minor";
  direction: "positive" | "negative" | "neutral";
}

// Wave E (April 2026): cross-sell opportunity reasoning.
export interface CrossSellReasoningResponse {
  reasoning: string;
  signals: string[];
  generated_at: string;
  source: "claude" | "rule_based";
}

// Wave E (April 2026): pulse auto-narrative + AI banner.
export interface PulseNarrativeResponse {
  paragraph: string;
  banner_text: string;
  generated_at: string;
  source: "claude" | "fallback";
}

// Wave C-2.3: per-company drilldown on a household.
// Mirrors HouseholdCompanyDetailResponse in
// workers/api/src/routes/household-company.ts.
export interface HouseholdCompanyDetailResponse {
  household_id: string;
  company: {
    id: string;
    slug: string;
    display_name: string;
  };
  relationship: {
    relationship_state: string;
    fit_assessment: string;
    fit_assessed_at: string | null;
    fit_assessed_by_user_id: string | null;
    current_advisor_user_id: string | null;
    current_advisor_name: string | null;
    client_since_this_company: string | null;
    /** 1 = derived FLOOR — render as "at least <date>" (migration 0200). */
    client_since_is_floor: number;
    /** True when this pair matches the AC-migration false-active
     *  artifact (recurring company, active_client, zero service
     *  records) — the "Not a client here" fix applies. */
    false_active_eligible: boolean;
    /** Where the fix lands: prospect (open opportunity) or
     *  engaged_lost. null when not eligible. */
    false_active_target: "prospect" | "engaged_lost" | null;
  } | null;
  service_records: Array<{
    id: string;
    service_type: string;
    service_family: string;
    display_label: string | null;
    status: string;
    effective_date: string | null;
    termination_date: string | null;
    product_metric: number | null;
    product_metric_unit: string | null;
    estimated_revenue_year_1: number | null;
  }>;
  open_opportunities: HouseholdOpportunityRow[];
  open_transactions: HouseholdTransactionRow[];
  active_rhythms: RhythmInstance[];
}

// Wave C-2.3: contact roles (per-(contact, company) role
// assignments — borrower / trustee / beneficiary / etc.)
export const CONTACT_ROLES = [
  "borrower",
  "co_borrower",
  "primary_insured",
  "co_insured",
  "beneficiary",
  "trustee",
  "successor_trustee",
  "executor",
  "guarantor",
  "referral_partner",
  "account_holder",
  "joint_holder",
  "poa",
  "other",
] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export interface ContactRoleRow {
  id: string;
  contact_id: string;
  company_id: string | null;
  company_slug: string | null;
  company_display_name: string | null;
  role: ContactRole;
  notes: string | null;
  effective_date: string | null;
  end_date: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// Wave D.2: FY targets per metric.
export interface DashboardTarget {
  id: string;
  metric_slug: string;
  fiscal_year: number;
  target_value: number;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// Wave D.3: dashboard event annotations.
export const DASHBOARD_EVENT_CATEGORIES = [
  "hire",
  "departure",
  "system_change",
  "marketing_campaign",
  "regulatory",
  "product_launch",
  "other",
] as const;
export type DashboardEventCategory = (typeof DASHBOARD_EVENT_CATEGORIES)[number];

export interface DashboardEvent {
  id: string;
  /** YYYY-MM-DD. */
  event_date: string;
  /** Null = system-wide pin (renders on every metric's chart). */
  metric_slug: string | null;
  title: string;
  description: string | null;
  category: DashboardEventCategory;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PulseDetailResponse {
  metric: PulseMetricSlug;
  label: string;
  kind: PulseMetricKind;
  window: PulseWindow;
  time_series: PulseMonthlyBucket[];
  current_value: number;
  prior_value: number;
  trend: {
    absolute_delta: number;
    percent_change: number | null;
    direction: "up" | "down" | "flat" | "insufficient_history";
  };
  source: "client_engine" | "quickbooks" | "quickbooks_placeholder";
  source_timestamp: string;
  formula: string;
  is_placeholder: boolean;
  has_per_company_breakdown: boolean;
  /** Wave D.5: MoM/YoY/YTD chip data. Each aggregate is null when
   *  there's insufficient history or the prior period was zero. */
  aggregates: PulseDetailAggregates;
}

// ─── Watchlist types ──────────────────────────────────────────────────

export type AtRiskReason = "stale_60d_plus" | "stale_jerry";
export type RiskBand = "healthy" | "watch" | "elevated" | "high";

export interface AtRiskHousehold {
  household_id: string;
  display_name: string;
  primary_advisor_name: string | null;
  primary_advisor_id: string | null;
  companies_served: string[];
  last_updated_at: string;
  days_since_contact: number;
  fit_assessment: string | null;
  reason: AtRiskReason;
  // At-risk v2: 0–100 score, band, ranked reasons (read from the
  // nightly batch when populated).
  risk_score: number;
  risk_band: RiskBand;
  risk_reasons: string[];
  // Score change vs ~14 days ago; null until snapshot history exists.
  score_delta_14d: number | null;
}

// ─── Relationship Engagement zones (two-axis complement) ─────────────
// The tier is written inline (not a shared EngagementTier alias) so this
// composes with the engagement-card PR (#1220) without a duplicate-name
// clash when both land in the merge queue.

export interface EngagementZoneHousehold {
  household_id: string;
  display_name: string;
  primary_advisor_name: string | null;
  primary_advisor_id: string | null;
  companies_served: string[];
  engagement_score: number;
  engagement_tier: "thriving" | "steady" | "slipping" | "disengaged";
  service_effort: number;
  responsiveness: number;
  expected_touch_days: number;
  days_since_outbound: number | null;
  days_since_inbound: number | null;
  top_reason: string | null;
  // Blended-score change vs ~14 days ago; for engagement a RISE is good.
  score_delta_14d: number | null;
}

export interface EngagementZoneSummary {
  we_owe: number;
  going_quiet: number;
  /** True once the nightly engagement batch has produced any row. */
  scored: boolean;
}

// ─── Relationship Health (household at-risk detail) ──────────────────

export interface AtRiskContribution {
  signal_id: string;
  /** "note" (2026-09-22): a fact worth 0 points, e.g. refinanced away. */
  kind: "risk" | "protective" | "action" | "bonus" | "note";
  points: number;
  reason: string;
}

export interface AtRiskActiveReduction {
  signal_id: string;
  points: number;
  reason: string;
  expires_at: string | null;
}

export interface AtRiskTrendPoint {
  date: string;
  score: number;
}

export interface AtRiskCalibrationReport {
  generated_at: string;
  total_scored: number;
  band_counts: Record<"healthy" | "watch" | "elevated" | "high", number>;
  histogram: Array<{ bucket: string; count: number }>;
  top_signals: Array<{
    signal_id: string;
    reason: string;
    kind: string;
    fires: number;
    total_points: number;
  }>;
  band_moves: { up: number; down: number; flat: number; compared: number };
  current_bands: { high: number; elevated: number; watch: number };
  /** The Watchlist Rules behind these scores (2026-09-22). */
  rules: { changed_7d: number; using_defaults: number };
  /** County standing (2026-09-22): mortgage households by county answer. */
  county: {
    mortgage_households: number;
    by_standing: Record<string, number>;
    refinanced_away: { households: number; points_off: number };
    sold: { households: number; points_off: number };
    quiet_off_households: number;
    left_now_healthy: number;
    left_still_listed: number;
    /** Why the refinance-out-of-insurance line stayed quiet, by reason. */
    equity_quiet: Record<string, number>;
  };
  proposed_bands: { high: number; elevated: number; watch: number } | null;
  rates_feed: {
    healthy: boolean;
    latest: { snapshot_date: string; r30: number } | null;
    recent_days: number;
  };
  activity_coverage: {
    households_with_activity: number;
    total_households: number;
  };
}

/** One Watchlist Rule as the rules screen shows it. `id` is null only
 *  when the rule has no saved row yet (its seed has not run). */
export interface WatchlistRuleRec {
  key: string;
  group: string;
  section: string;
  label: string;
  help: string | null;
  kind: "points" | "score" | "days" | "count" | "percent" | "rate_points" | "multiplier" | "years" | "date";
  min: number;
  max: number;
  default_value: number;
  signal_status: string | null;
  /** The signal a points / credit-days rule belongs to. */
  signal_id: string | null;
  /** "risk" | "protective" | "action" for a signal's rules. */
  signal_kind: string | null;
  /** The signal line this number is drawn on; null = its own line. */
  line: string | null;
  /** The word before the number on that line ("from", "within"). */
  cell: string | null;
  id: string | null;
  value: number;
  is_locked: boolean;
  locked_reason: string | null;
  version_number: number | null;
  updated_by_name: string | null;
  updated_at: string | null;
  created_at: string | null;
  changed: boolean;
}

export interface WatchlistRuleHistoryRec {
  id: string;
  rule_key: string;
  previous_numeric: number | null;
  new_numeric: number | null;
  reason: string | null;
  changed_by_user_id: string | null;
  changed_by_name: string | null;
  changed_at: string;
  change_kind: string;
}

export interface HouseholdSignal {
  id: string;
  household_id: string;
  signal_type: "life_event" | "concern";
  subtype: string | null;
  details: string | null;
  noted_by_user_id: string | null;
  noted_at: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution_note: string | null;
  // The signal ledger (Marketing Hub PR 1.4): what kind of knowledge, how
  // sure, who wrote it, what it may be used for, when it fades.
  epistemic_status: "fact" | "inference" | "prediction" | "restricted";
  confidence: number;
  source: string;
  source_ref: string | null;
  /** JSON array of use tags; see shared/signals/data-rights.ts. */
  permitted_uses: string;
  decay_at: string | null;
}

export type HouseholdAtRiskResponse =
  | { scored: false }
  | {
      scored: true;
      score: number;
      band: RiskBand;
      reasons: string[];
      contributions: AtRiskContribution[];
      active_reductions: AtRiskActiveReduction[];
      days_since_contact: number | null;
      computed_at: string;
      trend: AtRiskTrendPoint[];
      property_valuation: {
        estimated_value_cents: number;
        value_low_cents: number | null;
        value_high_cents: number | null;
        valued_at: string;
        source: "rentcast";
      } | null;
      /** What the county record says about the household's newest
       *  Stewardship loan, read live. Null when there is nothing to say. */
      county: {
        standing: "still_ours" | "refinanced_away" | "sold" | "unclear" | "not_our_loan";
        line: string;
        pulled_at: string;
      } | null;
      /** The home value the Watchlist trusts and the ones it set aside
       *  (ATTOM first, then RentCast). Null when none is on file. */
      home_value: {
        used: HomeValueSource | null;
        set_aside: HomeValueSource[];
        uncertain: string | null;
        /** The FHFA Phoenix price index applied to the original value: a
         *  check, never the value. */
        index_check: { value: number; from: string; to: string } | null;
      } | null;
    };

export interface HomeValueSource {
  source: "ATTOM" | "RentCast";
  value: number;
  low: number | null;
  high: number | null;
  pulled: string;
}

export type EngagementTier = "thriving" | "steady" | "slipping" | "disengaged";

export interface EngagementContribution {
  axis: "service_effort" | "responsiveness";
  label: string;
  points: number;
}

export interface EngagementTrendPoint {
  date: string;
  score: number;
}

export type HouseholdEngagementResponse =
  | { scored: false }
  | {
      scored: true;
      engagement_score: number;
      engagement_tier: EngagementTier;
      service_effort: number;
      responsiveness: number;
      reasons: string[];
      contributions: EngagementContribution[];
      expected_touch_days: number;
      days_since_outbound: number | null;
      days_since_inbound: number | null;
      computed_at: string;
      trend: EngagementTrendPoint[];
    };

export interface CrossSellOpportunity {
  household_id: string;
  display_name: string;
  primary_advisor_name: string | null;
  current_company: string;
  current_company_display: string;
  missing_companies: string[];
  has_ideal_fit: boolean;
  last_updated_at: string;
}

export interface StuckDeal {
  id: string;
  kind: "opportunity" | "transaction";
  display_name: string;
  household_id: string;
  household_display_name: string;
  company_slug: string;
  stage: string;
  days_in_stage: number;
  value_dollars: number;
  advisor_name: string | null;
  severity: "stuck" | "approaching_stuck";
}

export interface TeamCapacityAdvisor {
  user_id: string;
  name: string;
  role_slug: string | null;
  active_opportunities: number;
  active_transactions: number;
  active_rhythms: number;
  total_active: number;
  status: "available" | "at_capacity" | "over_capacity";
}

// Summary for the 4 dashboard "Needs Attention" tiles (real totals +
// breakdown + trend where history exists). Mirrors WatchlistSummary in
// shared/db/watchlist.ts.
export interface WatchlistSummary {
  at_risk: {
    total: number;
    high: number;
    elevated: number;
    watch: number;
    trend_delta_14d: number | null;
  };
  cross_sell: {
    total: number;
    ideal_fit: number;
    trend_delta_month: number | null;
  };
  stalled: {
    total: number;
    stuck: number;
    approaching: number;
    value_at_risk: number;
    trend_delta: number | null;
  };
  capacity: {
    over_capacity: number;
    at_capacity: number;
    total_advisors: number;
    trend_delta: number | null;
  };
  /** When the firm-wide figures were worked out (2026-09-15). The tiles
   *  display it — these four numbers come from a shared snapshot
   *  refreshed every 15 minutes, not from this request. */
  computed_at?: string | null;
}

// ─── List-view item shapes ────────────────────────────────────────────

export interface ListResponse<T> {
  total: number;
  items: T[];
}

export interface OpportunityListItem {
  id: string;
  household_id: string;
  household_display_name: string;
  opportunity_type: string;
  company_slug: string;
  stage: string;
  owner_user_id: string | null;
  owner_name: string | null;
  value: number | null;
  days_in_stage: number;
  last_updated_at: string;
  expected_close_date: string | null;
  fit_assessment: string | null;
  // The opportunity's primary attached person (0252 "whose record is
  // this"), shown only when the household has more than one active
  // person — same disambiguation rule as the Processing list. null
  // otherwise.
  record_person_name: string | null;
  // Realty board card fields (Nate, 2026-09-22). null on non-Realty rows.
  // Mirrors shared/db/list-queries.ts OpportunityListRow.
  primary_person_name: string | null;
  property_address: string | null;
  next_task: BoardNextTask | null;
  days_since_touch: number | null;
  last_touch_kind: string | null;
}

/** One task line on a Realty board card. Mirrors
 *  shared/realty/board-cards.ts BoardNextTask. */
export interface BoardNextTask {
  title: string;
  due_date: string | null;
  overdue: boolean;
}

// Processing redesign (PR1) — blocker chip mirror of
// shared/transactions/blockers.ts BlockerChip.
export type TransactionBlockerKind =
  | "stalled"
  | "blocked"
  | "closing_soon"
  | "needs_close_date"
  | "on_track";
export interface TransactionBlockerChip {
  kind: TransactionBlockerKind;
  label: string;
}

export interface TransactionExceptionCounts {
  stalled: number;
  closing_this_week: number;
  blocked: number;
  needs_close_date: number;
}

// Processing redesign (fix-PR10, M2) — true per-stage open-transaction
// totals for the Board view's column headers (vs the loaded page count).
export interface TransactionStageCount {
  stage: string;
  count: number;
}

export type PipelineWorkflow = "opportunity" | "transaction" | "ongoing_care";

export interface PipelineStage {
  slug: string;
  label: string;
  typical_days: number;
  sort_order: number;
  /** Ongoing Care only: a cycle here is done for the year (shared
   *  StageDef.counts_as_done). Absent = not done. */
  counts_as_done?: boolean;
}

// A pipeline ("board") resolved by GET /api/pipelines: config default
// overlaid with any DB edit, plus custom DB-only pipelines.
export interface ResolvedPipeline {
  company_slug: string;
  workflow: PipelineWorkflow;
  type: string;
  /** Human label (override display_name, else humanized type). */
  label: string;
  /** "custom" = an edited override exists; "config" = hardcoded default. */
  source: "custom" | "config";
  version_number: number | null;
  updated_at: string | null;
  stages: PipelineStage[];
  /**
   * Every concrete opportunity_type / transaction_type this ONE board
   * speaks for. Several types share a board by design (Realty's five
   * opportunity types, Estate Plan's two Processing spellings…), so the
   * boards resolve a card of any covered type to these stages, and the
   * Board picker filters on all of them at once.
   */
  covered_types: string[];
  /** Server-computed: may the signed-in user edit this pipeline? */
  can_edit: boolean;
}

export interface TransactionListItem {
  id: string;
  household_id: string;
  household_display_name: string;
  transaction_type: string;
  company_slug: string;
  stage: string;
  /** 1 = an in-house carrier switch born from a renewal cycle, not true
   *  new business — the "Renewal change" chip (Brandon, 2026-08-21). */
  is_renewal_change: number;
  owner_user_id: string | null;
  owner_name: string | null;
  value: number | null;
  target_close_date: string | null;
  last_updated_at: string;
  // Realty buy/sell-side annotation (Nate's feedback). null for non-realty.
  realty_side: RealtySide | null;
  // Realty property address ("123 E Main St, Gilbert"); null for
  // non-realty or when no address is on file.
  property_address: string | null;
  // The linked service_record's service_type ("purchase_mortgage",
  // "private_money_loan", "home_policy", …) — feeds transactionDisplayNoun
  // for the row's actual product name. null when no service record is
  // linked.
  service_type: string | null;
  // The linked service record's primary attached person, shown only when
  // the household has more than one active person — disambiguates two
  // records in the same household (Ryan Delviken, 2026-08-11: a couple
  // who each have their own Life Case). null otherwise.
  record_person_name: string | null;
  // Medicare plan type ("pdp" | "ma" | "medigap_g" | …) for the card's
  // short plan-type label (Ryan, 2026-09-03 — two Medicare Case cards for
  // the same household were indistinguishable). null for non-Planning
  // transactions or anything not yet captured; feed it to
  // medicareTierLabel() for display.
  plan_tier: string | null;
  // Pace / blocker surfacing (PR1).
  days_in_stage: number;
  typical_days: number;
  is_stalled: boolean;
  blocker_chips: TransactionBlockerChip[];
  // Realty board card fields (Nate, 2026-09-22). null on non-Realty rows.
  // Mirrors shared/db/list-queries.ts TransactionListRow.
  primary_person_name: string | null;
  days_on_market: number | null;
  next_task: BoardNextTask | null;
}

// v1.x phase 11.16 — opportunity / transaction detail-page payloads.
// Backend assembles these in one round-trip; the page renders without
// further fetches.
export interface StageDef {
  slug: string;
  label: string;
  typical_days: number;
  sort_order: number;
}
export interface StageTemplate {
  stages: StageDef[];
  total: number;
}

// Household member summary (mirrors shared/db/detail-queries.ts).
export interface HouseholdMemberSummary {
  id: string;
  name: string;
  household_role: string | null;
  email: string | null;
  phone: string | null;
  birthday: string | null;
  ssn_last_four: string | null;
  has_ssn_on_file: boolean;
}

/** One row of the "Already closed? Link it" picker. Mirrors
 *  LinkableExistingRecord in shared/db/opportunity-link-existing.ts. */
export interface LinkableExistingRecordDto {
  kind: "transaction" | "service_record";
  id: string;
  label: string;
  product_label: string | null;
  status: string | null;
  started_at: string | null;
  service_record_id: string | null;
  opened_after_card: boolean;
  claimed_by_opportunity_id: string | null;
}

export interface OpportunityDetail {
  id: string;
  household_id: string;
  household_display_name: string;
  /** Set on an employer's group sale: it belongs to the business. */
  business_entity_id: string | null;
  // Every live member of the household, not just the primary contact
  // household_display_name is derived from (Grant's 2026-07-24 feedback).
  household_members: HouseholdMemberSummary[];
  // What Matters Most (Category 6 judgment field, household-scoped) —
  // surfaced above the fold on the record page (S12). null when never
  // captured.
  what_matters_most: string | null;
  what_matters_most_updated_at: string | null;
  what_matters_most_updated_by_name: string | null;
  what_matters_most_unknown_reason: string | null;
  what_matters_most_unknown_at: string | null;
  // Whose opportunity is this (0252) — the record's people, primary
  // first, for the in-place edit control.
  people: ServiceRecordPerson[];
  // The primary person's name for the title chip — only in multi-member
  // households. null otherwise.
  record_person_name: string | null;
  company_id: string;
  company_slug: string;
  company_display_name: string;
  opportunity_type: string;
  // Realty buy/sell-side annotation (Nate's feedback). null for non-realty.
  realty_side: RealtySide | null;
  stage: string;
  outcome: "won" | "lost" | "abandoned" | null;
  outcome_reason: string | null;
  /** Won by "Already closed? Link it": nothing was created, and "Undo
   *  this win" only unlinks. Optional so an older API build reads false. */
  won_by_link?: boolean;
  /** Where this card's documents went when it was won (Documents tab
   *  PR 5): the Processing record and how many are there. Null when
   *  nothing was carried. */
  documents_carried_to?: { transaction_id: string; count: number } | null;
  owner_user_id: string | null;
  owner_name: string | null;
  product_metric: number | null;
  product_metric_unit: string | null;
  estimated_revenue_year_1: number | null;
  estimated_lifetime_revenue: number | null;
  expected_close_date: string | null;
  // Mortgage prequal letter expiration (null for non-prequal opps).
  prequal_expires_at: string | null;
  /** The subject property — WHICH HOUSE this opportunity is about.
   *  Present only on realty listing opportunities; null everywhere else
   *  (including buyer consultations, where there is no house yet). */
  subject_property: {
    line_1: string | null;
    line_2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    square_feet: number | null;
    display: string | null;
    /** Addresses already on this household, offered as one-tap choices on
     *  the form. Never pre-filled (a listing is often the rental). */
    address_options: SubjectPropertyAddressOption[];
  } | null;
  /** Income, subsidy and eligibility answers this marketplace quote is run
   *  on. Non-null only on Planning health/Medicare opportunities — every
   *  other kind gets null and renders nothing. */
  health_quote: HealthQuoteFacts | null;
  opened_at: string;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  days_open: number;
  days_in_stage: number;
  // When the opportunity entered its current stage — the timestamp
  // days_in_stage measures from (migration 0206). Powers the "correct
  // time-in-stage" control's default date.
  stage_entered_at: string;
  /** This opportunity's company's newest conversation (relevant touches,
   *  2026-09-22) — never another company's. */
  last_contact_at: string | null;
  last_contact_kind: string | null;
  relevant_touch: RelevantTouchLike;
  fit_assessment: string | null;
  stage_template: StageTemplate;
  current_stage_index: number;
  is_stalled: boolean;
  stalled_typical_days: number;
  stalled_reason: string | null;
  // Financial Advisor Workspace v2, O1 — "The opportunity" card's
  // plain-words advisor summary (Category 6 judgment) and its "Action
  // items" list (open tasks linked to this opportunity). Present on
  // every opportunity; the Financial lens decides whether the page
  // renders them (see web/src/lib/financial-lens.ts).
  advisor_summary: string | null;
  // Set once the record is archived; the page shows a notice and hides actions.
  archived_at: string | null;
  archived_reason: string | null;
  tasks: RailManualTask[];
  /** SERVED letter strip entries — Realty opportunities only; empty otherwise. */
  household_company_served: HouseholdCompanyServedEntry[];
}

// Where a transaction came from (mirrors shared/db/detail-queries.ts).
export type TransactionOrigin =
  | { kind: "opportunity"; opportunity_id: string; opportunity_type: string }
  | { kind: "ac_import" };

/**
 * What PATCH /api/transactions/:id/realty-fields answers with — the
 * record's WHOLE contract state, not just the fields the request moved.
 *
 * Deliberate: Nate's contract card re-renders from this one answer, and
 * a reply carrying only what changed would leave every untouched field
 * showing whatever the client happened to have. Same reason the
 * Processing workspace's mutations return the entire workspace.
 */
export interface RealtyContractFields {
  ok: true;
  concessions_amount: number | null;
  program_id: string | null;
  earnest_money: number | null;
  loan_type: string | null;
  loan_amount: number | null;
  // The buyer's total down payment (migration 2026-08-29, Nate's ask) —
  // includes earnest money, per his contract's own line items. See
  // shared/realty/contract-terms.ts for how this and loan_amount stay
  // in sync with contract price + earnest money.
  down_payment: number | null;
  possession: string | null;
  repair_credit: number | null;
  /** The other side's name as it prints on the contract — a courtesy
   *  field, merged onto the form but never a signer (Grant, 2026-09-03). */
  other_party_name: string | null;
  cda_sent_to_title_at: string | null;
  contract_dated: string | null;
  due_diligence_ends_at: string | null;
  due_diligence_days: number | null;
}

export interface TransactionDetail {
  id: string;
  household_id: string;
  household_display_name: string;
  // The person who owns compliance for this record's company, when the
  // OS can name exactly one (Baker, 2026-08-12). The hero uses it for
  // "next move owned by" at a compliance stage, where the record's own
  // owner — the loan officer — is the wrong answer. Null when there is
  // no compliance user for the company or more than one, and the hero
  // then says "Compliance" rather than guessing a name.
  compliance_owner_name: string | null;
  // Every live member of the household, not just the primary contact
  // household_display_name is derived from (Grant's 2026-07-24 feedback).
  household_members: HouseholdMemberSummary[];
  // What Matters Most (Category 6 judgment field, household-scoped) —
  // surfaced above the fold on the record page (S12). null when never
  // captured.
  what_matters_most: string | null;
  what_matters_most_updated_at: string | null;
  what_matters_most_updated_by_name: string | null;
  what_matters_most_unknown_reason: string | null;
  what_matters_most_unknown_at: string | null;
  // The linked service record's primary attached person, shown only when
  // the household has more than one member — disambiguates two records
  // in the same household (Ryan Delviken, 2026-08-11: a couple who each
  // have their own Life Case). null otherwise.
  record_person_name: string | null;
  // Whose record is this (0252) — the full people list for the in-place
  // edit control, primary first. Sourced from the linked service record
  // when linked ('service_record'; edits go through the SR people
  // endpoints), else the transaction's own rows ('transaction').
  people: ServiceRecordPerson[];
  people_source_kind: "service_record" | "transaction";
  // The linked service_record's family ('policy', 'loan', 'case', …) —
  // feeds personRoleNoun for the people card. null when unlinked.
  service_family: string | null;
  company_id: string;
  company_slug: string;
  company_display_name: string;
  transaction_type: string;
  // The linked service_record's service_type ("purchase_mortgage",
  // "private_money_loan", "home_policy", …) — feeds transactionDisplayNoun
  // for the record's actual product name. null when no service record is
  // linked.
  service_type: string | null;
  /**
   * What this record was WON as (the Mark Won picker's case/policy-type
   * answer). `service_type` above already coalesces to this when no
   * product record exists yet, so use it only to tell the two apart in
   * COPY — "this is what you won it as" vs "this is the policy on file".
   */
  intended_service_type: string | null;
  // Realty buy/sell-side annotation (Nate's feedback). null for non-realty.
  realty_side: RealtySide | null;
  // Realty property address ("123 E Main St, Gilbert"); null for
  // non-realty or when no address is on file.
  property_address: string | null;
  // Realty property-record state for the detail hero (Nate 2026-07-06).
  // null for non-realty; when present, `linked` says whether a property
  // record is attached (unlinked → show "Link property record"), and
  // `address` holds the editable parts once linked.
  realty_property: {
    linked: boolean;
    service_family: string | null;
    address: {
      line_1: string | null;
      line_2: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
    } | null;
    // realty_listings.id / realty_purchases.id — what GET/PATCH
    // /api/realty-records/:id take. Lets the page fetch + embed the
    // same editable sale-price/commission card the service-record page
    // uses. null when unlinked.
    realty_record_id: string | null;
  } | null;
  stage: string;
  outcome: "completed" | "fell_through" | "cancelled" | null;
  // M1 — stored close reason (mirrors OpportunityDetail.outcome_reason).
  // For fell_through it's "reason_code: reason_text" (or just one); the
  // closed-summary card resolves the code to a readable label.
  outcome_reason: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  product_metric: number | null;
  product_metric_unit: string | null;
  estimated_revenue_year_1: number | null;
  estimated_lifetime_revenue: number | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
  // Realty due-diligence period (migration 0164) — end date and/or length
  // in days. Realty-only in practice; null elsewhere.
  due_diligence_ends_at: string | null;
  due_diligence_days: number | null;
  // Realty tracked fields (migration 0205, Nate's N27 / N32). Concessions
  // dollar amount + Program ID. Realty-only in practice; null elsewhere.
  concessions_amount: number | null;
  program_id: string | null;
  // The rest of the contract's terms (2026-08-25), for Nate's contract
  // card. Same realty-only-in-practice shape as the two above.
  earnest_money: number | null;
  loan_type: string | null;
  loan_amount: number | null;
  down_payment: number | null;
  possession: string | null;
  repair_credit: number | null;
  /** Percentage-of-price pins (2026-09-22): set when the term was typed
   *  as a percentage. Optional so older fixtures stay valid. */
  earnest_money_pct?: number | null;
  down_payment_pct?: number | null;
  concessions_pct?: number | null;
  repair_credit_pct?: number | null;
  /** Commission credit at closing (2026-10-27): dollars, and 'Buyer' or
   *  'Seller' (null = none). Optional so older fixtures stay valid. */
  commission_credit_amount?: number | null;
  commission_credit_to?: string | null;
  /** The other side's name as it prints on the contract — a courtesy
   *  field, merged onto the form but never a signer (Grant, 2026-09-03). */
  other_party_name: string | null;
  cda_sent_to_title_at: string | null;
  /** The day the BUYER signed the purchase contract (Grant, 2026-10-14;
   *  AAR line 452). Not acceptance — that is the property record's
   *  under_contract_at. Optional so older fixtures stay valid. */
  contract_dated?: string | null;
  started_at: string;
  finalized_at: string | null;
  finalized_by_user_id: string | null;
  finalized_by_name: string | null;
  created_at: string;
  updated_at: string;
  days_open: number;
  days_in_stage: number;
  // When the transaction entered its current stage — the timestamp
  // days_in_stage measures from (migration 0206). Powers the "correct
  // time-in-stage" control's default date.
  stage_entered_at: string;
  // Realty per-stage clocks (Nate's N34). Derived from the realty
  // milestone dates, not from days_open/days_in_stage:
  //   * days_listed         = "days on market" (listed → close/now); null
  //     for buy-side or no list date.
  //   * days_under_contract = under_contract_at → close/now; null until
  //     under contract.
  // Both null for non-realty and for realty deals with no milestones.
  days_listed: number | null;
  days_under_contract: number | null;
  // Raw milestone dates for the tile sub-labels; realty-only, null else.
  listed_at: string | null;
  under_contract_at: string | null;
  linked_service_record_id: string | null;
  // The Opportunity whose won quote created this record, or null
  // (direct-created, or an AC-migrated record predating the link).
  // Drives the "filed on the originating opportunity" documents
  // section — Grant, 2026-08-19.
  converted_from_opportunity_id: string | null;
  // The renewal cycle this record was started from (an in-house carrier
  // switch), or null — the "Renewal change" chip + back-link
  // (Brandon, 2026-08-21).
  created_from_rhythm_instance_id: string | null;
  // The linked Planning case's product detail, so the Processing screen can
  // render the same editable plan card the policy-record page uses (Ryan
  // 2026-08-10: "I'm not seeing a place where I would enter the plan
  // information"). null for non-Planning records, for an unlinked record,
  // and for a Planning record whose subtype row doesn't exist yet — the
  // page stubs that last case so the first save creates the row.
  planning_case: PlanningCaseDetail | null;
  /**
   * S18: what Ryan already typed in about the product BEFORE
   * `planning_case` exists — carrier, policy number, plan name,
   * requested effective date, captured directly on this transaction.
   * null for every non-Planning transaction.
   */
  captured_product_facts: {
    carrier_vendor_id: string | null;
    carrier_name: string | null;
    policy_number: string | null;
    product_name: string | null;
    effective_date: string | null;
    // ACA / health / Medicare application facts (migration 20260829004952)
    // — what the advisor knows before the carrier has issued and the plan
    // record exists.
    plan_tier: string | null;
    monthly_premium: number | null;
    annual_premium: number | null;
    /** 0/1/null — null is "nobody asked", not "no subsidy". */
    has_subsidy: number | null;
    aca_income: number | null;
    subsidy_amount: number | null;
    /** Estate execution facts, known at the signing. */
    estate_plan_type: string | null;
    signed_at: string | null;
    attorney_name: string | null;
    /** estately | meagan | trust_and_will | angie | null — the estate
     *  drafting designation (Ryan, 2026-08-30; widened 2026-09-01). */
    estate_drafting_channel: string | null;
  } | null;
  /**
   * The linked mortgage loan's product detail (borrower, lender, amount,
   * rate, dates, fees) — so the Processing screen can render the SAME
   * editable card the loan-record page uses (Baker's 2026-08-12
   * field-visibility ask). null for non-Mortgage records, for an unlinked
   * record, and for a Mortgage record with no mortgage_loans subtype row.
   */
  mortgage_loan: MortgageLoanDetail | null;
  /**
   * The loan's numbers for the Processing cards, resolved from the funded
   * loan when one exists and from the in-Processing application record
   * when it doesn't (Joey Ellis, 2026-08-27). Non-null on every Mortgage
   * record, so the cards always have something to render and edit.
   */
  loan_facts: MortgageLoanFacts | null;
  /**
   * Whether the linked insurance service record already has an
   * insurance_policies row. false for a policy-family record with none —
   * the Processing screen offers the same "Add policy details" card the
   * record page shows in that case (Mylah, 2026-08-12), instead of the
   * fuller carrier-data card this endpoint doesn't fetch. null for
   * non-Insurance records and for an unlinked record.
   */
  has_insurance_policy: boolean | null;
  /**
   * Whether the linked service record's primary person has a Medicare
   * Beneficiary Identifier on file (Ryan Delviken, 2026-08-10: he'd
   * already entered it "somewhere else" and wanted that acknowledged
   * here). Only populated for a Medicare case; null otherwise, and null
   * when the record has no primary person on file. Never carries the
   * MBI itself — only the on-file flag and who to check it against.
   */
  /** An employer's implementation file: the employer, the group plan
   *  once it exists, and the BuddyIns service (the file's own pick until
   *  the plan exists, the plan's after). Null for every other record. */
  group_implementation: {
    employer_id: string;
    group_plan_id: string | null;
    enrollment_service: GroupPlanEnrollmentService | null;
  } | null;
  medicare_id_status: {
    contact_id: string;
    display_name: string;
    on_file: boolean;
  } | null;
  stage_template: StageTemplate;
  current_stage_index: number;
  // Pace / stalled surfacing (PR1) — mirrors OpportunityDetail.
  is_stalled: boolean;
  stalled_typical_days: number;
  stalled_reason: string | null;
  // Processing redesign (PR3) — count of conditions currently blocked.
  // Feeds the milestone-strip block flag on the detail page.
  blocked_condition_count: number;
  // Provenance line ("where did this come from?"); null when unknown.
  origin: TransactionOrigin | null;
  // Set when the record was archived (e.g. a duplicate, cleaned up via
  // the trash-can action). The record stays in history — never deleted.
  archived_at: string | null;
  archived_reason: string | null;
  /** The client-facing companies serving this household. Feeds the "no
   *  open work" chips in the header's "Also working with" row. */
  household_company_slugs: string[];
  /** Per-company relationship state for the SERVED letter strip. Only
   *  populated on Realty records — see the shared/db/detail-queries.ts
   *  field comment. Empty on every other company. */
  household_company_served: HouseholdCompanyServedEntry[];
}

export interface HouseholdCompanyServedEntry {
  company_slug: string;
  relationship_state: string;
  current_advisor_name: string | null;
}

// Realty transaction linking (Nate 2026-07-06). A candidate property
// record for the "Link property record" picker.
export interface LinkableServiceRecord {
  service_record_id: string;
  household_id: string;
  household_display_name: string | null;
  service_family: string; // 'listing' | 'home_purchase'
  /** The product: 'listing' | 'home_purchase' | 'rental_listing' | 'home_rental'. */
  service_type?: string;
  side: string; // same as service_family
  property_address: string | null;
  property_state: string | null;
}

/** One address the OS already has for a realty file, offered as a prefill
 *  in the "Link property record" flow rather than as something to link
 *  directly: a suggestion is an address, not a record. */
export interface PropertySuggestionAddress {
  line_1: string;
  line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export interface PropertySuggestionsResponse {
  offers: Array<{
    offer_id: string;
    label: string;
    status: string;
    display_line: string | null;
    address: PropertySuggestionAddress;
  }>;
  viewings: Array<{
    viewing_id: string;
    viewed_at: string;
    display_line: string | null;
    address: PropertySuggestionAddress;
  }>;
}

// Result of linking (existing or inline-created). `undo` carries what the
// 60s toast needs to reverse the link cleanly — including restoring the
// deal-value fields the link's BrokerMint write-through filled.
export interface LinkServiceRecordResult {
  ok: boolean;
  linked_service_record_id: string;
  created_service_record: boolean;
  side: "listing" | "home_purchase" | null;
  /** BrokerMint numbers the link just filled onto the transaction
   *  (Grant 2026-07-29 — the data comes over); null when nothing flowed. */
  pulled_numbers: {
    product_metric: number | null;
    estimated_revenue_year_1: number | null;
  } | null;
  undo: {
    previous_linked_service_record_id: string | null;
    delete_service_record_id: string | null;
    restore_numbers: {
      product_metric?: number | null;
      estimated_revenue_year_1?: number | null;
    } | null;
  };
}

// Processing redesign (PR3) — one checklist item on a transaction.
// Mirrors TransactionConditionRow in shared/db/transaction-conditions.ts.
export type ConditionStatus = "open" | "met" | "blocked" | "na";
export type ConditionType =
  | "doc"
  | "signature"
  | "underwriting"
  | "compliance"
  | "external"
  | "other";
export type ConditionOwnerKind = "client" | "advisor" | "processor" | "compliance" | "vendor";

// Joey program PR5 — Stew-suggested loan conditions.
export interface SuggestedCondition {
  label: string;
  condition_type: ConditionType;
  owner_kind: ConditionOwnerKind | null;
  rationale: string;
}
export interface LoanConditionSuggestionsResponse {
  suggestions: SuggestedCondition[];
}

export interface TransactionCondition {
  id: string;
  transaction_id: string;
  condition_key: string | null;
  label: string;
  condition_type: ConditionType;
  status: ConditionStatus;
  owner_kind: ConditionOwnerKind | null;
  owner_user_id: string | null;
  due_date: string | null;
  document_id: string | null;
  blocked_reason: string | null;
  source: "seeded" | "manual";
  satisfied_at: string | null;
  satisfied_by_user_id: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Nate feedback 2026-07-01 — the Tasks page's "Transaction tasks"
// section. Mirrors shared/db/transaction-work.ts.
export interface TransactionWorkCondition {
  id: string;
  label: string;
  condition_type: ConditionType;
  status: ConditionStatus; // 'open' | 'blocked' only in this payload
  owner_kind: ConditionOwnerKind | null;
  due_date: string | null;
  blocked_reason: string | null;
}
export interface TransactionWorkItem {
  id: string;
  label: string;
  household_id: string;
  household_display_name: string;
  company_slug: string;
  stage: string;
  expected_close_date: string | null;
  realty_side: RealtySide | null;
  conditions: TransactionWorkCondition[];
}

// Processing redesign (PR6) — one external system a transaction touches
// (a lender LOS, title company, signing room, carrier portal). Mirrors
// TransactionVendorRow in shared/db/transaction-vendors.ts, including
// the two joined fallback fields from the optional vendors row.
//
// Per Stewardship's source-of-truth doctrine these rows INDEX external
// systems (deep-link + staleness stamp) — they never mirror state.
export type TransactionVendorKind =
  | "lender"
  | "title"
  | "escrow"
  | "signing"
  | "carrier"
  | "custodian"
  | "other";
export type TransactionVendorStatus = "active" | "stale" | "done" | "na";

export interface TransactionVendor {
  id: string;
  transaction_id: string;
  vendor_id: string | null;
  system_label: string;
  system_kind: TransactionVendorKind;
  external_ref: string | null;
  portal_url: string | null;
  status: TransactionVendorStatus;
  last_synced_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Fallbacks from the linked vendors row (null when unlinked or absent).
  vendor_display_name: string | null;
  vendor_portal_url: string | null;
}

export interface AlsoHappeningItem {
  kind: "opportunity" | "transaction" | "rhythm";
  id: string;
  company_slug: string;
  label: string;
  status: string;
  last_touch_at: string | null;
}

// Realty party roster (migration 0205, Nate's N26). Mirrors
// TransactionPartyRole / TransactionPartyEntry in
// shared/db/transaction-parties.ts.
export type TransactionPartyRole =
  | "client"
  | "co_client"
  | "title"
  | "escrow"
  | "loan_officer"
  | "buyer_agent"
  | "listing_agent"
  | "transaction_coordinator"
  | "lender"
  | "inspector"
  | "other";
export type TransactionPartySide = "client" | "other";

// One resolved roster entry — a stored party OR a derived household
// client contact, in a shape the panel renders + multi-selects + emails.
export interface TransactionPartyEntry {
  key: string;
  party_id: string | null;
  role: TransactionPartyRole;
  role_label: string;
  side: TransactionPartySide;
  name: string;
  email: string | null;
  contact_id: string | null;
  vendor_id: string | null;
  source: "household" | "party";
  removable: boolean;
  do_not_email: boolean;
  // What a contract asks this party for. Present on a stored party;
  // null on a derived household contact (our own client has no licence
  // number and no brokerage to print).
  credentials: TransactionPartyCredentials | null;
}

// The business credentials an AAR contract asks the other side's agent
// for — purchase contract lines 457 to 461. Business facts, not personal
// ones: the other side's own private details are never carried here.
export interface TransactionPartyCredentials {
  phone: string | null;
  license_number: string | null;
  mls_code: string | null;
  firm_name: string | null;
  firm_mls_code: string | null;
  firm_license_number: string | null;
}

// Opportunity party roster (migration 0261, following Nate's N26
// pattern). Mirrors OpportunityPartyRole / OpportunityPartyEntry in
// shared/db/opportunity-parties.ts. Company-neutral role set (an
// Opportunity spans all five companies, not just Realty) plus a third
// linkage — user_id — for looping in a teammate.
export type OpportunityPartyRole =
  | "client"
  | "co_client"
  | "co_advisor"
  | "referral_partner"
  | "vendor_contact"
  | "other";
export type OpportunityPartySide = "client" | "other";

export interface OpportunityPartyEntry {
  key: string;
  party_id: string | null;
  role: OpportunityPartyRole;
  role_label: string;
  side: OpportunityPartySide;
  name: string;
  email: string | null;
  contact_id: string | null;
  vendor_id: string | null;
  user_id: string | null;
  source: "household" | "party";
  removable: boolean;
  do_not_email: boolean;
}

// Ongoing Care plan (rhythm instance) detail — /ongoing-care/:id.
export interface RhythmInstanceDetail {
  id: string;
  rhythm_template_id: string;
  rhythm_template_slug: string;
  rhythm_template_display_name: string;
  rhythm_template_anchor_type: string;
  household_id: string;
  household_display_name: string;
  // Every live member of the household — S12. Ongoing Care has no
  // per-record `people` array, so the record page's person strip is
  // driven entirely by household membership here.
  household_members: HouseholdMemberSummary[];
  // What Matters Most (Category 6 judgment field, household-scoped) —
  // surfaced above the fold on the record page (S12). null when never
  // captured.
  what_matters_most: string | null;
  what_matters_most_updated_at: string | null;
  what_matters_most_updated_by_name: string | null;
  what_matters_most_unknown_reason: string | null;
  what_matters_most_unknown_at: string | null;
  linked_service_record_id: string | null;
  linked_service_record_type: string | null;
  linked_service_record_label: string | null;
  company_slug: string | null;
  cycle_anchor_date: string;
  cycle_end_date: string;
  status: string;
  cycle_number: number;
  owner_user_id: string | null;
  owner_name: string | null;
  created_at: string;
  updated_at: string;
  // PR 2 — Ongoing Care build-out enrichment.
  is_live: number; // 1 = live (an actively tracked renewal cycle); 0 = draft
  version_number: number; // optimistic-lock version
  can_edit: boolean; // may the requesting viewer edit this plan?
  health: RhythmHealth;
  // PR 9b — the user-set custom plan name (null ⇒ UI uses the template
  // display name), and the household's other live work for the context rail.
  plan_name: string | null;
  // The linked service record's primary person (0252 "whose record is
  // this"), shown only for multi-member households. null otherwise.
  record_person_name: string | null;
  // The workflow stage on staged renewal boards (null = entry stage or
  // a stage-less plan) — 2026-08-21.
  renewal_stage: string | null;
  // The open carrier-switch Processing record born from this cycle, or
  // null. Drives "Start the switch" vs "Open the switch record →".
  switch_transaction: { id: string; stage: string } | null;
  also_happening: AlsoHappeningItem[];
  // C1 (financial-advisor-workspace.md) — the Accounts card's AUM +
  // fee-schedule revenue, via the same buildFinancialSummary() the
  // Household/Contact pages use. null for a non-Financial household.
  financial_summary?: FinancialSummary | null;
  // The Accounts card's per-account rows (mockup v4) — empty for a
  // non-Financial household.
  financial_accounts?: FinancialAccountRow[];
}

export type RhythmHealthClassification =
  | "on_track"
  | "behind"
  | "off_track"
  | "at_risk"
  | "completed";

export interface RhythmHealth {
  health: RhythmHealthClassification;
  days_elapsed: number;
  days_remaining: number;
  cycle_total_days: number;
  elapsed_ratio: number;
  summary: string;
}

// PR 4 — Ongoing Care Home command-center aggregates.
export interface OngoingCarePortfolio {
  active_total: number;
  live: number;
  draft_not_live: number;
  unassigned: number;
  overdue: number;
  due_30d: number;
  heaviest_month: number | null; // 1–12
  heaviest_month_count: number;
}

export interface OngoingCareCalendarItem {
  id: string;
  household_display_name: string;
  rhythm_template_slug: string;
  rhythm_template_display_name: string;
  health: RhythmHealthClassification;
}

export interface OngoingCareCalendarBucket {
  month: number; // 1–12
  count: number;
  sample: OngoingCareCalendarItem[];
}

// The full active working set — the single source for the redesigned
// Ongoing Care views (calendar / timeline / workload / renewal board). The
// renewal/event date is `cycle_anchor_date`; status is derived client-side
// via care-status.ts so every view agrees. Mirrors CarePlanRow in
// shared/db/ongoing-care-aggregates.ts.
export interface CarePlan {
  id: string;
  household_id: string;
  household_display_name: string;
  company_slug: string | null;
  rhythm_template_slug: string;
  rhythm_template_display_name: string;
  cycle_anchor_date: string;
  cycle_end_date: string;
  status: string;
  is_live: number;
  owner_user_id: string | null;
  owner_name: string | null;
  /** Linked service record's product (e.g. 'medicare_case'); null for
   *  unlinked household-level plans (Birthday). */
  service_type: string | null;
  /** The linked service record (the case itself); null on a
   *  household-level plan. Lets a card open the case directly. */
  linked_service_record_id: string | null;
  /** Current workflow stage slug (Andrea, 2026-08-30 — the renewal-status
   *  board's cards previously carried nothing distinguishing one plan
   *  from another). Resolve to a label via careStageLabelForRow. */
  renewal_stage: string | null;
  /** Insurance policy type off the record's primary policy; null when
   *  there is none. */
  policy_type: string | null;
  /** Carrier name off that same primary policy; null when none. */
  carrier_name: string | null;
  /** Medicare PDP/MAPD (or ACA metal level) off the linked Planning case;
   *  null outside Planning. */
  plan_tier: string | null;
}

// Plan DEFINITIONS — the user-owned plans layer (2026-07-23 doctrine:
// users create plans; users enroll cohorts into them). Mirrors
// PlanDefinitionRow in shared/db/ongoing-care-plan-definitions.ts.
export interface CarePlanDefinition {
  id: string;
  slug: string;
  display_name: string;
  company_scope: string | null; // raw JSON array string
  anchor_type: string;
  service_type: string | null;
  anchor_window_start: string | null; // 'MM-DD'
  anchor_window_end: string | null; // 'MM-DD'
  /** "household" = one cycle per household however many products it
   *  holds (an annual review); "service_record" = one per record (a
   *  policy renewal). Drives how the enrollment confirm states what a
   *  run will create. */
  enrollment_grain: string;
  auto_enroll: number;
  created_by_user_id: string | null;
  created_by_name: string | null;
  archived_at: string | null;
  archived_reason: string | null;
  description: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
  enrolled_count: number;
  live_count: number;
  /** Whether the CURRENT viewer may edit/enroll (company-scope). */
  manageable: boolean;
  // Ongoing Care v2 plan answers (restart, owner, skip reasons).
  eligible_plan_tiers?: string | null;
  restart_month_day?: string | null;
  restart_stage_slug?: string | null;
  unfinished_handling?: "flag" | "reset";
  backup_owner_user_id?: string | null;
  backup_owner_name?: string | null;
  /** Raw JSON array of skip reasons, or null. */
  skip_reasons?: string | null;
  /** Present on the list when asked for with counts (active plans only). */
  counts?: CarePlanCounts | null;
}

/** The plan's three words, plus done and unfinished (Ongoing Care v2). */
export interface CarePlanCounts {
  on: number;
  not_yet: number;
  skipped: number;
  /** Null when no stage on the board counts as done. */
  done_this_cycle: number | null;
  unfinished: number;
}

export interface CarePlanStage {
  slug: string;
  label: string;
  typical_days?: number;
  sort_order?: number;
  counts_as_done?: boolean;
}

export interface CarePlanOverview {
  definition: CarePlanDefinition;
  stages: CarePlanStage[];
  /** The Pipeline board these stages come from (null for a firm-wide plan). */
  board: { company_slug: string; type: string; can_edit: boolean } | null;
  counts: CarePlanCounts;
  scope: "mine" | "all";
  sentence: string;
  can_change_membership: boolean;
}

export type CarePlanMemberState = "on" | "not_yet" | "skipped" | "unfinished";

export interface CarePlanMemberRow {
  household_id: string;
  household_name: string | null;
  service_record_id: string | null;
  record_label: string | null;
  rhythm_instance_id: string | null;
  cycle_number: number | null;
  stage: string | null;
  cycle_anchor_date: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  unfinished_from_cycle: number | null;
  unfinished_stage: string | null;
  skip_id: string | null;
  skip_reason: string | null;
  skip_note: string | null;
  skipped_by_name: string | null;
  skipped_at: string | null;
}

export interface CarePlanMemberPage {
  state: CarePlanMemberState;
  rows: CarePlanMemberRow[];
  total: number;
  limit: number;
  offset: number;
  scope: "mine" | "all";
}

export interface CarePlanHistoryEntry {
  kind: "plan_edit" | "skip" | "unskip";
  at: string;
  actor_name: string | null;
  detail: Record<string, unknown>;
}

export interface CarePlanDefinitionDetail {
  definition: CarePlanDefinition;
  stages: { slug: string; label: string }[];
  suggested_window: { start: string; end: string; label: string } | null;
}

export interface CareEnrollmentPreview {
  eligible: number;
  already_enrolled: number;
  missing_date: number;
  skipped_on_purpose: number;
  window_anchor: string | null;
  sample: { household_name: string | null; anchor: string | null }[];
  /** The viewing advisor's own slice of the same cohort — null when
   *  they hold no book in this plan's company. */
  mine: {
    eligible: number;
    already_enrolled: number;
    missing_date: number;
    skipped_on_purpose: number;
    sample: { household_name: string | null; anchor: string | null }[];
  } | null;
}

// Month drill-down — plans renewing in a given month.
export interface MonthPlanItem {
  id: string;
  household_id: string;
  household_display_name: string;
  rhythm_template_slug: string;
  rhythm_template_display_name: string;
  company_slug: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  is_live: number;
  status: string;
  renewal_date: string; // YYYY-MM-DD — the cycle anchor, in this month
}

// PR 7 — mutation/activation shapes.
export interface OngoingCarePlanRow {
  id: string;
  rhythm_template_id: string;
  household_id: string;
  linked_service_record_id: string | null;
  cycle_anchor_date: string;
  cycle_end_date: string;
  status: string;
  cycle_number: number;
  owner_user_id: string | null;
  is_live: number;
  version_number: number;
  plan_name: string | null;
  paused_reason: string | null;
  cancel_reason: string | null;
  cycle_outcome: string | null;
}

/** One row of a household's plan, Ongoing Care v2 step 4
 *  (shared/db/care-plan-household-states.ts). */
export interface HouseholdCarePlanRow {
  service_record_id: string | null;
  record_label: string | null;
  state: "on" | "not_yet" | "skipped";
  rhythm_instance_id: string | null;
  cycle_number: number | null;
  cycle_anchor_date: string | null;
  stage_label: string | null;
  unfinished: boolean;
  skip_id: string | null;
  skip_reason: string | null;
  skipped_by_name: string | null;
  skipped_at: string | null;
  matched_record_label: string | null;
  blocked_reason: string | null;
}

export interface HouseholdCarePlanEntry {
  plan_id: string;
  display_name: string;
  company_slug: string | null;
  service_type: string | null;
  grain: "service_record" | "household";
  skip_reasons: string | null;
  /** May this viewer add or skip on this plan (company scope). */
  can_change: boolean;
  rows: HouseholdCarePlanRow[];
}

export interface HouseholdCarePlans {
  plans: HouseholdCarePlanEntry[];
  companies_without_plans: string[];
}

export interface RecordCarePlanOption {
  rhythm_template_id: string;
  display_name: string;
  cycle_anchor_date: string | null;
  already_enrolled: boolean;
  blocked_reason: string | null;
  /** Why a person left this record off the plan on purpose, when they did. */
  skipped_reason: string | null;
}

export interface CreatableCareTemplate {
  id: string;
  slug: string;
  display_name: string;
  company_scope: string | null;
  anchor_type: string;
  /** 'service_record' plans run one cycle per policy/loan/case and are
   *  started from that record, not from this one-household flow. */
  enrollment_grain: string;
  creatable: boolean;
}

export interface CareMutationResult {
  ok: boolean;
  rhythm: OngoingCarePlanRow;
}

export interface CareCreateResult extends CareMutationResult {
  activation: { requested: boolean; applied: boolean; reasons: string[] };
}

// Service Records PR 14 — Transaction linked-work panel.
// Mirrors shared/db/transaction-linked-work TransactionLinkedWorkResult.
export interface LinkedWorkContact {
  contact_id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  household_role: string | null;
}

export interface LinkedWorkAutomationRun {
  run_id: string;
  automation_id: string;
  automation_slug: string;
  automation_display_name: string;
  triggered_at: string;
  triggered_by: string | null;
  status: string;
  entity_type: string | null;
  entity_id: string | null;
  details_preview: string | null;
}

export interface TransactionLinkedWorkResponse {
  transaction_id: string;
  household: { id: string; display_name: string };
  contacts: LinkedWorkContact[];
  related_service_records: HouseholdPortfolioItem[];
  related_summary: HouseholdPortfolioSummary;
  recent_automation_runs: LinkedWorkAutomationRun[];
}

export interface RhythmListItem {
  id: string;
  household_id: string;
  household_display_name: string;
  rhythm_template_slug: string;
  rhythm_template_display_name: string;
  cycle_anchor_date: string;
  cycle_end_date: string;
  status: string;
  owner_name: string | null;
  owner_user_id: string | null;
  // PR 2 — Ongoing Care build-out enrichment.
  company_slug: string | null; // display company (null = firm-wide)
  is_live: number; // 1 = live (fires touchpoints); 0 = draft
  version_number: number; // optimistic-lock version
  can_edit: boolean; // may the requesting viewer edit this plan?
  // Joy feedback PR 3 — linked-policy enrichment ("premium current and
  // renewal"). All null when the plan has no linked insurance policy.
  policy_type: string | null;
  carrier_name: string | null;
  current_premium: number | null;
  renewal_premium_offer: number | null;
  /** The linked policy's effective_date — tells the card whether the
   *  term is already upcoming, in which case pre_renewal_premium (not
   *  renewal_premium_offer) is what "current" should show. Null when the
   *  plan has no linked insurance policy (Joy, 2026-09-14). */
  policy_effective_date: string | null;
  /** Snapshot of what current_premium read just before the linked
   *  policy's most recent renewal rolled forward. */
  pre_renewal_premium: number | null;
  // Joy PR 6b — the 8-stage renewal workflow position. NULL displays
  // as the entry stage (Renewal Approaching) on insurance renewals.
  renewal_stage: string | null;
  renewal_stage_updated_at: string | null;
  coverage_preferences: string | null;
  /** Linked SR's product slug ('medicare_case', …); null when unlinked. */
  service_type: string | null;
  /** The linked service record (the case itself); null on a
   *  household-level plan. */
  linked_service_record_id: string | null;
  // The linked service record's primary person (0252 "whose record is
  // this"), shown only for multi-member households. null otherwise.
  record_person_name: string | null;
  /** Email to reach this plan's person — the linked record's primary
   *  contact, falling back to the household's primary contact when
   *  that person has none on file. NOT gated on household size (unlike
   *  record_person_name) — a one-person household still needs an
   *  address (Andrea, 2026-09-22). Null when neither has an email. */
  record_person_email: string | null;
  /** Medicare type ('ma' | 'pdp' | 'medigap_*') or ACA metal level; null
   *  for anything else. S23. */
  plan_tier: string | null;
  /** Canonical household AUM in cents (shared/db/canonical-aum.ts —
   *  the same definition every dashboard uses). 0 when the household
   *  has no counted AUM. Carried on every row; only the Financial
   *  renewal board displays it (C2). */
  aum_cents: number;
  /** Last real touch on the household (shared/db/last-touch.ts); null
   *  when never touched. */
  last_contact_at: string | null;
  /** Other active Medicare renewals in this household, excluding this
   *  row. Always [] outside Medicare / single-client households. S23,
   *  andrea-planning-ops.md §7b. */
  medicare_household_siblings: {
    id: string;
    name: string | null;
    renewal_stage: string | null;
  }[];
}

// Server-side saved view shape (snake_case to match the JSON response
// from /api/saved-views). v2 (Saved-Filter Builder PR 6): three-tier
// audiences + the full captured lens (conditions, columns, density)
// + publisher attribution.
export interface ServerSavedView {
  id: string;
  user_id: string | null;
  role_slug: string | null;
  company_slug: string | null;
  audience: "personal" | "company" | "global" | "role";
  entity_type: string;
  slug: string;
  label: string;
  filters: { key: string; value: string }[];
  conditions: FilterCondition[];
  columns: string[] | null;
  density: "comfortable" | "compact" | "condensed" | null;
  sort_by: string | null;
  sort_dir: "asc" | "desc" | null;
  is_pinned: boolean;
  sort_order: number;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

// Ad-audience definition shape (Marketing & Ads Phase 0) — snake_case
// to match /api/admin/integrations/ad-audiences responses. Definitions
// + capability notes only; never identifier data.
// ─── Microsoft Bookings sync ─────────────────────────────────────────
// One row per Bookings page in the tenant. Pages are auto-discovered on
// every sweep (each employee sets up their own), so this roster grows
// without an admin step.
export interface BookingsPageDto {
  id: string;
  display_name: string | null;
  is_active: number;
  last_synced_at: string | null;
  last_error: string | null;
  last_appointment_count: number | null;
}

export interface BookingsSyncStatus {
  /** False when this environment carries no Microsoft app credentials. */
  configured: boolean;
  pages: BookingsPageDto[];
  totals: {
    appointments: number;
    contacts_created: number;
    unresolved: number;
  };
}

export interface BookingsSweepSummaryDto {
  configured: boolean;
  pages_discovered: number;
  pages_synced: number;
  pages_failed: number;
  appointments_scanned: number;
  created_contacts: number;
  updated: number;
  unresolved: number;
  fields_filled: number;
  fields_replaced: number;
  conflicts_skipped: number;
  notifications: number;
  duration_ms: number;
}

export interface AdAudienceSummaryDto {
  id: string;
  label: string;
  slug: string;
  saved_view_id: string | null;
  filter_set: { version: number; match: "all"; conditions: FilterCondition[] };
  platforms: string[];
  purpose: "inclusion" | "exclusion" | "lookalike_seed";
  ad_category: "standard" | "credit" | "housing";
  include_value_column: boolean;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  capabilities?: Record<string, { customAudience: boolean; lookalike: boolean; note: string }>;
}

// Service Records PR 5a — list page row shape.
export interface ServiceRecordListItem {
  id: string;
  household_id: string;
  household_display_name: string;
  service_type: string;
  service_family: string;
  display_label: string | null;
  company_slug: string;
  status: string;
  current_advisor_user_id: string | null;
  current_advisor_name: string | null;
  effective_date: string | null;
  termination_date: string | null;
  external_ref: string | null;
  external_system: string | null;
  carrier_name: string | null;
  /** Derived loan outcome (loan-family SRs only; null elsewhere) —
   *  'closed' (funded) vs 'cancelled' (fell through) vs 'open'. */
  loan_disposition: "closed" | "cancelled" | "open" | null;
  /** Funded/closing date when closed; cancelled/withdrawn when cancelled. */
  loan_disposition_date: string | null;
  /** What the county record says about the house behind a traditional
   *  loan (win-back). Null for records the county doesn't speak for. */
  county_standing?:
    | "still_ours"
    | "refinanced_away"
    | "sold"
    | "unclear"
    | "none"
    | "ours_newer"
    | null;
  last_updated_at: string;
}

// Advisor-assignment result shapes (Joy, 2026-07-01). Mirror the server
// return types in shared/db/service-record-advisor.ts.
export interface AssignServiceRecordAdvisorResult {
  ok: true;
  service_record_id: string;
  household_id: string | null;
  company_slug: string;
  prior_advisor_user_id: string | null;
  new_advisor_user_id: string | null;
  service_records_propagated: number;
  advisor_history_changed: boolean;
}

export interface BulkAssignAdvisorResult {
  bulk_action_id: string;
  relationships_assigned: number;
  service_records_affected: number;
  skipped: { service_record_id: string; reason: string }[];
}

export interface UndoBulkAssignAdvisorResult {
  ok: boolean;
  bulk_action_id: string;
  restored: number;
  skipped: number;
  errors: { entity_id: string; message: string }[];
  message?: string;
}

export interface ContactListItem {
  id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  phone_primary: string | null;
  city: string | null;
  state: string | null;
  birthday: string | null;
  household_id: string | null;
  household_display_name: string | null;
  company_slugs_csv: string | null;
  // Who this person asked to stop marketing email, in words, or null.
  marketing_opt_out: string | null;
  // Record-edit time (contacts.updated_at), shown as "Last update".
  // Not the last-contact value the last_contact_days filter uses.
  updated_at: string | null;
  fit_assessment: string | null;
  contact_type: string;
}

// ─── admin / team types ─────────────────────────────────────────────────

export interface AdminTeamUser {
  id: string;
  name: string | null;
  email: string;
  // Uploaded profile photo (users.image) or null → render initials.
  image: string | null;
  role_slug: string | null;
  is_active: boolean;
  // True when the user is MFA-enrolled. Drives the "Reset MFA" row action.
  mfa_enabled: boolean;
  last_sign_in_at: string | null;
  advisor_user_id: string;
  has_credential: boolean;
  // Phase 1f — Baker v1.3 + v1.4. Tracks how the auto-disbursement
  // flow notifies this advisor when their commission report lands.
  commission_notification_preference: CommissionNotificationPreference;
  // Polish #42 — Service Records PR 25 staffing fields.
  buffer_percent: number;
  skill_tags: string[];
  // Email-PDF action — the advisor's most recent commission report that
  // has a PDF attached (null when none yet). Drives whether "Send
  // commission report" is offered on the Team row + bulk bar.
  latest_commission_report_id: string | null;
  latest_commission_period: string | null;
  // Edit-user panel fields (2026-08-15).
  job_title: string | null;
  phone: string | null;
  /** Raw stored JSON, e.g. '["realty"]'. NULL means EVERY company;
   *  '[]' means none. The two are genuinely different — see
   *  parseCompanyScope in shared/db/cleanup-authz.ts — so this is not
   *  parsed into an array here, where null and [] would blur. */
  company_scope: string | null;
  employment_type: string | null;
  employment_started_at: string | null;
  employment_ended_at: string | null;
}

export type CommissionNotificationPreference = "email_pdf" | "teams_dm" | "both" | "none";

export interface AdminTeamInvite {
  id: string;
  email: string;
  role_slug: string;
  user_id: string;
  expires_at: string;
  created_at: string;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_by_email: string | null;
}

export interface CreateAdminInviteInput {
  email: string;
  role: string;
  advisor_user_id: string;
  expires_in_hours?: number;
}

// Ambassador provisioning (program PR 4). Deliberately a SUPERSET of
// CreateAdminInviteResponse — it carries the same setup_url /
// email_sent / console_message fields, so the existing setup-link
// dialog renders an ambassador invite with no changes.
export interface CreateAmbassadorResponse {
  user_id: string;
  invite_id: string;
  email: string;
  name: string;
  role: "ambassador";
  company_scope: string[];
  expires_at: string;
  setup_url: string;
  email_sent: boolean;
  console_message: string | null;
}

export interface CreateAdminInviteResponse {
  invite_id: string;
  email: string;
  role: string;
  advisor_user_id: string;
  expires_at: string;
  setup_url: string;
  email_sent: boolean;
  console_message: string | null;
}

// ─── sensitive-field request + response types ───────────────────────────

export type SensitiveFieldName =
  | "ssn"
  | "spouse_ssn"
  | "coborrower_ssn"
  | "drivers_license"
  | "bank_account_number"
  | "bank_routing_number"
  | "bank_account_name"
  | "medicare_beneficiary_id"
  // Payment card (2026-08-19) — number/CVV/name are encrypted like bank
  // details; the CVV additionally auto-purges 7 days after capture.
  | "card_number"
  | "card_cvv"
  | "card_name";

// Planning line-of-business snapshot (Ryan's dashboard).
export type PlanningLob = "health" | "life_di_ltc" | "medicare" | "estate" | "other";
export interface PlanningLobBucket {
  lob: PlanningLob;
  active_cases: number;
  open_opportunities: number;
}
export interface PlanningLobSummary {
  buckets: PlanningLobBucket[];
  total_active_cases: number;
  total_open_opportunities: number;
}

// PR 15 — one row of Ryan's "Today's 5" briefing card. Mirrors
// TodaysFiveItem in shared/db/todays-five.ts.
export interface TodaysFiveItem {
  household_id: string;
  display_name: string;
  primary_contact_id: string | null;
  primary_contact_name: string | null;
  primary_phone: string | null;
  primary_email: string | null;
  why: string;
  risk_score: number;
  risk_band: string;
  days_since_contact: number;
}

// Contact health lists (migration 0138) — providers + medications.
export interface ContactProvider {
  id: string;
  /** Null for a household member with no profile in the OS — an ACA
   *  dependent. `person_label` names them instead. */
  contact_id: string | null;
  household_id: string | null;
  person_label: string | null;
  provider_name: string;
  specialty: string | null;
  phone: string | null;
  npi: string | null;
  street_address: string | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ContactMedication {
  id: string;
  contact_id: string | null;
  household_id: string | null;
  person_label: string | null;
  medication_name: string;
  dosage: string | null;
  frequency: string | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** One household member's two lists. `contact_id` null means a person
 *  with no profile in the OS; `person_label` is the only name we have. */
/** The Medicare quote on a Medicare case (or a Planning opportunity) for
 *  one plan year: GET /api/medicare-quotes/:kind/:id/:year. Health
 *  information throughout; every call is behind the health tier. */
export type MedicareQuoteKind = "service_record" | "opportunity";

export interface MedicareQuoteFile {
  id: string;
  household_id: string;
  context_kind: MedicareQuoteKind;
  context_id: string;
  plan_year: number;
  file_kind: "medicare_plan_list" | "medicare_plan_page" | "carrier_provider_record";
  file_name: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  read_json: string | null;
  reader_version: string | null;
  medicare_plan_id: string | null;
  carrier_name: string | null;
  version_number: number;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface MedicareQuotePlan {
  id: string;
  household_id: string;
  medicare_plan_id: string | null;
  plan_name: string;
  carrier: string | null;
  plan_kind: "pdp" | "mapd" | null;
  network_type: string | null;
  is_current: number;
  is_recommended: number;
  list_file_id: string | null;
  page_file_id: string | null;
  version_number: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface MedicareQuoteDoctorCheck {
  id: string;
  quote_plan_id: string;
  provider_id: string;
  location: string | null;
  status: "in_network" | "out_of_network" | "not_sure";
  carrier_record_file_id: string | null;
  checked_on: string | null;
  note: string | null;
  version_number: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export type AcaQuoteKind = "service_record" | "opportunity";

export interface AcaQuoteFile {
  id: string;
  file_kind: "aca_plan_snapshot" | "aca_plan_details" | "carrier_coverage_proof";
  file_name: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  read_json: string | null;
  reader_version: string | null;
  hios_plan_id: string | null;
  carrier_name: string | null;
  version_number: number;
  created_at: string;
}

export interface AcaQuotePlan {
  id: string;
  hios_plan_id: string | null;
  plan_name: string;
  carrier: string | null;
  metal_tier: string | null;
  network_type: string | null;
  network_name: string | null;
  is_current: number;
  is_recommended: number;
  snapshot_file_id: string | null;
  details_file_id: string | null;
  search_id: string | null;
  version_number: number;
}

/** One saved healthcare.gov search; read it with storedSearch(). */
export interface AcaQuoteSearch {
  id: string;
  request_json: string;
  result_json: string;
  plans_total: number;
  plans_read: number;
  created_at: string;
}

export type AcaQuotePlanInput = import("@shared/planning/aca-quote").AcaQuotePlanInput;
export type AcaQuoteHospitalInput = import("@shared/planning/aca-quote").AcaQuoteHospitalInput;
export type AcaCoverageCheckInput = Omit<
  import("@shared/planning/aca-quote").AcaCoverageCheckInput,
  never
>;

export interface AcaQuoteHospital {
  id: string;
  contact_id: string | null;
  person_label: string | null;
  hospital_name: string;
  note: string | null;
  version_number: number;
}

export interface AcaCoverageCheck {
  id: string;
  quote_plan_id: string;
  check_kind: import("@shared/planning/aca-quote").AcaCoverageCheckKind;
  provider_id: string | null;
  medication_id: string | null;
  hospital_id: string | null;
  location: string;
  status: import("@shared/planning/aca-quote").AcaCoverageStatus;
  coverage_detail: string | null;
  proof_file_id: string | null;
  checked_on: string | null;
  note: string | null;
  version_number: number;
}

/** What the quote page's "Draft the email" section offers. */
export interface AcaQuoteEmailOptions {
  senders: {
    user_id: string;
    name: string | null;
    email: string;
    is_you: boolean;
    is_advisor: boolean;
    can_draft: boolean;
    booking_links: { label: string; url: string }[];
  }[];
  templates: Record<"stay" | "switch", { subject: string; body: string }>;
  recipients: { name: string; has_email: boolean }[];
  emails: {
    id: string;
    email_kind: "stay" | "switch";
    draft_mailbox_upn: string;
    draft_web_link: string | null;
    created_at: string;
  }[];
  lists: AcaQuoteCoverage["lists"];
}

/** The household's doctors and medications with what the lists say. */
export interface AcaQuoteCoverage {
  lists: {
    contact_id: string | null;
    person_label: string | null;
    providers: {
      id: string;
      provider_name: string;
      specialty: string | null;
      npi: string | null;
      street_address: string | null;
    }[];
    medications: { id: string; medication_name: string; dosage: string | null }[];
  }[];
  hints: import("@shared/aca/quote-coverage").AcaQuoteHints | null;
  /** Why there are no hints, in a sentence, when the year's lists aren't in. */
  hints_unavailable: string | null;
  vintage: { plan_year: string | null; updated: string | null } | null;
  /** Where the doctor and medication hints came from: the carriers' files
   *  loaded for the year, or healthcare.gov when the year isn't loaded. */
  hint_source: "carrier_files" | "healthcare_gov" | null;
  /** Hospitals, from healthcare.gov only. Null when it wasn't asked. */
  hospital_hints: Record<string, import("@shared/aca/quote-coverage").AcaSubjectHints> | null;
  hospital_hints_unavailable: string | null;
}

export interface AcaQuoteResponse {
  context: {
    kind: AcaQuoteKind;
    id: string;
    plan_year: number;
    household_id: string;
    household_name: string | null;
  };
  /** The people the record is for, primary first. */
  people: { contact_id: string; name: string; first_name: string | null; is_primary: number }[];
  /** What the case and the questionnaire already know. */
  facts: {
    members: {
      contact_id: string;
      display_name: string;
      birthday: string | null;
      age: number | null;
    }[];
    plan_case: {
      carrier_name: string | null;
      product_name: string | null;
      monthly_premium: number | null;
      has_subsidy: boolean | null;
      subsidy_amount: number | null;
      aca_income: number | null;
      eligibility: { eligibility_tobacco_use?: boolean | null };
      eligibility_answered_at: string | null;
    } | null;
    opportunity_quote: {
      aca_income: number | null;
      eligibility: { eligibility_tobacco_use?: boolean | null };
      eligibility_answered_at: string | null;
    } | null;
    questionnaire: {
      anything_else: unknown;
      anticipated_income: unknown;
      submitted_at: string;
    } | null;
    /** The household's ZIP, to start a search with. */
    household_zip: string | null;
  };
  files: AcaQuoteFile[];
  plans: AcaQuotePlan[];
  hospitals: AcaQuoteHospital[];
  coverage_checks: AcaCoverageCheck[];
  /** The newest search first, then older ones a plan is still priced by. */
  searches: AcaQuoteSearch[];
  /** False until an admin saves the CMS key. */
  plan_search_ready: boolean;
}

export interface MedicareQuoteResponse {
  context: {
    kind: MedicareQuoteKind;
    id: string;
    plan_year: number;
    household_id: string;
    household_name: string | null;
  };
  /** The people the record is for, primary first, with the preferred
   *  pharmacy from their Health Information. */
  people: {
    contact_id: string;
    name: string;
    first_name: string | null;
    is_primary: number;
    preferred_pharmacy: string | null;
  }[];
  files: MedicareQuoteFile[];
  plans: MedicareQuotePlan[];
  doctor_checks: MedicareQuoteDoctorCheck[];
}

/** What the quote page's "Draft the email" section offers (step 4). */
export interface MedicareQuoteEmailOptions {
  senders: {
    user_id: string;
    name: string | null;
    email: string;
    is_you: boolean;
    is_advisor: boolean;
    can_draft: boolean;
    booking_links: { label: string; url: string }[];
  }[];
  templates: Record<"stay" | "switch", { subject: string; body: string }>;
  recipients: { name: string; has_email: boolean }[];
  emails: {
    id: string;
    email_kind: "stay" | "switch";
    draft_mailbox_upn: string;
    draft_web_link: string | null;
    created_at: string;
  }[];
}

/** A doctor's network answer for one plan, as read on the carrier's site. */
export interface MedicareQuoteDoctorCheckInput {
  quote_plan_id: string;
  provider_id: string;
  location: string | null;
  status: MedicareQuoteDoctorCheck["status"];
  carrier_record_file_id: string | null;
  checked_on: string | null;
  note: string | null;
}

/** The fields a plan is added or edited with (the server replaces all of
 *  them on an edit; shared/planning/medicare-quote-view.ts builds them). */
export interface MedicareQuotePlanInput {
  medicare_plan_id: string | null;
  plan_name: string;
  carrier: string | null;
  plan_kind: "pdp" | "mapd" | null;
  network_type: string | null;
  is_current: boolean;
  is_recommended: boolean;
  list_file_id: string | null;
  page_file_id: string | null;
}

/** Mirror of shared/forms/ltc-case-intake.ts's response. */
export interface LtcCaseIntake {
  people: { contact_id: string; display_name: string }[];
  prequals: {
    submission_id: string;
    /** The prequal, or the full health questionnaire (PR 6). */
    form: "prequal" | "questionnaire";
    contact_id: string | null;
    person_name: string | null;
    submitted_at: string | null;
    sent_from_this_case: boolean;
    stopped_early: { reason: string; question: string } | null;
    /** Advisor-only flags the answers raise ("Likely rated": Nationwide's
     *  rate-class question), with what raised each. */
    flags?: { key: string; label: string; because: string[] }[];
    /** 70 or older on the date of birth they gave: Nationwide requires a
     *  cognitive screening. */
    cognitive_screening?: boolean;
    /** The review task the submission made (PR 10), or null. */
    review_task?: { assignee_name: string | null; completed_at: string | null } | null;
    /** The prequal's limited permission (PR 12). Null on a questionnaire. */
    permission?:
      | { state: "active" | "expired"; expires_on: string }
      | { state: "withdrawn"; expires_on: string; withdrawn_at: string; note: string | null }
      | null;
    /** Who filled it in with the client on a call, or null (2026-10-14). */
    entered_by_name?: string | null;
  }[];
  truncated: boolean;
  /** Which carriers were asked and what each said (PR 8). */
  carriers: LtcCaseCarrier[];
  /** Who can open this case's health answers, by the gate's own rules. */
  readers: { planning: string[]; advisors: string[]; with_reason: string[] };
}

export interface HealthReadsLog {
  rows: {
    id: string;
    occurred_at: string;
    action: "health_read" | "health_write" | "health_read_denied" | "health_write_denied";
    actor_name: string | null;
    role: string | null;
    surface: string | null;
    basis: string | null;
    reason_code: string | null;
    reason_note: string | null;
  }[];
  /** The true count (its own COUNT), for "showing X of Y". */
  total: number;
  limit: number;
}

export interface LtcCaseSummary {
  people: {
    contact_id: string;
    display_name: string;
    height_inches: number | null;
    weight_lbs: number | null;
    tobacco_use: boolean | null;
    medications: { name: string; dosage: string | null; frequency: string | null }[];
    doctors: { name: string; specialty: string | null }[];
  }[];
  applications: {
    contact_id: string;
    display_name: string;
    source: {
      submission_id: string;
      form: "questionnaire" | "prequal";
      submitted_at: string | null;
    } | null;
    sections: {
      key: string;
      title: string;
      answers: { key: string; label: string; value: string | null; lines?: string[] }[];
    }[];
  }[];
}

export type LtcCarrierKind = "informal_inquiry" | "application";
export type LtcCarrierAnswer =
  | "waiting"
  | "offered"
  | "offered_other_class"
  | "declined"
  | "withdrawn";

export interface LtcCarrierInput {
  contact_id: string | null;
  carrier_vendor_id?: string | null;
  carrier_name: string;
  kind: LtcCarrierKind;
  sent_on: string | null;
  answer: LtcCarrierAnswer;
  answered_on: string | null;
  rating_class: string | null;
  note: string | null;
}

export interface LtcCaseCarrier extends LtcCarrierInput {
  id: string;
  person_name?: string | null;
  version_number: number;
  updated_at: string;
}

export interface HouseholdHealthPerson {
  contact_id: string | null;
  person_label: string | null;
  providers: ContactProvider[];
  medications: ContactMedication[];
}

// ─── Reviews (ryan-planning-views.md §2 zone 3) ───────────────────────

export interface RecentReview {
  id: string;
  household_id: string | null;
  contact_id: string | null;
  company_id: string | null;
  company_slug: string | null;
  company_name: string | null;
  reviewer_name: string | null;
  rating_stars: number | null;
  rating_bucket: "four_or_five_stars" | "three_or_fewer_stars" | "unspecified";
  body_text: string | null;
  submitted_at: string;
  source_platform: string | null;
  source_url: string | null;
}

export interface RecentReviewsResponse {
  reviews: RecentReview[];
  /** Real COUNT(*) — always prefer this over reviews.length. */
  total: number;
}

// ─── Requote worksheet (S4, andrea-planning-ops.md §4 step 3) ────────

export interface RequoteWorksheetMember {
  contact_id: string;
  display_name: string;
  birthday: string | null;
  age: number | null;
}

export interface RequoteWorksheetCase {
  service_record_id: string;
  case_type: "medicare" | "health_insurance";
  display_label: string | null;
  status: string;
  covered_contact_ids: string[];
  carrier_name: string | null;
  product_name: string | null;
  plan_tier: string | null;
  policy_number: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  monthly_premium: number | null;
  annual_premium: number | null;
  has_subsidy: boolean | null;
  aca_income: number | null;
  subsidy_amount: number | null;
  /** The four open-enrollment eligibility answers (S7). null on any of the
   *  four means the question has never been answered for this case. */
  eligibility: EligibilityAnswers;
  /** When those four were last answered, or null — so a year-old answer is
   *  shown for what it is instead of as this year's fact. */
  eligibility_answered_at: string | null;
  last_sync_source: string | null;
}

export type {
  ComparisonPlan,
  ComparisonPlanField,
  ComparisonPlanInput,
  ComparisonPlanRole,
  PlanComparison,
} from "@shared/planning/plan-comparison-fields";

export type {
  PlanningBookLineRow,
  PlanningBookOfBusiness,
} from "@shared/db/planning-book-of-business";

export interface RequoteWorksheet {
  household_id: string;
  household_display_name: string;
  members: RequoteWorksheetMember[];
  cases: RequoteWorksheetCase[];
  providers_by_contact: Record<string, ContactProvider[]>;
  medications_by_contact: Record<string, ContactMedication[]>;
  /** Whether ANY case on this household carries at least one eligibility
   *  answer. Distinguishes "nobody has answered these yet" from "this one
   *  case was never asked" — the two read very differently to Andrea. */
  eligibility_available: boolean;
  known_gaps: readonly string[];
  /** True when the caller passed the Planning company gate but not the
   *  HEALTH tier, so providers/medications were left out on purpose. The
   *  worksheet still renders; the health section shows a locked door
   *  instead of empty lists, which would read as "nobody has any". */
  health_lists_withheld: boolean;
  /** The same refusal the per-list routes send, present exactly when the
   *  lists were withheld: who to ask, and whether a reason would open it. */
  health_access_denial?: HealthAccessDenial | null;
  /** Present only when the worksheet was opened FOR an opportunity — the
   *  new-business path, where the household may hold no coverage at all. */
  opportunity?: RequoteWorksheetOpportunity | null;
}

/** The shopping side: an opportunity being quoted, and what the advisor
 *  has learned so far. Every quote field is tri-state — null means nobody
 *  has been asked, which is not the same as an answer of "no". */
export interface RequoteWorksheetOpportunity {
  opportunity_id: string;
  household_id: string;
  company_slug: string;
  opportunity_type: string;
  stage: string;
  outcome: string | null;
  quote: HealthQuoteFacts;
}

/**
 * What a plan comparison hangs off. A case being requoted, or an
 * opportunity being shopped — the same builder, the same twelve rows, a
 * different parent record.
 */
export type ComparisonTargetRef =
  | { kind: "case"; serviceRecordId: string }
  | { kind: "opportunity"; opportunityId: string };

/** The URL prefix every comparison call for a target shares. One place, so
 *  the four methods below cannot disagree about the shape. */
export function comparisonBasePath(householdId: string, target: ComparisonTargetRef): string {
  const hh = encodeURIComponent(householdId);
  return target.kind === "case"
    ? `/api/households/${hh}/requote-worksheet/${encodeURIComponent(target.serviceRecordId)}`
    : `/api/households/${hh}/requote-worksheet/opportunity/${encodeURIComponent(target.opportunityId)}`;
}

// S15 phase 2 — ACA formulary/network coverage check.

export interface DrugCoverageRow {
  carrier: string;
  plan_id: string;
  plan_name: string;
  tier: number;
  pa: boolean;
  st: boolean;
  ql: boolean;
}

export interface ProviderCoverageRow {
  carrier: string;
  plan_id: string;
  plan_name: string;
  in_network: boolean;
  accepting_new: boolean;
}

export interface MedicationCoverageResult {
  medication_id: string;
  medication_name: string;
  found: boolean;
  matched_via: string | null;
  coverage: DrugCoverageRow | null;
}

export interface ProviderMatchWithCoverage {
  provider_id: number;
  display_name: string;
  npi: string;
  specialty: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  matched_via: string;
  coverage: ProviderCoverageRow | null;
}

export interface ProviderCoverageResult {
  provider_id: string;
  provider_name: string;
  found: boolean;
  matches: ProviderMatchWithCoverage[];
  more_exist: boolean;
}

export interface MemberCoverageResult {
  contact_id: string;
  display_name: string;
  medications: MedicationCoverageResult[];
  providers: ProviderCoverageResult[];
}

export type RequoteCoverageResult =
  | { available: false; reason: "not_aca_case" | "carrier_not_recognized" }
  | {
      available: true;
      carrier: string;
      data_vintage: { version: string | null; state: string | null; updated: string | null };
      members: MemberCoverageResult[];
    };

// ─── S7 Planning fields ─────────────────────────────────────────────────

/** One year of a contact's income history (disability underwriting). */
export interface ContactIncomeYear {
  id: string;
  contact_id: string;
  /** The calendar year the income was EARNED. */
  income_year: number;
  annual_income: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/** One line of an estate case's schedule of assets. */
export interface PlanningCaseAsset {
  id: string;
  service_record_id: string;
  asset_type: AssetType;
  description: string;
  estimated_value: number | null;
  valued_at: string | null;
  ownership: AssetOwnership | null;
  owner_contact_id: string | null;
  /** Tri-state: null = not yet determined, false = not retitled. */
  in_trust: boolean | null;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface PlanningCaseAssetInput {
  asset_type: AssetType;
  description: string;
  estimated_value?: number | null;
  valued_at?: string | null;
  ownership?: AssetOwnership | null;
  owner_contact_id?: string | null;
  in_trust?: boolean | null;
  notes?: string | null;
}

export interface SensitiveFieldResponse {
  field: SensitiveFieldName;
  value: string;
}

// ─── Grant's CEO Cockpit dashboard types ─────────────────────────────
//
// Mirrors workers/api/src/routes/dashboard.ts — the two shapes MUST
// stay in sync. When adding a new pulse tile or forecast-card field,
// update both this file and the route handler in lock-step.

export type ClientFacingCompanySlug =
  | "mortgage"
  | "insurance"
  | "planning"
  | "realty"
  | "financial";

export interface TrendIndicator {
  percent_change: number | null;
  absolute_delta: number | null;
  direction: "up" | "down" | "flat" | "insufficient_history";
}

export type DashboardTileSource = "client_engine" | "quickbooks" | "quickbooks_placeholder";

export interface PulseTileBase {
  label: string;
  source: DashboardTileSource;
  source_timestamp: string | null;
  trend: TrendIndicator;
}

export interface PulseRevenueTile extends PulseTileBase {
  kind: "revenue_ttm";
  value_dollars: number;
  company_breakdown: Array<{
    company_slug: ClientFacingCompanySlug;
    revenue: number;
    /**
     * Year-over-year growth for THIS company — current trailing-12
     * vs prior trailing-12, expressed as a percent (e.g., 18.4 means
     * +18.4%). Null when prior was zero or insufficient history.
     */
    trend_percent: number | null;
  }>;
}

export interface PulseQbTile extends PulseTileBase {
  kind: "net_op_profit" | "cash_on_hand";
  value_dollars: number;
  is_placeholder: boolean;
  /**
   * Optional per-company breakdown rendered inside the tile.
   * `value_dollars` per row sums to the headline `value_dollars`
   * above. `trend_percent` is null on Cash on Hand (no prior-month
   * query yet) and computed for Net Op Profit (last-3 vs prior-3
   * net income for the company).
   */
  company_breakdown?: Array<{
    company_slug: ClientFacingCompanySlug;
    value_dollars: number;
    trend_percent: number | null;
  }>;
}

export interface PulseHouseholdsTile extends PulseTileBase {
  kind: "households_served";
  value_count: number;
  net_new_current_quarter: number;
}

export interface PulseCrossSellTile extends PulseTileBase {
  kind: "cross_sell_rate";
  value_percent: number;
  distribution: {
    one_company: number;
    two_companies: number;
    three_companies: number;
    four_companies: number;
    five_companies: number;
  };
}

export interface PulseIdealMixTile extends PulseTileBase {
  kind: "ideal_client_mix";
  /**
   * April 2026 (Grant 2026-04-29): the headline is the TOTAL number
   * of households flagged Jerry across all of Stewardship's books,
   * not the % of last-30d new clients flagged Ideal.
   */
  value_count: number;
  /** Total Ideal / Typical / Not A Fit / Unassessed counts. `typical` is optional because a dashboard snapshot cached before 2026-10-31 lacks it. */
  total_counts: {
    ideal_fit: number;
    typical?: number;
    poor_fit: number;
    unassessed: number;
  };
  /** Legacy fields, kept for /api/dashboard/pulse-detail back-compat. */
  value_percent: number;
  counts: {
    new_total: number;
    ideal_fit: number;
    typical: number;
    poor_fit: number;
    unassessed: number;
  };
}

export interface DashboardLateStageDeal {
  id: string;
  kind: "opportunity" | "transaction";
  value_dollars: number;
  household_display_name: string;
  household_id: string;
  stage_display: string;
  advisor_first_name: string | null;
  needs_data: boolean;
}

export interface DashboardCompanyForecastCard {
  slug: ClientFacingCompanySlug;
  display_name: string;
  active_deal_count: number;
  weighted_pipeline_dollars: number;
  unweighted_pipeline_dollars: number;
  need_data_count: number;
  late_stage: {
    total_count: number;
    top_three: DashboardLateStageDeal[];
  };
  week_over_week_delta: {
    amount_dollars: number;
    direction: "up" | "down" | "flat" | "insufficient_history";
  };
}

export interface DashboardPipelineForecastZone {
  totals: {
    active_deal_count: number;
    weighted_pipeline_dollars: number;
    unweighted_pipeline_dollars: number;
    need_data_count: number;
  };
  companies: DashboardCompanyForecastCard[];
}

export interface DashboardMonthlyCompanyBucket {
  month: string;
  mortgage: number;
  insurance: number;
  planning: number;
  realty: number;
  financial: number;
  total: number;
}

export interface DashboardMonthlyRateBucket {
  month: string;
  rate: number;
}

export interface DashboardTrendZone {
  revenue_by_company_monthly: DashboardMonthlyCompanyBucket[];
  new_households_by_originator_monthly: DashboardMonthlyCompanyBucket[];
  cross_sell_rate_monthly: DashboardMonthlyRateBucket[];
}

export interface DashboardMeta {
  as_of: string;
  quickbooks_last_sync: string | null;
  quickbooks_is_placeholder: boolean;
  client_engine_last_update: string;
  ideal_fit_tracking_enabled: boolean;
}

// ─── Admin / QuickBooks types ─────────────────────────────────────────

export type StewardshipCompanySlug =
  | "mortgage"
  | "insurance"
  | "planning"
  | "realty"
  | "financial"
  | "productions";

export type QuickBooksConnectionStatus = "active" | "expired" | "revoked" | "error";

export interface QuickBooksCompanyStatus {
  stewardship_company: StewardshipCompanySlug;
  display_name: string;
  connected: boolean;
  realm_id: string | null;
  intuit_company_name: string | null;
  connected_at: string | null;
  refresh_token_expires_at: string | null;
  status: QuickBooksConnectionStatus | null;
  last_refresh_error: string | null;
  last_refresh_at: string | null;
}

export interface GustoConnectionView {
  environment: "production" | "demo";
  status: "registered" | "connected" | "needs_reconnect" | "disconnected";
  status_word: string;
  companies: Array<{ uuid: string; name: string }>;
  scopes: string[];
  token_info_at: string | null;
  connected_at: string | null;
  last_error_code: string | null;
}

export interface QuickBooksStatusResponse {
  companies: QuickBooksCompanyStatus[];
}

export interface QuickBooksDisconnectResponse {
  stewardship_company: StewardshipCompanySlug;
  display_name: string;
  disconnected: boolean;
}

export interface QuickBooksRefreshResponse {
  company: StewardshipCompanySlug;
  display_name: string;
  pulled: number;
  skipped: number;
  errors: Array<{ snapshot_month: string; message: string }>;
}

export interface QuickBooksBackfillResultItem {
  company: StewardshipCompanySlug;
  display_name: string;
  pulled: number;
  skipped: number;
  errors: Array<{ snapshot_month: string; message: string }>;
}

export interface QuickBooksBackfillResponse {
  months: number;
  results: QuickBooksBackfillResultItem[];
  message?: string;
}

export interface QuickBooksLastRefreshedResponse {
  last_refreshed: Record<string, string | null>;
}

// The hourly line-level QuickBooks sync's progress, per company. Mirrors
// QuickBooksTransactionSyncStatus in shared/db/quickbooks-transaction-sync.ts.
export interface QuickBooksTransactionHistoryStatus {
  stewardship_company: StewardshipCompanySlug;
  history_status: "not_started" | "loading" | "complete";
  history_completed_at: string | null;
  possibly_incomplete: boolean;
  transaction_count: number;
  earliest_txn_date: string | null;
  latest_txn_date: string | null;
  last_success_at: string | null;
  last_run_at: string | null;
  last_error: string | null;
}

// The money ledger's QuickBooks reader, per company (step A3). Mirrors
// MoneyQuickBooksIngestStatus in shared/db/money-quickbooks-ingest.ts.
export interface MoneyLedgerReaderStatus {
  stewardship_company: StewardshipCompanySlug;
  status: "waiting" | "reading" | "current";
  skipped_reason: string | null;
  ledger_start_date: string;
  receipts_in_force: number;
  last_success_at: string | null;
  last_run_at: string | null;
  last_error: string | null;
}

export interface QuickBooksTransactionHistoryResponse {
  companies: QuickBooksTransactionHistoryStatus[];
  money_ledger?: MoneyLedgerReaderStatus[];
}

// Phase B — per-account snapshot backfill response. Re-parses
// existing quickbooks_snapshots.raw_response_json into the
// quickbooks_account_snapshots projection. No Intuit API calls.
export interface QuickBooksAccountSnapshotsBackfillResultItem {
  company: string;
  display_name: string;
  months_processed: number;
  account_rows_written: number;
  errors: Array<{ snapshot_month: string; message: string }>;
}

export interface QuickBooksAccountSnapshotsBackfillResponse {
  results: QuickBooksAccountSnapshotsBackfillResultItem[];
  message?: string;
}

// ─── Microsoft Graph (Office 365) types — per-advisor connection ─────

export type MicrosoftGraphConnectionStatus =
  | "active"
  | "expired"
  | "revoked"
  | "error"
  | "subscription_lost";

export interface MicrosoftGraphConnectionStatus_Row {
  user_id: string;
  connected: boolean;
  microsoft_account_id: string | null;
  microsoft_principal_name: string | null;
  granted_scopes: string[] | null;
  has_signature: boolean;
  connected_at: string | null;
  refresh_token_expires_at: string | null;
  status: MicrosoftGraphConnectionStatus | null;
  last_refresh_error: string | null;
  last_refresh_at: string | null;
}

export interface MicrosoftGraphStatusResponse {
  connection: MicrosoftGraphConnectionStatus_Row;
}

export interface MicrosoftGraphDisconnectResponse {
  user_id: string;
  disconnected: boolean;
}

// v1.x phase 11.16 (Tier A item 5) — SharePoint folder-shape
// migration response. The endpoint walks every SharePoint-backed
// documents row, computes the new household-rooted path, and (when
// dry_run is false) physically moves files. Idempotent.
export type SharePointMigrationOutcomeStatus =
  | "skipped_already_migrated"
  | "skipped_archived"
  | "skipped_dry_run"
  | "migrated"
  | "error";

export interface SharePointMigrationOutcome {
  document_id: string;
  status: SharePointMigrationOutcomeStatus;
  from?: string | null;
  to?: string;
  reason?: string;
}

export interface SharePointMigrationResponse {
  summary: {
    scanned: number;
    migrated: number;
    skipped: number;
    errored: number;
    dry_run: boolean;
    limit: number;
    // Firm-scale paging (Compliance Program PR 3): total
    // SharePoint-backed rows, this window's start, and where the next
    // window starts (null once the sweep has covered every row).
    eligible_total: number;
    offset: number;
    next_offset: number | null;
  };
  outcomes: SharePointMigrationOutcome[];
}

// ─── R2 → SharePoint lift (2026-08-11) ───────────────────────────────

export type SharePointR2LiftOutcomeStatus = "lifted" | "would_lift" | "error";

export interface SharePointR2LiftOutcome {
  document_id: string;
  filename: string;
  status: SharePointR2LiftOutcomeStatus;
  to?: string;
  reason?: string;
}

export interface SharePointR2LiftResponse {
  summary: {
    dry_run: boolean;
    /** Every stranded row, from a COUNT over the whole table — NOT the
     *  size of this run's page. `scanned` is the page. */
    stranded_total: number;
    eligible_total: number;
    archived_skipped: number;
    scanned: number;
    lifted: number;
    errored: number;
    remaining: number;
    limit: number;
    /** Null on a dry run (nothing to undo). Pass to undoSharePointR2Lift. */
    batch_id: string | null;
  };
  outcomes: SharePointR2LiftOutcome[];
}

export interface SharePointR2LiftUndoResponse {
  batch_id: string;
  in_batch: number;
  reverted: number;
  left_alone: number;
}

export interface MicrosoftGraphAdminRosterRow extends MicrosoftGraphConnectionStatus_Row {
  user_name: string | null;
  user_email: string;
  employee_role_slug: string | null;
}

export interface MicrosoftGraphAdminRosterResponse {
  users: MicrosoftGraphAdminRosterRow[];
}

// ─── Email Flow B types ──────────────────────────────────────────────

export interface EmailMailFolder {
  id: string;
  displayName: string;
  wellKnownName: string | null;
  parentFolderId: string | null;
  childFolderCount: number;
  totalItemCount: number;
  unreadItemCount: number;
  children: EmailMailFolder[];
}

/** One live snooze. `snooze_until` is ISO 8601 UTC; the client compares
 *  it against its own clock, exactly as the old browser store did. */
export interface EmailSnooze {
  message_id: string;
  snooze_until: string;
  created_at: string;
}

export interface EmailFoldersResponse {
  folders: EmailMailFolder[];
}

export interface EmailMessagePreview {
  id: string;
  conversationId: string | null;
  subject: string;
  bodyPreview: string;
  fromAddress: string | null;
  fromName: string | null;
  toAddresses: string[];
  receivedDateTime: string;
  hasAttachments: boolean;
  isRead: boolean;
  isDraft: boolean;
  importance: "low" | "normal" | "high";
  // Phase 5: Microsoft's Focused Inbox classification.
  // null when the inference hasn't run yet — the FOCUSED tab treats
  // nulls as focused so messages don't vanish during the brief gap.
  inferenceClassification: "focused" | "other" | null;
  // v1.x: server-of-truth replied/forwarded indicator from
  // PR_LAST_VERB_EXECUTED. When present the message list prefers
  // this over the localStorage fallback tracker.
  replyVerb: "replied" | "replied_all" | "forwarded" | null;
  // v1.x phase 11.17 (Tier B-1): Outlook flag state. True when the
  // user has flagged the message (or marked it complete — Microsoft
  // keeps complete as a flagged sub-state). Used to render the flag
  // pill in the row + the Flag toggle button.
  isFlagged: boolean;
}

export interface EmailMessagesResponse {
  messages: EmailMessagePreview[];
  // Encoded pagination cursor. Pass back as `?next=` to fetch the
  // next page. null when there are no more messages.
  nextLink: string | null;
}

// Phase 6: single message detail + thread expansion.

export interface EmailMessageBody {
  contentType: "html" | "text";
  content: string;
}

export interface EmailRecipient {
  address: string;
  name: string | null;
}

export interface EmailMessageDetail {
  id: string;
  conversationId: string | null;
  subject: string;
  body: EmailMessageBody;
  bodyPreview: string;
  fromAddress: string | null;
  fromName: string | null;
  toRecipients: EmailRecipient[];
  ccRecipients: EmailRecipient[];
  bccRecipients: EmailRecipient[];
  receivedDateTime: string;
  sentDateTime: string | null;
  hasAttachments: boolean;
  isRead: boolean;
  isDraft: boolean;
  internetMessageId: string | null;
  importance: "low" | "normal" | "high";
  // v1.x phase 11.17 (Tier B-1): Outlook flag state. Mapped from
  // Microsoft Graph's `flag.flagStatus` — true when flagged or
  // complete, false otherwise.
  isFlagged: boolean;
}

// Phase 4: non-inline attachment metadata returned alongside the
// message. Inline images are already rewritten to data: URLs in the
// body, so they don't appear in this list.
export interface EmailAttachmentMetadata {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId: string | null;
  attachmentType: "file" | "item" | "reference" | "unknown";
}

export interface EmailSingleMessageResponse {
  message: EmailMessageDetail;
  attachments?: EmailAttachmentMetadata[];
}

// Where an email attachment may be filed. Mirrors
// SUPPORTED_ENTITY_TYPES in workers/api/src/routes/email/
// save-attachment.ts — keep the two in step.
export type SaveAttachmentEntityType =
  | "household"
  | "contact"
  | "transaction"
  | "opportunity"
  | "service_record";

// One row of a household's open work, as offered by the
// Save-attachment dialog's optional second step.
export interface HouseholdWorkRecord {
  id: string;
  // The record's own type slug — `transaction_type` /
  // `opportunity_type`. Humanized for display by the caller.
  record_type: string;
  stage: string;
  company_slug: string;
  company_display_name: string | null;
}

export interface HouseholdWorkRecordsResponse {
  transactions: HouseholdWorkRecord[];
  opportunities: HouseholdWorkRecord[];
}

export interface EmailMessageSibling {
  id: string;
  subject: string;
  fromAddress: string | null;
  fromName: string | null;
  receivedDateTime: string;
  bodyPreview: string;
}

export interface EmailConversationResponse {
  messages: EmailMessageSibling[];
}

export interface EmailSearchResponse {
  messages: EmailMessagePreview[];
}

// Phase 3 — recipient autocomplete suggestion shape (matches the
// shape returned by /api/contacts/email-suggestions).
//
// Two kinds as of 2026-08-13 (Nate's "can I loop in Joey" ask): a
// household contact, or an active teammate. Discriminated on `kind` so
// the UI can group them without guessing from field presence.
export interface ContactEmailRecipientSuggestion {
  kind: "contact";
  contactId: string;
  name: string;
  preferredName: string | null;
  email: string;
  householdLabel: string;
  companies: string[];
}

export interface EmployeeEmailRecipientSuggestion {
  kind: "employee";
  userId: string;
  name: string;
  email: string;
  roleLabel: string | null;
}

export type EmailRecipientSuggestion =
  | ContactEmailRecipientSuggestion
  | EmployeeEmailRecipientSuggestion;

export interface EmailRecipientSuggestionsResponse {
  suggestions: EmailRecipientSuggestion[];
}

export interface EmailSignatureResponse {
  signature_html: string | null;
  signature_text: string | null;
  last_saved_at: string | null;
}

export interface EmailSignatureSaveResponse extends EmailSignatureResponse {
  cid_images_stripped: boolean;
}

// A file already in the OS's world, attached by reference so its bytes
// never round-trip through the browser. The server fetches them at
// send time — see shared/email/outbound-attachments.ts.
export interface OutboundAttachmentRef {
  source: "document" | "onedrive" | "sharepoint" | "staged";
  id: string;
}

// A big file off the advisor's own computer, uploaded ahead of the
// send (Nate Swartz, 2026-08-25). Small ones still ride inside the
// send request as base64; past a couple of megabytes that stops being
// viable — see shared/email/staged-attachments.ts.
export interface StagedEmailAttachment {
  id: string;
  name: string;
  bytes: number;
  content_type: string;
}

export interface SendEmailInput {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  // Files off the advisor's own computer — the browser is the only
  // thing that has these bytes, so they travel inline.
  attachments?: Array<{ name: string; contentType: string; contentBase64: string }>;
  attachmentRefs?: OutboundAttachmentRef[];
  householdId?: string;
  contactId?: string;
  /** The workflow record the composer was opened on (S14). The server
   *  validates it belongs to the same household and records it on the
   *  email row, so "which emails went out about this opportunity" is
   *  answerable — the activity itself still lands at household grain,
   *  which is what keeps the email in the household timeline and in
   *  every last-touch / at-risk calculation that reads it. */
  recordEntityType?: "service_record" | "opportunity" | "transaction" | "rhythm_instance";
  recordEntityId?: string;
  // Phase 3: pass null when there is no thread context (forward) or
  // omit entirely. Reply / Reply All set this to the original message's
  // internetMessageId so the recipient's mail client groups the thread.
  inReplyToMessageId?: string | null;
  // v1.x phase 11.8: send from a registered shared mailbox (e.g.
  // insurance@stewardship.pro). Server validates the UPN against
  // shared-mailboxes.ts; arbitrary UPNs return 400. Omit / null = send
  // from the signed-in user's own mailbox (default behavior).
  fromMailboxUpn?: string | null;
}

// Phase 3: send route now allows household/contact context to be
// derived from the recipients (no explicit id required for Reply).
// activity_id + occurred_at can be null when no recipient matches a
// Stewardship contact (vendor mail).
export interface SendEmailResponse {
  activity_id: string | null;
  conversation_id: string;
  occurred_at: string | null;
}

// v1.x scheduled send. Same shape as SendEmailInput minus attachments
// (v1 scheduled-send doesn't pass attachments — Outlook's draft-set
// flow has its own attachment endpoint we'd need to wire in
// separately; deferred until anyone asks for "schedule send WITH
// attachments"). Plus the required `sendAtIso` UTC datetime.
export interface SaveEmailDraftInput {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  // Same two attachment shapes as the send path — a draft carries the
  // paperwork or it isn't the same email.
  attachments?: Array<{ name: string; contentType: string; contentBase64: string }>;
  attachmentRefs?: OutboundAttachmentRef[];
  // Optional registered shared mailbox UPN. Same validation as
  // /api/email/send.
  fromMailboxUpn?: string | null;
}

export interface SaveEmailDraftResponse {
  graph_message_id: string;
  // Outlook web link the advisor opens to finish + send the draft.
  // null in the rare case Microsoft Graph doesn't return one.
  web_link: string | null;
}

export interface SendScheduledEmailInput {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  householdId?: string;
  contactId?: string;
  // ISO 8601 UTC datetime, e.g. "2026-05-02T13:00:00Z". Server
  // requires this be at least 60s in the future and at most 90 days
  // out. Anything further out belongs in a calendar reminder.
  sendAtIso: string;
  // v1.x phase 11.8: see SendEmailInput.fromMailboxUpn.
  fromMailboxUpn?: string | null;
}

export interface SendScheduledEmailResponse {
  ok: boolean;
  graph_message_id: string;
  send_at: string;
  activity_id: string | null;
  conversation_id: string;
  occurred_at: string | null;
}

// v1.x search folders.
export interface SearchFolderSummary {
  id: string;
  displayName: string;
  filterQuery: string;
  includeNestedFolders: boolean;
  sourceFolderIds: string[];
  totalItemCount: number;
  unreadItemCount: number;
}

// One of the convenience presets the backend accepts. The UI nudges
// advisors toward these — raw OData filterQuery is supported but not
// surfaced in the v1 picker.
export type SearchFolderPreset =
  | { preset: "from_sender"; emailAddress: string }
  | { preset: "from_or_to"; emailAddress: string }
  | { preset: "unread" }
  | { preset: "flagged" }
  | { filterQuery: string };

export type CreateSearchFolderInput = {
  displayName: string;
  // "inbox" (default) scopes to the inbox + descendants. "all"
  // scopes to every top-level folder including Sent + Archive.
  scope?: "inbox" | "all";
  includeNestedFolders?: boolean;
} & SearchFolderPreset;

// v1.x — Microsoft To Do (Flow B live proxy).
export interface TodoTaskList {
  id: string;
  displayName: string;
  isOwner: boolean;
  isShared: boolean;
  isDefault: boolean;
}

export interface TodoTask {
  id: string;
  listId: string;
  title: string;
  status: "notStarted" | "inProgress" | "completed" | "waitingOnOthers" | "deferred";
  importance: "low" | "normal" | "high";
  bodyContent: string | null;
  dueDateTime: string | null;
  completedDateTime: string | null;
  createdDateTime: string;
  lastModifiedDateTime: string;
}

export interface CreateTodoTaskInput {
  title: string;
  bodyContent?: string;
  dueDateTimeIso?: string;
  importance?: "low" | "normal" | "high";
}

export interface UpdateTodoTaskPatch {
  title?: string;
  bodyContent?: string | null;
  dueDateTimeIso?: string | null;
  importance?: "low" | "normal" | "high";
  status?: "notStarted" | "inProgress" | "completed" | "waitingOnOthers" | "deferred";
}

// v1.x phase 11.5 — per-user To Do list picker.
export interface TodoListOption {
  id: string;
  display_name: string;
  is_default: boolean;
}
export interface TodoListPreferenceResponse {
  preferred_list_id: string | null;
  preferred_list_name: string | null;
  lists: TodoListOption[];
  lists_unavailable: boolean;
}

// Contact-sync-to-phone.
export type ContactSyncScopeValue = "all" | "associated" | "clients" | "company";
export interface ContactSyncPreferenceResponse {
  enabled: boolean;
  scope: ContactSyncScopeValue;
  scopes: ContactSyncScopeValue[];
  connected: boolean;
  contacts_scope_granted: boolean;
  reconnect_required: boolean;
  last_synced_at: string | null;
  last_sync_status: "ok" | "partial" | "error" | null;
  last_sync_error: string | null;
  last_sync_counts: {
    created: number;
    updated: number;
    deleted: number;
    total: number;
  } | null;
}
export interface ContactSyncRunResult {
  outcome: string;
  created: number;
  updated: number;
  deleted: number;
  unchanged: number;
  errors: number;
  total: number;
  more_pending: boolean;
  remaining: number;
}

// v1.x — calendar (read-only proxy).
export interface CalendarMatchedAttendee {
  email: string | null;
  name: string | null;
  contact_id: string | null;
  contact_display_name: string | null;
  household_id: string | null;
  household_display_name: string | null;
  response_status: string;
  attendee_type: string;
}

export interface CreateCalendarEventInput {
  subject: string;
  startIso: string;
  endIso: string;
  body?: string;
  location?: string;
  attendees?: string[];
  isOnlineMeeting?: boolean;
  // No specific time — "this is happening that day." startIso/endIso
  // are still required: the caller computes Phoenix-local
  // midnight-to-midnight (exclusive end, Graph's own all-day
  // convention) before sending. Nate Swartz, 2026-09-07.
  isAllDay?: boolean;
  // When true, the server returns existing events that overlap
  // [startIso, endIso] WITHOUT creating. Lets the UI warn the user
  // before committing. Subsequent call without this flag commits.
  conflictCheck?: boolean;
}

// v1.x phase 11.16 (Tier B item 7) — calendar event mutation
// payloads. All fields optional; PATCH semantics mean omitted fields
// are unchanged.
export interface UpdateCalendarEventInput {
  subject?: string;
  startIso?: string;
  endIso?: string;
  body?: string;
  location?: string;
  attendees?: string[];
  isOnlineMeeting?: boolean;
}

export interface CalendarEventMutationResponse {
  kind: "updated";
  event: {
    id: string;
    subject: string;
    start_iso: string;
    end_iso: string;
    location_display_name: string;
    web_link: string | null;
    online_meeting_url: string | null;
  };
}

export interface CalendarEventConflict {
  id: string;
  subject: string;
  start_iso: string;
  end_iso: string;
  location_display_name: string;
  web_link: string | null;
}

export interface CreateCalendarEventResponse {
  kind: "created" | "conflict_check";
  // Populated when kind === "created"
  event?: {
    id: string;
    subject: string;
    start_iso: string;
    end_iso: string;
    location_display_name: string;
    web_link: string | null;
    online_meeting_url: string | null;
  };
  // Populated when kind === "conflict_check"
  conflicts?: CalendarEventConflict[];
}

export interface UpcomingCalendarEvent {
  id: string;
  // Stable across mailboxes for the same meeting — used to detect
  // whether a .ics invite parsed from an email is already on the
  // calendar before offering "Add to calendar" again.
  ical_uid: string | null;
  subject: string;
  body_preview: string;
  start_iso: string;
  end_iso: string;
  is_all_day: boolean;
  is_cancelled: boolean;
  location_display_name: string;
  organizer_email: string | null;
  organizer_name: string | null;
  // The current user's own RSVP to this event. "organizer" => the
  // user owns it (RSVP not applicable). Drives the RSVP control's
  // pre-selected state.
  response_status: string;
  attendees: CalendarMatchedAttendee[];
  web_link: string | null;
  online_meeting_url: string | null;
  matched_households: Array<{ id: string; display_name: string }>;
  /** Set when this event is a native booking (came through the OS's
   *  booking system) — matched on the Outlook event id server-side. */
  booking: {
    appointment_id: string;
    kind: string;
    booked_via: string;
    type_name: string | null;
    household_id: string | null;
  } | null;
}

export interface UpcomingCalendarResponse {
  events: UpcomingCalendarEvent[];
  window_start: string;
  window_end: string;
  days: number;
}

// "My Week" digest (Weekly Digest button). Mirrors MyWeekResponse in
// shared/types/dashboard-home.ts.
export interface MyWeekClosing {
  id: string;
  kind: "opportunity" | "transaction";
  household_id: string;
  household_name: string;
  label: string;
  stage: string | null;
  company: "mortgage" | "insurance" | "planning" | "realty" | "financial" | null;
  expected_close_date: string;
  is_overdue: boolean;
}
export interface MyWeekTask {
  id: string;
  title: string;
  due_date: string;
  is_overdue: boolean;
  entity_type: string | null;
  entity_id: string | null;
  entity_label: string | null;
}
export interface MyWeekResponse {
  as_of: string;
  window_start: string;
  window_end: string;
  closings: MyWeekClosing[];
  tasks_due: MyWeekTask[];
}

// Joey Ellis program PR4 — mortgage family-view summary.
export interface MortgageLoanSummary {
  mortgage_loan_id: string;
  service_record_id: string;
  display_label: string | null;
  loan_type: string;
  loan_product: string | null;
  interest_rate: number | null;
  interest_rate_is_estimate: 0 | 1;
  rate_type: string | null;
  origination_amount: number | null;
  current_balance: number | null;
  ltv_percent: number | null;
  funded_date: string | null;
  property_label: string | null;
  estimated_value_cents: number | null;
  valued_at: string | null;
  current_ltv_percent: number | null;
  current_ltv_basis: "current_balance" | "origination" | null;
  gross_monthly_income: number | null;
  dti_percent: number | null;
  financials_as_of: string | null;
  /** What the county record says beside this loan (win-back). Only sent to
   *  users whose company scope includes Mortgage; null when there is
   *  nothing to say. */
  county_line?: CountyLine | null;
}
/** Coverage of the county record over funded traditional loans. */
export interface CountyCoverage {
  total: number;
  checked: number;
  last_pull: string | null;
  scope: "advisor" | "all";
}
/** The county-record sentence beside a Stewardship loan
 *  (shared/mortgage/county-line.ts). Warn = refinanced away or sold;
 *  quiet = the county confirms the loan is still ours. */
export interface CountyLine {
  standing: "still_ours" | "refinanced_away" | "sold";
  tone: "warn" | "quiet";
  sentence: string;
  as_of: string;
}
export interface MortgageSummaryResponse {
  loans: MortgageLoanSummary[];
}

// Joey program PR6 — rate estimate.
export interface MortgageRateEstimateInput {
  term_years: 15 | 30;
  ltv_percent: number;
  credit_band: "760_plus" | "720_759" | "680_719" | "640_679" | "below_640";
  occupancy?: "primary" | "second_home" | "investment";
  purpose?: "purchase" | "rate_term_refi" | "cash_out_refi";
}
export interface MortgageRateEstimateResponse {
  base_rate: number;
  base_term: 15 | 30;
  estimated_rate: number;
  adjustments: Array<{ label: string; bps: number }>;
  total_adjustment_bps: number;
  disclaimer: string;
  benchmark_as_of: string;
}
export interface MortgageFinancialsUpdateResponse {
  ok: true;
  mortgage_loan_id: string;
  gross_monthly_income: number | null;
  dti_percent: number | null;
  financials_as_of: string | null;
  changed: boolean;
}

// v1.x phase 11.16 (Tier B item 8) — Donna's dashboard aggregate.
export interface DonnaVisitorAttendee {
  email: string | null;
  name: string | null;
  contact_id: string | null;
  contact_display_name: string | null;
  household_id: string | null;
  household_display_name: string | null;
}

export interface DonnaTodayVisitor {
  event_id: string;
  start_iso: string;
  end_iso: string;
  is_all_day: boolean;
  subject: string;
  location: string;
  organizer_name: string | null;
  online_meeting_url: string | null;
  attendees: DonnaVisitorAttendee[];
  matched_households: Array<{ id: string; display_name: string }>;
}

export interface DonnaTask {
  id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  priority: "low" | "normal" | "high" | "urgent" | null;
  entity_type: string | null;
  entity_id: string | null;
  entity_label: string | null;
}

export interface DonnaActiveHousehold {
  household_id: string;
  display_name: string;
  last_activity_at: string;
  last_activity_summary: string;
  companies: string[];
  primary_contact_name: string | null;
}

export interface DonnaDashboardResponse {
  today_visitors: DonnaTodayVisitor[];
  my_tasks: DonnaTask[];
  active_households: DonnaActiveHousehold[];
  as_of: string;
}

// All-advisor "Coming In Today" schedule (Donna's #1 ask). Aggregated
// across every connected advisor's Outlook calendar.
export interface OfficeScheduleAdvisor {
  user_id: string;
  name: string;
  email: string;
}

export interface OfficeVisitorAttendee {
  email: string | null;
  name: string | null;
  contact_id: string | null;
  contact_display_name: string | null;
  household_id: string | null;
  household_display_name: string | null;
  // Room mailboxes (COLLAB, Conference Room) invited as resources.
  is_resource: boolean;
}

// ── Rooms (front-desk visibility + booking) ─────────────────────
export interface RoomScheduleBlock {
  start_iso: string;
  end_iso: string;
  status: "tentative" | "busy" | "oof" | "workingElsewhere" | "unknown";
  subject: string | null;
  // The meeting behind the block, when the room's own calendar could be
  // read (organization credential). Null for a free/busy-only block,
  // which can be shown but not cancelled from the board.
  booking?: RoomBookingInfo | null;
  // A Cancel booking click still inside its five-minute undo window.
  cancel_pending?: { id: string; execute_after: string } | null;
}

export interface RoomBookingInfo {
  event_id: string;
  organizer_name: string | null;
  organizer_email: string | null;
  organizer_outside: boolean;
  is_my_hold: boolean;
}

export interface TeamBirthdaysResponse {
  today: string;
  window_days: number;
  upcoming: Array<{ user_id: string; name: string; occurs_on: string; days_until: number }>;
  mine: { month: number; day: number } | null;
  prefs: { day_before: boolean; day_of: boolean };
  can_set_anyone: boolean;
  people: Array<{ user_id: string; name: string; month: number | null; day: number | null }>;
}

export interface RoomCancellationTicket {
  id: string;
  action: "cancel_hold" | "decline";
  status: string;
  execute_after: string;
}

export interface RoomDay {
  id: string;
  email: string;
  name: string;
  source: "auto" | "manual";
  blocks: RoomScheduleBlock[];
  now: { state: "free" | "busy"; until_iso: string | null } | null;
  has_overlap: boolean;
  availability_error: string | null;
}

export interface RoomScheduleResponse {
  rooms: RoomDay[];
  date: string;
  as_of: string;
  lookup_via: "caller" | "fallback" | null;
}

export interface OfficeVisitorHousehold {
  id: string;
  display_name: string;
  // Category-6 greeting context, shown on in-office visit cards.
  what_matters_most?: string | null;
  // Set when the association is a manual "Link to household".
  via_link?: boolean;
  // Set when the association came from a NAME FOUND IN THE EVENT TITLE
  // (shared/microsoft-graph/office-schedule.ts) rather than an actual
  // invitation or a human's deliberate link — a text guess, not a fact.
  via_title?: boolean;
}

// Mirrors shared/microsoft-graph/office-schedule.ts MeetingKind — the
// colour-coded kinds on the front-desk board (office green, phone red,
// video grey, off-site amber) plus the honest "we couldn't tell"
// fallback. A visitor whose kind Donna corrected carries the override in
// meeting_kind and the original guess in auto_meeting_kind.
export type OfficeMeetingKind = "in_office" | "phone" | "video" | "off_site" | "unknown";

// The subset Donna can actually set — "unknown" isn't a correction,
// it's the absence of one (clear the override instead).
export type OfficeMeetingKindOverride = Exclude<OfficeMeetingKind, "unknown">;

export interface OfficeVisitCheckin {
  visit_key: string;
  visit_date: string;
  status: "checked_in" | "done";
  checked_in_at: string | null;
  done_at: string | null;
}

// One advisor's own copy of this event — a merged visitor (a meeting
// two Stewardship advisors are both on) carries several. Cancel and
// reschedule act on a SPECIFIC advisor's copy, addressed by
// graph_event_id, so the drawer needs this even in the common
// one-advisor case.
export interface OfficeVisitorAdvisorEvent {
  advisor_user_id: string;
  graph_event_id: string;
  // Only the organizer's copy can be cancelled or moved for everyone —
  // an attendee's copy can only be declined, which this drawer doesn't
  // offer yet.
  is_organizer: boolean;
  // The OS appointment behind this event, when it was booked through
  // Stewardship OS rather than being the advisor's own Outlook entry.
  appointment_id: string | null;
}

export interface OfficeVisitor {
  key: string;
  start_iso: string;
  end_iso: string;
  is_all_day: boolean;
  subject: string;
  location: string;
  online_meeting_url: string | null;
  body_preview: string;
  web_link: string | null;
  meeting_kind: OfficeMeetingKind;
  // The system's automatic guess, always — even when Donna corrected
  // it, so the drawer can show what changed.
  auto_meeting_kind: OfficeMeetingKind;
  meeting_kind_overridden: boolean;
  is_private: boolean;
  advisors: OfficeScheduleAdvisor[];
  attendees: OfficeVisitorAttendee[];
  matched_households: OfficeVisitorHousehold[];
  // Front-desk check-in state for the served date; null = waiting.
  checkin: OfficeVisitCheckin | null;
  // Parallel to `advisors` — one entry per (advisor, event) pair merged
  // into this visitor, carrying what cancel/reschedule needs.
  advisor_events: OfficeVisitorAdvisorEvent[];
  // Which /book routing card the client picked, when this appointment
  // was booked through Stewardship OS (null for an advisor's own
  // Outlook entry, a personal-link/front-desk booking, or a since-
  // deleted card). See EventQuickView's callout.
  booking_need_slug: string | null;
  // The card's live label, resolved server-side. Falls back to
  // booking_need_slug itself if this is null but the slug isn't.
  booking_need_label: string | null;
}

export interface OfficeScheduleCoverage {
  connected_count: number;
  total_advisors: number;
  connected_advisors: OfficeScheduleAdvisor[];
  // Employees with no personal Office 365 connection whose calendar was
  // still read via the organization credential — on the board, readable.
  app_read_advisors: OfficeScheduleAdvisor[];
  // Employees whose calendar could not be read at all this load. Their
  // board columns say "no calendar access", never "free".
  missing_advisors: OfficeScheduleAdvisor[];
  errored_advisors: OfficeScheduleAdvisor[];
}

export interface OfficeScheduleResponse {
  visitors: OfficeVisitor[];
  coverage: OfficeScheduleCoverage;
  as_of: string;
  // Phoenix-local calendar date this schedule covers (YYYY-MM-DD).
  date: string;
  view: "clients" | "all";
  viewer_can_see_all: boolean;
}

// [Removed 2026-08-06] PostVisitFollowUp / PostVisitFollowUpResponse —
// the "Follow Up Today" queue. Follow-ups are tasks now; see the
// office-visit sweep and the "someone comes in for an in-office
// meeting" automation trigger.

// Onboarding cases (Donna's new-client workflow).
export type OnboardingCeaStatus = "not_sent" | "sent" | "signed";
export type OnboardingCaseStatus = "open" | "complete" | "cancelled";

export interface OnboardingStep {
  step_key: string;
  label: string;
  done: boolean;
  done_at: string | null;
  done_by_user_id: string | null;
  sort_order: number;
}

export interface OnboardingCase {
  id: string;
  household_id: string;
  household_display_name: string | null;
  company_id: string | null;
  status: OnboardingCaseStatus;
  cea_status: OnboardingCeaStatus;
  cea_sent_at: string | null;
  cea_signed_at: string | null;
  initiated_by_user_id: string | null;
  assigned_to_user_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  steps: OnboardingStep[];
}

// Monthly recurring checklist (Donna's Schwab tasks).
export interface MonthlyChecklistItem {
  key: string;
  label: string;
  done: boolean;
  completed_at: string | null;
}

export interface MonthlyChecklist {
  period: string;
  items: MonthlyChecklistItem[];
}

// v1.x phase 11.16 (Tier B item 11) — magic-link client upload.
export type ClientUploadTokenStatus = "active" | "expired" | "revoked";

export interface ClientUploadTokenSummary {
  id: string;
  entity_type: "household" | "contact";
  entity_id: string;
  created_by_user_id: string;
  recipient_label: string | null;
  prompt_note: string | null;
  expires_at: string;
  revoked_at: string | null;
  upload_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  status: ClientUploadTokenStatus;
}

export interface MintClientUploadTokenResponse {
  token: ClientUploadTokenSummary;
  // Returned ONCE at mint time — the raw token for the URL the
  // advisor will share. Never returned again.
  raw_token: string;
  path: string; // "/upload/{raw_token}"
}

export interface ClientUploadTokenLookupResponse {
  recipient_label: string | null;
  prompt_note: string | null;
  expires_at: string;
  upload_count: number;
}

export interface ClientUploadFileResponse {
  ok: true;
  filename: string;
  file_size_bytes: number;
}

// v1.x phase 11.16 (Tier B item 16) — voice brief widget. Reads
// either a daily brief for the focal date OR a weekly recap covering
// the 7 days ending on the focal date. The `text` field is the
// canonical body; the structured fields are for skim-readable UI
// rendering of the same content.
export interface VoiceBriefVisitor {
  start_iso: string;
  end_iso: string;
  is_all_day: boolean;
  subject: string;
  organizer_name: string | null;
  household_names: string[];
}

export interface VoiceBriefTask {
  title: string;
  due_date: string | null;
  entity_label: string | null;
}

export interface VoiceBriefHousehold {
  display_name: string;
  last_activity_summary: string;
  companies: string[];
}

// ── Advisor scheduling (Donna, 2026-08-04) ──────────────────────────
// Click an advisor on the front-desk board → their day, their booking
// links, and (front desk / admins) a form that books it here.

export interface AdvisorBusyBlock {
  start_iso: string;
  end_iso: string;
  /** "Busy" for events the advisor marked private in Outlook. */
  subject: string;
  is_private: boolean;
  is_all_day: boolean;
  location: string;
  web_link: string | null;
  /** False for events marked Free in Outlook — shown, but they don't
   *  close a slot (an all-day golf trip is not a full day of meetings). */
  blocks_availability: boolean;
  /** True for events marked "Working elsewhere" in Outlook — the advisor
   *  is working but not from the office, so phone and video still book
   *  and in-office does not. */
  works_elsewhere: boolean;
}

export interface AdvisorOpenSlot {
  start_iso: string;
  end_iso: string;
  minutes: number;
}

/** Why a day has no openings. Null when it has some. */
export type AdvisorDayClosedReason =
  | "not_accepting"
  | "day_off"
  | "too_soon"
  | "too_far_out"
  | "day_full"
  | "fully_booked";

export interface AdvisorAvailabilityDay {
  date: string;
  busy: AdvisorBusyBlock[];
  open: AdvisorOpenSlot[];
  /** Stretches the advisor is working remotely; in-office can't use them. */
  remote_windows: Array<{ start_iso: string; end_iso: string }>;
  closed_reason: AdvisorDayClosedReason | null;
  /** Plain-English version of closed_reason, ready to render. */
  closed_message: string | null;
}

/** One bookable window on one weekday, in Phoenix-local minutes. */
export interface AdvisorBookingHourWindow {
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
  start_minute: number;
  end_minute: number;
}

/** An advisor's own booking parameters (Settings → Booking rules). */
export interface AdvisorBookingRules {
  accepts_bookings: boolean;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  min_notice_hours: number;
  max_days_ahead: number;
  max_per_day: number | null;
  default_duration_minutes: number;
  allow_phone: boolean;
  allow_video: boolean;
  allow_in_office: boolean;
  front_desk_note: string | null;
  /** Email + bell alert when someone ELSE books this calendar. Default
   *  true — off is an opt-out, not the starting state. */
  notify_on_booking: boolean;
  /** Other Outlook calendars (subscribed/shared) whose events also
   *  block availability. Empty = main calendar only. */
  blocked_calendars: Array<{ id: string; name: string }>;
  /** Per-kind usual lengths; null = use default_duration_minutes. */
  kind_durations: Record<AppointmentKind, number | null>;
  hours: AdvisorBookingHourWindow[];
  /** False when this advisor has never saved the form — the values are
   *  the OS defaults, not their choices. */
  is_configured: boolean;
}

/** One of the user's Outlook calendars, from the live Graph list. */
export interface AdvisorOutlookCalendar {
  id: string;
  name: string;
  owner_name: string | null;
}

export interface AdvisorCalendarsResponse {
  /** False when the list couldn't be read (not configured, Graph down). */
  connected: boolean;
  calendars: AdvisorOutlookCalendar[];
  message?: string;
}

/** What the booking screen is told about an advisor's rules. */
export interface AdvisorBookingRulesSummary {
  accepts_bookings: boolean;
  allowed_kinds: AppointmentKind[];
  default_duration_minutes: number;
  /** Per-kind usual lengths; null = the general default applies. */
  kind_durations: Record<AppointmentKind, number | null>;
  min_notice_hours: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  max_per_day: number | null;
  max_days_ahead: number;
  front_desk_note: string | null;
  is_configured: boolean;
  summary_lines: string[];
}

export interface AdvisorBookingRulesResponse {
  advisor: { user_id: string; name: string | null; email: string };
  rules: AdvisorBookingRules;
  can_edit: boolean;
  summary_lines: string[];
}

// ── Native scheduling Phase B1 ──────────────────────────────────────

export interface AppointmentTypeRow {
  id: string;
  advisor_user_id: string;
  name: string;
  description: string | null;
  visibility: "public" | "link_only" | "internal";
  durations_minutes_json: string;
  allow_phone: number;
  allow_video: number;
  allow_in_office: number;
  buffer_before_minutes: number | null;
  buffer_after_minutes: number | null;
  min_notice_hours: number | null;
  max_days_ahead: number | null;
  max_per_day: number | null;
  requires_confirmation: number;
  post_booking_form_slug: string | null;
  creates_opportunity: number;
  opportunity_company_slug: string | null;
  opportunity_type: string | null;
  /** JSON array of service_type values; see AppointmentTypeInput. */
  opportunity_covered_service_types: string | null;
  sort_order: number;
  is_active: number;
}

export interface AppointmentTypeInput {
  id?: string;
  name: string;
  description: string | null;
  visibility: "public" | "link_only" | "internal";
  durations_minutes: number[];
  allow_phone: boolean;
  allow_video: boolean;
  allow_in_office: boolean;
  buffer_before_minutes: number | null;
  buffer_after_minutes: number | null;
  min_notice_hours: number | null;
  max_days_ahead: number | null;
  max_per_day: number | null;
  requires_confirmation: boolean;
  /** Slug of the form a client is asked to fill in AFTER booking this
   *  meeting (Grant, 2026-09-04). Null = book, confirm, done. */
  post_booking_form_slug: string | null;
  creates_opportunity: boolean;
  opportunity_company_slug: string | null;
  opportunity_type: string | null;
  /** Products that mean "they already have this, so booking is service
   *  work, not a sale". Empty = every booking opens an Opportunity. */
  opportunity_covered_service_types: string[];
  sort_order: number;
  is_active: boolean;
}

export interface AdvisorAppointmentTypesResponse {
  advisor: { user_id: string; name: string | null; email: string };
  types: AppointmentTypeRow[];
  can_edit: boolean;
}

export interface AdvisorBookingProfileResponse {
  advisor: { user_id: string; name: string | null; email: string };
  profile: {
    user_id: string;
    public_bio: string | null;
    title: string | null;
    serves_needs: string[];
    audience: "any" | "new" | "existing";
    is_configured: boolean;
  };
  available_needs: Array<{ slug: string; label: string }>;
  can_edit: boolean;
}

export type BookingQuestionType = "text" | "single_choice" | "multi_choice";

export interface BookingRoutingNeed {
  id?: string;
  slug: string;
  label: string;
  description: string | null;
  clarifying_question: string | null;
  /** How the question is asked; choice types carry their options. */
  question_type?: BookingQuestionType;
  question_required?: boolean | number;
  /** On load: the stored JSON string; on save: the option list. */
  question_options?: string | string[] | null;
  /** Card-level auto-Opportunity hook — a booking through this card
   *  opens an Opportunity for the routed advisor. A type-level hook
   *  still wins when both are set. */
  creates_opportunity?: boolean | number;
  opportunity_company_slug?: string | null;
  opportunity_type?: string | null;
  /** Products that mean "already covered, so no new Opportunity". On load:
   *  the stored JSON string; on save: the list. Empty = never skip. */
  opportunity_covered_service_types?: string | string[] | null;
  sort_order: number;
  is_published: boolean | number;
}

export interface BookingRoutingRuleView {
  id?: string;
  need_slug: string;
  answer_value: string | null;
  audience: "any" | "new" | "existing";
  selection_strategy: "single" | "choice" | "priority" | "round_robin";
  pool: Array<{ advisor_user_id: string; priority: number }>;
}

export interface BookingRoutingCoverageProblem {
  need_slug: string;
  answer_value: string | null;
  audience: "any" | "new" | "existing";
  code: "no_rule" | "empty_pool" | "reserved_strategy" | "orphaned_answer";
}

export interface BookingRoutingResponse {
  needs: BookingRoutingNeed[];
  rules: BookingRoutingRuleView[];
  coverage: { problems: BookingRoutingCoverageProblem[]; checked_needs: number };
  advisors: Array<{ id: string; name: string | null; email: string; bookable: number }>;
}

export interface BookingRoutingSaveInput {
  needs: BookingRoutingNeed[];
  rules: BookingRoutingRuleView[];
}

export type AdvisorBookingKind = "phone" | "video" | "in_office" | "other";

/** Whose service this is, relative to the advisor the screen is showing.
 *  "unknown" means Microsoft never told us who staffs it — kept visible,
 *  never folded into "other" (a missing signal isn't a negative one). */
export type AdvisorBookingServiceOwnership = "mine" | "other" | "unknown";

export interface AdvisorBookingService {
  id: string;
  name: string;
  duration_minutes: number | null;
  kind: AdvisorBookingKind;
  url: string | null;
  ownership: AdvisorBookingServiceOwnership;
}

export interface AdvisorBookingPage {
  page_id: string;
  page_name: string | null;
  page_url: string | null;
  last_synced_at: string | null;
  /** True when this page is staffed by more than one employee — a
   *  shared firm page (e.g. "Stewardship") rather than one person's
   *  own page. */
  is_shared: boolean;
  services: AdvisorBookingService[];
}

export interface AdvisorScheduleResponse {
  advisor: { user_id: string; name: string | null; email: string };
  date: string;
  days: AdvisorAvailabilityDay[];
  booking_pages: AdvisorBookingPage[];
  can_book: boolean;
  /** The advisor's own parameters, so the screen can explain a closed
   *  day and offer only the appointment kinds they take. */
  booking_rules: AdvisorBookingRulesSummary;
  office_hours: { start_hour: number; end_hour: number };
  /** Plain-English reason the calendar couldn't be read; null when fine.
   *  Never render an empty day as "free" when this is set. */
  calendar_error: string | null;
  /** Checked blocking calendars that couldn't be read this load, by name.
   *  Openings are real but may include time these would have blocked, so
   *  the screen warns rather than presenting them as vetted. */
  unreadable_calendars?: string[];
  as_of: string;
}

export type AppointmentKind = "phone" | "video" | "in_office";

// One row of the Appointments list (CALENDAR → Appointments). Mirrors
// shared/db/appointments.ts AppointmentListItem.
export type AppointmentListItemStatus =
  | "scheduled"
  | "rescheduled"
  | "cancelled"
  | "completed"
  | "no_show";

export interface AppointmentListItem {
  id: string;
  advisor_user_id: string;
  advisor_name: string | null;
  kind: string;
  status: AppointmentListItemStatus;
  start_at: string;
  end_at: string;
  subject: string;
  /** The client's own free-text "what would you like to talk about". */
  notes: string | null;
  location: string | null;
  booked_via: "front_desk" | "self" | "personal_link" | "public" | "bookings_sync";
  /** The live Outlook event id — what Cancel/Reschedule address. Null
   *  for a row whose event was never attached or already cleared. */
  graph_event_id: string | null;
  graph_web_link: string | null;
  type_name: string | null;
  household_id: string | null;
  primary_contact_id: string | null;
  primary_contact_name: string | null;
  opportunity_id: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  /** The form this booking's meeting type asks for — null if it asks
   *  none, or asked one before this column existed (2026-09-06). */
  prep_form: AppointmentPrepForm | null;
}

export interface AppointmentPrepForm {
  slug: string;
  title: string;
  /** Null until the client has answered. */
  submitted_at: string | null;
  /** How many questions the template asks — 0 if it can't be resolved. */
  question_count: number;
  /** Up to 3 answered fields, template order, for the inline preview. */
  top_answers: Array<{ label: string; value: string }>;
  /** The form_submissions.id, once submitted — opens the shared Form
   *  Results view directly. Null until answered. */
  submission_id: string | null;
  /** Health-classified form: `top_answers` is always empty here; the
   *  answers open on Form Results, behind the health tier. */
  health_classified?: boolean;
}

export interface AppointmentsNeedsAttention {
  /** Upcoming, in the main window, but nobody could be matched to it. */
  unmatched: AppointmentListItem[];
  /** Cancelled within the lookback window — a rebook decision nobody's
   *  made yet. */
  cancelled_recent: AppointmentListItem[];
  /** Asked a prep form and it hasn't come back yet. */
  prep_form_missing: AppointmentListItem[];
}

export interface AppointmentsListResponse {
  /** The scope actually in effect — the header renders THIS, never the
   *  scope that was asked for (the header never lies). */
  scope: "mine" | "office";
  can_see_office: boolean;
  from: string;
  to: string;
  days: number;
  appointments: AppointmentListItem[];
  past: AppointmentListItem[];
  past_from: string;
  past_to: string;
  past_days: number;
  needs_attention: AppointmentsNeedsAttention;
}

export interface CreateAdvisorAppointmentInput {
  kind: AppointmentKind;
  startIso: string;
  endIso: string;
  subject: string;
  notes?: string;
  location?: string;
  attendees?: string[];
  household_id?: string;
  /** The PERSON the appointment is for — the contact picked in the form
   *  (2026-08-15 audit: without this the server filed every booking
   *  against the household PRIMARY, so booking the spouse logged the
   *  appointment under the wrong person). Must belong to household_id. */
  contact_id?: string;
  /** An optional +1 ("bring the spouse") — same field the public
   *  booking page carries (2026-09-09). */
  guest_name?: string;
  guest_email?: string;
}

export interface CreateAdvisorAppointmentResponse {
  event_id: string;
  web_link: string | null;
  online_meeting_url: string | null;
  start_iso: string;
  end_iso: string;
  subject: string;
  advisor: { user_id: string; name: string | null; email: string };
  household_id: string | null;
}

export interface HouseholdAttendee {
  contact_id: string;
  name: string;
  email: string | null;
  is_primary: boolean;
}

export interface VoiceBriefResponse {
  kind: "daily" | "weekly";
  date_iso: string;
  range_start_iso: string;
  range_end_iso: string;
  text: string;
  visitors: VoiceBriefVisitor[];
  tasks: VoiceBriefTask[];
  active_households: VoiceBriefHousehold[];
}

// v1.x phase 11.16 (Tier B item 13) — OneDrive browse + copy.
export interface OneDriveItem {
  id: string;
  name: string;
  isFolder: boolean;
  size: number | null;
  mimeType: string | null;
  lastModifiedAt: string | null;
  webUrl: string | null;
  parentPath: string | null;
  // The server strips downloadUrl from browse responses (it's only
  // used server-side during the copy step). Keep the type so the
  // shape stays identical to the shared OneDriveItem.
  downloadUrl?: string | null;
}

export interface OneDriveBrowseResponse {
  items: OneDriveItem[];
  nextLink: string | null;
  mode: "browse" | "search";
}

export type OneDriveCopyEntityType =
  | "household"
  | "contact"
  | "transaction"
  | "opportunity"
  | "service_record"
  | "compliance_checklist_instance";

export interface OneDriveCopyResponse {
  document: DocumentRec;
  source: "onedrive";
}

// The saved copy of one logged email (GET /api/email/activities/:id).
export interface SavedEmail {
  activity_id: string;
  occurred_at: string;
  direction: "outbound" | "inbound" | null;
  sent_by_user_id: string | null;
  subject: string | null;
  from_address: string;
  to_addresses: string[];
  cc_addresses: string[];
  graph_message_id: string | null;
  body: { content_type: "html" | "text"; content: string } | null;
}

// v1.x phase 11.5 — email history on records.
export interface EmailHistoryItem {
  activity_id: string;
  occurred_at: string;
  direction: "outbound" | "inbound" | null;
  user_id: string | null;
  subject: string | null;
  from_address: string;
  to_addresses: string[];
  preview: string;
  graph_message_id: string | null;
}

// v1.x phase 11 Flow A — Stewardship-linked tasks. Local rows
// from manual_tasks that ALSO mirror into the user's "Stewardship OS"
// Microsoft To Do list. The frontend reads the same shape returned
// by the existing dashboard-baker / compliance routes, plus the
// new sync-status fields from migration 0043.
export interface StewardshipTask {
  id: string;
  title: string;
  description: string | null;
  company: "mortgage" | "insurance" | "planning" | "realty" | "financial" | "all" | null;
  due_date: string | null;
  priority: "low" | "normal" | "high" | "urgent" | null;
  created_by_user_id: string;
  // Creator's display name, for the "from Brandon" delegation chip.
  // Optional so the UI tolerates a Worker that predates the field.
  created_by_name?: string | null;
  assigned_to_user_id: string;
  completed_at: string | null;
  completed_by_user_id: string | null;
  completion_notes: string | null;
  created_at: string;
  updated_at: string;
  entity_type:
    | "contact"
    | "household"
    | "opportunity"
    | "transaction"
    | "service_record"
    | "rhythm_instance"
    | null;
  entity_id: string | null;
  // Sync state machine (migration 0043).
  graph_task_id: string | null;
  graph_list_id: string | null;
  sync_status: "local_only" | "pending_push" | "synced" | "sync_error";
  last_synced_at: string | null;
  last_sync_error: string | null;
  last_modified_by_source: "os" | "graph" | null;
  // v1.x phase 11.14 follow-up — sub-task list. Null when none.
  steps: ManualTaskStep[] | null;
  // v1.x phase 11.17 polish round 5 (migration 0052) — per-task
  // To Do list override. Null = use connection-level default.
  target_list_id: string | null;
  /** Row 8.11: the family send a mail or card task came from; the Mail
   *  work list is where it is pressed Mailed. Null for every other task. */
  series_family_send_id?: string | null;
}

export interface ManualTaskStep {
  id: string;
  title: string;
  // ISO 8601 UTC timestamp when the step was checked off; null
  // while open. UI strikes through completed steps and shows them
  // at the bottom of the list.
  completed_at: string | null;
  // Joey subtask round-trip (migration 0158) — the matching Microsoft
  // To Do checklistItem id. The server owns this mapping; the client
  // echoes it back on PATCH so steps update (not duplicate) on the
  // next Graph push. Null until the step has synced to Graph.
  graph_checklist_item_id?: string | null;
}

export interface CreateStewardshipTaskInput {
  title: string;
  description?: string | null;
  due_date?: string | null;
  priority?: "low" | "normal" | "high" | "urgent" | null;
  entity_type?: StewardshipTask["entity_type"];
  entity_id?: string | null;
  company?: StewardshipTask["company"];
  // v1.x phase 11.14 — cross-user task assignment. When omitted,
  // the task is assigned to the creator (default behavior). When
  // set, the task lands in the assignee's "Stewardship OS" To Do
  // list — they see it on /tasks; the creator does not, by default.
  // Server validates the id is an active employee.
  assigned_to_user_id?: string | null;
  // v1.x phase 11.14 follow-up — sub-task list. Each step is
  // { id?, title, completed_at? }. id is generated server-side
  // when omitted.
  steps?: Array<{
    id?: string;
    title: string;
    completed_at?: string | null;
    graph_checklist_item_id?: string | null;
  }> | null;
  // v1.x phase 11.17 polish round 5 — per-task To Do list pick.
  // When set, the Graph push targets this list id verbatim. When
  // omitted, push falls back to the connection-level default
  // (the bootstrapped "Stewardship OS" list).
  list_id?: string | null;
}

// One row in an automation-builder picker whose options come from the
// server (2026-08-16). `group` renders as an optgroup; `inactive` marks
// a retired row that is still SHOWN — a rule pinned to it must look
// wrong rather than go quietly inert.
export interface BuilderPickerOption {
  value: string;
  label: string;
  group?: string;
  inactive?: boolean;
  /** Appointment-type options only (2026-09-04): the form this type asks
   *  the client to fill out AFTER booking, so the email builder can say
   *  "after they book, they're asked to fill out the X form" next to a
   *  booking link — the step Brian couldn't find. Null = book, done. */
  post_booking_form_slug?: string | null;
  post_booking_form_title?: string | null;
}

// v1.x phase 11.14 — assignable employees for the task picker.
export interface BulkAssignPlanOwnerResult {
  bulk_action_id: string;
  plans_changed: number;
  plans_unchanged: number;
  skipped: { plan_id: string; reason: string }[];
}

export interface AssignableUser {
  id: string;
  email: string;
  name: string | null;
  role_slug: string | null;
}

/** One row of the firm-wide appointment-type directory — enough to
 *  describe a meeting ("60 min · phone, video") and who offers it, so
 *  the form builder can connect to one by name. */
export interface AppointmentTypeDirectoryEntry {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  visibility: "public" | "link_only" | "internal";
  durations_minutes: number[];
  allow_phone: boolean;
  allow_video: boolean;
  allow_in_office: boolean;
  post_booking_form_slug: string | null;
  advisor_user_id: string;
  advisor_name: string;
  advisor_email: string | null;
  /** The advisor's company scope; null = every company. */
  company_slugs: string[] | null;
}

export interface ConnectFormToMeetingResult {
  ok: true;
  updated_advisor_ids: string[];
  missing_advisor_ids: string[];
  skipped_advisor_ids: string[];
}

export interface CreateSharedMeetingInput {
  advisor_user_ids: string[];
  name: string;
  description: string | null;
  visibility: AppointmentTypeInput["visibility"];
  durations_minutes: number[];
  allow_phone: boolean;
  allow_video: boolean;
  allow_in_office: boolean;
  post_booking_form_slug: string | null;
}

/** One teammate in the booking-page switcher (admins + front desk).
 *  Employee names only — no client data, no availability. */
export interface BookingDirectoryEntry {
  user_id: string;
  name: string;
  email: string | null;
  /** Has appointment types or a saved booking profile, i.e. a page
   *  worth opening. Unset people are still listed, just sorted after. */
  is_set_up: boolean;
}

// Where an existing opportunity came from — the detail page's
// referral-source row, including any ambassador credited on it.
export interface OpportunitySource {
  opportunity_id: string;
  source_type: string | null;
  source_detail: string | null;
  internal_referrer_user_id: string | null;
  internal_referrer_name: string | null;
  internal_referred_from_company_slug: string | null;
  internal_referred_from_company_name: string | null;
  ambassador_referral: {
    id: string;
    ambassador_user_id: string;
    ambassador_name: string | null;
    /** 'ambassador_form' = she filed it; 'advisor_designated' = staff credited her. */
    origin: string;
    created_at: string;
  } | null;
  /** Local Services only: what Google says about the lead that made this Opportunity (read-only, labeled as Google's). */
  google_lead?: GoogleLeadOnOpportunity | null;
}

export type { GoogleLeadOnOpportunity } from "@shared/ads/local-services-state";

// An active Ambassador Agent, for the "which ambassador sent them?"
// picker that appears when an employee designates a referral (migration 20260814223351).
export interface AmbassadorOption {
  id: string;
  name: string | null;
  email: string;
}

// Cross-company referral tracking (2026-07-09) — one of Stewardship's
// 6 companies (5 client-facing + Stewardship Productions).
export interface CompanyOption {
  id: string;
  slug: string;
  display_name: string;
  is_support_entity: boolean;
}

// Admin — Shared Mailbox Access page.
export interface SharedMailboxGrantRow {
  grant_id: string;
  employee_user_id: string;
  employee_name: string | null;
  employee_email: string;
  granted_by_name: string | null;
  created_at: string;
}

export interface OutlookCategoryRow {
  id: string;
  displayName: string;
  /** Graph preset ("preset0".."preset24") or "none". */
  color: string;
}

export interface SharedMailboxAccessRow {
  upn: string;
  /** True for a mailbox an admin connected (can be disconnected here);
   *  false for the built-in ones that live in code. */
  connected_by_admin: boolean;
  display_name: string;
  description: string;
  default_audience: string;
  grants: SharedMailboxGrantRow[];
}

export interface UpdateStewardshipTaskPatch {
  title?: string;
  description?: string | null;
  due_date?: string | null;
  priority?: "low" | "normal" | "high" | "urgent" | null;
  // v1.x phase 11.14 follow-up — entity link, steps, reassignment
  // are now editable on the existing task. Pass null to clear; omit
  // to leave unchanged.
  entity_type?: StewardshipTask["entity_type"];
  entity_id?: string | null;
  steps?: Array<{
    id?: string;
    title: string;
    completed_at?: string | null;
    graph_checklist_item_id?: string | null;
  }> | null;
  assigned_to_user_id?: string;
}

// v1.x phase 11.14 follow-up — unified record search for the LINK TO
// picker on the task modal. S2 PR 1 widened the entity set (service
// records + Ongoing Care plans) and added confidence + match_reason.
export interface RecordSearchResult {
  entity_type:
    | "household"
    | "contact"
    | "opportunity"
    | "transaction"
    | "vendor"
    | "employer"
    | "service_record"
    | "rhythm_instance"
    // Content arms (gs follow-ons B2) — opt-in via types=.
    | "note"
    | "logged_email";
  id: string;
  label: string;
  sub_label: string | null;
  confidence: number;
  // Context line explaining the hit ("Matched spouse: Mary Smith");
  // null when self-evident from label/sub_label.
  match_reason: string | null;
  // True for a non-live record kept findable (archived / replaced /
  // cancelled / completed service record or care plan). The API sorts
  // these below every live result; render them muted / badged. Absent
  // = live.
  inactive?: boolean;
  // Content rows only: [match]…[/match]-marked snippet + an explicit
  // href (owning record's page + highlight anchor).
  snippet?: string;
  href?: string;
}

// gs follow-ons A1 — vendor reference surface (vendors are NOT
// contacts per doctrine; no client-style features).
// ── Employers (employer groups) ──────────────────────────────────────
export type EmployerPersonRole = "owner" | "hr_contact" | "benefits_admin" | "payroll_contact" | "other";

export interface EmployerListItem {
  id: string;
  display_name: string;
  employee_count: number | null;
  industry: string | null;
  city: string | null;
  state: string | null;
  primary_company_slug: string | null;
  relationships: { company_slug: string; relationship_state: string }[];
}

export interface EmployerListResponse {
  employers: EmployerListItem[];
  total: number;
  scope: { mode: "mine" | "all"; companies: string[] | null };
  can_widen: boolean;
}

export interface EmployerCreateInput {
  display_name: string;
  legal_name?: string | null;
  primary_company_slug?: string;
  employee_count?: number | null;
  industry?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

export interface EmployerPerson {
  principal_id: string;
  contact_id: string;
  household_id: string;
  role: string;
  display_name: string;
  /** Their own (personal) email and phone, from their contact record. */
  email: string | null;
  phone: string | null;
  /** How we reach them at this employer; null when not recorded. */
  work_email: string | null;
  work_phone: string | null;
  job_title: string | null;
  /** 0 until work details are first saved. */
  work_details_version: number;
  started_at: string;
}

export interface EmployerRecord {
  kind: "opportunity" | "transaction";
  id: string;
  record_type: string;
  /** What a Processing record delivers; null on an opportunity. */
  service_type: string | null;
  stage: string;
  outcome: string | null;
  company_slug: string;
  owner_user_id: string | null;
  owner_name: string | null;
  expected_close_date: string | null;
  opened_at: string;
}

export interface EmployerRelationship {
  id: string;
  company_slug: string;
  relationship_state: string;
  fit_assessment: string;
  current_advisor_user_id: string | null;
  client_since_this_company: string | null;
}

export interface EmployerDetail {
  id: string;
  display_name: string;
  legal_name: string | null;
  employee_count: number | null;
  industry: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  primary_company_slug: string | null;
  version_number: number;
  created_at: string;
  anchor_household_id: string | null;
  people: EmployerPerson[];
  relationships: EmployerRelationship[];
  records: EmployerRecord[];
  group_plans: GroupPlan[];
}

export interface EmployerDetailResponse {
  employer: EmployerDetail;
  can_open_group_sale: boolean;
  /** The companies whose group plans this viewer may edit. */
  editable_group_plan_companies: string[];
}

export type GroupPlanFunding = "employer_paid" | "shared" | "voluntary";
export type GroupPlanErisaReview = "yes" | "no" | "not_determined";
/** The BuddyIns service a group uses (shared/employers/enrollment-service.ts). */
export type GroupPlanEnrollmentService = "full_service" | "split_80_20" | "self_service";

/** An employer's contract with a carrier (shared/db/group-plans.ts). */
export interface GroupPlan {
  id: string;
  business_entity_id: string;
  company_id: string;
  company_slug: string;
  line: string;
  status: "active" | "ended";
  ended_on: string | null;
  ended_reason: string | null;
  carrier_vendor_id: string | null;
  carrier_name: string | null;
  product_name: string | null;
  master_policy_number: string | null;
  enrollment_opens_on: string | null;
  enrollment_closes_on: string | null;
  effective_date: string | null;
  anniversary_date: string | null;
  funding_arrangement: GroupPlanFunding | null;
  erisa_review: GroupPlanErisaReview;
  compliance_duties: string | null;
  compliance_owner_user_id: string | null;
  compliance_owner_name: string | null;
  enrollment_platform: string;
  enrollment_platform_url: string | null;
  enrollment_service: GroupPlanEnrollmentService | null;
  source_transaction_id: string | null;
  version_number: number;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface GroupPlanEdit {
  expected_version_number: number;
  carrier_name?: string | null;
  product_name?: string | null;
  master_policy_number?: string | null;
  enrollment_opens_on?: string | null;
  enrollment_closes_on?: string | null;
  effective_date?: string | null;
  anniversary_date?: string | null;
  funding_arrangement?: GroupPlanFunding | null;
  erisa_review?: GroupPlanErisaReview;
  compliance_duties?: string | null;
  compliance_owner_user_id?: string | null;
  enrollment_platform?: string;
  enrollment_platform_url?: string | null;
  enrollment_service?: GroupPlanEnrollmentService | null;
}

export interface GroupPlanMember {
  id: string;
  contact_id: string;
  display_name: string;
  household_id: string;
  household_display_name: string | null;
  service_record_id: string | null;
  certificate_number: string | null;
  coverage: string | null;
  monthly_premium: number | null;
  effective_date: string | null;
  status: "enrolled" | "portable" | "ended";
  ended_on: string | null;
  ended_reason: string | null;
  version_number: number;
  came_in_as: "new_client" | "existing_client";
  coverage_role: "employee" | "spouse" | "dependent";
  bulk_action_id: string | null;
  created_at: string;
}

/** shared/db/group-plan-commissions.ts */
export interface GroupPlanCommissionAmount {
  expected_cents: number;
  unknown: number;
  received_cents: number;
}

export interface GroupPlanCommissions {
  certificates: {
    member_id: string;
    display_name: string;
    household_id: string;
    member_status: "enrolled" | "portable" | "ended";
    service_record_id: string;
    certificate_number: string | null;
    first_year: GroupPlanCommissionAmount | null;
    renewals: GroupPlanCommissionAmount & { years: number };
    received_cents: number;
    outstanding_cents: number;
  }[];
  totals: {
    first_year_expected_cents: number;
    renewal_expected_cents: number;
    received_cents: number;
    outstanding_cents: number;
    unknown: number;
    not_written_yet: number;
  };
}

export interface GroupPlanRoster {
  members: GroupPlanMember[];
  total: number;
  /** Still enrolled through the employer (portable and ended are not). */
  enrolled: number;
  monthly_premium_total: number;
  with_premium: number;
  undoable_bulk_action_id: string | null;
  undoable_until: string | null;
}

export interface BringInCandidate {
  candidate: {
    contact_id: string;
    household_id: string;
    household_display_name: string | null;
    first_name: string | null;
    preferred_name: string | null;
    last_name: string | null;
    birthday: string | null;
    planning_state: string | null;
  };
  signals: string[];
}

export interface BringInPreviewRow {
  row: EnrollmentRowInput;
  match: {
    row_number: number;
    bucket: "existing" | "new" | "needs_look" | "already_enrolled";
    candidates: BringInCandidate[];
    why: string;
  };
}

export type BringInDecision =
  | { kind: "existing"; contact_id: string }
  | { kind: "new"; join_household_id?: string | null };

export interface BringInRowResult {
  row_number: number;
  outcome: "linked" | "created" | "already_enrolled" | "refused";
  contact_id: string | null;
  household_id: string | null;
  member_id: string | null;
  message: string | null;
}

export interface GroupPlanHistoryEntry {
  version_number: number;
  source: "created" | "manual_edit" | "rollback";
  reason: string | null;
  created_at: string;
  created_by_name: string | null;
  changed_fields: string[];
}

export interface VendorSummary {
  id: string;
  slug: string | null;
  display_name: string;
  type: string;
  portal_url: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  primary_contact_phone: string | null;
  primary_contact_address?: string | null;
  relationship_owner_user_id: string | null;
  notes: string | null;
  /** 'approved' | 'pending' | 'not_approved'; null = never assessed. */
  approval_status: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** One account executive at a lender, as the approved-lender roster shows them. */
export interface LenderAccountExecutive {
  contact_id: string;
  display_name: string;
  email: string | null;
  phone_primary: string | null;
  phone_secondary: string | null;
}

/** GET /api/comp-schedules/:company. */
export interface CompSchedulesResponse {
  company: string;
  can_edit: boolean;
  /** The people named for this company's comp plans (system admins edit too). */
  editors: string[];
  cards: CompScheduleCard[];
  quickbooks_accounts: Array<{ qb_id: string; name: string }>;
}

/** GET /api/planning-deposits. */
export interface PlanningDepositsResponse {
  can_place: boolean;
  matching_live_from: string | null;
  deposits: (PlanningDeposit & {
    placements: { cash_allocation_id: string; obligation_id: string; amount_cents: number; placed_by: string | null; created_at: string; undone_at: string | null }[];
  })[];
  carrier_months: CarrierMonthRow[];
}

/** GET /api/comp-schedules/:company/carriers. */
export interface PlanningStatementsResponse {
  carrier: { id: string; name: string };
  format: PlanningStatementFormat;
  format_label: string;
  can_upload: boolean;
  statements: PlanningStatementView[];
}

export interface PlanningStatementDraft {
  sha256: string;
  file_name: string;
  read_ok: boolean;
  message: string | null;
  draft: Record<string, unknown>;
}

export interface PlanningStatementSaved {
  status: "saved" | "already_uploaded";
  batch_id: string;
  lines?: { line_number: number; policy_number: string; outcome: string }[];
  statements: PlanningStatementView[];
}

export interface CarriersResponse {
  company: "insurance" | "planning";
  rows: CarrierRow[];
  rates_visible: boolean;
  editors: string[];
}

/** A new version as the comp plan editor sends it. */
export interface CompVersionDraft {
  effective_from: string;
  status: "active" | "ended";
  calculator: CompCalculatorKey | null;
  /** The calculator's terms; a Planning rate's terms carry nested year bands. */
  terms: Record<string, unknown>;
  rate_certainty: "contract" | "default_estimate";
  qb_account_qb_id: string | null;
  source_kind: "payer_comp_plan" | "rate_list" | "company_decision" | "other";
  source_note: string;
  source_document_id?: string | null;
  bonus_program: "not_recorded" | "none" | "recorded";
  bonus_program_note: string | null;
  reason: string;
}

/** One company credential as a screen sees it: never the secret. */
export interface CompanyCredentialMeta {
  id: string;
  credential_key: string;
  account_ref: string | null;
  account_name: string | null;
  source: "email" | "manual" | "reused";
  effective_at: string;
  mailbox_user_name: string | null;
  set_by_user_name: string | null;
  reused_from_id: string | null;
}

export interface CompanyCredentialStatus {
  current: CompanyCredentialMeta | null;
  history: CompanyCredentialMeta[];
}

export interface ApprovedLender {
  id: string;
  slug: string | null;
  display_name: string;
  portal_url: string | null;
  notes: string | null;
  account_executives: LenderAccountExecutive[];
  /** What the lender offers; empty means not tagged yet. */
  programs: LenderProgram[];
}

/** One lender as the roster editor sees it after a change. */
export interface RosterLender {
  id: string;
  display_name: string;
  portal_url: string | null;
  notes: string | null;
  approval_status: string | null;
  type: string;
  is_active: number;
}

/** The person an email already belongs to, on a 409 from adding a rep. */
export interface RosterExistingContact {
  id: string;
  display_name: string;
  contact_type: string;
  archived: boolean;
}

export interface CenterOfInfluenceFacts {
  last_touch_at: string | null;
  last_referral_from_at: string | null;
  referrals_from_count: number;
  last_referred_to_at: string | null;
  reciprocal_cadence_months: number | null;
  reciprocal_due: boolean;
}

/** The fields a person may type on a vendor; present keys change, null clears. */
export interface VendorDetailsPatch {
  portal_url?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  primary_contact_address?: string | null;
  relationship_owner_user_id?: string | null;
  notes?: string | null;
}

export interface VendorDetailResponse {
  vendor: VendorSummary;
  owner_name: string | null;
  linked_contacts: Array<{
    id: string;
    display_name: string;
    household_id: string | null;
  }>;
  /** Marketing Hub row 4.1: only for a vendor typed referral partner. */
  center_of_influence: CenterOfInfluenceFacts | null;
  /** Duplicates merged into this vendor, newest first (vendor merge). */
  merged_here?: MergedVendorSummary[];
}

export interface MergedVendorSummary {
  id: string;
  display_name: string;
  merged_at: string;
  bulk_action_id: string | null;
  can_undo: boolean;
}

/** On a 404 for a vendor that was merged away (ApiError.data.merged_away). */
export interface MergedAwayVendor {
  id: string;
  display_name: string;
  merged_at: string;
  merged_into: { id: string; display_name: string };
  now_at: { id: string; display_name: string };
  bulk_action_id: string | null;
  can_undo: boolean;
}

export interface VendorMergePreview {
  duplicate: { id: string; display_name: string; type: string };
  keeper: { id: string; display_name: string; type: string };
  moves: Array<{ table: string; column: string; label: string; rows: number }>;
  total_rows: number;
  fills: Array<{ field: string; label: string; value: string }>;
  blockers: string[];
  needs_type_to_confirm: boolean;
}

export interface VendorMergeResult {
  ok: true;
  bulk_action_id: string;
  rows_moved: number;
  versions_written: number;
  fills: number;
  duplicate_archived: boolean;
  problems: string[];
  keeper: { id: string; display_name: string; type: string };
  duplicate: { id: string; display_name: string; type: string };
}

// S2 PR 2 — one recently-viewed record (palette empty state).
// Recents are RECORDS only — content kinds (note/logged_email) are
// never visited as pages, so this union stays the 7 record types.
export interface RecentVisit {
  entity_type:
    | "household"
    | "contact"
    | "opportunity"
    | "transaction"
    | "vendor"
    | "service_record"
    | "rhythm_instance";
  entity_id: string;
  label: string;
  sub_label: string | null;
  visited_at: string;
}

// The entity types a Stewardship Task may link to — mirrors the
// manual_tasks.entity_type CHECK constraint (migration 0035).
// Vendors are searchable but NOT linkable; the task modals pass this
// list as searchRecords' types filter so a vendor row can never be
// picked into a link the INSERT would reject.
export const TASK_LINKABLE_RECORD_TYPES: ReadonlyArray<RecordSearchResult["entity_type"]> = [
  "contact",
  "household",
  "opportunity",
  "transaction",
  "service_record",
  "rhythm_instance",
];

// Phase 10: AI compose
export interface AiPreferencesResponse {
  custom_instructions: string | null;
  model_preference: "sonnet" | "opus";
  suggestions_accepted_count: number;
  suggestions_rejected_count: number;
}

export type ComposeMode = "draft" | "rewrite" | "shorten" | "tone_warmer" | "tone_formal" | "reply";

// One message inside a reply thread. Trimmed by the client + capped
// again on the server (defense-in-depth) before reaching Anthropic.
export interface ComposeThreadMessage {
  fromName: string | null;
  fromEmail: string | null;
  receivedDateTime: string | null;
  bodyExcerpt: string;
}

export interface ComposeAssistInput {
  mode: ComposeMode;
  instruction: string;
  draft?: string | null;
  context?: {
    recipientName?: string | null;
    recipientEmail?: string | null;
    householdName?: string | null;
    // v1.x phase 11.11: additional recipients beyond the primary
    // (multi-recipient compose). Server caps to 5 entries.
    additionalRecipients?: Array<{
      name?: string | null;
      email?: string | null;
      householdName?: string | null;
    }>;
    inReplyToSubject?: string | null;
    inReplyToBodyPreview?: string | null;
    // v1.x: prior messages in the same conversation, oldest first.
    // The host fetches via /api/email/conversations/:id/messages
    // and trims bodies before sending. Server caps to 5 messages
    // and 600 chars per body even if the client sends more.
    threadMessages?: ComposeThreadMessage[];
  };
}

export interface ComposeAssistResponse {
  suggestion: string;
  tokens_used: {
    input: number;
    cachedInput: number;
    output: number;
    total: number;
  };
  model: string;
}

export interface SubjectSuggestInput {
  body_text: string;
  attachment_names: string[];
  // The record's own address, when composing from a Processing
  // (transaction) record — the model doesn't invent this, it's
  // prepended verbatim ahead of whatever topic it suggests.
  address_prefix?: string | null;
}

export interface SubjectSuggestResponse {
  // Null when there wasn't enough to go on yet (empty body, no
  // attachments) or the AI call failed — not an error the writer
  // needs to see, the composer just doesn't show a suggestion.
  suggestion: string | null;
  tokens_used?: number;
}

export interface GrantDashboardResponse {
  pulse: {
    revenue_ttm: PulseRevenueTile;
    net_op_profit: PulseQbTile;
    cash_on_hand: PulseQbTile;
    households_served: PulseHouseholdsTile;
    cross_sell_rate: PulseCrossSellTile;
    ideal_client_mix: PulseIdealMixTile;
  };
  pipeline_forecast: DashboardPipelineForecastZone;
  trend: DashboardTrendZone;
  meta: DashboardMeta;
}

// ─── Brandon's dashboard types ────────────────────────────────────────
//
// Mirrors workers/api/src/routes/dashboard-brandon.ts — keep in sync.
// Zone 1 = Book of Business Health, Zone 2 = Opportunities & Pending,
// Zone 3 = Tasks. Loss ratio was removed entirely (no honest fix in
// v1 — needs claims data / IVANS, v2); every other metric that used
// to be a placeholder is now real.

export interface BrandonPremiumTile {
  kind: "total_premium";
  label: string;
  source: "client_engine";
  source_timestamp: string;
  total_annual_premium: number;
  policy_count: number;
  household_count: number;
}

export interface BrandonNewBusinessTile {
  kind: "new_business_mtd";
  label: string;
  source: "client_engine";
  source_timestamp: string;
  revenue_mtd: number;
  count_mtd: number;
  month_label: string;
}

export interface BrandonCarrierPremiumRow {
  carrier_vendor_id: string | null;
  carrier_display_name: string;
  annual_premium: number;
  policy_count: number;
}

export interface BrandonProductPremiumRow {
  policy_type: string;
  annual_premium: number;
  policy_count: number;
  household_count: number;
}

export interface BrandonPolicyDensity {
  total_insurance_households: number;
  one_policy_count: number;
  two_policy_count: number;
  three_plus_count: number;
  average_policies_per_household: number;
}

// Real retention tile — mirrors BrandonRetentionTile in
// workers/api/src/routes/dashboard-brandon.ts. Numbers are pulled
// from the same computation that powers /dashboard/insurance.
export interface BrandonRetentionTile {
  kind: "retention";
  label: string;
  source: "client_engine";
  source_timestamp: string;
  household_retention_ttm_pct: number | null;
  household_retention_lifetime_pct: number | null;
  households_active: number;
  policy_agency_retention_ttm_pct: number | null;
  policy_carrier_retention_ttm_pct: number | null;
  lost_premium_lifetime: number;
  lost_premium_ttm: number;
  ttm_cancellation_count: number;
}

// Mirrors CarrierConcentration in shared/db/dashboard-brandon.ts.
export interface BrandonCarrierConcentration {
  top1_name: string | null;
  top1_pct: number;
  top2_pct: number;
  top3_pct: number;
  risk: "low" | "moderate" | "high";
  carrier_count: number;
}

export interface BrandonZone1 {
  total_premium: BrandonPremiumTile;
  retention_rate: BrandonRetentionTile;
  new_business_mtd: BrandonNewBusinessTile;
  premium_by_carrier: BrandonCarrierPremiumRow[];
  premium_by_product: BrandonProductPremiumRow[];
  household_density: BrandonPolicyDensity;
  carrier_concentration: BrandonCarrierConcentration;
}

export interface BrandonCrossSellItem {
  household_id: string;
  household_display_name: string;
  insurance_policy_types: string[];
  other_company_slugs: string[];
  household_premium: number;
  carrier_display_name: string | null;
  earliest_renewal_date: string | null;
  last_contact_at: string | null;
  missing_line_recommendation: string;
}

export interface BrandonPipelineStage {
  stage: string;
  stage_display: string;
  count: number;
  weighted_revenue: number;
  unweighted_revenue: number;
}

// Mirrors PendingCancellationRow in shared/db/dashboard-brandon.ts.
export interface BrandonPendingCancellationRow {
  service_record_id: string;
  household_id: string;
  household_display_name: string;
  carrier_display_name: string | null;
  policy_type: string;
  expiration_date: string | null;
}

export interface BrandonZone2 {
  cross_sell: {
    households: BrandonCrossSellItem[];
    total_one_policy_count: number;
  };
  pending_cancellations: {
    count: number;
    policies: BrandonPendingCancellationRow[];
  };
  new_business_pipeline: {
    total_count: number;
    total_weighted_revenue: number;
    total_unweighted_revenue: number;
    by_stage: BrandonPipelineStage[];
  };
}

// Mirrors BrianTaskRow — Brandon's Tasks tile reuses the same
// manual_tasks query + household-label enrichment, scoped to "mine".
export interface BrandonTaskRow {
  id: string;
  title: string;
  due_date: string | null;
  is_overdue: boolean;
  entity_type: string | null;
  entity_id: string | null;
  entity_label: string | null;
}

export interface BrandonZone3 {
  tasks: BrandonTaskRow[];
}

// Mirrors BrandonCommissionResult in shared/db/dashboard-brandon.ts.
// QB-driven: 42.5% of gross commission receipts (one-month lag) +
// estimated profit share (profit × 90% × 10%). Dollars, not cents.
export interface BrandonCommissionResult {
  most_recent_full_month: string | null;
  paid_gross_months: string[];
  commission_ytd_paid: number;
  commission_pending: number;
  profit_share_ytd: number;
  profit_share_pending: number;
  total_income: number;
  paid_window_incomplete: boolean;
}

// Mirrors BrandonAttentionStrip in shared/db/dashboard-brandon.ts.
// "Where must I act now" — all counts are real SQL. A null
// documents_missing_signature means no v1 schema for e-sign tracking
// (UI marks v1.1, not a fabricated number).
export interface BrandonAttentionStrip {
  renewals_30d: number;
  renewals_60d: number;
  pending_cancellations: number;
  new_business_awaiting_response: number;
  team_tasks_overdue: number;
  documents_missing_signature: number | null;
}

export interface BrandonDashboardResponse {
  zone1: BrandonZone1;
  zone2: BrandonZone2;
  zone3: BrandonZone3;
  commission: BrandonCommissionResult;
  attention: BrandonAttentionStrip;
  meta: {
    as_of: string;
    client_engine_last_update: string;
  };
}

// ─── Jake's dashboard types (mirror workers dashboard-jake.ts) ──────
export type JakeClientTier = "A" | "B" | "C" | "D";
export type JakeLifeStage = "accumulator" | "pre_retiree" | "retiree" | "unknown";

export interface JakeAumTile {
  kind: "aum";
  label: string;
  source: "client_engine";
  source_timestamp: string;
  jake_aum_cents: number;
  firm_aum_cents: number;
  brian_aum_cents: number;
  jake_household_count: number;
  jake_pct_of_firm: number;
  /** Households in Jake's book whose AUM is under the $200K planning-fee
   *  threshold (shared/deal-value/financial-fee-schedule.ts). */
  households_under_planning_fee_threshold: number;
}
export interface JakeCountTile {
  kind: "count";
  label: string;
  source: "client_engine";
  source_timestamp: string;
  value: number;
  sublabel: string;
}
export interface JakeSegmentRow {
  tier: JakeClientTier;
  household_count: number;
  aum_cents: number;
  est_annual_revenue_cents: number;
}
export interface JakeLifeStageRow {
  life_stage: JakeLifeStage;
  household_count: number;
  aum_cents: number;
}
export interface JakeWatchlistRow {
  household_id: string;
  display_name: string;
  last_contact_at: string | null;
  days_since: number | null;
  cross_company: string[];
}
export interface JakePipelineRow {
  stage: string;
  stage_label: string;
  count: number;
  oldest_days: number | null;
}
export interface JakeDashboardResponse {
  zone1: {
    aum: JakeAumTile;
    active_households: JakeCountTile;
    open_pipeline: JakeCountTile;
  };
  zone2: {
    segments: JakeSegmentRow[];
    life_stages: JakeLifeStageRow[];
    watchlist: JakeWatchlistRow[];
  };
  zone3: {
    pipeline: JakePipelineRow[];
    probability_note: string;
  };
  // "My Book" row (2026-09-04 D1 rebuild, parity with Brian's dashboard).
  lenses: {
    onboarding: { stages: JakePipelineRow[]; total: number };
    annual_review: { steps: AnnualReviewStepRow[]; total: number };
  };
  attention: {
    never_contacted: number;
    stale_over_90d: number;
    pipeline_stalled_over_60d: number;
    open_pipeline_total: number;
  };
  // My Day additions (2026-08-28 redesign — parity with Brian's):
  // caller's tasks, Jake's-book reach-out + needs-attention queue.
  tasks_today: BrianTaskRow[];
  reach_out: BrianReachOutRow[];
  needs_attention: NeedsAttentionPayload;
  book_revenue: {
    annual_revenue_cents: number;
    household_count: number;
    note: string;
  };
  // Cross-company Planning slice (PR 4 — landed alongside the Planning
  // AC data migration). Active Planning Cases on households Jake
  // advises in Financial; Year-1 revenue stacks on his Financial
  // advisory revenue.
  planning: {
    active_cases: number;
    households_with_planning: number;
    year1_revenue_usd: number;
    cases_by_type: Array<{ case_type: string; count: number }>;
  };
  meta: {
    as_of: string;
    source: string;
  };
}

// ─── Voice-to-notes ───────────────────────────────────────────────────
export interface VoiceNote {
  id: string;
  entity_type: "household" | "contact";
  entity_id: string;
  author_user_id: string | null;
  body: string;
  title: string | null;
  pinned: boolean;
  important: boolean;
  source: string;
  created_at: string;
  updated_at: string;
}
export interface VoiceNoteSuggestion {
  id: string;
  advisor_user_id: string;
  household_id: string | null;
  source_entity_type: string;
  source_entity_id: string;
  suggestion_title: string;
  suggested_due_date: string | null;
  status: string;
  task_id: string | null;
  created_at: string;
}

// ─── Brian Ream's Financial advisor dashboard (relationship briefing) ────

export interface BrianCustodianSplit {
  source: string;
  label: string;
  aum_cents: number;
  account_count: number;
  household_count: number;
  /** Latest custodian-upload period_end; null before the first upload. */
  as_of: string | null;
}
export interface BrianTaskRow {
  id: string;
  title: string;
  due_date: string | null;
  is_overdue: boolean;
  entity_type: string | null;
  entity_id: string | null;
  entity_label: string | null;
  graph_task_id: string | null;
}
export interface BrianReachOutRow {
  household_id: string;
  display_name: string;
  score: number | null;
  band: string;
  reasons: string[];
  days_since_contact: number | null;
  cross_company: string[];
  basis: "at_risk" | "last_contact";
  /** The household's total counted AUM (canonical, custodian-sourced),
   *  null when the household has none yet. */
  aum_cents: number | null;
  /** The same OS-wide "last real touch" (shared/db/last-touch.ts) —
   *  null when nothing has ever been logged. Populated on the
   *  `at_risk` basis only; the `last_contact` fallback's reason text
   *  already states the recency (or its absence) in different words. */
  last_touch: {
    at: string;
    kind: string;
    direction: "inbound" | "outbound" | string | null;
  } | null;
}
/** One record in the "Needs your attention" queue (My Day redesign). */
export interface StalledRecordCard {
  id: string;
  household_id: string;
  household_name: string;
  stage_label: string;
  days_open: number;
}
export interface NeedsAttentionPayload {
  stalled_opportunities: StalledRecordCard[];
  stalled_onboarding: StalledRecordCard[];
}
export interface BrianFundQuote {
  symbol: string;
  label: string | null;
  price: number | null;
  change_percent: number | null;
}
export interface BrianFundsResponse {
  ok: boolean;
  as_of: string | null;
  funds: BrianFundQuote[];
}
/** One step of the Financial "Annual Review & Tax Planning" yearly cycle
 *  — household count currently at that step, cycle order, `stage: null`
 *  is "Not started" (2026-09-04 D1 rebuild). */
export interface AnnualReviewStepRow {
  stage: string | null;
  stage_label: string;
  count: number;
}
export interface BrianDashboardResponse {
  zone1: {
    aum: {
      label: string;
      source: "client_engine";
      source_timestamp: string;
      total_cents: number;
      by_custodian: BrianCustodianSplit[];
      firm_aum_cents: number;
      est_book_revenue_cents: number;
      /** Households in the book whose AUM is under the $200K planning-fee
       *  threshold (shared/deal-value/financial-fee-schedule.ts) — the
       *  "N households under $200K paying the $189/mo planning fee" line. */
      households_under_planning_fee_threshold: number;
    };
    household_count: number;
    open_pipeline_count: number;
  };
  tasks_today: BrianTaskRow[];
  reach_out: BrianReachOutRow[];
  needs_attention: NeedsAttentionPayload;
  /** Computed for the reach-out fallback; no longer rendered as its own
   *  panel (Brian, 2026-09-03: "awful, just get rid of it"). */
  watchlist: JakeWatchlistRow[];
  lenses: {
    prospects: { stages: JakePipelineRow[]; total: number };
    onboarding: { stages: JakePipelineRow[]; total: number };
    annual_review: { steps: AnnualReviewStepRow[]; total: number };
  };
  attention: {
    never_contacted: number;
    stale_over_90d: number;
    pipeline_stalled_over_60d: number;
    open_pipeline_total: number;
  };
  meta: {
    as_of: string;
    source: string;
  };
}

// ─── Ryan Delviken's Planning dashboard re-cut (program S2) ─────────

export interface TrendIndicator {
  percent_change: number | null;
  absolute_delta: number | null;
  direction: "up" | "down" | "flat" | "insufficient_history";
}

export interface AttentionLaneItem {
  household_id: string;
  household_name: string;
  days_in_stage: number;
}

export interface AttentionLane {
  available: boolean;
  count: number;
  items: AttentionLaneItem[];
  unavailable_reason: string | null;
}

export interface RecentReviewRow {
  id: string;
  household_id: string | null;
  household_display_name: string | null;
  contact_id: string | null;
  contact_first_name: string | null;
  contact_phone: string | null;
  rating_stars: number | null;
  rating_bucket: string;
  body: string | null;
  reviewer_name: string | null;
  submitted_at: string;
}

export interface RyanPlanningDashboardResponse {
  as_of: string;
  gross_income: {
    ttm_usd: number | null;
    ytd_usd: number | null;
    trend: TrendIndicator;
    last_full_month: string | null;
    data_available: boolean;
  };
  estimated_income: {
    configured: true;
    gross_received_usd: number | null;
    company_gross_received_usd: number | null;
    estimated_income_usd: number | null;
    last_full_month: string | null;
    data_available: boolean;
    own_case_fraction: number;
    own_case_method: "expected_revenue" | "case_count" | "none";
    formula_source: "comp_plan" | "built_in";
    share_bps: number;
    step_bps: number[];
    /** One line under the figure: which cases it leaves out. */
    excluded_note: string;
  };
  households_served: {
    total_active: number;
    trend: TrendIndicator;
    by_type: Array<{
      case_type: string;
      active_households: number;
      trend: TrendIndicator;
    }>;
  };
  attention: {
    quotes_sent: AttentionLane;
    quoted_no_reply: AttentionLane;
    application_submitted: AttentionLane;
    ongoing_care_not_engaging: AttentionLane;
  };
  reviews: {
    since: string;
    items: RecentReviewRow[];
  };
}

// ─── Andrea Middleton's Planning Ops dashboard re-cut (program S3) ──

export interface EstateQueueRow {
  household_id: string;
  household_name: string;
  transaction_id: string;
  stage: string;
  days_in_stage: number;
}

export interface CareQueueRow {
  household_id: string;
  household_name: string;
  rhythm_instance_id: string;
  renewal_stage: string | null;
}

export interface NoResponseQueueRow {
  household_id: string;
  household_name: string;
  sent_at: string | null;
  days_since_sent: number;
}

export type {
  CloseOutQueueFile,
  CloseOutTask,
  CloseOutTaskGroup,
  MortgageCloseOutQueue,
  MortgageCloseOutTasks,
  MortgageUpcomingClosings,
  UpcomingClosing,
} from "@shared/db/mortgage-close-out-queue";

/** The three zones of Cambria's close-out cockpit, in one response. */
export interface MortgageCloseOutCockpit {
  queue: MortgageCloseOutQueue;
  upcoming: MortgageUpcomingClosings;
  tasks: MortgageCloseOutTasks;
}

export type {
  PmLoanDesk,
  PmLoanDeskProcessing,
  PmLoanDeskRequests,
  PmLoanRequest,
  PmProcessingLoan,
} from "@shared/db/private-money-loan-desk";

export interface AndreaPlanningOpsResponse {
  as_of: string;
  queues: {
    estate_ready_to_prepare: { count: number; items: EstateQueueRow[] };
    estate_waiting_on_client: { count: number; items: EstateQueueRow[] };
    open_enrollment_info_received: { count: number; items: CareQueueRow[] };
    open_enrollment_no_response: {
      available: boolean;
      count: number;
      items: NoResponseQueueRow[];
      unavailable_reason: string | null;
    };
  };
  this_week: {
    signings: { count: number; date_filtered: false; note: string };
    season: {
      active: Array<{ season: string; label: string }>;
      next: { window: { season: string; label: string }; daysUntil: number } | null;
    };
    call_block: {
      available: boolean;
      started: boolean;
      count: number;
      unavailable_reason: string | null;
    };
  };
}

// ─── Baker's dashboard types ────────────────────────────────────────

export type ComplianceCompanySlug =
  | "mortgage"
  | "insurance"
  | "planning"
  | "realty"
  | "financial"
  | "all";

export type ComplianceSeverity = "red" | "yellow" | "green";

export interface BakerZone1Card {
  company: ComplianceCompanySlug;
  display_name: string;
  red_count: number;
  yellow_count: number;
  green_count: number;
  total_count: number;
  active_checklists_count: number;
  top_issues: Array<{
    title: string;
    severity: string;
    entity_display_name: string | null;
    issue_id: string;
  }>;
  // Phase 4.2: only populated when company === 'mortgage'.
  // Per-severity counts for CE / OnlineEd / NMLS plus a one-line
  // next-quarterly-NMLS summary. Shape mirrors
  // BakerMortgageBreakdown defined later in this file.
  mortgage_breakdown?: {
    ce: { red: number; yellow: number; green: number };
    online_ed: { red: number; yellow: number; green: number };
    nmls_quarterly: { red: number; yellow: number; green: number };
    next_nmls_status: "green" | "yellow" | "red" | null;
    next_nmls_due_date: string | null;
    next_nmls_quarter: 1 | 2 | 3 | 4 | null;
  };
  // Phase 4.3: only populated when company === 'realty'.
  realty_breakdown?: {
    licenses: { red: number; yellow: number; green: number; total: number };
    checklist_tasks: {
      red: number;
      yellow: number;
      green: number;
      total: number;
    };
    next_license_status: "green" | "yellow" | "red" | null;
    next_license_expires_at: string | null;
    next_license_user_name: string | null;
  };
  // Phase 4.4: only populated when company === 'insurance'.
  insurance_breakdown?: {
    licenses: { red: number; yellow: number; green: number; total: number };
    checklist_tasks: {
      red: number;
      yellow: number;
      green: number;
      total: number;
    };
    next_license_status: "green" | "yellow" | "red" | null;
    next_license_expires_at: string | null;
    next_license_user_name: string | null;
  };
}

export interface BakerZone1 {
  cards: BakerZone1Card[];
  total_open_issues: number;
}

export interface BakerZone2RowPlaceholder {
  kind: "placeholder";
  company: ComplianceCompanySlug;
  display_name: string;
  reason: "needs_qb_phase_4";
  explanation: string;
}

export interface BakerZone2 {
  placeholder: true;
  rows: BakerZone2RowPlaceholder[];
  explanation: string;
}

export interface BakerTaskListItem {
  kind: "compliance" | "manual";
  id: string;
  title: string;
  source: string;
  company: ComplianceCompanySlug | null;
  due_date: string | null;
  priority: string | null;
  age_days: number;
  completed: boolean;
}

export interface BakerZone3 {
  compliance_tasks: BakerTaskListItem[];
  manual_tasks: BakerTaskListItem[];
  total_open: number;
}

export interface BakerZone4Bucket {
  source: string;
  count: number;
}

export interface BakerZone4 {
  month: { total: number; buckets: BakerZone4Bucket[] };
  quarter: { total: number; buckets: BakerZone4Bucket[] };
  year: { total: number; buckets: BakerZone4Bucket[] };
}

export interface BakerDashboardResponse {
  zone1: BakerZone1;
  zone2: BakerZone2;
  zone3: BakerZone3;
  zone4: BakerZone4;
  meta: { as_of: string };
}

export interface BakerComplianceIssue {
  id: string;
  rule_id: string;
  company: string;
  entity_type: string;
  entity_id: string | null;
  entity_display_name: string | null;
  severity: ComplianceSeverity;
  title: string;
  detail: string | null;
  checklist_instance_id: string | null;
  flagged_at: string;
  resolved_at: string | null;
}

export interface BakerChecklistInstance {
  id: string;
  template_id: string;
  entity_type: string;
  entity_id: string;
  entity_display_name: string | null;
  company: string;
  triggered_at: string;
  triggered_by_user_id: string | null;
  triggered_by_rule_id: string | null;
  due_date: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  notes: string | null;
}

export interface BakerChecklistTask {
  id: string;
  instance_id: string;
  template_item_id: string;
  sort_order: number;
  title: string;
  description: string | null;
  assigned_to_user_id: string | null;
  completed_at: string | null;
  completed_by_user_id: string | null;
  completion_notes: string | null;
  evidence_url: string | null;
  evidence_text: string | null;
}

export interface BakerComplianceRule {
  id: string;
  company: ComplianceCompanySlug;
  name: string;
  description: string | null;
  severity: ComplianceSeverity;
  rule_type: string;
  config_json: string | null;
  associated_checklist_id: string | null;
  active: boolean;
  last_evaluated_at: string | null;
}

export interface BakerChecklistTemplate {
  id: string;
  name: string;
  description: string | null;
  company: ComplianceCompanySlug;
  trigger_type: string;
  trigger_config_json: string | null;
  active: boolean;
  // The Checklists area (2026-09-29). Optional: older responses lack them.
  kind?: ChecklistKind;
  applies_to?: ChecklistAppliesTo | null;
  side?: ChecklistSide | null;
  default_from_stage?: string | null;
  is_default?: boolean;
}

// ─── v1.1 types ─────────────────────────────────────────────────────

export interface BakerZone2Row {
  kind: "real";
  company: ComplianceCompanySlug;
  display_name: string;
  expected_cents: number;
  actual_cents: number;
  delta_cents: number;
  delta_pct: number | null;
  severity: "red" | "yellow" | "green";
  qb_snapshot_missing: boolean;
}

export interface BakerZone2Real {
  placeholder: false;
  period_label: string;
  rows: BakerZone2Row[];
  total_expected_cents: number;
  total_actual_cents: number;
}

export interface BakerZone5LoanRow {
  loan_id: string;
  loan_type: string;
  loan_number: string | null;
  display_label: string | null;
  household_id: string | null;
  target_close: string | null;
  required_count: number;
  present_count: number;
  verified_count: number;
  severity: "red" | "yellow" | "green" | "blue";
}

export interface BakerZone5 {
  rows: BakerZone5LoanRow[];
  total_active: number;
  red_count: number;
  yellow_count: number;
}

export interface BakerZone6Row {
  disbursement_id: string;
  entity_type: string;
  entity_id: string;
  advisor_user_id: string;
  advisor_name: string | null;
  company: string;
  gross_cents: number;
  costs_cents: number;
  net_cents: number;
  disbursement_cents: number;
  status: string;
  period_end: string | null;
  created_at: string;
  // Phase 3b — Insurance v1.3 auto-disbursement metadata. Populated
  // for cron-generated rows; null for manually-created v1.2 rows.
  auto_generated: boolean;
  commission_report_id: string | null;
  commission_report_status: string | null; // 'pending' | 'sent' | 'failed'
  derived_from_bonus_id: string | null;
}

export interface BakerZone6 {
  rows: BakerZone6Row[];
  total_active: number;
  pending_count: number;
  calculated_count: number;
  approved_count: number;
  paid_ytd_cents: number;
}

// Phase 4.0 — Compliance Watchtower types mirror the new server shapes
export type BakerCriticalAlertKind =
  | "compliance_issue"
  | "license_expiring"
  | "commission_pending"
  | "loan_file_audit";

export interface BakerCriticalAlertRow {
  id: string;
  kind: BakerCriticalAlertKind;
  company: ComplianceCompanySlug | "all";
  severity: "red" | "yellow" | "green";
  title: string;
  sub_line: string | null;
  due_label: string | null;
  action_label: string;
  action_href: string | null;
  flagged_at: string;
}

export interface BakerCriticalAlertsBlock {
  rows: BakerCriticalAlertRow[];
  open_count: number;
  dispatched_count: number;
}

export interface BakerCrossCompanyAuditSection {
  company: ComplianceCompanySlug;
  display_name: string;
  rows: BakerZone5LoanRow[];
  red_count: number;
  yellow_count: number;
  total_count: number;
}

export interface BakerCrossCompanyAuditBlock {
  sections: BakerCrossCompanyAuditSection[];
  total_files: number;
}

export interface BakerSignOffQueueBlock {
  total_count: number;
  by_kind: {
    commission_disbursements_calculated: number;
    quarterly_audit_pending: number;
    license_renewals_due: number;
  };
}

export interface BakerDashboardV11Response {
  zone1: BakerZone1;
  zone2: BakerZone2Real;
  zone3: BakerZone3;
  zone4: BakerZone4;
  zone5: BakerZone5;
  zone6: BakerZone6;
  // Phase 4.0 additions
  critical_alerts: BakerCriticalAlertsBlock;
  cross_company_audit: BakerCrossCompanyAuditBlock;
  sign_off_queue: BakerSignOffQueueBlock;
  meta: { as_of: string };
}

// Joey program PR7 — Stew document analysis result.
export interface DocumentIncomeItem {
  source: string;
  amount: string;
  period: string;
  note: string;
}
export interface DocumentFlag {
  severity: "green" | "red" | "info";
  label: string;
  detail: string;
}
export interface DocumentAnalysis {
  document_kind: string;
  qualifying_income_note: string;
  income_items: DocumentIncomeItem[];
  flags: DocumentFlag[];
}
export interface DocumentAnalysisResponse {
  analysis: DocumentAnalysis;
}

// E-signature send half (Phase 1).
/** A bulk signature send: the preview before it starts, and the
 *  progress once it has. */
export interface EsignBulkStatus {
  batch: {
    id: string;
    status: "draft" | "running" | "completed" | "cancelled";
    title: string | null;
    company_slug: string | null;
    created_at: string;
    quoted_cost_cents: number | null;
  };
  counts: { total: number; pending: number; sent: number; failed: number; skipped: number };
  cost: { marginal_cents: number; marginal_display: string; included_remaining: number };
  will_send: number;
  needs_confirmation: boolean;
  confirm_threshold: number;
  /** Named, not counted. A list of households with a reason each is
   *  something an advisor can act on before committing; "12 skipped" is
   *  a footnote. */
  skipped: {
    household_id: string | null;
    household_name: string | null;
    entity_id: string;
    reason: string | null;
    reason_sentence: string;
  }[];
  failed: { household_id: string | null; household_name: string | null; error: string | null }[];
}

/** How many envelopes have actually gone out. Admin-only — this is
 *  plan-and-contract information plus a per-person send count. */
export interface EsignUsage {
  months: { month: string; envelopes: number; costCents: number }[];
  /** First month an envelope ever went out — months before it are
   *  history that didn't exist, not $30 charges. Null = never sent. */
  sinceMonth: string | null;
  totalEnvelopes: number;
  thisMonthEnvelopes: number;
  byCompany: { key: string | null; label: string; envelopes: number }[];
  bySender: { key: string | null; label: string; envelopes: number }[];
  /** Money, in cents — summed per month, since the allowance resets
   *  monthly. Formatted for display via formatCents(). */
  totalCostCents: number;
  thisMonthCostCents: number;
  /** Included envelopes still unspent this month — the number that
   *  answers "can I fire this batch without paying for it?" */
  includedRemaining: number;
  includedPerMonth: number;
}

/** One field as the prep screen sends it — the element type of both
 *  `fields` and `ghosts` on sendForSignature. `fields` keeps its inline
 *  copy below (with the per-key notes); this alias exists so a GHOST is
 *  declared as exactly the same shape rather than as "anything". */
export type EsignSentFieldPayload = {
  signerKey: string;
  roleKey?: string | null;
  fieldType: string;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  value?: string | null;
  signerMayEdit?: boolean;
  fontSize?: number;
  mergeToken?: string | null;
  options?: string[] | null;
  groupName?: string | null;
  fillRule?: string | null;
};

export interface EsignSendResponse {
  ok: true;
  envelope_id: string;
  signers: Array<{ email: string; name: string; signUrl: string; emailed: boolean }>;
  /** How the links were delivered. "in_person" means the server emailed
   *  nobody on purpose — every `emailed` is false because nothing was
   *  attempted, not because sending failed. */
  delivery?: "email" | "in_person";
  /** True when the server filed the package under the Realty offer it
   *  was for (PR 8e), so the offer pane need not attach it again. */
  realty_offer_attached?: boolean;
}

export interface EsignEnvelopeSignerRec {
  signerId: string;
  contactId: string | null;
  name: string;
  email: string;
  hasActiveLink: boolean;
  /** Where this person has got to. Null means nothing has come back
   *  about them yet — not the same as "sent and waiting". */
  status: "pending" | "delivered" | "viewed" | "signed" | "declined" | null;
  signedAt: string | null;
  declinedAt: string | null;
  /** The address the request started at, when an advisor has since moved
   *  it somewhere else. Null on a signer nobody has moved. */
  previousEmail: string | null;
  emailChangedAt: string | null;
  /** The client's own words when they declined, if they gave any. */
  declineReason?: string | null;
  /** Outlook refused to send this person's signing email (not a bounce:
   *  the OS cannot see returned mail). Cleared by a later successful send. */
  emailFailedAt?: string | null;
  emailFailedAddress?: string | null;
  emailFailureReason?: string | null;
}

/** One row of the E-Sign workspace list. */
/** One signer's journey through an envelope. Each timestamp is set once,
 *  on first arrival — the screen shows a JOURNEY, so collapsing these
 *  into one "last changed" would throw away the thing being displayed. */
export interface EsignSignerProgress {
  signerId: string;
  contactId: string | null;
  name: string;
  email: string;
  /** Null means we have heard NOTHING about this person — which is not
   *  the same as "sent and waiting", and must never be rendered as it. */
  status: "pending" | "delivered" | "viewed" | "signed" | "declined" | null;
  deliveredAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  previousEmail: string | null;
  emailChangedAt: string | null;
  emailChangedByName: string | null;
  /** Set when this person's signing email never went out. */
  emailFailedAt: string | null;
  emailFailureReason: string | null;
}

export interface EsignEnvelopeDocument {
  id: string;
  documentId: string;
  /** 0-based, and the order it was shown to the signer. */
  position: number;
  /** The filename as sent. */
  title: string | null;
  pageCount: number | null;
  /** The source file has since been archived or deleted from the
   *  record. The manifest still names it — a signed package is a
   *  record of what went out. */
  archived: boolean;
}

export interface EsignEnvelopeDetail {
  id: string;
  providerDocumentId: string;
  title: string | null;
  status: string;
  message: string | null;
  documentType: string | null;
  companySlug: string | null;
  entityType: string | null;
  entityId: string | null;
  householdId: string | null;
  householdName: string | null;
  sentByUserId: string | null;
  sentByName: string | null;
  sentAt: string | null;
  completedAt: string | null;
  sealedDocumentId: string | null;
  auditCertificateDocumentId: string | null;
  /** Why the package is dead (failed send or call-back), when it is. */
  voidedReason?: string | null;
  sourceDocumentId: string | null;
  /** Every document in the package, in the order the signer met them.
   *  One entry for an ordinary single-document envelope; empty for
   *  envelopes sent before packages existed, where sourceDocumentId is
   *  still the answer. */
  documents: EsignEnvelopeDocument[];
  signers: EsignSignerProgress[];
  /** Newest first — every delivery received, including ones whose
   *  wording we did not recognise. */
  events: {
    eventType: string;
    signerEmail: string | null;
    occurredAt: string;
    receivedAt: string;
  }[];
}

/** The `?company=` a standalone (household) e-sign call carries. Every
 *  other kind reads its company off its own row, so this is empty for
 *  them — one helper rather than four call sites deciding for
 *  themselves. */
function esignCompanyQuery(companySlug?: string | null): string {
  return companySlug ? `?company=${encodeURIComponent(companySlug)}` : "";
}

export interface DocumentMoveTarget {
  entityType: string;
  entityId: string;
  label: string;
  /** "The family", "People", or a company name — the picker's grouping. */
  group: string;
  companySlug: string | null;
}

export interface ContactRelatedDocumentRec extends DocumentRec {
  /** Which record it is filed on, in words. */
  attached_to_label: string | null;
  attached_to_type: string;
  attached_to_id: string;
}

export interface EsignWorkspaceRow {
  id: string;
  providerDocumentId: string;
  title: string | null;
  status: string;
  companySlug: string | null;
  entityType: string | null;
  entityId: string | null;
  householdId: string | null;
  sentByUserId: string | null;
  sentByName: string | null;
  sentAt: string | null;
  completedAt: string | null;
  sealedDocumentId: string | null;
  signers: {
    name: string;
    email: string;
    status: "pending" | "delivered" | "viewed" | "signed" | "declined" | null;
    signedAt: string | null;
    declinedAt: string | null;
    declineReason: string | null;
    /** Set when this person's signing email never went out. */
    emailFailedAt: string | null;
    emailFailureReason: string | null;
  }[];
  awaitingCount: number;
}

export interface EsignEnvelopeRec {
  id: string;
  providerDocumentId: string;
  title: string | null;
  status: string;
  sentAt: string | null;
  completedAt: string | null;
  archivedAt: string | null;
  /** Set once the executed copy is filed — null until then. */
  sealedDocumentId: string | null;
  /** The certificate of completion filed beside it (PR 9a). */
  auditCertificateDocumentId?: string | null;
  /** When the provider stops accepting signatures, if a deadline was set. */
  expiresAt?: string | null;
  /** The package's first document: what "Resend" reopens on an expired one. */
  sourceDocumentId?: string | null;
  /** Why it was voided from the OS. */
  voidedReason?: string | null;
  /** "What was sent" (PR 8b). Optional on older fixtures. */
  emailSubject?: string | null;
  message?: string | null;
  /** The email's lines changed for this send (PR 8f), with tokens. */
  emailBody?: string | null;
  videoUrl?: string | null;
  videoTitle?: string | null;
  signers: EsignEnvelopeSignerRec[];
  /** The latest "Fix what hasn't been signed yet" on this package (PR
   *  10b). Absent from an older Worker. */
  correction?: {
    status: "pending" | "applied" | "failed";
    reason: string;
    failureMessage: string | null;
    createdAt: string;
    resolvedAt: string | null;
  } | null;
}

/** One document in a package draft (PR 8c), in signing order. */
export interface EsignDraftDocument {
  documentId: string;
  filename: string;
  documentType: string | null;
  origin: string | null;
  /** Rides along to be read; nothing is signed on it. */
  attachment: boolean;
}

/** The Documents tab's draft line. */
export interface EsignDraftSummary {
  id: string;
  entityType: string;
  entityId: string;
  title: string;
  documents: EsignDraftDocument[];
  createdByUserId: string;
  createdByName: string | null;
  updatedAt: string;
  versionNumber: number;
  /** The Realty offer it was drafted for (PR 8e), or null. */
  realtyOfferId: string | null;
}

/** A draft opened for editing: the line plus the send screen's state. */
export interface EsignDraft extends EsignDraftSummary {
  state: unknown;
}

export interface EsignConsentStatus {
  state: "none" | "consented" | "withdrawn";
  consentedAt: string | null;
  withdrawnAt: string | null;
  withdrawalNote: string | null;
}

// Nate Swartz party extraction (2026-08-04). Mirrors ExtractedParty /
// PartyExtractionResult in shared/ai/party-extractor.ts, plus the
// vendor_match the route attaches (shared/db/vendors.ts
// matchVendorForParty).
export type ExtractedPartyRole =
  | "title"
  | "escrow"
  | "loan_officer"
  | "buyer_agent"
  | "listing_agent"
  | "transaction_coordinator"
  | "lender"
  | "inspector"
  | "other";
export interface ExtractedPartyVendorMatch {
  id: string;
  display_name: string;
  type: string;
}
export interface ExtractedParty {
  role_guess: ExtractedPartyRole;
  name: string;
  email: string | null;
  phone: string | null;
  vendor_match: ExtractedPartyVendorMatch | null;
}
export interface PartyExtractionResponse {
  document_kind: string;
  parties: ExtractedParty[];
  /** The inspection window the same read found (Nate, 2026-08-11), or
   *  null when the document named neither half. A PROPOSAL — the advisor
   *  applies it; nothing is written by the extraction itself. */
  inspection: ExtractedInspectionPeriod | null;
  /** The contract-card terms the same read found (Nate, 2026-08-24 §9),
   *  or null when the document stated none of them. Also a PROPOSAL. */
  contract_terms: ExtractedContractTerms | null;
  /** TC-7 (2026-08-29): whether the document looks fully executed, plus
   *  what it prints about its own form/revision. A NOTICE, not a
   *  proposal — nothing to apply, nothing writes from it. */
  document_audit: ExtractedDocumentAudit | null;
  /** The printed form/revision compared against the forms catalog —
   *  realty only (null on a mortgage file, same boundary as
   *  contract_terms above), and null when there wasn't enough printed
   *  text to compare. */
  form_check: FormRevisionCheck | null;
  /** Property facts (sewer, water, HOA, solar, year built…) the same read
   *  found. Realty only; null when the document states none. A PROPOSAL,
   *  applied through the property-facts write path. */
  property_facts: ExtractedPropertyFactsDto | null;
}

// "No ARMLS" tier 3 (2026-08-30) — a property a buyer has viewed.
export interface RealtyPropertyViewingDto {
  id: string;
  household_id: string;
  property_address_line_1: string;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  viewed_at: string;
  source: "advisor_logged" | "calendar_confirmed";
  graph_event_id: string | null;
  notes: string | null;
  logged_by_user_id: string;
  logged_by_name: string | null;
  created_at: string;
}

// A calendar event that looks like it might be a showing, read live off
// the caller's own Outlook — nothing is stored until confirmed.
export interface ViewingCandidateDto {
  graph_event_id: string;
  subject: string;
  location_text: string;
  start_at: string;
  matched_reason: "subject" | "location" | "both";
}

// "No ARMLS" tier 1 (2026-08-30) — mirrors ExtractedPropertyFacts in
// shared/ai/property-facts-extractor.ts. Every field independently
// nullable; the confirm UI shows only the non-null ones.
export interface ExtractedPropertyFactsDto {
  apn: string | null;
  legal_description: string | null;
  county: string | null;
  subdivision: string | null;
  lot_size_sqft: number | null;
  year_built: number | null;
  sewer_type: string | null;
  water_source: string | null;
  hoa_status: string | null;
  hoa_name: string | null;
  solar_status: string | null;
  in_unincorporated_area: boolean | null;
}

export interface ExtractedDocumentAudit {
  /** True when every signature/initial block Stew could see looks filled
   *  in; false when something looks blank or incomplete; null when the
   *  document isn't the kind of thing that gets signed, or Stew can't
   *  tell. A visual read, never a legal determination. */
  fully_executed: boolean | null;
  /** What looks blank or incomplete — set only when fully_executed is
   *  false. */
  signature_concern: string;
  /** The form's own printed name, read verbatim. Null when not printed
   *  or not readable. */
  form_name_printed: string | null;
  /** The form's own printed revision label, read verbatim. Null when not
   *  printed or not readable. */
  form_revision_printed: string | null;
}

// Mirrors FormRevisionCheck in shared/realty/contract-form-revision-check.ts.
// Reuses ContractFormSummary (the Forms Library's own row shape, below)
// rather than a second type for the same server row.
export type FormRevisionCheck =
  | { status: "current"; form: ContractFormSummary }
  | { status: "outdated"; current: ContractFormSummary; printed_revision: string }
  | { status: "unrecognized" };

export interface ExtractedInspectionPeriod {
  /** Day zero, ISO "YYYY-MM-DD". */
  contract_executed_at: string | null;
  inspection_period_days: number | null;
  /** What the document said, quoted, so the reading can be checked
   *  without reopening the PDF. */
  evidence: string;
}

export interface ExtractedContractTerms {
  sale_price: number | null;
  /** The seller's name as printed on the offer / counter / addendum. */
  seller_name: string | null;
  /** Possession date (contract section 1e), ISO "YYYY-MM-DD". */
  possession: string | null;
  earnest_money: number | null;
  seller_concessions: number | null;
  /** ISO "YYYY-MM-DD". */
  appraisal_deadline: string | null;
  /** ISO "YYYY-MM-DD". */
  close_of_escrow: string | null;
  /** Buyer's name as printed (written only on a listing). */
  buyer_name: string | null;
  list_price: number | null;
  down_payment: number | null;
  loan_type: string | null;
  loan_amount: number | null;
  /** ISO "YYYY-MM-DD". */
  final_walkthrough: string | null;
  home_warranty: number | null;
  repair_credit: number | null;
  /** Percent of price offered to the buyer's broker (2.5 = 2.5%). */
  buyer_broker_pct: number | null;
  /** What the document said, quoted. */
  evidence: string;
}

export interface DocumentRec {
  id: string;
  entity_type: string;
  entity_id: string;
  document_type: string | null;
  filename: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  r2_key: string;
  signed_status: string | null;
  signed_at: string | null;
  verified_at: string | null;
  verified_by_user_id: string | null;
  verification_notes: string | null;
  has_issues: boolean;
  issue_notes: string | null;
  uploaded_by_user_id: string | null;
  uploaded_at: string;
  archived: boolean;
  // SharePoint backend (migration 0048). Populated when the file
  // lives in SharePoint instead of R2. The download endpoint
  // branches on `sharepoint_item_id IS NOT NULL`. `sharepoint_web_url`
  // is the advisor-facing "Open in SharePoint" deep-link.
  sharepoint_drive_id?: string | null;
  sharepoint_item_id?: string | null;
  sharepoint_web_url?: string | null;
  /** Which shelf this document is on (Nate's Aug 24 spec §9, PR5 of 5).
   *  'internal' is the default and the safe one — no client-facing route
   *  reads this yet (no client portal exists in v1). */
  visibility: "client" | "internal";
  /** Who wrote this piece of paper. "received" means the other side
   *  did — a counteroffer arriving as a PDF — which is what earns it a
   *  page-by-page check before we place our boxes on it and send it.
   *  See shared/esign/document-origin.ts. */
  origin?: "ours" | "received";
  /** The package this was cut from by the close-out splitter, or null. */
  split_from_document_id: string | null;
  /** The Opportunity this file was carried from when it was won
   *  (Documents tab PR 5). Absent on older payloads. */
  carried_from_opportunity_id?: string | null;
}

export interface DocumentDefinitionRec {
  id: string;
  applies_to_entity_type: string;
  applies_to_entity_subtype: string | null;
  company: string;
  code: string | null;
  name: string;
  owner: string | null;
  when_required: string | null;
  purpose: string | null;
  compliance_source: string | null;
  auditor_looks_for: string | null;
  acceptable_evidence: string | null;
  common_issues: string | null;
  /** The conditionality rule, verbatim from document_definitions —
   *  e.g. `{"refi_only": true}` on a Right to Cancel. The server has
   *  always sent this (it projects the whole row); declaring it lets
   *  the "Change type" control say which documents a retype will start
   *  or stop asking for, using the SAME shared predicate the server
   *  derives statuses with (shared/transactions/close-out-status). */
  required_if_json: string | null;
  sort_order: number;
  active: number | boolean;
}

// NMLS Mortgage Call Report worksheet (NMLS program PR C). Mirrors
// shared/db/mcr-report.ts.
export interface McrBucketRec {
  count: number;
  amount: number;
}
export interface McrWorksheetRec {
  year: number;
  quarter: 1 | 2 | 3 | 4;
  period_start: string;
  period_end_exclusive: string;
  closed: {
    total: McrBucketRec;
    by_program: {
      conventional: McrBucketRec;
      fha: McrBucketRec;
      va: McrBucketRec;
      fsa_rhs: McrBucketRec;
      program_not_set: McrBucketRec;
    };
    by_purpose: { purchase: McrBucketRec; refinancing: McrBucketRec; other_unknown: McrBucketRec };
    hoepa: { yes: number; no: number; unanswered: number };
    lien: {
      first_lien: number;
      subordinate_lien: number;
      not_secured_by_lien: number;
      unanswered: number;
    };
    qm: { qm: number; non_qm: number; not_subject: number; unanswered: number };
    broker_fees: number;
  };
  pipeline: {
    received: McrBucketRec;
    actions: {
      closed_funded: number;
      approved_not_accepted: number;
      denied: number;
      withdrawn: number;
      file_closed_incomplete: number;
      unclassified: number;
    };
  };
  mlo: Array<{ loan_officer_name: string | null; count: number; amount: number }>;
  mlo_ties_out: boolean;
  readiness: {
    loans_missing: Array<{
      mortgage_loan_id: string;
      service_record_id: string;
      household_id: string | null;
      borrower_name: string | null;
      loan_type: string;
      close_date: string | null;
      missing: Array<"lien_status" | "hoepa_status" | "qm_status" | "loan_program_type">;
    }>;
    loans_missing_total: number;
    applications_unclassified: Array<{
      transaction_id: string;
      household_id: string;
      household_display_name: string | null;
      outcome: string | null;
      outcome_reason: string | null;
      resolved_on: string | null;
    }>;
    applications_unclassified_total: number;
  };
}

// The auditor's Loan Log (mortgage compliance program, phase 3).
// Mirrors shared/db/loan-log.ts — BOTH halves of this type must match
// that file, because the browser keeps its own copy of the response
// shape and only `tsc -p web/tsconfig.json` checks this one.
export interface LoanLogRowRec {
  id: string;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  application_date: string | null;
  /** Dollars. Blank where the OS holds none — never substituted. */
  initial_loan_amount: number | null;
  final_disposition_date: string | null;
  loan_officer_name: string | null;
  /** Always "withdrawn" for a cancelled file; `denied` does not exist. */
  disposition: "funded" | "withdrawn";
}

export interface LoanLogRec {
  window: { start: string; end: string };
  rows: LoanLogRowRec[];
  /** COUNT(*) over the same filter — the whole population. */
  total: number;
  truncated: boolean;
}

// Closed Loan Report (close-out program PR 5). Mirrors
// shared/db/closed-loan-report.ts.
export interface ClosedLoanReportRowRec {
  id: string;
  service_record_id: string;
  household_id: string | null;
  household_display_name: string | null;
  borrower_name: string | null;
  loan_number: string | null;
  lender_name: string | null;
  loan_officer_name: string | null;
  /** True when loan_officer_name is filled in from the Processing
   *  owner rather than a name entered on the loan. */
  loan_officer_is_fallback: boolean;
  loan_program_type: string | null;
  loan_purpose: string | null;
  application_date: string | null;
  initial_loan_amount: number | null;
  loan_amount: number | null;
  interest_rate: number | null;
  term_months: number | null;
  closing_date: string | null;
  funded_date: string | null;
  cancelled_date: string | null;
  /** The date the file ended, whichever way it ended. */
  final_disposition_date: string | null;
  /** Always "withdrawn" for a cancelled file — Stewardship brokers and
   *  makes no credit decisions, so `denied` is not a value this takes. */
  disposition: "funded" | "withdrawn";
  property_address: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  broker_fee: number | null;
  voe_condo_other_fee: number | null;
  amount_earned: number | null;
  credit_report_fee: number | null;
  client_savings: number | null;
  notes: string | null;
}

// ── Insurance commission reconciliation ──────────────────────────────

export interface CommissionReconciliationTotalsRec {
  /** Rows in the engine's own table — NOT the live population when a
   *  sweep is mid-drain. See live_policies_total. */
  policies_total: number;
  /** COUNT(*) over insurance_policies directly — computed independently
   *  of policies_total so a stalled sweep can't hide behind its own row
   *  count. */
  live_policies_total: number;
  /** live_policies_total - policies_total. Nonzero means the nightly
   *  sweep hasn't reached every policy yet. */
  policies_not_yet_processed: number;
  policies_with_coverage: number;
  /** Policies no statement feed can judge — reported as prominently as
   *  the findings, so nobody reads the totals as covering the book. */
  policies_without_coverage: number;
  unpaid_count: number;
  unpaid_expected_cents: number;
  variance_count: number;
  variance_cents: number;
  unplaced_line_count: number;
  unplaced_line_cents: number;
  carriers_with_feed: number;
  carriers_total: number;
  last_computed_at: string | null;
}

export interface CommissionReconciliationCarrierRec {
  carrier_key: string;
  carrier_display_name: string;
  policy_count: number;
  expected_cents: number;
  matched_cents: number;
  unpaid_count: number;
  unpaid_expected_cents: number;
  variance_count: number;
  variance_cents: number;
  no_coverage_count: number;
  awaiting_count: number;
  has_statement_feed: number;
}

export interface CommissionTieoutRec {
  carrier_key: string;
  carrier_display_name: string;
  snapshot_month: string;
  statement_commission_cents: number | null;
  statement_line_count: number;
  qb_account_name: string | null;
  qb_income_cents: number | null;
  /**
   * What the policies themselves say we were owed for terms starting that
   * month. The only "are we being paid?" number available for a carrier
   * that transmits no statements.
   */
  expected_commission_cents: number | null;
  expected_commission_max_cents: number | null;
  expected_policy_count: number | null;
  variance_cents: number | null;
  status: "aligned" | "variance" | "no_statements" | "no_qb_mapping" | "no_data";
}

/** One row of a carrier's typed schedule, next to what it actually pays. */
export interface CommissionCarrierScheduleRec {
  carrier_key: string;
  carrier_display_name: string;
  policy_type: string;
  basis: "new_business" | "renewal";
  bundling: "any" | "bundled" | "monoline";
  rate_milli: number;
  rate_max_milli: number | null;
  effective_from: string;
  effective_to: string | null;
  unrepresented_note: string | null;
  source_note: string | null;
  observed_rate_milli: number | null;
  observed_confidence: "high" | "medium" | "low" | null;
  observation_count: number | null;
}

export interface CommissionLearnedRateRec {
  carrier_display_name: string;
  policy_type: string;
  basis: string;
  rate_milli: number;
  observation_count: number;
  distinct_rate_count: number;
  concentration_pct: number;
  confidence: "high" | "medium" | "low";
  source: "observed" | "carrier_comp_plan";
}

export interface CommissionReconciliationRec {
  totals: CommissionReconciliationTotalsRec;
  carriers: CommissionReconciliationCarrierRec[];
  tieout: CommissionTieoutRec[];
  rates: CommissionLearnedRateRec[];
  schedules: CommissionCarrierScheduleRec[];
  reliable_feed_start_month: string;
  from_month: string;
}

// The commission exception working list. Mirrors CommissionExceptionRow
// in shared/db/commission-reconciliation.ts.
export interface CommissionExceptionRec {
  kind: "unpaid" | "short_paid" | "overpaid" | "unplaced_line";
  carrier: string;
  household_name: string | null;
  household_id: string | null;
  policy_number: string | null;
  policy_type: string | null;
  basis: string | null;
  term_effective: string | null;
  premium_cents: number | null;
  expected_cents: number | null;
  received_cents: number | null;
  difference_cents: number | null;
  overdue_since: string | null;
  statement_date: string | null;
  insured_on_statement: string | null;
  rate_basis: string | null;
  what_to_check: string;
}

export interface CommissionExceptionsRec {
  rows: CommissionExceptionRec[];
  /** True when a source query filled its cap — the page says so. */
  truncated: boolean;
  /** COUNT(*) over the whole population, not rows.length. */
  total: number;
}

export interface ClosedLoanReportRec {
  year: number;
  loan_type: "traditional" | "private_money";
  rows: ClosedLoanReportRowRec[];
  /** COUNT(*) over the same filter — the whole population. */
  total: number;
  truncated: boolean;
  years: number[];
}

// The report's cancelled tab (Grant, 2026-09-17): files that ended
// without funding, read from Processing records — a cancelled
// application is never a loan record. Mirrors
// shared/db/closed-loan-report.ts CancelledApplicationRow.
export interface CancelledApplicationRowRec {
  transaction_id: string;
  household_id: string;
  household_display_name: string | null;
  borrower_name: string | null;
  loan_number: string | null;
  lender_name: string | null;
  loan_officer_name: string | null;
  loan_program_type: string | null;
  /** intended_service_type slug (purchase_mortgage, …). */
  loan_purpose: string | null;
  application_date: string | null;
  loan_amount: number | null;
  interest_rate: number | null;
  term_months: number | null;
  /** Null when the sheet never said when the file ended. */
  final_disposition_date: string | null;
  /** The year it is reported under: the end date's year, else the year of
   *  the workbook the row came from. */
  final_disposition_year: number;
  disposition:
    | "withdrawn"
    | "denied"
    | "approved_not_accepted"
    | "file_closed_incomplete"
    | "unclassified";
  property_address: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  outcome_reason: string | null;
}

export interface CancelledApplicationsReportRec {
  year: number;
  loan_type: "traditional" | "private_money";
  rows: CancelledApplicationRowRec[];
  total: number;
  truncated: boolean;
  years: number[];
}

// Closing Disclosure comparison (close-out program PR 4). Mirrors
// shared/transactions/cd-compare.ts + shared/ai/cd-extractor.ts.
export interface CdCompareRowRec {
  field:
    | "loan_amount"
    | "interest_rate"
    | "loan_term"
    | "closing_date"
    | "cash_to_close"
    | "monthly_principal_interest";
  label: string;
  os_value: string | null;
  cd_value: string | null;
  /** true = agree · false = genuinely differ · null = can't compare. */
  matches: boolean | null;
  writable: boolean;
}
export interface CdCompareResponse {
  extraction: {
    document_kind: string;
    is_closing_disclosure: boolean;
    note: string;
  } & Record<string, unknown>;
  rows: CdCompareRowRec[];
  mortgage_loan_id: string;
  cd_writable: {
    loan_amount_cents: number | null;
    interest_rate: number | null;
    term_months: number | null;
    closing_date: string | null;
    /** From the CD's DISBURSEMENT date — never its closing date. The two
     *  sit side by side on the form and are routinely days apart. */
    funded_date: string | null;
    cash_to_close_cents: number | null;
    monthly_pi_cents: number | null;
  };
}

/** One row of the Note ↔ loan record comparison. */
export interface NoteCompareRowRec {
  field: "first_payment_date" | "principal_amount" | "interest_rate" | "monthly_principal_interest";
  label: string;
  os_value: string | null;
  note_value: string | null;
  /** true = agree · false = genuinely differ · null = can't compare. */
  matches: boolean | null;
  writable: boolean;
}

export interface NoteCompareResponse {
  extraction: {
    document_kind: string;
    is_promissory_note: boolean;
    note: string;
  } & Record<string, unknown>;
  rows: NoteCompareRowRec[];
  mortgage_loan_id: string;
  note_writable: {
    first_payment_date: string | null;
    loan_amount_cents: number | null;
    interest_rate: number | null;
    monthly_pi_cents: number | null;
  };
}

/**
 * "Checked against the closing package" — who last reconciled this loan
 * against the legal document, and whether the figures have moved since.
 *
 * An attestation, not a computed claim that the numbers agree: it records
 * that a person looked. `stale` answers the separate question of whether
 * the record changed after they did.
 */
export interface ClosingPackageVerificationRec {
  mortgage_loan_id: string;
  verified_at: string;
  verified_by_user_id: string;
  verified_by_name: string | null;
  document_id: string | null;
  document_filename: string | null;
  stale: boolean;
  /** Plain-language names of the fields that moved since the check. */
  changed_fields: string[];
}

// Mortgage file close-out (Cambria + the assigned LO's shared
// checklist). Mirrors shared/db/mortgage-close-out.ts.
export type CloseOutItemStatus =
  | "missing"
  | "uploaded"
  | "signed"
  | "issue"
  | "verified"
  | "not_applicable";

export type CloseOutCourt = "lo" | "compliance" | "done" | "none";

export interface CloseOutItemRec {
  definition: DocumentDefinitionRec;
  status: CloseOutItemStatus;
  court: CloseOutCourt;
  /** Set when a person marked this row "not needed on this file". */
  waiver?: {
    id: string;
    reason: string;
    created_by_name: string | null;
    created_at: string;
  } | null;
  document: (DocumentRec & { verified_by_name: string | null }) | null;
  matched_count: number;
  /**
   * The OTHER definitions that satisfy this same requirement — empty on
   * every ordinary row, and one entry on private money's "Credit Report
   * OR Borrower Ability to Repay" pair (Baker, 2026-09-10). Optional
   * here so a client running against an older API doesn't crash on its
   * absence.
   */
  alternatives?: DocumentDefinitionRec[];
}

// ─── Commission worksheet generated from the broker check ─────────────
//
// The "Check or Wire" and "Disbursement" rows of the close-out checklist
// are the same document (Grant + Baker, 2026-08-11). These types carry
// the disbursement half of the close-out card.

export interface CheckVsExpectedRec {
  /** "matches" · "differs" · "unknown" (a side we can't compare). */
  agreement: "matches" | "differs" | "unknown";
  check_cents: number | null;
  expected_cents: number | null;
  difference_cents: number | null;
  sentence: string;
}

export interface CloseOutDocumentRefRec {
  id: string;
  filename: string;
  web_url: string | null;
  verified: boolean;
}

export interface LoanWorksheetStateRec {
  check_document: CloseOutDocumentRefRec | null;
  disbursement_document: CloseOutDocumentRefRec | null;
  worksheet: {
    id: string;
    status: string;
    gross_cents: number;
    total_costs_cents: number;
    net_cents: number;
    advisor_disbursement_cents: number;
    advisor_split_bps: number | null;
    advisor_name: string;
    costs_missing: boolean;
    /** The document the Total Broker Check was last read from; null = not known. */
    source_document_id: string | null;
    /** False once paid: a paid worksheet is corrected by an adjustment. */
    restatable: boolean;
  } | null;
  blocked: {
    reason: "not_traditional_mortgage" | "no_loan_record" | "no_loan_officer";
    message: string;
  } | null;
  comparison: CheckVsExpectedRec | null;
  /** The worksheet no longer describes the check on file (null = agrees, or not known). */
  check_mismatch: "different_document" | "document_removed" | null;
}

export interface RestatedWorksheetResponse {
  worksheet_id: string;
  changed: boolean;
  was_approved: boolean;
  before: {
    gross_cents: number;
    net_cents: number;
    advisor_disbursement_cents: number;
  };
  gross_cents: number;
  net_cents: number;
  advisor_disbursement_cents: number;
  advisor_split_bps: number | null;
  superseded_document_id: string | null;
  comparison: CheckVsExpectedRec;
}

export interface GenerateDisbursementResponse {
  worksheet_id: string;
  created: boolean;
  status: string;
  message?: string;
  gross_cents?: number;
  net_cents?: number;
  advisor_disbursement_cents?: number;
  advisor_split_bps?: number | null;
  comparison?: CheckVsExpectedRec;
}

export type FiledDisbursement =
  | { ok: true; document_id: string; web_url: string; filename: string }
  | { ok: false; reason: string; message: string };

export interface PayrollAdvisorRowRec {
  advisor_user_id: string;
  advisor_name: string;
  decided_cents: number;
  decided_count: number;
  paid_cents: number;
  paid_count: number;
  outstanding_cents: number;
  outstanding_count: number;
  awaiting_approval_cents: number;
  awaiting_approval_count: number;
  paychecks_cents: number[];
  companies: string[];
}

export interface CommissionPayrollRec {
  period: string;
  rows: PayrollAdvisorRowRec[];
  totals: {
    advisor_count: number;
    item_count: number;
    outstanding_cents: number;
    awaiting_approval_cents: number;
    awaiting_approval_count: number;
    paid_cents: number;
  };
  unassigned: { count: number; cents: number };
  available_periods: string[];
}

export interface PayrollAdvisorItemRec {
  id: string;
  display_label: string;
  entity_type: string;
  entity_id: string;
  company: string;
  status: string;
  gross_commission_cents: number;
  net_income_cents: number;
  advisor_disbursement_cents: number;
  notes: string | null;
}

/** One page's scrubbed snippet, as the browser prepares it. */
export interface PackagePageSnippet {
  page: number;
  text: string;
  has_text: boolean;
  /** A scanned page's top+bottom bands as a base64 JPEG — only on a
   *  page with no text (shared/documents/package-classification.ts). */
  image_jpeg_base64?: string;
  /** The footer of a typed page, and its printed "Page N of M". */
  tail?: string;
  printed_page?: number;
  printed_of?: number;
}

/** One document Stew claims to have found inside a combined package. */
export interface PackageProposalRec {
  /** The checklist requirement it fills, or null for "found it, but it
   *  isn't one of the required documents". */
  definition_id: string | null;
  label: string;
  start_page: number;
  end_page: number;
  confidence: "high" | "low";
  note: string;
}

export interface PackageClassificationResponse {
  documents: PackageProposalRec[];
  /** Pages no proposal covers — shown, never silently dropped. */
  unaccounted_pages: number[];
  page_count: number;
}

/**
 * One window's un-ruled-on answer (2026-09-02).
 *
 * Deliberately loose: these are the model's own claims, and every field
 * is checked by normalizeProposals once the caller has merged all the
 * windows. Typing them as known-good here would be a lie the merge step
 * then has to undo.
 */
export interface PackageClassificationWindowResponse {
  raw_documents: unknown[];
}

export type MortgageCloseOutResponse =
  | {
      applicable: true;
      transaction_id: string;
      /** Deep-link to the servicer/investor system (Sonar/Jupiter) — the
       *  authoritative source for post-close loan data; the OS links
       *  out, never mirrors. Null when the loan has none recorded. */
      servicer_portal_url: string | null;
      lender_name: string | null;
      /** NMLS lien status on the loan, or null when unanswered. Read off
       *  the final 1003 — a Closing Disclosure does not state it. */
      lien_status?: string | null;
      /** The linked service record + its type. The type decides which
       *  documents apply (Right to Cancel is refi-only), so the card
       *  states it and offers the retype rather than letting a
       *  mis-typed file hide required rows. */
      service_record_id: string | null;
      service_type: string | null;
      /** The linked mortgage_loans row, when one exists. Null means the
       *  Processing record has no loan behind it — the loan-details
       *  panel and the commission worksheet both need one. */
      mortgage_loan_id: string | null;
      /** Purchase or refinance, and when neither, why we can't tell.
       *  An unknown purpose makes the checklist over-ask on purpose;
       *  the card says so rather than silently listing extra rows. */
      loan_purpose: "purchase" | "refinance" | "unknown";
      loan_purpose_reason: "known" | "not_set" | "conflict" | "other_product";
      /** Rows on the list ONLY because the purpose is unknown. */
      rows_pending_loan_purpose: number;
      items: CloseOutItemRec[];
      untagged: DocumentRec[];
      /** Packages the splitter cut documents from — shown as sources,
       *  never offered for filing (2026-09-02). */
      source_packages: {
        document: DocumentRec;
        piece_count: number;
        filed_count: number;
      }[];
      progress: {
        total_applicable: number;
        verified: number;
        awaiting_upload: number;
        awaiting_verification: number;
        not_applicable: number;
      };
      /** The commission worksheet / disbursement half of the card. */
      disbursement?: LoanWorksheetStateRec;
      /** Who last reconciled this loan against the closing package, and
       *  whether the figures moved since. Null when nobody has — which
       *  the card renders as an ask, never as reassurance. */
      closing_package_verification?: ClosingPackageVerificationRec | null;
    }
  | {
      applicable: false;
      // "private_money" was removed 2026-09-18 — private money has its
      // own close-out checklist now rather than being refused one.
      reason: "not_mortgage" | "not_loan_progression";
    };

export interface CommissionDisbursementRec {
  id: string;
  /** Server-built name: "Loan - Wellman", "Commission Pool - September 2026". */
  display_label: string;
  entity_type: string;
  entity_id: string;
  advisor_user_id: string;
  company: string;
  gross_commission_cents: number;
  total_transaction_costs_cents: number;
  net_income_cents: number;
  advisor_split_bps: number | null;
  advisor_disbursement_cents: number;
  brokerage_retained_cents: number;
  status: string;
  calculated_at: string | null;
  approved_at: string | null;
  paid_at: string | null;
  paid_method: string | null;
  paid_reference: string | null;
  period_start: string | null;
  period_end: string | null;
  notes: string | null;
}

export interface CommissionCostRec {
  id: string;
  disbursement_id: string;
  cost_type: string;
  description: string | null;
  amount_cents: number;
}

/** The record a Commission Worksheet was generated from — the "back to
 *  the record" link. Null for a monthly commission pool, which has no
 *  single record to return to. */
export interface DisbursementSourceLinkRec {
  kind: "processing" | "opportunity" | "household";
  id: string;
  label: string;
  href: string;
}

export interface MyCommissionsResponse {
  ytd_paid_cents: number;
  pending_count: number;
  pending_total_cents: number;
  latest_paid: {
    amount_cents: number;
    paid_at: string;
    reference: string | null;
  } | null;
  recent: CommissionDisbursementRec[];
}

export interface LoanAuditListItem {
  loan_id: string;
  loan_type: string;
  loan_number: string | null;
  display_label: string | null;
  household_id: string | null;
  target_close: string | null;
  required_count: number;
  present_count: number;
  verified_count: number;
  severity: "red" | "yellow" | "green" | "blue";
}

export interface LoanAuditDetail {
  loan: {
    id: string;
    loan_type: string;
    loan_number: string | null;
    funded_date: string | null;
    clear_to_close_date: string | null;
    display_label: string | null;
    household_id: string | null;
    household_name: string | null;
  };
  definitions: DocumentDefinitionRec[];
  documents: DocumentRec[];
}

// v1.1 API helpers. Exported as standalone functions instead of
// appended to the `api` object so TypeScript picks them up without
// re-declaring the api object's type.
/** One checkbox in the Realty offer builder. */
export interface RealtyOfferFormChoice {
  templateId: string;
  label: string;
  documentType: string | null;
  formKey: string | null;
  revisionLabel: string | null;
  category: string | null;
  /** Plain English: why this one is pre-checked, or what it is for. */
  because: string;
  /** The blank's content fingerprint, so Prepare & Send recognizes a
   *  form the team has laid out before without re-deriving it. */
  contentFingerprint: string | null;
  /** "AAR" or "Stewardship" — who publishes the form. Rendered as a
   *  badge on every row (Nate liked BrokerMint's). Null when nobody has
   *  recorded one. */
  publisher: string | null;
}

/** One blank document in a company's Industry Documents library, as the
 *  Templates editor reads it. */
export interface EsignTemplateRow {
  id: string;
  companySlug: string;
  name: string;
  description: string | null;
  documentType: string | null;
  filename: string;
  contentFingerprint: string | null;
  publisher: string | null;
  revisionLabel: string | null;
  category: string | null;
  appliesToSide: string | null;
  archivedAt: string | null;
}

/** One document inside a saved Template, in signing order. */
export interface EsignTemplatePackageItem {
  id: string;
  esignTemplateId: string;
  position: number;
  templateName: string | null;
  templateDocumentType: string | null;
  templateContentFingerprint: string | null;
  templatePublisher: string | null;
  templateRevisionLabel: string | null;
  /** The blank was archived after this Template was saved. Shown, never
   *  hidden — sending a package with a hole in it is the failure this
   *  flag exists to prevent. */
  templateArchived: boolean;
  /** Not always needed (PR 8a): unticked in the wizard's step 1, with
   *  its note. Optional on older fixtures. */
  optional?: boolean;
  conditionNote?: string | null;
}

/** A signer SEAT the Template expects — "Buyer", "Seller" — never a
 *  person. Resolved against whoever is on the record at send time. */
export interface EsignTemplatePackageSigner {
  id: string;
  position: number;
  roleLabel: string;
}

/** A Template: a named, ordered package a company sends in one click. */
export interface EsignTemplatePackage {
  id: string;
  companySlug: string;
  name: string;
  description: string | null;
  /** "buy" | "sell" | "both" | "na". */
  side: string;
  purposeTag: string | null;
  /** Step 1's heading ("Buyer", "Contract-to-close"); null groups by side
   *  (PR 8a). Optional on older fixtures. */
  groupLabel?: string | null;
  /** Stage slugs it fits; empty means any stage (PR 8a). */
  stageFit?: string[];
  createdByUserId: string | null;
  createdByName: string | null;
  updatedByUserId: string | null;
  updatedByName: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items: EsignTemplatePackageItem[];
  signers: EsignTemplatePackageSigner[];
}

export interface EsignTemplatePackageWrite {
  name?: string;
  description?: string | null;
  side?: string;
  purpose_tag?: string | null;
  /** IN SIGNING ORDER — the array index becomes the position, so
   *  reordering is just sending the list rearranged. */
  template_ids?: string[];
  signer_roles?: string[];
  /** Step 1's grouping and stage fit (PR 8a). */
  group_label?: string | null;
  stage_fit?: string[];
  /** Which forms are optional and when they're needed (PR 8a). */
  item_settings?: Array<{ template_id: string; optional: boolean; condition_note: string | null }>;
}

/** Step 1 of the package wizard: the templates for one record (PR 8a). */
export interface TemplatesForRecordResponse {
  record: {
    kind: "opportunity" | "transaction";
    id: string;
    company: string;
    side: "buy" | "sell" | null;
    stage: string;
  };
  packages: EsignTemplatePackage[];
  hidden_other_side: number;
  used: Array<{ package_id: string; last_used_at: string }>;
  /** The file's document items still to come, by type: what a ticked
   *  form checks off when the package completes (PR 11). Absent when the
   *  caller can't use this company's templates. */
  checklist_to_collect?: Array<{ document_definition_id: string; title: string; checklist_name: string }>;
}

/** What POST /api/esign/templates/:id/new-revision hands back — the new
 *  revision plus the carry-over report: what came across untouched, what
 *  landed on a caption that moved, and what could not be matched and is
 *  now unmapped for a person to place. */
export interface TemplateRevisionResult {
  template: { id: string; name: string; companySlug: string; revisionLabel: string | null };
  superseded_template_id: string;
  contract_form_id: string | null;
  /** Plain English naming the packages that now use the new revision,
   *  when any do. Null for a document no package includes. */
  package_pickup: string | null;
  layout_id: string | null;
  carry_over: {
    total: number;
    carried: number;
    needs_review: number;
    summary: string;
    review: Array<{
      what: string;
      quality: "moved" | "reworded";
      page: number;
      matched_caption: string;
    }>;
    /** How many there ARE. The list above is capped for display, so a
     *  screen showing fifty of ninety can say so instead of reading as
     *  the whole story. */
    review_total: number;
    missed: Array<{
      what: string;
      reason: "no_anchor" | "caption_gone" | "ambiguous";
      former_caption: string | null;
    }>;
    missed_total: number;
  };
}

export const apiV11 = {
  async getBakerDashboardV11(): Promise<BakerDashboardV11Response> {
    return request<BakerDashboardV11Response>("/api/dashboard/baker");
  },

  async getMyCommissions(): Promise<MyCommissionsResponse> {
    return request<MyCommissionsResponse>("/api/dashboard/my-commissions");
  },

  async listLoanFileAudits(): Promise<{
    items: LoanAuditListItem[];
    total: number;
  }> {
    return request<{ items: LoanAuditListItem[]; total: number }>("/api/admin/loan-file-audits");
  },

  async getLoanFileAudit(loanId: string): Promise<LoanAuditDetail> {
    return request<LoanAuditDetail>(`/api/admin/loan-file-audits/${encodeURIComponent(loanId)}`);
  },

  async listCommissionDisbursements(
    params: { company?: string; status?: string; advisor?: string } = {},
  ): Promise<{ items: CommissionDisbursementRec[]; total: number }> {
    const qs = new URLSearchParams();
    if (params.company) qs.set("company", params.company);
    if (params.status) qs.set("status", params.status);
    if (params.advisor) qs.set("advisor", params.advisor);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ items: CommissionDisbursementRec[]; total: number }>(
      `/api/admin/commission-disbursements${suffix}`,
    );
  },

  async getCommissionDisbursement(id: string): Promise<{
    disbursement: CommissionDisbursementRec;
    costs: CommissionCostRec[];
    source_link: DisbursementSourceLinkRec | null;
    /** True when the advisor is paid through Gusto (preselects Payroll). */
    paid_through_gusto: boolean;
  }> {
    return request<{
      disbursement: CommissionDisbursementRec;
      costs: CommissionCostRec[];
      source_link: DisbursementSourceLinkRec | null;
      paid_through_gusto: boolean;
    }>(`/api/admin/commission-disbursements/${encodeURIComponent(id)}`);
  },

  async calculateDisbursement(
    id: string,
    body: {
      gross_commission_cents?: number;
      costs?: Array<{
        cost_type: string;
        description?: string;
        amount_cents: number;
      }>;
    },
  ): Promise<{
    ok: true;
    gross: number;
    total_costs: number;
    net_income: number;
    advisor_split_bps: number | null;
    advisor_disbursement: number;
    brokerage_retained: number;
  }> {
    return request(`/api/admin/commission-disbursements/${encodeURIComponent(id)}/calculate`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Correct a loan worksheet's Total Broker Check by hand (system admin).
  // Costs are kept; NET and the split are recomputed server-side, and an
  // approved worksheet goes back to "calculated" for re-approval.
  async restateDisbursementCheck(
    id: string,
    body: { gross_commission_cents: number; reason: string },
  ): Promise<{
    ok: true;
    changed: boolean;
    was_approved: boolean;
    gross: number;
    total_costs: number;
    net_income: number;
    advisor_split_bps: number | null;
    advisor_disbursement: number;
    brokerage_retained: number;
    superseded_document_id: string | null;
  }> {
    return request(
      `/api/admin/commission-disbursements/${encodeURIComponent(id)}/restate-check`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  // Approving a per-loan mortgage worksheet also files its PDF onto the
  // loan's close-out checklist. `filed` reports that half: null when the
  // worksheet isn't a loan file (an Insurance/Financial monthly pool row
  // has no checklist), otherwise the outcome of the filing.
  async approveDisbursement(id: string): Promise<{ ok: true; filed: FiledDisbursement | null }> {
    return request<{ ok: true; filed: FiledDisbursement | null }>(
      `/api/admin/commission-disbursements/${encodeURIComponent(id)}/approve`,
      { method: "POST", body: "{}" },
    );
  },

  // Retry filing when approval succeeded but storage was unavailable.
  async fileDisbursementDocument(id: string): Promise<{ ok: boolean; filed: FiledDisbursement }> {
    return request<{ ok: boolean; filed: FiledDisbursement }>(
      `/api/admin/commission-disbursements/${encodeURIComponent(id)}/file-document`,
      { method: "POST", body: "{}" },
    );
  },

  // Monthly payroll rollup — what each advisor is owed for a month.
  async getCommissionPayroll(period?: string): Promise<CommissionPayrollRec> {
    const qs = period ? `?period=${encodeURIComponent(period)}` : "";
    return request<CommissionPayrollRec>(`/api/admin/commission-payroll${qs}`);
  },
  async getCommissionPayrollForAdvisor(
    advisorUserId: string,
    period: string,
  ): Promise<{
    period: string;
    advisor_user_id: string;
    items: PayrollAdvisorItemRec[];
    total: number;
  }> {
    return request(
      `/api/admin/commission-payroll/${encodeURIComponent(advisorUserId)}?period=${encodeURIComponent(period)}`,
    );
  },

  async markDisbursementPaid(
    id: string,
    body: { paid_method?: string; paid_reference?: string } = {},
  ): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/admin/commission-disbursements/${encodeURIComponent(id)}/mark-paid`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  async uploadDocument(
    file: File,
    options: {
      entity_type: string;
      entity_id: string;
      document_type?: string;
    },
  ): Promise<{ document_id: string; filename: string }> {
    const reserve = await request<{
      document_id: string;
      upload_url: string;
      r2_key: string;
    }>("/api/files/upload-url", {
      method: "POST",
      body: JSON.stringify({
        filename: file.name,
        mime_type: file.type,
        entity_type: options.entity_type,
        entity_id: options.entity_id,
        document_type: options.document_type,
      }),
    });
    const putRes = await fetch(reserve.upload_url, {
      method: "PUT",
      body: await file.arrayBuffer(),
      credentials: "include",
    });
    if (!putRes.ok) {
      throw new Error(`upload_failed_${putRes.status}`);
    }
    return { document_id: reserve.document_id, filename: file.name };
  },

  // v1.x phase 11.15 — SharePoint upload. Replaces the R2
  // reserve-then-PUT dance for new uploads. The bytes go through the
  // Worker once; the Worker uploads to SharePoint via Graph and
  // persists the documents row in one transaction.
  //
  // Two server routes, picked here by size (2026-08-11): under 4 MB
  // goes to /upload as multipart (one request, simplest path); over
  // goes to /upload-large, which streams the raw body into a Graph
  // upload session. Callers don't choose — they just pass a File, and
  // an 80 MB closing package works the same as a 200 KB W-2.
  async uploadDocumentSharePoint(
    file: File,
    options: {
      entity_type: string;
      entity_id: string;
      document_type?: string;
      /** The package this file was cut from (close-out splitter). */
      split_from_document_id?: string;
      /** "received" when the other side wrote this file — the listing
       *  agent's counteroffer, arriving as a PDF. Omitted means ours.
       *  See shared/esign/document-origin.ts. */
      origin?: "ours" | "received";
    },
  ): Promise<{ document: DocumentRec }> {
    const FOUR_MB = 4 * 1024 * 1024;
    const large = file.size > FOUR_MB;

    let res: Response;
    if (large) {
      const qs = new URLSearchParams({
        entity_type: options.entity_type,
        entity_id: options.entity_id,
        filename: file.name,
        size: String(file.size),
      });
      if (options.document_type) qs.set("document_type", options.document_type);
      if (options.split_from_document_id) {
        qs.set("split_from_document_id", options.split_from_document_id);
      }
      if (options.origin === "received") qs.set("origin", "received");
      res = await fetch(apiUrl(`/api/files/upload-large?${qs.toString()}`), {
        method: "POST",
        // The File itself is the body — the browser streams it rather
        // than materializing it, which is the whole point of this path.
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
        credentials: "include",
      });
    } else {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", options.entity_type);
      fd.append("entity_id", options.entity_id);
      if (options.document_type) {
        fd.append("document_type", options.document_type);
      }
      if (options.split_from_document_id) {
        fd.append("split_from_document_id", options.split_from_document_id);
      }
      if (options.origin === "received") fd.append("origin", "received");
      // We can't use request() here because it sets Content-Type to
      // application/json by default. Use fetch directly — credentials
      // include for the auth cookie. apiUrl() handles the optional
      // API_ORIGIN prefix for cross-origin dev environments.
      res = await fetch(apiUrl("/api/files/upload"), {
        method: "POST",
        body: fd,
        credentials: "include",
      });
    }
    if (!res.ok) {
      let payload: unknown = null;
      try {
        payload = await res.json();
      } catch {
        // ignore parse errors
      }
      const err =
        payload && typeof payload === "object"
          ? (payload as { error?: string; message?: string })
          : {};
      throw new ApiError(
        res.status,
        err.message ?? err.error ?? `upload_failed_${res.status}`,
        err.error ?? null,
      );
    }
    return (await res.json()) as { document: DocumentRec };
  },

  async listDocumentsForEntity(
    entityType: string,
    entityId: string,
  ): Promise<{ items: DocumentRec[]; total: number }> {
    const qs = new URLSearchParams({
      entity_type: entityType,
      entity_id: entityId,
    });
    return request<{ items: DocumentRec[]; total: number }>(
      `/api/files/documents?${qs.toString()}`,
    );
  },

  // v1.x phase 11.16 (Tier B item 13) — OneDrive read-only browse.
  // Either lists root / a folder's children, follows a nextLink, or
  // searches by query — server picks the mode based on params. The
  // response items NEVER carry the @microsoft.graph.downloadUrl —
  // that's server-only.
  async browseOneDrive(
    opts: { folderId?: string | null; next?: string | null; q?: string | null } = {},
  ): Promise<OneDriveBrowseResponse> {
    const qs = new URLSearchParams();
    if (opts.folderId) qs.set("folder_id", opts.folderId);
    if (opts.next) qs.set("next", opts.next);
    if (opts.q && opts.q.trim().length > 0) qs.set("q", opts.q.trim());
    const tail = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<OneDriveBrowseResponse>(`/api/files/onedrive/browse${tail}`);
  },

  // v1.x phase 11.16 (Tier B item 13) — copy a OneDrive file to
  // SharePoint as a Stewardship-managed document. Server fetches
  // bytes from OneDrive via Graph + uploads to SharePoint via the
  // existing graph-client-files helpers. 4 MB cap (matches
  // SharePoint upload cap; v1.1 lifts to upload sessions).
  async copyOneDriveItemToEntity(input: {
    onedrive_item_id: string;
    entity_type: OneDriveCopyEntityType;
    entity_id: string;
    document_type?: string;
  }): Promise<OneDriveCopyResponse> {
    return request<OneDriveCopyResponse>(`/api/files/onedrive/copy-to-entity`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // v1.x phase 11.16 (Tier B item 11) — magic-link client upload.
  // Mint, list, revoke. The advisor uses these to manage links on
  // a household / contact record. The raw token is returned ONCE
  // at mint time so the caller can copy it into a client email.
  async mintClientUploadToken(input: {
    entity_type: "household" | "contact";
    entity_id: string;
    recipient_label?: string | null;
    prompt_note?: string | null;
    window_days?: number;
    expires_at?: string;
  }): Promise<MintClientUploadTokenResponse> {
    return request<MintClientUploadTokenResponse>(`/api/client-upload/tokens`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async listClientUploadTokens(
    entityType: "household" | "contact",
    entityId: string,
  ): Promise<{ tokens: ClientUploadTokenSummary[] }> {
    const qs = new URLSearchParams({
      entity_type: entityType,
      entity_id: entityId,
    });
    return request<{ tokens: ClientUploadTokenSummary[] }>(
      `/api/client-upload/tokens?${qs.toString()}`,
    );
  },
  async revokeClientUploadToken(
    tokenId: string,
  ): Promise<{ token: ClientUploadTokenSummary; already_revoked?: boolean }> {
    return request<{
      token: ClientUploadTokenSummary;
      already_revoked?: boolean;
    }>(`/api/client-upload/tokens/${encodeURIComponent(tokenId)}/revoke`, {
      method: "POST",
      body: "{}",
    });
  },

  async verifyDocument(id: string, notes?: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/files/documents/${encodeURIComponent(id)}/verify`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    });
  },

  async unverifyDocument(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/files/documents/${encodeURIComponent(id)}/unverify`, {
      method: "POST",
      body: "{}",
    });
  },

  async flagDocumentIssue(id: string, notes: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/files/documents/${encodeURIComponent(id)}/flag-issue`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    });
  },

  // v1.x phase 11.16 (Tier B item 6) — short-lived preview URL for
  // inline rendering. Returns a Microsoft-issued URL safe to drop
  // into an iframe. SharePoint-only; R2 rows return 422.
  async getDocumentPreviewUrl(id: string): Promise<{
    preview_url: string;
    expires_at: string;
    filename: string;
    mime_type: string | null;
  }> {
    return request(`/api/files/preview-url/${encodeURIComponent(id)}`);
  },

  // v1.x phase 11.16 (Tier A items 1+2) — rename a document. Goes
  // to PATCH /api/files/documents/:id which renames in SharePoint
  // (when SharePoint-backed) and updates the documents row.
  async renameDocument(id: string, filename: string): Promise<{ document: DocumentRec }> {
    return request<{ document: DocumentRec }>(`/api/files/documents/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ filename }),
    });
  },

  async archiveDocument(id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/files/documents/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      body: "{}",
    });
  },
  // Remove several documents in one action (Grant + Cambria,
  // 2026-08-13). Returns the ids it actually archived — pass those to
  // unarchiveDocumentsBulk to undo the whole run.
  async archiveDocumentsBulk(documentIds: string[]): Promise<{
    ok: true;
    archived_count: number;
    skipped_count: number;
    bulk_action_id: string;
    document_ids: string[];
  }> {
    return request(`/api/files/documents/archive-bulk`, {
      method: "POST",
      body: JSON.stringify({ document_ids: documentIds }),
    });
  },
  async unarchiveDocumentsBulk(
    documentIds: string[],
  ): Promise<{ ok: true; restored_count: number }> {
    return request(`/api/files/documents/unarchive-bulk`, {
      method: "POST",
      body: JSON.stringify({ document_ids: documentIds }),
    });
  },

  // ── Household documents, grouped by company ────────────────────────
  // Grant's relevance-ordering decision (2026-08-16): every line of
  // business can see every document, but your own leads and the rest
  // sit behind extra clicks. The server orders; nothing is filtered
  // out, so a collapsed group still contains every document in it.

  // Documents filed on records this PERSON is named on — the companion
  // to listHouseholdDocuments, so moving a document to a loan doesn't
  // take it off the person's page.
  async listContactRelatedDocuments(contactId: string): Promise<{
    documents: ContactRelatedDocumentRec[];
  }> {
    return request(`/api/files/contact-documents/${encodeURIComponent(contactId)}`);
  },

  // Where a document could be filed — scoped to one family, so another
  // household's records can never appear as an option.
  async listDocumentMoveTargets(householdId: string): Promise<{
    targets: DocumentMoveTarget[];
  }> {
    return request(`/api/files/move-targets/${encodeURIComponent(householdId)}`);
  },

  // Re-file a document onto the record it belongs to. `moved_from`
  // comes back so the caller can offer Undo without remembering where
  // it was — undo is this same call in reverse.
  async moveDocument(
    documentId: string,
    to: { entity_type: string; entity_id: string },
    reason?: string,
  ): Promise<{
    document: DocumentRec;
    moved_from: { entity_type: string; entity_id: string };
    household_id: string | null;
  }> {
    return request(`/api/files/documents/${encodeURIComponent(documentId)}/move`, {
      method: "POST",
      body: JSON.stringify({ ...to, ...(reason ? { reason } : {}) }),
    });
  },

  async listHouseholdDocuments(householdId: string): Promise<{
    groups: HouseholdDocumentGroupRec[];
    total: number;
  }> {
    return request(`/api/files/household-documents/${encodeURIComponent(householdId)}`);
  },

  // ── Exam packs (system_admin / compliance only) ────────────────────
  // The logs a regulator asks for, one per company. Counting is cheap
  // and comes first; producing carries the truncation truth back in
  // headers so a partial production is never mistaken for a complete
  // one.

  async listExamPacks(): Promise<{ packs: ExamPackSummary[] }> {
    return request(`/api/exam-packs`);
  },

  // ─── The retention schedule (Document Compliance Program, R-4) ──
  //
  // Reading is open to every employee — "why is this kept ten years?"
  // is a fair question for anyone filing a document. Revising is
  // system_admin + compliance, enforced server-side.

  async listRetentionClasses(): Promise<{
    classes: RetentionClassSummary[];
    total: number;
  }> {
    return request(`/api/files/retention-classes`);
  },

  async listRetentionClassVersions(
    slug: string,
  ): Promise<{ versions: RetentionClassVersionSummary[]; total: number }> {
    return request(`/api/files/retention-classes/${encodeURIComponent(slug)}/versions`);
  },

  async reviseRetentionClass(
    slug: string,
    input: { min_years: number | null; change_reason: string },
  ): Promise<{ ok: true; version: number }> {
    return request(`/api/files/retention-classes/${encodeURIComponent(slug)}/revise`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  // ─── Legal matters (Document Compliance Program, R-3) ───────────
  //
  // A hold that covers a whole matter — a household, a record, a
  // company, the firm — rather than one document at a time. system_admin
  // + compliance only; the endpoints enforce the same gate.

  async listLegalMatters(includeReleased = false): Promise<{
    matters: LegalMatterSummary[];
    total: number;
  }> {
    return request(`/api/files/legal-matters${includeReleased ? "?include_released=1" : ""}`);
  },

  /** How many live documents a scope would hold — asked BEFORE opening,
   *  because a firm-wide hold placed by accident is expensive to notice. */
  async previewLegalMatterScope(
    scopeType: string,
    scopeId: string | null,
  ): Promise<{ scope_type: string; scope_id: string | null; documents_covered: number }> {
    return request(`/api/files/legal-matters/preview`, {
      method: "POST",
      body: JSON.stringify({ scope_type: scopeType, scope_id: scopeId }),
    });
  },

  async openLegalMatter(input: {
    name: string;
    reason: string;
    scope_type: string;
    scope_id: string | null;
  }): Promise<{ ok: true; id: string; documents_covered: number }> {
    return request(`/api/files/legal-matters`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async releaseLegalMatter(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/files/legal-matters/${encodeURIComponent(id)}/release`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  async previewExamPack(
    slug: string,
    window: { from?: string; to?: string },
  ): Promise<{ slug: string; total: number }> {
    return request(`/api/exam-packs/${encodeURIComponent(slug)}/preview`, {
      method: "POST",
      body: JSON.stringify({ from: window.from || null, to: window.to || null }),
    });
  },

  async exportExamPackCsv(
    slug: string,
    window: { from?: string; to?: string },
  ): Promise<{
    blob: Blob;
    filename: string;
    rowCount: number;
    total: number;
    truncated: boolean;
  }> {
    const response = await fetch(apiUrl(`/api/exam-packs/${encodeURIComponent(slug)}/export`), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "text/csv" },
      body: JSON.stringify({ from: window.from || null, to: window.to || null }),
    });
    if (!response.ok) {
      throw new ApiError(response.status, response.statusText || `HTTP ${response.status}`, null);
    }
    const blob = await response.blob();
    const cd = response.headers.get("Content-Disposition") ?? "";
    const filename = /filename="([^"]+)"/.exec(cd)?.[1] ?? `${slug}.csv`;
    return {
      blob,
      filename,
      rowCount: Number(response.headers.get("X-Export-Row-Count") ?? "0"),
      total: Number(response.headers.get("X-Export-Total-Matching") ?? "0"),
      truncated: response.headers.get("X-Export-Truncated") === "true",
    };
  },

  // ── Retention disposition (system_admin / compliance only) ─────────
  // The three answers to a retention-due Cleanup item. Destruction is
  // the only irreversible action in the OS's document layer; the other
  // two are the safe answers and are deliberately cheaper to reach.

  async documentDisposalEligibility(id: string): Promise<{
    eligible: boolean;
    reason: string | null;
    message: string | null;
  }> {
    return request(`/api/files/documents/${encodeURIComponent(id)}/disposal-eligibility`);
  },

  /** Permanent. Deletes the SharePoint file AND the firm's duplicate
   *  copy. `failures` non-empty means a copy may have survived. */
  async destroyDocument(
    id: string,
    input: { confirm: string; reasonCode: string; reason: string },
  ): Promise<{
    ok: true;
    deleted_from: string[];
    failures: string[];
    message: string;
  }> {
    return request(`/api/files/documents/${encodeURIComponent(id)}/destroy`, {
      method: "POST",
      body: JSON.stringify({
        confirm: input.confirm,
        reason_code: input.reasonCode,
        reason: input.reason,
      }),
    });
  },

  async extendDocumentRetention(
    id: string,
    input: { newExpiresAt: string; reason: string },
  ): Promise<{ ok: true; new_expires_at: string }> {
    return request(`/api/files/documents/${encodeURIComponent(id)}/extend-retention`, {
      method: "POST",
      body: JSON.stringify({
        new_expires_at: input.newExpiresAt,
        reason: input.reason,
      }),
    });
  },

  async setDocumentLegalHold(
    id: string,
    input: { hold: boolean; reason: string },
  ): Promise<{ ok: true; hold: boolean }> {
    return request(`/api/files/documents/${encodeURIComponent(id)}/legal-hold`, {
      method: "POST",
      body: JSON.stringify({ hold: input.hold, reason: input.reason }),
    });
  },
  // Mortgage close-out — tag an already-uploaded document as satisfying
  // a required checklist item (null clears the tag).
  async setDocumentType(
    id: string,
    documentType: string | null,
  ): Promise<{ document: DocumentRec }> {
    return request<{ document: DocumentRec }>(
      `/api/files/documents/${encodeURIComponent(id)}/set-type`,
      {
        method: "POST",
        body: JSON.stringify({ document_type: documentType }),
      },
    );
  },
  // "Share with client" / "Move to internal" (Nate's Aug 24 spec §9,
  // PR5 of 5) — flips which shelf a document is on.
  async setDocumentVisibility(
    id: string,
    visibility: "client" | "internal",
  ): Promise<{ document: DocumentRec; changed: boolean }> {
    return request<{ document: DocumentRec; changed: boolean }>(
      `/api/files/documents/${encodeURIComponent(id)}/set-visibility`,
      {
        method: "POST",
        body: JSON.stringify({ visibility }),
      },
    );
  },
  // Joey program PR7 — Stew analyzes a loan document (income + flags).
  async analyzeDocument(id: string): Promise<DocumentAnalysisResponse> {
    return request<DocumentAnalysisResponse>(`/api/documents/${encodeURIComponent(id)}/analyze`, {
      method: "POST",
    });
  },
  // E-signature send half (Phase 1) — send an already-uploaded document
  // on a Processing record for signature, to signers already linked to
  // that record.
  async sendForSignature(
    recordKind: EsignSendableKind,
    recordId: string,
    body: {
      /** The one-document form. Mutually exclusive with `documents` —
       *  send one or the other, never both. */
      documentId?: string;
      /** A PACKAGE: several documents signed together as one envelope,
       *  in the order the recipient meets them. Each entry carries its
       *  own fields, page sizes, stamps and fingerprint — page numbers
       *  inside an entry are pages of THAT document, and the server
       *  translates them into the merged envelope's numbering. */
      documents?: Array<{
        documentId: string;
        fields?: Array<{
          signerKey: string;
          /** The seat a LOCKED box reads its person from (Baker,
           *  2026-09-16). */
          roleKey?: string | null;
          fieldType: string;
          pageNumber: number;
          x: number;
          y: number;
          width: number;
          height: number;
          required: boolean;
          value?: string | null;
          /** Lets the signer change a value we filled in (Baker,
           *  2026-09-18). The default is that a filled-in TEXT field
           *  prints and cannot be retyped; this is the per-box opt-out
           *  for a prefill meant as a head start. */
          signerMayEdit?: boolean;
          /** Points — what a printed value is set in, read off the type
           *  around the blank. Omitted when the page had no readable
           *  text, in which case the server uses the forms' own body
           *  size rather than the provider's web-form default. */
          fontSize?: number;
          /** A text box spanning several printed lines and the points
           *  between its rules (2026-09-29). The server draws a printed
           *  multi-line value in itself, one line per rule. */
          multiline?: boolean;
          linePitch?: number;
          firstLineIndent?: number;
          /** How a locked text box's type looks (2026-10-26). */
          textStyle?: EsignTextStyle;
          mergeToken?: string | null;
          options?: string[] | null;
          groupName?: string | null;
          /** The atlas's fill rule for this blank. Sent so the server
           *  can honour a rule that is about the ENVELOPE rather than
           *  the record — today only the purchase contract's twelve
           *  "Addenda Incorporated" boxes, which tick when their
           *  addendum is in this package. */
          fillRule?: string | null;
        }>;
        /** GHOSTS (2026-09-29): the blanks the screen drew and the sender
         *  left alone. Never placed or printed; the server reads them for
         *  one thing only, re-teaching this document's saved map, so the
         *  next send offers every blank this one did. Same shape as
         *  `fields`. See shared/esign/ghost-offers.ts. */
        ghosts?: EsignSentFieldPayload[];
        pageSizes?: Array<{ pageNumber: number; width: number; height: number }>;
        stamps?: Array<{
          pageNumber: number;
          x: number;
          y: number;
          width: number;
          height: number;
        }>;
        contentFingerprint?: string;
        /** What kind of document THIS ONE is — only when it doesn't
         *  already have a type. Recorded on the package manifest, not
         *  the source document, and read back at archival to auto-file
         *  this document's own signed piece once the envelope
         *  completes. */
        documentType?: string;
        /** The pages the sender confirmed by eye. Only meaningful for a
         *  document the other side wrote, and the server decides which
         *  those are by reading `origin` off the row rather than
         *  trusting this request to say. See
         *  shared/esign/page-confirmation.ts. */
        confirmedPages?: number[];
      }>;
      /** THE SIGNER LIST (2026-09-03): one entry per signer, each
       *  either a person on this record (`contactId`) or a name and an
       *  email typed by the sender — a co-op agent, a seller, a title
       *  officer. The second kind creates NO contact and NO household;
       *  it is one row on the envelope's signer list with an empty
       *  contact link.
       *
       *  Order matters: it is the signing order when one is enforced,
       *  and it is the ordinal a saved layout is taught in. */
      signers: Array<{
        contactId?: string | null;
        name?: string;
        email?: string;
        /** For a signer with no contact: the key their placed fields are
         *  filed under. Sent explicitly rather than left to the server
         *  to derive from position — the two disagree the moment a
         *  signer between them is unchecked, and a field filed under a
         *  key nobody has is a signature line that silently vanishes. */
        signerKey?: string;
        role?: "signer" | "cc";
        /** SIGNING ROLES (2026-09-15): the seat this signer was cast
         *  into ("buyer_1", "co_borrower") and whether they sign or are
         *  stamped. Recorded on the signer row so the envelope can say
         *  who signed AS what; omitted on a send that did not cast. */
        roleKey?: string;
        roleKind?: "signs" | "stamp";
      }>;
      /** The whole casting as the envelope goes out: every seat the
       *  document names, who filled it (name and contact id, never an
       *  email) or that it was left off. Name-only seats live only
       *  here — nobody is emailed for them. */
      casting?: Array<{
        roleKey: string;
        roleKind: "signs" | "name_only" | "stamp";
        name: string | null;
        contactId: string | null;
        outcome: "sure" | "ask" | "nobody" | "manual";
        leftOff: boolean;
      }>;
      message?: string | undefined;
      /** Review & send (PR 8b): the subject the sender kept or typed.
       *  Empty or absent keeps the server's own default. */
      emailSubject?: string;
      /** A Loom share link shown as a card above the sign button. The
       *  server looks up its title itself; only Loom is accepted. */
      video?: string;
      /** The draft this send was opened from (PR 8c); cleared once sent. */
      draftId?: string;
      /** The Realty offer this package is for (PR 8e): the server files
       *  the sent package under it. */
      realtyOfferId?: string;
      /** The email's lines changed for this send (PR 8f), plain text with
       *  {{tokens}}. Absent sends the library's wording. */
      emailBody?: string;
      /** Placed fields in PDF points, top-left origin, 1-based pages
       *  (Prepare & Send). One-document form only. */
      fields?: Array<{
        signerKey: string;
        /** The seat a LOCKED box reads its person from (Baker,
         *  2026-09-16). */
        roleKey?: string | null;
        fieldType: string;
        pageNumber: number;
        x: number;
        y: number;
        width: number;
        height: number;
        required: boolean;
        value?: string | null;
        /** Lets the signer change a value we filled in (Baker,
         *  2026-09-18). The default is that a filled-in TEXT field
         *  prints and cannot be retyped; this is the per-box opt-out
         *  for a prefill meant as a head start. */
        signerMayEdit?: boolean;
        /** Points — what a printed value is set in, read off the type
         *  around the blank. Omitted when the page had no readable
         *  text, in which case the server uses the forms' own body
         *  size rather than the provider's web-form default. */
        fontSize?: number;
        /** A text box spanning several printed lines and the points
         *  between its rules (2026-09-29). */
        multiline?: boolean;
        linePitch?: number;
        firstLineIndent?: number;
        textStyle?: EsignTextStyle;
        /** The record field this text field pulls from, when bound. The
         *  server resolves it at send time and refuses the send if it
         *  resolves to nothing. */
        mergeToken?: string | null;
        /** Pick-one only: the choices, in order. The server refuses a
         *  pick-one with an empty list — the signer could not satisfy it. */
        options?: string[] | null;
        /** Choose-one only: which buttons rule each other out. The server
         *  refuses an ungrouped one, because the provider would then treat
         *  it as its own yes/no and accept BOTH sides of an either/or. */
        groupName?: string | null;
      }>;
      /** The one-document form of `ghosts` above. */
      ghosts?: EsignSentFieldPayload[];
      /** Per-page sizes in the same space, for server-side bounds checks. */
      pageSizes?: Array<{ pageNumber: number; width: number; height: number }>;
      /** The one-document form of the per-entry field above: the pages
       *  the sender confirmed by eye on a document the other side
       *  wrote. */
      confirmedPages?: number[];
      /** The advisor's answer to "this document and the record disagree
       *  about a contract term — update the record too?" Absent on a
       *  first send; the server answers 409 `record_terms_differ` with
       *  the terms, and the same send is repeated carrying the answer.
       *  Deliberately has no remembered form — it is asked every time. */
      recordSync?: "update_record" | "document_only";
      /** Superseded by `signers` above, which carries each signer's
       *  role on the signer itself. Still accepted by the server for a
       *  prep screen loaded before this Worker deployed. */
      signerRoles?: Array<"signer" | "cc">;
      /** Ask signers one at a time, in the order given. */
      enforceSigningOrder?: boolean;
      /** How long the envelope stays open and how hard the OS chases an
       *  unsigned signer. The server re-validates and clamps all three —
       *  the cadence decides how many emails a household gets. Minutes
       *  (2026-09-29, as short as one hour) win over days when both are
       *  sent; days remains for older callers. */
      schedule?: {
        expiryDays: number | null;
        reminderEveryMinutes?: number | null;
        reminderEveryDays: number | null;
        reminderMax: number;
      };
      /** Completion routing (Nate, 2026-08-26): who receives the
       *  EXECUTED copy once everyone signs — third parties (title,
       *  loan officer, the other side's agent) picked on the prep
       *  screen. Delivered as a PDF attachment from the sender's own
       *  Outlook after the signed copy files. Server-capped at 10. */
      forwardTo?: Array<{
        email: string;
        name?: string | null;
        source?: "party" | "contact" | "vendor" | "record" | "address";
        sourceDetail?: string | null;
      }>;
      /** Advisor self-signing: spots where the SENDER's own saved
       *  signature gets stamped into the document before it reaches
       *  BoldSign — no signer, no envelope field. */
      stamps?: Array<{ pageNumber: number; x: number; y: number; width: number; height: number }>;
      /** "in_person" when the advisor is sitting with the client: the
       *  envelope is created exactly as a normal send, but NOTHING is
       *  emailed and reminders are forced off. The signing links come
       *  back in the response to be handed over on the spot. */
      deliver?: "email" | "in_person";
      /** Lets this send teach a saved layout for the same document. */
      contentFingerprint?: string;
      /** What KIND of document this is, when the document itself has no
       *  type yet (Grant, 2026-08-23 — the automation trigger filters on
       *  it). Only ever FILLS a blank: the server ignores this when the
       *  document is already typed, so a send can't silently reclassify
       *  something someone filed deliberately. */
      documentType?: string | null;
      /** Standalone (household) sends only — which company this document
       *  is for. The server refuses a household send without it rather
       *  than guessing: the company decides which merge fields exist,
       *  and Realty's commission-disclosure fields are one of them. */
      companySlug?: string | null;
    },
  ): Promise<EsignSendResponse> {
    return request<EsignSendResponse>(
      `/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/send`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  // Stew reads the document and proposes fields (rung 4). The free
  // rungs run in the browser; only what they leave comes here, because
  // this is the one that costs money. A failure is never an error wall
  // — the response carries a plain-English line and an empty list, and
  // manual placement keeps working.
  /** Stew reads the OTHER documents in a package for the blanks still
   *  open (2026-09-30). Every reading names the document and the line
   *  it was read from; the server has already checked the value is
   *  printed there. A failure carries a plain-English line and an
   *  empty list; the blanks stay the sender's to type. */
  async readEsignPackage(
    recordKind: EsignSendableKind,
    recordId: string,
    body: {
      companySlug?: string | null;
      blanks: Array<{
        key: string;
        documentName: string;
        caption: string | null;
        fieldType: string;
        fillRule: string | null;
      }>;
      sources: Array<{
        documentId: string;
        spans: Array<{
          id: string;
          pageNumber: number;
          text: string;
          x: number;
          y: number;
          width: number;
          height: number;
        }>;
      }>;
    },
  ): Promise<{
    readings: Array<{
      key: string;
      value: string;
      sourceDocumentId: string;
      sourceDocumentName: string;
      sourceSpanId: string;
    }>;
    tokens?: number;
    message?: string;
  }> {
    return request(
      `/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/read-package`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  async suggestEsignFields(
    recordKind: EsignSendableKind,
    recordId: string,
    body: {
      documentId: string;
      /** Lets the server check for a saved layout before spending a
       *  model call. Absent for a scan. */
      contentFingerprint?: string;
      /** Who is selected to sign, in order — a saved layout maps onto
       *  these positionally. */
      signerContactIds?: string[];
      /** WHO HOLDS EACH SEAT right now (2026-09-15). A saved layout
       *  that knows its seats places each field on whoever is in that
       *  seat, instead of on whoever is in that position. Omitted when
       *  nothing has been cast, which keeps the positional path. */
      seats?: Array<{
        roleKey: string;
        signerKey: string | null;
        label?: string;
        /** True on the ONE seat the sender fills themselves — their own
         *  stamp seat, verified here because only the client knows who
         *  is signed in. Its signature, initials and date lines come
         *  back under `stamps` instead of `suggestions`. */
        stampsAsSender?: boolean;
      }>;
      /** Realty's buy / sell side, as the casting context reported it.
       *  Lets the server confirm a seat claiming to be stamped is one
       *  the catalog says is stamped on THIS side. */
      side?: "buy" | "sell" | "na";
      /** The reviewed catalog forms this copy was recognized as
       *  (shared/esign/form-recognition.ts). The server applies their
       *  maps when it has no closer match. */
      recognizedForms?: Array<{ formKey: string }>;
      spans: Array<{
        id: string;
        pageNumber: number;
        text: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
      /** Standalone (household) sends only — see sendForSignature. */
      companySlug?: string | null;
    },
  ): Promise<{
    suggestions: Array<{
      /** Set on a box from a RECOGNIZED form's map: which form, so the
       *  screen can move it from the blank's pages onto this copy's. */
      formKey?: string | null;
      fieldType: string;
      pageNumber: number;
      x: number;
      y: number;
      width: number;
      height: number;
      source: string;
      confidence: number;
      reason: string;
      signerHint: string | null;
      /** Saved-layout fields only: which signer by POSITION, and what
       *  was typed into the field last time. A read of the document
       *  can't know either. */
      signerIndex?: number;
      /** Who the box belongs to, already resolved. A saved layout sends
       *  it so the screen never has to work an owner out from a
       *  position — a locked box has no position (Baker, 2026-09-15). */
      signerKey?: string;
      value?: string | null;
      isRequired?: boolean;
      /** Saved-layout fields only: what the field is BOUND to, when it
       *  merges record data. The layout stores the binding rather than
       *  the value, so a repeat send reads today's numbers. */
      mergeToken?: string | null;
      /** Saved-layout fields only: a pick-one's choices and a
       *  choose-one's group. Unlike `value`, these are the FORM's own
       *  wording rather than record data, so remembering them verbatim
       *  is right. */
      options?: string[] | null;
      groupName?: string | null;
      /** The seat a LOCKED box reads its person from (Baker,
       *  2026-09-16) — see the twin field on the other suggest
       *  response. */
      roleKey?: string | null;
      /** A text box spanning several printed lines, from a map that was
       *  taught one (2026-09-29) — see shared/esign/multiline-text.ts. */
      multiline?: boolean | null;
      linePitch?: number | null;
      firstLineIndent?: number | null;
      textStyle?: EsignTextStyle | null;
      /** HOW THIS BLANK FILLS (fill-rules.ts), from a saved map. On the
       *  wire since the atlas import (applyLayout carries it); declared
       *  here since 2026-09-29, because the screen never copied a key
       *  its type did not name, and every mapped locked box arrived on
       *  a real send as "no rule". */
      fillRule?: string | null;
    }>;
    /** The sender's OWN signature, initials and date-signed lines, from
     *  a saved layout that knows its seats. Separate from `suggestions`
     *  because nobody signs these: the screen stamps the sender's saved
     *  signature over the first two and prints today's date on the
     *  third. Empty on every send that cast no stamp seat of its own. */
    stamps?: Array<{
      roleKey: string;
      /** As on `suggestions`: the recognized form this line belongs to. */
      formKey?: string | null;
      fieldType: string;
      pageNumber: number;
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
    tokens?: number;
    message?: string;
    /** Present when the map came from a form this copy was RECOGNIZED
     *  as (Rung 1c), one entry per form whose map was applied. */
    recognizedForms?: Array<{ formKey: string; layoutName: string }>;
    /** Present when the fields came from a saved layout rather than
     *  from reading the document. */
    layout?: {
      id: string;
      name: string;
      unplaced: number;
      /** These box positions were reconstructed from the standard form
       *  rather than measured off the real PDF. The prep screen says so
       *  before the document goes. */
      positionsReconstructed?: boolean;
      /** Who saved this map, when, and how many sends it has served —
       *  answers "is this Stew guessing, or is this what the team did
       *  last time, and who set it up" (Joey Ellis, 2026-09-18: the
       *  banner said "you've sent this before" with no way to tell
       *  whose map it was). Null creator is a map nobody attributed
       *  (an import, or a user row since removed) — the screen says so
       *  rather than guessing a name. */
      createdByName?: string | null;
      createdAt?: string;
      timesApplied?: number;
    };
  }> {
    return request(
      `/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/suggest-fields`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  /** Teach this document's field map WITHOUT sending anything.
   *
   *  Until 2026-09-01 a map could only be learned by sending a real
   *  contract to a real client, so the first person to use one of the 27
   *  AAR forms paid the whole mapping cost live on somebody's listing —
   *  and lost it if they stopped before sending. This saves the same map
   *  the send would have taught, for everyone in the company, now. */
  async saveEsignLayout(
    recordKind: EsignSendableKind,
    recordId: string,
    body: {
      documentId: string;
      contentFingerprint: string;
      signerContactIds: string[];
      /** WHO HOLDS EACH SEAT right now (2026-09-15). A saved layout
       *  that knows its seats places each field on whoever is in that
       *  seat, instead of on whoever is in that position. Omitted when
       *  nothing has been cast, which keeps the positional path. */
      seats?: Array<{ roleKey: string; signerKey: string | null; label?: string }>;
      fields: unknown[];
      pageSizes: Array<{ pageNumber: number; width: number; height: number }>;
      name?: string;
      companySlug?: string | null;
    },
  ): Promise<{
    ok: true;
    layout_id: string | null;
    /** False when this REPLACED a map the company already had for this
     *  document — worth saying out loud, because overwriting the team's
     *  previous answer is a different act from recording a first one. */
    created: boolean;
    field_count: number;
    name: string;
  }> {
    return request(`/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/save-layout`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  // The document types this Processing record's uploads can be — the
  // options behind "what kind of document is this?" on the prep screen.
  // The record's OWN requirements, not the whole catalog (see
  // listDocumentTypeOptionsForTransaction). An empty list is normal.
  async listEsignDocumentTypes(
    recordKind: EsignSendableKind,
    recordId: string,
    /** Standalone (household) sends only — a household carries no
     *  company of its own, and the company decides which document types
     *  and which merge fields exist. Ignored for every other kind. */
    companySlug?: string | null,
  ): Promise<{
    types: Array<{ id: string; name: string; purpose: string | null; sort_order: number }>;
  }> {
    return request(
      `/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/document-types${esignCompanyQuery(companySlug)}`,
    );
  },
  // What a text field on a document can be bound to, WITH this record's
  // real values — the values are the usability, which is why this is a
  // request rather than a static list.
  async listEsignMergeFields(
    recordKind: EsignSendableKind,
    recordId: string,
    companySlug?: string | null,
    // The contact whose values the preview should show by default.
    signerContactId?: string | null,
    /** EVERY selected signer who is one of our contacts. A contact token
     *  prints the person whose box it is (Baker, 2026-09-14), so the
     *  screen needs a value per signer or it shows one name in both
     *  boxes — which is how the bug was noticed. */
    allSignerContactIds?: readonly string[],
    /** The Realty offer this contract is drafted from (Nate,
     *  2026-10-15). Offer, other-agent and property values resolve
     *  against it; without it the server can only guess, and with two
     *  draft offers on one opportunity the guess is nothing. */
    realtyOfferId?: string | null,
  ): Promise<{
    /** What this company has been taught its form captions mean — see
     *  shared/db/esign-caption-bindings.ts. Sent with the tokens so the
     *  mapping pass has the vocabulary in hand before it reads the
     *  document. */
    learned_captions?: Array<{
      caption_key: string;
      label: string;
      token: string | null;
      field_type: string | null;
    }>;
    fields: Array<{
      token: string;
      label: string;
      group: string;
      group_label: string;
      value: string | null;
      sample: string;
    }>;
    /** Contact-token values per signer contact id. Absent for a signer
     *  who isn't one of our contacts — the same thing the send says. */
    values_by_contact?: Record<string, Record<string, string | null>>;
  }> {
    const company = esignCompanyQuery(companySlug);
    const parts: string[] = [];
    if (signerContactId) parts.push(`signer_contact_id=${encodeURIComponent(signerContactId)}`);
    if (allSignerContactIds && allSignerContactIds.length > 0) {
      parts.push(`signer_contact_ids=${encodeURIComponent(allSignerContactIds.join(","))}`);
    }
    if (realtyOfferId) parts.push(`offer_id=${encodeURIComponent(realtyOfferId)}`);
    const signerParam = parts.length > 0 ? `${company ? "&" : "?"}${parts.join("&")}` : "";
    return request(
      `/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/merge-fields${company}${signerParam}`,
    );
  },

  // Who a Stewardship Productions document can be sent to: every team
  // member in the OS (2026-09-28). 403 for anyone the Productions rule
  // doesn't name.
  async listEsignPersonnelTeam(): Promise<{
    members: Array<{
      id: string;
      name: string;
      email: string;
      is_active: boolean;
      employment_type: "w2" | "contractor";
    }>;
  }> {
    return request("/api/esign/personnel/team");
  },

  // Who may sign a document sent from this record — the SAME list the
  // send itself restricts to, so the picker can never offer someone the
  // send would refuse.
  async listEsignRecordSigners(
    recordKind: EsignSendableKind,
    recordId: string,
    companySlug?: string | null,
  ): Promise<{
    // This file's own ServiceRecordPerson (declared above), which is
    // the same shape the server returns. Reusing it rather than
    // restating the fields inline is what lets the caller assign the
    // result straight into a ServiceRecordPerson[] with no `as` cast —
    // and an `as` here would be the thing that hides a future mismatch.
    signers: ServiceRecordPerson[];
    household_id: string | null;
    /** Null on the standalone lanes: a household and a contact belong
     *  to no single company, and this call deliberately doesn't ask —
     *  see the route comment. The send screen asks instead. */
    company_slug: string | null;
    /** SIGNING ROLES (2026-09-15): what the prep screen needs to cast
     *  this record's seats before anyone clicks. Read live from the
     *  rows that own each fact. */
    casting: {
      owner: { userId: string; displayName: string } | null;
      parties: Array<{
        key: string;
        partyRole: string;
        displayName: string;
        email: string | null;
        contactId: string | null;
      }>;
      otherPartyName: string | null;
      /** The firm's standing holder of a seat (Realty's managing broker). */
      firmRoles: Array<{ roleKey: string; userId: string; displayName: string; email: string | null }>;
      side: "buy" | "sell" | "na";
    };
  }> {
    return request(
      `/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/signers${esignCompanyQuery(companySlug)}`,
    );
  },
  /** Which seats each document's saved map has signer boxes for (Grant,
   *  2026-10-27). `known: false` means the map can't say; the send then
   *  casts the whole record as before. */
  async getEsignFormSeats(
    recordKind: EsignSendableKind,
    recordId: string,
    documentIds: string[],
    companySlug?: string | null,
  ): Promise<{ documents: { documentId: string; known: boolean; seats: string[] }[] }> {
    return request(`/api/esign/records/${recordKind}/${encodeURIComponent(recordId)}/form-seats`, {
      method: "POST",
      body: JSON.stringify({ documentIds, ...(companySlug ? { companySlug } : {}) }),
    });
  },
  // Advisor self-signing (Grant, 2026-08-22) — every employee's own
  // saved signature, drawn once in Preferences and reused across every
  // document they stamp. Never someone else's: the server keys this
  // purely off the caller's own session, no id in the path.
  async getMyEsignSignature(): Promise<{
    signature: {
      imagePngBase64: string;
      naturalWidth: number;
      naturalHeight: number;
      updatedAt: string;
    } | null;
  }> {
    return request("/api/esign/my-signature");
  },
  async saveMyEsignSignature(body: {
    imagePngBase64: string;
    naturalWidth: number;
    naturalHeight: number;
  }): Promise<{ ok: true; updatedAt: string }> {
    return request("/api/esign/my-signature", { method: "POST", body: JSON.stringify(body) });
  },
  async deleteMyEsignSignature(): Promise<{ ok: true }> {
    return request("/api/esign/my-signature", { method: "DELETE" });
  },
  // Saved initials — the second mark (Grant, 2026-08-26: "draw or type
  // and adopt initials"). Same self-only contract as the signature.
  async getMyEsignInitials(): Promise<{
    initials: {
      imagePngBase64: string;
      naturalWidth: number;
      naturalHeight: number;
      updatedAt: string;
    } | null;
  }> {
    return request("/api/esign/my-initials");
  },
  async saveMyEsignInitials(body: {
    imagePngBase64: string;
    naturalWidth: number;
    naturalHeight: number;
  }): Promise<{ ok: true; updatedAt: string }> {
    return request("/api/esign/my-initials", { method: "POST", body: JSON.stringify(body) });
  },
  async deleteMyEsignInitials(): Promise<{ ok: true }> {
    return request("/api/esign/my-initials", { method: "DELETE" });
  },
  // The E-Sign workspace list. `scope` defaults to "mine" server-side,
  // so an omitted or unknown value narrows rather than widens.
  // Reusable documents — the blank forms a company sends repeatedly.
  async listEsignTemplates(): Promise<{ templates: EsignTemplateRow[] }> {
    return request(`/api/esign/templates`);
  },

  /** The offer checklist: which Realty forms are pre-checked when an
   *  advisor drafts an offer, what else the library holds, and which
   *  curated forms have no blank uploaded yet.
   *
   *  Takes no record on purpose — drafting an offer happens on the
   *  Opportunity, before there is a Processing record or a property to
   *  reason about. See the route's own header. */
  async listRealtyOfferForms(side: "purchase" | "listing" = "purchase"): Promise<{
    side: string;
    /** The saved Templates for this side (2026-09-03) — the offer
     *  builder picks one of these instead of reading a single boolean
     *  on the AAR catalog. */
    packages: EsignTemplatePackage[];
    curated: RealtyOfferFormChoice[];
    additional: RealtyOfferFormChoice[];
    missing: Array<{ formKey: string; displayName: string; revisionLabel: string }>;
  }> {
    return request(`/api/realty/offer-forms?side=${encodeURIComponent(side)}`);
  },

  /** Upload a blank document as a reusable template. Multipart, matching
   *  uploadDocumentSharePoint's pattern — request() can't be used here
   *  because it forces a JSON Content-Type. Used by the Realty Forms
   *  Library page to add a form's blank PDF; from there it gets linked
   *  to its catalog row via linkContractFormTemplate. */
  async createEsignTemplate(
    file: File,
    options: {
      company_slug: string;
      name: string;
      description?: string | null;
      document_type?: string | null;
      // Industry Documents catalog metadata (2026-09-02) — optional;
      // the Realty view doesn't send these since its catalog metadata
      // lives on contract_forms instead.
      publisher?: string | null;
      revision_label?: string | null;
      category?: string | null;
      applies_to_side?: string | null;
      /** SHA-256 of the document's normalized text, computed in the
       *  browser (readDocumentForUpload). Without it the template can
       *  never be recognized, so no field map can be saved for it —
       *  which is what quietly happened to every document added through
       *  a library page until 2026-09-03. */
      content_fingerprint?: string | null;
    },
  ): Promise<{ template: { id: string; name: string; companySlug: string } }> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_slug", options.company_slug);
    fd.append("name", options.name);
    if (options.description) fd.append("description", options.description);
    if (options.document_type) fd.append("document_type", options.document_type);
    if (options.publisher) fd.append("publisher", options.publisher);
    if (options.revision_label) fd.append("revision_label", options.revision_label);
    if (options.category) fd.append("category", options.category);
    if (options.applies_to_side) fd.append("applies_to_side", options.applies_to_side);
    if (options.content_fingerprint) fd.append("content_fingerprint", options.content_fingerprint);
    const res = await fetch(apiUrl("/api/esign/templates"), {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      let payload: unknown = null;
      try {
        payload = await res.json();
      } catch {
        // ignore parse errors
      }
      const err =
        payload && typeof payload === "object"
          ? (payload as { error?: string; message?: string })
          : {};
      throw new ApiError(
        res.status,
        err.message ?? err.error ?? `upload_failed_${res.status}`,
        err.error ?? null,
      );
    }
    return (await res.json()) as { template: { id: string; name: string; companySlug: string } };
  },

  /** Remove a document from (or restore it to) a company's library.
   *  Archives, never deletes — the blank PDF, every field map ever
   *  taught for it, and every envelope already sent from it are
   *  untouched, and this same call with `false` undoes it (Baker +
   *  Joey, 2026-09-18: the Industry Documents page had no way to
   *  remove a document at all). */
  async renameEsignTemplate(templateId: string, name: string): Promise<{ template: unknown }> {
    return request(`/api/esign/templates/${encodeURIComponent(templateId)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
  },

  async archiveEsignTemplate(
    templateId: string,
    archived: boolean,
  ): Promise<{ ok: boolean; archived: boolean }> {
    return request(`/api/esign/templates/${encodeURIComponent(templateId)}/archive`, {
      method: "POST",
      body: JSON.stringify({ archived }),
    });
  },

  /** Copy a document's field map into its own library entry for one side
   *  of a deal (Nate, 2026-10-26). The original is untouched; removing the
   *  copy (archiveEsignTemplate) is the undo. */
  async duplicateEsignTemplateMap(
    templateId: string,
    body: { name: string; side: "listing" | "purchase" },
  ): Promise<{
    ok: boolean;
    template: { id: string; name: string };
    layout_id: string | null;
    field_count: number;
    contract_form_id: string | null;
  }> {
    return request(`/api/esign/templates/${encodeURIComponent(templateId)}/duplicate-map`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Which live packages carry this document, so a removal can say what
   *  it would break — a stale-package send failure is a bad way to
   *  find out (see the send route's own `stale_package` refusal). */
  async getPackagesUsingEsignTemplate(
    templateId: string,
  ): Promise<{ packages: { id: string; name: string }[] }> {
    return request(`/api/esign/templates/${encodeURIComponent(templateId)}/packages`);
  },

  /**
   * Replace a library document with a newer revision of itself, carrying
   * its field map across.
   *
   * The old revision keeps its file and its own map, so envelopes
   * already out for signature still resolve against the document they
   * were built on. What comes back is the carry-over report: what came
   * across untouched, what landed on a caption that moved (worth a
   * look), and what could not be matched at all and is now unmapped.
   */
  async uploadTemplateRevision(
    templateId: string,
    file: File,
    body: {
      revision_label: string;
      content_fingerprint: string | null;
      spans: Array<{
        pageNumber: number;
        text: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
      page_sizes: Array<{ pageNumber: number; width: number; height: number }>;
    },
  ): Promise<TemplateRevisionResult> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("revision_label", body.revision_label);
    if (body.content_fingerprint) fd.append("content_fingerprint", body.content_fingerprint);
    fd.append("spans", JSON.stringify(body.spans));
    fd.append("page_sizes", JSON.stringify(body.page_sizes));
    const res = await fetch(apiUrl(`/api/esign/templates/${templateId}/new-revision`), {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      let payload: unknown = null;
      try {
        payload = await res.json();
      } catch {
        // ignore parse errors
      }
      const err =
        payload && typeof payload === "object"
          ? (payload as { error?: string; message?: string })
          : {};
      throw new ApiError(
        res.status,
        err.message ?? err.error ?? `upload_failed_${res.status}`,
        err.error ?? null,
      );
    }
    return (await res.json()) as TemplateRevisionResult;
  },

  /** Put a copy of a template on a record. Returns an ordinary document
   *  id — everything downstream (prep screen, saved layouts, send) then
   *  behaves exactly as it does for an uploaded file. */
  async useEsignTemplate(
    templateId: string,
    body: { entity_type: string; entity_id: string },
  ): Promise<{
    document_id: string;
    content_fingerprint: string | null;
    company_slug: string;
  }> {
    return request(`/api/esign/templates/${templateId}/use`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // ── Templates: the saved, ordered PACKAGES ────────────────────────
  //
  // Named separately from the single-document routes above because they
  // are different objects: a template package is a LIST of the blanks
  // those routes serve. See workers/api/src/routes/esign-template-packages.ts.

  async listEsignTemplatePackages(options?: {
    company?: string;
    archived?: boolean;
  }): Promise<{ packages: EsignTemplatePackage[] }> {
    const params = new URLSearchParams();
    if (options?.company) params.set("company", options.company);
    if (options?.archived) params.set("archived", "1");
    const qs = params.toString();
    return request(`/api/esign/template-packages${qs ? `?${qs}` : ""}`);
  },

  async createEsignTemplatePackage(
    body: EsignTemplatePackageWrite & { company_slug: string },
  ): Promise<{ package: EsignTemplatePackage }> {
    return request(`/api/esign/template-packages`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async updateEsignTemplatePackage(
    id: string,
    body: Partial<EsignTemplatePackageWrite>,
  ): Promise<{ package: EsignTemplatePackage }> {
    return request(`/api/esign/template-packages/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  async archiveEsignTemplatePackage(
    id: string,
    archived: boolean,
  ): Promise<{ ok: boolean; archived: boolean }> {
    return request(`/api/esign/template-packages/${id}/archive`, {
      method: "POST",
      body: JSON.stringify({ archived }),
    });
  },

  /** Copy forms from the library onto a record for one package (the
   *  wizard's "No template" path, PR 8a). Hands back what Prepare & Send
   *  opens against, in order. */
  async assembleEsignRecordPackage(
    kind: "opportunity" | "transaction",
    id: string,
    templateIds: string[],
  ): Promise<{
    documents: Array<{
      document_id: string;
      filename: string;
      document_type: string | null;
      content_fingerprint: string | null;
      company_slug: string | null;
    }>;
  }> {
    return request(`/api/esign/records/${kind}/${encodeURIComponent(id)}/package`, {
      method: "POST",
      body: JSON.stringify({ items: templateIds.map((templateId) => ({ templateId })) }),
    });
  },

  /** Step 1 of the package wizard (PR 8a): this record's templates. */
  async listTemplatesForRecord(
    kind: "opportunity" | "transaction",
    id: string,
  ): Promise<TemplatesForRecordResponse> {
    return request(`/api/esign/template-packages/for-record/${kind}/${encodeURIComponent(id)}`);
  },

  async duplicateEsignTemplatePackage(id: string): Promise<{ package: EsignTemplatePackage }> {
    return request(`/api/esign/template-packages/${id}/duplicate`, { method: "POST" });
  },

  /** Put a whole package on a record. Copies each blank onto it in
   *  order and hands back what Prepare & Send opens against. */
  async useEsignTemplatePackage(
    id: string,
    /** `template_ids`: the forms ticked in step 1, in order (PR 8a).
     *  Omitted means the whole package. */
    body: { entity_type: string; entity_id: string; template_ids?: string[] },
  ): Promise<{
    package_name: string;
    company_slug: string;
    signer_roles: string[];
    documents: Array<{
      document_id: string;
      filename: string;
      document_type: string | null;
      content_fingerprint: string | null;
    }>;
  }> {
    return request(`/api/esign/template-packages/${id}/use`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // ── Mapping a blank form's fields, no deal needed (2026-09-01) ──
  //
  // Grant, relaying Nate: "he needs to be able to create a contract
  // without having to do all this mapping and work." These three call
  // the template-scoped counterparts of the record-based suggest/save
  // calls above — same shapes, so PrepareAndSend's mapping mode reuses
  // its existing handling of the responses unchanged.

  async suggestTemplateFields(
    templateId: string,
    body: {
      /** The mapping slots. `roleKey` names the catalog seat a slot
       *  stands for, when the mapper has picked one — that is what lets
       *  a blank form be taught in seats rather than positions. */
      signers: Array<{ id: string; label: string; roleKey?: string | null }>;
      /** This form's own fingerprint, computed from the PDF this screen
       *  just read. Sent so the OS can learn what the form IS the first
       *  time anybody opens it — every Realty AAR form in production
       *  still has none, and a map imported from an atlas only reaches
       *  ordinary sends once it does. */
      contentFingerprint?: string;
      spans: Array<{
        id: string;
        pageNumber: number;
        text: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
    },
  ): Promise<{
    suggestions: Array<{
      fieldType: string;
      pageNumber: number;
      x: number;
      y: number;
      width: number;
      height: number;
      source: string;
      confidence: number;
      reason: string;
      signerHint: string | null;
      signerIndex?: number;
      /** Who the box belongs to, already resolved. A saved layout sends
       *  it so the screen never has to work an owner out from a
       *  position — a locked box has no position (Baker, 2026-09-15). */
      signerKey?: string;
      value?: string | null;
      isRequired?: boolean;
      mergeToken?: string | null;
      options?: string[] | null;
      groupName?: string | null;
      /** How this blank fills — see shared/esign/fill-rules.ts. Present
       *  when a saved map answered and that map knows; it is what the
       *  "only what you know" list reads. */
      fillRule?: string | null;
      /** The seat a LOCKED box reads its person from (Baker,
       *  2026-09-16). A locked box belongs to the preparer and so names
       *  no signer; when it prints somebody's name, this is the chair
       *  whose occupant it reads. */
      roleKey?: string | null;
      /** A text box spanning several printed lines, from a map that was
       *  taught one (2026-09-29) — see shared/esign/multiline-text.ts. */
      multiline?: boolean | null;
      linePitch?: number | null;
      firstLineIndent?: number | null;
      textStyle?: EsignTextStyle | null;
    }>;
    tokens?: number;
    message?: string;
    layout?: {
      id: string;
      name: string;
      unplaced: number;
      /** These box positions were reconstructed from the standard form
       *  rather than measured off the real PDF. The prep screen says so
       *  before the document goes. */
      positionsReconstructed?: boolean;
      /** Who saved this map, when, and how many sends it has served —
       *  see the twin field on the record-based suggest response. */
      createdByName?: string | null;
      createdAt?: string;
      timesApplied?: number;
    };
    /** Present only when a saved map answered: the signer slots that map
     *  was taught with, which the screen adopts so every remembered
     *  field has somewhere to land. Sent by the server because only it
     *  knows how many signers the stored map holds. */
    signers?: Array<{ id: string; label: string; roleKey?: string | null }>;
  }> {
    return request(`/api/esign/templates/${templateId}/suggest-fields`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // WHAT THE OS HAS BEEN TAUGHT about this company's form captions, and
  // how to take back a lesson that was wrong. Saving a map is what
  // teaches these; until now nothing could show them or undo one.
  async listEsignLearnedCaptions(companySlug: string): Promise<{
    lessons: Array<{
      id: string;
      /** The normalized key the matcher looks a caption up by. */
      caption_key: string;
      /** The caption as it was actually printed on the form. */
      caption_sample: string;
      /** What it fills, when it fills something from the record. */
      merge_token: string | null;
      /** The kind of box it taught — signature, initials, date signed. */
      field_type: string | null;
      times_taught: number;
      updated_at: string;
    }>;
  }> {
    return request(`/api/esign/learned-captions?company=${encodeURIComponent(companySlug)}`);
  },

  async forgetEsignLearnedCaption(companySlug: string, id: string): Promise<{ ok: true }> {
    return request(
      `/api/esign/learned-captions/${encodeURIComponent(id)}?company=${encodeURIComponent(companySlug)}`,
      { method: "DELETE" },
    );
  },

  async listTemplateMergeFields(templateId: string): Promise<{
    /** What this company has been taught its form captions mean — see
     *  shared/db/esign-caption-bindings.ts. Sent with the tokens so the
     *  mapping pass has the vocabulary in hand before it reads the
     *  document. */
    learned_captions?: Array<{
      caption_key: string;
      label: string;
      token: string | null;
      field_type: string | null;
    }>;
    fields: Array<{
      token: string;
      label: string;
      group: string;
      group_label: string;
      value: string | null;
      sample: string;
    }>;
  }> {
    return request(`/api/esign/templates/${templateId}/merge-fields`);
  },

  async saveTemplateLayout(
    templateId: string,
    body: {
      signers: Array<{ id: string; label: string; roleKey?: string | null }>;
      fields: unknown[];
      pageSizes: Array<{ pageNumber: number; width: number; height: number }>;
      /** Sent so a blank stored before 2026-09-02 (when the library
       *  pages started computing it) can be repaired the first time
       *  someone maps it. The server fills a NULL only. */
      contentFingerprint?: string;
      /** Save it as "nothing to sign — it goes out to be read". Sent
       *  with no fields. */
      readOnly?: boolean;
    },
  ): Promise<{
    ok: true;
    layout_id: string | null;
    created: boolean;
    field_count: number;
    name: string;
  }> {
    return request(`/api/esign/templates/${templateId}/save-layout`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async listEsignWorkspace(
    scope: "mine" | "company" | "unassigned",
    opts: {
      limit?: number;
      offset?: number;
      /** Document name, signer name/email, or household name. */
      q?: string;
      /** all | open | stalled | completed | closed */
      state?: string;
    } = {},
  ): Promise<{
    scope: string;
    state: string;
    envelopes: EsignWorkspaceRow[];
    total: number;
    company_scope: string[] | null;
    can_see_unassigned: boolean;
  }> {
    const params = new URLSearchParams({ scope });
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts.offset !== undefined) params.set("offset", String(opts.offset));
    if (opts.q) params.set("q", opts.q);
    if (opts.state && opts.state !== "all") params.set("state", opts.state);
    return request(`/api/esign/envelopes?${params.toString()}`);
  },

  // ── One envelope, in full ─────────────────────────────────────────
  //
  // Reading is universal (any employee), same as the per-record
  // envelope list. `can_act` is the server's own answer to "may THIS
  // reader nudge or cancel", computed from the envelope's record — an
  // affordance for hiding buttons, never the gate; every action
  // re-checks server-side.
  // Bulk send. Creating a batch RESOLVES the records and writes them
  // down as a draft — it sends nothing. Confirm is what starts it.
  async createEsignBulkSend(body: {
    kind: "service_record" | "transaction" | "opportunity";
    recordIds: string[];
    /** One of these. A bulk send is almost always a saved template —
     *  the blank form a company sends over and over — because 200
     *  households share one file and there is no single record to copy
     *  it onto. */
    templateId?: string;
    documentId?: string;
    companySlug?: string;
    message?: string;
  }): Promise<{ batch_id: string }> {
    return request("/api/esign/bulk", { method: "POST", body: JSON.stringify(body) });
  },
  async getEsignBulkSend(batchId: string): Promise<EsignBulkStatus> {
    return request(`/api/esign/bulk/${encodeURIComponent(batchId)}`);
  },
  /** `acknowledged` is REQUIRED server-side above the threshold, so a
   *  stale client cannot skip the confirmation on a spend. */
  async confirmEsignBulkSend(
    batchId: string,
    acknowledged: boolean,
  ): Promise<{ ok: true; sending: number; quoted_cost_cents: number }> {
    return request(`/api/esign/bulk/${encodeURIComponent(batchId)}/confirm`, {
      method: "POST",
      body: JSON.stringify({ acknowledged }),
    });
  },
  async cancelEsignBulkSend(
    batchId: string,
  ): Promise<{ ok: true; stopped: number; already_sent: number }> {
    return request(`/api/esign/bulk/${encodeURIComponent(batchId)}/cancel`, { method: "POST" });
  },
  // Admin-only: plan-and-contract information plus a per-person send
  // count. Rates live in shared/esign/pricing.ts.
  async getEsignUsage(): Promise<{ usage: EsignUsage }> {
    return request("/api/esign/usage");
  },
  async getEsignEnvelope(
    envelopeId: string,
  ): Promise<{ envelope: EsignEnvelopeDetail; can_act: boolean }> {
    return request(`/api/esign/envelopes/${encodeURIComponent(envelopeId)}`);
  },

  // Calls a document back. Revokes it at the provider and kills every
  // live signing link, so a signer holding the email gets "this link
  // isn't valid" rather than a document that shouldn't have gone out.
  //
  // `notifySigners` (2026-09-29): email each person who was asked that
  // there is nothing left to sign, with the reason. The Documents tab and
  // the envelope page always pass it; `notices` says who was emailed.
  async cancelEsignEnvelope(
    envelopeId: string,
    reason?: string,
    opts?: { notifySigners?: boolean; closeHereAnyway?: boolean },
  ): Promise<{
    ok: true;
    notices?: Array<{ name: string; emailed: boolean }>;
    closed_here_only?: boolean;
  }> {
    return request(`/api/esign/envelopes/${encodeURIComponent(envelopeId)}/cancel`, {
      method: "POST",
      body: JSON.stringify({
        ...(reason ? { reason } : {}),
        ...(opts?.notifySigners ? { notify_signers: true } : {}),
        ...(opts?.closeHereAnyway ? { close_here_anyway: true } : {}),
      }),
    });
  },

  // The exact email each person would get if this package were voided now
  // (Baker, 2026-10-05). Read-only: nothing is sent or changed.
  async previewEsignVoidNotice(
    envelopeId: string,
    reason: string,
  ): Promise<{ notices: Array<{ name: string; email: string; subject: string; body: string }> }> {
    const qs = reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : "";
    return request(`/api/esign/envelopes/${encodeURIComponent(envelopeId)}/void-preview${qs}`);
  },

  // A sent package back as a new draft on its record (Documents tab PR
  // 10a): "correct" before a void, "resend" for an expired or declined
  // one. Saves the draft only; nothing is voided, sent or emailed.
  // `fields_kept` is false when the package predates drafts and its
  // fields will be suggested again.
  async redraftEsignEnvelope(
    envelopeId: string,
    purpose: "correct" | "resend",
  ): Promise<{ draft: EsignDraft; fields_kept: boolean; dropped_documents: string[] }> {
    return request(`/api/esign/envelopes/${encodeURIComponent(envelopeId)}/redraft`, {
      method: "POST",
      body: JSON.stringify({ purpose }),
    });
  },

  // "Fix what hasn't been signed yet" (PR 10b): the package's own saved
  // work, to open the correction screen on. Saves nothing.
  async openEsignEnvelopeForCorrection(envelopeId: string): Promise<{
    copy: { documents: EsignDraftDocument[]; state: unknown; realtyOfferId: string | null };
    fields_kept: boolean;
    dropped_documents: string[];
  }> {
    return request(`/api/esign/envelopes/${encodeURIComponent(envelopeId)}/redraft`, {
      method: "POST",
      body: JSON.stringify({ purpose: "edit" }),
    });
  },

  // Send a correction: the package as it should now be (the send body's
  // shape) and why. The signing service applies it after accepting; the
  // row shows "Correction in progress" until it confirms.
  async correctEsignEnvelope(
    envelopeId: string,
    // The send body's shape (see sendForSignature), plus the reason. Typed
    // loosely here because the send's own parameter can't be named from
    // inside this object; PrepareAndSend builds it typed as the send's.
    body: Record<string, unknown> & { reason: string },
  ): Promise<{ ok: true; status: "pending"; correction_id: string }> {
    return request(`/api/esign/envelopes/${encodeURIComponent(envelopeId)}/correct`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Ask whether the signing service has applied a pending correction.
  async checkEsignCorrection(envelopeId: string): Promise<{ status: "none" | "pending" | "applied" | "failed" }> {
    return request(`/api/esign/envelopes/${encodeURIComponent(envelopeId)}/correction/check`, {
      method: "POST",
    });
  },

  // A fresh link for one signer, when the first email never arrived.
  // The original link is unrecoverable by design (only its hash is
  // stored), so this mints a new one and retires the old.
  async resendEsignSigner(
    envelopeId: string,
    signerId: string,
  ): Promise<{ ok: true; sign_url: string; emailed: boolean; email_error?: string | null }> {
    return request(
      `/api/esign/envelopes/${encodeURIComponent(envelopeId)}/signers/${encodeURIComponent(signerId)}/resend`,
      { method: "POST" },
    );
  },
  // Sends a pending request to a DIFFERENT address for the same person —
  // the address was typed wrong, it bounces, or the client asks for it
  // somewhere else. Not a resend: the signing service is told the
  // recipient changed (it keys the ceremony on the address), every old
  // link dies, and a fresh invitation goes to the new address.
  //
  // `invited` comes back false when this person isn't the one being
  // asked yet — a later turn under signing order, or a copy recipient.
  // Their address is corrected and the OS writes to it when their
  // moment comes.
  async changeEsignSignerEmail(
    envelopeId: string,
    signerId: string,
    body: { email: string; name?: string; reason?: string },
  ): Promise<{
    ok: true;
    email: string;
    name: string;
    previous_email: string;
    invited: boolean;
    sign_url: string | null;
    emailed: boolean;
  }> {
    return request(
      `/api/esign/envelopes/${encodeURIComponent(envelopeId)}/signers/${encodeURIComponent(signerId)}/change-email`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  // A working link for one signer, WITHOUT emailing anyone — "give me
  // the link and I'll text it". Unlike resend, this never touches the
  // link the signer already has: it only adds a second way to reach
  // them, so nothing already sent is silently broken.
  async copyEsignSignerLink(
    envelopeId: string,
    signerId: string,
  ): Promise<{ ok: true; name: string; email: string; sign_url: string }> {
    return request(
      `/api/esign/envelopes/${encodeURIComponent(envelopeId)}/signers/${encodeURIComponent(signerId)}/copy-link`,
      { method: "POST" },
    );
  },
  // ── Package drafts: "Save as draft" (Documents tab PR 8c) ──────────
  async listEsignDrafts(kind: EsignSendableKind, recordId: string): Promise<{ drafts: EsignDraftSummary[] }> {
    return request(`/api/esign/records/${encodeURIComponent(kind)}/${encodeURIComponent(recordId)}/drafts`);
  },
  async getEsignDraft(kind: EsignSendableKind, recordId: string, draftId: string): Promise<{ draft: EsignDraft }> {
    return request(
      `/api/esign/records/${encodeURIComponent(kind)}/${encodeURIComponent(recordId)}/drafts/${encodeURIComponent(draftId)}`,
    );
  },
  async createEsignDraft(
    kind: EsignSendableKind,
    recordId: string,
    body: { documents: EsignDraftDocument[]; state: unknown; realty_offer_id?: string | null },
  ): Promise<{ id: string; version_number: number }> {
    return request(`/api/esign/records/${encodeURIComponent(kind)}/${encodeURIComponent(recordId)}/drafts`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  async saveEsignDraft(
    kind: EsignSendableKind,
    recordId: string,
    draftId: string,
    body: { documents: EsignDraftDocument[]; state: unknown; expected_version_number: number },
  ): Promise<{ id: string; version_number: number }> {
    return request(
      `/api/esign/records/${encodeURIComponent(kind)}/${encodeURIComponent(recordId)}/drafts/${encodeURIComponent(draftId)}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  },
  async discardEsignDraft(kind: EsignSendableKind, recordId: string, draftId: string): Promise<{ ok: true }> {
    return request(
      `/api/esign/records/${encodeURIComponent(kind)}/${encodeURIComponent(recordId)}/drafts/${encodeURIComponent(draftId)}/discard`,
      { method: "POST" },
    );
  },
  async restoreEsignDraft(kind: EsignSendableKind, recordId: string, draftId: string): Promise<{ ok: true }> {
    return request(
      `/api/esign/records/${encodeURIComponent(kind)}/${encodeURIComponent(recordId)}/drafts/${encodeURIComponent(draftId)}/restore`,
      { method: "POST" },
    );
  },
  /** The signing invitation's wording (PR 8f): what "Before it goes"
   *  shows as the email's lines and what an edit starts from. */
  async getSigningEmailTemplate(): Promise<{ body: string; from_library: boolean }> {
    return request("/api/esign/signing-email-template");
  },
  /** Review & send's "Add a video" (PR 8b): what Loom says about a link. */
  async lookupSigningVideo(url: string): Promise<{
    url: string;
    title: string | null;
    duration_seconds: number | null;
    thumbnail_url: string | null;
  }> {
    return request(`/api/esign/video-lookup?url=${encodeURIComponent(url)}`);
  },

  async listEsignEnvelopes(
    entityType: string,
    entityId: string,
  ): Promise<{ envelopes: EsignEnvelopeRec[] }> {
    return request<{ envelopes: EsignEnvelopeRec[] }>(
      `/api/esign/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/envelopes`,
    );
  },
  // The Contact page's "E-signature" status — whether this contact has
  // ever consented, and whether they've since withdrawn.
  async getEsignConsentStatus(contactId: string): Promise<EsignConsentStatus> {
    return request<EsignConsentStatus>(
      `/api/esign/contacts/${encodeURIComponent(contactId)}/consent-status`,
    );
  },
  // Records that a client asked to stop receiving documents for
  // electronic signature — blocks future sends and kills any live link.
  async withdrawEsignConsent(
    contactId: string,
    note?: string,
  ): Promise<{ ok: true; status: EsignConsentStatus }> {
    return request(`/api/esign/contacts/${encodeURIComponent(contactId)}/withdraw-consent`, {
      method: "POST",
      // confirm reflects the dialog the user just clicked through — the
      // server refuses without it, so no caller can withdraw by accident.
      body: JSON.stringify(note ? { note, confirm: true } : { confirm: true }),
    });
  },
  // The admin/compliance-only undo for a withdrawal recorded in error
  // (or a client who changed their mind). Requires a reason.
  async restoreEsignConsent(
    contactId: string,
    note: string,
  ): Promise<{ ok: true; status: EsignConsentStatus }> {
    return request(`/api/esign/contacts/${encodeURIComponent(contactId)}/restore-consent`, {
      method: "POST",
      body: JSON.stringify({ note }),
    });
  },
  // Close-out program PR 4 — Stew reads the Closing Disclosure and
  // compares it against the loan record. Read-only; corrections go
  // through updateMortgageLoanDetails (audited).
  async compareCdToLoan(documentId: string, transactionId: string): Promise<CdCompareResponse> {
    return request<CdCompareResponse>(
      `/api/documents/${encodeURIComponent(documentId)}/compare-cd`,
      {
        method: "POST",
        body: JSON.stringify({ transaction_id: transactionId }),
      },
    );
  },

  /**
   * Read the promissory Note and compare it to the loan record.
   *
   * Separate from the CD comparison because the first payment date is
   * not on a Closing Disclosure — it is on the Note.
   */
  async compareNoteToLoan(documentId: string, transactionId: string): Promise<NoteCompareResponse> {
    return request<NoteCompareResponse>(
      `/api/documents/${encodeURIComponent(documentId)}/compare-note`,
      { method: "POST", body: JSON.stringify({ transaction_id: transactionId }) },
    );
  },
  // Close-out: Stew reads the uploaded broker check / wire confirmation
  // and creates the loan's commission worksheet (which IS the
  // disbursement document) from it. Idempotent server-side — a loan that
  // already has one gets it back rather than a second.
  async generateDisbursementFromCheck(
    documentId: string,
    transactionId: string,
  ): Promise<GenerateDisbursementResponse> {
    return request<GenerateDisbursementResponse>(
      `/api/documents/${encodeURIComponent(documentId)}/generate-disbursement`,
      {
        method: "POST",
        body: JSON.stringify({ transaction_id: transactionId }),
      },
    );
  },
  // "Re-read the check": Stew reads the document on the loan's Check or
  // Wire row NOW and restates the existing worksheet from it (for when
  // the wrong file was uploaded first and then replaced).
  async rereadDisbursementCheck(
    documentId: string,
    transactionId: string,
  ): Promise<RestatedWorksheetResponse> {
    return request<RestatedWorksheetResponse>(
      `/api/documents/${encodeURIComponent(documentId)}/reread-disbursement`,
      {
        method: "POST",
        body: JSON.stringify({ transaction_id: transactionId }),
      },
    );
  },
  // Nate Swartz party extraction (2026-08-04) — Stew proposes the
  // other-side parties named in a dropped Realty contract. Nothing is
  // saved by this call; the confirm UI writes each kept row through
  // api.createTransactionParty.
  async extractDocumentParties(id: string): Promise<PartyExtractionResponse> {
    return request<PartyExtractionResponse>(
      `/api/documents/${encodeURIComponent(id)}/extract-parties`,
      { method: "POST" },
    );
  },

  // "No ARMLS" tier 1 (2026-08-30) — Stew proposes property facts read
  // off a dropped MLS sheet / property profile. Nothing is saved by this
  // call; the confirm UI writes kept facts through
  // api.updateRealtyRecordDetails with fact_source: "document_extraction".
  async extractPropertyFacts(
    id: string,
  ): Promise<{ facts: ExtractedPropertyFactsDto; found_count: number }> {
    return request(`/api/documents/${encodeURIComponent(id)}/extract-property-facts`, {
      method: "POST",
    });
  },

  // Closing-package split (Baker, 2026-08-11). Sends ONLY the scrubbed
  // per-page snippets the browser read locally — never the file — and
  // gets back proposed document boundaries. Writes nothing; the review
  // UI uploads each confirmed piece through the normal upload path.
  async classifyPackage(body: {
    transaction_id: string;
    filename: string;
    pages: PackagePageSnippet[];
  }): Promise<PackageClassificationResponse> {
    return request<PackageClassificationResponse>("/api/documents/classify-package", {
      method: "POST",
      // Sent unconditionally by this build: it tells the server this tab
      // knows how to draw scanned pages, so a request with no pictures
      // from an OLD tab can be answered "reload" instead of "we couldn't
      // draw your file" (Baker, 2026-09-01).
      body: JSON.stringify({ ...body, supports_page_images: true }),
    });
  },

  // Fetch a document's bytes into the browser, same-origin (Grant,
  // 2026-09-02) — so a package already on the record can be split
  // without asking the user to find the file on their computer again.
  //
  // NOT downloadDocument/openDocument: both of those hand the browser a
  // redirect to SharePoint, which is right for a download and useless
  // to JavaScript (cross-origin, no CORS headers, unreadable). This
  // route streams the bytes through the Worker instead. Same permission
  // check, same audit row — see the note on GET /api/files/content/:id.
  //
  // `onProgress` reports REAL bytes received, and only when the server
  // sent a Content-Length; a package can be 80 MB and silence reads as
  // a hung screen. No length → no fraction, and the caller says so
  // rather than inventing one.
  async fetchDocumentBytes(
    documentId: string,
    onProgress?: (received: number, total: number | null) => void,
  ): Promise<Blob> {
    const response = await fetch(apiUrl(`/api/files/content/${encodeURIComponent(documentId)}`), {
      credentials: "include",
    });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const body = (await response.json()) as { message?: string; error?: string };
        message = body.message ?? body.error ?? message;
      } catch {
        // Not JSON — keep the status.
      }
      throw new ApiError(response.status, message, null, null);
    }
    const lengthHeader = response.headers.get("content-length");
    const total = lengthHeader ? Number(lengthHeader) : null;
    if (!response.body || !onProgress) return response.blob();

    // Read it chunk by chunk so progress is the bytes that actually
    // arrived, not a guess.
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        received += value.byteLength;
        onProgress(received, Number.isFinite(total) ? total : null);
      }
    }
    return new Blob(chunks as BlobPart[], {
      type: response.headers.get("content-type") ?? "application/pdf",
    });
  },

  // Nate Swartz, 2026-09-20: the certificate of completion is filed but
  // not listed (a signed document's row no longer shows it as a
  // separate file). This is how the "Download with certificate" option
  // finds it — 404 is the ordinary "nothing filed" answer, not an
  // error, so it resolves to null rather than throwing.
  async getCertificateForSignedDocument(
    documentId: string,
  ): Promise<{ certificate_document_id: string } | null> {
    try {
      return await request<{ certificate_document_id: string }>(
        `/api/files/documents/${encodeURIComponent(documentId)}/certificate`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  // ONE WINDOW of a package (Grant, 2026-09-02). Sending the windows as
  // separate requests is what lets the close-out screen show a progress
  // bar that fills on work actually finished — a 108-page package sent
  // whole is one call with nothing to report for 90 seconds, which is
  // how the old screen ended up counting elapsed seconds against a
  // guess that was wrong by 4x.
  //
  // Returns the model's raw claims. The caller merges every window and
  // runs normalizeProposals ONCE over the lot — see the note on
  // SplitOutcome.raw for why that cannot be done per window.
  async classifyPackageWindow(body: {
    transaction_id: string;
    filename: string;
    pages: PackagePageSnippet[];
    /** Pages in the WHOLE package, for the audit row's sake — so a
     *  window never lands in the log looking like a whole file. */
    total_page_count: number;
  }): Promise<PackageClassificationWindowResponse> {
    return request<PackageClassificationWindowResponse>("/api/documents/classify-package", {
      method: "POST",
      body: JSON.stringify({ ...body, supports_page_images: true, raw: true }),
    });
  },

  // v1.2 — editable splits + per-entity overrides + comp preview.

  async listCommissionSplits(
    params: { include_inactive?: boolean; advisor?: string; company?: string } = {},
  ): Promise<{
    items: CommissionSplitRec[];
    total: number;
  }> {
    const qs = new URLSearchParams();
    if (params.include_inactive) qs.set("include_inactive", "true");
    if (params.advisor) qs.set("advisor", params.advisor);
    if (params.company) qs.set("company", params.company);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{ items: CommissionSplitRec[]; total: number }>(
      `/api/admin/commission-splits${suffix}`,
    );
  },

  // Manager-override rules (kickbacks) — e.g. Brian Ream's $500 when
  // Joey Ellis closes a mortgage file. Scoped under the split they fire
  // from (Grant, 2026-08-11).
  async listCommissionKickbacks(
    splitId: string,
  ): Promise<{ items: CommissionKickbackRec[]; total: number }> {
    return request<{ items: CommissionKickbackRec[]; total: number }>(
      `/api/admin/commission-splits/${encodeURIComponent(splitId)}/kickbacks`,
    );
  },

  // Comp plans: the one home of every pay rule (build step B2). The old
  // split and override routes above are read-only history.
  async listCompPlans(): Promise<{ plans: CompPlanDetail[]; gaps: CompPlanGap[]; startup_send_done?: boolean; today?: string }> {
    return request<{ plans: CompPlanDetail[]; gaps: CompPlanGap[]; startup_send_done?: boolean; today?: string }>(
      "/api/admin/comp-plans",
    );
  },

  async getCompPlansForUser(userId: string): Promise<{
    person: { id: string; name: string | null; email: string };
    plans: CompPlanDetail[];
    startup_send_done?: boolean;
    today?: string;
  }> {
    return request(`/api/admin/comp-plans/users/${encodeURIComponent(userId)}`);
  },

  async createCompPlan(body: CompPlanChangeBody & { user_id: string; company: string }): Promise<CompPlanSaveResult> {
    return request<CompPlanSaveResult>("/api/admin/comp-plans", { method: "POST", body: JSON.stringify(body) });
  },

  async addCompPlanVersion(
    planId: string,
    body: CompPlanChangeBody & { expected_version_number: number },
  ): Promise<CompPlanSaveResult> {
    return request<CompPlanSaveResult>(`/api/admin/comp-plans/${encodeURIComponent(planId)}/versions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async markCompPlanReviewed(versionId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/admin/comp-plans/versions/${encodeURIComponent(versionId)}/reviewed`, {
      method: "POST",
    });
  },

  /** The Undo on "Approve <name>'s plan", while the form has not gone out. */
  async clearCompPlanApproval(versionId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/admin/comp-plans/versions/${encodeURIComponent(versionId)}/unreviewed`, {
      method: "POST",
    });
  },

  async withdrawCompPlanVersion(versionId: string, reason: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/admin/comp-plans/versions/${encodeURIComponent(versionId)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  async sendCompPlanForm(versionId: string): Promise<{ envelope_id: string; emails: { email: string; emailed: boolean }[] }> {
    return request(`/api/admin/comp-plans/versions/${encodeURIComponent(versionId)}/send`, { method: "POST" });
  },

  async resendCompPlanForm(versionId: string): Promise<{ emails: { email: string; emailed: boolean }[] }> {
    return request(`/api/admin/comp-plans/versions/${encodeURIComponent(versionId)}/resend`, { method: "POST" });
  },

  /** The one-page form as the person receives it (opens in a new tab). */
  compPlanFormUrl(versionId: string): string {
    return `/api/admin/comp-plans/versions/${encodeURIComponent(versionId)}/form.pdf`;
  },

  async getMyCompPlans(): Promise<{ plans: CompPlanDetail[]; today: string }> {
    return request<{ plans: CompPlanDetail[]; today: string }>("/api/comp-plans/mine");
  },

  async resendMyCompPlanForm(versionId: string): Promise<{ emails: { email: string; emailed: boolean }[] }> {
    return request(`/api/comp-plans/mine/versions/${encodeURIComponent(versionId)}/resend`, { method: "POST" });
  },

  async getOpportunityExpectedComp(id: string): Promise<ExpectedCompPayload> {
    return request<ExpectedCompPayload>(
      `/api/opportunities/${encodeURIComponent(id)}/expected-comp`,
    );
  },

  async getTransactionExpectedComp(id: string): Promise<ExpectedCompPayload> {
    return request<ExpectedCompPayload>(
      `/api/transactions/${encodeURIComponent(id)}/expected-comp`,
    );
  },

  async setOpportunitySplitOverride(
    id: string,
    body: { override_bps: number | null; reason?: string },
  ): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/opportunities/${encodeURIComponent(id)}/split-override`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async setTransactionSplitOverride(
    id: string,
    body: { override_bps: number | null; reason?: string },
  ): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/api/transactions/${encodeURIComponent(id)}/split-override`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // ─── v1.3 / v1.4 — Send commission paperwork (Phase 1e) ─────────
  // Triggers the Send-to-Advisor flow on a commission_reports row.
  // Renders the attached PDF, sends from the calling user's mailbox
  // via Microsoft Graph, marks the row sent. Returns recipient count
  // on success.
  async sendCommissionReport(
    reportId: string,
    body: {
      composer_message?: string;
      channel?: "email_pdf" | "teams_dm" | "both";
      recipient_emails_override?: string[];
    } = {},
  ): Promise<SendCommissionReportResponse> {
    return request<SendCommissionReportResponse>(
      `/api/admin/commission/reports/${encodeURIComponent(reportId)}/send`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  // ─── Phase 4.1 — Financial Commission Auto-Disbursement ──────────

  async listFinancialDeposits(
    period: string,
  ): Promise<{ period: string; rows: FinancialFeeDepositRec[] }> {
    return request<{ period: string; rows: FinancialFeeDepositRec[] }>(
      `/api/admin/financial/deposits?period=${encodeURIComponent(period)}`,
    );
  },

  async createFinancialDeposit(body: {
    source_slug: string;
    period: string;
    amount_cents: number;
    deposit_date?: string | undefined;
    recipient_user_id?: string | undefined;
    external_account_number?: string | undefined;
    external_client_label?: string | undefined;
    notes?: string | undefined;
  }): Promise<{ ok: true; id: string; recipient_user_id: string | null }> {
    return request("/api/admin/financial/deposits", { method: "POST", body: JSON.stringify(body) });
  },

  async archiveFinancialDeposit(id: string): Promise<{ ok: true }> {
    return request(`/api/admin/financial/deposits/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      body: "{}",
    });
  },

  async ingestBettermentFees(file: File, period: string): Promise<FinancialIngestResult> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("period", period);
    return request("/api/admin/financial/ingest/betterment", {
      method: "POST",
      body: fd,
    });
  },

  async ingestAltruistFees(file: File, period: string): Promise<FinancialIngestResult> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("period", period);
    return request("/api/admin/financial/ingest/altruist", {
      method: "POST",
      body: fd,
    });
  },

  async listFinancialAccountOverrides(
    sourceSlug: string,
  ): Promise<{ source_slug: string; rows: FinancialAccountOverrideRec[] }> {
    return request(
      `/api/admin/financial/account-overrides?source=${encodeURIComponent(sourceSlug)}`,
    );
  },

  async createFinancialAccountOverride(body: {
    source_slug: string;
    external_account_number: string;
    advisor_user_id: string;
    effective_from: string;
    reason?: string | undefined;
  }): Promise<{ ok: true; id: string }> {
    return request("/api/admin/financial/account-overrides", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async deactivateFinancialAccountOverride(id: string): Promise<{ ok: true }> {
    return request(`/api/admin/financial/account-overrides/${encodeURIComponent(id)}/deactivate`, {
      method: "POST",
      body: "{}",
    });
  },

  async runFinancialDisbursement(
    period?: string,
    advisorUserId?: string,
  ): Promise<FinancialDisbursementRunResult> {
    const params = new URLSearchParams();
    if (period) params.set("period", period);
    if (advisorUserId) params.set("advisor_user_id", advisorUserId);
    const qs = params.toString();
    return request(`/api/admin/auto-disbursement/financial/run-now${qs ? `?${qs}` : ""}`, {
      method: "POST",
      body: "{}",
    });
  },

  // ─── Insurance month re-run control (preview / re-run / undo) ───
  async previewInsuranceRerun(period: string): Promise<{ ok: true; preview: InsuranceRerunPreview }> {
    return request(`/api/admin/auto-disbursement/insurance/preview?period=${encodeURIComponent(period)}`, {
      method: "POST",
      body: "{}",
    });
  },

  async rerunInsuranceMonth(
    period: string,
    confirmPeriod?: string,
  ): Promise<{ ok: true; undo_token: string; preview: InsuranceRerunPreview }> {
    return request(`/api/admin/auto-disbursement/insurance/rerun?period=${encodeURIComponent(period)}`, {
      method: "POST",
      body: JSON.stringify(confirmPeriod ? { confirm_period: confirmPeriod } : {}),
    });
  },

  async undoInsuranceRerun(undoToken: string): Promise<{ ok: true; period: string }> {
    return request("/api/admin/auto-disbursement/insurance/undo", {
      method: "POST",
      body: JSON.stringify({ undo_token: undoToken }),
    });
  },

  // ─── Monthly Mortgage Summary (preview + email to the loan officer) ───
  async listMortgageSummaryAdvisors(): Promise<{ advisors: MortgageSummaryAdvisor[] }> {
    return request("/api/admin/commission/mortgage-summary/advisors");
  },

  async previewMortgageSummary(body: {
    advisor_user_id: string;
    period: string;
    note?: string | undefined;
  }): Promise<{ ok: true; summary: MortgageMonthlySummaryRec; email: MortgageSummaryEmailRec }> {
    return request("/api/admin/commission/mortgage-summary/preview", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async sendMortgageSummary(body: {
    advisor_user_id: string;
    period: string;
    note?: string | undefined;
  }): Promise<{ ok: true; to: string; subject: string }> {
    return request("/api/admin/commission/mortgage-summary/send", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // ─── Phase 4.2 — Mortgage Compliance ─────────────────────────────

  async getMyMortgageCompliance(year: number): Promise<MyMortgageComplianceResponse> {
    return request(`/api/me/mortgage-compliance/${year}`);
  },

  async markOnlineEdComplete(
    year: number,
    body?: { completed_courses?: number },
  ): Promise<{
    ok: true;
    online_ed_id: string;
    baker_review_task_id: string | null;
  }> {
    return request(`/api/me/mortgage-compliance/online-ed/${year}/mark-complete`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },

  async logCeCompletion(
    year: number,
    body: {
      hours_completed: number;
      certificate_document_id?: string | undefined;
      notes?: string | undefined;
    },
  ): Promise<{ ok: true; ce_id: string; hours_completed: number }> {
    return request(`/api/me/mortgage-compliance/ce/${year}/log-completion`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async getMortgageComplianceForYear(year: number): Promise<AdminMortgageComplianceResponse> {
    return request(`/api/admin/mortgage-compliance/year/${year}`);
  },

  async setCeRequiredHours(
    id: string,
    requiredHours: number,
  ): Promise<{ ok: true; id: string; required_hours: number }> {
    return request(`/api/admin/mortgage-compliance/ce/${encodeURIComponent(id)}/required-hours`, {
      method: "POST",
      body: JSON.stringify({ required_hours: requiredHours }),
    });
  },

  async verifyCe(id: string, notes?: string): Promise<{ ok: true }> {
    return request(`/api/admin/mortgage-compliance/ce/${encodeURIComponent(id)}/verify`, {
      method: "POST",
      body: JSON.stringify({ notes: notes ?? null }),
    });
  },

  async verifyOnlineEd(id: string, notes?: string): Promise<{ ok: true }> {
    return request(`/api/admin/mortgage-compliance/online-ed/${encodeURIComponent(id)}/verify`, {
      method: "POST",
      body: JSON.stringify({ notes: notes ?? null }),
    });
  },

  async fileNmlsReport(
    id: string,
    body: {
      filing_reference?: string | undefined;
      filing_document_id?: string | undefined;
      notes?: string | undefined;
    },
  ): Promise<{ ok: true }> {
    return request(`/api/admin/mortgage-compliance/nmls/${encodeURIComponent(id)}/file`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async seedMortgageComplianceYear(year: number): Promise<{
    ok: true;
    year: number;
    mlo_count: number;
    ce_created: number;
    online_ed_created: number;
    nmls_created: number;
  }> {
    return request("/api/admin/mortgage-compliance/seed-year", {
      method: "POST",
      body: JSON.stringify({ year }),
    });
  },

  // ─── Phase 4.3 — Realty Compliance ───────────────────────────────

  async getRealtyComplianceForYear(year: number): Promise<{
    year: number;
    licenses: RealtyLicenseRec[];
    expiring_this_year: string[];
  }> {
    return request(`/api/admin/realty-compliance/year/${year}`);
  },

  async listRealtyLicenses(): Promise<{ licenses: RealtyLicenseRec[] }> {
    return request(`/api/admin/realty-compliance/licenses`);
  },

  async logRealtyLicenseRenewed(
    id: string,
    body: { new_expires_at: string; notes?: string | undefined },
  ): Promise<{ ok: true; id: string; expires_at: string }> {
    return request(`/api/admin/realty-compliance/licenses/${encodeURIComponent(id)}/renewed`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async setRealtyChecklistAnchorDates(
    instanceId: string,
    body: {
      listing_date?: string | null | undefined;
      acceptance_date?: string | null | undefined;
      closing_date?: string | null | undefined;
      buyer_representation_signed_at?: string | null | undefined;
    },
  ): Promise<{
    ok: true;
    updated_tasks: number;
    anchor_dates: Record<string, string | null>;
  }> {
    return request(
      `/api/admin/realty-compliance/instances/${encodeURIComponent(instanceId)}/anchor-dates`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  async instantiateRealtyChecklist(body: {
    template_id: RealtyChecklistTemplateId;
    entity_type:
      | "transaction"
      | "opportunity"
      | "policy"
      | "rhythm_instance"
      | "household"
      | "contact"
      | "user";
    entity_id: string;
    anchor_dates: {
      listing_date?: string | null | undefined;
      acceptance_date?: string | null | undefined;
      closing_date?: string | null | undefined;
      buyer_representation_signed_at?: string | null | undefined;
    };
    entity_display_name?: string | undefined;
    notes?: string | undefined;
  }): Promise<{
    ok: true;
    status: "created" | "already_exists" | "template_not_found";
    instance_id: string | null;
    task_count: number;
  }> {
    return request("/api/admin/realty-compliance/instantiate", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // ─── Transaction checklist — record-detail widget ────────────────
  //
  // The non-admin surface: any employee with write scope on the
  // transaction can start a checklist, read it, submit a task, and
  // refresh it. Only a reviewer (system_admin/compliance/the company's
  // company_admin) can accept or reject — enforced server-side;
  // canReviewCompanyChecklists in utils/role-visibility.ts is the UX
  // mirror that decides whether to show the button at all.
  //
  // Company-neutral: the server reads the company off the transaction and
  // returns the templates that company can start, so the same card serves
  // Realty and Insurance without knowing anything about either.

  async getTransactionChecklist(transactionId: string): Promise<{
    company: string;
    /** The FIRST active checklist and its steps. Kept because Insurance's
     *  single-checklist card reads them; the Realty file view reads
     *  `checklists` instead, which carries every active one. */
    instance: RealtyChecklistInstanceRec | null;
    tasks: RealtyChecklistTaskRec[];
    checklists: RealtyChecklistWithTasks[];
    templates: Array<{ id: string; name: string }>;
    /** May THIS viewer accept / send back on this company's files? Server
     *  -answered: since grants exist, it is not derivable from role alone
     *  in the browser (Mark can, Nate cannot, same role). */
    can_review: boolean;
  }> {
    return request(`/api/compliance/transactions/${encodeURIComponent(transactionId)}/checklist`);
  },

  async startTransactionChecklist(
    transactionId: string,
    templateId: string,
  ): Promise<{
    status: "created" | "already_exists" | "template_not_found";
    instance_id: string | null;
    task_count: number;
    skipped_not_applicable: number;
  }> {
    return request(
      `/api/compliance/transactions/${encodeURIComponent(transactionId)}/checklist/start`,
      { method: "POST", body: JSON.stringify({ template_id: templateId }) },
    );
  },

  async refreshTransactionChecklist(
    transactionId: string,
    /** Omit to refresh every active checklist on the record. */
    instanceId?: string,
  ): Promise<{
    updated_tasks: number;
    items_added: number;
    still_not_applicable: number;
  }> {
    return request(
      `/api/compliance/transactions/${encodeURIComponent(transactionId)}/checklist/refresh`,
      {
        method: "POST",
        body: JSON.stringify(instanceId ? { instance_id: instanceId } : {}),
      },
    );
  },

  async submitChecklistTaskForReview(
    taskId: string,
    body: {
      notes?: string | undefined;
      evidence_url?: string | undefined;
      evidence_text?: string | undefined;
    },
  ): Promise<{ ok: true }> {
    return request(`/api/compliance/tasks/${encodeURIComponent(taskId)}/submit`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async reviewChecklistTask(
    taskId: string,
    body: { decision: "accepted" | "rejected"; reason?: string | undefined },
  ): Promise<{ ok: true; manual_task_id: string | null }> {
    return request(`/api/compliance/tasks/${encodeURIComponent(taskId)}/review`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Reassign a checklist step. The server gates on the checklist's own
   *  company scope — the same rule as submitting one. */
  async assignChecklistTask(
    taskId: string,
    assignedToUserId: string | null,
  ): Promise<{ ok: true }> {
    return request(`/api/compliance/tasks/${encodeURIComponent(taskId)}/assign`, {
      method: "POST",
      body: JSON.stringify({ assigned_to_user_id: assignedToUserId }),
    });
  },

  async listChecklistTaskReviews(
    taskId: string,
  ): Promise<{ reviews: RealtyChecklistTaskReviewRec[]; total: number }> {
    return request(`/api/compliance/tasks/${encodeURIComponent(taskId)}/reviews`);
  },

  // ─── Exemptions ──────────────────────────────────────────────────
  //
  // "This step does not apply to this file, and here is why." Asking is
  // open to anyone who can work the file; the DECISION goes through
  // reviewChecklistTask above, which is reviewer-gated. Asking is not
  // deciding — that separation is the whole point.

  /** Ask for a step to be waived. The reason is required and permanent. */
  async requestChecklistTaskExemption(taskId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/compliance/tasks/${encodeURIComponent(taskId)}/exempt`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  /** Pull the request back before anyone decides it — for when you realise
   *  the step does apply after all and would rather just do it. */
  async withdrawChecklistTaskExemption(taskId: string): Promise<{ ok: true }> {
    return request(`/api/compliance/tasks/${encodeURIComponent(taskId)}/exempt/withdraw`, {
      method: "POST",
    });
  },

  // ─── Checklist template editor (admin) ───────────────────────────

  async getChecklistTemplateForEditor(templateId: string): Promise<{
    template: BakerChecklistTemplate;
    items: ChecklistTemplateItemRec[];
    can_edit: boolean;
    edit_denied_reason: string | null;
    /** Files currently running this checklist — the number beside "apply
     *  to files already in flight," so the choice is made against a real
     *  count. */
    active_instance_count: number;
    // The Checklists area (2026-09-29). Optional: an older Worker omits them.
    latest_version?: {
      version_number: number;
      changed_at: string;
      changed_by_name: string | null;
    } | null;
    can_manage_authority?: boolean;
    editors?: ChecklistAuthorityHolderRec[];
    reviewers?: ChecklistAuthorityHolderRec[];
  }> {
    return request(`/api/admin/checklist-templates/${encodeURIComponent(templateId)}`);
  },

  /** Save the whole checklist: header fields and the full ordered item
   *  list in one write. `apply_to_in_flight` is required — the server
   *  refuses to guess. */
  async saveChecklistTemplateItems(
    templateId: string,
    body: {
      name?: string;
      description?: string | null;
      active?: boolean;
      kind?: ChecklistKind;
      applies_to?: ChecklistAppliesTo | null;
      side?: ChecklistSide | null;
      default_from_stage?: string | null;
      is_default?: boolean;
      items?: ChecklistTemplateItemDraft[];
      apply_to_in_flight: boolean;
    },
  ): Promise<{
    ok: true;
    no_change: boolean;
    version_id?: string;
    version_number?: number;
    change_summary?: string;
    propagation?: ChecklistPropagationReport | null;
  }> {
    return request(`/api/admin/checklist-templates/${encodeURIComponent(templateId)}/items`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async listChecklistTemplateVersions(templateId: string): Promise<{
    versions: ChecklistTemplateVersionRec[];
    total: number;
  }> {
    return request(`/api/admin/checklist-templates/${encodeURIComponent(templateId)}/versions`);
  },

  /** Restore an earlier version. Appends a new version carrying the old
   *  content — it never erases the versions it undoes. */
  async rollbackChecklistTemplate(
    templateId: string,
    versionId: string,
    applyToInFlight: boolean,
  ): Promise<{
    ok: true;
    no_change: boolean;
    version_number: number;
    change_summary: string;
    propagation: ChecklistPropagationReport | null;
  }> {
    return request(`/api/admin/checklist-templates/${encodeURIComponent(templateId)}/rollback`, {
      method: "POST",
      body: JSON.stringify({ version_id: versionId, apply_to_in_flight: applyToInFlight }),
    });
  },

  // ─── A file's checklist (Documents tab PR 4, 2026-09-30) ───────────
  /** Every live checklist on an Opportunity or Processing record, its
   *  items and the counts (one server query), plus what may be added. */
  async getRecordChecklist(
    kind: RecordChecklistKind,
    id: string,
  ): Promise<RecordChecklistResponse> {
    return request(`/api/records/${kind}/${encodeURIComponent(id)}/checklist`);
  },

  /** Add a checklist to a file. A file may carry several; adding never
   *  touches the ones already there. */
  async assignRecordChecklist(
    kind: RecordChecklistKind,
    id: string,
    templateId: string,
  ): Promise<{ ok: true; instance_id: string; already: boolean }> {
    return request(`/api/records/${kind}/${encodeURIComponent(id)}/checklist/assign`, {
      method: "POST",
      body: JSON.stringify({ template_id: templateId }),
    });
  },

  /** "Remove checklist": cancels it on this file, history kept. */
  async removeRecordChecklist(
    kind: RecordChecklistKind,
    id: string,
    instanceId: string,
  ): Promise<{ ok: true; name: string }> {
    return request(
      `/api/records/${kind}/${encodeURIComponent(id)}/checklist/${encodeURIComponent(instanceId)}/remove`,
      { method: "POST", body: "{}" },
    );
  },

  /** Undo "Remove checklist". */
  async restoreRecordChecklist(
    kind: RecordChecklistKind,
    id: string,
    instanceId: string,
  ): Promise<{ ok: true; name: string }> {
    return request(
      `/api/records/${kind}/${encodeURIComponent(id)}/checklist/${encodeURIComponent(instanceId)}/restore`,
      { method: "POST", body: "{}" },
    );
  },

  /** "+ Add item to this checklist": an item for this file only. */
  async addRecordChecklistItem(
    kind: RecordChecklistKind,
    id: string,
    body: { instance_id: string; title: string; item_type: "document" | "step"; required: boolean },
  ): Promise<{ ok: true; task_id: string }> {
    return request(`/api/records/${kind}/${encodeURIComponent(id)}/checklist/items`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** "Mark done" on a step to confirm. */
  async markRecordChecklistStepDone(
    kind: RecordChecklistKind,
    id: string,
    taskId: string,
    note: string | null,
  ): Promise<{ ok: true }> {
    return request(
      `/api/records/${kind}/${encodeURIComponent(id)}/checklist/items/${encodeURIComponent(taskId)}/done`,
      { method: "POST", body: JSON.stringify({ note }) },
    );
  },

  /** Link a document filed on the record to a document item. */
  async linkRecordChecklistDocument(
    kind: RecordChecklistKind,
    id: string,
    taskId: string,
    documentId: string | null,
  ): Promise<{ ok: true }> {
    return request(
      `/api/records/${kind}/${encodeURIComponent(id)}/checklist/items/${encodeURIComponent(taskId)}/document`,
      { method: "POST", body: JSON.stringify({ document_id: documentId }) },
    );
  },

  /** "Submit for broker review": every collected, unsent item. */
  async submitRecordChecklist(
    kind: RecordChecklistKind,
    id: string,
    instanceId: string,
  ): Promise<{ ok: true; submitted: number }> {
    return request(`/api/records/${kind}/${encodeURIComponent(id)}/checklist/submit-all`, {
      method: "POST",
      body: JSON.stringify({ instance_id: instanceId }),
    });
  },

  /** Undo "done" on a step (the existing route; reviewer-gated when it
   *  would reverse a broker's acceptance). */
  async uncompleteChecklistTask(taskId: string): Promise<{ ok: true }> {
    return request(`/api/compliance/tasks/${encodeURIComponent(taskId)}/uncomplete`, {
      method: "POST",
      body: "{}",
    });
  },

  /** The Tasks page's "Checklist items for you". */
  async listMyChecklistItems(): Promise<{ items: ChecklistItemForYouRec[] }> {
    return request("/api/checklist-items/mine");
  },

  // ─── Document requests between teammates (Documents tab PR 6a) ─────
  /** A file's open requests, plus who the request window pre-picks. */
  async listRecordRequests(
    kind: RecordChecklistKind,
    id: string,
  ): Promise<{
    requests: DocumentRequestRec[];
    suggested_recipient: SuggestedRecipientRec | null;
    /** The CLUE tab's defaults (PR 7). Optional so older fixtures read. */
    clue?: {
      assignee: { user_id: string; name: string } | null;
      property_address: string | null;
    };
  }> {
    return request(`/api/records/${kind}/${encodeURIComponent(id)}/requests`);
  },

  /** "Send to {name}": ask a teammate for documents on this file. */
  async createRecordRequest(
    kind: RecordChecklistKind,
    id: string,
    body: {
      /** A teammate; or `recipient_email` for someone outside (PR 6b). */
      recipient_user_id?: string;
      recipient_email?: string;
      slots: Array<{ label?: string | null; checklist_task_id?: string | null }>;
      visibility: "internal" | "client";
      note: string | null;
      /** A CLUE request (PR 7): its one box is built on the server. */
      request_type?: "documents" | "clue";
      property_address?: string;
      subject_party?: "buyer" | "seller";
    },
  ): Promise<{ ok: true; id: string; link_path?: string }> {
    return request(`/api/records/${kind}/${encodeURIComponent(id)}/requests`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** The Tasks page's "Requests for you". */
  async listMyRequests(): Promise<{ requests: DocumentRequestRec[] }> {
    return request("/api/requests/mine");
  },

  /** One request, for its drop panel. */
  async getDocumentRequest(
    id: string,
  ): Promise<{ request: DocumentRequestRec; is_recipient: boolean; can_manage: boolean }> {
    return request(`/api/requests/${encodeURIComponent(id)}`);
  },

  /** File an uploaded document into one of the request's boxes. */
  async attachToRequestSlot(
    requestId: string,
    slotId: string,
    documentId: string,
  ): Promise<{ ok: true; closed: boolean }> {
    return request(
      `/api/requests/${encodeURIComponent(requestId)}/slots/${encodeURIComponent(slotId)}/attach`,
      { method: "POST", body: JSON.stringify({ document_id: documentId }) },
    );
  },

  /** "Nudge": ring the recipient's bell again. */
  async nudgeDocumentRequest(id: string): Promise<{ ok: true }> {
    return request(`/api/requests/${encodeURIComponent(id)}/nudge`, { method: "POST", body: "{}" });
  },

  /** "Copy a new link": an outsider's request gets a fresh link; the old
   *  one stops working (PR 6b). */
  async reissueRequestLink(id: string): Promise<{ ok: true; link_path: string }> {
    return request(`/api/requests/${encodeURIComponent(id)}/link`, { method: "POST", body: "{}" });
  },

  /** "Revoke": withdraw the request. */
  async revokeDocumentRequest(id: string): Promise<{ ok: true }> {
    return request(`/api/requests/${encodeURIComponent(id)}/revoke`, { method: "POST", body: "{}" });
  },

  /** The Checklists area's list for one company (2026-09-29). */
  async listChecklistsArea(
    company: string,
    opts?: { archived?: boolean },
  ): Promise<{
    rows: ChecklistListRowRec[];
    total: number;
    can_edit: boolean;
    edit_denied_reason: string | null;
    can_manage_authority: boolean;
  }> {
    const qs = new URLSearchParams({ company });
    if (opts?.archived) qs.set("archived", "1");
    return request(`/api/admin/checklist-templates/area?${qs.toString()}`);
  },

  /** "+ New checklist": creates it and writes version 1. */
  async createChecklist(body: {
    company: string;
    name: string;
    kind: ChecklistKind;
    applies_to: ChecklistAppliesTo | null;
    side: ChecklistSide | null;
    default_from_stage: string | null;
    is_default: boolean;
    items?: ChecklistTemplateItemDraft[];
  }): Promise<{ ok: true; id: string; version_number: number }> {
    return request(`/api/admin/checklist-templates`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Archive: files that have it keep it; new files stop getting it. */
  async archiveChecklist(templateId: string): Promise<{ ok: true }> {
    return request(`/api/admin/checklist-templates/${encodeURIComponent(templateId)}`, {
      method: "DELETE",
    });
  },

  async listChecklistAuthority(company?: string): Promise<{
    grants: ChecklistAuthorityGrantRec[];
    total: number;
  }> {
    const suffix = company ? `?company=${encodeURIComponent(company)}` : "";
    return request(`/api/admin/checklist-authority${suffix}`);
  },

  async grantChecklistAuthority(body: {
    user_id: string;
    company: string;
    capability: "edit_templates" | "broker_review";
    reason?: string;
  }): Promise<{ ok: true; id: string; created: boolean }> {
    return request(`/api/admin/checklist-authority`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async revokeChecklistAuthority(id: string, reason?: string): Promise<{ ok: true }> {
    return request(`/api/admin/checklist-authority/${encodeURIComponent(id)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason: reason ?? null }),
    });
  },

  // ─── Phase 4.4 — Insurance Compliance ────────────────────────────

  async getInsuranceComplianceForYear(year: number): Promise<{
    year: number;
    licenses: RealtyLicenseRec[];
    expiring_this_year: string[];
  }> {
    return request(`/api/admin/insurance-compliance/year/${year}`);
  },

  async listInsuranceLicenses(): Promise<{ licenses: RealtyLicenseRec[] }> {
    return request(`/api/admin/insurance-compliance/licenses`);
  },

  async logInsuranceLicenseRenewed(
    id: string,
    body: { new_expires_at: string; notes?: string | undefined },
  ): Promise<{ ok: true; id: string; expires_at: string }> {
    return request(`/api/admin/insurance-compliance/licenses/${encodeURIComponent(id)}/renewed`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // ─── Phase 4.5 + license-corrections — Planning Compliance ───────

  async getPlanningComplianceForYear(year: number): Promise<{
    year: number;
    licenses: RealtyLicenseRec[];
    expiring_this_year: string[];
  }> {
    return request(`/api/admin/planning-compliance/year/${year}`);
  },

  async listPlanningLicenses(): Promise<{ licenses: RealtyLicenseRec[] }> {
    return request(`/api/admin/planning-compliance/licenses`);
  },

  async logPlanningLicenseRenewed(
    id: string,
    body: { new_expires_at: string; notes?: string | undefined },
  ): Promise<{ ok: true; id: string; expires_at: string }> {
    return request(`/api/admin/planning-compliance/licenses/${encodeURIComponent(id)}/renewed`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // ─── Unified Licenses admin — all five companies ─────────────────
  // Baker 2026-08-03: correct wrong data (not just push the date out)
  // and give Mortgage/Financial a real licenses screen.

  /** What is waiting on the broker to review, oldest first, with how
   *  long each has been sitting against Arizona's ten-business-day
   *  window. The aging is computed server-side — see the route. */
  async listAwaitingBrokerReview(company?: string): Promise<{
    tasks: BrokerReviewQueueItem[];
    /** Documents submitted on the document checklist, waiting on the same
     *  broker under the same ten-business-day clock (2026-09-18). Before
     *  that these sat in no queue at all. */
    documents: BrokerReviewDocumentItem[];
    /** Everything waiting on the broker — tasks AND documents. This is
     *  the number the ten-day rule is about. Each count below names its
     *  own grain deliberately: a bare `total` beside two lists is a
     *  number that reads as "everything" and isn't. */
    waiting_total: number;
    overdue_waiting_total: number;
    task_total: number;
    overdue_task_total: number;
    document_total: number;
    overdue_document_total: number;
    /** How many of the TASKS waiting are requests to SKIP a step rather
     *  than work to check — a different decision, counted separately. A
     *  document entry has no exemption path. */
    exemption_total: number;
    review_window_business_days: number;
    /** Null when no company was named (the flag has no company to be
     *  about). */
    can_review: boolean | null;
  }> {
    const qs = company ? `?company=${encodeURIComponent(company)}` : "";
    return request(`/api/compliance/tasks/awaiting-review${qs}`);
  },

  /** What this Processing record's checklist requires and what's on file,
   *  via the linked service record. `checklist` is null when the deal
   *  has no linked service record yet — nothing to check, not a failure. */
  async getTransactionDocumentChecklist(
    transactionId: string,
  ): Promise<TransactionDocumentChecklistDto> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/document-checklist`);
  },

  /** Lock a closed file's numbers — compliance only (TC-8). */
  async finalizeTransaction(
    transactionId: string,
    body: { note?: string } = {},
  ): Promise<{ ok: true; finalized_at: string }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/finalize`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Unlock it again. A written reason is required — someone signed off
   *  on these numbers, and why that's being undone is part of the record. */
  async unfinalizeTransaction(
    transactionId: string,
    body: { reason: string },
  ): Promise<{ ok: true; finalized_at: null }> {
    return request(`/api/transactions/${encodeURIComponent(transactionId)}/unfinalize`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Ask someone to provide one missing document. The OS doesn't know
   *  who owes it (see the route's own header) — the advisor picks. */
  async requestChecklistDocument(
    transactionId: string,
    definitionId: string,
    body: { assignee_user_id: string; note?: string },
  ): Promise<{
    ok: true;
    task_id: string;
    assignee: { id: string; name: string | null; email: string };
  }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/document-checklist/${encodeURIComponent(definitionId)}/request`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  /** The AGENT's action: put a requirement up for the broker to look at.
   *  A denial is answered by calling this again, which opens the next
   *  round rather than editing the denial away. */
  async submitChecklistRequirement(
    transactionId: string,
    definitionId: string,
    body: { document_id?: string | null; note?: string; is_final?: boolean },
  ): Promise<{ entry: { id: string; round: number; status: string } }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/document-checklist/${encodeURIComponent(definitionId)}/submit`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  /** The BROKER's answer. A denial needs a reason the agent can act on;
   *  the server refuses one that is too short to be one. */
  async reviewChecklistEntry(
    transactionId: string,
    entryId: string,
    body: { decision: "accepted" | "rejected"; reason?: string },
  ): Promise<{ entry: { id: string; status: string } }> {
    return request(
      `/api/transactions/${encodeURIComponent(transactionId)}/document-checklist/entries/${encodeURIComponent(entryId)}/review`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  async listAllLicenses(company?: LicenseCompany): Promise<{
    licenses: LicenseAdminRec[];
    holders: LicenseHolderOption[];
  }> {
    const qs = company && company !== "all" ? `?company=${encodeURIComponent(company)}` : "";
    return request(`/api/admin/licenses${qs}`);
  },

  async createLicense(body: {
    user_id: string;
    license_type: string;
    license_number: string;
    state?: string | null | undefined;
    issued_at?: string | null | undefined;
    expires_at: string;
    company: Exclude<LicenseCompany, "all">;
    notes?: string | null | undefined;
  }): Promise<{ ok: true; license: LicenseAdminRec }> {
    return request(`/api/admin/licenses`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async correctLicense(
    id: string,
    body: {
      license_type?: string | undefined;
      license_number?: string | undefined;
      state?: string | null | undefined;
      issued_at?: string | null | undefined;
      expires_at?: string | undefined;
      company?: Exclude<LicenseCompany, "all"> | undefined;
      notes?: string | null | undefined;
      reason_code?: string | undefined;
      reason_text?: string | undefined;
    },
  ): Promise<{ ok: true; license: LicenseAdminRec }> {
    return request(`/api/admin/licenses/${encodeURIComponent(id)}/correct`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async renewLicense(
    id: string,
    body: { new_expires_at: string; notes?: string | undefined },
  ): Promise<{ ok: true; license: LicenseAdminRec }> {
    return request(`/api/admin/licenses/${encodeURIComponent(id)}/renew`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async retireLicense(
    id: string,
    body: { reason_code?: string | undefined; reason_text?: string | undefined },
  ): Promise<{ ok: true; license: LicenseAdminRec }> {
    return request(`/api/admin/licenses/${encodeURIComponent(id)}/retire`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Front-desk license lookup (Jean/Baker email, 2026-08-28) — read
  // only, every employee, no correct/renew/retire.
  async searchLicenses(query: string): Promise<{ licenses: LicenseAdminRec[] }> {
    return request(`/api/licenses/lookup?q=${encodeURIComponent(query)}`);
  },
};

// ─── IVANS carrier feed types ────────────────────────────────────

export type IvansFileTypeDto = "policy" | "claim" | "edoc" | "commission" | "unknown";

export interface IvansFileDto {
  id: string;
  filename: string;
  content_sha256: string;
  byte_size: number;
  file_type: IvansFileTypeDto;
  r2_key: string;
  source: "transfer_manager" | "file_transfer_api" | "manual_upload";
  mailbox: string | null;
  received_at: string;
  file_modified_at: string | null;
  processing_status: "stored" | "parsed" | "failed" | "ignored";
  processing_error: string | null;
  parsed_at: string | null;
  created_at: string;
  updated_at: string;
}

// Integrations page shapes live in shared/types/integrations.ts.
export type {
  ConnectionStatusDto,
  ConnectionCheckDto,
  ConnectionLinkDto,
  ConnectionGroupDto,
  ConnectionRowDto,
  ConnectionsHealthDto,
  IntegrationMeterDto,
  IntegrationChargeLineDto,
  IntegrationInvoiceDto,
  IntegrationCostRowDto,
  IntegrationCostReportDto,
  CostBillingPeriod,
  CostChargeKind,
  CostMonthCost,
  CostTierMode,
};

export interface IvansFeedStatsDto {
  total: number;
  last_24h: number;
  last_7d: number;
  pending: number;
  failed: number;
  latest_received_at: string | null;
  by_type: Record<IvansFileTypeDto, number>;
  total_bytes: number;
  /** Feed has history but nothing arrived in 48h — something upstream broke. */
  stale: boolean;
  /** When the Cloud PC uploader last checked in. It reports EVERY run,
   *  including the ones that found no files, so this answers "is the
   *  machine alive?" separately from "did carriers send anything?". */
  uploader_last_heartbeat_at: string | null;
  uploader_last_scan: {
    found: number | null;
    stored: number | null;
    duplicate: number | null;
    failed: number | null;
    host: string | null;
    version: string | null;
  };
  /** Checked in before, but not in the last few hours. Never true before
   *  the first heartbeat — an uploader not yet updated is "not set up",
   *  not "broken". */
  uploader_silent: boolean;
}

// ── ByAllAccounts feed (Schwab + Betterment via Morningstar, 2026-09-03) ──

export type ByAllAccountsFileKindDto = "accounts" | "positions" | "transactions" | "unknown";

export interface ByAllAccountsKindStatusDto {
  kind: ByAllAccountsFileKindDto;
  last_received_at: string | null;
  last_status: "stored" | "imported" | "failed" | "ignored" | null;
  last_summary: Record<string, unknown> | null;
  last_error: string | null;
}

export interface ByAllAccountsFeedStatsDto {
  total_files: number;
  latest_received_at: string | null;
  by_kind: ByAllAccountsKindStatusDto[];
  exporter_silent: boolean;
  heartbeat: {
    last_heartbeat_at: string | null;
    last_run_status: string | null;
    last_run_files: number | null;
    last_run_stored: number | null;
    last_run_duplicate: number | null;
    last_run_failed: number | null;
    last_run_host: string | null;
    last_run_version: string | null;
    last_run_message: string | null;
    heartbeat_count: number;
  };
}

export interface ByAllAccountsManualUploadResult {
  status: "stored" | "duplicate";
  file_id: string;
  file_kind: ByAllAccountsFileKindDto;
  message?: string;
  ingest?: {
    kind: ByAllAccountsFileKindDto;
    status: "imported" | "ignored" | "failed";
    counts: Record<string, number>;
    error?: string;
  };
}

export interface ByAllAccountsUploaderConfigDto {
  endpoint: string;
  heartbeat_endpoint: string;
  token: string | null;
  configured: boolean;
  token_source: "explicit_secret" | "derived";
  token_generation: number;
  rotated_at: string | null;
}

export interface IvansUploaderConfigDto {
  endpoint: string;
  token: string | null;
  configured: boolean;
  token_source: "explicit_secret" | "derived";
  /** How many times the token has been rotated (migration 0260). 0 =
   *  never — the original derived token is still in use. */
  token_generation: number;
  rotated_at: string | null;
}

export interface IvansReclassifyResultDto {
  examined: number;
  relabeled: number;
  changed_to: Partial<Record<IvansFileTypeDto, number>>;
}

// Phase B — parse progress. Every count is a server-side COUNT(*).
export interface IvansParseStatsDto {
  files: { stored: number; parsed: number; failed: number; ignored: number };
  /** 'stored' files the parse pass will actually pick up. */
  parse_backlog: number;
  transactions: {
    total: number;
    policy: number;
    commission_statements: number;
    other: number;
    by_cycle: Record<string, number>;
  };
  last_parsed_at: string | null;
}

export interface IvansParsePassSummaryDto {
  ignored_by_type: number;
  examined: number;
  parsed: number;
  failed: number;
  ignored_by_content: number;
  relabeled_commission: number;
  transactions_written: number;
  more_pending: boolean;
}

// ─── Unified Licenses admin types ────────────────────────────────

export type LicenseCompany = "mortgage" | "insurance" | "planning" | "realty" | "financial" | "all";

/** One requirement on a record's document checklist, as the panel
 *  renders it. Status mirrors CloseOutItemStatus
 *  (shared/transactions/close-out-status.ts) but is read as a plain
 *  string here rather than importing the shared union, matching how
 *  every other DTO in this file stays decoupled from the server's
 *  internal types. */
export interface TransactionDocumentChecklistItem {
  definition: {
    id: string;
    name: string;
    code: string | null;
    when_required: string | null;
  };
  status: "missing" | "uploaded" | "signed" | "issue" | "verified" | "not_applicable";
  /** Where this requirement stands with the broker (Baker, 2026-09-16).
   *  Null on every requirement nobody has submitted, which is how a
   *  screen tells "nothing has happened" apart from "round 1 is open". */
  review: {
    rounds: number;
    waitingOnBroker: boolean;
    sentBack: boolean;
    closed: boolean;
    reason: string | null;
  } | null;
  /** Every round, newest first. Each carries its own id, because a round
   *  is the thing the broker approves or sends back. */
  rounds: {
    id: string;
    round: number;
    status: "open" | "submitted" | "accepted" | "rejected";
    isFinal: boolean;
    decisionReason: string | null;
    submittedAt: string | null;
    reviewedAt: string | null;
  }[];
}

export interface TransactionDocumentChecklistDto {
  transaction_id: string;
  linked_service_record_id: string | null;
  checklist: {
    company_slug: string;
    items: TransactionDocumentChecklistItem[];
    missing: TransactionDocumentChecklistItem[];
    progress: {
      total_applicable: number;
      present: number;
      missing: number;
      verified: number;
      not_applicable: number;
    };
  } | null;
  /** The retention clock on this file's documents (TC-8). Null when the
   *  record has no documents at all. */
  retention: {
    clocked: number;
    unclocked: number;
    /** ISO date — the last date anything on this file comes off
     *  retention, i.e. when the file as a whole stops being required. */
    latest_expires_at: string | null;
    retention_class: string | null;
  } | null;
}

/** One item waiting on the designated broker, as the queue renders it. */
export interface BrokerReviewQueueItem {
  id: string;
  title: string;
  description: string | null;
  submitted_at: string | null;
  submitted_by_name: string | null;
  review_round: number;
  entity_type: string;
  entity_id: string;
  entity_display_name: string | null;
  company: string;
  /** Set when what is waiting is a request to SKIP this step rather than
   *  work to check. Both sit in the same queue, on the same ten-business-
   *  day clock, and are decided with the same two buttons — but they are
   *  different decisions, so the row says which. */
  exemption_requested_at: string | null;
  /** The asker's written reason. On an exemption this IS the substance of
   *  what the broker is deciding. */
  exemption_reason: string | null;
  aging: {
    business_days_waiting: number;
    business_days_remaining: number;
    overdue: boolean;
    due_soon: boolean;
  };
}

/** One DOCUMENT waiting on the broker, from the document checklist.
 *  Same clock and same queue as a task, different endpoint to decide it
 *  and different words on screen — which is why it is its own list
 *  rather than a task with a flag. */
export interface BrokerReviewDocumentItem {
  entryId: string;
  documentName: string;
  serviceRecordId: string;
  /** Null on a service record with no Processing record, which is a real
   *  state — the row links to the record instead. */
  transactionId: string | null;
  householdName: string | null;
  company: string;
  round: number;
  submittedAt: string | null;
  submittedByName: string | null;
  /** The agent's note on this round, not the broker's reason. */
  note: string | null;
  aging: {
    business_days_waiting: number;
    business_days_remaining: number;
    overdue: boolean;
    due_soon: boolean;
  };
}

export interface LicenseAdminRec {
  id: string;
  user_id: string;
  license_type: string;
  license_number: string;
  state: string | null;
  issued_at: string | null;
  expires_at: string;
  company: string | null;
  notes: string | null;
  active: number;
  created_at: string;
  updated_at: string;
  user_email: string | null;
  user_name: string | null;
}

export interface LicenseHolderOption {
  id: string;
  name: string | null;
  email: string;
}

// ─── Phase 4.1 types ─────────────────────────────────────────────

export interface FinancialFeeDepositRec {
  id: string;
  period: string;
  source_id: string;
  recipient_user_id: string | null;
  amount_cents: number;
  deposit_date: string | null;
  ingestion_method: string;
  source_file_id: string | null;
  source_file_row_count: number | null;
  external_account_number: string | null;
  external_client_label: string | null;
  notes: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinancialAccountOverrideRec {
  id: string;
  source_id: string;
  external_account_number: string;
  advisor_user_id: string;
  reason: string | null;
  effective_from: string;
  effective_to: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinancialIngestResult {
  ok: true;
  period: string;
  rows_processed: number;
  deposits_upserted: number;
  total_fee_cents: number;
  // Betterment-specific:
  advisors_seen?: number;
  unmatched_advisors?: string[];
  // Altruist-specific:
  unmatched_rows?: number;
  no_commission_total_cents?: number;
  parse_warnings: string[];
}

export interface FinancialDisbursementRunResult {
  ok: boolean;
  status:
    | "ok"
    | "no_qb_snapshot"
    | "no_pool_config"
    | "no_deposits_for_advisor"
    | "no_split"
    | "reconciliation_mismatch";
  period: string;
  qb_snapshot_id?: string | null;
  qb_total_cents?: number;
  deposits_total_cents?: number;
  variance_cents?: number;
  per_advisor?: Array<{
    advisor_user_id: string;
    advisor_email: string | null;
    gross_cents: number;
    advisor_cents: number;
    brokerage_cents: number;
    paychecks_cents: number[];
    commission_disbursement_id: string;
    commission_report_id: string | null;
    pdf_skipped: boolean;
    pdf_error: string | null;
  }>;
  reconciliation_detail?: {
    tolerance_cents: number;
    variance_cents: number;
  };
  detail?: string | null;
}

export interface SendCommissionReportResponse {
  ok: true;
  report_id: string;
  recipient_count: number;
  channel: "email_pdf" | "teams_dm" | "both";
}

// Re-exported from its canonical home so the client, the Edit-user
// panel, and the CHECK constraint on commission_splits.split_type
// (migration 0056) cannot drift — a hand-copied union here is exactly
// how the API route came to reject a split type the schema allowed.
// 'advisor_keeps_minus_brokerage_fixed' is Mike Zins + Cole Sundt's
// model: the advisor keeps the gross minus a fixed brokerage retainer
// held in fixed_amount_cents.
export type CommissionSplitTypeValue = CommissionSplitType;

export interface CommissionSplitRec {
  id: string;
  advisor_user_id: string;
  company: string;
  split_type: CommissionSplitTypeValue;
  split_bps: number | null;
  fixed_amount_cents: number | null;
  applies_to_json: string | null;
  effective_from: string;
  effective_to: string | null;
  agreement_document_id: string | null;
  notes: string | null;
  active: boolean;
  display_label?: string | null;
  // How many paychecks a pay period's amount splits across. All
  // Stewardship companies pay 2× monthly (Grant, 2026-04-24), but the
  // column defaults to 1 — so this is worth showing, not hiding.
  paycheck_count?: number;
}

// A manager-override rule — fires a companion disbursement to
// `recipient_user_id` whenever a worksheet is generated from the split
// it's attached to. Only active (non-deactivated) rules are ever
// returned by listCommissionKickbacks.
export interface CommissionKickbackRec {
  id: string;
  source_split_id: string;
  recipient_user_id: string;
  amount_cents: number;
  trigger_event: "closed_file";
  applies_when_outcome: "won" | "any";
  description: string | null;
  effective_from: string;
  effective_to: string | null;
  active: boolean;
  notes: string | null;
}

/** A comp plan change, as the editor sends it (shared/comp-plans/form.ts validates it). */
export interface CompPlanChangeBody {
  origin: "change" | "correction";
  effective_from: string;
  summary: string;
  reason_code?: string | null;
  reason_text?: string | null;
  add_rules: NewRuleInput[];
  end_rules: { rule_id: string; effective_to: string }[];
  withdraw_rules: { rule_id: string }[];
  terms: Record<string, string | number | null>;
}

export interface CompPlanSaveResult {
  plan_id?: string;
  version_id: string;
  version_n?: number;
  send:
    | { sent: true; envelope_id: string; emails: { email: string; emailed: boolean }[] }
    | { sent: false; reason: string; message?: string; taken_back?: boolean };
}

export interface ExpectedCompPayload {
  entity_type: "opportunity" | "transaction";
  entity_id: string;
  owner_user_id: string | null;
  advisor_user_id: string | null;
  advisor_disbursement_cents: number;
  gross_cents: number | null;
  brokerage_cents: number | null;
  split_bps: number | null;
  split_type: string | null;
  match_reason: "override" | "tag_rule" | "default" | "none" | "no_value";
  override_reason: string | null;
  formula: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Admin reconciliation (PR14 — Phase 8)
// ─────────────────────────────────────────────────────────────────────────

export interface ReconciliationSummary {
  pending_quarantine: number;
  resolved_quarantine: number;
  pending_merge_queue: number;
  households_total: number;
  households_quarantined: number;
}

export interface QuarantineRowDisplay {
  id: string;
  source: string;
  source_file: string | null;
  display_name: string | null;
  display_email: string | null;
  display_phone: string | null;
  raw_data: unknown;
  suggested_household_ids: unknown;
  resolution: string;
  resolved_household_id: string | null;
  resolution_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuarantineListResponse {
  total: number;
  limit: number;
  offset: number;
  items: QuarantineRowDisplay[];
}

export type QuarantineResolveAction = "merge_with" | "create_new" | "mark_orphan" | "skip";

// Duplicate PEOPLE inside a household merge (Grant, 2026-09-17). Merging
// two duplicate households used to leave one person sitting twice under
// two spellings; these types carry the proposal in and the per-pair
// result back out.
export type { MemberMergeOutcome, MemberMergePair, MemberMergeRequest };

export const reconciliationApi = {
  async getSummary(): Promise<ReconciliationSummary> {
    return request<ReconciliationSummary>("/api/admin/reconciliation/summary");
  },

  async listQuarantine(
    opts: { status?: string; limit?: number; offset?: number } = {},
  ): Promise<QuarantineListResponse> {
    const qs = new URLSearchParams();
    if (opts.status) qs.set("status", opts.status);
    if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
    if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
    const query = qs.toString();
    return request<QuarantineListResponse>(
      `/api/admin/reconciliation/quarantine${query ? `?${query}` : ""}`,
    );
  },

  async resolveQuarantine(
    id: string,
    body: {
      action: QuarantineResolveAction;
      resolved_household_id?: string;
      notes?: string;
    },
  ): Promise<{ ok: true; resolution: string }> {
    return request(`/api/admin/reconciliation/quarantine/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /**
   * Which PEOPLE in these two households look like the same person under
   * two spellings. Read-only — it proposes, the user decides.
   */
  async getMergeMemberPairs(
    survivorHouseholdId: string,
    mergedHouseholdId: string,
    opts: { samePersonConfirmed?: boolean } = {},
  ): Promise<{ pairs: MemberMergePair[] }> {
    const qs = new URLSearchParams({
      merged_household_id: mergedHouseholdId,
    });
    // The person already answered "same person under two surnames", so
    // the server also offers the pair whose surnames differ.
    if (opts.samePersonConfirmed) qs.set("same_person", "1");
    return request(
      `/api/admin/households/${encodeURIComponent(survivorHouseholdId)}/merge-member-pairs?${qs.toString()}`,
    );
  },

  async mergeHouseholds(
    primaryHouseholdId: string,
    body: {
      merged_household_id: string;
      reason: string;
      /**
       * Per-field overrides for the household-row's own columns.
       * Wave 6 (May 2026) — added so HouseholdMergeReview can pass
       * per-field "merged" picks. Missing keys default to "primary"
       * (no change). Unknown keys are silently dropped server-side.
       */
      field_overrides?: Record<string, "primary" | "merged">;
      /**
       * Duplicate people to fold together as part of this merge (Grant,
       * 2026-09-17). Each pair keeps the survivor contact and fills its
       * blanks from the other record. Omitted = household merge only,
       * exactly as before.
       */
      member_merges?: MemberMergeRequest[];
    },
  ): Promise<{
    success: boolean;
    primary_household_id: string;
    merged_household_id: string;
    audit_log_id: string;
    counts: Record<string, number>;
    /** One entry per requested member pair — a pair that could not be
     *  merged comes back with `merged: false` and a reason. */
    member_merge_outcomes?: MemberMergeOutcome[];
    applied_field_overrides?: Array<{
      field_name: string;
      from_value: unknown;
      to_value: unknown;
    }>;
    skipped_blank_overrides?: Array<{
      field_name: string;
      from_value: unknown;
      to_value: unknown;
    }>;
  }> {
    return request(`/api/admin/households/${encodeURIComponent(primaryHouseholdId)}/merge`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  // Household UNMERGE — the rollback path for mergeHouseholds (Ryan's
  // 2026-07-02 wrong-merge). Ghosts are households merged INTO the
  // given survivor that can still be restored.
  async listMergedGhosts(survivorHouseholdId: string): Promise<{
    ghosts: Array<{
      ghost: {
        id: string;
        display_name: string;
        merged_into_household_id: string;
        merged_at: string;
      };
      /** Per-table counts of rows that would move back; null when the
       *  preview couldn't be computed. */
      will_move_back: Record<string, number> | null;
      merge_counts: Record<string, number> | null;
      /** Duplicate people this merge folded together, which undoing it
       *  splits back into separate records. Absent when the preview
       *  couldn't be computed. */
      will_undo_member_merges?: number;
    }>;
  }> {
    return request(
      `/api/admin/households/${encodeURIComponent(survivorHouseholdId)}/merged-ghosts`,
    );
  },

  async unmergeHousehold(
    ghostHouseholdId: string,
    body: { reason?: string } = {},
  ): Promise<{
    success: boolean;
    restored_household_id: string;
    survivor_household_id: string;
    moved_back: Record<string, number>;
  }> {
    return request(`/api/admin/households/${encodeURIComponent(ghostHouseholdId)}/unmerge`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

// v1.x phase 11.16 (Tier B item 11) — anonymous (no-auth) helpers
// for the public client-upload landing page. The `credentials:
// "include"` is harmless here — the server ignores any session
// cookie that happens to ride along.
// Joy PR 14 — anonymous intake-form API (the public /intake/r/:token
// page). Errors carry the server's plain-English message.
export interface IntakePagePayload {
  title: string;
  household_display_name: string;
  status: string;
  expires_at: string;
  definition: import("@shared/intake/definition").IntakeDefinition;
  responses: Record<string, unknown>;
}

async function intakeFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await resp.json().catch(() => ({}))) as T & {
    error?: string;
    message?: string;
  };
  if (!resp.ok) {
    throw new ApiError(resp.status, body.message ?? "Something went wrong.", body.error ?? null);
  }
  return body;
}

// Joy PR 15 — mint an intake link for a household (internal; the raw
// link is returned exactly once).
export interface CreateIntakeFormResponse {
  ok: true;
  instance_id: string;
  link: string;
  expires_at: string;
}

// Forms Platform F2 — internal builder/list types (mirror forms-db).
export type FormsListScope = "mine" | "company" | "all";

export interface FormSummary {
  slug: string;
  title: string;
  company_slug: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  status: "draft" | "live" | "archived";
  version: number;
  has_unpublished_changes: boolean;
  page_count: number;
  /** Fields a person answers (info/hidden excluded). */
  question_count?: number;
  submission_count: number;
  last_submitted_at: string | null;
  updated_at: string;
  /** The form's side of a form↔meeting connection, when set. */
  books_after?: { company_slug: string | null; appointment_type_name: string | null } | null;
}

/** Real COUNT(*) per scope (never a measurement of the returned list),
 *  so an empty scope can honestly say how many forms exist one click
 *  wider. `all` is null when the actor can't reach the firm-wide tier. */
export interface FormsScopeCounts {
  mine: number;
  company: number;
  all: number | null;
}

export interface FormDetail extends FormSummary {
  definition: import("@shared/intake/definition").IntakeDefinition | null;
  draft_definition: import("@shared/intake/definition").IntakeDefinition | null;
  draft_updated_at: string | null;
  settings: Record<string, unknown>;
  versions: Array<{ version: number; published: boolean; created_at: string }>;
}

export interface FormLinkRow {
  id: string;
  kind: string;
  public_slug: string | null;
  label: string | null;
  revoked_at: string | null;
  expires_at: string | null;
  household_id: string | null;
  household_display_name: string | null;
  created_via?: string;
  used_at?: string | null;
  created_at: string;
  submission_count: number;
  url: string | null;
}

/** Mirrors shared/forms/forms-db.ts FormLinkCounts. */
export interface FormLinkCounts {
  personal_total: number;
  personal_live: number;
  personal_expired: number;
  personal_revoked: number;
  personal_answered: number;
  households_with_extra_live: number;
  personal_matching: number;
}

export interface HouseholdFormLinkRow {
  id: string;
  template_slug: string;
  form_title: string;
  company_slug: string | null;
  kind: string;
  created_via: string;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  used_at: string | null;
  submission_count: number;
  is_live: boolean;
}

export interface FormSubmissionRow {
  id: string;
  template_version: number;
  household_id: string | null;
  contact_id: string | null;
  match_status: string;
  apply_state: string;
  /** Whether a person still needs to look at this submission -- mapped
   *  fields, health-list changes (pharmacy/doctors/medications), and a
   *  free-text note, not apply_state alone. See
   *  shared/forms/review-status.ts. */
  review_status: "none" | "needs_review" | "reviewed";
  status: string;
  submitted_at: string | null;
  last_saved_at: string | null;
  household_display_name: string | null;
  /** "legacy" = sent before this form moved onto the Forms Platform —
   *  read-only, no apply/review panel (there was never a mapping to
   *  apply). Grant relayed from Joy, 2026-08-20. */
  source: "platform" | "legacy";
}

export interface FormSubmissionDetail extends FormSubmissionRow {
  template_slug: string;
  review_reasons: string[];
  reviewed_at: string | null;
  reviewed_by_user_id: string | null;
  responses: Record<string, unknown>;
}

export interface FormApplyPlanEntry {
  target: string;
  label: string;
  tier: "auto" | "review";
  field: string;
  changes: Array<{ column: string; current: string | null; incoming: string }>;
  skipped?: string;
}

/** What protected answers a submission can save onto the contact record.
 *  Masked by construction — `last_four` is the most the browser ever sees. */
export interface FormSensitiveApplyPlan {
  submission_id: string;
  contact_id: string;
  entries: Array<{
    family: string;
    field: string;
    last_four: string | null;
    /** True when the contact already has a value for this family, so the
     *  reviewer knows they are replacing rather than filling a blank. */
    replaces_existing: boolean;
    /** True when this exact answer is already the value on the record
     *  (filed by a person, or automatically when the client submitted).
     *  Optional for an older Worker's response. */
    already_filed?: boolean;
    /** Where approving files it: the person, the business entity (EIN),
     *  or the Private Money loan (its wire details — never the person's
     *  own bank account). Optional for an older Worker's response. */
    destination?: "contact" | "business_entity" | "loan_wire" | "co_borrower";
    /** Set when there's nowhere to file it yet. */
    unavailable_reason?: string;
    /** What to send back to approve this entry; the family when absent.
     *  A co-borrower's SSN carries its own key (KlinkLoans loan
     *  documents form), since several entries share the `ssn` family. */
    approval_key?: string;
    /** destination "co_borrower": the co-borrower's own record + name. */
    target_contact_id?: string;
    person_name?: string;
  }>;
}

/** Mirrors shared/forms/household-payment-info.ts. Masks only. */
export interface HouseholdPaymentInfo {
  members: Array<{
    contact_id: string;
    name: string;
    bank: {
      has_account: boolean;
      account_last_four: string | null;
      has_routing: boolean;
      has_name: boolean;
      account_type: string | null;
    } | null;
    card: {
      has_number: boolean;
      number_last_four: string | null;
      expiration: string | null;
      billing_zip: string | null;
      has_security_code: boolean;
      has_name: boolean;
    } | null;
  }>;
  pending: Array<{
    submission_id: string;
    form_title: string;
    submitted_at: string | null;
    contact_id: string;
    items: Array<{
      family: string;
      label: string;
      last_four: string | null;
      replaces_existing: boolean;
    }>;
  }>;
}

export interface FormApplyPlan {
  contact_id: string;
  entries: FormApplyPlanEntry[];
}

export interface FormSubmissionRecordCandidate {
  id: string;
  type_slug: string | null;
  display_label: string | null;
  created_at: string;
  label: string;
}

export interface FormSubmissionRecordState {
  submission_id: string;
  household_id: string | null;
  destination: {
    record_type: "household" | "opportunity" | "transaction" | "service_record";
    company_slug: string | null;
    service_types: string[] | null;
  };
  record_id: string | null;
  message: string | null;
  candidates: FormSubmissionRecordCandidate[];
  /** True when at least one of the form's mapped fields actually has an
   *  answer on this submission — the picker only makes sense to show
   *  when this is true. */
  has_pending_answers: boolean;
}

// ─── PR3 (2026-09-06): Forms reachable from household/contact/appointment ──

export interface RecordFormSubmissionRow {
  id: string;
  template_slug: string;
  template_title: string;
  template_version: number;
  household_id: string | null;
  contact_id: string | null;
  match_status: string;
  apply_state: string;
  status: string;
  submitted_at: string | null;
  last_saved_at: string | null;
  source: "platform" | "legacy";
}

export interface RecordFormSubmissionsResponse {
  submissions: RecordFormSubmissionRow[];
  total: number;
}

export interface PendingReviewColumn {
  column: string;
  submission_id: string;
}

export interface FormResultsApplyEntry {
  target: string;
  label: string;
  entity:
    | "contact"
    | "household"
    | "opportunity"
    | "transaction"
    | "service_record"
    | "planning_case";
  record_id: string | null;
  field: string;
  changes: Array<{ column: string; current: string | null; incoming: string }>;
}

export interface FormResultsField {
  key: string;
  label: string;
  type: string;
  /** The answer in plain words — the option's own wording, an address as
   *  an address — or null when there is no answer. */
  value: string | null;
  /** Repeater rows / checkbox picks, so they can stack instead of
   *  running together. `value` holds the same content joined. */
  lines?: string[];
  answered: boolean;
  mapped: boolean;
  review_only: boolean;
}

export interface FormResultsSection {
  key: string;
  title: string;
  fields: FormResultsField[];
}

/** Which wording the answers are shown against — see
 *  shared/forms/apply-submission.ts. "current_version" means the version
 *  the client answered could not be found and today's wording was used,
 *  which the page says out loud. */
export type FormResultsDefinitionSource = "as_answered" | "current_version" | "none";

export interface FormResultsAppointment {
  id: string;
  subject: string;
  start_at: string;
}

export interface FormResultsDetail {
  id: string;
  template_slug: string;
  template_title: string;
  template_version: number;
  household_id: string | null;
  household_display_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  match_status: string;
  apply_state: string;
  status: string;
  submitted_at: string | null;
  last_saved_at: string | null;
  fields: FormResultsField[];
  sections: FormResultsSection[];
  definition_source: FormResultsDefinitionSource;
  has_unmatched_answers: boolean;
  applied_entries: FormResultsApplyEntry[];
  pending_entries: FormResultsApplyEntry[];
  appointment: FormResultsAppointment | null;
  /** The early stop these answers triggered, or null (LTC prequal). */
  stopped_early?: { reason: string; question: string } | null;
  /** Advisor-only flags these answers raise ("Likely rated"). */
  flags?: { key: string; label: string; because: string[] }[];
  /** Who filled this in WITH the client, when someone did (2026-10-14).
   *  Null when the client answered it themselves. */
  entered_by?: { user_id: string; name: string } | null;
}

/** What the Planning open-enrollment questionnaire would change about a
 *  household's doctor and medication lists — the changed-since-last-year
 *  diff (S9). Empty `operations` for every other form, which is how the
 *  panel self-hides. */
export interface FormHealthWriteBackPlan {
  submission_id: string;
  /** "2 new medications, 1 removed doctor" — empty when nothing changed. */
  headline: string;
  operations: Array<{
    id: string;
    /** "pharmacy" is CONTACTS.preferred_pharmacy, not a list row — see
     *  planning-oep-health-writeback.ts's HealthWriteBackKind. */
    list_kind: "provider" | "medication" | "pharmacy";
    op: "add" | "update" | "remove";
    contact_id: string;
    person_name: string;
    row_id: string | null;
    label: string;
    summary: string;
    changes: Array<{
      column: string;
      label: string;
      current: string | null;
      incoming: string;
    }>;
    /** A caveat the reviewer must see before approving, e.g. a row that
     *  left the record after the questionnaire was sent. */
    caveat?: string;
  }>;
  /** Warnings that are not operations — chiefly "this list was too long
   *  to show, so removals aren't offered." Always rendered. */
  notices: string[];
  /** Every person the plan reasoned about, including those with no
   *  changes. The server authorizes against this, not the operations. */
  subject_contact_ids: string[];
  subject_household_id?: string | null;
}

// Forms Platform F1 — the hosted public form page's client.
export interface PublicFormPayload {
  title: string;
  /** Whose form this is, from the respondent's point of view — the
   *  wordmark, the line under it, and the number to call. Follows the
   *  TEMPLATE, not the hostname (shared/forms/form-branding.ts), so a
   *  KlinkLoans application looks like one wherever it is served. */
  brand?: import("@shared/forms/form-branding").FormBrand;
  definition: import("@shared/intake/definition").IntakeDefinition;
  render_token: string | null;
  /** Personalized (tokenized) links only — non-sensitive mapped answers
   *  pre-filled from the record. */
  prefill?: Record<string, unknown>;
  /** Personalized links only — the rendered version, echoed on submit so
   *  the server validates against exactly what the client saw. */
  template_version?: number;
  /** Personalized links only — whether "Save & finish later" is offered
   *  (reusable non-sensitive links; the server refuses drafts otherwise). */
  can_save?: boolean;
  /** Resume links only — the answers saved so far. */
  saved?: Record<string, unknown>;
  /** Public links only, when Turnstile is configured on the environment. */
  turnstile_site_key?: string;
  /** Public links only — this form offers the "have we worked together
   *  before?" step before its first page (§8.5). */
  returning_lookup?: boolean;
  /** Personalized links only — this form is connected to a meeting, so a
   *  calendar step follows the last page on the same page (2026-09-05). */
  books_after?: boolean;
  /** Set only when an employee opened this link to fill the form in WITH
   *  the client ("Fill in a form with the client", 2026-10-14). */
  assisted?: { by_name: string; person_name: string | null } | null;
}

export interface FormVersionFunnel {
  version: number;
  views: number;
  submissions: number;
  partial_saves: number;
  completion_rate: number | null;
}

export interface PublicFormSubmitResponse {
  ok: true;
  /** `stop` is set when the server found an early stop in the answers:
   *  the page draws the stop from its own definition instead of the
   *  thank-you (LTC health intake, 2026-09-23). */
  confirmation: {
    message?: string;
    redirect_url?: string;
    stop?: { key: string; reason: string };
  };
  /** The calendar step for a form connected to a meeting, as something
   *  the page can run inline (the invite token loads the booking page's
   *  own API). Null when the form asks no meeting or none could be
   *  minted; older pages follow confirmation.redirect_url instead. */
  next_booking?: {
    invite_token: string;
    url: string;
    advisor_user_id: string | null;
    appointment_type_id: string | null;
  } | null;
  /** The "Add your documents" step on a Private Money application's
   *  confirmation screen: a short-lived, write-only upload token for the
   *  one Opportunity the application opened. Absent/null for every other
   *  form. */
  document_upload?: { token: string; expires_at: string } | null;
}

// E-signature client-facing signing page — no account, token IS the
// credential, same posture as formsAnonymousApi below.
export interface EsignLinkPayload {
  title: string;
  status: string;
  signer_name: string;
  /** The advisor who sent it, and which company — the trust line on the
   *  landing card. Null on an envelope that arrived from outside the OS. */
  sender_name: string | null;
  company_slug: string | null;
  requires_consent: boolean;
  already_completed: boolean;
  /** Other requests still waiting on this same person (2026-10-02): a true
   *  count plus up to five titles. Optional so an older cached response
   *  simply shows nothing extra. */
  other_waiting?: { count: number; titles: string[] };
}

/** "Ask the one question" answer links (Marketing Hub): the public page's
 *  two calls. No session; the link is the credential. */
export interface AnswerLinkView {
  ok: true;
  product: string;
  company_name: string;
  sender_first_name: string;
  answered: string | null;
  can_change: boolean;
}

export const answerAnonymousApi = {
  load(token: string): Promise<AnswerLinkView> {
    return intakeFetch(`/api/public/answer/${encodeURIComponent(token)}`);
  },
  answer(token: string, choice: string): Promise<{ ok: true; choice: string; changed: boolean }> {
    return intakeFetch(`/api/public/answer/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ choice }),
    });
  },
};

/** The review ask's private page (Marketing Hub row 4.4): the public
 *  page's two calls. No session; the link is the credential. */
export interface ReviewLinkView {
  ok: true;
  company_name: string;
  sender_first_name: string;
  google_review_url: string | null;
}

export const reviewAnonymousApi = {
  load(token: string): Promise<ReviewLinkView> {
    return intakeFetch(`/api/public/review/${encodeURIComponent(token)}`);
  },
  answer(token: string, choice: "concern" | "not_now", text: string | null): Promise<{ ok: true; choice: string }> {
    return intakeFetch(`/api/public/review/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ choice, text }),
    });
  },
};

/** The introduction ask's page (Marketing Hub row 4.5): the public page's
 *  two calls. No session; the link is the credential. */
export interface IntroduceLinkView {
  ok: true;
  company_name: string;
  sender_first_name: string;
  first_name: string;
  shareable_text: string;
}

export const introduceAnonymousApi = {
  load(token: string): Promise<IntroduceLinkView> {
    return intakeFetch(`/api/public/introduce/${encodeURIComponent(token)}`);
  },
  answer(
    token: string,
    choice: "introduce" | "later",
    form: { name: string; reach: string; words: string | null; permission: boolean } | null,
  ): Promise<{ ok: true; choice: string; referral_opened: boolean }> {
    return intakeFetch(`/api/public/introduce/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ choice, form }),
    });
  },
};

/** The preference center (Marketing Hub row 3.5): the public page's two
 *  calls. No session; the signed footer link is the credential. */
export const preferencesAnonymousApi = {
  load(token: string): Promise<PreferenceView> {
    return intakeFetch(`/api/public/preferences/${encodeURIComponent(token)}`);
  },
  save(token: string, body: PreferenceSave): Promise<{ ok: true; view: PreferenceView | null }> {
    return intakeFetch(`/api/public/preferences/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  /** Slice 6 audit M4: turn one company's marketing email back on. */
  restartCompanyEmail(token: string, company: string): Promise<{ ok: true; view: PreferenceView | null }> {
    return intakeFetch(`/api/public/preferences/${encodeURIComponent(token)}/company-email`, {
      method: "POST",
      body: JSON.stringify({ company }),
    });
  },
};

/** The annual check-in (Marketing Hub row 3.7): the public page's two
 *  calls. No session; the signed, dated personal link is the credential. */
export const checkinAnonymousApi = {
  load(token: string): Promise<CheckinView> {
    return intakeFetch(`/api/public/checkin/${encodeURIComponent(token)}`);
  },
  send(token: string, answers: CheckinAnswers): Promise<{ ok: true; sent_on: string }> {
    return intakeFetch(`/api/public/checkin/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ answers }),
    });
  },
  /** Row 6.8: the household review, once this person has sent this year's. */
  review(token: string): Promise<CheckinReviewResponse> {
    return intakeFetch(`/api/public/checkin/${encodeURIComponent(token)}/review`);
  },
  talk(token: string, companySlug: string): Promise<{ ok: true; hand_raised: boolean }> {
    return intakeFetch(`/api/public/checkin/${encodeURIComponent(token)}/review/talk`, {
      method: "POST",
      body: JSON.stringify({ company_slug: companySlug }),
    });
  },
};

/** Row 6.8: the household review behind the check-in (shared/db/checkin-review.ts). */
export type CheckinReviewResponse = Omit<import("@shared/db/checkin-review").CheckinReview, "household_id">;

/** Row 6.6: the household digest's page (shared/db/digest-page.ts). */
export interface DigestPageResponse {
  first_name: string;
  month: string;
  has_digest: boolean;
  last_day: string;
  sections: {
    index: number;
    section: import("@shared/marketing/digest").DigestSection;
    advisor_first_name: string | null;
    answered: "check" | "not_now" | "fine" | null;
  }[];
  taps: Record<string, string[]>;
  /** Row 6.8: this person's own check-in link, when the digest carries it. */
  checkin_url: string | null;
}

/** The digest page's three calls. No session; the signed, per-month
 *  personal link is the credential. */
export const digestAnonymousApi = {
  load(token: string): Promise<DigestPageResponse> {
    return intakeFetch(`/api/public/digest/${encodeURIComponent(token)}`);
  },
  tap(token: string, section: number, choice: "opened" | "talk" | "check" | "not_now" | "fine"): Promise<{ ok: true; hand_raised: boolean }> {
    return intakeFetch(`/api/public/digest/${encodeURIComponent(token)}/tap`, { method: "POST", body: JSON.stringify({ section, choice }) });
  },
  freshLink(token: string): Promise<{ ok: true; message: string }> {
    return intakeFetch(`/api/public/digest/${encodeURIComponent(token)}/fresh-link`, { method: "POST" });
  },
};

export const esignAnonymousApi = {
  load(token: string): Promise<EsignLinkPayload> {
    return intakeFetch(`/api/public/esign/${encodeURIComponent(token)}`);
  },
  consent(token: string): Promise<{ ok: true }> {
    return intakeFetch(`/api/public/esign/${encodeURIComponent(token)}/consent`, {
      method: "POST",
    });
  },
  embedLink(token: string): Promise<{ sign_link: string }> {
    return intakeFetch(`/api/public/esign/${encodeURIComponent(token)}/embed-link`, {
      method: "POST",
    });
  },
  /** Did the signature really take? Asked after the provider sends the
   *  browser back, because that redirect alone is not proof. */
  confirm(token: string): Promise<{
    outcome: "signed" | "not_signed" | "closed" | "unknown";
    other_waiting?: { count: number; titles: string[] };
  }> {
    return intakeFetch(`/api/public/esign/${encodeURIComponent(token)}/confirm`, {
      method: "POST",
    });
  },
  /** Has the sealed copy been filed yet? The webhook lands ~a minute
   *  after the last signer finishes, so the completion page polls this
   *  rather than offering a download button that would 202. */
  signedDocumentStatus(token: string): Promise<{ ready: boolean }> {
    return intakeFetch(`/api/public/esign/${encodeURIComponent(token)}/signed-document/status`);
  },
  /** Direct URL — used as an <a href> so the browser handles the save
   *  dialog itself rather than buffering a PDF through fetch. */
  signedDocumentUrl(token: string): string {
    return `/api/public/esign/${encodeURIComponent(token)}/signed-document`;
  },
};

export const formsAnonymousApi = {
  load(publicSlug: string): Promise<PublicFormPayload> {
    return intakeFetch(`/api/forms/p/${encodeURIComponent(publicSlug)}`);
  },
  /** The returning-borrower door (§8.5). Its answer is deliberately the
   *  same whether or not we found anybody — the client is told nothing to
   *  render differently, because the two answers differing at all would
   *  turn a public form into a way to check who is a client. */
  continueExisting(
    publicSlug: string,
    body: { identifier: string; website: string; turnstile_token?: string },
  ): Promise<{ ok: boolean; message: string }> {
    return anonymousFormPost(
      `/api/forms/p/${encodeURIComponent(publicSlug)}/continue`,
      body,
    ) as unknown as Promise<{ ok: boolean; message: string }>;
  },
  /** F4 — personalized/single-use tokenized links (the link IS the credential). */
  loadToken(token: string): Promise<PublicFormPayload> {
    return intakeFetch(`/api/forms/p/t/${encodeURIComponent(token)}`);
  },
  submit(
    publicSlug: string,
    body: {
      responses: Record<string, unknown>;
      render_token: string | null;
      website: string;
      turnstile_token?: string;
      /** Row 5.7: the coded link the form was opened with. */
      content_code?: string;
    },
  ): Promise<PublicFormSubmitResponse> {
    return anonymousFormPost(`/api/forms/p/${encodeURIComponent(publicSlug)}/submit`, body);
  },
  submitToken(
    token: string,
    body: {
      responses: Record<string, unknown>;
      website: string;
      template_version?: number;
      content_code?: string;
    },
  ): Promise<PublicFormSubmitResponse> {
    return anonymousFormPost(`/api/forms/p/t/${encodeURIComponent(token)}/submit`, body);
  },
  // F6 — explicit save-and-resume on public forms.
  async save(
    publicSlug: string,
    body: { responses: Record<string, unknown>; website: string },
  ): Promise<{ ok: true; resume_url: string | null; resume_token: string }> {
    const resp = await fetch(`${API_ORIGIN}/api/forms/p/${encodeURIComponent(publicSlug)}/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const parsed = (await resp.json().catch(() => ({}))) as {
      ok?: true;
      resume_url?: string | null;
      resume_token?: string;
      message?: string;
      error?: string;
    };
    if (!resp.ok) {
      throw new ApiError(resp.status, parsed.message ?? "Couldn't save.", parsed.error ?? null);
    }
    return parsed as { ok: true; resume_url: string | null; resume_token: string };
  },
  loadResume(rtoken: string): Promise<PublicFormPayload> {
    return intakeFetch(`/api/forms/p/r/${encodeURIComponent(rtoken)}`);
  },
  /** Save-for-later on a personalized link (draft keeps the household
   *  binding; resume rides the same /f/r/ flow). */
  async saveToken(
    token: string,
    body: { responses: Record<string, unknown>; website: string },
  ): Promise<{ ok: true; resume_url: string | null; resume_token: string }> {
    const resp = await fetch(`${API_ORIGIN}/api/forms/p/t/${encodeURIComponent(token)}/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const parsed = (await resp.json().catch(() => ({}))) as {
      ok?: true;
      resume_url?: string | null;
      resume_token?: string;
      message?: string;
      error?: string;
    };
    if (!resp.ok) {
      throw new ApiError(resp.status, parsed.message ?? "Couldn't save.", parsed.error ?? null);
    }
    return parsed as { ok: true; resume_url: string | null; resume_token: string };
  },
  async resave(rtoken: string, responses: Record<string, unknown>): Promise<{ ok: true }> {
    const resp = await fetch(`${API_ORIGIN}/api/forms/p/r/${encodeURIComponent(rtoken)}/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ responses }),
    });
    if (!resp.ok) {
      const parsed = (await resp.json().catch(() => ({}))) as { message?: string; error?: string };
      throw new ApiError(resp.status, parsed.message ?? "Couldn't save.", parsed.error ?? null);
    }
    return { ok: true };
  },
  submitResume(
    rtoken: string,
    body: {
      responses: Record<string, unknown>;
      website: string;
      turnstile_token?: string;
    },
  ): Promise<PublicFormSubmitResponse> {
    return anonymousFormPost(`/api/forms/p/r/${encodeURIComponent(rtoken)}/submit`, body);
  },
  /** "Add your documents": one file onto the Opportunity the application
   *  opened. The token is the one the submit response carried. */
  async uploadApplicationDocument(
    token: string,
    body: { file: File; kind: string },
  ): Promise<{ ok: true; filename: string; file_size_bytes: number; kind: string }> {
    const form = new FormData();
    form.append("file", body.file);
    form.append("kind", body.kind);
    const resp = await fetch(
      `${API_ORIGIN}/api/application-upload/upload?token=${encodeURIComponent(token)}`,
      { method: "POST", body: form },
    );
    const parsed = (await resp.json().catch(() => ({}))) as {
      ok?: true;
      filename?: string;
      file_size_bytes?: number;
      kind?: string;
      message?: string;
      error?: string;
    };
    if (!resp.ok) {
      throw new ApiError(
        resp.status,
        parsed.message ??
          (resp.status === 404
            ? "This upload step has closed. Your application is in — ask us and we’ll email you an upload link."
            : "That file didn't go through — try again."),
        parsed.error ?? null,
      );
    }
    return parsed as { ok: true; filename: string; file_size_bytes: number; kind: string };
  },
};

async function anonymousFormPost(path: string, body: unknown): Promise<PublicFormSubmitResponse> {
  const resp = await fetch(`${API_ORIGIN}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const parsed = (await resp.json().catch(() => ({}))) as {
    ok?: true;
    confirmation?: PublicFormSubmitResponse["confirmation"];
    error?: string;
    message?: string;
    field_errors?: Record<string, string>;
  };
  if (!resp.ok) {
    const err = new ApiError(
      resp.status,
      parsed.message ?? "Something went wrong.",
      parsed.error ?? null,
    ) as ApiError & { field_errors?: Record<string, string> };
    if (parsed.field_errors) err.field_errors = parsed.field_errors;
    throw err;
  }
  return parsed as PublicFormSubmitResponse;
}

export const intakeAnonymousApi = {
  load(token: string): Promise<IntakePagePayload> {
    return intakeFetch(`/api/intake/${encodeURIComponent(token)}`);
  },
  save(token: string, responses: Record<string, unknown>): Promise<{ ok: true }> {
    return intakeFetch(`/api/intake/${encodeURIComponent(token)}`, {
      method: "PATCH",
      body: JSON.stringify({ responses }),
    });
  },
  submit(token: string, responses: Record<string, unknown>): Promise<{ ok: true }> {
    return intakeFetch(`/api/intake/${encodeURIComponent(token)}/submit`, {
      method: "POST",
      body: JSON.stringify({ responses }),
    });
  },
};

/** What the no-login request page shows (Documents tab PR 6b). */
export interface RequestLinkLookup {
  requester_name: string | null;
  company: string;
  note: string | null;
  expires_at: string;
  slots: Array<{ id: string; label: string; filled: boolean }>;
  max_bytes: number;
}

/** The no-login side of a document request (PR 6b): /request/:token. */
export const requestLinkApi = {
  async lookup(rawToken: string): Promise<RequestLinkLookup | null> {
    const res = await fetch(
      apiUrl(`/api/request-upload/look-up?token=${encodeURIComponent(rawToken)}`),
      { credentials: "omit" },
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, `lookup_failed_${res.status}`, null);
    return (await res.json()) as RequestLinkLookup;
  },
  /** Streams the file as the request body, whatever its size. */
  async upload(rawToken: string, slotId: string, file: File): Promise<{ ok: true; closed: boolean }> {
    const qs = new URLSearchParams({
      token: rawToken,
      slot: slotId,
      filename: file.name,
      size: String(file.size),
    });
    const res = await fetch(apiUrl(`/api/request-upload/upload?${qs.toString()}`), {
      method: "POST",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
      credentials: "omit",
    });
    if (!res.ok) {
      let payload: { error?: string; message?: string } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        // ignore parse failures
      }
      throw new ApiError(
        res.status,
        payload.message ?? payload.error ?? `upload_failed_${res.status}`,
        payload.error ?? null,
      );
    }
    return (await res.json()) as { ok: true; closed: boolean };
  },
};

export const clientUploadAnonymousApi = {
  async lookup(rawToken: string): Promise<ClientUploadTokenLookupResponse | null> {
    const url = apiUrl(`/api/client-upload/look-up?token=${encodeURIComponent(rawToken)}`);
    const res = await fetch(url, { credentials: "include" });
    if (res.status === 404) return null;
    if (!res.ok) {
      throw new ApiError(res.status, `lookup_failed_${res.status}`, null);
    }
    return (await res.json()) as ClientUploadTokenLookupResponse;
  },
  async uploadFile(rawToken: string, file: File): Promise<ClientUploadFileResponse> {
    const fd = new FormData();
    fd.append("file", file);
    const url = apiUrl(`/api/client-upload/upload?token=${encodeURIComponent(rawToken)}`);
    const res = await fetch(url, {
      method: "POST",
      body: fd,
      credentials: "include",
    });
    if (!res.ok) {
      let payload: { error?: string; message?: string } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        // ignore parse failures
      }
      throw new ApiError(
        res.status,
        payload.message ?? payload.error ?? `upload_failed_${res.status}`,
        payload.error ?? null,
      );
    }
    return (await res.json()) as ClientUploadFileResponse;
  },
};

// ─── Services Check types ─────────────────────────────────────────
//
// Mirrors shared/db/service-check.ts. Five states render, three are
// stored: "have it with us" is derived from the product record and
// "not asked yet" is the absence of a row.

export type ServiceCheckRowState =
  | "have_it_with_us"
  | "has_it_elsewhere"
  | "not_right_now"
  | "do_not_ask"
  | "interested"
  | "not_applicable"
  | "not_asked_yet";

export interface ServiceCheckRow {
  service_key: string;
  label: string;
  state: ServiceCheckRowState;
  provider_name: string | null;
  set_up_year: number | null;
  revisit_month: string | null;
  note: string | null;
  answered_by_name: string | null;
  answered_at: string | null;
  /** 'advisor', 'backfill', 'requote_consent', … ; null for the two
   *  states the OS works out itself. */
  source: string | null;
  told_us_on: string | null;
  /** "Told us Mar 3, 2026 (from a cancelled policy) · recorded …". */
  record_line: string | null;
  with_us_detail: string | null;
  due: boolean;
}

export interface ServiceCheckCompanyBlock {
  company_id: string;
  company_slug: string;
  company_display_name: string;
  rows: ServiceCheckRow[];
  with_us: number;
  asked: number;
  never_asked: number;
  due: number;
}

export interface ServiceCheckCatalogService {
  key: string;
  label: string;
  satisfiedBy: string[];
  revisitMonths: number | null;
  revisitBecause: string;
}

export interface HouseholdServiceCheckResponse {
  companies: ServiceCheckCompanyBlock[];
  to_ask: number;
  catalog: Array<{ company: string; services: ServiceCheckCatalogService[] }>;
}

// The five states GET /api/services-check/list can actually return:
// "have it with us" and "not asked yet" are never stored, so they never
// appear as a list row (see shared/db/service-check.ts's file header).
// "interested" and "not_applicable" arrived with the Marketing Hub's
// service map (2026-09-27); the card does not offer them yet.
export type ServiceCheckStoredState =
  | "has_it_elsewhere"
  | "not_right_now"
  | "do_not_ask"
  | "interested"
  | "not_applicable";

export interface ServiceCheckListRow {
  id: string;
  household_id: string;
  household_display_name: string;
  company_id: string;
  company_slug: string;
  company_display_name: string;
  service_key: string;
  service_label: string;
  state: ServiceCheckStoredState;
  provider_name: string | null;
  set_up_year: number | null;
  revisit_month: string | null;
  note: string | null;
  answered_by_user_id: string | null;
  answered_by_name: string | null;
  answered_at: string;
  told_us_on: string | null;
  source: string;
  due: boolean;
}

export interface ServiceCheckListParams {
  /** Comma-separated slugs, or "all". Omit for "all" as well — the
   *  server applies no default of its own; the caller (the page)
   *  resolves the default from the viewer's own company scope, the
   *  same lens every other list uses. */
  company?: string;
  /** A user id to scope to "my book" (their households, this company),
   *  or omit for everyone. */
  owner?: string;
  /** Comma-separated states, or omit for all three. */
  state?: string;
  service_key?: string;
  due_only?: boolean;
  due_within_months?: number;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ServiceCheckListResponse {
  rows: ServiceCheckListRow[];
  /** A real COUNT(*) over the same filters — never rows.length. */
  total: number;
}

// ─── Phase 4.2 — Mortgage Compliance types ────────────────────────

export type MortgageComplianceSeverity = "green" | "yellow" | "red";

export interface MortgageContinuingEducationRec {
  id: string;
  user_id: string;
  year: number;
  required_hours: number;
  completed_hours: number;
  completed_at: string | null;
  certificate_document_id: string | null;
  baker_verified_at: string | null;
  baker_verified_by_user_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface MortgageOnlineEdAssignmentRec {
  id: string;
  user_id: string;
  year: number;
  total_courses: number;
  completed_courses: number;
  advisor_marked_complete_at: string | null;
  baker_verified_at: string | null;
  baker_verified_by_user_id: string | null;
  baker_review_task_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface MortgageNmlsQuarterlyReportRec {
  id: string;
  year: number;
  quarter: number;
  due_date: string;
  filed_at: string | null;
  filed_by_user_id: string | null;
  filing_reference: string | null;
  filing_document_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActiveMortgageMloRec {
  user_id: string;
  email: string;
  name: string | null;
}

export interface MyMortgageComplianceResponse {
  year: number;
  user_id: string;
  ce: MortgageContinuingEducationRec | null;
  online_ed: MortgageOnlineEdAssignmentRec | null;
  nmls_quarterly: MortgageNmlsQuarterlyReportRec[];
}

export interface AdminMortgageComplianceResponse {
  year: number;
  mlos: ActiveMortgageMloRec[];
  ce: MortgageContinuingEducationRec[];
  online_ed: MortgageOnlineEdAssignmentRec[];
  nmls_quarterly: MortgageNmlsQuarterlyReportRec[];
}

// Per-MLO breakdown surfaced on the Baker Mortgage Zone 1 card. Mirrors
// BakerMortgageComplianceBreakdown from workers/api/src/routes/dashboard-baker.ts.
export interface BakerMortgageBreakdown {
  ce: { red: number; yellow: number; green: number };
  online_ed: { red: number; yellow: number; green: number };
  nmls_quarterly: { red: number; yellow: number; green: number };
  next_nmls_status: MortgageComplianceSeverity | null;
  next_nmls_due_date: string | null;
  next_nmls_quarter: 1 | 2 | 3 | 4 | null;
}

// ─── Phase 4.3 — Realty Compliance types ──────────────────────────

export type RealtyComplianceSeverity = "green" | "yellow" | "red";

export interface RealtyLicenseRec {
  id: string;
  user_id: string;
  license_type: string;
  license_number: string;
  state: string | null;
  issued_at: string | null;
  expires_at: string;
  company: string | null;
  notes: string | null;
  active: number;
  created_at: string;
  updated_at: string;
  user_email: string | null;
  user_name: string | null;
}

export type RealtyChecklistTemplateId =
  | "rly-tmpl-buyer-broker-init"
  | "rly-tmpl-active-listing"
  | "rly-tmpl-buying"
  | "rly-tmpl-new-build"
  | "rly-tmpl-pending-listing";

export interface RealtyChecklistInstanceRec {
  id: string;
  template_id: string;
  entity_type: string;
  entity_id: string;
  entity_display_name: string | null;
  company: string;
  triggered_at: string;
  due_date: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  notes: string | null;
}

export type RealtyChecklistTaskStatus = "open" | "submitted" | "accepted" | "rejected";

/** One checklist a record is running, with its steps and the template's
 *  name — the name is what the file view's left pane lists, so it comes
 *  down with the instance rather than needing a second lookup. */
export interface RealtyChecklistWithTasks {
  instance: RealtyChecklistInstanceRec;
  template_name: string;
  tasks: RealtyChecklistTaskRec[];
}

export interface RealtyChecklistTaskRec {
  id: string;
  instance_id: string;
  template_item_id: string;
  /** The board stage this step belongs to (2026-09-22), or null when it
   *  is not tied to one. Read from the step's template item. */
  stage_slug?: string | null;
  sort_order: number;
  title: string;
  description: string | null;
  assigned_to_user_id: string | null;
  due_date: string | null;
  completed_at: string | null;
  completed_by_user_id: string | null;
  completion_notes: string | null;
  evidence_url: string | null;
  evidence_text: string | null;
  created_at: string;
  status: RealtyChecklistTaskStatus;
  submitted_at: string | null;
  submitted_by_user_id: string | null;
  review_round: number;
  // ─── Exemption ────────────────────────────────────────────────────
  // A PENDING request to skip this step: set alongside status
  // 'submitted', so the step is with the broker exactly as submitted work
  // is, on the same ten-business-day clock.
  exemption_requested_at: string | null;
  exemption_requested_by_user_id: string | null;
  exemption_reason: string | null;
  // APPROVED. An exempted step is 'accepted' + completed_at like any other
  // cleared step, so every count keeps working; this is what makes the UI
  // say "Exempt" rather than "Done".
  exempted_at: string | null;
  exempted_by_user_id: string | null;
}

// ─── Checklist template editor ──────────────────────────────────────
//
// The admin editor's shapes. Mirrors shared/db/checklist-template-editor.ts.

/** One item as the editor reads it — retired ones included, so they can be
 *  brought back. */
export interface ChecklistTemplateItemRec {
  id: string;
  sort_order: number;
  title: string;
  description: string | null;
  default_assigned_role: string | null;
  default_assigned_user_id: string | null;
  estimated_duration_hours: number | null;
  required: boolean;
  evidence_required: string | null;
  /** Which of the record's own dates the due date counts from. */
  anchor_event: string | null;
  /** Signed days from that anchor: -5 is five days before, +1 one day
   *  after, 0 the same day. Null (with a null anchor) means a plain
   *  checkbox with no due date. */
  due_date_offset_days: number | null;
  /** The "only required if…" rule, as stored JSON. */
  required_if_json: string | null;
  /** The board stage a Realty file draws this step inside (2026-09-23).
   *  Null = "Not tied to a stage". Optional so older fixtures stay valid. */
  stage_slug?: string | null;
  // The Checklists area (2026-09-29). Optional so older fixtures stay valid.
  item_type?: ChecklistItemType;
  collect_via?: string | null;
  document_definition_id?: string | null;
  satisfied_by_template_package_id?: string | null;
  /** OR one library form (esign_templates.id), never both. */
  satisfied_by_esign_template_id?: string | null;
  default_visibility?: ChecklistVisibility | null;
  push_to_todo?: boolean;
  require_explanation?: boolean;
  /** Removed from the checklist. New files stop being asked; files already
   *  running keep whatever is attached. Clearing it brings the item back. */
  retired_at: string | null;
}

/** One item as the editor SENDS it. No id means a brand-new item. */
export interface ChecklistTemplateItemDraft {
  id?: string | null;
  title: string;
  description?: string | null;
  required?: boolean;
  evidence_required?: string | null;
  default_assigned_role?: string | null;
  default_assigned_user_id?: string | null;
  estimated_duration_hours?: number | null;
  anchor_event?: string | null;
  due_date_offset_days?: number | null;
  required_if_json?: string | null;
  /** Omit to leave the stored stage alone; null = "Not tied to a stage". */
  stage_slug?: string | null;
  // The Checklists area (2026-09-29). Each: omit to leave it alone.
  item_type?: ChecklistItemType;
  collect_via?: string | null;
  document_definition_id?: string | null;
  satisfied_by_template_package_id?: string | null;
  /** OR one library form (esign_templates.id), never both. */
  satisfied_by_esign_template_id?: string | null;
  default_visibility?: ChecklistVisibility | null;
  push_to_todo?: boolean;
  require_explanation?: boolean;
}

/** One row of a company's list in the Checklists area. */
export interface ChecklistListRowRec {
  id: string;
  name: string;
  company: string;
  kind: ChecklistKind;
  applies_to: ChecklistAppliesTo | null;
  side: ChecklistSide | null;
  default_from_stage: string | null;
  is_default: boolean;
  active: boolean;
  item_count: number;
  required_count: number;
  updated_at: string;
  updated_by_name: string | null;
}

/** Someone who may edit (or review) a company's checklists, and why. */
export interface ChecklistAuthorityHolderRec {
  user_id: string;
  name: string;
  because: string;
  grant_id: string | null;
}

/** What "apply to files already in flight" actually did. Every number is
 *  reported, including the ones it deliberately did NOT touch — a sweep
 *  that quietly skipped things would read as a clean sweep. */
export interface ChecklistPropagationReport {
  instances: number;
  tasks_added: number;
  tasks_updated: number;
  tasks_removed: number;
  /** Steps for a removed item that were KEPT because somebody had already
   *  worked them. */
  tasks_kept_with_work: number;
  /** Newly added conditional items, which a template-level pass cannot
   *  evaluate; they land on each file's next "Refresh". */
  conditional_items_deferred: number;
}

export interface ChecklistTemplateVersionRec {
  id: string;
  template_id: string;
  version_number: number;
  change_summary: string;
  change_kind: "edit" | "rollback";
  restored_from_version_id: string | null;
  applied_to_in_flight: boolean;
  propagation: ChecklistPropagationReport | null;
  changed_by_user_id: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

export interface ChecklistAuthorityGrantRec {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  company: string;
  capability: "edit_templates" | "broker_review";
  reason: string | null;
  granted_by_user_id: string | null;
  granted_at: string;
}

export interface RealtyChecklistTaskReviewRec {
  id: string;
  task_id: string;
  round: number;
  submitted_at: string;
  submitted_by_user_id: string | null;
  decision: "accepted" | "rejected";
  reason: string | null;
  /** What was decided — submitted WORK, or a request to EXEMPT the step.
   *  Absent on rounds recorded before exemptions existed, which were all
   *  about work. */
  subject?: "work" | "exemption";
  reviewed_by_user_id: string;
  reviewed_at: string;
}

// Per-MLO breakdown surfaced on the Baker Realty Zone 1 card. Mirrors
// BakerRealtyComplianceBreakdown from workers/api/src/routes/dashboard-baker.ts.
export interface BakerRealtyBreakdown {
  licenses: { red: number; yellow: number; green: number; total: number };
  checklist_tasks: {
    red: number;
    yellow: number;
    green: number;
    total: number;
  };
  next_license_status: RealtyComplianceSeverity | null;
  next_license_expires_at: string | null;
  next_license_user_name: string | null;
}

/** One exam pack's self-description — the catalog entry, without the
 *  query. Mirrors shared/exports/exam-packs.ts's definition minus
 *  `run`. */
export interface RetentionClassSummary {
  slug: string;
  display_name: string;
  plain_english_basis: string;
  citation: string;
  min_years: number | null;
  event_basis: string;
  disposition: string;
  version: number;
}

export interface RetentionClassVersionSummary {
  slug: string;
  version: number;
  min_years: number | null;
  effective_from: string;
  superseded_at: string | null;
  change_reason: string | null;
}

export interface LegalMatterSummary {
  id: string;
  name: string;
  reason: string;
  scope_type: string;
  scope_id: string | null;
  opened_at: string;
  released_at: string | null;
  release_reason: string | null;
}

export interface ExamPackSummary {
  slug: string;
  companySlug: string;
  title: string;
  regulator: string;
  citation: string;
  purpose: string;
  productionSla: string | null;
  columns: Array<{ key: string; label: string }>;
}

/** A household's documents for one company (or "shared" — the family's
 *  own). `isOwn` marks the viewer's line of business, which is what
 *  the UI expands by default. */
export interface HouseholdDocumentGroupRec {
  key: string;
  label: string;
  isOwn: boolean;
  documents: Array<DocumentRec & { company_slug: string | null; attached_to_label: string | null }>;
}

// ─── Board Setup types ─────────────────────────────────────────────────
// The shapes the setup screen edits. Drafts are what the editor holds
// while you are typing (no ids — identity is the key), and BoardSetup is
// what the server hands back for one board.

export interface BoardSetupTaskDraft {
  stage_slug: string;
  task_key: string;
  label: string;
  owner_kind: StageTaskOwnerKind;
  due_anchor_kind: "record_date" | "key_date" | null;
  due_anchor_key: string | null;
  due_offset_days: number;
  side: "listing" | "buyer" | null;
}

export interface BoardSetupDateDraft {
  date_key: string;
  label: string;
  rule_text: string;
  resolution: "computed" | "user_set";
  anchor_kind: "record_date" | "key_date" | null;
  anchor_key: string | null;
  offset_days: number;
  show_on_timeline: boolean;
  band_from_key: string | null;
  side: "listing" | "buyer" | null;
}

export interface BoardSetup {
  ok: true;
  company_slug: string;
  workflow: "transaction" | "opportunity";
  type: string;
  stages: { slug: string; label: string; typical_days: number; sort_order: number }[];
  tasks: StageTaskTemplate[];
  dates: ScopedKeyDateDefinition[];
  /** False for a company you may look at but not change — the editor
   *  renders read-only with a reason rather than a dead end. */
  editable: boolean;
  record_anchors: { key: string; label: string }[];
}

// ─── Structured notes (Stewardship Financial) ───────────────────────────
//
// A standalone export rather than another method on `api` — same reason
// apiV11 exists, plus it keeps this feature's surface out of a 23,000-line
// object that several sessions edit at once.

export type {
  BarrierType,
  CouponFrequency,
  NoteStatus,
  NoteType,
  StructuredNote,
} from "@shared/structured-notes/types";
export type {
  NotePerformance,
  NoteTimeline,
  CouponEconomics,
  // Renamed on the way out: the OS already exports a RiskBand for
  // at-risk households, and the two scales mean different things.
  RiskBand as NoteRiskBand,
} from "@shared/structured-notes/payoff";

/** A note plus everything the API computed from live market data. */
export interface StructuredNoteView extends StructuredNote {
  performance: NotePerformance;
  coupons: CouponEconomics;
  timeline: NoteTimeline;
}

export interface StructuredNoteListResponse {
  notes: StructuredNoteView[];
  /** COUNT over the same filter — not notes.length. */
  total: number;
  /** Newest quote timestamp across every symbol, unix seconds. */
  prices_as_of: number | null;
  /** At least one symbol came back without a price. */
  prices_degraded: boolean;
}

export interface StructuredNoteHistoryResponse {
  from: string;
  underlyings: {
    ticker: string;
    initial_level: number | null;
    series: { date: string; close: number }[];
  }[];
}

/** The terms Stew proposed from a term sheet. All strings — the form parses. */
export interface ExtractedNoteTermsView {
  name: string | null;
  issuer: string | null;
  cusip: string | null;
  note_type: string | null;
  trade_date: string | null;
  issue_date: string | null;
  maturity_date: string | null;
  face_value: string | null;
  coupon_rate: string | null;
  coupon_frequency: string | null;
  coupon_barrier_pct: string | null;
  memory_coupon: boolean | null;
  barrier_pct: string | null;
  barrier_type: string | null;
  leverage_pct: string | null;
  call_premium_pct: string | null;
  call_condition: string | null;
  call_dates: string[];
  underlyings: { name: string | null; ticker: string | null; initial_level: string | null }[];
  note: string;
}

/** Write shape. Mirrors the route's validator — everything optional but
 *  name, type, and at least one underlying. */
export interface StructuredNoteWriteBody {
  name: string;
  issuer?: string | null;
  cusip?: string | null;
  note_type: NoteType;
  note_status?: NoteStatus;
  called_date?: string | null;
  trade_date?: string | null;
  issue_date?: string | null;
  maturity_date?: string | null;
  face_value?: number | null;
  quantity?: number | null;
  current_note_price?: number | null;
  current_note_price_as_of?: string | null;
  coupon_rate?: number | null;
  coupon_frequency?: CouponFrequency | null;
  coupon_barrier_pct?: number | null;
  memory_coupon?: boolean;
  coupons_paid?: number | null;
  barrier_pct?: number | null;
  barrier_type?: BarrierType | null;
  leverage_pct?: number | null;
  call_premium_pct?: number | null;
  call_condition?: string | null;
  call_dates?: string[];
  terms_notes?: string | null;
  underlyings: {
    name?: string | null;
    ticker: string;
    initial_level: number;
    weight_pct?: number | null;
  }[];
  /** Sent on update so a concurrent save is refused rather than clobbered. */
  expected_version_number?: number;
  source?: "manual_entry" | "term_sheet_extract";
}

export const structuredNotesApi = {
  async list(status?: NoteStatus): Promise<StructuredNoteListResponse> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<StructuredNoteListResponse>(`/api/structured-notes${qs}`);
  },

  async get(id: string): Promise<{ note: StructuredNoteView; prices_as_of: number | null }> {
    return request(`/api/structured-notes/${encodeURIComponent(id)}`);
  },

  async history(id: string): Promise<StructuredNoteHistoryResponse> {
    return request(`/api/structured-notes/${encodeURIComponent(id)}/history`);
  },

  async create(body: StructuredNoteWriteBody): Promise<{ note: StructuredNoteView }> {
    return request("/api/structured-notes", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async update(id: string, body: StructuredNoteWriteBody): Promise<{ note: StructuredNoteView }> {
    return request(`/api/structured-notes/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  async remove(
    id: string,
  ): Promise<{ ok: true; undo: { structured_note_id: string; name: string } }> {
    return request(`/api/structured-notes/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  async restore(id: string): Promise<{ note: StructuredNoteView }> {
    return request(`/api/structured-notes/${encodeURIComponent(id)}/restore`, {
      method: "POST",
    });
  },

  /** Read a term sheet. Base64 in JSON so it rides the standard request
   *  helper, the same way voice notes send audio. */
  async extractTerms(input: {
    file_base64: string;
    mime_type: string;
    file_name: string;
  }): Promise<{ terms: ExtractedNoteTermsView }> {
    return request("/api/structured-notes/extract-terms", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
};

// ─── Structured note holdings (Phase 2) ─────────────────────────────────

export type {
  StructuredNoteHolding,
  StructuredNoteHoldingView,
} from "@shared/structured-notes/types";

/** A household's holding, decorated with the note's own facts — the
 *  household screen's "Structured Notes" card shape. */
export interface HouseholdNoteHoldingView {
  id: string;
  structured_note_id: string;
  household_id: string;
  portfolio_id: string | null;
  quantity_held: number;
  notes: string | null;
  version_number: number;
  note_name: string;
  note_status: NoteStatus;
  note_maturity_date: string | null;
  risk_band: StructuredNoteRiskBand;
}

export interface NoteHoldingsResponse {
  holdings: import("@shared/structured-notes/types").StructuredNoteHoldingView[];
  total_quantity_held: number;
  household_count: number;
  note_quantity: number | null;
}

export interface HoldingWriteBody {
  household_id: string;
  portfolio_id?: string | null;
  quantity_held: number;
  notes?: string | null;
  expected_version_number?: number;
}

export const structuredNoteHoldingsApi = {
  async listForNote(noteId: string): Promise<NoteHoldingsResponse> {
    return request(`/api/structured-notes/${encodeURIComponent(noteId)}/holdings`);
  },

  async create(
    noteId: string,
    body: HoldingWriteBody,
  ): Promise<{ holding: import("@shared/structured-notes/types").StructuredNoteHolding }> {
    return request(`/api/structured-notes/${encodeURIComponent(noteId)}/holdings`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async update(
    holdingId: string,
    body: Omit<HoldingWriteBody, "household_id">,
  ): Promise<{ holding: import("@shared/structured-notes/types").StructuredNoteHolding }> {
    return request(`/api/structured-notes/holdings/${encodeURIComponent(holdingId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  async remove(holdingId: string): Promise<{ ok: true; undo: { holding_id: string } }> {
    return request(`/api/structured-notes/holdings/${encodeURIComponent(holdingId)}`, {
      method: "DELETE",
    });
  },

  async restore(
    holdingId: string,
  ): Promise<{ holding: import("@shared/structured-notes/types").StructuredNoteHolding }> {
    return request(`/api/structured-notes/holdings/${encodeURIComponent(holdingId)}/restore`, {
      method: "POST",
    });
  },

  async listForHousehold(householdId: string): Promise<{ holdings: HouseholdNoteHoldingView[] }> {
    return request(`/api/households/${encodeURIComponent(householdId)}/structured-notes`);
  },
};

export type {
  FinancialBook,
  FinancialBookOwner,
  FinancialBookRow,
  FinancialBookSort,
  FinancialBookStatusFilter,
  FinancialCareTrail,
  TrailEvent,
} from "@shared/db/financial-care-book";


// ─── A file's checklist (Documents tab PR 4) ─────────────────────────
// Mirrors shared/db/record-checklist.ts.

export type RecordChecklistKind = "opportunity" | "transaction";

export interface RecordChecklistItemRec {
  id: string;
  instance_id: string;
  title: string;
  description: string | null;
  item_type: "document" | "step";
  required: boolean;
  state: "missing" | "awaiting_signature" | "collected" | "with_broker" | "waived";
  status: "open" | "submitted" | "accepted" | "rejected";
  collect_via: string | null;
  document_definition_id: string | null;
  require_explanation: boolean;
  satisfied_by_name: string | null;
  /** The template package's id: "Prepare" opens Prepare & send on it. */
  satisfied_by_template_package_id?: string | null;
  /** A single library form tied to the item: "Draft now" opens it. */
  satisfied_by_esign_template_id?: string | null;
  satisfied_by_form_name?: string | null;
  record_level: boolean;
  assigned_to_user_id: string | null;
  assignee_name: string | null;
  due_date: string | null;
  completed_at: string | null;
  completion_notes: string | null;
  document_id: string | null;
  document_filename: string | null;
  sent_back_reason: string | null;
  exemption_requested: boolean;
  /** When awaiting signature: the package it is out in (PR 11). */
  awaiting_envelope_title?: string | null;
  awaiting_envelope_sent_at?: string | null;
}

export interface RecordChecklistSummaryRec {
  total: number;
  collected: number;
  with_broker: number;
  /** Out for signature in a live package (PR 11). */
  awaiting_signature?: number;
  missing: number;
  required_outstanding: number;
  ready_to_submit: number;
}

export interface RecordChecklistRec {
  instance_id: string;
  template_id: string;
  name: string;
  kind: "compliance_audit" | "workflow_sales";
  assigned_how: "default" | "person" | null;
  assigned_at: string;
  assigned_by_name: string | null;
  summary: RecordChecklistSummaryRec;
  items: RecordChecklistItemRec[];
}

export interface FittingChecklistRec {
  id: string;
  name: string;
  kind: "compliance_audit" | "workflow_sales";
  side: "buy" | "sell" | "both" | "na" | null;
  is_default: boolean;
  default_from_stage: string | null;
}

export interface RecordChecklistResponse {
  record: { kind: RecordChecklistKind; id: string; company: string; side: "buy" | "sell" | null };
  checklists: RecordChecklistRec[];
  options: FittingChecklistRec[];
  can_edit: boolean;
  can_review: boolean;
}

/** One box in a document request (Documents tab PR 6a). */
export interface DocumentRequestSlotRec {
  id: string;
  label: string;
  checklist_task_id: string | null;
  document_type: string | null;
  document_id: string | null;
  filename: string | null;
  uploaded_at: string | null;
}

/** A request for documents from a teammate (Documents tab PR 6a). */
export interface DocumentRequestRec {
  id: string;
  entity_type: RecordChecklistKind;
  entity_id: string;
  company: string | null;
  household_display_name: string | null;
  recipient_user_id: string | null;
  recipient_name: string | null;
  /** Someone outside Stewardship (PR 6b). Optional on older fixtures. */
  recipient_email?: string | null;
  requester_user_id: string;
  requester_name: string | null;
  visibility: "internal" | "client";
  note: string | null;
  /** "clue": one CLUE report for a property (PR 7). Optional on older
   *  fixtures; absent reads as "documents". */
  request_type?: "documents" | "clue";
  property_address?: string | null;
  subject_party?: "buyer" | "seller" | null;
  expires_at: string;
  created_at: string;
  last_nudged_at: string | null;
  status: "open" | "filled" | "revoked" | "expired";
  slots: DocumentRequestSlotRec[];
  filled_count: number;
}

/** Who the request window pre-picks, and why. */
export interface SuggestedRecipientRec {
  user_id: string;
  name: string;
  reason: "lender" | "last";
}

export interface ChecklistItemForYouRec {
  id: string;
  title: string;
  item_type: "document" | "step";
  required: boolean;
  state: "missing" | "sent_back";
  due_date: string | null;
  checklist_name: string;
  record_kind: RecordChecklistKind;
  record_id: string;
  company: string;
  household_display_name: string | null;
}

// ─── Company Coverage (Stewardship's own insurance; step 1, 2026-10-01) ──
//
// The firm's OWN policies, not Stewardship Insurance's client book. The
// server decides who sees what (shared/company-coverage/access.ts); these
// calls only carry it. Design: docs/design/company-coverage.md.

export type CompanyPolicyWithSummary = CompanyPolicyAggregate & {
  summary: PolicySummary;
};

export interface CompanyCoverageOverview {
  today: string;
  capabilities: CoverageCapabilities;
  companies: CoverageCompany[];
  company_filter: string | null;
  policies: CompanyPolicyWithSummary[];
  attention: CompanyCoverageAttentionItem[];
  totals: CoverageTotals;
  payers: PayerLine[];
}

export interface CompanyCoverageCheck {
  today: string;
  capabilities: CoverageCapabilities;
  companies: CoverageCompany[];
  types: Array<{ type: CoverageType; label: string }>;
  cells: CheckCell[];
  research: CoverageResearchNote[];
  counts: { gaps: number; to_check: number; to_decide: number };
}

export interface CompanyCoverageSummary {
  renewing_count: number;
  ended_count: number;
  gaps: number;
  to_check: number;
  to_decide: number;
}

export interface CompanyPolicyShare {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string;
  level: "details" | "details_and_documents";
  reason: string;
  granted_by_name: string | null;
  created_at: string;
  revoked_at: string | null;
  revoked_by_name: string | null;
  revoke_reason: string | null;
}

export interface CompanyPolicyAccess {
  can_share: boolean;
  shares: CompanyPolicyShare[];
  held_back_document_ids: string[];
  last_opened: Array<{ user_id: string; user_name: string | null; last_opened_at: string }>;
  employees: Array<{ user_id: string; user_name: string | null; user_email: string; role_slug: string | null }>;
}

export interface CompanyPolicyDetail {
  /** How the signed-in person sees this policy (step 4). */
  sight: "by_role" | "details" | "details_and_documents" | null;
  today: string;
  capabilities: CoverageCapabilities;
  companies: CoverageCompany[];
  policy: CompanyPolicyWithSummary;
  versions: Array<{
    version_number: number;
    source: "manual_edit" | "rollback" | "import";
    reason: string | null;
    created_by_user_id: string | null;
    created_by_name: string | null;
    created_at: string;
  }>;
}

export interface CompanyCoverageLifeAccess extends LifeAccessView {
  employees: Array<{ user_id: string; user_name: string | null; user_email: string; role_slug: string | null }>;
  can_edit: boolean;
}

export interface CompanyCoverageManagers extends CoverageManagersView {
  employees: Array<{ user_id: string; user_name: string | null; user_email: string; role_slug: string | null }>;
  can_manage: boolean;
}

export interface CompanyCoverageProgramReviewList {
  reviews: ProgramReview[];
  capabilities: CoverageCapabilities;
}

export interface CompanyCoverageProgramReviewDetail {
  review: ProgramReview;
  items: ProgramReviewItem[];
  broker_note: string | null;
  companies: CoverageCompany[];
  capabilities: CoverageCapabilities;
}

export const companyCoverageApi = {
  overview(company: string | null = null): Promise<CompanyCoverageOverview> {
    const q = company ? `?company=${encodeURIComponent(company)}` : "";
    return request(`/api/company-coverage/overview${q}`);
  },
  policy(id: string): Promise<CompanyPolicyDetail> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(id)}`);
  },
  create(policy: CompanyPolicyInput): Promise<{ policy: CompanyPolicyWithSummary }> {
    return request(`/api/company-coverage/policies`, { method: "POST", body: JSON.stringify(policy) });
  },
  update(
    id: string,
    policy: CompanyPolicyInput,
    expectedVersion: number,
    reason: string | null = null,
  ): Promise<{ policy: CompanyPolicyWithSummary }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({ policy, expected_version_number: expectedVersion, reason }),
    });
  },
  remove(id: string, expectedVersion: number, reason: string): Promise<{ policy: CompanyPolicyWithSummary }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(id)}`, {
      method: "DELETE",
      body: JSON.stringify({ expected_version_number: expectedVersion, reason }),
    });
  },
  confirmNamed(
    id: string,
    companyId: string,
    expectedVersion: number,
  ): Promise<{ policy: CompanyPolicyWithSummary }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(id)}/confirm-named`, {
      method: "POST",
      body: JSON.stringify({ company_id: companyId, expected_version_number: expectedVersion }),
    });
  },
  undo(id: string): Promise<{ policy: CompanyPolicyWithSummary }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(id)}/undo`, { method: "POST" });
  },
  lifeAccess(): Promise<CompanyCoverageLifeAccess> {
    return request(`/api/company-coverage/life-access`);
  },
  addLifeAccess(userId: string, reason: string): Promise<{ ok: true; id: string }> {
    return request(`/api/company-coverage/life-access`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, reason }),
    });
  },
  removeLifeAccess(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/life-access/${encodeURIComponent(id)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    });
  },
  // ── Managers (2026-10-06) ──
  managers(): Promise<CompanyCoverageManagers> {
    return request(`/api/company-coverage/managers`);
  },
  addManager(userId: string, reason: string): Promise<{ ok: true; id: string }> {
    return request(`/api/company-coverage/managers`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, reason }),
    });
  },
  removeManager(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/managers/${encodeURIComponent(id)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    });
  },
  // ── "What might we be missing?" (2026-10-06) ──
  programReviews(): Promise<CompanyCoverageProgramReviewList> {
    return request(`/api/company-coverage/program-reviews`);
  },
  runProgramReview(): Promise<{ ok: true; id: string }> {
    return request(`/api/company-coverage/program-reviews`, { method: "POST" });
  },
  programReview(id: string): Promise<CompanyCoverageProgramReviewDetail> {
    return request(`/api/company-coverage/program-reviews/${encodeURIComponent(id)}`);
  },
  decideProgramReviewItem(
    reviewId: string,
    itemId: string,
    decision: ProgramReviewDecision,
    reason: string,
  ): Promise<{ ok: true }> {
    return request(
      `/api/company-coverage/program-reviews/${encodeURIComponent(reviewId)}/items/${encodeURIComponent(itemId)}/decide`,
      { method: "POST", body: JSON.stringify({ decision, reason }) },
    );
  },
  undoProgramReviewItem(reviewId: string, itemId: string): Promise<{ ok: true }> {
    return request(
      `/api/company-coverage/program-reviews/${encodeURIComponent(reviewId)}/items/${encodeURIComponent(itemId)}/undo`,
      { method: "POST" },
    );
  },
  // ── Step 2 ──
  readDecPage(
    policyId: string,
    documentId: string,
  ): Promise<{ reading: DecPageReading; document: { id: string; filename: string } }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(policyId)}/read-dec-page`, {
      method: "POST",
      body: JSON.stringify({ document_id: documentId }),
    });
  },
  startImport(readerNotes: string | null, brokerName: string | null): Promise<{ import: CoverageImportSummary }> {
    return request(`/api/company-coverage/imports`, {
      method: "POST",
      body: JSON.stringify({ reader_notes: readerNotes, broker_name: brokerName }),
    });
  },
  listImports(): Promise<{ imports: CoverageImportSummary[] }> {
    return request(`/api/company-coverage/imports`);
  },
  getImport(id: string): Promise<CoverageImportDetail> {
    return request(`/api/company-coverage/imports/${encodeURIComponent(id)}`);
  },
  readImport(
    id: string,
    body: { pdf_document_id: string | null; sheet_text: string | null; reader_notes: string | null; broker_name: string | null },
  ): Promise<{ reading: CoverageImportReading }> {
    return request(`/api/company-coverage/imports/${encodeURIComponent(id)}/read`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  applyImport(id: string, keys: string[]): Promise<{ ok: true; created_policy_ids: string[] }> {
    return request(`/api/company-coverage/imports/${encodeURIComponent(id)}/apply`, {
      method: "POST",
      body: JSON.stringify({ keys }),
    });
  },
  // ── Step 5a ──
  check(): Promise<CompanyCoverageCheck> {
    return request(`/api/company-coverage/check`);
  },
  summary(): Promise<CompanyCoverageSummary> {
    return request(`/api/company-coverage/summary`);
  },
  setRequirement(body: {
    company_id: string;
    coverage_type: CoverageType;
    min_limit_cents: number;
    limit_label: string | null;
    basis: RequirementBasis;
    reason: string;
  }): Promise<{ ok: true; id: string }> {
    return request(`/api/company-coverage/requirements`, { method: "PUT", body: JSON.stringify(body) });
  },
  removeRequirement(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/requirements/${encodeURIComponent(id)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    });
  },
  restoreRequirement(id: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/requirements/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },
  setDecision(body: {
    company_id: string;
    coverage_type: CoverageType;
    decision: "needed" | "not_needed";
    reason: string;
  }): Promise<{ ok: true; id: string }> {
    return request(`/api/company-coverage/decisions`, { method: "PUT", body: JSON.stringify(body) });
  },
  removeDecision(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/decisions/${encodeURIComponent(id)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    });
  },
  restoreDecision(id: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/decisions/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },
  // ── Step 4 ──
  access(policyId: string): Promise<CompanyPolicyAccess> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(policyId)}/access`);
  },
  share(
    policyId: string,
    userId: string,
    level: "details" | "details_and_documents",
    reason: string,
  ): Promise<{ ok: true; id: string }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(policyId)}/shares`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, level, reason }),
    });
  },
  stopShare(shareId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/shares/${encodeURIComponent(shareId)}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    });
  },
  restoreShare(shareId: string): Promise<{ ok: true }> {
    return request(`/api/company-coverage/shares/${encodeURIComponent(shareId)}/restore`, {
      method: "POST",
    });
  },
  holdBack(policyId: string, documentId: string, held: boolean): Promise<{ ok: true }> {
    return request(
      `/api/company-coverage/policies/${encodeURIComponent(policyId)}/documents/${encodeURIComponent(documentId)}/hold-back`,
      { method: "POST", body: JSON.stringify({ held }) },
    );
  },
  // ── Step 3 ──
  reviews(policyId: string): Promise<{ reviews: RenewalReview[] }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(policyId)}/reviews`);
  },
  startReview(
    policyId: string,
    renewalDocumentId: string,
    priorDocumentId?: string | null,
  ): Promise<{ review: RenewalReview }> {
    return request(`/api/company-coverage/policies/${encodeURIComponent(policyId)}/reviews`, {
      method: "POST",
      body: JSON.stringify(
        priorDocumentId === undefined
          ? { renewal_document_id: renewalDocumentId }
          : { renewal_document_id: renewalDocumentId, prior_document_id: priorDocumentId },
      ),
    });
  },
  review(reviewId: string): Promise<{
    review: RenewalReview;
    policy: CompanyPolicyWithSummary;
    capabilities: CoverageCapabilities;
  }> {
    return request(`/api/company-coverage/reviews/${encodeURIComponent(reviewId)}`);
  },
  decideReview(
    reviewId: string,
    decision: "accept" | "push_back" | "shop",
    reason: string,
    expectedVersion: number | null,
  ): Promise<{ review: RenewalReview }> {
    return request(`/api/company-coverage/reviews/${encodeURIComponent(reviewId)}/decide`, {
      method: "POST",
      body: JSON.stringify({ decision, reason, expected_version_number: expectedVersion }),
    });
  },
  undoImport(id: string, reason: string): Promise<{ ok: true; removed: number; skipped: number }> {
    return request(`/api/company-coverage/imports/${encodeURIComponent(id)}/undo`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
};

export interface CoverageImportSummary {
  id: string;
  status: "started" | "read" | "applied" | "undone";
  reader_notes: string | null;
  broker_name: string | null;
  applied_policy_count: number | null;
  undo_reason: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  applied_at: string | null;
  undone_at: string | null;
}

export interface CoverageImportDetail {
  import: CoverageImportSummary;
  reading: CoverageImportReading | null;
  live_policy_ids: string[];
}

// ─── Trusts (2026-10-14; docs/design/trusts.md) ───────────────────────
//
// A trust is its own record, owned by a household. The server is the one
// that decides permissions (company scope on product links, who may
// confirm a found trust); these calls just carry the screen's intent.

export type {
  TrustDetail,
  TrustHistoryEntry,
  TrustHoldingView,
  TrustPersonView,
  TrustPersonRole,
  TrustHoldingRole,
  TrustAssetKind,
  TrustKind,
  TrustRow,
} from "../../shared/types/trust";
import type {
  TrustAssetKind as TrustAssetKindT,
  TrustDetail as TrustDetailT,
  TrustHistoryEntry as TrustHistoryEntryT,
  TrustHoldingRole as TrustHoldingRoleT,
  TrustKind as TrustKindT,
  TrustPersonRole as TrustPersonRoleT,
} from "../../shared/types/trust";

/** What every trust write answers with: the trust as it is now (null when
 *  it was removed), its history, and whether this viewer may confirm it. */
export interface TrustWriteResponse {
  ok: true;
  trust_id: string;
  removed: boolean;
  detail: TrustDetailT | null;
  history: TrustHistoryEntryT[];
  viewer_can_confirm: boolean;
  confirm_who: string;
}

export interface TrustFieldsPayload {
  legal_name?: string;
  dated?: string | null;
  trust_kind?: TrustKindT;
  status?: "active" | "ended";
  ended_on?: string | null;
  governing_state?: string | null;
  notes?: string | null;
}

export interface TrustPersonPayload {
  contact_id: string;
  role: TrustPersonRoleT;
  position?: number | null;
  note?: string | null;
}

export type TrustHoldingPayload =
  | { service_record_id: string; holding_role: TrustHoldingRoleT; note?: string | null }
  | { asset_kind: TrustAssetKindT; description: string; estimated_value_cents?: number | null; note?: string | null };

export const trustsApi = {
  async forHousehold(householdId: string): Promise<{
    owned: TrustDetailT[];
    related: TrustDetailT[];
    viewer_can_confirm: boolean;
    confirm_who: string;
  }> {
    return request(`/api/trusts/by-household/${encodeURIComponent(householdId)}`);
  },
  async forContact(contactId: string): Promise<{ trusts: TrustDetailT[] }> {
    return request(`/api/trusts/by-contact/${encodeURIComponent(contactId)}`);
  },
  async forServiceRecord(serviceRecordId: string): Promise<{ trusts: TrustDetailT[] }> {
    return request(`/api/trusts/by-service-record/${encodeURIComponent(serviceRecordId)}`);
  },
  async get(trustId: string): Promise<{
    detail: TrustDetailT;
    history: TrustHistoryEntryT[];
    viewer_can_confirm: boolean;
    confirm_who: string;
  }> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}`);
  },
  async create(
    input: TrustFieldsPayload & {
      household_id: string;
      legal_name: string;
      people?: TrustPersonPayload[];
      holdings?: TrustHoldingPayload[];
    },
  ): Promise<TrustWriteResponse> {
    return request(`/api/trusts`, { method: "POST", body: JSON.stringify(input) });
  },
  async edit(
    trustId: string,
    input: TrustFieldsPayload & { document_id?: string | null; version_number: number },
  ): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  async move(
    trustId: string,
    input: { household_id: string; version_number: number; reason_code?: string | null; reason_text?: string | null },
  ): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/move`, { method: "POST", body: JSON.stringify(input) });
  },
  async confirm(trustId: string, versionNumber: number): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/confirm`, {
      method: "POST",
      body: JSON.stringify({ version_number: versionNumber }),
    });
  },
  async remove(
    trustId: string,
    input: { version_number: number; reason_code: string; reason_text?: string | null },
  ): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/remove`, { method: "POST", body: JSON.stringify(input) });
  },
  async addPerson(trustId: string, input: TrustPersonPayload & { version_number: number }): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/people`, { method: "POST", body: JSON.stringify(input) });
  },
  async removePerson(trustId: string, rowId: string, versionNumber: number): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/people/${encodeURIComponent(rowId)}/remove`, {
      method: "POST",
      body: JSON.stringify({ version_number: versionNumber }),
    });
  },
  async addHolding(trustId: string, input: TrustHoldingPayload & { version_number: number }): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/holdings`, { method: "POST", body: JSON.stringify(input) });
  },
  async removeHolding(trustId: string, rowId: string, versionNumber: number): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/holdings/${encodeURIComponent(rowId)}/remove`, {
      method: "POST",
      body: JSON.stringify({ version_number: versionNumber }),
    });
  },
  async undo(trustId: string, versionNumber: number): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/undo`, {
      method: "POST",
      body: JSON.stringify({ version_number: versionNumber }),
    });
  },
  async rollback(trustId: string, toVersion: number, versionNumber: number): Promise<TrustWriteResponse> {
    return request(`/api/trusts/${encodeURIComponent(trustId)}/rollback`, {
      method: "POST",
      body: JSON.stringify({ to_version: toVersion, version_number: versionNumber }),
    });
  },
};

// ─── Pay runs (commissions build program B3) ─────────────────────────────
// Payroll by pay date, one company's run, the unpaid earnings ledger, and
// the payroll setup on the Team page. Every route checks the company on the
// server; the screens only show what it returns.

// The John Hancock 401(k) file (commissions build program 9.5). These calls
// carry pay dates, team names and name pairings only. The template and the
// Gusto reports are read in the browser and never sent: nothing here takes a
// file, an SSN or an amount.
export const retirementFileApi = {
  async setup(): Promise<{ ok: true; setup: RetirementFileSetup }> {
    return request("/api/retirement-file/setup");
  },
  /** Paydays whose contribution is due and not marked uploaded (the home-screen strip). */
  async due(): Promise<{ ok: true; today: string; due: RetirementFileSetup["paydays"] }> {
    return request("/api/retirement-file/due");
  },
  async markUploaded(input: { pay_date: string; uploaded_on: string }): Promise<{ ok: true; id: string }> {
    return request("/api/retirement-file/uploads", { method: "POST", body: JSON.stringify(input) });
  },
  async withdrawUpload(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/retirement-file/uploads/${encodeURIComponent(id)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async saveNameLink(input: { source: RetirementNameSource; file_name: string; user_id: string }): Promise<{ ok: true; id: string; name_key: string }> {
    return request("/api/retirement-file/name-links", { method: "POST", body: JSON.stringify(input) });
  },
  async endNameLink(id: string): Promise<{ ok: true }> {
    return request(`/api/retirement-file/name-links/${encodeURIComponent(id)}/end`, { method: "POST", body: "{}" });
  },
  // Gusto's 401(k) numbers for one payday through the read-only connection.
  // The response is journal-shaped CSV text for the browser to read; nothing
  // here sends a file, an SSN or an amount.
  async gustoJournals(payDate: string): Promise<{
    ok: true;
    pay_date: string;
    journals: Array<{ company_slug: string | null; company_name: string; off_cycle: boolean; fetched_at: string; people: number; filename: string; csv: string }>;
  }> {
    return request(`/api/retirement-file/gusto-journals?pay_date=${encodeURIComponent(payDate)}`);
  },
};

/** The operations desk page (build step C1). Reads only. */
export const opsDeskApi = {
  async desk(scope: "mine" | "all"): Promise<{ desk: OpsDeskView }> {
    return request(`/api/ops-desk?scope=${scope}`);
  },
  async report(month: string): Promise<{ report: ManualActionReport }> {
    return request(`/api/ops-desk/report?month=${encodeURIComponent(month)}`);
  },
};

export const payRunsApi = {
  // ── Statements (step B5) ──
  async myStatements(): Promise<{ statements: MyStatements }> {
    return request("/api/pay-runs/statements/mine");
  },
  async statement(statementId: string): Promise<{ ok: true; statement: PayStatementView }> {
    return request(`/api/pay-runs/statements/${encodeURIComponent(statementId)}`);
  },
  statementPdfUrl(statementId: string): string {
    return `/api/pay-runs/statements/${encodeURIComponent(statementId)}/statement.pdf`;
  },
  async answerStatement(
    statementId: string,
    input: { kind: "looks_right" | "question" | "reply"; body?: string; asks_payroll_change?: boolean },
  ): Promise<{ ok: true; message_id: string }> {
    return request(`/api/pay-runs/statements/${encodeURIComponent(statementId)}/messages`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async markPayrollChangeDone(messageId: string, note: string | null): Promise<{ ok: true }> {
    return request(`/api/pay-runs/statement-messages/${encodeURIComponent(messageId)}/payroll-change-done`, {
      method: "POST",
      body: JSON.stringify({ note }),
    });
  },
  async runStatements(runId: string): Promise<{ ok: true; statements: RunStatements }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/statements`);
  },
  async sendStatements(runId: string): Promise<{ ok: true; frozen: number; mode: "shadow" | "live" }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/statements/send`, { method: "POST", body: "{}" });
  },
  async access(): Promise<{
    access: PayAccess;
    companies: { company: PayCompany; label: string; channel: "gusto" | "ach"; waits_on: string | null }[];
    today: string;
    may_use_retirement_file?: boolean;
  }> {
    return request("/api/pay-runs/access");
  },
  async payDates(): Promise<{
    pay_dates: { pay_date: string; nominal: string | null; moved_because: string | null; what_it_pays: string[]; runs: number; off_cycle: boolean }[];
    today: string;
  }> {
    return request("/api/pay-runs/pay-dates");
  },
  async payDate(payDate: string): Promise<{ overview: PayDateOverview }> {
    return request(`/api/pay-runs/pay-dates/${encodeURIComponent(payDate)}`);
  },
  async draft(input: { company: string; pay_date: string; payroll_kind?: "regular" | "off_cycle"; note?: string }): Promise<{ ok: true; run_id: string }> {
    return request("/api/pay-runs/runs", { method: "POST", body: JSON.stringify(input) });
  },
  async run(runId: string): Promise<{ ok: true; detail: PayRunDetail }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}`);
  },
  async rebuild(runId: string): Promise<{ ok: true; lines: number; checks: number }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/rebuild`, { method: "POST", body: "{}" });
  },
  async approve(runId: string, linesHash: string): Promise<{ ok: true; approved_as: "founder" | "backup"; approver_paid_on_run: boolean }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/approve`, {
      method: "POST",
      body: JSON.stringify({ lines_hash: linesHash }),
    });
  },
  async newVersion(runId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/new-version`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async discard(runId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/discard`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async exportFile(runId: string, reason: string | null): Promise<{
    ok: true;
    export: PayRunExportRow;
    content: string;
  }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/exports`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async markSubmitted(runId: string, input: { export_id: string; external_reference: string; check_date: string }): Promise<{ ok: true }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/submissions`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async withdrawSubmission(submissionId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/submissions/${encodeURIComponent(submissionId)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async unpaid(params: { company?: string | null; who?: "all" | "current" | "former" }): Promise<{ ledger: UnpaidLedger }> {
    const q = new URLSearchParams();
    if (params.company) q.set("company", params.company);
    if (params.who && params.who !== "all") q.set("who", params.who);
    const qs = q.toString();
    return request(`/api/pay-runs/unpaid${qs ? `?${qs}` : ""}`);
  },
  async decideHeld(earningId: string, decision: "release" | "not_payable", reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/earnings/${encodeURIComponent(earningId)}/decision`, {
      method: "POST",
      body: JSON.stringify({ decision, reason }),
    });
  },
  async setup(company: string): Promise<{
    calendar: PayCalendarSettingsRow;
    approvers: PayApproverRow[];
    gusto_links: GustoLinkRow[];
    import_tests: GustoImportTestRow[];
    may_edit: boolean;
  }> {
    return request(`/api/pay-runs/setup/${encodeURIComponent(company)}`);
  },
  async saveCalendar(company: string, input: { processing_days: number | null; submission_cutoff_az: string | null; note?: string | null }): Promise<{ ok: true }> {
    return request(`/api/pay-runs/setup/${encodeURIComponent(company)}/calendar`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
  async recordImportTest(company: string, input: { case_key: string; result: "passed" | "failed" | "not_run"; notes: string | null }): Promise<{
    ok: true;
    import_tests: GustoImportTestRow[];
  }> {
    return request(`/api/pay-runs/setup/${encodeURIComponent(company)}/import-tests`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async team(userId: string): Promise<{
    gusto_links: GustoLinkRow[];
    separations: SeparationRow[];
    gusto_companies: { company: string; label: string; gusto_company_name: string | null }[];
  }> {
    return request(`/api/pay-runs/team/${encodeURIComponent(userId)}`);
  },
  async setGustoLink(userId: string, company: string, input: { gusto_employee_id: string | null; gusto_first_name: string; gusto_last_name: string }): Promise<{ ok: true }> {
    return request(`/api/pay-runs/team/${encodeURIComponent(userId)}/gusto-links/${encodeURIComponent(company)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
  async endGustoLink(userId: string, company: string): Promise<{ ok: boolean }> {
    return request(`/api/pay-runs/team/${encodeURIComponent(userId)}/gusto-links/${encodeURIComponent(company)}`, { method: "DELETE" });
  },
  async recordSeparation(userId: string, input: { last_day: string; kind: "let_go" | "left"; note: string | null }): Promise<{ ok: true }> {
    return request(`/api/pay-runs/team/${encodeURIComponent(userId)}/separations`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  async withdrawSeparation(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/separations/${encodeURIComponent(id)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  async confirmFinalWages(id: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/separations/${encodeURIComponent(id)}/final-wages`, { method: "POST", body: "{}" });
  },
  async lookup(input: { company: string; pay_date: string; payroll_kind?: "regular" | "off_cycle" }): Promise<{ run_id: string | null }> {
    const q = new URLSearchParams({ company: input.company, pay_date: input.pay_date, payroll_kind: input.payroll_kind ?? "regular" });
    return request(`/api/pay-runs/lookup?${q.toString()}`);
  },

  // ─── Proof of pay (step B4) ───────────────────────────────────────────
  async uploadGustoReport(runId: string, input: { text: string; filename: string; purpose: "proof" | "seeded_test"; note?: string | null }): Promise<{
    ok: true;
    report_id: string;
    open_count: number;
  }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/gusto-reports`, { method: "POST", body: JSON.stringify(input) });
  },
  // Load Gusto's report through the read-only connection instead of uploading it. Same comparison as an upload.
  async loadGustoReportFromGusto(runId: string): Promise<{
    ok: true;
    report_id: string;
    open_count: number;
  }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/gusto-reports/from-gusto`, { method: "POST", body: "{}" });
  },
  async withdrawGustoReport(reportId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/gusto-reports/${encodeURIComponent(reportId)}/withdraw`, { method: "POST", body: JSON.stringify({ reason }) });
  },
  async resolveDifference(reportId: string, input: { row_key: string; resolution: string; reason: string; recover_cents?: number | null }): Promise<{
    ok: true;
    resolution_id: string;
  }> {
    return request(`/api/pay-runs/gusto-reports/${encodeURIComponent(reportId)}/resolutions`, { method: "POST", body: JSON.stringify(input) });
  },
  async withdrawResolution(resolutionId: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/resolutions/${encodeURIComponent(resolutionId)}/withdraw`, { method: "POST", body: JSON.stringify({ reason }) });
  },
  async classifyCode(company: string, input: { code: string; kind: "commission" | "bonus" | "not_commission"; note?: string | null }): Promise<{ ok: true }> {
    return request(`/api/pay-runs/setup/${encodeURIComponent(company)}/earnings-codes`, { method: "PUT", body: JSON.stringify(input) });
  },
  async setQboAccounts(company: string, type: "commission" | "bonus", accountIds: string[]): Promise<{ ok: true }> {
    return request(`/api/pay-runs/setup/${encodeURIComponent(company)}/qbo-accounts/${type}`, {
      method: "PUT",
      body: JSON.stringify({ account_ids: accountIds }),
    });
  },
  async recordPaymentException(runId: string, input: {
    line_id: string;
    kind: PaymentExceptionRow["kind"];
    amount_cents: number;
    occurred_on: string;
    reference?: string | null;
    note?: string | null;
    replaces_exception_id?: string | null;
  }): Promise<{ ok: true; exception_id: string }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/payment-exceptions`, { method: "POST", body: JSON.stringify(input) });
  },
  async withdrawPaymentException(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/payment-exceptions/${encodeURIComponent(id)}/withdraw`, { method: "POST", body: JSON.stringify({ reason }) });
  },
  async paidLines(company: string, userId: string): Promise<{
    lines: { line_id: string; pay_date: string; earnings_type: "commission" | "bonus"; amount_cents: number }[];
  }> {
    const q = new URLSearchParams({ company, user_id: userId });
    return request(`/api/pay-runs/recoveries/lines?${q.toString()}`);
  },
  async recordChargeback(input: {
    company: string;
    user_id: string;
    original_line_id: string | null;
    description: string;
    chargeback_cents: number;
    received_on: string;
    recover_cents: number | null;
  }): Promise<{ ok: true; recovery_id: string }> {
    return request("/api/pay-runs/recoveries", { method: "POST", body: JSON.stringify(input) });
  },
  async decideRecovery(id: string, decision: "approve" | "absorb", reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/recoveries/${encodeURIComponent(id)}/decision`, { method: "POST", body: JSON.stringify({ decision, reason }) });
  },
  async recordOverpayment(input: {
    company: string;
    user_id: string;
    description: string;
    overpaid_cents: number;
    paid_on: string;
  }): Promise<{ ok: true; recovery_id: string }> {
    return request("/api/pay-runs/recoveries/overpayments", { method: "POST", body: JSON.stringify(input) });
  },
  async monthProof(month: string): Promise<{ proof: PayMonthProof }> {
    return request(`/api/pay-runs/months/${encodeURIComponent(month)}/proof`);
  },
  async confirmAch(runId: string, input: { line_id: string; proof_kind: AchConfirmationRow["proof_kind"]; proof_reference: string; confirmed_on: string }): Promise<{
    ok: true;
  }> {
    return request(`/api/pay-runs/runs/${encodeURIComponent(runId)}/ach-confirmations`, { method: "POST", body: JSON.stringify(input) });
  },
  async withdrawAchConfirmation(id: string, reason: string): Promise<{ ok: true }> {
    return request(`/api/pay-runs/ach-confirmations/${encodeURIComponent(id)}/withdraw`, { method: "POST", body: JSON.stringify({ reason }) });
  },
};
